import { IrresistableGapMatrixResult, IrresistableGapViolationType, IrresistableGapOrigin, IrresistableGapEndpointModuleType, IrresistableGapEndpointContextType, ObjectionPocket, InterventionPocket, MarketTaxonomy, MarketTaxonomyGroup, MarketTaxonomyLabel, AnyMarketTaxonomyLabel, OfferConfig, ModuleCategory, ModuleTier, SessionMemory, MicroGoalConfig, ElitsModule, InterruptRule, PhaseTonality, PriorityConfig, SubsystemConfig, TranslationRegistry, Lang, DifficultyRulesRegistry, VoiceProfile, PersonaPreset } from './types';


// Run Outcome

// ============================================================
// RUN OUTCOME – TECHNISCHE OPTIONEN
// ============================================================

export const RUN_OUTCOME_OPTIONS = [
  { value: 'sold', label: 'Abschluss erzielt' },
  { value: 'follow_up', label: 'Follow-up / Entscheidung später' },
  { value: 'disqualified', label: 'Interessent disqualifiziert' },
  { value: 'failed', label: 'Kein Abschluss erzielt' },
  { value: 'ended', label: 'Gespräch beendet' },
  { value: 'logout', label: 'Logout' },
];


// ============================================================
// RUN OUTCOME – DEUTSCHE LABELS
// ============================================================

export const RUN_OUTCOME_LANGUAGE_DE = {
  sold: {
    label: 'Abschluss erzielt',
  },
  follow_up: {
    label: 'Follow-up / Entscheidung später',
  },
  disqualified: {
    label: 'Interessent disqualifiziert',
  },
  failed: {
    label: 'Kein Abschluss erzielt',
  },
  ended: {
    label: 'Gespräch beendet',
  },
  logout: {
    label: 'Logout',
  },
} as const;


// ============================================================
// RUN OUTCOME – ENGLISCHE LABELS
// ============================================================

export const RUN_OUTCOME_LANGUAGE_EN = {
  sold: {
    label: 'Deal Closed',
  },
  follow_up: {
    label: 'Follow-up / Decision Later',
  },
  disqualified: {
    label: 'Prospect Disqualified',
  },
  failed: {
    label: 'No Deal Closed',
  },
  ended: {
    label: 'Conversation Ended',
  },
  logout: {
    label: 'Logout',
  },
} as const;


// ============================================================
// RUN OUTCOME – SPRACHZUORDNUNG
// ============================================================

export const RUN_OUTCOME_LANGUAGES = {
  de: RUN_OUTCOME_LANGUAGE_DE,
  en: RUN_OUTCOME_LANGUAGE_EN,
} as const;


// Module Scoring

// ============================================================
// SCORE SUMMARY TYPES – TECHNISCHE DEFINITIONEN
// ============================================================

export const SCORE_SUMMARY_TYPE_OPTIONS = [
    // MICROGOAL VARIANTS
    { value: 'BASE',               label: 'Mikroziel erreicht',    icon: '🎯',     sort: 1 },
    { value: 'BASE_DEEP',          label: 'Mikroziel (+Tiefe)',    icon: '🎯👁',   sort: 2 },
    { value: 'BASE_TONE',          label: 'Mikroziel (x Ton)',     icon: '🎯🔝',   sort: 3 },
    { value: 'BASE_DEEP_TONE',     label: 'Mikroziel (Perfekt)',   icon: '🎯👁🔝', sort: 4 },
    { value: 'BONUS_DIAMOND',      label: 'Deepness Diamant',      icon: '💎',     sort: 5 },

    // BONUSES
    { value: 'BONUS_EFFICIENCY',   label: 'Effizienz (Turns)',     icon: '🚀',     sort: 10 },
    { value: 'BONUS_SPEECH',       label: 'Redeanteil Top',        icon: '🗣',     sort: 11 },
    { value: 'BONUS_TONALITY',     label: 'Tonalität (Gesamt)',    icon: '🔝',     sort: 12 },
    { value: 'BONUS_COMBO',        label: 'Double Combo',          icon: '🎯🎯',   sort: 13 },
    { value: 'BONUS_TRIPLE',       label: 'Triple Combo',          icon: '🎯🎯🎯', sort: 14 },
    { value: 'BONUS_INTERVENTION', label: 'Einwand gelöst',        icon: '✅',     sort: 15 },
    { value: 'BONUS_OUTCOME',      label: 'Geschäftsabschluss',   icon: '🏆',     sort: 16 },

    // PENALTIES
    { value: 'PENALTY_HINT',       label: 'Hilfe genutzt',         icon: '❓',     sort: 50 },
    { value: 'PENALTY_REVEAL',     label: 'Aufgedeckt',            icon: '🔍',     sort: 51 },
    { value: 'PENALTY_MANUAL',     label: 'Manuell erfüllt',       icon: '❌',     sort: 52 },
    { value: 'PENALTY_TURN',       label: 'Zu viele Turns',        icon: '❗️',    sort: 53 },
    { value: 'PENALTY_SANDBOX',    label: 'Ghost Mode Abzug',      icon: '🧊',     sort: 99 },
] as const;


// ============================================================
// SCORE SUMMARY TYPES – DEUTSCHE LABELS
// ============================================================

export const SCORE_SUMMARY_TYPE_LANGUAGE_DE = {
    BASE:               { label: 'Mikroziel erreicht' },
    BASE_DEEP:          { label: 'Mikroziel (+Tiefe)' },
    BASE_TONE:          { label: 'Mikroziel (x Ton)' },
    BASE_DEEP_TONE:     { label: 'Mikroziel (Perfekt)' },
    BONUS_DIAMOND:      { label: 'Deepness Diamant' },

    BONUS_EFFICIENCY:   { label: 'Effizienz (Turns)' },
    BONUS_SPEECH:       { label: 'Redeanteil Top' },
    BONUS_TONALITY:     { label: 'Tonalität (Gesamt)' },
    BONUS_COMBO:        { label: 'Double Combo' },
    BONUS_TRIPLE:       { label: 'Triple Combo' },
    BONUS_INTERVENTION: { label: 'Einwand gelöst' },
    BONUS_OUTCOME:      { label: 'Geschäftsabschluss' },

    PENALTY_HINT:       { label: 'Hilfe genutzt' },
    PENALTY_REVEAL:     { label: 'Aufgedeckt' },
    PENALTY_MANUAL:     { label: 'Manuell erfüllt' },
    PENALTY_TURN:       { label: 'Zu viele Turns' },
    PENALTY_SANDBOX:    { label: 'Ghost Mode Abzug' },
};


// ============================================================
// SCORE SUMMARY TYPES – ENGLISH LABELS
// ============================================================

export const SCORE_SUMMARY_TYPE_LANGUAGE_EN = {
    BASE:               { label: 'Micro Goal Achieved' },
    BASE_DEEP:          { label: 'Micro Goal (+Depth)' },
    BASE_TONE:          { label: 'Micro Goal (x Tone)' },
    BASE_DEEP_TONE:     { label: 'Micro Goal (Perfect)' },
    BONUS_DIAMOND:      { label: 'Deepness Diamond' },

    BONUS_EFFICIENCY:   { label: 'Efficiency (Turns)' },
    BONUS_SPEECH:       { label: 'Speech Share Top' },
    BONUS_TONALITY:     { label: 'Tonality (Overall)' },
    BONUS_COMBO:        { label: 'Double Combo' },
    BONUS_TRIPLE:       { label: 'Triple Combo' },
    BONUS_INTERVENTION: { label: 'Objection Resolved' },
    BONUS_OUTCOME:      { label: 'Deal Closed' },

    PENALTY_HINT:       { label: 'Hint Used' },
    PENALTY_REVEAL:     { label: 'Revealed' },
    PENALTY_MANUAL:     { label: 'Manually Completed' },
    PENALTY_TURN:       { label: 'Too Many Turns' },
    PENALTY_SANDBOX:    { label: 'Ghost Mode Deduction' },
};


// ============================================================
// SCORE SUMMARY TYPES – SPRACHZUORDNUNG
// ============================================================

export const SCORE_SUMMARY_TYPE_LANGUAGES = {
    de: SCORE_SUMMARY_TYPE_LANGUAGE_DE,
    en: SCORE_SUMMARY_TYPE_LANGUAGE_EN
};



// Lizenz Management

// ============================================================
// LICENSE_BILLING_PERIOD – Abrechnungszeitraum
// ============================================================

// Technische Definition – sprachunabhängig.
export const LICENSE_BILLING_PERIOD_OPTIONS = [
  { value: 'monthly', label: 'monthly' },
  { value: 'yearly', label: 'yearly' },
];

export const LICENSE_BILLING_PERIOD_LANGUAGE_DE = {
  monthly: { label: 'Monatlich' },
  yearly: { label: 'Jährlich' },
};

export const LICENSE_BILLING_PERIOD_LANGUAGE_EN = {
  monthly: { label: 'Monthly' },
  yearly: { label: 'Yearly' },
};

export const LICENSE_BILLING_PERIOD_LANGUAGES = {
  de: LICENSE_BILLING_PERIOD_LANGUAGE_DE,
  en: LICENSE_BILLING_PERIOD_LANGUAGE_EN,
};


// ============================================================
// LICENSE_PRICING_VIEW – Lizenzbereiche
// ============================================================

// Technische Definition – sprachunabhängig.
export const LICENSE_PRICING_VIEW_OPTIONS = [
  { value: 'solo', label: 'solo' },
  { value: 'team', label: 'team' },
  { value: 'enabler', label: 'enabler' },
  { value: 'member', label: 'member' },
];

export const LICENSE_PRICING_VIEW_LANGUAGE_DE = {
  solo: { label: 'Solo' },
  team: { label: 'Team' },
  enabler: { label: 'Enabler' },
  member: { label: 'Personen' },
};

export const LICENSE_PRICING_VIEW_LANGUAGE_EN = {
  solo: { label: 'Solo' },
  team: { label: 'Team' },
  enabler: { label: 'Enabler' },
  member: { label: 'Members' },
};

export const LICENSE_PRICING_VIEW_LANGUAGES = {
  de: LICENSE_PRICING_VIEW_LANGUAGE_DE,
  en: LICENSE_PRICING_VIEW_LANGUAGE_EN,
};




// Overlay

// ============================================================
// FORMALITY
// ============================================================

// Technische Definition – sprachunabhängig.
export const FORMALITY_OPTIONS = [
  { value: 'formal', label: 'Formell' },
  { value: 'informal', label: 'Informell' },
];

export const FORMALITY_LANGUAGE_DE = {
  formal: { label: 'Formell' },
  informal: { label: 'Informell' },
};

export const FORMALITY_LANGUAGE_EN = {
  formal: { label: 'Formal' },
  informal: { label: 'Informal' },
};

export const FORMALITY_LANGUAGES = {
  de: FORMALITY_LANGUAGE_DE,
  en: FORMALITY_LANGUAGE_EN,
};


// ============================================================
// DIFFICULTY
// ============================================================

// Technische Definition – sprachunabhängig.
export const DIFFICULTY_OPTIONS = [
  { value: 'rookie', label: 'Einsteiger' },
  { value: 'seller', label: 'Verkäufer' },
  { value: 'closer', label: 'Closer' },
  { value: 'elite', label: 'Elite Closer' },
];

export const DIFFICULTY_LANGUAGE_DE = {
  rookie: { label: 'Einsteiger' },
  seller: { label: 'Verkäufer' },
  closer: { label: 'Closer' },
  elite: { label: 'Elite Closer' },
};

export const DIFFICULTY_LANGUAGE_EN = {
  rookie: { label: 'Rookie' },
  seller: { label: 'Seller' },
  closer: { label: 'Closer' },
  elite: { label: 'Elite Closer' },
};

export const DIFFICULTY_LANGUAGES = {
  de: DIFFICULTY_LANGUAGE_DE,
  en: DIFFICULTY_LANGUAGE_EN,
};


// ============================================================
// USER ROLE
// ============================================================

// Technische Definition – sprachunabhängig.
export const USER_ROLE_OPTIONS = [
  { value: 'closer', label: 'Closer (Training - Du führst)' },
  { value: 'prospect', label: 'Prospect (Coaching - AI führt)' },
];

export const USER_ROLE_LANGUAGE_DE = {
  closer: { label: 'Closer (Training - Du führst)' },
  prospect: { label: 'Prospect (Coaching - AI führt)' },
};

export const USER_ROLE_LANGUAGE_EN = {
  closer: { label: 'Closer (Training - You lead)' },
  prospect: { label: 'Prospect (Coaching - AI leads)' },
};

export const USER_ROLE_LANGUAGES = {
  de: USER_ROLE_LANGUAGE_DE,
  en: USER_ROLE_LANGUAGE_EN,
};


// ============================================================
// EXECUTION MODE
// ============================================================

// Technische Definition – sprachunabhängig.
export const EXECUTION_MODE_OPTIONS = [
  { value: 'simulation', label: 'Simulation' },
  { value: 'life', label: 'Life' },
];

export const EXECUTION_MODE_LANGUAGE_DE = {
  simulation: { label: 'Simulation' },
  life: { label: 'Life' },
};

export const EXECUTION_MODE_LANGUAGE_EN = {
  simulation: { label: 'Simulation' },
  life: { label: 'Life' },
};

export const EXECUTION_MODE_LANGUAGES = {
  de: EXECUTION_MODE_LANGUAGE_DE,
  en: EXECUTION_MODE_LANGUAGE_EN,
};


// ObjectManagement

export const TECHNIQUE_TRIAL_POINTS_BY_USES: Record<string, string> = {
  '3': '300',
  '5': '400',
  '10': '700',
};

export const SCRIPT_TRIAL_POINTS_BY_USES: Record<string, string> = {
  '3': '500',
  '5': '700',
  '10': '1200',
};



// ============================================================
// OBJECT_STATUS – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_STATUS_OPTIONS = [
  { value: "approved", label: "approved" },
  { value: "submitted", label: "submitted" },
  { value: "inactive", label: "inactive" },
  { value: "denied", label: "denied" },
];

export const OBJECT_STATUS_LANGUAGE_DE = {
  "approved": { label: "Freigegeben" },
  "submitted": { label: "Eingereicht" },
  "inactive": { label: "Inaktiv" },
  "denied": { label: "Abgelehnt" },
};

export const OBJECT_STATUS_LANGUAGE_EN = {
  "approved": { label: "approved" },
  "submitted": { label: "submitted" },
  "inactive": { label: "inactive" },
  "denied": { label: "denied" },
};

export const OBJECT_STATUS_LANGUAGES = {
  de: OBJECT_STATUS_LANGUAGE_DE,
  en: OBJECT_STATUS_LANGUAGE_EN,
};


// ============================================================
// OBJECT_TIER – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_TIER_OPTIONS = [
  { value: "BASIC", label: "BASIC" },
  { value: "PRO", label: "PRO" },
  { value: "ELITE", label: "ELITE" },
  { value: "LEGENDARY", label: "LEGENDARY" },
];

export const OBJECT_TIER_LANGUAGE_DE = {
  "BASIC": { label: "BASIC" },
  "PRO": { label: "PRO" },
  "ELITE": { label: "ELITE" },
  "LEGENDARY": { label: "LEGENDARY" },
};

export const OBJECT_TIER_LANGUAGE_EN = {
  "BASIC": { label: "BASIC" },
  "PRO": { label: "PRO" },
  "ELITE": { label: "ELITE" },
  "LEGENDARY": { label: "LEGENDARY" },
};

export const OBJECT_TIER_LANGUAGES = {
  de: OBJECT_TIER_LANGUAGE_DE,
  en: OBJECT_TIER_LANGUAGE_EN,
};


// ============================================================
// OBJECT_PRICING_MODEL – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_PRICING_MODEL_OPTIONS = [
  { value: "free", label: "kostenlos" },
  { value: "trial_points", label: "Trial Punkte" },
  { value: "points", label: "Punkte" },
  { value: "eur", label: "Euro" },
];

export const OBJECT_PRICING_MODEL_LANGUAGE_DE = {
  "free": { label: "kostenlos" },
  "trial_points": { label: "Trial Punkte" },
  "points": { label: "Punkte" },
  "eur": { label: "Euro" },
};

export const OBJECT_PRICING_MODEL_LANGUAGE_EN = {
  "free": { label: "free" },
  "trial_points": { label: "Trial points" },
  "points": { label: "Points" },
  "eur": { label: "Euro" },
};

export const OBJECT_PRICING_MODEL_LANGUAGES = {
  de: OBJECT_PRICING_MODEL_LANGUAGE_DE,
  en: OBJECT_PRICING_MODEL_LANGUAGE_EN,
};


// ============================================================
// OBJECT_UNLOCK_MODE – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_UNLOCK_MODE_OPTIONS = [
  { value: "PERMANENT", label: "Nur Kauf" },
  { value: "TEMPORARY", label: "Testnutzung" },
  { value: "BOTH", label: "Empfehlung + Kauf" },
];

export const OBJECT_UNLOCK_MODE_LANGUAGE_DE = {
  "PERMANENT": { label: "Nur Kauf" },
  "TEMPORARY": { label: "Testnutzung" },
  "BOTH": { label: "Empfehlung + Kauf" },
};

export const OBJECT_UNLOCK_MODE_LANGUAGE_EN = {
  "PERMANENT": { label: "Purchase only" },
  "TEMPORARY": { label: "Trial use" },
  "BOTH": { label: "Recommendation + purchase" },
};

export const OBJECT_UNLOCK_MODE_LANGUAGES = {
  de: OBJECT_UNLOCK_MODE_LANGUAGE_DE,
  en: OBJECT_UNLOCK_MODE_LANGUAGE_EN,
};


// ============================================================
// OBJECT_CURRENCY – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_CURRENCY_OPTIONS = [
  { value: "EUR", label: "Euro" },
  { value: "PTS", label: "Punkte" },
];

export const OBJECT_CURRENCY_LANGUAGE_DE = {
  "EUR": { label: "Euro" },
  "PTS": { label: "Punkte" },
};

export const OBJECT_CURRENCY_LANGUAGE_EN = {
  "EUR": { label: "Euro" },
  "PTS": { label: "Points" },
};

export const OBJECT_CURRENCY_LANGUAGES = {
  de: OBJECT_CURRENCY_LANGUAGE_DE,
  en: OBJECT_CURRENCY_LANGUAGE_EN,
};


// ============================================================
// OBJECT_TRIAL_USES – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_TRIAL_USES_OPTIONS = [
  { value: "3", label: "3 Trials" },
  { value: "5", label: "5 Trials" },
  { value: "10", label: "10 Trials" },
];

export const OBJECT_TRIAL_USES_LANGUAGE_DE = {
  "3": { label: "3 Trials" },
  "5": { label: "5 Trials" },
  "10": { label: "10 Trials" },
};

export const OBJECT_TRIAL_USES_LANGUAGE_EN = {
  "3": { label: "3 Trials" },
  "5": { label: "5 Trials" },
  "10": { label: "10 Trials" },
};

export const OBJECT_TRIAL_USES_LANGUAGES = {
  de: OBJECT_TRIAL_USES_LANGUAGE_DE,
  en: OBJECT_TRIAL_USES_LANGUAGE_EN,
};


// ============================================================
// OBJECT_ACTION – technische Werte und Sprachlabels
// ============================================================
export const OBJECT_ACTION_OPTIONS = [
  { value: "insert", label: "insert" },
  { value: "update", label: "update" },
];

export const OBJECT_ACTION_LANGUAGE_DE = {
  "insert": { label: "Einfügen" },
  "update": { label: "Aktualisieren" },
};

export const OBJECT_ACTION_LANGUAGE_EN = {
  "insert": { label: "insert" },
  "update": { label: "update" },
};

export const OBJECT_ACTION_LANGUAGES = {
  de: OBJECT_ACTION_LANGUAGE_DE,
  en: OBJECT_ACTION_LANGUAGE_EN,
};




// OfferCreator

// ============================================================
// TIME METRIC
// ============================================================

// Technische Definition – sprachunabhängig.
export const TIME_METRIC_OPTIONS = [
  { value: 'day', label: 'Tag' },
  { value: 'week', label: 'Woche' },
  { value: 'month', label: 'Monat' },
  { value: 'quarter', label: 'Quartal' },
  { value: 'year', label: 'Jahr' }
];

export const TIME_METRIC_LANGUAGE_DE = {
  day: { label: 'Tag' },
  week: { label: 'Woche' },
  month: { label: 'Monat' },
  quarter: { label: 'Quartal' },
  year: { label: 'Jahr' }
};

export const TIME_METRIC_LANGUAGE_EN = {
  day: { label: 'Day' },
  week: { label: 'Week' },
  month: { label: 'Month' },
  quarter: { label: 'Quarter' },
  year: { label: 'Year' }
};

export const TIME_METRIC_LANGUAGES = {
  de: TIME_METRIC_LANGUAGE_DE,
  en: TIME_METRIC_LANGUAGE_EN
};


// ============================================================
// INVESTMENT MODE
// ============================================================

// Technische Definition – sprachunabhängig.
export const INVESTMENT_MODE_OPTIONS = [
  { value: 'one_time', label: 'Einmalig' },
  { value: 'recurring', label: 'Über den aktiven Zeitraum verteilt' },
  { value: 'mixed', label: 'Einmalig + über den aktiven Zeitraum verteilt' }
];

export const INVESTMENT_MODE_LANGUAGE_DE = {
  one_time: { label: 'Einmalig' },
  recurring: { label: 'Über den aktiven Zeitraum verteilt' },
  mixed: { label: 'Einmalig + über den aktiven Zeitraum verteilt' }
};

export const INVESTMENT_MODE_LANGUAGE_EN = {
  one_time: { label: 'One-time' },
  recurring: { label: 'Spread over the active period' },
  mixed: { label: 'One-time + spread over the active period' }
};

export const INVESTMENT_MODE_LANGUAGES = {
  de: INVESTMENT_MODE_LANGUAGE_DE,
  en: INVESTMENT_MODE_LANGUAGE_EN
};


// ============================================================
// CONTINUATION
// ============================================================

// Technische Definition – sprachunabhängig.
export const CONTINUATION_OPTIONS = [
  { value: 'no', label: 'Nein' },
  { value: 'yes', label: 'Ja' }
];

export const CONTINUATION_LANGUAGE_DE = {
  no: { label: 'Nein' },
  yes: { label: 'Ja' }
};

export const CONTINUATION_LANGUAGE_EN = {
  no: { label: 'No' },
  yes: { label: 'Yes' }
};

export const CONTINUATION_LANGUAGES = {
  de: CONTINUATION_LANGUAGE_DE,
  en: CONTINUATION_LANGUAGE_EN
};




// Deepness

export const DEEPNESS_SCALE_OPTIONS = [
    { value: 'clarity', label: 'Klarheit' },
    { value: 'emotion', label: 'Emotion' },
    { value: 'scene', label: 'Szene' },
    { value: 'meaning', label: 'Bedeutung' },
    { value: 'magnitude', label: 'Ausmaß' },
    { value: 'consequence', label: 'Impact' }
] as const;

export const DEEPNESS_SCALE_LANGUAGE_DE = {
    clarity: { label: 'Klarheit' },
    emotion: { label: 'Emotion' },
    scene: { label: 'Szene' },
    meaning: { label: 'Bedeutung' },
    magnitude: { label: 'Ausmaß' },
    consequence: { label: 'Impact' }
};

export const DEEPNESS_SCALE_LANGUAGE_EN = {
    clarity: { label: 'Clarity' },
    emotion: { label: 'Emotion' },
    scene: { label: 'Scene' },
    meaning: { label: 'Meaning' },
    magnitude: { label: 'Magnitude' },
    consequence: { label: 'Impact' }
};

export const DEEPNESS_SCALE_LANGUAGES = {
    de: DEEPNESS_SCALE_LANGUAGE_DE,
    en: DEEPNESS_SCALE_LANGUAGE_EN
};





// Persona Psychology Graph

export const PSYCHOLOGY_RADAR_OPTIONS = [
    { value: 'trust_closer', label: 'VERTRAUEN' },
    { value: 'offer', label: 'ANGEBOT' },
    { value: 'clarity', label: 'KLARHEIT' },
    { value: 'pain', label: 'SCHMERZ' },
    { value: 'owner', label: 'OWNER' },
    { value: 'self', label: 'SELF' },
    { value: 'skeptic', label: 'OFFENHEIT' }
] as const;

export const PSYCHOLOGY_RADAR_LANGUAGE_DE = {
    trust_closer: { label: 'VERTRAUEN' },
    offer: { label: 'ANGEBOT' },
    clarity: { label: 'KLARHEIT' },
    pain: { label: 'SCHMERZ' },
    owner: { label: 'OWNER' },
    self: { label: 'SELF' },
    skeptic: { label: 'OFFENHEIT' }
};

export const PSYCHOLOGY_RADAR_LANGUAGE_EN = {
    trust_closer: { label: 'TRUST' },
    offer: { label: 'OFFER' },
    clarity: { label: 'CLARITY' },
    pain: { label: 'PAIN' },
    owner: { label: 'OWNER' },
    self: { label: 'SELF' },
    skeptic: { label: 'OPENNESS' }
};

export const PSYCHOLOGY_RADAR_LANGUAGES = {
    de: PSYCHOLOGY_RADAR_LANGUAGE_DE,
    en: PSYCHOLOGY_RADAR_LANGUAGE_EN
};


// Overlay

export const PERSONA_MODE_OPTIONS = [
    { value: 'Prospect', label: 'Prospect' },
    { value: 'Closer', label: 'Closer' },
];

export const PERSONA_MODE_LANGUAGE_DE = {
    Prospect: { label: 'Prospect' },
    Closer: { label: 'Closer' },
};

export const PERSONA_MODE_LANGUAGE_EN = {
    Prospect: { label: 'Prospect' },
    Closer: { label: 'Closer' },
};

export const PERSONA_MODE_LANGUAGES = {
    de: PERSONA_MODE_LANGUAGE_DE,
    en: PERSONA_MODE_LANGUAGE_EN,
};


// --- ROLEPLAY MODE ---

export const ROLEPLAY_MODE_OPTIONS = [
    { value: 'ki', label: '1.1 Rollenspiel mit der KI' },
    { value: 'team', label: '1.2 Rollenspiel mit Team' },
];

export const ROLEPLAY_MODE_LANGUAGE_DE = {
    ki: { label: '1.1 Rollenspiel mit der KI' },
    team: { label: '1.2 Rollenspiel mit Team' },
};

export const ROLEPLAY_MODE_LANGUAGE_EN = {
    ki: { label: '1.1 Roleplay with AI' },
    team: { label: '1.2 Team Roleplay' },
};

export const ROLEPLAY_MODE_LANGUAGES = {
    de: ROLEPLAY_MODE_LANGUAGE_DE,
    en: ROLEPLAY_MODE_LANGUAGE_EN,
};


// --- KI TRAINING GOALS ---

export const KI_TRAINING_GOAL_OPTIONS = [
    {
        value: '1.2.1 KI als Verkäufer kennenlernen',
        label: '1.2.1 KI als Verkäufer kennenlernen',
    },
    {
        value: '1.2.2 Verkaufsskills optimieren',
        label: '1.2.2 Verkaufsskills optimieren',
    },
    {
        value: '1.2.3 Auswending lernen',
        label: '1.2.3 Auswending lernen',
    },
];

export const KI_TRAINING_GOAL_LANGUAGE_DE = {
    '1.2.1 KI als Verkäufer kennenlernen': {
        label: '1.2.1 KI als Verkäufer kennenlernen',
    },
    '1.2.2 Verkaufsskills optimieren': {
        label: '1.2.2 Verkaufsskills optimieren',
    },
    '1.2.3 Auswending lernen': {
        label: '1.2.3 Auswending lernen',
    },
};

export const KI_TRAINING_GOAL_LANGUAGE_EN = {
    '1.2.1 KI als Verkäufer kennenlernen': {
        label: '1.2.1 Get to know AI as a salesperson',
    },
    '1.2.2 Verkaufsskills optimieren': {
        label: '1.2.2 Improve sales skills',
    },
    '1.2.3 Auswending lernen': {
        label: '1.2.3 Memorization practice',
    },
};

export const KI_TRAINING_GOAL_LANGUAGES = {
    de: KI_TRAINING_GOAL_LANGUAGE_DE,
    en: KI_TRAINING_GOAL_LANGUAGE_EN,
};


// --- TEAM TRAINING GOALS ---

export const TEAM_TRAINING_GOAL_OPTIONS = [
    {
        value: '1.2.1 Verkaufsskills optimieren',
        label: '1.2.1 Verkaufsskills optimieren',
    },
    {
        value: '1.2.2 Auswending lernen',
        label: '1.2.2 Auswending lernen',
    },
];

export const TEAM_TRAINING_GOAL_LANGUAGE_DE = {
    '1.2.1 Verkaufsskills optimieren': {
        label: '1.2.1 Verkaufsskills optimieren',
    },
    '1.2.2 Auswending lernen': {
        label: '1.2.2 Auswending lernen',
    },
};

export const TEAM_TRAINING_GOAL_LANGUAGE_EN = {
    '1.2.1 Verkaufsskills optimieren': {
        label: '1.2.1 Improve sales skills',
    },
    '1.2.2 Auswending lernen': {
        label: '1.2.2 Memorization practice',
    },
};

export const TEAM_TRAINING_GOAL_LANGUAGES = {
    de: TEAM_TRAINING_GOAL_LANGUAGE_DE,
    en: TEAM_TRAINING_GOAL_LANGUAGE_EN,
};


// --- TRAINING INTERNALISATION ---

export const TRAINING_INTERNALISATION_OPTIONS = [
    { value: 'fast', label: 'Zunehmende Verinnerlichung' },
    { value: 'random', label: 'Zufällige Lücken' },
];

export const TRAINING_INTERNALISATION_LANGUAGE_DE = {
    fast: { label: 'Zunehmende Verinnerlichung' },
    random: { label: 'Zufällige Lücken' },
};

export const TRAINING_INTERNALISATION_LANGUAGE_EN = {
    fast: { label: 'Progressive Internalization' },
    random: { label: 'Random Gaps' },
};

export const TRAINING_INTERNALISATION_LANGUAGES = {
    de: TRAINING_INTERNALISATION_LANGUAGE_DE,
    en: TRAINING_INTERNALISATION_LANGUAGE_EN,
};


// --- TRAINING CONTENT ---

export const TRAINING_CONTENT_OPTIONS = [
    { value: 'Skripte', label: 'Skripte' },
    { value: 'Techniken', label: 'Techniken' },
];

export const TRAINING_CONTENT_LANGUAGE_DE = {
    Skripte: { label: 'Skripte' },
    Techniken: { label: 'Techniken' },
};

export const TRAINING_CONTENT_LANGUAGE_EN = {
    Skripte: { label: 'Scripts' },
    Techniken: { label: 'Techniques' },
};

export const TRAINING_CONTENT_LANGUAGES = {
    de: TRAINING_CONTENT_LANGUAGE_DE,
    en: TRAINING_CONTENT_LANGUAGE_EN,
};


// --- GAP MODE ---

export const GAP_MODE_OPTIONS = [
    { value: 'life', label: '3.1 Life Coaching' },
    { value: 'ki', label: '3.2 Verkaufsskills mit KI optimieren' },
    { value: 'team', label: '3.3 Rollenspiel mit Team' },
];

export const GAP_MODE_LANGUAGE_DE = {
    life: { label: '3.1 Life Coaching' },
    ki: { label: '3.2 Verkaufsskills mit KI optimieren' },
    team: { label: '3.3 Rollenspiel mit Team' },
};

export const GAP_MODE_LANGUAGE_EN = {
    life: { label: '3.1 Life Coaching' },
    ki: { label: '3.2 Improve Sales Skills with AI' },
    team: { label: '3.3 Team Roleplay' },
};

export const GAP_MODE_LANGUAGES = {
    de: GAP_MODE_LANGUAGE_DE,
    en: GAP_MODE_LANGUAGE_EN,
};



export const DEEPNESS_REASON_OPTIONS = [
    { value: 'clarity_reason', label: 'Klarheit' },
    { value: 'emotion_reason', label: 'Emotion' },
    { value: 'scene_reason', label: 'Szene' },
    { value: 'meaning_reason', label: 'Bedeutung' },
    { value: 'magnitude_reason', label: 'Ausmaß' },
    { value: 'consequence_reason', label: 'Konsequenz' }
] as const;

export const DEEPNESS_REASON_LANGUAGE_DE = {
    clarity_reason: { label: 'Klarheit' },
    emotion_reason: { label: 'Emotion' },
    scene_reason: { label: 'Szene' },
    meaning_reason: { label: 'Bedeutung' },
    magnitude_reason: { label: 'Ausmaß' },
    consequence_reason: { label: 'Konsequenz' }
};

export const DEEPNESS_REASON_LANGUAGE_EN = {
    clarity_reason: { label: 'Clarity' },
    emotion_reason: { label: 'Emotion' },
    scene_reason: { label: 'Scene' },
    meaning_reason: { label: 'Meaning' },
    magnitude_reason: { label: 'Magnitude' },
    consequence_reason: { label: 'Consequence' }
};

export const DEEPNESS_REASON_LANGUAGES = {
    de: DEEPNESS_REASON_LANGUAGE_DE,
    en: DEEPNESS_REASON_LANGUAGE_EN
};



export const SIDEBAR_ACTION_OPTIONS = [
    { value: 'AUDIO', label: '🔇 TON EINSCHALTEN' },
    { value: 'HISTORY', label: '📜 VERLAUF' },
    { value: 'SAVE', label: '💾 SPEICHERN' },
    { value: 'RELOAD', label: '🔄 NEULADEN' },
    { value: 'EXIT', label: '❌ BEENDEN' },
    { value: 'LOGOUT', label: '🚪 LOGOUT' }
];


export const SIDEBAR_ACTION_LANGUAGE_DE = {
    AUDIO: { label: '🔇 TON EINSCHALTEN' },
    AUDIO_ACTIVE: { label: '🔊 TON AUSSCHALTEN' },
    HISTORY: { label: '📜 VERLAUF' },
    SAVE: { label: '💾 SPEICHERN' },
    RELOAD: { label: '🔄 NEULADEN' },
    EXIT: { label: '❌ BEENDEN' },
    LOGOUT: { label: '🚪 LOGOUT' }
};


export const SIDEBAR_ACTION_LANGUAGE_EN = {
    AUDIO: { label: '🔇 TURN SOUND ON' },
    AUDIO_ACTIVE: { label: '🔊 TURN SOUND OFF' },
    HISTORY: { label: '📜 HISTORY' },
    SAVE: { label: '💾 SAVE' },
    RELOAD: { label: '🔄 RELOAD' },
    EXIT: { label: '❌ EXIT' },
    LOGOUT: { label: '🚪 LOGOUT' }
};


export const SIDEBAR_ACTION_LANGUAGES = {
    de: SIDEBAR_ACTION_LANGUAGE_DE,
    en: SIDEBAR_ACTION_LANGUAGE_EN
};




// ============================================================
// SALES STAGE / MODUL AREA
// ============================================================

// Technische Definition – unabhängig von jeder Sprache.
export const SALES_STAGE_OPTIONS = [
  { value: 'prospecting', label: 'Prospecting' },
  { value: 'qualification', label: 'Qualification' },
  { value: 'closing', label: 'Closing' },
  { value: 'after_sales', label: 'After-Sales' },
];

export const SALES_STAGE_DE = {
  prospecting: { label: 'Prospecting' },
  qualification: { label: 'Qualifizierung' },
  closing: { label: 'Closing' },
  after_sales: { label: 'After Sales' },
} as const;

export const SALES_STAGE_EN = {
  prospecting: { label: 'Prospecting' },
  qualification: { label: 'Qualification' },
  closing: { label: 'Closing' },
  after_sales: { label: 'After-Sales' },
} as const;

export const SALES_STAGE_LANGUAGES = {
  de: SALES_STAGE_DE,
  en: SALES_STAGE_EN,
} as const;


// ============================================================
// CONTACT TOUCHPOINT
// ============================================================

// Technische Definition – unabhängig von jeder Sprache.
export const CONTACT_TOUCHPOINT_OPTIONS = [
  { value: 'initial', label: 'Initial' },
  { value: 'follow_up', label: 'Follow-Up' },
];

export const CONTACT_TOUCHPOINT_DE = {
  initial: { label: 'Erstkontakt' },
  follow_up: { label: 'Folgekontakt' },
} as const;

export const CONTACT_TOUCHPOINT_EN = {
  initial: { label: 'Initial' },
  follow_up: { label: 'Follow-Up' },
} as const;

export const CONTACT_TOUCHPOINT_LANGUAGES = {
  de: CONTACT_TOUCHPOINT_DE,
  en: CONTACT_TOUCHPOINT_EN,
} as const;


// ============================================================
// CUSTOMER RELATIONSHIP
// ============================================================

// Technische Definition – unabhängig von jeder Sprache.
export const CUSTOMER_RELATIONSHIP_OPTIONS = [
  { value: 'new_customer', label: 'Neukunde' },
  { value: 'existing_customer', label: 'Bestandskunde' },
  { value: 'both', label: 'Beides' },
];

export const CUSTOMER_RELATIONSHIP_DE = {
  new_customer: { label: 'Neukunde' },
  existing_customer: { label: 'Bestandskunde' },
  both: { label: 'Beides' },
} as const;

export const CUSTOMER_RELATIONSHIP_EN = {
  new_customer: { label: 'New Customer' },
  existing_customer: { label: 'Existing Customer' },
  both: { label: 'Both' },
} as const;

export const CUSTOMER_RELATIONSHIP_LANGUAGES = {
  de: CUSTOMER_RELATIONSHIP_DE,
  en: CUSTOMER_RELATIONSHIP_EN,
} as const;


// ============================================================
// SCENARIO
// ============================================================

// Technische Definition – unabhängig von jeder Sprache.
export const SCENARIO_OPTIONS = [
  { value: 'b2b', label: 'B2B' },
  { value: 'b2c', label: 'B2C' },
  { value: 'business', label: 'Business' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'health', label: 'Health' },
];

export const SCENARIO_DE = {
  b2b: { label: 'B2B' },
  b2c: { label: 'B2C' },
  business: { label: 'Business' },
  improvement: { label: 'Optimierung' },
  health: { label: 'Gesundheit' },
} as const;

export const SCENARIO_EN = {
  b2b: { label: 'B2B' },
  b2c: { label: 'B2C' },
  business: { label: 'Business' },
  improvement: { label: 'Improvement' },
  health: { label: 'Health' },
} as const;

export const SCENARIO_LANGUAGES = {
  de: SCENARIO_DE,
  en: SCENARIO_EN,
} as const;



// Company Size Segment

export const COMPANY_SIZE_SEGMENT_OPTIONS = [
  { value: 'solo', label: '1 Person' },
  { value: '2-10 MA', label: '2 bis 10 Personen' },
  { value: '11-30 MA', label: '11 bis 30 Personen' },
  { value: '31-50 MA', label: '31 bis 50 Personen' },
  { value: '51-100 MA', label: '51 bis 100 Personen' },
  { value: '101-300 MA', label: '101 bis 300 Personen' },
  { value: '301-1000 MA', label: '301 bis 1000 Personen' },
  { value: '1001+ MA', label: 'über 1000 Personen' },
];


export const COMPANY_SIZE_SEGMENT_DE = {
  'solo': {
    label: '1 Person',
  },
  '2-10 MA': {
    label: '2 bis 10 Personen',
  },
  '11-30 MA': {
    label: '11 bis 30 Personen',
  },
  '31-50 MA': {
    label: '31 bis 50 Personen',
  },
  '51-100 MA': {
    label: '51 bis 100 Personen',
  },
  '101-300 MA': {
    label: '101 bis 300 Personen',
  },
  '301-1000 MA': {
    label: '301 bis 1000 Personen',
  },
  '1001+ MA': {
    label: 'über 1000 Personen',
  },
};

export const COMPANY_SIZE_SEGMENT_EN = {
  'solo': {
    label: '1 Person',
  },
  '2-10 MA': {
    label: '2 to 10 People',
  },
  '11-30 MA': {
    label: '11 to 30 People',
  },
  '31-50 MA': {
    label: '31 to 50 People',
  },
  '51-100 MA': {
    label: '51 to 100 People',
  },
  '101-300 MA': {
    label: '101 to 300 People',
  },
  '301-1000 MA': {
    label: '301 to 1000 People',
  },
  '1001+ MA': {
    label: 'More than 1000 People',
  },
};


export const COMPANY_SIZE_SEGMENT_LANGUAGES: Record<
  string,
  Record<string, { label: string }>
> = {
  de: COMPANY_SIZE_SEGMENT_DE,
  en: COMPANY_SIZE_SEGMENT_EN,
};


// Tonalitätsregeln

export const TONALITY_LABELS: Record<
    string,
    Record<NonNullable<TonalityResult>, string>
> = {
    de: {
        sicher: 'Sicher',
        besorgt: 'Besorgt',
        verwirrt: 'Verwirrt',
        spielerisch: 'Spielerisch',
        neugierig: 'Neugierig',
        skeptisch: 'Skeptisch'
    },
    en: {
        sicher: 'Confident',
        besorgt: 'Concerned',
        verwirrt: 'Confused',
        spielerisch: 'Playful',
        neugierig: 'Curious',
        skeptisch: 'Skeptical'
    },
    fr: {
        sicher: 'Assuré',
        besorgt: 'Inquiet',
        verwirrt: 'Perplexe',
        spielerisch: 'Joueur',
        neugierig: 'Curieux',
        skeptisch: 'Sceptique'
    },
    es: {
        sicher: 'Seguro',
        besorgt: 'Preocupado',
        verwirrt: 'Confundido',
        spielerisch: 'Juguetón',
        neugierig: 'Curioso',
        skeptisch: 'Escéptico'
    },
    it: {
        sicher: 'Sicuro',
        besorgt: 'Preoccupato',
        verwirrt: 'Confuso',
        spielerisch: 'Giocoso',
        neugierig: 'Curioso',
        skeptisch: 'Scettico'
    },
    pt: {
        sicher: 'Confiante',
        besorgt: 'Preocupado',
        verwirrt: 'Confuso',
        spielerisch: 'Brincalhão',
        neugierig: 'Curioso',
        skeptisch: 'Cético'
    },
    nl: {
        sicher: 'Zelfverzekerd',
        besorgt: 'Bezorgd',
        verwirrt: 'Verward',
        spielerisch: 'Speels',
        neugierig: 'Nieuwsgierig',
        skeptisch: 'Sceptisch'
    },
    pl: {
        sicher: 'Pewny siebie',
        besorgt: 'Zaniepokojony',
        verwirrt: 'Zdezorientowany',
        spielerisch: 'Żartobliwy',
        neugierig: 'Ciekawy',
        skeptisch: 'Sceptyczny'
    },
    tr: {
        sicher: 'Kendinden emin',
        besorgt: 'Endişeli',
        verwirrt: 'Kafası karışmış',
        spielerisch: 'Şakacı',
        neugierig: 'Meraklı',
        skeptisch: 'Şüpheci'
    },
    ar: {
        sicher: 'واثق',
        besorgt: 'قلق',
        verwirrt: 'مرتبك',
        spielerisch: 'مرح',
        neugierig: 'فضولي',
        skeptisch: 'متشكك'
    },
    uk: {
        sicher: 'Впевнений',
        besorgt: 'Стривожений',
        verwirrt: 'Розгублений',
        spielerisch: 'Грайливий',
        neugierig: 'Допитливий',
        skeptisch: 'Скептичний'
    },
    zh: {
        sicher: '自信',
        besorgt: '担忧',
        verwirrt: '困惑',
        spielerisch: '俏皮',
        neugierig: '好奇',
        skeptisch: '持怀疑态度'
    },
    ja: {
        sicher: '自信がある',
        besorgt: '心配している',
        verwirrt: '混乱している',
        spielerisch: '遊び心がある',
        neugierig: '好奇心が強い',
        skeptisch: '懐疑的'
    }
};

// Muttersprachen

export const PRIMARY_LANGUAGE_OPTIONS = [
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'es', label: 'Español' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Brazil Português' },
    { value: 'nl', label: 'Nederlands' },
    { value: 'pl', label: 'Polski' },
    { value: 'tr', label: 'Türkçe' },
    { value: 'ar', label: 'العربية' },
    { value: 'uk', label: 'Українська' },
    { value: 'zh', label: '中文' },
    { value: 'ja', label: '日本語' }
];



export const TTS_LANGUAGES = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
    { value: 'fr', label: 'Français' },
    { value: 'es', label: 'Español' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' },
] as const;

export const INTERFACE_LANGUAGES = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
] as const;


export const OBJECT_LANGUAGE_OPTIONS = [
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'English' }
];



// Future Check - Selbstkorrektur Mechanismus

// ============================================================
// FINAL MATRIX TYPES
// ============================================================

export type IrresistableGapModuleTypeMatrix = Record<
  IrresistableGapViolationType,
  Record<
    IrresistableGapOrigin,
    Record<
      IrresistableGapEndpointModuleType,
      IrresistableGapMatrixResult
    >
  >
>;


export type IrresistableGapContextTypeMatrix = Record<
  IrresistableGapViolationType,
  Record<
    IrresistableGapOrigin,
    Record<
      IrresistableGapEndpointContextType,
      IrresistableGapMatrixResult
    >
  >
>;


// ============================================================
// DRAFT MATRIX TYPES
//
// null means:
// The case has not been classified yet.
//
// Once every case is filled, the constants can be typed with:
//
// IrresistableGapModuleTypeMatrix
// IrresistableGapContextTypeMatrix
// ============================================================

export type IrresistableGapModuleTypeMatrixDraft = Record<
  IrresistableGapViolationType,
  Record<
    IrresistableGapOrigin,
    Record<
      IrresistableGapEndpointModuleType,
      IrresistableGapMatrixResult | null
    >
  >
>;


export type IrresistableGapContextTypeMatrixDraft = Record<
  IrresistableGapViolationType,
  Record<
    IrresistableGapOrigin,
    Record<
      IrresistableGapEndpointContextType,
      IrresistableGapMatrixResult | null
    >
  >
>;


// ============================================================
// MATRIX 1
//
// false_X
//   -> origin
//     -> endpoint_module_type
//
// This matrix answers:
// WHAT TYPE OF MODULE contains the endpoint?
// ============================================================

export const IRRESISTABLE_GAP_MODULE_TYPE_MATRIX:
  IrresistableGapModuleTypeMatrixDraft = {

  // ============================================================
  // FALSE NEGATIVE
  // ============================================================

  false_negative: {

    // ----------------------------------------------------------
    // ORIGIN: PHASE MODULE
    // ----------------------------------------------------------

    phase_module: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: OBJECTION TECHNIQUE
    // ----------------------------------------------------------

    objection_technique: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: SPECIAL TECHNIQUE
    // ----------------------------------------------------------

    special_technique: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: NORMAL TECHNIQUE
    // ----------------------------------------------------------

    normal_technique: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: SUBPROCESS
    // ----------------------------------------------------------

    subprocess: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    }
  },


  // ============================================================
  // FALSE POSITIVE
  // ============================================================

  false_positive: {

    // ----------------------------------------------------------
    // ORIGIN: PHASE MODULE
    // ----------------------------------------------------------

    phase_module: {
      phase_module: 'currentFocuGoalFinished',
      objection_technique: 'currentAboveTheStackModuleFinished',
      special_technique: 'currentAboveTheStackModuleFinished',
      normal_technique: 'currentAboveTheStackModuleFinished',
      subprocess: 'currentAboveTheStackModuleFinished'
    },


    // ----------------------------------------------------------
    // ORIGIN: OBJECTION TECHNIQUE
    // ----------------------------------------------------------

    objection_technique: {
      phase_module: 'currentFocuGoalFinished',
      objection_technique: 'nothing',
      special_technique: 'currentFocuGoalFinished',
      normal_technique: 'currentFocuGoalFinished',
      subprocess: 'currentAboveTheStackModuleFinished'
    },


    // ----------------------------------------------------------
    // ORIGIN: SPECIAL TECHNIQUE
    // ----------------------------------------------------------

    special_technique: {
      phase_module: 'currentFocuGoalFinished',
      objection_technique: 'currentAboveTheStackModuleFinished',
      special_technique: 'nothing',
      normal_technique: 'currentFocuGoalFinished',
      subprocess: 'currentFocuGoalFinished'
    },


    // ----------------------------------------------------------
    // ORIGIN: NORMAL TECHNIQUE
    // ----------------------------------------------------------

    normal_technique: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: SUBPROCESS
    // ----------------------------------------------------------

    subprocess: {
      phase_module: 'nothing',
      objection_technique: 'nothing',
      special_technique: 'nothing',
      normal_technique: 'nothing',
      subprocess: 'nothing'
    }
  }
};


// ============================================================
// MATRIX 2
//
// false_X
//   -> origin
//     -> endpoint_context_type
//
// This matrix answers:
// WHERE is the endpoint structurally relative to the Race?
// ============================================================

export const IRRESISTABLE_GAP_CONTEXT_TYPE_MATRIX:
  IrresistableGapContextTypeMatrixDraft = {

  // ============================================================
  // FALSE NEGATIVE
  // ============================================================

  false_negative: {

    // ----------------------------------------------------------
    // ORIGIN: PHASE MODULE
    // ----------------------------------------------------------

    phase_module: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: OBJECTION TECHNIQUE
    // ----------------------------------------------------------

    objection_technique: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: SPECIAL TECHNIQUE
    // ----------------------------------------------------------

    special_technique: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: NORMAL TECHNIQUE
    // ----------------------------------------------------------

    normal_technique: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: SUBPROCESS
    // ----------------------------------------------------------

    subprocess: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    }
  },


  // ============================================================
  // FALSE POSITIVE
  // ============================================================

  false_positive: {

    // ----------------------------------------------------------
    // ORIGIN: PHASE MODULE
    // ----------------------------------------------------------

    phase_module: {
      same: 'currentFocuGoalFinished',
      above_the_stack: 'currentAboveTheStackModuleFinished',
      next_module: 'currentFocuGoalFinished',
      under_the_stack: 'currentFocuGoalFinished'
    },


    // ----------------------------------------------------------
    // ORIGIN: OBJECTION TECHNIQUE
    // ----------------------------------------------------------

    objection_technique: {
      same: 'nothing',
      above_the_stack: 'currentAboveTheStackModuleFinished',
      next_module: 'nothing',
      under_the_stack: 'currentFocuGoalFinished'
    },


    // ----------------------------------------------------------
    // ORIGIN: SPECIAL TECHNIQUE
    // ----------------------------------------------------------

    special_technique: {
      same: 'nothing',
      above_the_stack: 'currentAboveTheStackModuleFinished',
      next_module: 'nothing',
      under_the_stack: 'currentFocuGoalFinished'
    },


    // ----------------------------------------------------------
    // ORIGIN: NORMAL TECHNIQUE
    // ----------------------------------------------------------

    normal_technique: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    },


    // ----------------------------------------------------------
    // ORIGIN: SUBPROCESS
    // ----------------------------------------------------------

    subprocess: {
      same: 'nothing',
      above_the_stack: 'nothing',
      next_module: 'nothing',
      under_the_stack: 'nothing'
    }
  }
};



// Taxonimie spezifische Thresholds und Wächter Vorgaben

export const PERSONA_SENSOR_THRESHOLDS = {
  calculated_trust_in_closer: { min: 61 },
  calculated_trust_in_self: { min: 61, max: 75 },
  calculated_trust_in_offer: { min: 61 },
  calculated_clarity_now: { min: 61 },
  calculated_clarity_consequence: { min: 61, max: 80 },
  calculated_skepticism: { max: 35 },
  calculated_commitment_ownership: { min: 61 }
};


export const DIRECTLY_REPEATABLE_TECHNIQUE_CATEGORIES =
  new Set<string>([
    'Präsentations Technik'
  ]);


export const PERSONA_SENSOR_EFFECT_MAP: Record<string, string> = {
  calculated_trust_in_closer: 'TC',
  calculated_trust_in_self: 'TS',
  calculated_trust_in_offer: 'TO',
  calculated_clarity_now: 'CN',
  calculated_clarity_consequence: 'CC',
  calculated_skepticism: 'SK',
  calculated_commitment_ownership: 'CO',
};

export const DEEPNESS_SENSOR_EFFECT_MAP: Record<string, string> = {
  clarity: 'CL',
  emotion: 'EM',
  scene: 'SC',
  meaning: 'ME',
  magnitude: 'MA',
  consequence: 'CO',
};


export const PROMPT_BLOCKING_RULES = {
  Einwand: {
    blockingScope: 'register',
    maxBlockedTurns: 7
  },

  Vorwandbehandlung: {
    blockingScope: 'register',
    maxBlockedTurns: 5
  },

  Sprachmuster: {
    blockingScope: 'register',
    maxBlockedTurns: 10
  },

  Persona: {
    blockingScope: 'goal',
    maxBlockedTurns: 10
  },

  Deepness: {
    blockingScope: 'goal',
    maxBlockedTurns: 5
  },

  Einwandvorwegnahme: {
    blockingScope: 'register',
    maxBlockedTurns: 6
  },

  Gegenfrage: {
    blockingScope: 'register',
    maxBlockedTurns: 7
  },

  default: {
    blockingScope: 'goal',
    maxBlockedTurns: 5
  }
} as const;


export const PREHANDLING_CAUSE_MAP: Record<string, string> = {
  ursache_mentale_blockade: 'Mentale-Blockade-Vorwegnahme',

  ursache_vertrauensdefizit: 'Vertrauens-Vorwegnahme',
  ursache_vertrauensdefizit_angebot: 'Angebotsvertrauen-Vorwegnahme',
  ursache_vertrauensdefizit_anbieter: 'Anbietervertrauen-Vorwegnahme',
  ursache_vertrauensdefizit_in_sich_selbst: 'Selbstvertrauen-Vorwegnahme',
  ursache_vertrauensdefizit_in_methode: 'Methodenvertrauen-Vorwegnahme',

  ursache_beduerfnisluecke: 'Bedürfnislücken-Vorwegnahme',
  ursache_beduerfnisluecke_wachstum: 'Wachstumsbedürfnis-Vorwegnahme',
  ursache_beduerfnisluecke_verbundenheit: 'Verbundenheitsbedürfnis-Vorwegnahme',
  ursache_beduerfnisluecke_anerkennung: 'Anerkennungsbedürfnis-Vorwegnahme',
  ursache_beduerfnisluecke_abwechslung: 'Abwechslungsbedürfnis-Vorwegnahme',
  ursache_beduerfnisluecke_sicherheit: 'Sicherheitsbedürfnis-Vorwegnahme',

  ursache_externalisierung: 'Externalisierungs-Vorwegnahme',

  ursache_logistische_blockade: 'Logistik-Vorwegnahme',
  ursache_logistische_blockade_finanzen: 'Budget-Vorwegnahme',
  ursache_logistische_blockade_personal: 'Personal-Vorwegnahme',
  ursache_logistische_blockade_zeit: 'Zeit-Vorwegnahme',
  ursache_logistische_blockade_ressourcen_ausser_zeit_geld_personal: 'Ressourcen-Vorwegnahme',

  ursache_erfahrungsbedingte_skepsis: 'Skepsis-Vorwegnahme',
};



export const NATURAL_INTERVENTION_SEQUENCE: InterventionPocket[] = [
  'Präsentation',
  'Vorabschluss',
  'ROI',
  'Kaufsignal',
  'Vorwand',
  'Einwand',
  'Gegenfrage',
  'Vorwegnahme',
  'Sprachmuster',
  'Deepness',
  'Persona',
];


export const NATURAL_OBJECTION_SEQUENCE: ObjectionPocket[] = [
  'Ablehnung',
  'Wert',
  'Budget/Preis',
  'Entscheidung',
  'Externalisierung', 
  'Zeit',
  'Logistik',
  'Angst'
];



export const CORE_3_OBJECTIONS = [
  'Wert',
  'Budget/Preis',
  'Entscheidung'
]as const;


export const PERMISSION_STACKING_MATRIX: Record<
  InterventionPocket,
  Record<InterventionPocket, boolean>
> = {
  Einwand: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: true,
    Deepness: true,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal:true,
    Vorabschluss: false,
  },

  Vorwand: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: true,
    Vorwegnahme: false,
    Sprachmuster: true,
    Deepness: true,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal: true,
    Vorabschluss: false,
  },

  Vorwegnahme: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: true,
    Deepness: true,
    Persona: true,
    Gegenfrage: true,
    Kaufsignal: true,
    Vorabschluss: false,
  },

  Sprachmuster: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: false,
    Deepness: true,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal: true,
    Vorabschluss: false,
  },

  Deepness: {
    Präsentation: false,
    ROI: false,
    Vorwand: true,
    Einwand: true,
    Vorwegnahme: true,
    Sprachmuster: true,
    Deepness: false,
    Persona: false,
    Gegenfrage: false,
    Kaufsignal: false,
    Vorabschluss: false,
  },

  Persona: {
    Präsentation: false,
    ROI: false,
    Vorwand: true,
    Einwand: true,
    Vorwegnahme: true,
    Sprachmuster: true,
    Deepness: true,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal: true,
    Vorabschluss: false,
  },
  Gegenfrage: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: false,
    Deepness: true,
    Persona: false,
    Gegenfrage: false,
    Kaufsignal: true,
    Vorabschluss: false,
  },

  Kaufsignal: {
    Präsentation: false,
    ROI: false,
    Vorwand: true,
    Einwand: true,
    Vorwegnahme: true,
    Sprachmuster: true,
    Deepness: true,
    Persona: true,
    Gegenfrage: true,
    Kaufsignal: false,
    Vorabschluss: false,
  },

  ROI: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: false,
    Deepness: true,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal: false,
    Vorabschluss: false,
  },

  Präsentation: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: true,
    Deepness: true,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal: true,
    Vorabschluss: false,
  },


  Vorabschluss: {
    Präsentation: false,
    ROI: false,
    Vorwand: false,
    Einwand: false,
    Vorwegnahme: false,
    Sprachmuster: false,
    Deepness: false,
    Persona: false,
    Gegenfrage: true,
    Kaufsignal: true,
    Vorabschluss: false,
  },

};


export const PENDING_ALLOWED: Record<InterventionPocket, boolean> = {
  Vorabschluss: true,
  Präsentation: true,
  ROI: true,
  Gegenfrage: true,
  Kaufsignal: true,
  Vorwand: true,
  Einwand: true,
  Vorwegnahme: true,
  Sprachmuster: true,
  Deepness: false,
  Persona: false,
};


export const PENDING_EXPIRY_RULES = {
  Vorwand: {
    type: 'time',
    maxAgeMs: 10 * 60 * 1000,
  },

  Einwand: {
    type: 'none',
  },

  Vorwegnahme: {
    type: 'relatedObjectionCompleted',
  },

  Sprachmuster: {
    type: 'time',
    maxAgeMs: 10 * 60 * 1000,
  },
} as const;


export const PREHANDLING_OBJECTION_CATEGORY_MAP = {
  "Einwand (Wert)": [
    "Bedürfnislücken-Vorwegnahme",
    "Wachstumsbedürfnis-Vorwegnahme",
    "Verbundenheitsbedürfnis-Vorwegnahme",
    "Anerkennungsbedürfnis-Vorwegnahme",
    "Abwechslungsbedürfnis-Vorwegnahme",
    "Wettbewerber-Vorwegnahme"
  ],

  "Einwand (Budget/Preis)": [
    "Preis-Vorwegnahme",
    "Budget-Vorwegnahme"
  ],

  "Einwand (Logistik)": [
    "Logistik-Vorwegnahme",
    "Personal-Vorwegnahme",
    "Ressourcen-Vorwegnahme"
  ],

  "Einwand (Entscheidung)": [
    "Partner-Vorwegnahme"
  ],

  "Einwand (Zeit)": [
    "Zeit-Vorwegnahme"
  ],

  "Einwand (Angst)": [
    "Mentale-Blockade-Vorwegnahme",
    "Angebotsvertrauen-Vorwegnahme",
    "Anbietervertrauen-Vorwegnahme",
    "Selbstvertrauen-Vorwegnahme",
    "Methodenvertrauen-Vorwegnahme",
    "Sicherheitsbedürfnis-Vorwegnahme",
    "Externalisierungs-Vorwegnahme",
    "Vertrauens-Vorwegnahme",
    "Risiko-Vorwegnahme",
    "Skepsis-Vorwegnahme",
    "Commitment-Vorwegnahme",
    "Komplexitäts-Vorwegnahme"
  ]
};



export const TECHNIQUE_TAXONOMY = [
{
  label: 'Vorabschlusstechnik',
  aliases: [
    'vorabschlusstechnik',
    'vorabschluss technik',
    'vorabschluss',
    'vorabschlussfrage',
    'vorabschluss frage',
    'vorabschlussprüfung',
    'vorabschluss prüfung',
    'pre close',
    'pre-close',
    'pre close technique',
    'pre-close technique',
    'trial close',
    'trial-close',
  ],
  applications: [
    {
      label: 'Vorabschluss',
      aliases: [
        'vorabschluss',
        'vorabschluss durchführen',
        'vorabschluss einleiten',
        'vorabschluss prüfen',
        'vorabschlussfrage stellen',
        'gesamtfit prüfen',
        'gesamt fit prüfen',
        'lösung als gesamtheit prüfen',
        'zustimmung zur gesamtlösung prüfen',
        'passung der lösung prüfen',
        'lösung zusammenfassen und prüfen',
        'pre close',
        'pre-close',
        'trial close',
        'trial-close',
      ],
    },
  ],
},
{
  label: 'Präsentations Technik',
  aliases: [
    'präsentations technik',
    'präsentationstechnik',
    'präsentation',
    'praesentation',
    'angebot präsentieren',
    'angebot vorstellen',
    'lösung präsentieren',
    'lösung vorstellen',
    'leistungspräsentation',
    'angebotspräsentation',
    'sales präsentation',
    'sales presentation',
    'presentation technique',
    'offer presentation',
    'solution presentation',
  ],
  applications: [
    {
      label: 'Präsentations Technik',
      aliases: [
        'präsentations technik',
        'präsentationstechnik',
        'präsentation',
        'angebot präsentieren',
        'lösung präsentieren',
        'angebotspräsentation',
        'leistungspräsentation',
        'sales präsentation',
        'presentation technique',
        'offer presentation',
      ],
    },
  ],
},
{
  label: 'ROI Rechnung',
  aliases: [
    'roi rechnung',
    'roi berechnung',
    'roi kalkulation',
    'roi abgleich',
    'roi annahmen abgleichen',
    'roi werte abgleichen',
    'roi berechnungsbasis abgleichen',
    'return on investment',
    'return on investment rechnung',
    'return on investment berechnung',
    'wirtschaftlichkeitsrechnung',
    'wirtschaftlichkeitsberechnung',
    'rentabilitätsrechnung',
    'rentabilitätsberechnung',
    'investment rechnen',
    'return berechnen',
    'wirtschaftlichen return berechnen',
    'wirtschaftliche annahmen abgleichen',
    'wirtschaftliche werte abgleichen',
    'berechnungsbasis abgleichen',
    'roi calculation',
    'return calculation',
  ],
  applications: [
    {
      label: 'ROI Rechnung',
      aliases: [
        'roi rechnung',
        'roi berechnung',
        'roi kalkulation',
        'return on investment',
        'wirtschaftlichkeitsrechnung',
        'rentabilitätsrechnung',
        'investment rechnen',
        'return berechnen',
        'roi calculation',
        'return calculation',
      ],
    },
    {
      label: 'ROI Abgleich',
      aliases: [
        'roi abgleich',
        'roi annahmen abgleichen',
        'roi werte abgleichen',
        'roi berechnungsbasis abgleichen',
        'wirtschaftliche annahmen abgleichen',
        'wirtschaftliche werte abgleichen',
        'berechnungsbasis abgleichen',
      ],
    },
  ],
},
{
  label: 'Kaufsignal',
  aliases: [
    'kaufsignal',
    'kaufsignale',
    'kauf signal',
    'kauf signale',
    'buying signal',
    'buying signals',
    'buy signal',
    'buy signals',
    'purchase signal',
    'purchase signals',
    'kaufinteresse',
    'konkretes kaufinteresse',
    'kaufbereitschaft',
    'konkrete kaufbereitschaft',
    'kaufabsicht',
    'konkrete kaufabsicht',
    'kaufintention',
    'konkrete kaufintention',
    'abschlussinteresse',
    'abschlussbereitschaft',
    'entscheidungsinteresse',
    'entscheidungsbereitschaft',
    'interesse an zusammenarbeit',
    'interesse an der zusammenarbeit',
    'interesse an möglicher zusammenarbeit',
    'interesse an einer zusammenarbeit',
    'zusammenarbeitsinteresse',
    'gedankliche vorwegnahme',
    'gedankliche vorwegnahme der zusammenarbeit',
    'gedankliche vorwegnahme einer zusammenarbeit',
    'gedankliche vorwegnahme möglicher zusammenarbeit',
    'mentale vorwegnahme der zusammenarbeit',
    'mentale vorwegnahme einer zusammenarbeit',
    'prospect denkt zusammenarbeit voraus',
    'prospect denkt mögliche zusammenarbeit voraus',
    'prospect stellt sich zusammenarbeit vor',
    'prospect stellt sich mögliche zusammenarbeit vor',
    'kunde denkt zusammenarbeit voraus',
    'kunde denkt mögliche zusammenarbeit voraus',
    'kunde stellt sich zusammenarbeit vor',
    'kunde stellt sich mögliche zusammenarbeit vor',
    'signal für mögliche zusammenarbeit',
    'signal für zusammenarbeit',
    'signal zur zusammenarbeit',
    'signal für kaufbereitschaft',
    'signal für kaufinteresse',
    'signal für kaufabsicht',
    'signal für entscheidungsbereitschaft',
    'buying intent signal',
    'purchase intent signal',
    'buying intent',
    'purchase intent',
    'buying intention',
    'purchase intention',
  ],
  applications: [
    {
      label: 'Kaufsignal als Frage',
      aliases: [
        'kaufsignal als frage',
        'kaufsignal frage',
        'kaufsignal-frage',
        'kauffrage',
        'kaufinteressierte frage',
        'frage mit kaufsignal',
        'frage als kaufsignal',
        'frage mit kaufinteresse',
        'frage mit kaufabsicht',
        'frage mit kaufintention',
        'frage mit kaufbereitschaft',
        'frage zur zusammenarbeit',
        'frage zur möglichen zusammenarbeit',
        'frage über die zusammenarbeit',
        'frage über mögliche zusammenarbeit',
        'frage zum ablauf der zusammenarbeit',
        'frage zur ausgestaltung der zusammenarbeit',
        'frage zur gestaltung der zusammenarbeit',
        'frage zur umsetzung der zusammenarbeit',
        'frage zur nutzung der lösung',
        'frage zur konkreten nutzung',
        'frage zur konkreten umsetzung',
        'frage zum konkreten ablauf',
        'prospect fragt zur zusammenarbeit',
        'prospect fragt nach zusammenarbeit',
        'prospect fragt nach möglicher zusammenarbeit',
        'prospect fragt wie zusammenarbeit aussieht',
        'prospect fragt wie zusammenarbeit abläuft',
        'prospect fragt wie umsetzung aussieht',
        'prospect fragt wie umsetzung abläuft',
        'prospect fragt wie betreuung aussieht',
        'prospect fragt wie betreuung abläuft',
        'kunde fragt zur zusammenarbeit',
        'kunde fragt nach zusammenarbeit',
        'kunde fragt nach möglicher zusammenarbeit',
        'kunde fragt wie zusammenarbeit aussieht',
        'kunde fragt wie zusammenarbeit abläuft',
        'kunde fragt wie umsetzung aussieht',
        'kunde fragt wie umsetzung abläuft',
        'kunde fragt wie betreuung aussieht',
        'kunde fragt wie betreuung abläuft',
        'gedankliche vorwegnahme als frage',
        'vorwegnahme der zusammenarbeit als frage',
        'vorwegnahme möglicher zusammenarbeit als frage',
        'buying signal question',
        'buying question',
        'purchase signal question',
        'buying intent question',
        'purchase intent question',
      ],
    },
    {
      label: 'Kaufsignal als Aussage',
      aliases: [
        'kaufsignal als aussage',
        'kaufsignal aussage',
        'kaufsignal-aussage',
        'kaufaussage',
        'aussage mit kaufsignal',
        'aussage als kaufsignal',
        'aussage mit kaufinteresse',
        'aussage mit kaufabsicht',
        'aussage mit kaufintention',
        'aussage mit kaufbereitschaft',
        'aussage zur zusammenarbeit',
        'aussage zur möglichen zusammenarbeit',
        'aussage über die zusammenarbeit',
        'aussage über mögliche zusammenarbeit',
        'anforderung an zusammenarbeit',
        'anforderung an die zusammenarbeit',
        'anforderung an mögliche zusammenarbeit',
        'wunsch an zusammenarbeit',
        'wunsch an die zusammenarbeit',
        'wunsch an mögliche zusammenarbeit',
        'erwartung an zusammenarbeit',
        'erwartung an die zusammenarbeit',
        'erwartung an mögliche zusammenarbeit',
        'präferenz für zusammenarbeit',
        'präferenz zur zusammenarbeit',
        'präferenz bei zusammenarbeit',
        'vorstellung zur zusammenarbeit',
        'vorstellung von zusammenarbeit',
        'vorstellung von möglicher zusammenarbeit',
        'prospect beschreibt zusammenarbeit',
        'prospect beschreibt gewünschte zusammenarbeit',
        'prospect beschreibt mögliche zusammenarbeit',
        'prospect äußert wunsch zur zusammenarbeit',
        'prospect äußert erwartung zur zusammenarbeit',
        'prospect äußert anforderung zur zusammenarbeit',
        'prospect äußert präferenz zur zusammenarbeit',
        'prospect sagt was ihm bei zusammenarbeit wichtig ist',
        'kunde beschreibt zusammenarbeit',
        'kunde beschreibt gewünschte zusammenarbeit',
        'kunde beschreibt mögliche zusammenarbeit',
        'kunde äußert wunsch zur zusammenarbeit',
        'kunde äußert erwartung zur zusammenarbeit',
        'kunde äußert anforderung zur zusammenarbeit',
        'kunde äußert präferenz zur zusammenarbeit',
        'kunde sagt was ihm bei zusammenarbeit wichtig ist',
        'gedankliche vorwegnahme als aussage',
        'vorwegnahme der zusammenarbeit als aussage',
        'vorwegnahme möglicher zusammenarbeit als aussage',
        'buying signal statement',
        'buying statement',
        'purchase signal statement',
        'buying intent statement',
        'purchase intent statement',
      ],
    },
  ],
},
{
  label: 'Gegenfrage',
  aliases: [
    'gegenfrage',
    'gegenfragen',
    'rückfrage',
    'rückfragen',
    'counter question',
    'prospect question'
  ],
  applications: [
    {
      label: 'Reaktion auf Inhaltsfrage',
      aliases: [
        'inhaltlich antworten',
        'inhaltlich reagieren',
        'frage beantworten',
        'rückfrage beantworten',
        'gegenfrage beantworten',
        'sachlich antworten',
        'direkt antworten',
        'auf die frage eingehen',
        'auf die rückfrage eingehen',
        'inhaltlich aufgreifen',
        'sachlich darauf eingehen',
        'antwort zum thema geben',
        'inhaltliche antwort geben',
        'fachlich antworten'
      ]
    },
    {
      label: 'Reaktion auf Kontrollgewinn',
      aliases: [
        'kontrolle zurückholen',
        'gesprächskontrolle zurückholen',
        'führung zurückgewinnen',
        'gesprächsführung übernehmen',
        'führung wieder übernehmen',
        'gespräch wieder führen',
        'gespräch zurücklenken',
        'gespräch aktiv lenken',
        'kontrolle übernehmen',
        'richtung wieder vorgeben',
        'führung übernehmen',
        'gespräch steuern',
        'initiative zurückholen',
        'steuerung zurückgewinnen'
      ]
    },
    {
      label: 'Reaktion auf Angriff / Machtdemonstration',
      aliases: [
        'angreifend reagieren',
        'mit angriff reagieren',
        'dominant reagieren',
        'macht demonstrieren',
        'dominanz zeigen',
        'konfrontativ reagieren',
        'provokant reagieren',
        'druck aufbauen',
        'gegenangriff starten',
        'zurückschießen',
        'hart kontern',
        'dominant kontern',
        'herausfordernd reagieren',
        'machtposition zeigen'
      ]
    },
    {
      label: 'Reaktion auf unpassende Frage',
      aliases: [
        'unpassend reagieren',
        'unangemessen reagieren',
        'am thema vorbeireagieren',
        'nicht auf die frage eingehen',
        'unpassend antworten',
        'irrelevant antworten',
        'sachfremd reagieren',
        'kontextfremd antworten',
        'falsch auf die frage reagieren',
        'unangebrachte antwort geben',
        'am kontext vorbeigehen',
        'nicht passend antworten',
        'themenfremd reagieren',
        'unangemessen antworten'
      ]
    },
    {
      label: 'Pattern Interrupt',
      aliases: [
        'pattern interrupt einsetzen',
        'muster unterbrechen',
        'gesprächsmuster brechen',
        'reaktionsmuster unterbrechen',
        'unerwartet reagieren',
        'überraschend reagieren',
        'mit musterbruch reagieren',
        'routine durchbrechen',
        'gesprächsfluss bewusst brechen',
        'unerwarteten impuls setzen',
        'kommunikationsmuster brechen',
        'mit überraschung reagieren',
        'musterwechsel einsetzen',
        'pattern break einsetzen'
      ]
    },
    {
      label: 'Reaktion auf Ablenkungsmanöver',
      aliases: [
        'ablenken',
        'mit ablenkung reagieren',
        'vom thema wegführen',
        'thema verschieben',
        'gespräch umlenken',
        'auf ein anderes thema wechseln',
        'frage umgehen',
        'rückfrage umgehen',
        'gegenfrage umgehen',
        'ausweichend reagieren',
        'thema bewusst wechseln',
        'auf anderes thema lenken',
        'antwort vermeiden',
        'mit ausweichmanöver reagieren',
        'gespräch bewusst weglenken'
      ]
    }
  ]
},
  {
    label: 'Handlungsimpuls (P CN+, P CC+, P CO+)',
    aliases: [
      'handlungsimpuls',
      'aktion auslösen',
      'kunde soll handeln',
      'ins handeln bringen',
      'entscheidung aktivieren',
      'nächsten schritt auslösen',
      'abschluss vorbereiten',
      'verbindlichkeit herstellen',
      'umsetzung anstoßen',
      'entscheidungsbereitschaft erhöhen',
      'handlungsbereitschaft prüfen',
      'vom interesse zur aktion',
      'kunde kommt in bewegung',
      'verkaufsgespräch aktivieren',
      'entscheidung nicht vertagen',
    ],
    applications: [
      {
        label: 'Dringlichkeit (P CC+, P CO+)',
        aliases: [
          'dringlichkeit erzeugen',
          'dringlichkeit aufbauen',
          'warum jetzt handeln',
          'kunde wartet zu lange',
          'nicht länger aufschieben',
          'zeitdruck im verkauf',
          'zeitliche konsequenz zeigen',
          'kosten des wartens',
          'dringenden grund herausarbeiten',
          'jetzt handeln begründen',
          'status quo wird teurer',
          'aufschieben hinterfragen',
          'warteschaden aufzeigen',
          'handlungsdruck ohne push',
          'zeitkritische entscheidung',
        ],
      },
      {
        label: 'Commitment (P CO+)',
        aliases: [
          'commitment holen',
          'commitment abfragen',
          'verbindlichkeit schaffen',
          'zusage einholen',
          'echtes ja prüfen',
          'kunde soll sich festlegen',
          'verbindlichen nächsten schritt sichern',
          'abschlussbereitschaft testen',
          'interesse oder commitment',
          'kunde meint es ernst',
          'entscheidung verbindlich machen',
          'halbes ja erkennen',
          'unverbindlichkeit klären',
          'commitment frage',
          'verbindlichkeit ohne druck',
        ],
      },
      {
        label: 'Nächster Schritt (P CO+)',
        aliases: [
          'nächsten schritt vereinbaren',
          'next step vereinbaren',
          'follow up setzen',
          'folgeaktion festlegen',
          'nächsten termin machen',
          'anschlusstermin sichern',
          'weiteres vorgehen klären',
          'call sauber abschließen',
          'konkrete nächste aktion',
          'verkaufsgespräch nicht offen lassen',
          'prozess nach dem call',
          'nächste phase einleiten',
          'entscheidungspfad festlegen',
          'gesprächsergebnis sichern',
          'weiteres Vorgehen vereinbaren',
        ],
      },
      {
        label: 'Eigenverantwortung (P CO+)',
        aliases: [
          'eigenverantwortung aktivieren',
          'verantwortung beim kunden lassen',
          'kunde übernimmt verantwortung',
          'verantwortungsfrage stellen',
          'entscheidung liegt beim kunden',
          'kunde erkennt eigene rolle',
          'selbstverantwortung im verkauf',
          'verantwortung spiegeln',
          'nicht handeln verantwortung',
          'kunde ist zuständig',
          'ownership frage',
          'verantwortung ohne schuldzuweisung',
          'eigenanteil erkennen lassen',
          'entscheidung selbst treffen lassen',
          'verantwortungsübernahme fördern',
        ],
      },
      {
        label: 'Entscheidungsimpuls (P CN+, P CO+)',
        aliases: [
          'entscheidung anstoßen',
          'entscheidung auslösen',
          'kunde soll entscheiden',
          'entscheidungsfrage stellen',
          'entscheidungsklarheit schaffen',
          'unentschlossenheit lösen',
          'zögern beenden',
          'entscheidung nicht vertagen',
          'abschlussimpuls setzen',
          'entscheidungsbereitschaft erhöhen',
          'entscheidungspunkt markieren',
          'kunde trifft klare wahl',
          'entscheidungsblockade lösen',
          'entscheiden ohne druck',
          'abschluss vorbereiten',
        ],
      },
      {
        label: 'Dynamik (P CO+)',
        aliases: [
          'gesprächsdynamik halten',
          'momentum halten',
          'verkaufsfluss halten',
          'call in bewegung halten',
          'energie im gespräch erhöhen',
          'kunde verliert interesse',
          'dynamik nach einwand zurückholen',
          'gespräch wieder aktivieren',
          'momentum nach bedarf nutzen',
          'verkaufsgespräch stockt',
          'interesse warm halten',
          'entscheidung kühlt ab',
          'call vorwärts führen',
          'verkaufsprozess beschleunigen',
          'kunde bleibt passiv',
        ],
      },
      {
        label: 'Konsequenz (P CC+)',
        aliases: [
          'konsequenzfrage stellen',
          'folgen nicht handeln',
          'was passiert wenn nichts passiert',
          'kosten des nicht handelns',
          'auswirkungen klären',
          'problemfolgen vertiefen',
          'status quo konsequenz',
          'nichtentscheidung hinterfragen',
          'risiko des wartens',
          'folgeschaden aufzeigen',
          'langfristige konsequenz',
          'wartepreis sichtbar machen',
          'kunde spricht folgen aus',
          'konsequenz des zögerns',
          'implikationen fragen',
        ],
      },
      {
        label: 'Zeitfenster (P CC+, P CO+)',
        aliases: [
          'zeitfenster nutzen',
          'opportunity window',
          'passender zeitpunkt',
          'jetzt ist der richtige zeitpunkt',
          'zeitlich begrenzte chance',
          'terminfenster sichern',
          'timing für entscheidung',
          'gelegenheit nicht verpassen',
          'aktuelles fenster klären',
          'saisonales zeitfenster',
          'zeitpunkt für umsetzung',
          'warum jetzt starten',
          'momentum zeitlich nutzen',
          'fenster läuft ab',
          'günstiger entscheidungszeitpunkt',
        ],
      },
      {
        label: 'Aktivierung (P CO+)',
        aliases: [
          'kunde aktivieren',
          'umsetzung starten',
          'erste aktion auslösen',
          'ins tun bringen',
          'vom verstehen ins handeln',
          'startimpuls setzen',
          'handlungsbereitschaft aktivieren',
          'aktion nach erkenntnis',
          'umsetzungsimpuls',
          'kunde beginnt',
          'startentscheidung sichern',
          'erste handlung vereinbaren',
          'aktivierungsfrage',
          'nicht mehr abwarten',
          'handlung nach dem call',
        ],
      },
    ],
  },
{
  label: 'Einwand (Wert)',
  aliases: [
    'werteinwand',
    'wert nicht erkannt',
    'mehrwert nicht erkannt',
    'nutzen nicht erkannt',
    'gegenwert nicht erkannt',
  ],
  applications: [
    {
      label: 'Wettbewerber',
      aliases: [
        'wettbewerber einwand',
        'konkurrenzangebot',
        'anderer anbieter',
        'kunde vergleicht',
        'muss noch vergleichen',
        'vergleichsangebot',
        'anbieter vergleichen',
        'alternative lösung',
        'marktvergleich',
        'kunde prüft mitbewerber',
        'differenzierung gegen konkurrenz',
        'andere optionen offen',
        'wettbewerbsvergleich',
      ],
    },
    {
      label: 'Bedarf',
      aliases: [
        'kein bedarf',
        'brauchen wir nicht',
        'nicht relevant',
        'kein problem',
        'alles läuft',
        'kein interesse',
        'bedarfseinwand',
        'kunde sieht keinen nutzen',
        'kunde erkennt problem nicht',
        'problemwahrnehmung fehlt',
        'keine notwendigkeit',
        'nicht passend für uns',
        'haben wir schon gelöst',
        'nutzen nicht klar',
        'bedarf herausarbeiten',
        'preis rechtfertigen',
        'wert statt preis',
        'kosten nutzen klären',
        'kunde versteht es nicht',
        'angebot ist unübersichtlich',
        'einfacher erklären',
        'verständnisproblem',
        'nicht greifbar',
        'kunde braucht vereinfachung',
      ],
    },
    {
      label: 'Priorität',
      aliases: [
        'keine priorität',
        'nicht wichtig genug',
        'steht nicht auf agenda',
        'andere projekte wichtiger',
        'später priorisieren',
        'nachrangiges thema',
        'wird zurückgestellt',
        'prioritätseinwand',
        'gerade nicht oben',
        'nicht dringend',
        'kunde verschiebt priorität',
        'projekt hat keine priorität',
        'interne prioritäten',
        'thema liegt auf eis',
        'priorität klären',
        'budgetpriorität',
      ],
    },
  ],
},

{
  label: 'Einwand (Budget/Preis)',
  aliases: [
    'finanzieller einwand',
    'finanzielle belastung',
    'preis oder budget als blocker',
    'finanzielle machbarkeit',
  ],
  applications: [
    {
      label: 'Preis',
      aliases: [
        'preiseinwand',
        'zu teuer',
        'preis ist zu hoch',
        'kosten einwand',
        'kunde fragt nach rabatt',
        'honorar zu hoch',
        'preisbedenken',
        'kunde zweifelt am preis',
        'investition zu hoch',
        'preiswiderstand',
        'andere sind günstiger',
        'konkurrenz ist billiger',
      ],
    },
    {
      label: 'Budget',
      aliases: [
        'kein budget',
        'budgeteinwand',
        'budget aufgebraucht',
        'finanzierung offen',
        'nicht eingeplant',
        'budgetjahr vorbei',
        'kostenstelle blockiert',
        'kein geld da',
        'kauf scheitert an budget',
        'investitionsstopp',
        'kann ich mir nicht leisten',
      ],
    },
  ],
},

{
  label: 'Einwand (Logistik)',
  aliases: [
    'logistischer einwand',
    'ressourcen fehlen',
    'mitarbeiter fehlen',
    'personal fehlt',
    'kompetenzen fehlen',
    'skills fehlen',
    'geräte fehlen',
    'maschinen fehlen',
    'infrastruktur fehlt',
    'operative ressourcen fehlen',
    'umsetzungsressourcen fehlen',
  ],
  applications: [
    {
      label: 'Personelle Kapazität fehlt',
      aliases: [
        'zu wenig personal',
        'keine mitarbeiter verfügbar',
        'uns fehlen mitarbeiter',
        'personelle kapazitäten fehlen',
        'team ist unterbesetzt',
        'keine freien mitarbeiter',
        'personaldecke ist zu dünn',
        'zusätzliche manpower fehlt',
        'wir haben niemanden dafür',
        'keine personellen ressourcen',
        'teamkapazität reicht nicht',
        'mitarbeiter sind vollständig ausgelastet',
      ],
    },
    {
      label: 'Fachkompetenz und Know-how fehlen',
      aliases: [
        'know-how fehlt',
        'fachwissen fehlt',
        'kompetenz fehlt',
        'qualifikation fehlt',
        'skills fehlen',
        'erfahrung fehlt',
        'niemand kann das umsetzen',
        'intern fehlt die expertise',
        'spezialwissen ist nicht vorhanden',
        'team ist dafür nicht qualifiziert',
        'fachliche ressourcen fehlen',
        'uns fehlt ein experte',
      ],
    },
    {
      label: 'Technische Ausstattung fehlt',
      aliases: [
        'geräte fehlen',
        'maschinen fehlen',
        'werkzeuge fehlen',
        'technische ausstattung fehlt',
        'hardware fehlt',
        'equipment fehlt',
        'anlagen fehlen',
        'keine passenden geräte vorhanden',
        'maschinenkapazität fehlt',
        'benötigte technik ist nicht vorhanden',
        'notwendige werkzeuge fehlen',
        'ausstattung reicht nicht aus',
      ],
    },
    {
      label: 'IT- und Systeminfrastruktur fehlt',
      aliases: [
        'it-infrastruktur fehlt',
        'systeme fehlen',
        'software fehlt',
        'schnittstellen fehlen',
        'technische integration fehlt',
        'netzwerk reicht nicht aus',
        'serverkapazität fehlt',
        'digitale infrastruktur fehlt',
        'systemlandschaft ist nicht vorbereitet',
        'keine passende plattform vorhanden',
        'bestehende systeme unterstützen das nicht',
        'technische voraussetzungen fehlen',
      ],
    },
    {
      label: 'Räumliche und betriebliche Infrastruktur fehlt',
      aliases: [
        'räumlichkeiten fehlen',
        'platz fehlt',
        'lagerfläche fehlt',
        'produktionsfläche fehlt',
        'bürofläche fehlt',
        'standortkapazität fehlt',
        'keine geeigneten räume vorhanden',
        'betriebliche infrastruktur fehlt',
        'vorhandene fläche reicht nicht aus',
        'lager ist bereits voll',
        'räumliche kapazitäten fehlen',
        'standort ist dafür nicht ausgelegt',
      ],
    },
    {
      label: 'Material und Waren fehlen',
      aliases: [
        'material fehlt',
        'rohstoffe fehlen',
        'waren fehlen',
        'bestand reicht nicht aus',
        'lieferfähigkeit fehlt',
        'nachschub fehlt',
        'teile fehlen',
        'komponenten fehlen',
        'materialverfügbarkeit ist nicht gegeben',
        'lieferkette reicht nicht aus',
        'keine ausreichenden vorräte vorhanden',
        'beschaffung ist nicht gesichert',
      ],
    },
    {
      label: 'Projekt- und Umsetzungsressourcen fehlen',
      aliases: [
        'projektressourcen fehlen',
        'umsetzungskapazität fehlt',
        'keiner kann das projekt übernehmen',
        'projektteam fehlt',
        'ressourcen für die einführung fehlen',
        'keine kapazität für die umsetzung',
        'implementierungsressourcen fehlen',
        'wir können das nicht einführen',
        'projektverantwortlicher fehlt',
        'für die umsetzung fehlt das team',
        'rollout-kapazität fehlt',
        'interne projektkapazität reicht nicht aus',
      ],
    },
    {
      label: 'Operative Kapazität fehlt',
      aliases: [
        'im tagesgeschäft nicht umsetzbar',
        'operative kapazität fehlt',
        'betrieb ist vollständig ausgelastet',
        'prozesse lassen das nicht zu',
        'keine freien ressourcen im betrieb',
        'laufender betrieb hat keine kapazität',
        'organisation kann das nicht aufnehmen',
        'im alltag nicht integrierbar',
        'betriebliche abläufe sind ausgelastet',
        'operativ nicht leistbar',
        'prozesskapazität fehlt',
        'team ist im tagesgeschäft gebunden',
      ],
    },
    {
      label: 'Externe Partner und Dienstleister fehlen',
      aliases: [
        'dienstleister fehlt',
        'externer partner fehlt',
        'lieferant fehlt',
        'subunternehmer fehlt',
        'implementierungspartner fehlt',
        'kein geeigneter anbieter vorhanden',
        'uns fehlt ein fachpartner',
        'keine externe unterstützung verfügbar',
        'partnernetzwerk fehlt',
        'kein technischer dienstleister vorhanden',
        'keine verfügbaren spezialisten',
        'externe ressourcen fehlen',
      ],
    },
    {
      label: 'Transport- und Logistikkapazität fehlt',
      aliases: [
        'fahrzeuge fehlen',
        'transportkapazität fehlt',
        'fuhrpark fehlt',
        'lagerlogistik fehlt',
        'zustellung ist nicht möglich',
        'logistikpartner fehlt',
        'tourenkapazität fehlt',
        'versandkapazität fehlt',
        'transportmittel fehlen',
        'lieferlogistik reicht nicht aus',
        'keine fahrer verfügbar',
        'distribution ist nicht vorhanden',
      ],
    },
  ],
},
{
  label: 'Einwand (Externalisierung)',
  aliases: [
    'externalisierung als einwand',
    'externalisierungseinwand',
    'verantwortung externalisieren',
    'verantwortung nach außen verlagern',
    'entscheidung nach außen verlagern',
    'verantwortung auf andere verlagern',
    'verantwortung bei anderen sehen',
    'äußere instanz als blocker',
    'externer blocker',
    'äußere umstände als blocker',
    'andere müssen entscheiden',
    'andere sind verantwortlich',
    'nicht selbst entscheiden können',
    'nicht in eigener hand',
    'äußeren einfluss als grund nennen',
  ],

  applications: [
    {
      label: 'Partner / Ehepartner',
      aliases: [
        'muss mit partner sprechen',
        'muss ich mit meinem partner besprechen',
        'partner muss zustimmen',
        'partner entscheidet mit',
        'ehepartner muss zustimmen',
        'muss ich zuhause besprechen',
        'muss ich erst zuhause klären',
        'frau muss zustimmen',
        'mann muss zustimmen',
        'entscheidung mit partner',
        'nicht allein entscheiden',
        'kann ich nicht allein entscheiden',
        'familie muss zustimmen',
        'partner veto',
        'private abstimmung notwendig',
        'mitentscheider privat',
        'partner entscheidet darüber',
        'muss ich erst mit meiner frau besprechen',
        'muss ich erst mit meinem mann besprechen',
        'zuhause abstimmen',
      ],
    },

    {
      label: 'Autorität / Entscheidungsbefugnis',
      aliases: [
        'nicht entscheidungsbefugt',
        'ich darf das nicht entscheiden',
        'kann ich nicht entscheiden',
        'entscheider fehlt',
        'muss mit chef sprechen',
        'muss ich mit meinem chef besprechen',
        'chef muss zustimmen',
        'geschäftsführer muss zustimmen',
        'vorgesetzter muss zustimmen',
        'vorgesetzter entscheidet',
        'nicht mein budget',
        'interne freigabe notwendig',
        'brauche interne freigabe',
        'entscheidungsgremium entscheidet',
        'budgetverantwortlicher entscheidet',
        'nicht autorisiert',
        'muss ich intern eskalieren',
        'entscheidungsträger nicht da',
        'freigabeprozess notwendig',
        'budgetfreigabe notwendig',
        'finanzielle freigabe notwendig',
        'muss durch die geschäftsführung',
        'muss durch den vorstand',
        'muss durch den einkauf',
        'muss durch die zentrale',
      ],
    },

    {
      label: 'Markt',
      aliases: [
        'der markt ist schuld',
        'der markt gibt das nicht her',
        'der markt ist schwierig',
        'der markt ist schlecht',
        'der markt funktioniert nicht',
        'liegt am markt',
        'das liegt am markt',
        'unsere branche ist schwierig',
        'in unserer branche funktioniert das nicht',
        'bei uns ist der markt anders',
        'unsere kunden sind anders',
        'unsere kunden kaufen das nicht',
        'die kunden wollen das nicht',
        'die nachfrage ist nicht da',
        'es gibt keine nachfrage',
        'der markt ist gesättigt',
        'der markt ist leergefegt',
        'es gibt keine kandidaten',
        'es gibt keine bewerber',
        'es gibt keine guten bewerber',
        'die leute wollen nicht arbeiten',
        'gute mitarbeiter gibt es nicht',
        'fachkräfte gibt es nicht',
        'der fachkräftemarkt ist leer',
        'die konkurrenz ist zu stark',
        'der wettbewerb ist zu groß',
        'gegen die großen kommen wir nicht an',
        'unsere region ist zu schwierig',
        'an unserem standort funktioniert das nicht',
        'die wirtschaftliche lage ist schuld',
        'die konjunktur ist zu schlecht',
        'die branche gibt das nicht her',
        'der markt lässt kein wachstum zu',
        'äußere marktbedingungen verhindern das',
      ],
    },
  ],
},
{
  label: 'Einwand (Entscheidung)',
  aliases: [
    'entscheidungseinwand',
    'entscheidungsblockade',
    'fehlendes commitment',
    'fehlende entscheidungsbereitschaft',
    'entscheidung wird vermieden',
    'entscheidung wird zurückgehalten',
    'entscheidung wird vertagt',
    'kunde will sich nicht entscheiden',
    'kunde möchte sich nicht festlegen',
    'kunde kann sich nicht festlegen',
    'kunde vermeidet commitment',
    'kunde hält sich entscheidung offen',
    'fehlende verbindlichkeit',
    'keine klare entscheidung',
    'keine verbindliche entscheidung',
    'zögern vor der entscheidung',
    'innere entscheidungsblockade',
    'abschlussentscheidung wird vermieden',
  ],

  applications: [
    {
      label: 'Commitment',
      aliases: [
        'will sich nicht festlegen',
        'möchte sich nicht festlegen',
        'kann sich nicht festlegen',
        'noch nicht bereit zu entscheiden',
        'noch nicht bereit',
        'nicht final entscheiden',
        'möchte noch nicht entscheiden',
        'will noch nicht entscheiden',
        'möchte mich noch nicht festlegen',
        'will mich noch nicht festlegen',
        'entscheidung offen halten',
        'möchte es offen halten',
        'will es offen halten',
        'commitment einwand',
        'fehlendes commitment',
        'kein commitment',
        'kunde zögert',
        'kunde bleibt unverbindlich',
        'unverbindliches interesse',
        'kunde sagt vielleicht',
        'keine klare zusage',
        'keine zusage',
        'halbes ja',
        'fehlende verbindlichkeit',
        'nicht committen',
        'möchte sich noch nicht binden',
        'will sich noch nicht binden',
        'entscheidung noch nicht treffen',
        'noch keine entscheidung treffen',
        'noch keine verbindliche entscheidung',
      ],
    },
  ],
},
{
  label: 'Einwand (Zeit)',
  aliases: [
    'zeit als blocker',
    'zeitlicher blocker',
    'zeitpunkt als blocker',
    'zeit oder timing',
  ],
  applications: [
    {
      label: 'Zeit',
      aliases: [
        'zeiteinwand',
        'keine zeit',
        'dauert zu lange',
        'zeitmangel',
        'keine kapazität',
        'kalender voll',
        'später sprechen',
        'zeitaufwand zu hoch',
        'terminproblem',
        'jetzt keine zeit',
        'zu wenig zeit',
        'zeitliche priorität',
        'kunde verschiebt wegen zeit',
        'keine zeit für umsetzung',
        'zeitinvestment klären',
      ],
    },
    {
      label: 'Timing',
      aliases: [
        'falscher zeitpunkt',
        'nicht jetzt',
        'timing einwand',
        'später starten',
        'nach dem projekt',
        'nächstes quartal',
        'nächstes jahr',
        'zeitpunkt passt nicht',
        'jetzt ungünstig',
        'kunde verschiebt timing',
        'startzeitpunkt klären',
        'erst nach urlaub',
        'noch zu früh',
        'gerade nicht passend',
        'kaufzeitpunkt klären',
        'zu früh für entscheidung',
      ],
    },
  ],
},

{
  label: 'Einwand (Angst)',
  aliases: [
    'angst als blocker',
    'unsicherheit als blocker',
    'angst vor kaufentscheidung',
    'innere kaufblockade',
    'sicherheitsbedürfnis bei kaufentscheidung',
  ],
  applications: [
    {
      label: 'Vertrauen',
      aliases: [
        'vertrauenseinwand',
        'kunde vertraut nicht',
        'misstrauen',
        'skeptisch wegen anbieter',
        'glaubwürdigkeit fehlt',
        'vertrauensproblem',
        'bauchgefühl schlecht',
        'vertrauen aufbauen bei einwand',
        'anbieter wirkt unsicher',
        'vertrauensdefizit',
        'misstrauischer kunde',
      ],
    },
    {
      label: 'Risiko',
      aliases: [
        'risikoeinwand',
        'zu riskant',
        'was wenn es nicht klappt',
        'angst vor fehlentscheidung',
        'kunde braucht absicherung',
        'unsicherheit wegen ergebnis',
        'garantie fehlt',
        'risiko senken',
        'kaufangst',
        'falsche investition',
        'verlustangst',
        'entscheidung wirkt unsicher',
        'risiko im kaufprozess',
        'sicherheitsbedürfnis',
        'risiko nicht handeln',
        'preisangst',
        'kunde braucht sicherheit',
      ],
    },
    {
      label: 'Erfahrung',
      aliases: [
        'schlechte erfahrung',
        'negative erfahrung',
        'schon mal enttäuscht',
        'vorheriger anbieter',
        'hat früher nicht funktioniert',
        'kunde ist verbrannt',
        'alte erfahrung blockiert',
        'skepsis durch erfahrung',
        'schlechte erfahrung mit branche',
        'vertrauen nach enttäuschung',
        'erfahrungseinwand',
        'frühere lösung gescheitert',
        'kunde überträgt vergangenheit',
        'alte Enttäuschung',
        'vorbelasteter kunde',
      ],
    },
    {
      label: 'Komplexität',
      aliases: [
        'zu kompliziert',
        'komplexitätseinwand',
        'lösung wirkt zu komplex',
        'zu viele details',
        'überforderung',
        'technisch zu kompliziert',
        'komplexität reduzieren',
        'zu aufwendig',
        'kompliziertheit blockiert kauf',
      ],
    },
    {
      label: 'Bedenkzeit / Drüber schlafen',
      aliases: [
        'brauche bedenkzeit',
        'nochmal drüber schlafen',
        'noch einmal drüber schlafen',
        'eine nacht drüber schlafen',
        'erst mal drüber schlafen',
      ],
    },
    {
      label: 'Skepsis',
      aliases: [
        'skeptischer kunde',
        'skepsis einwand',
        'kunde ist kritisch',
        'kunde zweifelt',
        'kritische rückfrage',
        'abwartende haltung',
        'kunde glaubt nicht',
        'prüfhaltung',
        'zweifel klären',
        'skepsis abbauen',
        'nicht überzeugt',
        'kritische distanz',
        'kunde will beobachten',
        'skepsis öffnen',
        'kunde glaubt es nicht',
        'zweifel an lösung',
        'zu schön um wahr zu sein',
      ],
    },
  ],
},
{
  label: 'Einwand (Ablehnung)',
  aliases: [
    'ablehnung als blocker',
    'ablehnender einwand',
    'direkte ablehnung',
    'indirekte ablehnung',
    'gesprächsablehnung',
    'ablehnung oder abwimmelreaktion',
    'rückzugsantwort',
    'abwehrreaktion',
  ],
  applications: [
    {
      label: 'Keine Zeit',
      aliases: [
        'dafür habe ich keine zeit',
        'dafür haben wir keine zeit',
        'ich habe dafür keine zeit',
        'wir haben dafür keine zeit',
        'jetzt habe ich keine zeit',
        'aktuell keine zeit',
        'gerade keine zeit',
        'im moment keine zeit',
        'zeitlich nicht möglich',
        'keine zeit für das gespräch',
        'keine zeit mich damit zu beschäftigen',
        'kunde lehnt wegen fehlender zeit ab',
        'kunde blockt mit zeitmangel ab',
        'zeitmangel als ablehnung',
        'keine gesprächszeit',
      ],
    },
    {
      label: 'Selbst machen',
      aliases: [
        'wir machen das selbst',
        'das machen wir selbst',
        'ich mache das selbst',
        'das lösen wir selbst',
        'wir lösen das selbst',
        'das machen wir intern',
        'wir lösen das intern',
        'das übernehmen wir intern',
        'dafür haben wir eigene mitarbeiter',
        'dafür haben wir eine interne abteilung',
        'das übernimmt unser team',
        'wir benötigen dafür keine unterstützung',
        'wir brauchen dafür niemanden',
        'interne lösung als ablehnung',
        'kunde verweist auf eigenleistung',
      ],
    },
    {
      label: 'Bestehender Partner',
      aliases: [
        'wir haben schon einen partner',
        'wir haben bereits einen partner',
        'wir arbeiten schon mit einem partner',
        'wir haben dafür einen partner',
        'wir haben bereits einen dienstleister',
        'wir haben schon einen dienstleister',
        'das macht bereits ein dienstleister',
        'das macht unsere agentur',
        'wir arbeiten schon mit einer agentur',
        'wir sind bereits versorgt',
        'wir haben bereits eine lösung',
        'unser partner kümmert sich darum',
        'bestehender anbieter als ablehnung',
        'bestehende zusammenarbeit als ablehnung',
        'kunde verweist auf bestehenden partner',
      ],
    },
    {
      label: 'Schlechte Erfahrungen',
      aliases: [
        'damit haben wir schlechte erfahrungen',
        'wir haben damit schlechte erfahrungen gemacht',
        'damit hatte ich schlechte erfahrungen',
        'das haben wir schon ausprobiert',
        'das hatten wir schon einmal',
        'das hat bei uns nicht funktioniert',
        'damit sind wir auf die nase gefallen',
        'wir wurden damit enttäuscht',
        'das hat uns nichts gebracht',
        'das war rausgeworfenes geld',
        'das hat sich nicht gelohnt',
        'wir wollen das nicht noch einmal versuchen',
        'negative erfahrung als ablehnung',
        'kunde lehnt wegen schlechter erfahrung ab',
        'kunde verweist auf negative erfahrungen',
      ],
    },
    {
      label: 'Kein Interesse',
      aliases: [
        'kein interesse',
        'ich habe kein interesse',
        'wir haben kein interesse',
        'nicht interessiert',
        'ich bin nicht interessiert',
        'wir sind nicht interessiert',
        'interessiert uns nicht',
        'für uns nicht interessant',
        'das interessiert mich nicht',
        'das interessiert uns nicht',
        'kommt für uns nicht infrage',
        'möchten wir nicht',
        'wollen wir nicht',
        'kunde zeigt kein interesse',
        'kunde lehnt wegen fehlendem interesse ab',
      ],
    },
    {
      label: 'Kein Bedarf',
      aliases: [
        'kein bedarf',
        'ich habe keinen bedarf',
        'wir haben keinen bedarf',
        'aktuell kein bedarf',
        'dafür besteht kein bedarf',
        'brauchen wir nicht',
        'das brauchen wir nicht',
        'benötigen wir nicht',
        'wir benötigen das nicht',
        'für uns nicht notwendig',
        'das ist für uns nicht relevant',
        'wir kommen auch ohne das aus',
        'es gibt dafür keine notwendigkeit',
        'kunde verneint den bedarf',
        'kunde lehnt wegen fehlendem bedarf ab',
      ],
    },
    {
      label: 'Unterlagen zusenden',
      aliases: [
        'schicken sie uns unterlagen',
        'schicken sie mir unterlagen',
        'senden sie uns unterlagen',
        'senden sie mir unterlagen',
        'schicken sie uns informationen',
        'schicken sie mir informationen',
        'senden sie uns informationen',
        'senden sie mir informationen',
        'schicken sie mir eine mail',
        'schicken sie uns eine mail',
        'senden sie etwas per mail',
        'lassen sie uns etwas zukommen',
        'schicken sie erst einmal etwas zu',
        'kunde verlangt unterlagen statt gespräch',
        'unterlagenanforderung als ablehnung',
      ],
    },
    {
      label: 'Gesprächsabbruch',
      aliases: [
        'ich lege jetzt auf',
        'ich werde jetzt auflegen',
        'ich beende jetzt das gespräch',
        'wir beenden das gespräch',
        'beenden wir das gespräch',
        'das gespräch ist beendet',
        'rufen sie nicht mehr an',
        'rufen sie mich nicht wieder an',
        'lassen sie mich in ruhe',
        'ich möchte nicht weiterreden',
        'ich will das gespräch nicht fortsetzen',
        'ich möchte das gespräch beenden',
        'kunde beendet das gespräch',
        'kunde kündigt auflegen an',
        'kunde lehnt die gesprächsfortsetzung ab',
      ],
    },
  ],
},
{
  label: 'Vorwandbehandlung',
  aliases: [
    'vorwand',
    'verkaufsvorwand',
    'kundenvorwand',
    'vorgeschobener grund',
    'unechter einwand',
    'scheinargument',
    'ausrede im verkauf',
    'oberflächliche begründung',
    'ausweichreaktion',
    'schutzbehauptung',
    'unverbindliche antwort',
    'verdeckter widerstand',
    'standardausrede',
    'rückzugsantwort',
    'nicht der wahre grund',
  ],
  applications: [
    {
      label: 'Vorwand',
      aliases: [
        'vorwand',
        'verkaufsvorwand',
        'kundenvorwand',
        'vorgeschobener grund',
        'unechter einwand',
        'scheinargument',
        'ausrede im verkauf',
        'oberflächliche begründung',
        'ausweichreaktion',
        'schutzbehauptung',
        'unverbindliche antwort',
        'verdeckter widerstand',
        'standardausrede',
        'rückzugsantwort',
        'nicht der wahre grund',
      ],
    },
  ],
},
  {
    label: 'Einwandvorwegnahme',
    aliases: [
      'einwandvorwegnahme',
      'einwand vorwegnehmen',
      'vorabbehandlung',
      'einwand vorwegnehmen',
      'einwand vorab behandeln',
      'prehandling',
      'preframe',
      'einwandprävention',
      'kritische punkte früh klären',
      'späteren einwand vermeiden',
      'kundenbedenken vorab öffnen',
      'vor dem angebot klären',
      'widerstand vermeiden',
      'erwartung vorab setzen',
      'vorab qualifizieren',
      'einwand antizipieren',
      'bedenken früh ansprechen',
    ],
    applications: [
{
  label: 'Mentale-Blockade-Vorwegnahme',
  aliases: [
    'mentale blockade vorbehandlung',
    'mentale blockade vorwegnahme',
    'mentale blockade vorab behandeln',
    'mentale blockade präventiv behandeln',
    'mentale kaufblockade vorbehandlung',
    'mentale kaufblockade vorwegnehmen',
    'inneren widerstand vorbehandeln',
    'inneren widerstand vorwegnehmen',
    'mentalen widerstand früh behandeln',
    'blockierenden glaubenssatz vorbehandeln',
    'blockierenden glaubenssatz vorwegnehmen',
    'innere kaufblockade früh behandeln',
    'mentale entscheidungsblockade vorbehandlung',
    'mentale entscheidungsblockade vorwegnehmen',
    'psychologische blockade vorab behandeln',
  ],
},
{
  label: 'Angebotsvertrauen-Vorwegnahme',
  aliases: [
    'angebotsvertrauen vorbehandlung',
    'angebotsvertrauen vorwegnahme',
    'vertrauen in angebot vorbehandeln',
    'vertrauen in angebot vorwegnehmen',
    'zweifel am angebot vorbehandlung',
    'zweifel am angebot vorwegnehmen',
    'angebotszweifel vorab behandeln',
    'angebotszweifel früh behandeln',
    'misstrauen gegenüber angebot vorbehandeln',
    'vertrauensdefizit angebot vorwegnehmen',
    'vertrauensdefizit angebot vorbehandlung',
    'angebotsskepsis vorbehandlung',
    'angebotsskepsis vorwegnehmen',
    'glaubwürdigkeit des angebots vorab aufbauen',
    'vertrauen in die lösung vorab behandeln',
  ],
},
{
  label: 'Anbietervertrauen-Vorwegnahme',
  aliases: [
    'anbietervertrauen vorbehandlung',
    'anbietervertrauen vorwegnahme',
    'vertrauen in anbieter vorbehandeln',
    'vertrauen in anbieter vorwegnehmen',
    'anbieterzweifel vorbehandlung',
    'anbieterzweifel vorwegnehmen',
    'misstrauen gegenüber anbieter vorbehandeln',
    'misstrauen gegenüber anbieter vorwegnehmen',
    'vertrauensdefizit anbieter vorbehandlung',
    'vertrauensdefizit anbieter vorwegnehmen',
    'glaubwürdigkeit des anbieters vorbehandeln',
    'glaubwürdigkeit des anbieters vorab klären',
    'anbieter skepsis vorab behandeln',
    'anbieterunsicherheit vorbehandlung',
    'anbieterunsicherheit vorwegnehmen',
  ],
},
{
  label: 'Selbstvertrauen-Vorwegnahme',
  aliases: [
    'selbstvertrauen vorbehandlung',
    'selbstvertrauen vorwegnahme',
    'selbstzweifel vorbehandlung',
    'selbstzweifel vorwegnahme',
    'selbstzweifel vorab behandeln',
    'umsetzungszweifel vorbehandlung',
    'umsetzungszweifel vorwegnehmen',
    'vertrauen in sich selbst vorbehandeln',
    'vertrauen in sich selbst vorwegnehmen',
    'fehlendes selbstvertrauen früh behandeln',
    'zweifel an eigener fähigkeit vorbehandeln',
    'zweifel an eigener umsetzung vorwegnehmen',
    'ich schaffe das nicht vorbehandlung',
    'eigene erfolgsfähigkeit vorab klären',
    'umsetzungssicherheit vorbehandlung',
  ],
},
{
  label: 'Methodenvertrauen-Vorwegnahme',
  aliases: [
    'methodenvertrauen vorbehandlung',
    'methodenvertrauen vorwegnahme',
    'vertrauen in methode vorbehandeln',
    'vertrauen in methode vorwegnehmen',
    'methodenskepsis vorbehandlung',
    'methodenskepsis vorwegnahme',
    'zweifel an methode vorbehandeln',
    'zweifel an methode vorwegnehmen',
    'vertrauensdefizit methode vorbehandlung',
    'vertrauensdefizit methode vorwegnehmen',
    'vorgehensweise vorbehandlung',
    'vorgehensweise vorwegnehmen',
    'zweifel an der vorgehensweise vorab behandeln',
    'funktioniert die methode vorbehandlung',
    'methodenunsicherheit früh behandeln',
  ],
},
{
  label: 'Bedürfnislücken-Vorwegnahme',
  aliases: [
    'bedürfnislücke vorbehandlung',
    'bedürfnislücke vorwegnahme',
    'bedürfnislücke vorab behandeln',
    'fehlende bedürfniserfüllung vorbehandlung',
    'fehlende bedürfniserfüllung vorwegnehmen',
    'unerfülltes bedürfnis vorbehandlung',
    'unerfülltes bedürfnis vorwegnehmen',
    'bedürfnisdefizit vorbehandlung',
    'bedürfnisdefizit vorwegnahme',
    'bedürfnis früh behandeln',
    'bedürfnis vorab klären',
    'bedürfnis vorwegnehmen',
    'fehlendes bedürfnis früh öffnen',
    'kundenbedürfnis vorbehandlung',
    'bedürfnisblockade vorab behandeln',
  ],
},
{
  label: 'Wachstumsbedürfnis-Vorwegnahme',
  aliases: [
    'wachstumsbedürfnis vorbehandlung',
    'wachstumsbedürfnis vorwegnahme',
    'wachstumsbedürfnis vorab behandeln',
    'wachstumswunsch vorbehandlung',
    'wachstumswunsch vorwegnehmen',
    'wachstumsdefizit vorbehandlung',
    'fehlendes wachstum vorwegnehmen',
    'wachstumsblockade vorbehandlung',
    'wachstumsblockade vorwegnehmen',
    'weiterentwicklungsbedürfnis vorbehandlung',
    'weiterentwicklungsbedürfnis vorwegnehmen',
    'wachstumsbedarf früh behandeln',
    'wachstum vorab thematisieren',
    'wachstum früh vorbehandeln',
    'entwicklungsbedarf vorwegnehmen',
  ],
},
{
  label: 'Verbundenheitsbedürfnis-Vorwegnahme',
  aliases: [
    'verbundenheitsbedürfnis vorbehandlung',
    'verbundenheitsbedürfnis vorwegnahme',
    'verbundenheitsbedürfnis vorab behandeln',
    'zugehörigkeitsbedürfnis vorbehandlung',
    'zugehörigkeitsbedürfnis vorwegnehmen',
    'fehlende verbundenheit vorbehandlung',
    'fehlende verbundenheit vorwegnehmen',
    'soziale verbundenheit vorbehandeln',
    'soziale verbundenheit vorwegnehmen',
    'zugehörigkeit früh behandeln',
    'zugehörigkeit vorab klären',
    'verbundenheit früh thematisieren',
    'soziales bedürfnis vorbehandlung',
    'soziales bedürfnis vorwegnehmen',
    'beziehungsbedürfnis vorab behandeln',
  ],
},
{
  label: 'Anerkennungsbedürfnis-Vorwegnahme',
  aliases: [
    'anerkennungsbedürfnis vorbehandlung',
    'anerkennungsbedürfnis vorwegnahme',
    'anerkennungsbedürfnis vorab behandeln',
    'bedarf nach anerkennung vorbehandlung',
    'bedarf nach anerkennung vorwegnehmen',
    'fehlende anerkennung vorbehandlung',
    'fehlende anerkennung vorwegnehmen',
    'wertschätzungsbedürfnis vorbehandlung',
    'wertschätzungsbedürfnis vorwegnahme',
    'statusbedürfnis vorbehandlung',
    'statusbedürfnis vorwegnehmen',
    'anerkennung früh behandeln',
    'anerkennung vorab thematisieren',
    'wertschätzung vorab behandeln',
    'soziale anerkennung vorwegnehmen',
  ],
},
{
  label: 'Abwechslungsbedürfnis-Vorwegnahme',
  aliases: [
    'abwechslungsbedürfnis vorbehandlung',
    'abwechslungsbedürfnis vorwegnahme',
    'abwechslungsbedürfnis vorab behandeln',
    'bedarf nach abwechslung vorbehandlung',
    'bedarf nach abwechslung vorwegnehmen',
    'fehlende abwechslung vorbehandlung',
    'fehlende abwechslung vorwegnehmen',
    'monotonie vorbehandlung',
    'monotonie vorwegnahme',
    'routine vorbehandlung',
    'routine vorwegnehmen',
    'veränderungsbedürfnis vorbehandlung',
    'veränderungsbedürfnis vorwegnahme',
    'abwechslung früh thematisieren',
    'neues erleben vorab behandeln',
  ],
},
{
  label: 'Sicherheitsbedürfnis-Vorwegnahme',
  aliases: [
    'sicherheitsbedürfnis vorbehandlung',
    'sicherheitsbedürfnis vorwegnahme',
    'sicherheitsbedürfnis vorab behandeln',
    'bedarf nach sicherheit vorbehandlung',
    'bedarf nach sicherheit vorwegnehmen',
    'fehlende sicherheit vorbehandlung',
    'fehlende sicherheit vorwegnehmen',
    'unsicherheitsgefühl vorbehandlung',
    'unsicherheitsgefühl vorwegnahme',
    'sicherheitsdefizit vorbehandlung',
    'sicherheitsdefizit vorwegnehmen',
    'sicherheit früh behandeln',
    'sicherheit vorab thematisieren',
    'absicherungsbedürfnis vorbehandlung',
    'absicherungsbedürfnis vorwegnehmen',
  ],
},
{
  label: 'Externalisierungs-Vorwegnahme',
  aliases: [
    'externalisierung vorbehandlung',
    'externalisierung vorwegnahme',
    'externalisierung vorab behandeln',
    'verantwortungsverschiebung vorbehandlung',
    'verantwortungsverschiebung vorwegnehmen',
    'verantwortung nach außen vorbehandeln',
    'verantwortung nach außen vorwegnehmen',
    'schuld bei anderen vorbehandlung',
    'schuld bei anderen vorwegnehmen',
    'äußere umstände vorbehandlung',
    'äußere umstände vorwegnehmen',
    'opferhaltung vorbehandlung',
    'opferhaltung vorwegnahme',
    'eigenverantwortung früh behandeln',
    'externalen ursachenfokus vorab behandeln',
  ],
},
{
  label: 'Logistik-Vorwegnahme',
  aliases: [
    'logistik vorbehandlung',
    'logistik vorwegnahme',
    'logistische blockade vorbehandlung',
    'logistische blockade vorwegnahme',
    'logistische blockade vorab behandeln',
    'logistische hürde vorbehandlung',
    'logistische hürde vorwegnehmen',
    'umsetzungshürde vorbehandlung',
    'umsetzungshürde vorwegnahme',
    'operative blockade vorbehandlung',
    'operative blockade vorwegnehmen',
    'machbarkeit vorbehandlung',
    'machbarkeit vorwegnehmen',
    'logistische machbarkeit vorab klären',
    'umsetzungslogistik früh behandeln',
  ],
},
{
  label: 'Personal-Vorwegnahme',
  aliases: [
    'personal vorbehandlung',
    'personal vorwegnahme',
    'personalengpass vorbehandlung',
    'personalengpass vorwegnahme',
    'personelle blockade vorbehandlung',
    'personelle blockade vorwegnehmen',
    'personalmangel vorbehandlung',
    'personalmangel vorwegnehmen',
    'mitarbeitermangel vorbehandlung',
    'mitarbeitermangel vorwegnahme',
    'personelle kapazität vorbehandlung',
    'personelle kapazität vorwegnehmen',
    'personalressourcen vorab behandeln',
    'mitarbeiterressourcen vorab behandeln',
    'personelle machbarkeit vorbehandlung',
  ],
},
{
  label: 'Ressourcen-Vorwegnahme',
  aliases: [
    'ressourcen vorbehandlung',
    'ressourcen vorwegnahme',
    'ressourcenengpass vorbehandlung',
    'ressourcenengpass vorwegnahme',
    'ressourcenblockade vorbehandlung',
    'ressourcenblockade vorwegnehmen',
    'fehlende ressourcen vorbehandlung',
    'fehlende ressourcen vorwegnahme',
    'operative ressourcen vorbehandlung',
    'operative ressourcen vorwegnehmen',
    'umsetzungsressourcen vorbehandlung',
    'umsetzungsressourcen vorwegnahme',
    'ressourcenverfügbarkeit vorab behandeln',
    'ressourcenlage vorbehandlung',
    'ressourcenlage vorwegnehmen',
  ],
},
      {
        label: 'Preis-Vorwegnahme',
        aliases: [
          'preis vorab ansprechen',
          'preiseinwand vorwegnehmen',
          'investition früh klären',
          'budget vorab prüfen',
          'kostenrahmen vor angebot',
          'preisüberraschung vermeiden',
          'preisanker setzen',
          'preis vor dem pitch',
          'honorar früh thematisieren',
          'wert vor preis aufbauen',
          'zahlungsbereitschaft prüfen',
          'finanzielle erwartung setzen',
          'kostenpunkt vorbereiten',
          'preis nicht verstecken',
          'preisgespräch vorbereiten',
        ],
      },
      {
        label: 'Zeit-Vorwegnahme',
        aliases: [
          'zeit vorab klären',
          'zeiteinwand vorwegnehmen',
          'zeitaufwand früh ansprechen',
          'zeitrahmen setzen',
          'kundenzeit prüfen',
          'umsetzungsdauer erklären',
          'zeitinvestment vorbereiten',
          'kapazität vorab prüfen',
          'terminaufwand klären',
          'laufzeit vor angebot',
          'zeitbedarf nicht überraschen',
          'implementierungszeit klären',
          'call dauer framen',
          'terminrealität prüfen',
          'zeitliche erwartung setzen',
        ],
      },
      {
        label: 'Vertrauens-Vorwegnahme',
        aliases: [
          'vertrauen vorab aufbauen',
          'vertrauenseinwand vorwegnehmen',
          'skepsis früh abholen',
          'glaubwürdigkeit vor angebot',
          'zweifel früh klären',
          'proof vorab setzen',
          'referenzen früh nutzen',
          'sicherheitsgefühl schaffen',
          'vertrauensbasis legen',
          'misstrauen vorab öffnen',
          'beweise vorab einordnen',
          'glaubwürdigkeit rahmen',
          'vertrauensrisiko senken',
          'kundenbedenken offen machen',
          'vertrauensfrage vorwegnehmen',
        ],
      },
      {
        label: 'Risiko-Vorwegnahme',
        aliases: [
          'risiko vorab ansprechen',
          'risikoeinwand vorwegnehmen',
          'absicherung früh erklären',
          'sicherheitsbedenken vorab',
          'was wenn es nicht klappt vorwegnehmen',
          'garantie früh erklären',
          'kaufangst vorab senken',
          'risiko transparent machen',
          'entscheidungssicherheit vorbereiten',
          'verlustangst früh abholen',
          'risiko im voraus relativieren',
          'umsetzungsrisiko besprechen',
          'sicherheitsrahmen setzen',
          'risiko des nicht handelns vorbereiten',
          'kundenrisiko früh prüfen',
        ],
      },
      {
        label: 'Skepsis-Vorwegnahme',
        aliases: [
          'skepsis vorab behandeln',
          'skepsis vorwegnehmen',
          'zweifel früh ansprechen',
          'kritische rückfragen antizipieren',
          'kritischen kunden vorbereiten',
          'skepsis legitimieren',
          'prüfhaltung einordnen',
          'misstrauen vor pitch klären',
          'abwartende haltung vorab',
          'skepsis nicht bekämpfen',
          'kritik vorab abholen',
          'zweifel normalisieren',
          'skeptische haltung rahmen',
          'kundenkritik vorbereiten',
          'skepsis früh öffnen',
        ],
      },
      {
        label: 'Partner-Vorwegnahme',
        aliases: [
          'partnerfrage vorab klären',
          'partner einwand vorwegnehmen',
          'ehepartner früh einbinden',
          'mitentscheider vorab klären',
          'wer muss zustimmen',
          'abstimmungspflicht prüfen',
          'dritte person identifizieren',
          'partner veto vorbereiten',
          'entscheidungspartner klären',
          'alle entscheider sichern',
          'familienentscheidung vorbereiten',
          'zuhause besprechen vermeiden',
          'stakeholder früh herausfinden',
          'mitentscheider nicht vergessen',
          'abstimmung nach call vermeiden',
        ],
      },
      {
        label: 'Wettbewerber-Vorwegnahme',
        aliases: [
          'wettbewerber vorab ansprechen',
          'konkurrenz vorwegnehmen',
          'andere anbieter früh klären',
          'vergleichsprozess verstehen',
          'alternative lösungen klären',
          'marktvergleich einordnen',
          'differenzierung vorbereiten',
          'wettbewerbsfrage vorab',
          'kunde vergleicht abholen',
          'konkurrenzangebot vorweg',
          'vergleichskriterien definieren',
          'auswahlprozess klären',
          'mitbewerber nicht ignorieren',
          'anbieterwechsel rahmen',
          'warum nicht konkurrenz kaufen',
        ],
      },
      {
        label: 'Commitment-Vorwegnahme',
        aliases: [
          'commitment vorab prüfen',
          'verbindlichkeit früh abfragen',
          'abschlussbereitschaft vor angebot',
          'ernsthaftigkeit prüfen',
          'nur interesse oder kaufabsicht',
          'zusagefähigkeit klären',
          'entscheidungskriterien vorab',
          'kundencommitment vorbereiten',
          'unverbindlichkeit reduzieren',
          'commitmentrahmen setzen',
          'kaufbereitschaft einschätzen',
          'nächsten schritt vor pitch sichern',
          'entscheidung nach angebot vorbereiten',
          'verbindliches gesprächsziel',
          'commitmentlevel prüfen',
        ],
      },
      {
        label: 'Komplexitäts-Vorwegnahme',
        aliases: [
          'komplexität vorab reduzieren',
          'kompliziertheit vorwegnehmen',
          'lösung einfach erklären',
          'kunde nicht überfordern',
          'verständlichkeit sichern',
          'prozess einfach darstellen',
          'technische skepsis vorab',
          'komplexe details strukturieren',
          'einfachheitsrahmen setzen',
          'angebot übersichtlich machen',
          'umsetzung verständlich erklären',
          'überforderung vermeiden',
          'einfacher einstieg',
          'kunden auf struktur vorbereiten',
          'komplexität früh einordnen',
        ],
      },
      {
        label: 'Budget-Vorwegnahme',
        aliases: [
          'budget vorab klären',
          'budgeteinwand vorwegnehmen',
          'finanzielle freigabe prüfen',
          'investitionsrahmen klären',
          'budgetprozess verstehen',
          'kostenstelle früh klären',
          'zahlungsrahmen setzen',
          'budgetverfügbarkeit prüfen',
          'freigabeschleife klären',
          'budgetjahr berücksichtigen',
          'finanzierung vor pitch',
          'kaufrahmen definieren',
          'budgetrealität ansprechen',
          'kein budget später vermeiden',
          'wirtschaftlichen rahmen klären',
        ],
      },
    ],
  },
  {
    label: 'Vertrauensaufbau (P TC+, P SK-)',
    aliases: [
      'vertrauen aufbauen',
      'vertrauensaufbau im verkauf',
      'kundenvertrauen gewinnen',
      'glaubwürdigkeit erhöhen',
      'skepsis reduzieren',
      'rapport aufbauen',
      'vertrauensbasis schaffen',
      'kunde fühlt sich verstanden',
      'vertrauen vor abschluss',
      'sicherheit im kaufprozess',
      'professionell glaubwürdig wirken',
      'vertrauen durch fragen',
      'beziehung im call stärken',
      'kundenbedenken ernst nehmen',
      'vertrauen statt druck',
    ],
    applications: [
      {
        label: 'Autorität (P TC+)',
        aliases: [
          'autorität aufbauen',
          'expertenstatus zeigen',
          'fachliche führung übernehmen',
          'souverän im sales call',
          'beraterautorität',
          'kunde folgt gesprächsführung',
          'fachliche einordnung geben',
          'autorität durch diagnose',
          'professionelle sicherheit',
          'sachliche autorität',
          'verkaufsführung ohne dominanz',
          'kunde erkennt expertise',
          'führungsrolle im gespräch',
          'kompetent führen',
          'vertrauenswürdige autorität',
        ],
      },
      {
        label: 'Kompetenz (P TC+)',
        aliases: [
          'kompetenz zeigen',
          'fachkompetenz sichtbar machen',
          'kunde erkennt fachwissen',
          'kompetent antworten',
          'expertise vermitteln',
          'sachverstand beweisen',
          'professionelle erklärung',
          'diagnosekompetenz zeigen',
          'kundenproblem einordnen',
          'beraterkompetenz',
          'lösungsverständnis zeigen',
          'kompetenz durch fragen',
          'fachliche sicherheit',
          'kundenvertrauen durch wissen',
          'relevanz herstellen',
        ],
      },
      {
        label: 'Transparenz (P TC+, P SK-)',
        aliases: [
          'transparenz schaffen',
          'ehrlich verkaufen',
          'offen über preis sprechen',
          'prozess transparent erklären',
          'keine versteckten kosten',
          'risiken offen ansprechen',
          'klare erwartungen setzen',
          'angebot transparent machen',
          'kunden offen informieren',
          'nichts beschönigen',
          'transparente beratung',
          'ehrlicher sales call',
          'grenzen offen sagen',
          'kunden nicht überraschen',
          'offene entscheidungsgrundlage',
        ],
      },
      {
        label: 'Sozialer Beweis (P TC+, P SK-)',
        aliases: [
          'sozialer beweis',
          'social proof',
          'referenzen nutzen',
          'kundenbeispiele',
          'case study im sales call',
          'testimonials einsetzen',
          'kundenstimmen',
          'vergleichbare kunden nennen',
          'proof durch andere kunden',
          'ergebnisse anderer kunden',
          'referenzfall erzählen',
          'peer proof',
          'bewertungen nutzen',
          'kundenbeweis',
          'ähnlicher kunde beispiel',
        ],
      },
      {
        label: 'Sicherheit (P TC+, P SK-)',
        aliases: [
          'sicherheit geben',
          'kaufangst reduzieren',
          'kundenrisiko senken',
          'entscheidungssicherheit',
          'absicherung erklären',
          'garantie kommunizieren',
          'kunde fühlt sich sicher',
          'risikofreier nächster schritt',
          'sicherheitsbedürfnis erfüllen',
          'sicherheitsrahmen',
          'umsetzungsrisiko senken',
          'klare absicherung',
          'kundenängste klären',
          'sichere entscheidung ermöglichen',
          'kontrolle geben',
        ],
      },
      {
        label: 'Empathie (P TC+)',
        aliases: [
          'empathie im verkauf',
          'aktiv zuhören',
          'kunde fühlt sich verstanden',
          'kundenperspektive spiegeln',
          'verständnis zeigen',
          'einwand empathisch behandeln',
          'emotionalen zustand erkennen',
          'menschlich verkaufen',
          'kundensituation ernst nehmen',
          'empathische frage',
          'rapport durch verständnis',
          'kundenfrust anerkennen',
          'zuhören statt pitchen',
          'empathische einwandöffnung',
          'verständnis ohne zustimmung',
        ],
      },
      {
        label: 'Ähnlichkeit (P TC+)',
        aliases: [
          'ähnlichkeit nutzen',
          'gemeinsamkeiten finden',
          'gleiche sprache sprechen',
          'branchennähe zeigen',
          'ähnliche erfahrung',
          'vergleichbare situation',
          'kunde erkennt gleiche welt',
          'rapport durch gemeinsamkeit',
          'nähe ohne anbiedern',
          'peer bezug herstellen',
          'ähnliche kundenwelt',
          'vertrautheit schaffen',
          'gemeinsamer kontext',
          'ähnliche herausforderung',
          'beziehungsaufbau durch ähnlichkeit',
        ],
      },
      {
        label: 'Verlässlichkeit (P TC+)',
        aliases: [
          'verlässlichkeit zeigen',
          'zuverlässigkeit beweisen',
          'vertrauen durch verlässlichkeit',
          'realistische zusagen',
          'lieferfähigkeit zeigen',
          'verlässlicher partner',
          'erwartungen erfüllen',
          'stabilität vermitteln',
          'professionelle verbindlichkeit',
          'saubere prozesse zeigen',
          'kundenangst vor enttäuschung',
          'versprechen realistisch setzen',
          'umsetzung verlässlich erklären',
          'konstanz kommunizieren',
          'zuverlässig wirken',
        ],
      },
      {
        label: 'Expertise (P TC+)',
        aliases: [
          'expertise zeigen',
          'spezialwissen nutzen',
          'fachwissen vermitteln',
          'kundenproblem analysieren',
          'expertenrolle einnehmen',
          'branchenwissen einbringen',
          'insiderwissen nutzen',
          'tiefe diagnose',
          'expertise durch fragen',
          'lösungskompetenz zeigen',
          'beraterwissen einordnen',
          'fachliche tiefe',
          'spezialist statt verkäufer',
          'expertise ohne überheblichkeit',
          'kundenkontext verstehen',
        ],
      },
      {
        label: 'Beweis (P TC+, P SK-)',
        aliases: [
          'beweise nutzen',
          'proof point',
          'ergebnisse nachweisen',
          'zahlen als beweis',
          'daten für glaubwürdigkeit',
          'vorher nachher zeigen',
          'messbare resultate',
          'track record',
          'fakten statt behauptungen',
          'erfolgsnachweis',
          'benchmark nutzen',
          'dokumentierte ergebnisse',
          'wirkung nachweisen',
          'beleg für lösung',
          'kundenbeweis liefern',
        ],
      },
    ],
  },
  {
    label: 'Glaubenssatz-Wandel (P TO+, P CN+, P CC+, P TS+, P SK-, P CO+)',
    aliases: [
      'glaubenssatz verändern',
      'belief shift',
      'reframing',
      'umdenken auslösen',
      'kundenüberzeugung ändern',
      'falsche annahme lösen',
      'limitierende überzeugung',
      'perspektive ändern',
      'kaufblockade neu einordnen',
      'einwand reframen',
      'kunde erkennt neuen blickwinkel',
      'alte überzeugung hinterfragen',
      'einsicht erzeugen',
      'mindset im verkauf',
      'denkrahmen wechseln',
    ],
    applications: [
      {
        label: 'Falsche Annahme (P CN+, P SK-)',
        aliases: [
          'falsche annahme',
          'annahme aufdecken',
          'kunde geht von falschem aus',
          'irrtum klären',
          'denkfehler hinterfragen',
          'missverständnis korrigieren',
          'falsche vorstellung',
          'annahme prüfen',
          'kunde hat falsches bild',
          'fehleinschätzung',
          'trugschluss lösen',
          'falsche erwartung',
          'vorurteil öffnen',
          'mythos entkräften',
          'schlussfolgerung korrigieren',
        ],
      },
      {
        label: 'Limitierender Glaubenssatz (P TS+, P SK-)',
        aliases: [
          'limitierender glaubenssatz',
          'geht nicht überzeugung',
          'funktioniert bei uns nicht',
          'wir sind anders einwand',
          'kunde sieht blockade',
          'unmöglichkeitsglaube',
          'kann ich nicht',
          'nicht für unsere branche',
          'selbstlimitierung',
          'blockierende überzeugung',
          'mentale blockade',
          'einschränkendes denken',
          'kunde begrenzt möglichkeit',
          'alte grenze hinterfragen',
          'glaubenssatz lösen',
        ],
      },
      {
        label: 'Perspektivwechsel (P CN+, P SK-)',
        aliases: [
          'perspektivwechsel',
          'neuer blickwinkel',
          'anders betrachten',
          'reframe frage',
          'andere sichtweise',
          'kunde sieht andere seite',
          'problem neu einordnen',
          'blickwinkel wechseln',
          'rauszoomen',
          'metaperspektive',
          'denkrahmen wechseln',
          'kundenlogik drehen',
          'situation neu betrachten',
          'neue perspektive',
          'blickwinkelfrage',
        ],
      },
      {
        label: 'Ursachenwechsel (P CN+)',
        aliases: [
          'ursache neu einordnen',
          'ursachenwechsel',
          'falsche ursache',
          'eigentliche ursache',
          'symptom oder ursache',
          'problemursache klären',
          'nicht person sondern system',
          'systemische ursache',
          'prozessursache',
          'schuldfrage vermeiden',
          'wirkmechanismus erklären',
          'kunde sieht falschen auslöser',
          'ursachenfrage',
          'problem anders erklären',
          'ursache reframen',
        ],
      },
      {
        label: 'Verantwortungswechsel (P CO+)',
        aliases: [
          'verantwortung neu rahmen',
          'verantwortungswechsel',
          'kundenverantwortung',
          'eigene rolle erkennen',
          'handlungsspielraum erkennen',
          'verantwortungsfrage',
          'entscheidung verantworten',
          'zuständigkeit klären',
          'verantwortung ohne schuld',
          'einflussbereich erkennen',
          'handlungsmacht zurückgeben',
          'opferhaltung lösen',
          'aktive rolle',
          'verantwortungsübernahme',
          'eigener hebel',
        ],
      },
      {
        label: 'Wertverschiebung (P TO+)',
        aliases: [
          'wert statt preis',
          'investition statt kosten',
          'wertwahrnehmung',
          'preis in wert übersetzen',
          'mehrwert erklären',
          'kosten nutzen drehen',
          'kunde sieht wert nicht',
          'return klären',
          'preisfokus lösen',
          'nutzenfokus',
          'roi perspektive',
          'ausgabe als investition',
          'wirtschaftlicher wert',
          'wertlogik',
          'preis leistung neu rahmen',
        ],
      },
      {
        label: 'Risikowahrnehmung (P CC+)',
        aliases: [
          'risiko neu bewerten',
          'risikowahrnehmung ändern',
          'risiko des nicht handelns',
          'kaufrisiko überschätzt',
          'handlungsrisiko vergleichen',
          'risiko umkehren',
          'sicherheitsbedenken reframen',
          'kaufangst neu einordnen',
          'nichtentscheidung ist riskanter',
          'risiko in relation',
          'folgerisiko zeigen',
          'risikofrage',
          'risikoentscheidung',
          'sicherheitsdenken öffnen',
          'risikoausgleich',
        ],
      },
      {
        label: 'Chancenwahrnehmung (P TO+)',
        aliases: [
          'chance sichtbar machen',
          'chancenwahrnehmung',
          'kunde erkennt möglichkeit nicht',
          'potenzial herausarbeiten',
          'möglichkeit im problem',
          'kundenchance rahmen',
          'positive auswirkung',
          'upside klären',
          'gewinnchance',
          'möglichkeitsfrage',
          'potenzial statt problem',
          'chance aus veränderung',
          'nutzenraum öffnen',
          'wachstumsperspektive',
          'opportunity reframing',
        ],
      },
      {
        label: 'Kostenwahrnehmung (P TO+, P CC+)',
        aliases: [
          'kostenwahrnehmung',
          'preis relativieren',
          'kosten im verhältnis',
          'kosten pro tag',
          'teuer reframen',
          'kosten des nicht handelns',
          'preisgefühl ändern',
          'investition aufschlüsseln',
          'kunde sieht nur preis',
          'gesamtkosten betrachten',
          'preis in relation',
          'kurzfristkosten langfristkosten',
          'preisanker ändern',
          'wahrgenommener preis',
          'kostenfrage neu stellen',
        ],
      },
      {
        label: 'Selbstzweifel (P TS+)',
        aliases: [
          'selbstzweifel',
          'kunde traut sich nicht',
          'angst es nicht zu schaffen',
          'zweifel an eigener fähigkeit',
          'umsetzungszweifel',
          'fehlendes selbstvertrauen',
          'kunde fühlt sich nicht bereit',
          'überforderung durch entscheidung',
          'kann ich das schaffen',
          'erfolgsfähigkeit klären',
          'kompetenzzweifel',
          'umsetzungssicherheit',
          'zutrauen aufbauen',
          'innere unsicherheit',
          'kunde zweifelt an sich',
        ],
      },
    ],
  },
  {
    label: 'Identitäts-Rahmung (P TS+, P CO+)',
    aliases: [
      'identitätsrahmung',
      'identity framing',
      'selbstbild ansprechen',
      'kundenrolle rahmen',
      'wer will der kunde sein',
      'entscheiderrolle stärken',
      'zukünftiges selbst',
      'käuferrolle aktivieren',
      'verantwortungsrolle spiegeln',
      'kunde als macher',
      'entscheidung passt zum selbstbild',
      'handlungsidentität',
      'professionelle rolle',
      'selbstwahrnehmung im verkauf',
      'rolle im kaufprozess',
    ],
    applications: [
      {
        label: 'Selbstbild (P TS+)',
        aliases: [
          'selbstbild',
          'selbstbild des kunden',
          'selbstwahrnehmung',
          'eigenbild',
          'kunde sieht sich selbst',
          'entscheidung und selbstbild',
          'wer bin ich als entscheider',
          'kunde will stimmig handeln',
          'selbstverständnis',
          'selbstkonzept',
          'handlungslücke selbstbild',
          'kunde erkennt eigenes muster',
          'selbstbildfrage',
          'identität des kunden',
          'entscheidung passt zu mir',
        ],
      },
      {
        label: 'Status (P TS+)',
        aliases: [
          'status ansprechen',
          'image im verkauf',
          'ruf schützen',
          'ansehen des kunden',
          'professioneller anspruch',
          'statusverlust vermeiden',
          'reputation',
          'positionierung des kunden',
          'souverän wirken',
          'prestige im kaufprozess',
          'statusentscheidung',
          'ansehen im team',
          'führungsstatus',
          'imageverlust',
          'reputation als entscheidungsfaktor',
        ],
      },
      {
        label: 'Führungsidentität (P TS+, P CO+)',
        aliases: [
          'führungsidentität',
          'führungsrolle',
          'kunde als führungskraft',
          'leader identity',
          'verantwortung als chef',
          'führung durch entscheidung',
          'vorausschauende führung',
          'teamverantwortung',
          'entscheidung als führungsaufgabe',
          'strategische führungsrolle',
          'kunde als vorreiter',
          'führungsanspruch',
          'leadership frage',
          'verantwortung gegenüber team',
          'führungskraft entscheidet',
        ],
      },
      {
        label: 'Professionelle Identität (P TS+, P CO+)',
        aliases: [
          'professionelle identität',
          'kunde als profi',
          'beruflicher anspruch',
          'professionelle entscheidung',
          'kunde will seriös handeln',
          'professionalität',
          'fachliche rolle',
          'geschäftliche verantwortung',
          'professioneller standard',
          'berufliches selbstverständnis',
          'business identität',
          'professionalität und konsequenz',
          'seriöse käuferrolle',
          'geschäftsmäßige entscheidung',
          'profi anspruch',
        ],
      },
      {
        label: 'Käuferidentität (P CO+)',
        aliases: [
          'käuferidentität',
          'käuferrolle',
          'vom interessenten zum käufer',
          'interessent oder käufer',
          'kunde als auftraggeber',
          'buyer mindset',
          'ernsthafter käufer',
          'kaufbereitschaft als rolle',
          'vom prüfer zum käufer',
          'kunde übernimmt käuferrolle',
          'entscheidungsträgerrolle',
          'auftraggeberrolle',
          'käuferstatus',
          'buyer identity',
          'kaufrolle aktivieren',
        ],
      },
      {
        label: 'Wachstumsidentität (P TS+, P CO+)',
        aliases: [
          'wachstumsidentität',
          'kunde will wachsen',
          'weiterentwicklung',
          'growth mindset',
          'nächste stufe',
          'wachstumsanspruch',
          'kunde will fortschritt',
          'ambition spiegeln',
          'aufstiegsidentität',
          'nicht stehen bleiben',
          'entwicklung als selbstbild',
          'wachstum durch entscheidung',
          'zukunftswachstum',
          'weiterentwicklungsfrage',
          'kunde als lernender',
        ],
      },
      {
        label: 'Verantwortungsidentität (P CO+)',
        aliases: [
          'verantwortungsidentität',
          'kunde als verantwortlicher',
          'verantwortungsrolle',
          'pflichtgefühl',
          'verantwortungsbewusst handeln',
          'für team verantwortlich',
          'für ergebnis verantwortlich',
          'entscheidung verantworten',
          'verantwortung gegenüber unternehmen',
          'rolle als verantwortlicher',
          'verantwortung übernehmen',
          'verantwortungsgefühl aktivieren',
          'zuständigkeit als identität',
          'verantwortungsträger',
          'entscheidungspflicht',
        ],
      },
      {
        label: 'Kontrollidentität (P TS+, P CO+)',
        aliases: [
          'kontrollidentität',
          'kontrolle behalten',
          'kunde will kontrolle',
          'kontrollverlust vermeiden',
          'souveränität',
          'steuerung behalten',
          'überblick gewinnen',
          'selbstbestimmt entscheiden',
          'handlungskontrolle',
          'kontrollbedürfnis',
          'kunde will steuern',
          'entscheidung gibt kontrolle',
          'wieder handlungsfähig werden',
          'kontrolle über situation',
          'nicht fremdbestimmt sein',
        ],
      },
      {
        label: 'Entscheider-Identität (P TS+, P CO+)',
        aliases: [
          'entscheideridentität',
          'kunde als entscheider',
          'entscheidungsträger',
          'macherrolle',
          'entscheiderrolle aktivieren',
          'klare entscheidung treffen',
          'entscheidungskraft',
          'kunde trifft entscheidung',
          'verantwortlicher entscheider',
          'entschlusskraft',
          'buyer entscheidet',
          'nicht nur prüfen entscheiden',
          'entscheider selbstbild',
          'entscheidungskompetenz',
          'entscheiderfrage',
        ],
      },
      {
        label: 'Zukünftiges Selbst (P TS+)',
        aliases: [
          'zukünftiges selbst',
          'future self',
          'wer will der kunde werden',
          'zukunftsbild',
          'vision des kunden',
          'kunde in einem jahr',
          'zukünftige version',
          'bestes selbst',
          'zielidentität',
          'zukunftsrolle',
          'wo will der kunde hin',
          'zukünftige situation',
          'future pacing',
          'entwicklungsszenario',
          'zukünftiger zustand',
        ],
      },
    ],
  },
{
  label: 'Bedarfsweckung (D CL+, D SC+, D MA+, D ME+, D CO+)',
  aliases: [
    'bedarfsweckung',
    'bedarf wecken',
    'bedarf vertiefen',
    'mangel erkennen',
    'mangel herausarbeiten',
    'mangel konkretisieren',
    'bedarf sichtbar machen',
    'bedarf herausarbeiten',
    'bedarf entwickeln',
    'bedarf diagnostizieren',
    'mangelsituation verstehen',
    'bedarfssituation verstehen',
    'bedarf greifbar machen',
    'relevanz des mangels klären',
    'ausgangssituation vertiefen'
  ],
  applications: [
    {
      label: 'Mangelerkennung / Klarheit (D CL+)',
      aliases: [
        'mangel erkennen',
        'mangel konkretisieren',
        'klarheit zum mangel schaffen',
        'bedarf konkretisieren',
        'bedarf diagnostizieren',
        'mangel sichtbar machen',
        'woran festmachen',
        'aussage konkretisieren',
        'situation genauer verstehen',
        'bedarf greifbar machen',
        'mangel greifbar machen',
        'fehlende klarheit herstellen',
        'bedarf genauer verstehen',
        'ursache des mangels verstehen',
        'mangelsituation klären'
      ]
    },

    {
      label: 'Konkrete Szene (D SC+)',
      aliases: [
        'konkrete szene',
        'konkrete situation',
        'konkreten fall öffnen',
        'beispielsituation',
        'letzte situation',
        'letztes vorkommnis',
        'konkretes beispiel',
        'situation rekonstruieren',
        'ablauf konkretisieren',
        'realen fall durchgehen',
        'konkreten moment beschreiben',
        'situation detaillieren',
        'wann ist das passiert',
        'konkreten ablauf verstehen',
        'mangel in konkreter situation'
      ]
    },

    {
      label: 'Magnitude / Ausmaß (D MA+)',
      aliases: [
        'magnitude',
        'ausmaß',
        'umfang des mangels',
        'größe des mangels',
        'dauer des mangels',
        'häufigkeit des mangels',
        'reichweite des mangels',
        'größenordnung herausarbeiten',
        'mangel quantifizieren',
        'umfang konkretisieren',
        'dauer konkretisieren',
        'häufigkeit konkretisieren',
        'betroffene bereiche erfassen',
        'tragweite des mangels',
        'dimension des mangels'
      ]
    },

    {
      label: 'Relevanz / Bedeutung (D ME+)',
      aliases: [
        'relevanz herausarbeiten',
        'bedeutung herausarbeiten',
        'bedeutung des mangels',
        'relevanz des mangels',
        'warum ist das wichtig',
        'weshalb ist das wichtig',
        'was bedeutet das für sie',
        'persönliche bedeutung',
        'geschäftliche bedeutung',
        'relevanz für den alltag',
        'relevanz für das team',
        'relevanz für das unternehmen',
        'stellenwert des mangels',
        'bedeutung genauer verstehen',
        'warum spielt das eine rolle'
      ]
    },

    {
      label: 'Folgewirkung (D CO+)',
      aliases: [
        'folgewirkung',
        'einfluss des mangels',
        'auswirkung des mangels',
        'auswirkungen verstehen',
        'einfluss auf andere bereiche',
        'auswirkung auf den alltag',
        'auswirkung auf das team',
        'auswirkung auf das unternehmen',
        'weitere bereiche betroffen',
        'zusammenhänge des mangels',
        'was hängt damit zusammen',
        'welchen einfluss hat das',
        'was verändert sich dadurch',
        'welche bereiche beeinflusst das',
        'wirkung des mangels verstehen'
      ]
    }
  ]
},
  {
    label: 'Schmerz-Vertiefung (D EM+, D SC+, D ME+, D MA+, D CO+)',
    aliases: [
      'schmerz vertiefen',
      'pain digging',
      'problem vertiefen',
      'problemfolgen klären',
      'konsequenz vertiefen',
      'auswirkungen herausarbeiten',
      'status quo schmerz',
      'pain point fragen',
      'problem ernst machen',
      'nicht handeln schmerz',
      'kunde erkennt problemtiefe',
      'implikationsfrage',
      'auswirkungsfrage',
      'problem wird teuer',
      'dringlichkeit durch schmerz',
    ],
    applications: [
      {
        label: 'Finanzieller Schmerz (D MA+, D CO+)',
        aliases: [
          'finanzieller schmerz',
          'geldverlust',
          'umsatzverlust',
          'gewinnverlust',
          'kosten steigen',
          'geld verbrennen',
          'finanzielle auswirkung',
          'budgetloch',
          'verlorener umsatz',
          'teure ineffizienz',
          'kostenfalle',
          'wirtschaftlicher schaden',
          'finanzielle konsequenz',
          'marge verliert',
          'profit leidet',
        ],
      },
      {
        label: 'Zeitverlust (D MA+, D CO+)',
        aliases: [
          'zeitverlust',
          'zeitverschwendung',
          'ineffizienz',
          'stunden verlieren',
          'zu viel zeitaufwand',
          'doppelarbeit',
          'leerlauf',
          'wartezeit',
          'verzögerung',
          'zeitfresser',
          'prozess dauert zu lange',
          'verlorene arbeitszeit',
          'zeitkosten',
          'langsame abläufe',
          'produktive zeit geht verloren',
        ],
      },
      {
        label: 'Emotionaler Schmerz (D EM+)',
        aliases: [
          'emotionaler schmerz',
          'frust',
          'stress',
          'nervt den kunden',
          'belastung',
          'druck im alltag',
          'unzufriedenheit',
          'sorge',
          'genervt von problem',
          'emotional belastet',
          'schlaflose nächte',
          'bauchschmerzen wegen problem',
          'mentale belastung',
          'demotivation',
          'frustration vertiefen',
        ],
      },
      {
        label: 'Opportunitätskosten (D MA+, D CO+)',
        aliases: [
          'opportunitätskosten',
          'verpasste chance',
          'entgangener umsatz',
          'chance verpasst',
          'nicht genutztes potenzial',
          'wettbewerber zieht vorbei',
          'marktchance verloren',
          'zu spät reagieren',
          'verlorenes geschäft',
          'wachstum verpasst',
          'nicht dabei sein',
          'potenzial bleibt liegen',
          'chance nicht genutzt',
          'entgangener vorteil',
          'rückstand zum wettbewerb',
        ],
      },
      {
        label: 'Beziehungsschmerz (D EM+, D ME+, D CO+)',
        aliases: [
          'beziehungsschmerz',
          'kunden verlieren',
          'teamkonflikt',
          'interne spannung',
          'kundenunzufriedenheit',
          'mitarbeiter frust',
          'vertrauensverlust',
          'konflikte im team',
          'schlechte stimmung',
          'beziehung leidet',
          'kundenbindung sinkt',
          'team leidet unter problem',
          'kommunikationsproblem',
          'interner reibungsverlust',
          'fluktuation wegen problem',
        ],
      },
      {
        label: 'Statusverlust (D EM+, D ME+, D CO+)',
        aliases: [
          'statusverlust',
          'imageverlust',
          'ruf leidet',
          'ansehen verlieren',
          'peinlich vor team',
          'nicht professionell wirken',
          'reputation leidet',
          'vertrauensverlust im markt',
          'kunde wirkt rückständig',
          'wettbewerber wirkt besser',
          'nicht mehr führend',
          'zurückfallen',
          'professioneller anspruch gefährdet',
          'sichtbarer rückstand',
          'prestigeverlust',
        ],
      },
      {
        label: 'Kontrollverlust (D EM+, D ME+, D CO+)',
        aliases: [
          'kontrollverlust',
          'kunde verliert kontrolle',
          'chaos im prozess',
          'kein überblick',
          'nicht steuerbar',
          'abhängig von anderen',
          'fremdbestimmt',
          'hilflosigkeit',
          'problem eskaliert',
          'außer kontrolle',
          'nicht mehr handlungsfähig',
          'überforderung',
          'keine transparenz',
          'unkontrollierte situation',
          'steuerungsverlust',
        ],
      },
      {
        label: 'Wachstumsschmerz (D ME+, D CO+)',
        aliases: [
          'wachstumsschmerz',
          'stagnation',
          'stillstand',
          'nicht weiterkommen',
          'wachstum blockiert',
          'skalierung scheitert',
          'plateau erreicht',
          'fortschritt bleibt aus',
          'entwicklung stagniert',
          'limit erreicht',
          'wachstumsbremse',
          'keine entwicklung',
          'rückstand wächst',
          'wachstum bleibt liegen',
          'nächste stufe blockiert',
        ],
      },
      {
        label: 'Risikoschmerz (D EM+, D CO+)',
        aliases: [
          'risikoschmerz',
          'angst vor verlust',
          'sicherheitsrisiko',
          'fehlentscheidung droht',
          'risiko wächst',
          'unsicherer status quo',
          'potenzieller verlust',
          'problem kann kippen',
          'gefährliche entwicklung',
          'risiko wird größer',
          'absicherung fehlt',
          'verwundbare situation',
          'risiko durch warten',
          'Folgerisiko',
          'unkalkulierbares Risiko',
        ],
      },
      {
        label: 'Zukünftige Konsequenz (D SC+, D CO+)',
        aliases: [
          'zukünftige konsequenz',
          'was passiert später',
          'langfristiger schaden',
          'folgeproblem',
          'problem wird größer',
          'später teurer',
          'negative entwicklung',
          'wenn nichts passiert',
          'zukunftsrisiko',
          'spätfolge',
          'künftige auswirkung',
          'konsequenz in sechs monaten',
          'problem verschleppt',
          'zukünftiger druck',
          'langfristige folgen',
        ],
      },
    ],
  },
  {
    label: 'Sprachmuster',
    aliases: [
      'verhaltensmuster',
      'kundenmuster erkennen',
      'muster im verkauf',
      'verhalten analysieren',
      'entscheidungmuster',
      'kaufverhalten verstehen',
      'wiederkehrendes muster',
      'kundenverhalten spiegeln',
      'verkaufsmuster',
      'blockademuster',
      'handlungsblockade',
      'vermeidungsmuster',
      'diagnosefrage muster',
      'muster erkennen im call',
      'verhalten hinterfragen',
    ],
    applications: [
      {
        label: 'Ich vs. Wir',
        aliases: [
          'ich vs wir',
          'allein oder team',
          'einzelkämpfer',
          'teamentscheidung',
          'silo denken',
          'wir gefühl fehlt',
          'allein entscheiden',
          'gemeinsam entscheiden',
          'team einbinden',
          'kollektive verantwortung',
          'isoliertes handeln',
          'nicht integriert',
          'abstimmung im team',
          'team oder einzelperson',
          'gemeinsame lösung',
        ],
      },
      {
        label: 'Wunsch vs. Pflicht',
        aliases: [
          'wunsch vs pflicht',
          'müssen oder wollen',
          'kunde will nicht muss aber',
          'freiwillig oder gezwungen',
          'pflichtgefühl',
          'intrinsische motivation',
          'extrinsischer druck',
          'sollte aber will nicht',
          'notwendigkeit vs wunsch',
          'motivation klären',
          'kunde fühlt zwang',
          'wollen herausarbeiten',
          'muss problem',
          'pflichtentscheidung',
          'echte motivation prüfen',
        ],
      },
      {
        label: 'Reden vs. Handeln',
        aliases: [
          'reden vs handeln',
          'viel reden wenig tun',
          'nichts passiert',
          'nur geredet',
          'keine umsetzung',
          'heiße luft',
          'leere versprechen',
          'meeting ohne ergebnis',
          'diskussion statt aktion',
          'tatenlosigkeit',
          'kunde redet nur',
          'umsetzungslücke',
          'aktion fehlt',
          'wissen aber nicht handeln',
          'vom reden ins handeln',
        ],
      },
      {
        label: 'Problem vs. Priorität',
        aliases: [
          'problem vs priorität',
          'problem aber keine priorität',
          'nicht wichtig genug',
          'problem wird toleriert',
          'kunde akzeptiert problem',
          'priorität fehlt',
          'problem ignorieren',
          'nicht handlungsrelevant',
          'agenda fehlt',
          'randthema',
          'problem liegt brach',
          'dringlichkeit fehlt',
          'wichtig aber nicht dringend',
          'prioritätenkonflikt',
          'problem ernst machen',
        ],
      },
      {
        label: 'Bedenken vs. Konsequenz',
        aliases: [
          'bedenken vs konsequenz',
          'kunde grübelt',
          'zögern wegen bedenken',
          'analyse statt entscheidung',
          'sorge blockiert handlung',
          'bedenken wichtiger als folgen',
          'nicht handeln wegen bedenken',
          'konsequenz trotz bedenken',
          'kunde bleibt im überlegen',
          'bedenkenträger',
          'analysis paralysis',
          'grübeln statt handeln',
          'warten aus sorge',
          'folgen des zögerns',
          'bedenken öffnen',
        ],
      },
      {
        label: 'Erkenntnis vs. Entscheidung',
        aliases: [
          'erkenntnis vs entscheidung',
          'verstanden aber nicht entschieden',
          'aha moment ohne aktion',
          'einsicht ohne umsetzung',
          'wissen reicht nicht',
          'klarheit ohne konsequenz',
          'kunde versteht aber handelt nicht',
          'knowing doing gap',
          'durchbruch ohne entscheidung',
          'theorie statt praxis',
          'einsicht aktivieren',
          'entscheidung nach erkenntnis',
          'verstanden aber zögert',
          'nicht umgesetzt trotz einsicht',
          'erkenntnis in entscheidung wandeln',
        ],
      },
      {
        label: 'Zustimmung vs. Commitment',
        aliases: [
          'zustimmung vs commitment',
          'ja aber kein commitment',
          'kunde stimmt zu aber kauft nicht',
          'nicken ohne zusage',
          'halbes ja',
          'unverbindliche zustimmung',
          'zustimmung ohne handlung',
          'kunde sagt ja vielleicht',
          'nicht festlegen trotz zustimmung',
          'interesse ohne commitment',
          'scheinbare zustimmung',
          'echtes commitment prüfen',
          'zustimmung verbindlich machen',
          'kunde bleibt offen',
          'ja sagen und nichts tun',
        ],
      },
      {
        label: 'Interesse vs. Verantwortungsübernahme',
        aliases: [
          'interesse vs verantwortung',
          'nur interessiert',
          'zuschauer statt macher',
          'kunde beobachtet nur',
          'interesse ohne handeln',
          'neugier aber keine verantwortung',
          'tourist oder käufer',
          'passiv oder aktiv',
          'kunde übernimmt keine verantwortung',
          'window shopper',
          'interessent oder kunde',
          'verantwortung aktivieren',
          'vom zuschauer zum spieler',
          'engagement prüfen',
          'interesse in verantwortung wandeln',
        ],
      },
      {
        label: 'Kontrolle vs. Delegation',
        aliases: [
          'kontrolle vs delegation',
          'kunde kann nicht loslassen',
          'alles selbst machen',
          'delegieren fällt schwer',
          'mikromanagement',
          'kontrollbedürfnis',
          'abgeben lernen',
          'vertrauen in team fehlt',
          'kontrolle abgeben',
          'nicht delegieren können',
          'alle zügel behalten',
          'loslassen im verkauf',
          'team nutzen',
          'macht abgeben',
          'selbst machen oder abgeben',
        ],
      },
      {
        label: 'Vermeidung vs. Klarheit',
        aliases: [
          'vermeidung vs klarheit',
          'kopf in den sand',
          'problem vermeiden',
          'realität nicht anschauen',
          'wegsehen',
          'nicht wahrhaben wollen',
          'vermeidungsverhalten',
          'klarheit vermeiden',
          'konfrontation vermeiden',
          'reality check',
          'kunde verdrängt problem',
          'thema ausblenden',
          'klarheit schaffen',
          'problem anschauen',
          'vermeidung auflösen',
        ],
      },
      {
        label: 'Vergleich vs. Entscheidung',
        aliases: [
          'vergleich vs entscheidung',
          'immer vergleichen nie entscheiden',
          'vergleichsparalyse',
          'zu viele optionen',
          'choice overload',
          'kunde vergleicht endlos',
          'optionsparalyse',
          'benchmark statt kauf',
          'vergleichsfixiert',
          'nicht entscheiden wegen alternativen',
          'vergleichsrausch',
          'vergleich stoppen',
          'entscheidung trotz optionen',
          'optionen überfordern',
          'vergleichen statt kaufen',
        ],
      },
      {
        label: 'Komfort vs. Wachstum',
        aliases: [
          'komfort vs wachstum',
          'komfortzone',
          'komfortzone verlassen',
          'wachstum ist unbequem',
          'kunde bleibt bequem',
          'gewohnheit blockiert wachstum',
          'routine hält zurück',
          'angst vor veränderung',
          'unbequem aber nötig',
          'sicherer hafen',
          'wachstumsschritt',
          'veränderung vermeiden',
          'neuland betreten',
          'weiterentwicklung trotz unbehagen',
          'komfort blockiert entscheidung',
        ],
      },
      {
        label: 'Kosten vs. Konsequenz',
        aliases: [
          'kosten vs konsequenz',
          'preis oder folgekosten',
          'billig wird teuer',
          'falsch sparen',
          'kostenfokus',
          'langfristig teurer',
          'nicht investieren kostet mehr',
          'preis vs folgeschaden',
          'kurzfristig sparen',
          'langfristig verlieren',
          'kosten des nichts tuns',
          'billigste lösung teuerste lösung',
          'sparsamkeit blockiert',
          'investition vermeiden',
          'kosten gegen konsequenz stellen',
        ],
      },
      {
        label: 'Später vs. Jetzt',
        aliases: [
          'später vs jetzt',
          'aufschieben',
          'prokrastination',
          'nicht heute',
          'irgendwann später',
          'kunde verschiebt',
          'später vielleicht',
          'jetzt oder später',
          'aufschieberitis',
          'morgen entscheiden',
          'nächste woche',
          'nächstes jahr',
          'zeit gewinnen',
          'entscheidung vertagen',
          'aufschieben hinterfragen',
        ],
      },
      {
        label: 'Kosten vs. Investment',
        aliases: [
          'kosten vs investment',
          'kosten oder investment',
          'ausgabe vs investition',
          'kostenblock',
          'reine kosten',
          'geld ausgeben',
          'investment statt kosten',
          'investition statt ausgabe',
          'kostenfokus',
          'geld ist weg',
          'ausgabe als verlust',
          'investitionsdenken',
          'kostenmentalität',
          'ausgabe oder wertschöpfung',
          'kosten in investment umdenken',
        ],
      },
      {
        label: 'Risiko vs. Chance',
        aliases: [
          'risiko vs chance',
          'risiko oder chance',
          'nur risiko sehen',
          'chance nicht sehen',
          'angst vor risiko',
          'risikofokus',
          'möglichkeit ausblenden',
          'negative erwartung',
          'was wenn es nicht funktioniert',
          'scheitern statt erfolg',
          'downside statt upside',
          'verlustchance',
          'chance trotz risiko',
          'risiko einseitig betrachten',
          'von risiko zu chance',
        ],
      },
      {
        label: 'Kurzfristig vs. Langfristig',
        aliases: [
          'kurzfristig vs langfristig',
          'kurzfristiges denken',
          'langfristiges denken',
          'zu kurzer zeithorizont',
          'nur kurzfristig betrachten',
          'nur die nächsten monate',
          'kurzfristiger fokus',
          'langfristige perspektive fehlt',
          'kurzfristig bewerten',
          'langfristigen wert übersehen',
          'heute statt zukunft',
          'kurzer betrachtungszeitraum',
          'langfristige auswirkung',
          'kurzfristiger aufwand langfristiger nutzen',
          'zeithorizont erweitern',
        ],
      },
      {
        label: 'Kurzfristiges Commitment vs. nachhaltiger Erfolg',
        aliases: [
          'kurzfristiges commitment vs nachhaltiger erfolg',
          'kurzes commitment',
          'langfristiges commitment',
          'zu kurz ausprobieren',
          'nur kurz testen',
          'ein monat testen',
          'zwei monate testen',
          'zu kurze laufzeit',
          'erfolg braucht zeit',
          'nachhaltiger erfolg braucht commitment',
          'commitment als erfolgsvoraussetzung',
          'langfristig dranbleiben',
          'zu früh aufhören',
          'kontinuität für erfolg',
          'dauerhafte umsetzung',
        ],
      },
    ],
  },
  {
    label: 'Storytelling (P TC+, P TO+, P CN+, P TS+, P SK-)',
    aliases: [
      'storytelling',
      'verkaufsstory',
      'geschichte im verkauf',
      'story im sales call',
      'metapher nutzen',
      'analogie nutzen',
      'kundenstory erzählen',
      'case story',
      'beispielgeschichte',
      'narrativ im verkauf',
      'story statt pitch',
      'vertrauen durch story',
      'einwand mit story lösen',
      'problem durch story erklären',
      'entscheidung durch story erleichtern',
    ],
    applications: [
      {
        label: 'Kundenstory (P TC+, P TO+, P SK-)',
        aliases: [
          'kundenstory',
          'customer story',
          'kundenbeispiel',
          'fallbeispiel',
          'case study',
          'praxisbeispiel',
          'referenzfall',
          'kundenerfahrung',
          'kundenbericht',
          'ähnlicher kunde',
          'beispielkunde',
          'kundencase',
          'erfahrungsbericht',
          'kunde wie sie',
          'vergleichbarer kundenfall',
        ],
      },
      {
        label: 'Before-After (P TO+, P CN+, P TS+)',
        aliases: [
          'before after',
          'vorher nachher',
          'vorher nachher story',
          'transformation story',
          'wandel zeigen',
          'früher heute vergleich',
          'dann und jetzt',
          'ausgangslage ergebnis',
          'turnaround story',
          'kontrastgeschichte',
          'vorher nachher effekt',
          'kundenwandel',
          'ergebnisveränderung',
          'transformation erzählen',
          'vom problem zur lösung',
        ],
      },
      {
        label: 'Founder Story (P TC+)',
        aliases: [
          'founder story',
          'gründergeschichte',
          'warum wir',
          'wie alles begann',
          'entstehungsgeschichte',
          'ursprungsgeschichte',
          'mission erzählen',
          'warum ich das mache',
          'hintergrundgeschichte',
          'persönliche geschichte',
          'startup story',
          'gründerstory',
          'unternehmensgeschichte',
          'vision erzählen',
          'herkunft der lösung',
        ],
      },
      {
        label: 'Metapher (P CN+, P SK-)',
        aliases: [
          'metapher',
          'metapher im verkauf',
          'bildlicher vergleich',
          'bildsprache',
          'gleichnis',
          'verkaufsmetapher',
          'problem bildlich erklären',
          'lösung bildlich erklären',
          'symbolischer vergleich',
          'einwand mit metapher',
          'komplexität mit bild erklären',
          'bildhafte erklärung',
          'anschauliches bild',
          'metaphorische frage',
          'verkaufsbild',
        ],
      },
      {
        label: 'Analogie (P CN+, P SK-)',
        aliases: [
          'analogie',
          'analogie im verkauf',
          'vergleich nutzen',
          'wie bei vergleich',
          'ähnlicher fall',
          'parallelbeispiel',
          'vergleichsargument',
          'analoger fall',
          'lösung mit analogie erklären',
          'problem mit analogie erklären',
          'verkaufsanalogie',
          'verständnis durch vergleich',
          'vergleichbare situation',
          'analogiefrage',
          'kompliziertes einfach erklären',
        ],
      },
      {
        label: 'Erfolgsgeschichte (P TC+, P TO+, P TS+, P SK-)',
        aliases: [
          'erfolgsgeschichte',
          'success story',
          'erfolgsstory',
          'erfolgsfall',
          'kundenerfolg',
          'durchbruchgeschichte',
          'turnaround beispiel',
          'best case',
          'erfolgsbeispiel',
          'result story',
          'gewinnstory',
          'ergebnisgeschichte',
          'kundenresultat erzählen',
          'showcase story',
          'proof story',
        ],
      },
    ],
  },

  {
    label: 'Realitätsabgleich (P TS-)',
    aliases: [
      'realitätsabgleich',
      'realität prüfen',
      'realitätscheck',
      'selbsteinschätzung kalibrieren',
      'eigenwahrnehmung kalibrieren',
      'überschätzung prüfen',
      'realistische einschätzung herstellen',
      'selbstbild mit realität abgleichen',
      'fähigkeiten realistisch einschätzen',
      'ressourcen realistisch einschätzen',
      'komplexität realistisch einschätzen',
      'differenzierung realistisch einschätzen',
      'situation nüchtern prüfen',
      'realitätsprüfung',
      'überhöhte selbsteinschätzung korrigieren',
    ],
    applications: [
      {
        label: 'Realitätswahrnehmung (P TS-)',
        aliases: [
          'realitätswahrnehmung',
          'realität wahrnehmen',
          'situation realistisch einschätzen',
          'status quo realistisch sehen',
          'eigene lage prüfen',
          'realitätscheck der situation',
          'selbsteinschätzung prüfen',
          'überschätzung der lage',
          'realistische standortbestimmung',
          'fakten gegen wahrnehmung',
          'wahrnehmung mit fakten abgleichen',
          'realität statt annahme',
          'lage nüchtern bewerten',
          'tatsächlichen zustand prüfen',
          'realitätsbild kalibrieren',
        ],
      },
      {
        label: 'Komplexitätswahrnehmung (P TS-)',
        aliases: [
          'komplexitätswahrnehmung',
          'komplexität realistisch einschätzen',
          'aufwand unterschätzt',
          'ist nicht so einfach',
          'komplexität unterschätzt',
          'abhängigkeiten sichtbar machen',
          'schwierigkeit realistisch prüfen',
          'anforderungen unterschätzt',
          'komplexität kalibrieren',
          'aufgabe wirkt zu einfach',
          'mehrschrittigkeit sichtbar machen',
          'komplexe zusammenhänge prüfen',
          'unterschätzte schwierigkeit',
          'realer komplexitätsgrad',
          'komplexität nicht unterschätzen',
        ],
      },
      {
        label: 'Fähigkeitswahrnehmung (P TS-)',
        aliases: [
          'fähigkeitswahrnehmung',
          'eigene fähigkeiten realistisch einschätzen',
          'kompetenz realistisch prüfen',
          'können wir das wirklich selbst',
          'fähigkeiten überschätzt',
          'know how prüfen',
          'kompetenzlücke sichtbar machen',
          'eigenes können kalibrieren',
          'umsetzungsfähigkeit prüfen',
          'interne expertise prüfen',
          'fähigkeitsgrenzen erkennen',
          'skill level realistisch einschätzen',
          'kompetenzüberschätzung',
          'eigene expertise hinterfragen',
          'reale fähigkeiten prüfen',
        ],
      },
      {
        label: 'Ressourcenwahrnehmung (P TS-)',
        aliases: [
          'ressourcenwahrnehmung',
          'ressourcen realistisch einschätzen',
          'kapazitäten prüfen',
          'haben wir wirklich genug ressourcen',
          'personalkapazität prüfen',
          'zeitressourcen prüfen',
          'budgetressourcen prüfen',
          'infrastruktur prüfen',
          'ressourcenüberschätzung',
          'verfügbare kapazität realistisch',
          'umsetzungsressourcen prüfen',
          'ressourcenlage kalibrieren',
          'verfügbarkeit realistisch einschätzen',
          'interne kapazität hinterfragen',
          'reale ressourcen prüfen',
        ],
      },
      {
        label: 'Differenzierungswahrnehmung (P TS-)',
        aliases: [
          'differenzierungswahrnehmung',
          'wir sind anders',
          'unsere branche ist anders',
          'unsere kunden sind anders',
          'sonderfall prüfen',
          'differenzierung realistisch einschätzen',
          'besonderheit hinterfragen',
          'tatsächlichen unterschied prüfen',
          'relevante differenzierung',
          'vermeintliche differenzierung',
          'andersartigkeit kalibrieren',
          'marktunterschied realistisch prüfen',
          'besonderheit mit realität abgleichen',
          'unterschied wirklich relevant',
          'differenzierungsüberschätzung',
        ],
      },
    ],
  },
  {
    label: 'Erwartungsverzerrung (P TO-)',
    aliases: [
      'erwartungsverzerrung',
      'erwartungen kalibrieren',
      'unrealistische erwartung',
      'überhöhte erwartung',
      'angebotserwartung korrigieren',
      'lösungsversprechen realistisch machen',
      'erwartungsmanagement',
      'falsche erwartung an lösung',
      'zu hohe erwartung',
      'realistische ergebnisannahme',
      'erwartung an umsetzung kalibrieren',
      'keine falschen versprechen',
      'lösungsgrenzen klären',
      'angebot realistisch einordnen',
      'erwartungsbild korrigieren',
    ],
    applications: [
      {
        label: 'Aufwand / Zeit (P TO-)',
        aliases: [
          'aufwand zeit',
          'aufwand unterschätzt',
          'zeitaufwand unterschätzt',
          'geht nicht von allein',
          'umsetzung braucht zeit',
          'implementierungsaufwand',
          'laufzeit realistisch machen',
          'zeit bis ergebnis',
          'aufwand realistisch einschätzen',
          'nicht sofort erledigt',
          'umsetzungsdauer kalibrieren',
          'eigenaufwand erklären',
          'zeitliche erwartung korrigieren',
          'arbeitsaufwand realistisch',
          'geschwindigkeit nicht überschätzen',
        ],
      },
      {
        label: 'Fähigkeiten (P TO-)',
        aliases: [
          'fähigkeiten der lösung',
          'lösung kann nicht alles',
          'funktionsumfang realistisch',
          'fähigkeiten nicht überschätzen',
          'systemgrenzen erklären',
          'was die lösung kann',
          'was die lösung nicht kann',
          'leistungsumfang kalibrieren',
          'funktionserwartung korrigieren',
          'angebot kann nicht alles',
          'fähigkeitsgrenzen der lösung',
          'leistungsversprechen realistisch',
          'produktleistung realistisch',
          'lösung korrekt einordnen',
          'capability expectation',
        ],
      },
      {
        label: 'Erfolgswahrscheinlichkeit (P TO-)',
        aliases: [
          'erfolgswahrscheinlichkeit',
          'erfolg nicht garantiert',
          'wahrscheinlichkeit realistisch',
          'erfolgschance kalibrieren',
          'kein sicherer erfolg',
          'ergebniswahrscheinlichkeit',
          'erfolg hängt von faktoren ab',
          'chance statt garantie',
          'realistische erfolgserwartung',
          'ergebnis nicht versprechen',
          'erfolgsaussicht einordnen',
          'wahrscheinlichkeit statt sicherheit',
          'erfolg nicht automatisch',
          'realistische resultaterwartung',
          'erfolgschance nicht überschätzen',
        ],
      },
      {
        label: 'Garantien (P TO-)',
        aliases: [
          'garantien',
          'garantieerwartung',
          'keine erfolgsgarantie',
          'garantiegrenzen',
          'was wird garantiert',
          'was wird nicht garantiert',
          'garantie realistisch einordnen',
          'keine falsche garantie',
          'garantieumfang klären',
          'garantie nicht überinterpretieren',
          'vertragliche garantie klären',
          'garantieversprechen begrenzen',
          'ergebnisgarantie korrigieren',
          'garantieerwartung kalibrieren',
          'garantien transparent machen',
        ],
      },
      {
        label: 'Sicherheiten (P TO-)',
        aliases: [
          'sicherheiten',
          'sicherheitserwartung',
          'kein nullrisiko',
          'vollständige sicherheit gibt es nicht',
          'restrisiko klären',
          'absicherung realistisch',
          'sicherheitsgrenzen',
          'risiko bleibt bestehen',
          'keine absolute sicherheit',
          'sicherheitsversprechen kalibrieren',
          'absicherung nicht überschätzen',
          'risiko realistisch einordnen',
          'kontrolle nicht vollständig',
          'sicherheitsniveau erklären',
          'sicherheitserwartung korrigieren',
        ],
      },
    ],
  },
  {
    label: 'Lösungsbewusstsein (P CC-)',
    aliases: [
      'lösungsbewusstsein',
      'lösung wieder sichtbar machen',
      'hoffnung herstellen',
      'ausweg sichtbar machen',
      'problem ist lösbar',
      'handlungsfähigkeit zurückgeben',
      'aus hoffnungslosigkeit lösen',
      'lösbarkeit zeigen',
      'weg nach vorne öffnen',
      'problem nicht überwältigend',
      'wieder möglichkeiten sehen',
      'lösungsraum öffnen',
      'resignation lösen',
      'zuversicht in lösbarkeit',
      'vom problem zur lösung',
    ],
    applications: [
      {
        label: 'Lösbarkeit (P CC-)',
        aliases: [
          'lösbarkeit',
          'problem ist lösbar',
          'lösungsweg existiert',
          'es gibt eine lösung',
          'machbarkeit sichtbar machen',
          'lösungsansatz zeigen',
          'lösbar statt aussichtslos',
          'problem kann gelöst werden',
          'konkrete lösbarkeit',
          'machbare lösung',
          'lösung realistisch erreichbar',
          'lösungsmöglichkeit',
          'ausweg existiert',
          'lösbarkeit verankern',
          'machbarkeit herstellen',
        ],
      },
      {
        label: 'Hoffnung (P CC-)',
        aliases: [
          'hoffnung',
          'hoffnung herstellen',
          'zuversicht',
          'nicht hoffnungslos',
          'positive aussicht',
          'wieder perspektive sehen',
          'resignation lösen',
          'mut machen',
          'zukunft ist veränderbar',
          'aussicht auf verbesserung',
          'hoffnung auf lösung',
          'optimismus realistisch',
          'zuversicht zurückgeben',
          'ausweglosigkeit lösen',
          'hoffnung statt resignation',
        ],
      },
      {
        label: 'Handlungsfähigkeit (P CC-)',
        aliases: [
          'handlungsfähigkeit',
          'wieder handeln können',
          'handlungsoptionen sehen',
          'nicht ausgeliefert sein',
          'nächster machbarer hebel',
          'kontrolle zurückgewinnen',
          'aktive möglichkeit',
          'etwas tun können',
          'handlungsspielraum',
          'wirksamer schritt möglich',
          'nicht hilflos',
          'gestaltbarkeit zeigen',
          'handlungsraum öffnen',
          'wieder steuerbar',
          'handlungsfähigkeit herstellen',
        ],
      },
      {
        label: 'Fortschritt (P CC-)',
        aliases: [
          'fortschritt',
          'kleiner fortschritt',
          'schrittweise verbesserung',
          'nicht alles auf einmal',
          'entwicklung ist möglich',
          'erste verbesserung sichtbar',
          'fortschritt statt perfektion',
          'schritt für schritt',
          'machbare etappen',
          'teilfortschritt',
          'bewegung nach vorne',
          'verbesserungspfad',
          'fortschritt realistisch',
          'entwicklung möglich',
          'positive bewegung',
        ],
      },
      {
        label: 'Zielerreichbarkeit (P CC-)',
        aliases: [
          'zielerreichbarkeit',
          'ziel ist erreichbar',
          'realistisches ziel',
          'ziel nicht unerreichbar',
          'erreichbarkeit zeigen',
          'zielpfad sichtbar machen',
          'machbares ziel',
          'realistische zielerreichung',
          'ziel in reichweite',
          'ziel kann erreicht werden',
          'ergebnis ist möglich',
          'ziel nicht aufgeben',
          'erreichbares ergebnis',
          'realistische erfolgsperspektive',
          'zielerreichung plausibel',
        ],
      },
      {
        label: 'Ausweg / Optionen (P CC-)',
        aliases: [
          'ausweg optionen',
          'ausweg finden',
          'optionen sichtbar machen',
          'alternative wege',
          'mehrere lösungswege',
          'nicht in sackgasse',
          'es gibt optionen',
          'wahlmöglichkeiten öffnen',
          'alternativen sehen',
          'handlungsalternativen',
          'ausweg aus problem',
          'optionen statt ausweglosigkeit',
          'lösungsoptionen',
          'mögliche wege',
          'neue optionen erkennen',
        ],
      },
    ],
  },
] as const;



export const TECHNIC_LABELS_DE = {
  "Vorabschlusstechnik": {
    "label": "Vorabschlusstechnik",
    "aliases": [
      "vorabschlusstechnik",
      "vorabschluss technik",
      "vorabschluss",
      "vorabschlussfrage",
      "vorabschluss frage",
      "vorabschlussprüfung",
      "vorabschluss prüfung",
      "pre close",
      "pre-close",
      "pre close technique",
      "pre-close technique",
      "trial close",
      "trial-close"
    ],
    "applications": {
      "Vorabschluss": {
        "label": "Vorabschluss",
        "aliases": [
          "vorabschluss",
          "vorabschluss durchführen",
          "vorabschluss einleiten",
          "vorabschluss prüfen",
          "vorabschlussfrage stellen",
          "gesamtfit prüfen",
          "gesamt fit prüfen",
          "lösung als gesamtheit prüfen",
          "zustimmung zur gesamtlösung prüfen",
          "passung der lösung prüfen",
          "lösung zusammenfassen und prüfen",
          "pre close",
          "pre-close",
          "trial close",
          "trial-close"
        ]
      }
    }
  },
  "Präsentations Technik": {
    "label": "Präsentations Technik",
    "aliases": [
      "präsentations technik",
      "präsentationstechnik",
      "präsentation",
      "praesentation",
      "angebot präsentieren",
      "angebot vorstellen",
      "lösung präsentieren",
      "lösung vorstellen",
      "leistungspräsentation",
      "angebotspräsentation",
      "sales präsentation",
      "sales presentation",
      "presentation technique",
      "offer presentation",
      "solution presentation"
    ],
    "applications": {
      "Präsentations Technik": {
        "label": "Präsentations Technik",
        "aliases": [
          "präsentations technik",
          "präsentationstechnik",
          "präsentation",
          "angebot präsentieren",
          "lösung präsentieren",
          "angebotspräsentation",
          "leistungspräsentation",
          "sales präsentation",
          "presentation technique",
          "offer presentation"
        ]
      }
    }
  },
  "ROI Rechnung": {
    "label": "ROI Rechnung",
    "aliases": [
      "roi rechnung",
      "roi berechnung",
      "roi kalkulation",
      "roi abgleich",
      "roi annahmen abgleichen",
      "roi werte abgleichen",
      "roi berechnungsbasis abgleichen",
      "return on investment",
      "return on investment rechnung",
      "return on investment berechnung",
      "wirtschaftlichkeitsrechnung",
      "wirtschaftlichkeitsberechnung",
      "rentabilitätsrechnung",
      "rentabilitätsberechnung",
      "investment rechnen",
      "return berechnen",
      "wirtschaftlichen return berechnen",
      "wirtschaftliche annahmen abgleichen",
      "wirtschaftliche werte abgleichen",
      "berechnungsbasis abgleichen",
      "roi calculation",
      "return calculation"
    ],
    "applications": {
      "ROI Rechnung": {
        "label": "ROI Rechnung",
        "aliases": [
          "roi rechnung",
          "roi berechnung",
          "roi kalkulation",
          "return on investment",
          "wirtschaftlichkeitsrechnung",
          "rentabilitätsrechnung",
          "investment rechnen",
          "return berechnen",
          "roi calculation",
          "return calculation"
        ]
      },
      "ROI Abgleich": {
        "label": "ROI Abgleich",
        "aliases": [
          "roi abgleich",
          "roi annahmen abgleichen",
          "roi werte abgleichen",
          "roi berechnungsbasis abgleichen",
          "wirtschaftliche annahmen abgleichen",
          "wirtschaftliche werte abgleichen",
          "berechnungsbasis abgleichen"
        ]
      }
    }
  },
  "Kaufsignal": {
    "label": "Kaufsignal",
    "aliases": [
      "kaufsignal",
      "kaufsignale",
      "kauf signal",
      "kauf signale",
      "buying signal",
      "buying signals",
      "buy signal",
      "buy signals",
      "purchase signal",
      "purchase signals",
      "kaufinteresse",
      "konkretes kaufinteresse",
      "kaufbereitschaft",
      "konkrete kaufbereitschaft",
      "kaufabsicht",
      "konkrete kaufabsicht",
      "kaufintention",
      "konkrete kaufintention",
      "abschlussinteresse",
      "abschlussbereitschaft",
      "entscheidungsinteresse",
      "entscheidungsbereitschaft",
      "interesse an zusammenarbeit",
      "interesse an der zusammenarbeit",
      "interesse an möglicher zusammenarbeit",
      "interesse an einer zusammenarbeit",
      "zusammenarbeitsinteresse",
      "gedankliche vorwegnahme",
      "gedankliche vorwegnahme der zusammenarbeit",
      "gedankliche vorwegnahme einer zusammenarbeit",
      "gedankliche vorwegnahme möglicher zusammenarbeit",
      "mentale vorwegnahme der zusammenarbeit",
      "mentale vorwegnahme einer zusammenarbeit",
      "prospect denkt zusammenarbeit voraus",
      "prospect denkt mögliche zusammenarbeit voraus",
      "prospect stellt sich zusammenarbeit vor",
      "prospect stellt sich mögliche zusammenarbeit vor",
      "kunde denkt zusammenarbeit voraus",
      "kunde denkt mögliche zusammenarbeit voraus",
      "kunde stellt sich zusammenarbeit vor",
      "kunde stellt sich mögliche zusammenarbeit vor",
      "signal für mögliche zusammenarbeit",
      "signal für zusammenarbeit",
      "signal zur zusammenarbeit",
      "signal für kaufbereitschaft",
      "signal für kaufinteresse",
      "signal für kaufabsicht",
      "signal für entscheidungsbereitschaft",
      "buying intent signal",
      "purchase intent signal",
      "buying intent",
      "purchase intent",
      "buying intention",
      "purchase intention"
    ],
    "applications": {
      "Kaufsignal als Frage": {
        "label": "Kaufsignal als Frage",
        "aliases": [
          "kaufsignal als frage",
          "kaufsignal frage",
          "kaufsignal-frage",
          "kauffrage",
          "kaufinteressierte frage",
          "frage mit kaufsignal",
          "frage als kaufsignal",
          "frage mit kaufinteresse",
          "frage mit kaufabsicht",
          "frage mit kaufintention",
          "frage mit kaufbereitschaft",
          "frage zur zusammenarbeit",
          "frage zur möglichen zusammenarbeit",
          "frage über die zusammenarbeit",
          "frage über mögliche zusammenarbeit",
          "frage zum ablauf der zusammenarbeit",
          "frage zur ausgestaltung der zusammenarbeit",
          "frage zur gestaltung der zusammenarbeit",
          "frage zur umsetzung der zusammenarbeit",
          "frage zur nutzung der lösung",
          "frage zur konkreten nutzung",
          "frage zur konkreten umsetzung",
          "frage zum konkreten ablauf",
          "prospect fragt zur zusammenarbeit",
          "prospect fragt nach zusammenarbeit",
          "prospect fragt nach möglicher zusammenarbeit",
          "prospect fragt wie zusammenarbeit aussieht",
          "prospect fragt wie zusammenarbeit abläuft",
          "prospect fragt wie umsetzung aussieht",
          "prospect fragt wie umsetzung abläuft",
          "prospect fragt wie betreuung aussieht",
          "prospect fragt wie betreuung abläuft",
          "kunde fragt zur zusammenarbeit",
          "kunde fragt nach zusammenarbeit",
          "kunde fragt nach möglicher zusammenarbeit",
          "kunde fragt wie zusammenarbeit aussieht",
          "kunde fragt wie zusammenarbeit abläuft",
          "kunde fragt wie umsetzung aussieht",
          "kunde fragt wie umsetzung abläuft",
          "kunde fragt wie betreuung aussieht",
          "kunde fragt wie betreuung abläuft",
          "gedankliche vorwegnahme als frage",
          "vorwegnahme der zusammenarbeit als frage",
          "vorwegnahme möglicher zusammenarbeit als frage",
          "buying signal question",
          "buying question",
          "purchase signal question",
          "buying intent question",
          "purchase intent question"
        ]
      },
      "Kaufsignal als Aussage": {
        "label": "Kaufsignal als Aussage",
        "aliases": [
          "kaufsignal als aussage",
          "kaufsignal aussage",
          "kaufsignal-aussage",
          "kaufaussage",
          "aussage mit kaufsignal",
          "aussage als kaufsignal",
          "aussage mit kaufinteresse",
          "aussage mit kaufabsicht",
          "aussage mit kaufintention",
          "aussage mit kaufbereitschaft",
          "aussage zur zusammenarbeit",
          "aussage zur möglichen zusammenarbeit",
          "aussage über die zusammenarbeit",
          "aussage über mögliche zusammenarbeit",
          "anforderung an zusammenarbeit",
          "anforderung an die zusammenarbeit",
          "anforderung an mögliche zusammenarbeit",
          "wunsch an zusammenarbeit",
          "wunsch an die zusammenarbeit",
          "wunsch an mögliche zusammenarbeit",
          "erwartung an zusammenarbeit",
          "erwartung an die zusammenarbeit",
          "erwartung an mögliche zusammenarbeit",
          "präferenz für zusammenarbeit",
          "präferenz zur zusammenarbeit",
          "präferenz bei zusammenarbeit",
          "vorstellung zur zusammenarbeit",
          "vorstellung von zusammenarbeit",
          "vorstellung von möglicher zusammenarbeit",
          "prospect beschreibt zusammenarbeit",
          "prospect beschreibt gewünschte zusammenarbeit",
          "prospect beschreibt mögliche zusammenarbeit",
          "prospect äußert wunsch zur zusammenarbeit",
          "prospect äußert erwartung zur zusammenarbeit",
          "prospect äußert anforderung zur zusammenarbeit",
          "prospect äußert präferenz zur zusammenarbeit",
          "prospect sagt was ihm bei zusammenarbeit wichtig ist",
          "kunde beschreibt zusammenarbeit",
          "kunde beschreibt gewünschte zusammenarbeit",
          "kunde beschreibt mögliche zusammenarbeit",
          "kunde äußert wunsch zur zusammenarbeit",
          "kunde äußert erwartung zur zusammenarbeit",
          "kunde äußert anforderung zur zusammenarbeit",
          "kunde äußert präferenz zur zusammenarbeit",
          "kunde sagt was ihm bei zusammenarbeit wichtig ist",
          "gedankliche vorwegnahme als aussage",
          "vorwegnahme der zusammenarbeit als aussage",
          "vorwegnahme möglicher zusammenarbeit als aussage",
          "buying signal statement",
          "buying statement",
          "purchase signal statement",
          "buying intent statement",
          "purchase intent statement"
        ]
      }
    }
  },
  "Gegenfrage": {
    "label": "Gegenfrage",
    "aliases": [
      "gegenfrage",
      "gegenfragen",
      "rückfrage",
      "rückfragen",
      "counter question",
      "prospect question"
    ],
    "applications": {
      "Reaktion auf Inhaltsfrage": {
        "label": "Reaktion auf Inhaltsfrage",
        "aliases": [
          "inhaltlich antworten",
          "inhaltlich reagieren",
          "frage beantworten",
          "rückfrage beantworten",
          "gegenfrage beantworten",
          "sachlich antworten",
          "direkt antworten",
          "auf die frage eingehen",
          "auf die rückfrage eingehen",
          "inhaltlich aufgreifen",
          "sachlich darauf eingehen",
          "antwort zum thema geben",
          "inhaltliche antwort geben",
          "fachlich antworten"
        ]
      },
      "Reaktion auf Kontrollgewinn": {
        "label": "Reaktion auf Kontrollgewinn",
        "aliases": [
          "kontrolle zurückholen",
          "gesprächskontrolle zurückholen",
          "führung zurückgewinnen",
          "gesprächsführung übernehmen",
          "führung wieder übernehmen",
          "gespräch wieder führen",
          "gespräch zurücklenken",
          "gespräch aktiv lenken",
          "kontrolle übernehmen",
          "richtung wieder vorgeben",
          "führung übernehmen",
          "gespräch steuern",
          "initiative zurückholen",
          "steuerung zurückgewinnen"
        ]
      },
      "Reaktion auf Angriff / Machtdemonstration": {
        "label": "Reaktion auf Angriff / Machtdemonstration",
        "aliases": [
          "angreifend reagieren",
          "mit angriff reagieren",
          "dominant reagieren",
          "macht demonstrieren",
          "dominanz zeigen",
          "konfrontativ reagieren",
          "provokant reagieren",
          "druck aufbauen",
          "gegenangriff starten",
          "zurückschießen",
          "hart kontern",
          "dominant kontern",
          "herausfordernd reagieren",
          "machtposition zeigen"
        ]
      },
      "Reaktion auf unpassende Frage": {
        "label": "Reaktion auf unpassende Frage",
        "aliases": [
          "unpassend reagieren",
          "unangemessen reagieren",
          "am thema vorbeireagieren",
          "nicht auf die frage eingehen",
          "unpassend antworten",
          "irrelevant antworten",
          "sachfremd reagieren",
          "kontextfremd antworten",
          "falsch auf die frage reagieren",
          "unangebrachte antwort geben",
          "am kontext vorbeigehen",
          "nicht passend antworten",
          "themenfremd reagieren",
          "unangemessen antworten"
        ]
      },
      "Pattern Interrupt": {
        "label": "Pattern Interrupt",
        "aliases": [
          "pattern interrupt einsetzen",
          "muster unterbrechen",
          "gesprächsmuster brechen",
          "reaktionsmuster unterbrechen",
          "unerwartet reagieren",
          "überraschend reagieren",
          "mit musterbruch reagieren",
          "routine durchbrechen",
          "gesprächsfluss bewusst brechen",
          "unerwarteten impuls setzen",
          "kommunikationsmuster brechen",
          "mit überraschung reagieren",
          "musterwechsel einsetzen",
          "pattern break einsetzen"
        ]
      },
      "Reaktion auf Ablenkungsmanöver": {
        "label": "Reaktion auf Ablenkungsmanöver",
        "aliases": [
          "ablenken",
          "mit ablenkung reagieren",
          "vom thema wegführen",
          "thema verschieben",
          "gespräch umlenken",
          "auf ein anderes thema wechseln",
          "frage umgehen",
          "rückfrage umgehen",
          "gegenfrage umgehen",
          "ausweichend reagieren",
          "thema bewusst wechseln",
          "auf anderes thema lenken",
          "antwort vermeiden",
          "mit ausweichmanöver reagieren",
          "gespräch bewusst weglenken"
        ]
      }
    }
  },
  "Handlungsimpuls (P CN+, P CC+, P CO+)": {
    "label": "Handlungsimpuls",
    "aliases": [
      "handlungsimpuls",
      "aktion auslösen",
      "kunde soll handeln",
      "ins handeln bringen",
      "entscheidung aktivieren",
      "nächsten schritt auslösen",
      "abschluss vorbereiten",
      "verbindlichkeit herstellen",
      "umsetzung anstoßen",
      "entscheidungsbereitschaft erhöhen",
      "handlungsbereitschaft prüfen",
      "vom interesse zur aktion",
      "kunde kommt in bewegung",
      "verkaufsgespräch aktivieren",
      "entscheidung nicht vertagen"
    ],
    "applications": {
      "Dringlichkeit (P CC+, P CO+)": {
        "label": "Dringlichkeit",
        "aliases": [
          "dringlichkeit erzeugen",
          "dringlichkeit aufbauen",
          "warum jetzt handeln",
          "kunde wartet zu lange",
          "nicht länger aufschieben",
          "zeitdruck im verkauf",
          "zeitliche konsequenz zeigen",
          "kosten des wartens",
          "dringenden grund herausarbeiten",
          "jetzt handeln begründen",
          "status quo wird teurer",
          "aufschieben hinterfragen",
          "warteschaden aufzeigen",
          "handlungsdruck ohne push",
          "zeitkritische entscheidung"
        ]
      },
      "Commitment (P CO+)": {
        "label": "Commitment",
        "aliases": [
          "commitment holen",
          "commitment abfragen",
          "verbindlichkeit schaffen",
          "zusage einholen",
          "echtes ja prüfen",
          "kunde soll sich festlegen",
          "verbindlichen nächsten schritt sichern",
          "abschlussbereitschaft testen",
          "interesse oder commitment",
          "kunde meint es ernst",
          "entscheidung verbindlich machen",
          "halbes ja erkennen",
          "unverbindlichkeit klären",
          "commitment frage",
          "verbindlichkeit ohne druck"
        ]
      },
      "Nächster Schritt (P CO+)": {
        "label": "Nächster Schritt",
        "aliases": [
          "nächsten schritt vereinbaren",
          "next step vereinbaren",
          "follow up setzen",
          "folgeaktion festlegen",
          "nächsten termin machen",
          "anschlusstermin sichern",
          "weiteres vorgehen klären",
          "call sauber abschließen",
          "konkrete nächste aktion",
          "verkaufsgespräch nicht offen lassen",
          "prozess nach dem call",
          "nächste phase einleiten",
          "entscheidungspfad festlegen",
          "gesprächsergebnis sichern",
          "weiteres Vorgehen vereinbaren"
        ]
      },
      "Eigenverantwortung (P CO+)": {
        "label": "Eigenverantwortung",
        "aliases": [
          "eigenverantwortung aktivieren",
          "verantwortung beim kunden lassen",
          "kunde übernimmt verantwortung",
          "verantwortungsfrage stellen",
          "entscheidung liegt beim kunden",
          "kunde erkennt eigene rolle",
          "selbstverantwortung im verkauf",
          "verantwortung spiegeln",
          "nicht handeln verantwortung",
          "kunde ist zuständig",
          "ownership frage",
          "verantwortung ohne schuldzuweisung",
          "eigenanteil erkennen lassen",
          "entscheidung selbst treffen lassen",
          "verantwortungsübernahme fördern"
        ]
      },
      "Entscheidungsimpuls (P CN+, P CO+)": {
        "label": "Entscheidungsimpuls",
        "aliases": [
          "entscheidung anstoßen",
          "entscheidung auslösen",
          "kunde soll entscheiden",
          "entscheidungsfrage stellen",
          "entscheidungsklarheit schaffen",
          "unentschlossenheit lösen",
          "zögern beenden",
          "entscheidung nicht vertagen",
          "abschlussimpuls setzen",
          "entscheidungsbereitschaft erhöhen",
          "entscheidungspunkt markieren",
          "kunde trifft klare wahl",
          "entscheidungsblockade lösen",
          "entscheiden ohne druck",
          "abschluss vorbereiten"
        ]
      },
      "Dynamik (P CO+)": {
        "label": "Dynamik",
        "aliases": [
          "gesprächsdynamik halten",
          "momentum halten",
          "verkaufsfluss halten",
          "call in bewegung halten",
          "energie im gespräch erhöhen",
          "kunde verliert interesse",
          "dynamik nach einwand zurückholen",
          "gespräch wieder aktivieren",
          "momentum nach bedarf nutzen",
          "verkaufsgespräch stockt",
          "interesse warm halten",
          "entscheidung kühlt ab",
          "call vorwärts führen",
          "verkaufsprozess beschleunigen",
          "kunde bleibt passiv"
        ]
      },
      "Konsequenz (P CC+)": {
        "label": "Konsequenz",
        "aliases": [
          "konsequenzfrage stellen",
          "folgen nicht handeln",
          "was passiert wenn nichts passiert",
          "kosten des nicht handelns",
          "auswirkungen klären",
          "problemfolgen vertiefen",
          "status quo konsequenz",
          "nichtentscheidung hinterfragen",
          "risiko des wartens",
          "folgeschaden aufzeigen",
          "langfristige konsequenz",
          "wartepreis sichtbar machen",
          "kunde spricht folgen aus",
          "konsequenz des zögerns",
          "implikationen fragen"
        ]
      },
      "Zeitfenster (P CC+, P CO+)": {
        "label": "Zeitfenster",
        "aliases": [
          "zeitfenster nutzen",
          "opportunity window",
          "passender zeitpunkt",
          "jetzt ist der richtige zeitpunkt",
          "zeitlich begrenzte chance",
          "terminfenster sichern",
          "timing für entscheidung",
          "gelegenheit nicht verpassen",
          "aktuelles fenster klären",
          "saisonales zeitfenster",
          "zeitpunkt für umsetzung",
          "warum jetzt starten",
          "momentum zeitlich nutzen",
          "fenster läuft ab",
          "günstiger entscheidungszeitpunkt"
        ]
      },
      "Aktivierung (P CO+)": {
        "label": "Aktivierung",
        "aliases": [
          "kunde aktivieren",
          "umsetzung starten",
          "erste aktion auslösen",
          "ins tun bringen",
          "vom verstehen ins handeln",
          "startimpuls setzen",
          "handlungsbereitschaft aktivieren",
          "aktion nach erkenntnis",
          "umsetzungsimpuls",
          "kunde beginnt",
          "startentscheidung sichern",
          "erste handlung vereinbaren",
          "aktivierungsfrage",
          "nicht mehr abwarten",
          "handlung nach dem call"
        ]
      }
    }
  },
  "Einwand (Wert)": {
    "label": "Einwand (Wert)",
    "aliases": [
      "werteinwand",
      "wert nicht erkannt",
      "mehrwert nicht erkannt",
      "nutzen nicht erkannt",
      "gegenwert nicht erkannt"
    ],
    "applications": {
      "Wettbewerber": {
        "label": "Wettbewerber",
        "aliases": [
          "wettbewerber einwand",
          "konkurrenzangebot",
          "anderer anbieter",
          "kunde vergleicht",
          "muss noch vergleichen",
          "vergleichsangebot",
          "anbieter vergleichen",
          "alternative lösung",
          "marktvergleich",
          "kunde prüft mitbewerber",
          "differenzierung gegen konkurrenz",
          "andere optionen offen",
          "wettbewerbsvergleich"
        ]
      },
      "Bedarf": {
        "label": "Bedarf",
        "aliases": [
          "kein bedarf",
          "brauchen wir nicht",
          "nicht relevant",
          "kein problem",
          "alles läuft",
          "kein interesse",
          "bedarfseinwand",
          "kunde sieht keinen nutzen",
          "kunde erkennt problem nicht",
          "problemwahrnehmung fehlt",
          "keine notwendigkeit",
          "nicht passend für uns",
          "haben wir schon gelöst",
          "nutzen nicht klar",
          "bedarf herausarbeiten",
          "preis rechtfertigen",
          "wert statt preis",
          "kosten nutzen klären",
          "kunde versteht es nicht",
          "angebot ist unübersichtlich",
          "einfacher erklären",
          "verständnisproblem",
          "nicht greifbar",
          "kunde braucht vereinfachung"
        ]
      },
      "Priorität": {
        "label": "Priorität",
        "aliases": [
          "keine priorität",
          "nicht wichtig genug",
          "steht nicht auf agenda",
          "andere projekte wichtiger",
          "später priorisieren",
          "nachrangiges thema",
          "wird zurückgestellt",
          "prioritätseinwand",
          "gerade nicht oben",
          "nicht dringend",
          "kunde verschiebt priorität",
          "projekt hat keine priorität",
          "interne prioritäten",
          "thema liegt auf eis",
          "priorität klären",
          "budgetpriorität"
        ]
      }
    }
  },
  "Einwand (Budget/Preis)": {
    "label": "Einwand (Budget/Preis)",
    "aliases": [
      "finanzieller einwand",
      "finanzielle belastung",
      "preis oder budget als blocker",
      "finanzielle machbarkeit"
    ],
    "applications": {
      "Preis": {
        "label": "Preis",
        "aliases": [
          "preiseinwand",
          "zu teuer",
          "preis ist zu hoch",
          "kosten einwand",
          "kunde fragt nach rabatt",
          "honorar zu hoch",
          "preisbedenken",
          "kunde zweifelt am preis",
          "investition zu hoch",
          "preiswiderstand",
          "andere sind günstiger",
          "konkurrenz ist billiger"
        ]
      },
      "Budget": {
        "label": "Budget",
        "aliases": [
          "kein budget",
          "budgeteinwand",
          "budget aufgebraucht",
          "finanzierung offen",
          "nicht eingeplant",
          "budgetjahr vorbei",
          "kostenstelle blockiert",
          "kein geld da",
          "kauf scheitert an budget",
          "investitionsstopp",
          "kann ich mir nicht leisten"
        ]
      }
    }
  },
  "Einwand (Logistik)": {
    "label": "Einwand (Logistik)",
    "aliases": [
      "logistischer einwand",
      "ressourcen fehlen",
      "mitarbeiter fehlen",
      "personal fehlt",
      "kompetenzen fehlen",
      "skills fehlen",
      "geräte fehlen",
      "maschinen fehlen",
      "infrastruktur fehlt",
      "operative ressourcen fehlen",
      "umsetzungsressourcen fehlen"
    ],
    "applications": {
      "Personelle Kapazität fehlt": {
        "label": "Personelle Kapazität fehlt",
        "aliases": [
          "zu wenig personal",
          "keine mitarbeiter verfügbar",
          "uns fehlen mitarbeiter",
          "personelle kapazitäten fehlen",
          "team ist unterbesetzt",
          "keine freien mitarbeiter",
          "personaldecke ist zu dünn",
          "zusätzliche manpower fehlt",
          "wir haben niemanden dafür",
          "keine personellen ressourcen",
          "teamkapazität reicht nicht",
          "mitarbeiter sind vollständig ausgelastet"
        ]
      },
      "Fachkompetenz und Know-how fehlen": {
        "label": "Fachkompetenz und Know-how fehlen",
        "aliases": [
          "know-how fehlt",
          "fachwissen fehlt",
          "kompetenz fehlt",
          "qualifikation fehlt",
          "skills fehlen",
          "erfahrung fehlt",
          "niemand kann das umsetzen",
          "intern fehlt die expertise",
          "spezialwissen ist nicht vorhanden",
          "team ist dafür nicht qualifiziert",
          "fachliche ressourcen fehlen",
          "uns fehlt ein experte"
        ]
      },
      "Technische Ausstattung fehlt": {
        "label": "Technische Ausstattung fehlt",
        "aliases": [
          "geräte fehlen",
          "maschinen fehlen",
          "werkzeuge fehlen",
          "technische ausstattung fehlt",
          "hardware fehlt",
          "equipment fehlt",
          "anlagen fehlen",
          "keine passenden geräte vorhanden",
          "maschinenkapazität fehlt",
          "benötigte technik ist nicht vorhanden",
          "notwendige werkzeuge fehlen",
          "ausstattung reicht nicht aus"
        ]
      },
      "IT- und Systeminfrastruktur fehlt": {
        "label": "IT- und Systeminfrastruktur fehlt",
        "aliases": [
          "it-infrastruktur fehlt",
          "systeme fehlen",
          "software fehlt",
          "schnittstellen fehlen",
          "technische integration fehlt",
          "netzwerk reicht nicht aus",
          "serverkapazität fehlt",
          "digitale infrastruktur fehlt",
          "systemlandschaft ist nicht vorbereitet",
          "keine passende plattform vorhanden",
          "bestehende systeme unterstützen das nicht",
          "technische voraussetzungen fehlen"
        ]
      },
      "Räumliche und betriebliche Infrastruktur fehlt": {
        "label": "Räumliche und betriebliche Infrastruktur fehlt",
        "aliases": [
          "räumlichkeiten fehlen",
          "platz fehlt",
          "lagerfläche fehlt",
          "produktionsfläche fehlt",
          "bürofläche fehlt",
          "standortkapazität fehlt",
          "keine geeigneten räume vorhanden",
          "betriebliche infrastruktur fehlt",
          "vorhandene fläche reicht nicht aus",
          "lager ist bereits voll",
          "räumliche kapazitäten fehlen",
          "standort ist dafür nicht ausgelegt"
        ]
      },
      "Material und Waren fehlen": {
        "label": "Material und Waren fehlen",
        "aliases": [
          "material fehlt",
          "rohstoffe fehlen",
          "waren fehlen",
          "bestand reicht nicht aus",
          "lieferfähigkeit fehlt",
          "nachschub fehlt",
          "teile fehlen",
          "komponenten fehlen",
          "materialverfügbarkeit ist nicht gegeben",
          "lieferkette reicht nicht aus",
          "keine ausreichenden vorräte vorhanden",
          "beschaffung ist nicht gesichert"
        ]
      },
      "Projekt- und Umsetzungsressourcen fehlen": {
        "label": "Projekt- und Umsetzungsressourcen fehlen",
        "aliases": [
          "projektressourcen fehlen",
          "umsetzungskapazität fehlt",
          "keiner kann das projekt übernehmen",
          "projektteam fehlt",
          "ressourcen für die einführung fehlen",
          "keine kapazität für die umsetzung",
          "implementierungsressourcen fehlen",
          "wir können das nicht einführen",
          "projektverantwortlicher fehlt",
          "für die umsetzung fehlt das team",
          "rollout-kapazität fehlt",
          "interne projektkapazität reicht nicht aus"
        ]
      },
      "Operative Kapazität fehlt": {
        "label": "Operative Kapazität fehlt",
        "aliases": [
          "im tagesgeschäft nicht umsetzbar",
          "operative kapazität fehlt",
          "betrieb ist vollständig ausgelastet",
          "prozesse lassen das nicht zu",
          "keine freien ressourcen im betrieb",
          "laufender betrieb hat keine kapazität",
          "organisation kann das nicht aufnehmen",
          "im alltag nicht integrierbar",
          "betriebliche abläufe sind ausgelastet",
          "operativ nicht leistbar",
          "prozesskapazität fehlt",
          "team ist im tagesgeschäft gebunden"
        ]
      },
      "Externe Partner und Dienstleister fehlen": {
        "label": "Externe Partner und Dienstleister fehlen",
        "aliases": [
          "dienstleister fehlt",
          "externer partner fehlt",
          "lieferant fehlt",
          "subunternehmer fehlt",
          "implementierungspartner fehlt",
          "kein geeigneter anbieter vorhanden",
          "uns fehlt ein fachpartner",
          "keine externe unterstützung verfügbar",
          "partnernetzwerk fehlt",
          "kein technischer dienstleister vorhanden",
          "keine verfügbaren spezialisten",
          "externe ressourcen fehlen"
        ]
      },
      "Transport- und Logistikkapazität fehlt": {
        "label": "Transport- und Logistikkapazität fehlt",
        "aliases": [
          "fahrzeuge fehlen",
          "transportkapazität fehlt",
          "fuhrpark fehlt",
          "lagerlogistik fehlt",
          "zustellung ist nicht möglich",
          "logistikpartner fehlt",
          "tourenkapazität fehlt",
          "versandkapazität fehlt",
          "transportmittel fehlen",
          "lieferlogistik reicht nicht aus",
          "keine fahrer verfügbar",
          "distribution ist nicht vorhanden"
        ]
      }
    }
  },
  "Einwand (Externalisierung)": {
    "label": "Einwand (Externalisierung)",
    "aliases": [
      "externalisierung als einwand",
      "externalisierungseinwand",
      "verantwortung externalisieren",
      "verantwortung nach außen verlagern",
      "entscheidung nach außen verlagern",
      "verantwortung auf andere verlagern",
      "verantwortung bei anderen sehen",
      "äußere instanz als blocker",
      "externer blocker",
      "äußere umstände als blocker",
      "andere müssen entscheiden",
      "andere sind verantwortlich",
      "nicht selbst entscheiden können",
      "nicht in eigener hand",
      "äußeren einfluss als grund nennen"
    ],
    "applications": {
      "Partner / Ehepartner": {
        "label": "Partner / Ehepartner",
        "aliases": [
          "muss mit partner sprechen",
          "muss ich mit meinem partner besprechen",
          "partner muss zustimmen",
          "partner entscheidet mit",
          "ehepartner muss zustimmen",
          "muss ich zuhause besprechen",
          "muss ich erst zuhause klären",
          "frau muss zustimmen",
          "mann muss zustimmen",
          "entscheidung mit partner",
          "nicht allein entscheiden",
          "kann ich nicht allein entscheiden",
          "familie muss zustimmen",
          "partner veto",
          "private abstimmung notwendig",
          "mitentscheider privat",
          "partner entscheidet darüber",
          "muss ich erst mit meiner frau besprechen",
          "muss ich erst mit meinem mann besprechen",
          "zuhause abstimmen"
        ]
      },
      "Autorität / Entscheidungsbefugnis": {
        "label": "Autorität / Entscheidungsbefugnis",
        "aliases": [
          "nicht entscheidungsbefugt",
          "ich darf das nicht entscheiden",
          "kann ich nicht entscheiden",
          "entscheider fehlt",
          "muss mit chef sprechen",
          "muss ich mit meinem chef besprechen",
          "chef muss zustimmen",
          "geschäftsführer muss zustimmen",
          "vorgesetzter muss zustimmen",
          "vorgesetzter entscheidet",
          "nicht mein budget",
          "interne freigabe notwendig",
          "brauche interne freigabe",
          "entscheidungsgremium entscheidet",
          "budgetverantwortlicher entscheidet",
          "nicht autorisiert",
          "muss ich intern eskalieren",
          "entscheidungsträger nicht da",
          "freigabeprozess notwendig",
          "budgetfreigabe notwendig",
          "finanzielle freigabe notwendig",
          "muss durch die geschäftsführung",
          "muss durch den vorstand",
          "muss durch den einkauf",
          "muss durch die zentrale"
        ]
      },
      "Markt": {
        "label": "Markt",
        "aliases": [
          "der markt ist schuld",
          "der markt gibt das nicht her",
          "der markt ist schwierig",
          "der markt ist schlecht",
          "der markt funktioniert nicht",
          "liegt am markt",
          "das liegt am markt",
          "unsere branche ist schwierig",
          "in unserer branche funktioniert das nicht",
          "bei uns ist der markt anders",
          "unsere kunden sind anders",
          "unsere kunden kaufen das nicht",
          "die kunden wollen das nicht",
          "die nachfrage ist nicht da",
          "es gibt keine nachfrage",
          "der markt ist gesättigt",
          "der markt ist leergefegt",
          "es gibt keine kandidaten",
          "es gibt keine bewerber",
          "es gibt keine guten bewerber",
          "die leute wollen nicht arbeiten",
          "gute mitarbeiter gibt es nicht",
          "fachkräfte gibt es nicht",
          "der fachkräftemarkt ist leer",
          "die konkurrenz ist zu stark",
          "der wettbewerb ist zu groß",
          "gegen die großen kommen wir nicht an",
          "unsere region ist zu schwierig",
          "an unserem standort funktioniert das nicht",
          "die wirtschaftliche lage ist schuld",
          "die konjunktur ist zu schlecht",
          "die branche gibt das nicht her",
          "der markt lässt kein wachstum zu",
          "äußere marktbedingungen verhindern das"
        ]
      }
    }
  },
  "Einwand (Entscheidung)": {
    "label": "Einwand (Entscheidung)",
    "aliases": [
      "entscheidungseinwand",
      "entscheidungsblockade",
      "fehlendes commitment",
      "fehlende entscheidungsbereitschaft",
      "entscheidung wird vermieden",
      "entscheidung wird zurückgehalten",
      "entscheidung wird vertagt",
      "kunde will sich nicht entscheiden",
      "kunde möchte sich nicht festlegen",
      "kunde kann sich nicht festlegen",
      "kunde vermeidet commitment",
      "kunde hält sich entscheidung offen",
      "fehlende verbindlichkeit",
      "keine klare entscheidung",
      "keine verbindliche entscheidung",
      "zögern vor der entscheidung",
      "innere entscheidungsblockade",
      "abschlussentscheidung wird vermieden"
    ],
    "applications": {
      "Commitment": {
        "label": "Commitment",
        "aliases": [
          "will sich nicht festlegen",
          "möchte sich nicht festlegen",
          "kann sich nicht festlegen",
          "noch nicht bereit zu entscheiden",
          "noch nicht bereit",
          "nicht final entscheiden",
          "möchte noch nicht entscheiden",
          "will noch nicht entscheiden",
          "möchte mich noch nicht festlegen",
          "will mich noch nicht festlegen",
          "entscheidung offen halten",
          "möchte es offen halten",
          "will es offen halten",
          "commitment einwand",
          "fehlendes commitment",
          "kein commitment",
          "kunde zögert",
          "kunde bleibt unverbindlich",
          "unverbindliches interesse",
          "kunde sagt vielleicht",
          "keine klare zusage",
          "keine zusage",
          "halbes ja",
          "fehlende verbindlichkeit",
          "nicht committen",
          "möchte sich noch nicht binden",
          "will sich noch nicht binden",
          "entscheidung noch nicht treffen",
          "noch keine entscheidung treffen",
          "noch keine verbindliche entscheidung"
        ]
      }
    }
  },
  "Einwand (Zeit)": {
    "label": "Einwand (Zeit)",
    "aliases": [
      "zeit als blocker",
      "zeitlicher blocker",
      "zeitpunkt als blocker",
      "zeit oder timing"
    ],
    "applications": {
      "Zeit": {
        "label": "Zeit",
        "aliases": [
          "zeiteinwand",
          "keine zeit",
          "dauert zu lange",
          "zeitmangel",
          "keine kapazität",
          "kalender voll",
          "später sprechen",
          "zeitaufwand zu hoch",
          "terminproblem",
          "jetzt keine zeit",
          "zu wenig zeit",
          "zeitliche priorität",
          "kunde verschiebt wegen zeit",
          "keine zeit für umsetzung",
          "zeitinvestment klären"
        ]
      },
      "Timing": {
        "label": "Timing",
        "aliases": [
          "falscher zeitpunkt",
          "nicht jetzt",
          "timing einwand",
          "später starten",
          "nach dem projekt",
          "nächstes quartal",
          "nächstes jahr",
          "zeitpunkt passt nicht",
          "jetzt ungünstig",
          "kunde verschiebt timing",
          "startzeitpunkt klären",
          "erst nach urlaub",
          "noch zu früh",
          "gerade nicht passend",
          "kaufzeitpunkt klären",
          "zu früh für entscheidung"
        ]
      }
    }
  },
  "Einwand (Angst)": {
    "label": "Einwand (Angst)",
    "aliases": [
      "angst als blocker",
      "unsicherheit als blocker",
      "angst vor kaufentscheidung",
      "innere kaufblockade",
      "sicherheitsbedürfnis bei kaufentscheidung"
    ],
    "applications": {
      "Vertrauen": {
        "label": "Vertrauen",
        "aliases": [
          "vertrauenseinwand",
          "kunde vertraut nicht",
          "misstrauen",
          "skeptisch wegen anbieter",
          "glaubwürdigkeit fehlt",
          "vertrauensproblem",
          "bauchgefühl schlecht",
          "vertrauen aufbauen bei einwand",
          "anbieter wirkt unsicher",
          "vertrauensdefizit",
          "misstrauischer kunde"
        ]
      },
      "Risiko": {
        "label": "Risiko",
        "aliases": [
          "risikoeinwand",
          "zu riskant",
          "was wenn es nicht klappt",
          "angst vor fehlentscheidung",
          "kunde braucht absicherung",
          "unsicherheit wegen ergebnis",
          "garantie fehlt",
          "risiko senken",
          "kaufangst",
          "falsche investition",
          "verlustangst",
          "entscheidung wirkt unsicher",
          "risiko im kaufprozess",
          "sicherheitsbedürfnis",
          "risiko nicht handeln",
          "preisangst",
          "kunde braucht sicherheit"
        ]
      },
      "Erfahrung": {
        "label": "Erfahrung",
        "aliases": [
          "schlechte erfahrung",
          "negative erfahrung",
          "schon mal enttäuscht",
          "vorheriger anbieter",
          "hat früher nicht funktioniert",
          "kunde ist verbrannt",
          "alte erfahrung blockiert",
          "skepsis durch erfahrung",
          "schlechte erfahrung mit branche",
          "vertrauen nach enttäuschung",
          "erfahrungseinwand",
          "frühere lösung gescheitert",
          "kunde überträgt vergangenheit",
          "alte Enttäuschung",
          "vorbelasteter kunde"
        ]
      },
      "Komplexität": {
        "label": "Komplexität",
        "aliases": [
          "zu kompliziert",
          "komplexitätseinwand",
          "lösung wirkt zu komplex",
          "zu viele details",
          "überforderung",
          "technisch zu kompliziert",
          "komplexität reduzieren",
          "zu aufwendig",
          "kompliziertheit blockiert kauf"
        ]
      },
      "Bedenkzeit / Drüber schlafen": {
        "label": "Bedenkzeit / Drüber schlafen",
        "aliases": [
          "brauche bedenkzeit",
          "nochmal drüber schlafen",
          "noch einmal drüber schlafen",
          "eine nacht drüber schlafen",
          "erst mal drüber schlafen"
        ]
      },
      "Skepsis": {
        "label": "Skepsis",
        "aliases": [
          "skeptischer kunde",
          "skepsis einwand",
          "kunde ist kritisch",
          "kunde zweifelt",
          "kritische rückfrage",
          "abwartende haltung",
          "kunde glaubt nicht",
          "prüfhaltung",
          "zweifel klären",
          "skepsis abbauen",
          "nicht überzeugt",
          "kritische distanz",
          "kunde will beobachten",
          "skepsis öffnen",
          "kunde glaubt es nicht",
          "zweifel an lösung",
          "zu schön um wahr zu sein"
        ]
      }
    }
  },
  "Einwand (Ablehnung)": {
    "label": "Einwand (Ablehnung)",
    "aliases": [
      "ablehnung als blocker",
      "ablehnender einwand",
      "direkte ablehnung",
      "indirekte ablehnung",
      "gesprächsablehnung",
      "ablehnung oder abwimmelreaktion",
      "rückzugsantwort",
      "abwehrreaktion"
    ],
    "applications": {
      "Keine Zeit": {
        "label": "Keine Zeit",
        "aliases": [
          "dafür habe ich keine zeit",
          "dafür haben wir keine zeit",
          "ich habe dafür keine zeit",
          "wir haben dafür keine zeit",
          "jetzt habe ich keine zeit",
          "aktuell keine zeit",
          "gerade keine zeit",
          "im moment keine zeit",
          "zeitlich nicht möglich",
          "keine zeit für das gespräch",
          "keine zeit mich damit zu beschäftigen",
          "kunde lehnt wegen fehlender zeit ab",
          "kunde blockt mit zeitmangel ab",
          "zeitmangel als ablehnung",
          "keine gesprächszeit"
        ]
      },
      "Selbst machen": {
        "label": "Selbst machen",
        "aliases": [
          "wir machen das selbst",
          "das machen wir selbst",
          "ich mache das selbst",
          "das lösen wir selbst",
          "wir lösen das selbst",
          "das machen wir intern",
          "wir lösen das intern",
          "das übernehmen wir intern",
          "dafür haben wir eigene mitarbeiter",
          "dafür haben wir eine interne abteilung",
          "das übernimmt unser team",
          "wir benötigen dafür keine unterstützung",
          "wir brauchen dafür niemanden",
          "interne lösung als ablehnung",
          "kunde verweist auf eigenleistung"
        ]
      },
      "Bestehender Partner": {
        "label": "Bestehender Partner",
        "aliases": [
          "wir haben schon einen partner",
          "wir haben bereits einen partner",
          "wir arbeiten schon mit einem partner",
          "wir haben dafür einen partner",
          "wir haben bereits einen dienstleister",
          "wir haben schon einen dienstleister",
          "das macht bereits ein dienstleister",
          "das macht unsere agentur",
          "wir arbeiten schon mit einer agentur",
          "wir sind bereits versorgt",
          "wir haben bereits eine lösung",
          "unser partner kümmert sich darum",
          "bestehender anbieter als ablehnung",
          "bestehende zusammenarbeit als ablehnung",
          "kunde verweist auf bestehenden partner"
        ]
      },
      "Schlechte Erfahrungen": {
        "label": "Schlechte Erfahrungen",
        "aliases": [
          "damit haben wir schlechte erfahrungen",
          "wir haben damit schlechte erfahrungen gemacht",
          "damit hatte ich schlechte erfahrungen",
          "das haben wir schon ausprobiert",
          "das hatten wir schon einmal",
          "das hat bei uns nicht funktioniert",
          "damit sind wir auf die nase gefallen",
          "wir wurden damit enttäuscht",
          "das hat uns nichts gebracht",
          "das war rausgeworfenes geld",
          "das hat sich nicht gelohnt",
          "wir wollen das nicht noch einmal versuchen",
          "negative erfahrung als ablehnung",
          "kunde lehnt wegen schlechter erfahrung ab",
          "kunde verweist auf negative erfahrungen"
        ]
      },
      "Kein Interesse": {
        "label": "Kein Interesse",
        "aliases": [
          "kein interesse",
          "ich habe kein interesse",
          "wir haben kein interesse",
          "nicht interessiert",
          "ich bin nicht interessiert",
          "wir sind nicht interessiert",
          "interessiert uns nicht",
          "für uns nicht interessant",
          "das interessiert mich nicht",
          "das interessiert uns nicht",
          "kommt für uns nicht infrage",
          "möchten wir nicht",
          "wollen wir nicht",
          "kunde zeigt kein interesse",
          "kunde lehnt wegen fehlendem interesse ab"
        ]
      },
      "Kein Bedarf": {
        "label": "Kein Bedarf",
        "aliases": [
          "kein bedarf",
          "ich habe keinen bedarf",
          "wir haben keinen bedarf",
          "aktuell kein bedarf",
          "dafür besteht kein bedarf",
          "brauchen wir nicht",
          "das brauchen wir nicht",
          "benötigen wir nicht",
          "wir benötigen das nicht",
          "für uns nicht notwendig",
          "das ist für uns nicht relevant",
          "wir kommen auch ohne das aus",
          "es gibt dafür keine notwendigkeit",
          "kunde verneint den bedarf",
          "kunde lehnt wegen fehlendem bedarf ab"
        ]
      },
      "Unterlagen zusenden": {
        "label": "Unterlagen zusenden",
        "aliases": [
          "schicken sie uns unterlagen",
          "schicken sie mir unterlagen",
          "senden sie uns unterlagen",
          "senden sie mir unterlagen",
          "schicken sie uns informationen",
          "schicken sie mir informationen",
          "senden sie uns informationen",
          "senden sie mir informationen",
          "schicken sie mir eine mail",
          "schicken sie uns eine mail",
          "senden sie etwas per mail",
          "lassen sie uns etwas zukommen",
          "schicken sie erst einmal etwas zu",
          "kunde verlangt unterlagen statt gespräch",
          "unterlagenanforderung als ablehnung"
        ]
      },
      "Gesprächsabbruch": {
        "label": "Gesprächsabbruch",
        "aliases": [
          "ich lege jetzt auf",
          "ich werde jetzt auflegen",
          "ich beende jetzt das gespräch",
          "wir beenden das gespräch",
          "beenden wir das gespräch",
          "das gespräch ist beendet",
          "rufen sie nicht mehr an",
          "rufen sie mich nicht wieder an",
          "lassen sie mich in ruhe",
          "ich möchte nicht weiterreden",
          "ich will das gespräch nicht fortsetzen",
          "ich möchte das gespräch beenden",
          "kunde beendet das gespräch",
          "kunde kündigt auflegen an",
          "kunde lehnt die gesprächsfortsetzung ab"
        ]
      }
    }
  },
  "Vorwandbehandlung": {
    "label": "Vorwandbehandlung",
    "aliases": [
      "vorwand",
      "verkaufsvorwand",
      "kundenvorwand",
      "vorgeschobener grund",
      "unechter einwand",
      "scheinargument",
      "ausrede im verkauf",
      "oberflächliche begründung",
      "ausweichreaktion",
      "schutzbehauptung",
      "unverbindliche antwort",
      "verdeckter widerstand",
      "standardausrede",
      "rückzugsantwort",
      "nicht der wahre grund"
    ],
    "applications": {
      "Vorwand": {
        "label": "Vorwand",
        "aliases": [
          "vorwand",
          "verkaufsvorwand",
          "kundenvorwand",
          "vorgeschobener grund",
          "unechter einwand",
          "scheinargument",
          "ausrede im verkauf",
          "oberflächliche begründung",
          "ausweichreaktion",
          "schutzbehauptung",
          "unverbindliche antwort",
          "verdeckter widerstand",
          "standardausrede",
          "rückzugsantwort",
          "nicht der wahre grund"
        ]
      }
    }
  },
  "Einwandvorwegnahme": {
    "label": "Einwandvorwegnahme",
    "aliases": [
      "einwandvorwegnahme",
      "einwand vorwegnehmen",
      "vorabbehandlung",
      "einwand vorwegnehmen",
      "einwand vorab behandeln",
      "prehandling",
      "preframe",
      "einwandprävention",
      "kritische punkte früh klären",
      "späteren einwand vermeiden",
      "kundenbedenken vorab öffnen",
      "vor dem angebot klären",
      "widerstand vermeiden",
      "erwartung vorab setzen",
      "vorab qualifizieren",
      "einwand antizipieren",
      "bedenken früh ansprechen"
    ],
    "applications": {
      "Mentale-Blockade-Vorwegnahme": {
        "label": "Mentale-Blockade-Vorwegnahme",
        "aliases": [
          "mentale blockade vorbehandlung",
          "mentale blockade vorwegnahme",
          "mentale blockade vorab behandeln",
          "mentale blockade präventiv behandeln",
          "mentale kaufblockade vorbehandlung",
          "mentale kaufblockade vorwegnehmen",
          "inneren widerstand vorbehandeln",
          "inneren widerstand vorwegnehmen",
          "mentalen widerstand früh behandeln",
          "blockierenden glaubenssatz vorbehandeln",
          "blockierenden glaubenssatz vorwegnehmen",
          "innere kaufblockade früh behandeln",
          "mentale entscheidungsblockade vorbehandlung",
          "mentale entscheidungsblockade vorwegnehmen",
          "psychologische blockade vorab behandeln"
        ]
      },
      "Angebotsvertrauen-Vorwegnahme": {
        "label": "Angebotsvertrauen-Vorwegnahme",
        "aliases": [
          "angebotsvertrauen vorbehandlung",
          "angebotsvertrauen vorwegnahme",
          "vertrauen in angebot vorbehandeln",
          "vertrauen in angebot vorwegnehmen",
          "zweifel am angebot vorbehandlung",
          "zweifel am angebot vorwegnehmen",
          "angebotszweifel vorab behandeln",
          "angebotszweifel früh behandeln",
          "misstrauen gegenüber angebot vorbehandeln",
          "vertrauensdefizit angebot vorwegnehmen",
          "vertrauensdefizit angebot vorbehandlung",
          "angebotsskepsis vorbehandlung",
          "angebotsskepsis vorwegnehmen",
          "glaubwürdigkeit des angebots vorab aufbauen",
          "vertrauen in die lösung vorab behandeln"
        ]
      },
      "Anbietervertrauen-Vorwegnahme": {
        "label": "Anbietervertrauen-Vorwegnahme",
        "aliases": [
          "anbietervertrauen vorbehandlung",
          "anbietervertrauen vorwegnahme",
          "vertrauen in anbieter vorbehandeln",
          "vertrauen in anbieter vorwegnehmen",
          "anbieterzweifel vorbehandlung",
          "anbieterzweifel vorwegnehmen",
          "misstrauen gegenüber anbieter vorbehandeln",
          "misstrauen gegenüber anbieter vorwegnehmen",
          "vertrauensdefizit anbieter vorbehandlung",
          "vertrauensdefizit anbieter vorwegnehmen",
          "glaubwürdigkeit des anbieters vorbehandeln",
          "glaubwürdigkeit des anbieters vorab klären",
          "anbieter skepsis vorab behandeln",
          "anbieterunsicherheit vorbehandlung",
          "anbieterunsicherheit vorwegnehmen"
        ]
      },
      "Selbstvertrauen-Vorwegnahme": {
        "label": "Selbstvertrauen-Vorwegnahme",
        "aliases": [
          "selbstvertrauen vorbehandlung",
          "selbstvertrauen vorwegnahme",
          "selbstzweifel vorbehandlung",
          "selbstzweifel vorwegnahme",
          "selbstzweifel vorab behandeln",
          "umsetzungszweifel vorbehandlung",
          "umsetzungszweifel vorwegnehmen",
          "vertrauen in sich selbst vorbehandeln",
          "vertrauen in sich selbst vorwegnehmen",
          "fehlendes selbstvertrauen früh behandeln",
          "zweifel an eigener fähigkeit vorbehandeln",
          "zweifel an eigener umsetzung vorwegnehmen",
          "ich schaffe das nicht vorbehandlung",
          "eigene erfolgsfähigkeit vorab klären",
          "umsetzungssicherheit vorbehandlung"
        ]
      },
      "Methodenvertrauen-Vorwegnahme": {
        "label": "Methodenvertrauen-Vorwegnahme",
        "aliases": [
          "methodenvertrauen vorbehandlung",
          "methodenvertrauen vorwegnahme",
          "vertrauen in methode vorbehandeln",
          "vertrauen in methode vorwegnehmen",
          "methodenskepsis vorbehandlung",
          "methodenskepsis vorwegnahme",
          "zweifel an methode vorbehandeln",
          "zweifel an methode vorwegnehmen",
          "vertrauensdefizit methode vorbehandlung",
          "vertrauensdefizit methode vorwegnehmen",
          "vorgehensweise vorbehandlung",
          "vorgehensweise vorwegnehmen",
          "zweifel an der vorgehensweise vorab behandeln",
          "funktioniert die methode vorbehandlung",
          "methodenunsicherheit früh behandeln"
        ]
      },
      "Bedürfnislücken-Vorwegnahme": {
        "label": "Bedürfnislücken-Vorwegnahme",
        "aliases": [
          "bedürfnislücke vorbehandlung",
          "bedürfnislücke vorwegnahme",
          "bedürfnislücke vorab behandeln",
          "fehlende bedürfniserfüllung vorbehandlung",
          "fehlende bedürfniserfüllung vorwegnehmen",
          "unerfülltes bedürfnis vorbehandlung",
          "unerfülltes bedürfnis vorwegnehmen",
          "bedürfnisdefizit vorbehandlung",
          "bedürfnisdefizit vorwegnahme",
          "bedürfnis früh behandeln",
          "bedürfnis vorab klären",
          "bedürfnis vorwegnehmen",
          "fehlendes bedürfnis früh öffnen",
          "kundenbedürfnis vorbehandlung",
          "bedürfnisblockade vorab behandeln"
        ]
      },
      "Wachstumsbedürfnis-Vorwegnahme": {
        "label": "Wachstumsbedürfnis-Vorwegnahme",
        "aliases": [
          "wachstumsbedürfnis vorbehandlung",
          "wachstumsbedürfnis vorwegnahme",
          "wachstumsbedürfnis vorab behandeln",
          "wachstumswunsch vorbehandlung",
          "wachstumswunsch vorwegnehmen",
          "wachstumsdefizit vorbehandlung",
          "fehlendes wachstum vorwegnehmen",
          "wachstumsblockade vorbehandlung",
          "wachstumsblockade vorwegnehmen",
          "weiterentwicklungsbedürfnis vorbehandlung",
          "weiterentwicklungsbedürfnis vorwegnehmen",
          "wachstumsbedarf früh behandeln",
          "wachstum vorab thematisieren",
          "wachstum früh vorbehandeln",
          "entwicklungsbedarf vorwegnehmen"
        ]
      },
      "Verbundenheitsbedürfnis-Vorwegnahme": {
        "label": "Verbundenheitsbedürfnis-Vorwegnahme",
        "aliases": [
          "verbundenheitsbedürfnis vorbehandlung",
          "verbundenheitsbedürfnis vorwegnahme",
          "verbundenheitsbedürfnis vorab behandeln",
          "zugehörigkeitsbedürfnis vorbehandlung",
          "zugehörigkeitsbedürfnis vorwegnehmen",
          "fehlende verbundenheit vorbehandlung",
          "fehlende verbundenheit vorwegnehmen",
          "soziale verbundenheit vorbehandeln",
          "soziale verbundenheit vorwegnehmen",
          "zugehörigkeit früh behandeln",
          "zugehörigkeit vorab klären",
          "verbundenheit früh thematisieren",
          "soziales bedürfnis vorbehandlung",
          "soziales bedürfnis vorwegnehmen",
          "beziehungsbedürfnis vorab behandeln"
        ]
      },
      "Anerkennungsbedürfnis-Vorwegnahme": {
        "label": "Anerkennungsbedürfnis-Vorwegnahme",
        "aliases": [
          "anerkennungsbedürfnis vorbehandlung",
          "anerkennungsbedürfnis vorwegnahme",
          "anerkennungsbedürfnis vorab behandeln",
          "bedarf nach anerkennung vorbehandlung",
          "bedarf nach anerkennung vorwegnehmen",
          "fehlende anerkennung vorbehandlung",
          "fehlende anerkennung vorwegnehmen",
          "wertschätzungsbedürfnis vorbehandlung",
          "wertschätzungsbedürfnis vorwegnahme",
          "statusbedürfnis vorbehandlung",
          "statusbedürfnis vorwegnehmen",
          "anerkennung früh behandeln",
          "anerkennung vorab thematisieren",
          "wertschätzung vorab behandeln",
          "soziale anerkennung vorwegnehmen"
        ]
      },
      "Abwechslungsbedürfnis-Vorwegnahme": {
        "label": "Abwechslungsbedürfnis-Vorwegnahme",
        "aliases": [
          "abwechslungsbedürfnis vorbehandlung",
          "abwechslungsbedürfnis vorwegnahme",
          "abwechslungsbedürfnis vorab behandeln",
          "bedarf nach abwechslung vorbehandlung",
          "bedarf nach abwechslung vorwegnehmen",
          "fehlende abwechslung vorbehandlung",
          "fehlende abwechslung vorwegnehmen",
          "monotonie vorbehandlung",
          "monotonie vorwegnahme",
          "routine vorbehandlung",
          "routine vorwegnehmen",
          "veränderungsbedürfnis vorbehandlung",
          "veränderungsbedürfnis vorwegnahme",
          "abwechslung früh thematisieren",
          "neues erleben vorab behandeln"
        ]
      },
      "Sicherheitsbedürfnis-Vorwegnahme": {
        "label": "Sicherheitsbedürfnis-Vorwegnahme",
        "aliases": [
          "sicherheitsbedürfnis vorbehandlung",
          "sicherheitsbedürfnis vorwegnahme",
          "sicherheitsbedürfnis vorab behandeln",
          "bedarf nach sicherheit vorbehandlung",
          "bedarf nach sicherheit vorwegnehmen",
          "fehlende sicherheit vorbehandlung",
          "fehlende sicherheit vorwegnehmen",
          "unsicherheitsgefühl vorbehandlung",
          "unsicherheitsgefühl vorwegnahme",
          "sicherheitsdefizit vorbehandlung",
          "sicherheitsdefizit vorwegnehmen",
          "sicherheit früh behandeln",
          "sicherheit vorab thematisieren",
          "absicherungsbedürfnis vorbehandlung",
          "absicherungsbedürfnis vorwegnehmen"
        ]
      },
      "Externalisierungs-Vorwegnahme": {
        "label": "Externalisierungs-Vorwegnahme",
        "aliases": [
          "externalisierung vorbehandlung",
          "externalisierung vorwegnahme",
          "externalisierung vorab behandeln",
          "verantwortungsverschiebung vorbehandlung",
          "verantwortungsverschiebung vorwegnehmen",
          "verantwortung nach außen vorbehandeln",
          "verantwortung nach außen vorwegnehmen",
          "schuld bei anderen vorbehandlung",
          "schuld bei anderen vorwegnehmen",
          "äußere umstände vorbehandlung",
          "äußere umstände vorwegnehmen",
          "opferhaltung vorbehandlung",
          "opferhaltung vorwegnahme",
          "eigenverantwortung früh behandeln",
          "externalen ursachenfokus vorab behandeln"
        ]
      },
      "Logistik-Vorwegnahme": {
        "label": "Logistik-Vorwegnahme",
        "aliases": [
          "logistik vorbehandlung",
          "logistik vorwegnahme",
          "logistische blockade vorbehandlung",
          "logistische blockade vorwegnahme",
          "logistische blockade vorab behandeln",
          "logistische hürde vorbehandlung",
          "logistische hürde vorwegnehmen",
          "umsetzungshürde vorbehandlung",
          "umsetzungshürde vorwegnahme",
          "operative blockade vorbehandlung",
          "operative blockade vorwegnehmen",
          "machbarkeit vorbehandlung",
          "machbarkeit vorwegnehmen",
          "logistische machbarkeit vorab klären",
          "umsetzungslogistik früh behandeln"
        ]
      },
      "Personal-Vorwegnahme": {
        "label": "Personal-Vorwegnahme",
        "aliases": [
          "personal vorbehandlung",
          "personal vorwegnahme",
          "personalengpass vorbehandlung",
          "personalengpass vorwegnahme",
          "personelle blockade vorbehandlung",
          "personelle blockade vorwegnehmen",
          "personalmangel vorbehandlung",
          "personalmangel vorwegnehmen",
          "mitarbeitermangel vorbehandlung",
          "mitarbeitermangel vorwegnahme",
          "personelle kapazität vorbehandlung",
          "personelle kapazität vorwegnehmen",
          "personalressourcen vorab behandeln",
          "mitarbeiterressourcen vorab behandeln",
          "personelle machbarkeit vorbehandlung"
        ]
      },
      "Ressourcen-Vorwegnahme": {
        "label": "Ressourcen-Vorwegnahme",
        "aliases": [
          "ressourcen vorbehandlung",
          "ressourcen vorwegnahme",
          "ressourcenengpass vorbehandlung",
          "ressourcenengpass vorwegnahme",
          "ressourcenblockade vorbehandlung",
          "ressourcenblockade vorwegnehmen",
          "fehlende ressourcen vorbehandlung",
          "fehlende ressourcen vorwegnahme",
          "operative ressourcen vorbehandlung",
          "operative ressourcen vorwegnehmen",
          "umsetzungsressourcen vorbehandlung",
          "umsetzungsressourcen vorwegnahme",
          "ressourcenverfügbarkeit vorab behandeln",
          "ressourcenlage vorbehandlung",
          "ressourcenlage vorwegnehmen"
        ]
      },
      "Preis-Vorwegnahme": {
        "label": "Preis-Vorwegnahme",
        "aliases": [
          "preis vorab ansprechen",
          "preiseinwand vorwegnehmen",
          "investition früh klären",
          "budget vorab prüfen",
          "kostenrahmen vor angebot",
          "preisüberraschung vermeiden",
          "preisanker setzen",
          "preis vor dem pitch",
          "honorar früh thematisieren",
          "wert vor preis aufbauen",
          "zahlungsbereitschaft prüfen",
          "finanzielle erwartung setzen",
          "kostenpunkt vorbereiten",
          "preis nicht verstecken",
          "preisgespräch vorbereiten"
        ]
      },
      "Zeit-Vorwegnahme": {
        "label": "Zeit-Vorwegnahme",
        "aliases": [
          "zeit vorab klären",
          "zeiteinwand vorwegnehmen",
          "zeitaufwand früh ansprechen",
          "zeitrahmen setzen",
          "kundenzeit prüfen",
          "umsetzungsdauer erklären",
          "zeitinvestment vorbereiten",
          "kapazität vorab prüfen",
          "terminaufwand klären",
          "laufzeit vor angebot",
          "zeitbedarf nicht überraschen",
          "implementierungszeit klären",
          "call dauer framen",
          "terminrealität prüfen",
          "zeitliche erwartung setzen"
        ]
      },
      "Vertrauens-Vorwegnahme": {
        "label": "Vertrauens-Vorwegnahme",
        "aliases": [
          "vertrauen vorab aufbauen",
          "vertrauenseinwand vorwegnehmen",
          "skepsis früh abholen",
          "glaubwürdigkeit vor angebot",
          "zweifel früh klären",
          "proof vorab setzen",
          "referenzen früh nutzen",
          "sicherheitsgefühl schaffen",
          "vertrauensbasis legen",
          "misstrauen vorab öffnen",
          "beweise vorab einordnen",
          "glaubwürdigkeit rahmen",
          "vertrauensrisiko senken",
          "kundenbedenken offen machen",
          "vertrauensfrage vorwegnehmen"
        ]
      },
      "Risiko-Vorwegnahme": {
        "label": "Risiko-Vorwegnahme",
        "aliases": [
          "risiko vorab ansprechen",
          "risikoeinwand vorwegnehmen",
          "absicherung früh erklären",
          "sicherheitsbedenken vorab",
          "was wenn es nicht klappt vorwegnehmen",
          "garantie früh erklären",
          "kaufangst vorab senken",
          "risiko transparent machen",
          "entscheidungssicherheit vorbereiten",
          "verlustangst früh abholen",
          "risiko im voraus relativieren",
          "umsetzungsrisiko besprechen",
          "sicherheitsrahmen setzen",
          "risiko des nicht handelns vorbereiten",
          "kundenrisiko früh prüfen"
        ]
      },
      "Skepsis-Vorwegnahme": {
        "label": "Skepsis-Vorwegnahme",
        "aliases": [
          "skepsis vorab behandeln",
          "skepsis vorwegnehmen",
          "zweifel früh ansprechen",
          "kritische rückfragen antizipieren",
          "kritischen kunden vorbereiten",
          "skepsis legitimieren",
          "prüfhaltung einordnen",
          "misstrauen vor pitch klären",
          "abwartende haltung vorab",
          "skepsis nicht bekämpfen",
          "kritik vorab abholen",
          "zweifel normalisieren",
          "skeptische haltung rahmen",
          "kundenkritik vorbereiten",
          "skepsis früh öffnen"
        ]
      },
      "Partner-Vorwegnahme": {
        "label": "Partner-Vorwegnahme",
        "aliases": [
          "partnerfrage vorab klären",
          "partner einwand vorwegnehmen",
          "ehepartner früh einbinden",
          "mitentscheider vorab klären",
          "wer muss zustimmen",
          "abstimmungspflicht prüfen",
          "dritte person identifizieren",
          "partner veto vorbereiten",
          "entscheidungspartner klären",
          "alle entscheider sichern",
          "familienentscheidung vorbereiten",
          "zuhause besprechen vermeiden",
          "stakeholder früh herausfinden",
          "mitentscheider nicht vergessen",
          "abstimmung nach call vermeiden"
        ]
      },
      "Wettbewerber-Vorwegnahme": {
        "label": "Wettbewerber-Vorwegnahme",
        "aliases": [
          "wettbewerber vorab ansprechen",
          "konkurrenz vorwegnehmen",
          "andere anbieter früh klären",
          "vergleichsprozess verstehen",
          "alternative lösungen klären",
          "marktvergleich einordnen",
          "differenzierung vorbereiten",
          "wettbewerbsfrage vorab",
          "kunde vergleicht abholen",
          "konkurrenzangebot vorweg",
          "vergleichskriterien definieren",
          "auswahlprozess klären",
          "mitbewerber nicht ignorieren",
          "anbieterwechsel rahmen",
          "warum nicht konkurrenz kaufen"
        ]
      },
      "Commitment-Vorwegnahme": {
        "label": "Commitment-Vorwegnahme",
        "aliases": [
          "commitment vorab prüfen",
          "verbindlichkeit früh abfragen",
          "abschlussbereitschaft vor angebot",
          "ernsthaftigkeit prüfen",
          "nur interesse oder kaufabsicht",
          "zusagefähigkeit klären",
          "entscheidungskriterien vorab",
          "kundencommitment vorbereiten",
          "unverbindlichkeit reduzieren",
          "commitmentrahmen setzen",
          "kaufbereitschaft einschätzen",
          "nächsten schritt vor pitch sichern",
          "entscheidung nach angebot vorbereiten",
          "verbindliches gesprächsziel",
          "commitmentlevel prüfen"
        ]
      },
      "Komplexitäts-Vorwegnahme": {
        "label": "Komplexitäts-Vorwegnahme",
        "aliases": [
          "komplexität vorab reduzieren",
          "kompliziertheit vorwegnehmen",
          "lösung einfach erklären",
          "kunde nicht überfordern",
          "verständlichkeit sichern",
          "prozess einfach darstellen",
          "technische skepsis vorab",
          "komplexe details strukturieren",
          "einfachheitsrahmen setzen",
          "angebot übersichtlich machen",
          "umsetzung verständlich erklären",
          "überforderung vermeiden",
          "einfacher einstieg",
          "kunden auf struktur vorbereiten",
          "komplexität früh einordnen"
        ]
      },
      "Budget-Vorwegnahme": {
        "label": "Budget-Vorwegnahme",
        "aliases": [
          "budget vorab klären",
          "budgeteinwand vorwegnehmen",
          "finanzielle freigabe prüfen",
          "investitionsrahmen klären",
          "budgetprozess verstehen",
          "kostenstelle früh klären",
          "zahlungsrahmen setzen",
          "budgetverfügbarkeit prüfen",
          "freigabeschleife klären",
          "budgetjahr berücksichtigen",
          "finanzierung vor pitch",
          "kaufrahmen definieren",
          "budgetrealität ansprechen",
          "kein budget später vermeiden",
          "wirtschaftlichen rahmen klären"
        ]
      }
    }
  },
  "Vertrauensaufbau (P TC+, P SK-)": {
    "label": "Vertrauensaufbau",
    "aliases": [
      "vertrauen aufbauen",
      "vertrauensaufbau im verkauf",
      "kundenvertrauen gewinnen",
      "glaubwürdigkeit erhöhen",
      "skepsis reduzieren",
      "rapport aufbauen",
      "vertrauensbasis schaffen",
      "kunde fühlt sich verstanden",
      "vertrauen vor abschluss",
      "sicherheit im kaufprozess",
      "professionell glaubwürdig wirken",
      "vertrauen durch fragen",
      "beziehung im call stärken",
      "kundenbedenken ernst nehmen",
      "vertrauen statt druck"
    ],
    "applications": {
      "Autorität (P TC+)": {
        "label": "Autorität",
        "aliases": [
          "autorität aufbauen",
          "expertenstatus zeigen",
          "fachliche führung übernehmen",
          "souverän im sales call",
          "beraterautorität",
          "kunde folgt gesprächsführung",
          "fachliche einordnung geben",
          "autorität durch diagnose",
          "professionelle sicherheit",
          "sachliche autorität",
          "verkaufsführung ohne dominanz",
          "kunde erkennt expertise",
          "führungsrolle im gespräch",
          "kompetent führen",
          "vertrauenswürdige autorität"
        ]
      },
      "Kompetenz (P TC+)": {
        "label": "Kompetenz",
        "aliases": [
          "kompetenz zeigen",
          "fachkompetenz sichtbar machen",
          "kunde erkennt fachwissen",
          "kompetent antworten",
          "expertise vermitteln",
          "sachverstand beweisen",
          "professionelle erklärung",
          "diagnosekompetenz zeigen",
          "kundenproblem einordnen",
          "beraterkompetenz",
          "lösungsverständnis zeigen",
          "kompetenz durch fragen",
          "fachliche sicherheit",
          "kundenvertrauen durch wissen",
          "relevanz herstellen"
        ]
      },
      "Transparenz (P TC+, P SK-)": {
        "label": "Transparenz",
        "aliases": [
          "transparenz schaffen",
          "ehrlich verkaufen",
          "offen über preis sprechen",
          "prozess transparent erklären",
          "keine versteckten kosten",
          "risiken offen ansprechen",
          "klare erwartungen setzen",
          "angebot transparent machen",
          "kunden offen informieren",
          "nichts beschönigen",
          "transparente beratung",
          "ehrlicher sales call",
          "grenzen offen sagen",
          "kunden nicht überraschen",
          "offene entscheidungsgrundlage"
        ]
      },
      "Sozialer Beweis (P TC+, P SK-)": {
        "label": "Sozialer Beweis",
        "aliases": [
          "sozialer beweis",
          "social proof",
          "referenzen nutzen",
          "kundenbeispiele",
          "case study im sales call",
          "testimonials einsetzen",
          "kundenstimmen",
          "vergleichbare kunden nennen",
          "proof durch andere kunden",
          "ergebnisse anderer kunden",
          "referenzfall erzählen",
          "peer proof",
          "bewertungen nutzen",
          "kundenbeweis",
          "ähnlicher kunde beispiel"
        ]
      },
      "Sicherheit (P TC+, P SK-)": {
        "label": "Sicherheit",
        "aliases": [
          "sicherheit geben",
          "kaufangst reduzieren",
          "kundenrisiko senken",
          "entscheidungssicherheit",
          "absicherung erklären",
          "garantie kommunizieren",
          "kunde fühlt sich sicher",
          "risikofreier nächster schritt",
          "sicherheitsbedürfnis erfüllen",
          "sicherheitsrahmen",
          "umsetzungsrisiko senken",
          "klare absicherung",
          "kundenängste klären",
          "sichere entscheidung ermöglichen",
          "kontrolle geben"
        ]
      },
      "Empathie (P TC+)": {
        "label": "Empathie",
        "aliases": [
          "empathie im verkauf",
          "aktiv zuhören",
          "kunde fühlt sich verstanden",
          "kundenperspektive spiegeln",
          "verständnis zeigen",
          "einwand empathisch behandeln",
          "emotionalen zustand erkennen",
          "menschlich verkaufen",
          "kundensituation ernst nehmen",
          "empathische frage",
          "rapport durch verständnis",
          "kundenfrust anerkennen",
          "zuhören statt pitchen",
          "empathische einwandöffnung",
          "verständnis ohne zustimmung"
        ]
      },
      "Ähnlichkeit (P TC+)": {
        "label": "Ähnlichkeit",
        "aliases": [
          "ähnlichkeit nutzen",
          "gemeinsamkeiten finden",
          "gleiche sprache sprechen",
          "branchennähe zeigen",
          "ähnliche erfahrung",
          "vergleichbare situation",
          "kunde erkennt gleiche welt",
          "rapport durch gemeinsamkeit",
          "nähe ohne anbiedern",
          "peer bezug herstellen",
          "ähnliche kundenwelt",
          "vertrautheit schaffen",
          "gemeinsamer kontext",
          "ähnliche herausforderung",
          "beziehungsaufbau durch ähnlichkeit"
        ]
      },
      "Verlässlichkeit (P TC+)": {
        "label": "Verlässlichkeit",
        "aliases": [
          "verlässlichkeit zeigen",
          "zuverlässigkeit beweisen",
          "vertrauen durch verlässlichkeit",
          "realistische zusagen",
          "lieferfähigkeit zeigen",
          "verlässlicher partner",
          "erwartungen erfüllen",
          "stabilität vermitteln",
          "professionelle verbindlichkeit",
          "saubere prozesse zeigen",
          "kundenangst vor enttäuschung",
          "versprechen realistisch setzen",
          "umsetzung verlässlich erklären",
          "konstanz kommunizieren",
          "zuverlässig wirken"
        ]
      },
      "Expertise (P TC+)": {
        "label": "Expertise",
        "aliases": [
          "expertise zeigen",
          "spezialwissen nutzen",
          "fachwissen vermitteln",
          "kundenproblem analysieren",
          "expertenrolle einnehmen",
          "branchenwissen einbringen",
          "insiderwissen nutzen",
          "tiefe diagnose",
          "expertise durch fragen",
          "lösungskompetenz zeigen",
          "beraterwissen einordnen",
          "fachliche tiefe",
          "spezialist statt verkäufer",
          "expertise ohne überheblichkeit",
          "kundenkontext verstehen"
        ]
      },
      "Beweis (P TC+, P SK-)": {
        "label": "Beweis",
        "aliases": [
          "beweise nutzen",
          "proof point",
          "ergebnisse nachweisen",
          "zahlen als beweis",
          "daten für glaubwürdigkeit",
          "vorher nachher zeigen",
          "messbare resultate",
          "track record",
          "fakten statt behauptungen",
          "erfolgsnachweis",
          "benchmark nutzen",
          "dokumentierte ergebnisse",
          "wirkung nachweisen",
          "beleg für lösung",
          "kundenbeweis liefern"
        ]
      }
    }
  },
  "Glaubenssatz-Wandel (P TO+, P CN+, P CC+, P TS+, P SK-, P CO+)": {
    "label": "Glaubenssatz-Wandel",
    "aliases": [
      "glaubenssatz verändern",
      "belief shift",
      "reframing",
      "umdenken auslösen",
      "kundenüberzeugung ändern",
      "falsche annahme lösen",
      "limitierende überzeugung",
      "perspektive ändern",
      "kaufblockade neu einordnen",
      "einwand reframen",
      "kunde erkennt neuen blickwinkel",
      "alte überzeugung hinterfragen",
      "einsicht erzeugen",
      "mindset im verkauf",
      "denkrahmen wechseln"
    ],
    "applications": {
      "Falsche Annahme (P CN+, P SK-)": {
        "label": "Falsche Annahme",
        "aliases": [
          "falsche annahme",
          "annahme aufdecken",
          "kunde geht von falschem aus",
          "irrtum klären",
          "denkfehler hinterfragen",
          "missverständnis korrigieren",
          "falsche vorstellung",
          "annahme prüfen",
          "kunde hat falsches bild",
          "fehleinschätzung",
          "trugschluss lösen",
          "falsche erwartung",
          "vorurteil öffnen",
          "mythos entkräften",
          "schlussfolgerung korrigieren"
        ]
      },
      "Limitierender Glaubenssatz (P TS+, P SK-)": {
        "label": "Limitierender Glaubenssatz",
        "aliases": [
          "limitierender glaubenssatz",
          "geht nicht überzeugung",
          "funktioniert bei uns nicht",
          "wir sind anders einwand",
          "kunde sieht blockade",
          "unmöglichkeitsglaube",
          "kann ich nicht",
          "nicht für unsere branche",
          "selbstlimitierung",
          "blockierende überzeugung",
          "mentale blockade",
          "einschränkendes denken",
          "kunde begrenzt möglichkeit",
          "alte grenze hinterfragen",
          "glaubenssatz lösen"
        ]
      },
      "Perspektivwechsel (P CN+, P SK-)": {
        "label": "Perspektivwechsel",
        "aliases": [
          "perspektivwechsel",
          "neuer blickwinkel",
          "anders betrachten",
          "reframe frage",
          "andere sichtweise",
          "kunde sieht andere seite",
          "problem neu einordnen",
          "blickwinkel wechseln",
          "rauszoomen",
          "metaperspektive",
          "denkrahmen wechseln",
          "kundenlogik drehen",
          "situation neu betrachten",
          "neue perspektive",
          "blickwinkelfrage"
        ]
      },
      "Ursachenwechsel (P CN+)": {
        "label": "Ursachenwechsel",
        "aliases": [
          "ursache neu einordnen",
          "ursachenwechsel",
          "falsche ursache",
          "eigentliche ursache",
          "symptom oder ursache",
          "problemursache klären",
          "nicht person sondern system",
          "systemische ursache",
          "prozessursache",
          "schuldfrage vermeiden",
          "wirkmechanismus erklären",
          "kunde sieht falschen auslöser",
          "ursachenfrage",
          "problem anders erklären",
          "ursache reframen"
        ]
      },
      "Verantwortungswechsel (P CO+)": {
        "label": "Verantwortungswechsel",
        "aliases": [
          "verantwortung neu rahmen",
          "verantwortungswechsel",
          "kundenverantwortung",
          "eigene rolle erkennen",
          "handlungsspielraum erkennen",
          "verantwortungsfrage",
          "entscheidung verantworten",
          "zuständigkeit klären",
          "verantwortung ohne schuld",
          "einflussbereich erkennen",
          "handlungsmacht zurückgeben",
          "opferhaltung lösen",
          "aktive rolle",
          "verantwortungsübernahme",
          "eigener hebel"
        ]
      },
      "Wertverschiebung (P TO+)": {
        "label": "Wertverschiebung",
        "aliases": [
          "wert statt preis",
          "investition statt kosten",
          "wertwahrnehmung",
          "preis in wert übersetzen",
          "mehrwert erklären",
          "kosten nutzen drehen",
          "kunde sieht wert nicht",
          "return klären",
          "preisfokus lösen",
          "nutzenfokus",
          "roi perspektive",
          "ausgabe als investition",
          "wirtschaftlicher wert",
          "wertlogik",
          "preis leistung neu rahmen"
        ]
      },
      "Risikowahrnehmung (P CC+)": {
        "label": "Risikowahrnehmung",
        "aliases": [
          "risiko neu bewerten",
          "risikowahrnehmung ändern",
          "risiko des nicht handelns",
          "kaufrisiko überschätzt",
          "handlungsrisiko vergleichen",
          "risiko umkehren",
          "sicherheitsbedenken reframen",
          "kaufangst neu einordnen",
          "nichtentscheidung ist riskanter",
          "risiko in relation",
          "folgerisiko zeigen",
          "risikofrage",
          "risikoentscheidung",
          "sicherheitsdenken öffnen",
          "risikoausgleich"
        ]
      },
      "Chancenwahrnehmung (P TO+)": {
        "label": "Chancenwahrnehmung",
        "aliases": [
          "chance sichtbar machen",
          "chancenwahrnehmung",
          "kunde erkennt möglichkeit nicht",
          "potenzial herausarbeiten",
          "möglichkeit im problem",
          "kundenchance rahmen",
          "positive auswirkung",
          "upside klären",
          "gewinnchance",
          "möglichkeitsfrage",
          "potenzial statt problem",
          "chance aus veränderung",
          "nutzenraum öffnen",
          "wachstumsperspektive",
          "opportunity reframing"
        ]
      },
      "Kostenwahrnehmung (P TO+, P CC+)": {
        "label": "Kostenwahrnehmung",
        "aliases": [
          "kostenwahrnehmung",
          "preis relativieren",
          "kosten im verhältnis",
          "kosten pro tag",
          "teuer reframen",
          "kosten des nicht handelns",
          "preisgefühl ändern",
          "investition aufschlüsseln",
          "kunde sieht nur preis",
          "gesamtkosten betrachten",
          "preis in relation",
          "kurzfristkosten langfristkosten",
          "preisanker ändern",
          "wahrgenommener preis",
          "kostenfrage neu stellen"
        ]
      },
      "Selbstzweifel (P TS+)": {
        "label": "Selbstzweifel",
        "aliases": [
          "selbstzweifel",
          "kunde traut sich nicht",
          "angst es nicht zu schaffen",
          "zweifel an eigener fähigkeit",
          "umsetzungszweifel",
          "fehlendes selbstvertrauen",
          "kunde fühlt sich nicht bereit",
          "überforderung durch entscheidung",
          "kann ich das schaffen",
          "erfolgsfähigkeit klären",
          "kompetenzzweifel",
          "umsetzungssicherheit",
          "zutrauen aufbauen",
          "innere unsicherheit",
          "kunde zweifelt an sich"
        ]
      }
    }
  },
  "Identitäts-Rahmung (P TS+, P CO+)": {
    "label": "Identitäts-Rahmung",
    "aliases": [
      "identitätsrahmung",
      "identity framing",
      "selbstbild ansprechen",
      "kundenrolle rahmen",
      "wer will der kunde sein",
      "entscheiderrolle stärken",
      "zukünftiges selbst",
      "käuferrolle aktivieren",
      "verantwortungsrolle spiegeln",
      "kunde als macher",
      "entscheidung passt zum selbstbild",
      "handlungsidentität",
      "professionelle rolle",
      "selbstwahrnehmung im verkauf",
      "rolle im kaufprozess"
    ],
    "applications": {
      "Selbstbild (P TS+)": {
        "label": "Selbstbild",
        "aliases": [
          "selbstbild",
          "selbstbild des kunden",
          "selbstwahrnehmung",
          "eigenbild",
          "kunde sieht sich selbst",
          "entscheidung und selbstbild",
          "wer bin ich als entscheider",
          "kunde will stimmig handeln",
          "selbstverständnis",
          "selbstkonzept",
          "handlungslücke selbstbild",
          "kunde erkennt eigenes muster",
          "selbstbildfrage",
          "identität des kunden",
          "entscheidung passt zu mir"
        ]
      },
      "Status (P TS+)": {
        "label": "Status",
        "aliases": [
          "status ansprechen",
          "image im verkauf",
          "ruf schützen",
          "ansehen des kunden",
          "professioneller anspruch",
          "statusverlust vermeiden",
          "reputation",
          "positionierung des kunden",
          "souverän wirken",
          "prestige im kaufprozess",
          "statusentscheidung",
          "ansehen im team",
          "führungsstatus",
          "imageverlust",
          "reputation als entscheidungsfaktor"
        ]
      },
      "Führungsidentität (P TS+, P CO+)": {
        "label": "Führungsidentität",
        "aliases": [
          "führungsidentität",
          "führungsrolle",
          "kunde als führungskraft",
          "leader identity",
          "verantwortung als chef",
          "führung durch entscheidung",
          "vorausschauende führung",
          "teamverantwortung",
          "entscheidung als führungsaufgabe",
          "strategische führungsrolle",
          "kunde als vorreiter",
          "führungsanspruch",
          "leadership frage",
          "verantwortung gegenüber team",
          "führungskraft entscheidet"
        ]
      },
      "Professionelle Identität (P TS+, P CO+)": {
        "label": "Professionelle Identität",
        "aliases": [
          "professionelle identität",
          "kunde als profi",
          "beruflicher anspruch",
          "professionelle entscheidung",
          "kunde will seriös handeln",
          "professionalität",
          "fachliche rolle",
          "geschäftliche verantwortung",
          "professioneller standard",
          "berufliches selbstverständnis",
          "business identität",
          "professionalität und konsequenz",
          "seriöse käuferrolle",
          "geschäftsmäßige entscheidung",
          "profi anspruch"
        ]
      },
      "Käuferidentität (P CO+)": {
        "label": "Käuferidentität",
        "aliases": [
          "käuferidentität",
          "käuferrolle",
          "vom interessenten zum käufer",
          "interessent oder käufer",
          "kunde als auftraggeber",
          "buyer mindset",
          "ernsthafter käufer",
          "kaufbereitschaft als rolle",
          "vom prüfer zum käufer",
          "kunde übernimmt käuferrolle",
          "entscheidungsträgerrolle",
          "auftraggeberrolle",
          "käuferstatus",
          "buyer identity",
          "kaufrolle aktivieren"
        ]
      },
      "Wachstumsidentität (P TS+, P CO+)": {
        "label": "Wachstumsidentität",
        "aliases": [
          "wachstumsidentität",
          "kunde will wachsen",
          "weiterentwicklung",
          "growth mindset",
          "nächste stufe",
          "wachstumsanspruch",
          "kunde will fortschritt",
          "ambition spiegeln",
          "aufstiegsidentität",
          "nicht stehen bleiben",
          "entwicklung als selbstbild",
          "wachstum durch entscheidung",
          "zukunftswachstum",
          "weiterentwicklungsfrage",
          "kunde als lernender"
        ]
      },
      "Verantwortungsidentität (P CO+)": {
        "label": "Verantwortungsidentität",
        "aliases": [
          "verantwortungsidentität",
          "kunde als verantwortlicher",
          "verantwortungsrolle",
          "pflichtgefühl",
          "verantwortungsbewusst handeln",
          "für team verantwortlich",
          "für ergebnis verantwortlich",
          "entscheidung verantworten",
          "verantwortung gegenüber unternehmen",
          "rolle als verantwortlicher",
          "verantwortung übernehmen",
          "verantwortungsgefühl aktivieren",
          "zuständigkeit als identität",
          "verantwortungsträger",
          "entscheidungspflicht"
        ]
      },
      "Kontrollidentität (P TS+, P CO+)": {
        "label": "Kontrollidentität",
        "aliases": [
          "kontrollidentität",
          "kontrolle behalten",
          "kunde will kontrolle",
          "kontrollverlust vermeiden",
          "souveränität",
          "steuerung behalten",
          "überblick gewinnen",
          "selbstbestimmt entscheiden",
          "handlungskontrolle",
          "kontrollbedürfnis",
          "kunde will steuern",
          "entscheidung gibt kontrolle",
          "wieder handlungsfähig werden",
          "kontrolle über situation",
          "nicht fremdbestimmt sein"
        ]
      },
      "Entscheider-Identität (P TS+, P CO+)": {
        "label": "Entscheider-Identität",
        "aliases": [
          "entscheideridentität",
          "kunde als entscheider",
          "entscheidungsträger",
          "macherrolle",
          "entscheiderrolle aktivieren",
          "klare entscheidung treffen",
          "entscheidungskraft",
          "kunde trifft entscheidung",
          "verantwortlicher entscheider",
          "entschlusskraft",
          "buyer entscheidet",
          "nicht nur prüfen entscheiden",
          "entscheider selbstbild",
          "entscheidungskompetenz",
          "entscheiderfrage"
        ]
      },
      "Zukünftiges Selbst (P TS+)": {
        "label": "Zukünftiges Selbst",
        "aliases": [
          "zukünftiges selbst",
          "future self",
          "wer will der kunde werden",
          "zukunftsbild",
          "vision des kunden",
          "kunde in einem jahr",
          "zukünftige version",
          "bestes selbst",
          "zielidentität",
          "zukunftsrolle",
          "wo will der kunde hin",
          "zukünftige situation",
          "future pacing",
          "entwicklungsszenario",
          "zukünftiger zustand"
        ]
      }
    }
  },
  "Bedarfsweckung (D CL+, D SC+, D MA+, D ME+, D CO+)": {
    "label": "Bedarfsweckung",
    "aliases": [
      "bedarfsweckung",
      "bedarf wecken",
      "bedarf vertiefen",
      "mangel erkennen",
      "mangel herausarbeiten",
      "mangel konkretisieren",
      "bedarf sichtbar machen",
      "bedarf herausarbeiten",
      "bedarf entwickeln",
      "bedarf diagnostizieren",
      "mangelsituation verstehen",
      "bedarfssituation verstehen",
      "bedarf greifbar machen",
      "relevanz des mangels klären",
      "ausgangssituation vertiefen"
    ],
    "applications": {
      "Mangelerkennung / Klarheit (D CL+)": {
        "label": "Mangelerkennung / Klarheit",
        "aliases": [
          "mangel erkennen",
          "mangel konkretisieren",
          "klarheit zum mangel schaffen",
          "bedarf konkretisieren",
          "bedarf diagnostizieren",
          "mangel sichtbar machen",
          "woran festmachen",
          "aussage konkretisieren",
          "situation genauer verstehen",
          "bedarf greifbar machen",
          "mangel greifbar machen",
          "fehlende klarheit herstellen",
          "bedarf genauer verstehen",
          "ursache des mangels verstehen",
          "mangelsituation klären"
        ]
      },
      "Konkrete Szene (D SC+)": {
        "label": "Konkrete Szene",
        "aliases": [
          "konkrete szene",
          "konkrete situation",
          "konkreten fall öffnen",
          "beispielsituation",
          "letzte situation",
          "letztes vorkommnis",
          "konkretes beispiel",
          "situation rekonstruieren",
          "ablauf konkretisieren",
          "realen fall durchgehen",
          "konkreten moment beschreiben",
          "situation detaillieren",
          "wann ist das passiert",
          "konkreten ablauf verstehen",
          "mangel in konkreter situation"
        ]
      },
      "Magnitude / Ausmaß (D MA+)": {
        "label": "Magnitude / Ausmaß",
        "aliases": [
          "magnitude",
          "ausmaß",
          "umfang des mangels",
          "größe des mangels",
          "dauer des mangels",
          "häufigkeit des mangels",
          "reichweite des mangels",
          "größenordnung herausarbeiten",
          "mangel quantifizieren",
          "umfang konkretisieren",
          "dauer konkretisieren",
          "häufigkeit konkretisieren",
          "betroffene bereiche erfassen",
          "tragweite des mangels",
          "dimension des mangels"
        ]
      },
      "Relevanz / Bedeutung (D ME+)": {
        "label": "Relevanz / Bedeutung",
        "aliases": [
          "relevanz herausarbeiten",
          "bedeutung herausarbeiten",
          "bedeutung des mangels",
          "relevanz des mangels",
          "warum ist das wichtig",
          "weshalb ist das wichtig",
          "was bedeutet das für sie",
          "persönliche bedeutung",
          "geschäftliche bedeutung",
          "relevanz für den alltag",
          "relevanz für das team",
          "relevanz für das unternehmen",
          "stellenwert des mangels",
          "bedeutung genauer verstehen",
          "warum spielt das eine rolle"
        ]
      },
      "Folgewirkung (D CO+)": {
        "label": "Folgewirkung",
        "aliases": [
          "folgewirkung",
          "einfluss des mangels",
          "auswirkung des mangels",
          "auswirkungen verstehen",
          "einfluss auf andere bereiche",
          "auswirkung auf den alltag",
          "auswirkung auf das team",
          "auswirkung auf das unternehmen",
          "weitere bereiche betroffen",
          "zusammenhänge des mangels",
          "was hängt damit zusammen",
          "welchen einfluss hat das",
          "was verändert sich dadurch",
          "welche bereiche beeinflusst das",
          "wirkung des mangels verstehen"
        ]
      }
    }
  },
  "Schmerz-Vertiefung (D EM+, D SC+, D ME+, D MA+, D CO+)": {
    "label": "Schmerz-Vertiefung",
    "aliases": [
      "schmerz vertiefen",
      "pain digging",
      "problem vertiefen",
      "problemfolgen klären",
      "konsequenz vertiefen",
      "auswirkungen herausarbeiten",
      "status quo schmerz",
      "pain point fragen",
      "problem ernst machen",
      "nicht handeln schmerz",
      "kunde erkennt problemtiefe",
      "implikationsfrage",
      "auswirkungsfrage",
      "problem wird teuer",
      "dringlichkeit durch schmerz"
    ],
    "applications": {
      "Finanzieller Schmerz (D MA+, D CO+)": {
        "label": "Finanzieller Schmerz",
        "aliases": [
          "finanzieller schmerz",
          "geldverlust",
          "umsatzverlust",
          "gewinnverlust",
          "kosten steigen",
          "geld verbrennen",
          "finanzielle auswirkung",
          "budgetloch",
          "verlorener umsatz",
          "teure ineffizienz",
          "kostenfalle",
          "wirtschaftlicher schaden",
          "finanzielle konsequenz",
          "marge verliert",
          "profit leidet"
        ]
      },
      "Zeitverlust (D MA+, D CO+)": {
        "label": "Zeitverlust",
        "aliases": [
          "zeitverlust",
          "zeitverschwendung",
          "ineffizienz",
          "stunden verlieren",
          "zu viel zeitaufwand",
          "doppelarbeit",
          "leerlauf",
          "wartezeit",
          "verzögerung",
          "zeitfresser",
          "prozess dauert zu lange",
          "verlorene arbeitszeit",
          "zeitkosten",
          "langsame abläufe",
          "produktive zeit geht verloren"
        ]
      },
      "Emotionaler Schmerz (D EM+)": {
        "label": "Emotionaler Schmerz",
        "aliases": [
          "emotionaler schmerz",
          "frust",
          "stress",
          "nervt den kunden",
          "belastung",
          "druck im alltag",
          "unzufriedenheit",
          "sorge",
          "genervt von problem",
          "emotional belastet",
          "schlaflose nächte",
          "bauchschmerzen wegen problem",
          "mentale belastung",
          "demotivation",
          "frustration vertiefen"
        ]
      },
      "Opportunitätskosten (D MA+, D CO+)": {
        "label": "Opportunitätskosten",
        "aliases": [
          "opportunitätskosten",
          "verpasste chance",
          "entgangener umsatz",
          "chance verpasst",
          "nicht genutztes potenzial",
          "wettbewerber zieht vorbei",
          "marktchance verloren",
          "zu spät reagieren",
          "verlorenes geschäft",
          "wachstum verpasst",
          "nicht dabei sein",
          "potenzial bleibt liegen",
          "chance nicht genutzt",
          "entgangener vorteil",
          "rückstand zum wettbewerb"
        ]
      },
      "Beziehungsschmerz (D EM+, D ME+, D CO+)": {
        "label": "Beziehungsschmerz",
        "aliases": [
          "beziehungsschmerz",
          "kunden verlieren",
          "teamkonflikt",
          "interne spannung",
          "kundenunzufriedenheit",
          "mitarbeiter frust",
          "vertrauensverlust",
          "konflikte im team",
          "schlechte stimmung",
          "beziehung leidet",
          "kundenbindung sinkt",
          "team leidet unter problem",
          "kommunikationsproblem",
          "interner reibungsverlust",
          "fluktuation wegen problem"
        ]
      },
      "Statusverlust (D EM+, D ME+, D CO+)": {
        "label": "Statusverlust",
        "aliases": [
          "statusverlust",
          "imageverlust",
          "ruf leidet",
          "ansehen verlieren",
          "peinlich vor team",
          "nicht professionell wirken",
          "reputation leidet",
          "vertrauensverlust im markt",
          "kunde wirkt rückständig",
          "wettbewerber wirkt besser",
          "nicht mehr führend",
          "zurückfallen",
          "professioneller anspruch gefährdet",
          "sichtbarer rückstand",
          "prestigeverlust"
        ]
      },
      "Kontrollverlust (D EM+, D ME+, D CO+)": {
        "label": "Kontrollverlust",
        "aliases": [
          "kontrollverlust",
          "kunde verliert kontrolle",
          "chaos im prozess",
          "kein überblick",
          "nicht steuerbar",
          "abhängig von anderen",
          "fremdbestimmt",
          "hilflosigkeit",
          "problem eskaliert",
          "außer kontrolle",
          "nicht mehr handlungsfähig",
          "überforderung",
          "keine transparenz",
          "unkontrollierte situation",
          "steuerungsverlust"
        ]
      },
      "Wachstumsschmerz (D ME+, D CO+)": {
        "label": "Wachstumsschmerz",
        "aliases": [
          "wachstumsschmerz",
          "stagnation",
          "stillstand",
          "nicht weiterkommen",
          "wachstum blockiert",
          "skalierung scheitert",
          "plateau erreicht",
          "fortschritt bleibt aus",
          "entwicklung stagniert",
          "limit erreicht",
          "wachstumsbremse",
          "keine entwicklung",
          "rückstand wächst",
          "wachstum bleibt liegen",
          "nächste stufe blockiert"
        ]
      },
      "Risikoschmerz (D EM+, D CO+)": {
        "label": "Risikoschmerz",
        "aliases": [
          "risikoschmerz",
          "angst vor verlust",
          "sicherheitsrisiko",
          "fehlentscheidung droht",
          "risiko wächst",
          "unsicherer status quo",
          "potenzieller verlust",
          "problem kann kippen",
          "gefährliche entwicklung",
          "risiko wird größer",
          "absicherung fehlt",
          "verwundbare situation",
          "risiko durch warten",
          "Folgerisiko",
          "unkalkulierbares Risiko"
        ]
      },
      "Zukünftige Konsequenz (D SC+, D CO+)": {
        "label": "Zukünftige Konsequenz",
        "aliases": [
          "zukünftige konsequenz",
          "was passiert später",
          "langfristiger schaden",
          "folgeproblem",
          "problem wird größer",
          "später teurer",
          "negative entwicklung",
          "wenn nichts passiert",
          "zukunftsrisiko",
          "spätfolge",
          "künftige auswirkung",
          "konsequenz in sechs monaten",
          "problem verschleppt",
          "zukünftiger druck",
          "langfristige folgen"
        ]
      }
    }
  },
  "Sprachmuster": {
    "label": "Sprachmuster",
    "aliases": [
      "verhaltensmuster",
      "kundenmuster erkennen",
      "muster im verkauf",
      "verhalten analysieren",
      "entscheidungmuster",
      "kaufverhalten verstehen",
      "wiederkehrendes muster",
      "kundenverhalten spiegeln",
      "verkaufsmuster",
      "blockademuster",
      "handlungsblockade",
      "vermeidungsmuster",
      "diagnosefrage muster",
      "muster erkennen im call",
      "verhalten hinterfragen"
    ],
    "applications": {
      "Ich vs. Wir": {
        "label": "Ich vs. Wir",
        "aliases": [
          "ich vs wir",
          "allein oder team",
          "einzelkämpfer",
          "teamentscheidung",
          "silo denken",
          "wir gefühl fehlt",
          "allein entscheiden",
          "gemeinsam entscheiden",
          "team einbinden",
          "kollektive verantwortung",
          "isoliertes handeln",
          "nicht integriert",
          "abstimmung im team",
          "team oder einzelperson",
          "gemeinsame lösung"
        ]
      },
      "Wunsch vs. Pflicht": {
        "label": "Wunsch vs. Pflicht",
        "aliases": [
          "wunsch vs pflicht",
          "müssen oder wollen",
          "kunde will nicht muss aber",
          "freiwillig oder gezwungen",
          "pflichtgefühl",
          "intrinsische motivation",
          "extrinsischer druck",
          "sollte aber will nicht",
          "notwendigkeit vs wunsch",
          "motivation klären",
          "kunde fühlt zwang",
          "wollen herausarbeiten",
          "muss problem",
          "pflichtentscheidung",
          "echte motivation prüfen"
        ]
      },
      "Reden vs. Handeln": {
        "label": "Reden vs. Handeln",
        "aliases": [
          "reden vs handeln",
          "viel reden wenig tun",
          "nichts passiert",
          "nur geredet",
          "keine umsetzung",
          "heiße luft",
          "leere versprechen",
          "meeting ohne ergebnis",
          "diskussion statt aktion",
          "tatenlosigkeit",
          "kunde redet nur",
          "umsetzungslücke",
          "aktion fehlt",
          "wissen aber nicht handeln",
          "vom reden ins handeln"
        ]
      },
      "Problem vs. Priorität": {
        "label": "Problem vs. Priorität",
        "aliases": [
          "problem vs priorität",
          "problem aber keine priorität",
          "nicht wichtig genug",
          "problem wird toleriert",
          "kunde akzeptiert problem",
          "priorität fehlt",
          "problem ignorieren",
          "nicht handlungsrelevant",
          "agenda fehlt",
          "randthema",
          "problem liegt brach",
          "dringlichkeit fehlt",
          "wichtig aber nicht dringend",
          "prioritätenkonflikt",
          "problem ernst machen"
        ]
      },
      "Bedenken vs. Konsequenz": {
        "label": "Bedenken vs. Konsequenz",
        "aliases": [
          "bedenken vs konsequenz",
          "kunde grübelt",
          "zögern wegen bedenken",
          "analyse statt entscheidung",
          "sorge blockiert handlung",
          "bedenken wichtiger als folgen",
          "nicht handeln wegen bedenken",
          "konsequenz trotz bedenken",
          "kunde bleibt im überlegen",
          "bedenkenträger",
          "analysis paralysis",
          "grübeln statt handeln",
          "warten aus sorge",
          "folgen des zögerns",
          "bedenken öffnen"
        ]
      },
      "Erkenntnis vs. Entscheidung": {
        "label": "Erkenntnis vs. Entscheidung",
        "aliases": [
          "erkenntnis vs entscheidung",
          "verstanden aber nicht entschieden",
          "aha moment ohne aktion",
          "einsicht ohne umsetzung",
          "wissen reicht nicht",
          "klarheit ohne konsequenz",
          "kunde versteht aber handelt nicht",
          "knowing doing gap",
          "durchbruch ohne entscheidung",
          "theorie statt praxis",
          "einsicht aktivieren",
          "entscheidung nach erkenntnis",
          "verstanden aber zögert",
          "nicht umgesetzt trotz einsicht",
          "erkenntnis in entscheidung wandeln"
        ]
      },
      "Zustimmung vs. Commitment": {
        "label": "Zustimmung vs. Commitment",
        "aliases": [
          "zustimmung vs commitment",
          "ja aber kein commitment",
          "kunde stimmt zu aber kauft nicht",
          "nicken ohne zusage",
          "halbes ja",
          "unverbindliche zustimmung",
          "zustimmung ohne handlung",
          "kunde sagt ja vielleicht",
          "nicht festlegen trotz zustimmung",
          "interesse ohne commitment",
          "scheinbare zustimmung",
          "echtes commitment prüfen",
          "zustimmung verbindlich machen",
          "kunde bleibt offen",
          "ja sagen und nichts tun"
        ]
      },
      "Interesse vs. Verantwortungsübernahme": {
        "label": "Interesse vs. Verantwortungsübernahme",
        "aliases": [
          "interesse vs verantwortung",
          "nur interessiert",
          "zuschauer statt macher",
          "kunde beobachtet nur",
          "interesse ohne handeln",
          "neugier aber keine verantwortung",
          "tourist oder käufer",
          "passiv oder aktiv",
          "kunde übernimmt keine verantwortung",
          "window shopper",
          "interessent oder kunde",
          "verantwortung aktivieren",
          "vom zuschauer zum spieler",
          "engagement prüfen",
          "interesse in verantwortung wandeln"
        ]
      },
      "Kontrolle vs. Delegation": {
        "label": "Kontrolle vs. Delegation",
        "aliases": [
          "kontrolle vs delegation",
          "kunde kann nicht loslassen",
          "alles selbst machen",
          "delegieren fällt schwer",
          "mikromanagement",
          "kontrollbedürfnis",
          "abgeben lernen",
          "vertrauen in team fehlt",
          "kontrolle abgeben",
          "nicht delegieren können",
          "alle zügel behalten",
          "loslassen im verkauf",
          "team nutzen",
          "macht abgeben",
          "selbst machen oder abgeben"
        ]
      },
      "Vermeidung vs. Klarheit": {
        "label": "Vermeidung vs. Klarheit",
        "aliases": [
          "vermeidung vs klarheit",
          "kopf in den sand",
          "problem vermeiden",
          "realität nicht anschauen",
          "wegsehen",
          "nicht wahrhaben wollen",
          "vermeidungsverhalten",
          "klarheit vermeiden",
          "konfrontation vermeiden",
          "reality check",
          "kunde verdrängt problem",
          "thema ausblenden",
          "klarheit schaffen",
          "problem anschauen",
          "vermeidung auflösen"
        ]
      },
      "Vergleich vs. Entscheidung": {
        "label": "Vergleich vs. Entscheidung",
        "aliases": [
          "vergleich vs entscheidung",
          "immer vergleichen nie entscheiden",
          "vergleichsparalyse",
          "zu viele optionen",
          "choice overload",
          "kunde vergleicht endlos",
          "optionsparalyse",
          "benchmark statt kauf",
          "vergleichsfixiert",
          "nicht entscheiden wegen alternativen",
          "vergleichsrausch",
          "vergleich stoppen",
          "entscheidung trotz optionen",
          "optionen überfordern",
          "vergleichen statt kaufen"
        ]
      },
      "Komfort vs. Wachstum": {
        "label": "Komfort vs. Wachstum",
        "aliases": [
          "komfort vs wachstum",
          "komfortzone",
          "komfortzone verlassen",
          "wachstum ist unbequem",
          "kunde bleibt bequem",
          "gewohnheit blockiert wachstum",
          "routine hält zurück",
          "angst vor veränderung",
          "unbequem aber nötig",
          "sicherer hafen",
          "wachstumsschritt",
          "veränderung vermeiden",
          "neuland betreten",
          "weiterentwicklung trotz unbehagen",
          "komfort blockiert entscheidung"
        ]
      },
      "Kosten vs. Konsequenz": {
        "label": "Kosten vs. Konsequenz",
        "aliases": [
          "kosten vs konsequenz",
          "preis oder folgekosten",
          "billig wird teuer",
          "falsch sparen",
          "kostenfokus",
          "langfristig teurer",
          "nicht investieren kostet mehr",
          "preis vs folgeschaden",
          "kurzfristig sparen",
          "langfristig verlieren",
          "kosten des nichts tuns",
          "billigste lösung teuerste lösung",
          "sparsamkeit blockiert",
          "investition vermeiden",
          "kosten gegen konsequenz stellen"
        ]
      },
      "Später vs. Jetzt": {
        "label": "Später vs. Jetzt",
        "aliases": [
          "später vs jetzt",
          "aufschieben",
          "prokrastination",
          "nicht heute",
          "irgendwann später",
          "kunde verschiebt",
          "später vielleicht",
          "jetzt oder später",
          "aufschieberitis",
          "morgen entscheiden",
          "nächste woche",
          "nächstes jahr",
          "zeit gewinnen",
          "entscheidung vertagen",
          "aufschieben hinterfragen"
        ]
      },
      "Kosten vs. Investment": {
        "label": "Kosten vs. Investment",
        "aliases": [
          "kosten vs investment",
          "kosten oder investment",
          "ausgabe vs investition",
          "kostenblock",
          "reine kosten",
          "geld ausgeben",
          "investment statt kosten",
          "investition statt ausgabe",
          "kostenfokus",
          "geld ist weg",
          "ausgabe als verlust",
          "investitionsdenken",
          "kostenmentalität",
          "ausgabe oder wertschöpfung",
          "kosten in investment umdenken"
        ]
      },
      "Risiko vs. Chance": {
        "label": "Risiko vs. Chance",
        "aliases": [
          "risiko vs chance",
          "risiko oder chance",
          "nur risiko sehen",
          "chance nicht sehen",
          "angst vor risiko",
          "risikofokus",
          "möglichkeit ausblenden",
          "negative erwartung",
          "was wenn es nicht funktioniert",
          "scheitern statt erfolg",
          "downside statt upside",
          "verlustchance",
          "chance trotz risiko",
          "risiko einseitig betrachten",
          "von risiko zu chance"
        ]
      },
      "Kurzfristig vs. Langfristig": {
        "label": "Kurzfristig vs. Langfristig",
        "aliases": [
          "kurzfristig vs langfristig",
          "kurzfristiges denken",
          "langfristiges denken",
          "zu kurzer zeithorizont",
          "nur kurzfristig betrachten",
          "nur die nächsten monate",
          "kurzfristiger fokus",
          "langfristige perspektive fehlt",
          "kurzfristig bewerten",
          "langfristigen wert übersehen",
          "heute statt zukunft",
          "kurzer betrachtungszeitraum",
          "langfristige auswirkung",
          "kurzfristiger aufwand langfristiger nutzen",
          "zeithorizont erweitern"
        ]
      },
      "Kurzfristiges Commitment vs. nachhaltiger Erfolg": {
        "label": "Kurzfristiges Commitment vs. nachhaltiger Erfolg",
        "aliases": [
          "kurzfristiges commitment vs nachhaltiger erfolg",
          "kurzes commitment",
          "langfristiges commitment",
          "zu kurz ausprobieren",
          "nur kurz testen",
          "ein monat testen",
          "zwei monate testen",
          "zu kurze laufzeit",
          "erfolg braucht zeit",
          "nachhaltiger erfolg braucht commitment",
          "commitment als erfolgsvoraussetzung",
          "langfristig dranbleiben",
          "zu früh aufhören",
          "kontinuität für erfolg",
          "dauerhafte umsetzung"
        ]
      }
    }
  },
  "Storytelling (P TC+, P TO+, P CN+, P TS+, P SK-)": {
    "label": "Storytelling",
    "aliases": [
      "storytelling",
      "verkaufsstory",
      "geschichte im verkauf",
      "story im sales call",
      "metapher nutzen",
      "analogie nutzen",
      "kundenstory erzählen",
      "case story",
      "beispielgeschichte",
      "narrativ im verkauf",
      "story statt pitch",
      "vertrauen durch story",
      "einwand mit story lösen",
      "problem durch story erklären",
      "entscheidung durch story erleichtern"
    ],
    "applications": {
      "Kundenstory (P TC+, P TO+, P SK-)": {
        "label": "Kundenstory",
        "aliases": [
          "kundenstory",
          "customer story",
          "kundenbeispiel",
          "fallbeispiel",
          "case study",
          "praxisbeispiel",
          "referenzfall",
          "kundenerfahrung",
          "kundenbericht",
          "ähnlicher kunde",
          "beispielkunde",
          "kundencase",
          "erfahrungsbericht",
          "kunde wie sie",
          "vergleichbarer kundenfall"
        ]
      },
      "Before-After (P TO+, P CN+, P TS+)": {
        "label": "Before-After",
        "aliases": [
          "before after",
          "vorher nachher",
          "vorher nachher story",
          "transformation story",
          "wandel zeigen",
          "früher heute vergleich",
          "dann und jetzt",
          "ausgangslage ergebnis",
          "turnaround story",
          "kontrastgeschichte",
          "vorher nachher effekt",
          "kundenwandel",
          "ergebnisveränderung",
          "transformation erzählen",
          "vom problem zur lösung"
        ]
      },
      "Founder Story (P TC+)": {
        "label": "Founder Story",
        "aliases": [
          "founder story",
          "gründergeschichte",
          "warum wir",
          "wie alles begann",
          "entstehungsgeschichte",
          "ursprungsgeschichte",
          "mission erzählen",
          "warum ich das mache",
          "hintergrundgeschichte",
          "persönliche geschichte",
          "startup story",
          "gründerstory",
          "unternehmensgeschichte",
          "vision erzählen",
          "herkunft der lösung"
        ]
      },
      "Metapher (P CN+, P SK-)": {
        "label": "Metapher",
        "aliases": [
          "metapher",
          "metapher im verkauf",
          "bildlicher vergleich",
          "bildsprache",
          "gleichnis",
          "verkaufsmetapher",
          "problem bildlich erklären",
          "lösung bildlich erklären",
          "symbolischer vergleich",
          "einwand mit metapher",
          "komplexität mit bild erklären",
          "bildhafte erklärung",
          "anschauliches bild",
          "metaphorische frage",
          "verkaufsbild"
        ]
      },
      "Analogie (P CN+, P SK-)": {
        "label": "Analogie",
        "aliases": [
          "analogie",
          "analogie im verkauf",
          "vergleich nutzen",
          "wie bei vergleich",
          "ähnlicher fall",
          "parallelbeispiel",
          "vergleichsargument",
          "analoger fall",
          "lösung mit analogie erklären",
          "problem mit analogie erklären",
          "verkaufsanalogie",
          "verständnis durch vergleich",
          "vergleichbare situation",
          "analogiefrage",
          "kompliziertes einfach erklären"
        ]
      },
      "Erfolgsgeschichte (P TC+, P TO+, P TS+, P SK-)": {
        "label": "Erfolgsgeschichte",
        "aliases": [
          "erfolgsgeschichte",
          "success story",
          "erfolgsstory",
          "erfolgsfall",
          "kundenerfolg",
          "durchbruchgeschichte",
          "turnaround beispiel",
          "best case",
          "erfolgsbeispiel",
          "result story",
          "gewinnstory",
          "ergebnisgeschichte",
          "kundenresultat erzählen",
          "showcase story",
          "proof story"
        ]
      }
    }
  },
  "Realitätsabgleich (P TS-)": {
    "label": "Realitätsabgleich",
    "aliases": [
      "realitätsabgleich",
      "realität prüfen",
      "realitätscheck",
      "selbsteinschätzung kalibrieren",
      "eigenwahrnehmung kalibrieren",
      "überschätzung prüfen",
      "realistische einschätzung herstellen",
      "selbstbild mit realität abgleichen",
      "fähigkeiten realistisch einschätzen",
      "ressourcen realistisch einschätzen",
      "komplexität realistisch einschätzen",
      "differenzierung realistisch einschätzen",
      "situation nüchtern prüfen",
      "realitätsprüfung",
      "überhöhte selbsteinschätzung korrigieren"
    ],
    "applications": {
      "Realitätswahrnehmung (P TS-)": {
        "label": "Realitätswahrnehmung",
        "aliases": [
          "realitätswahrnehmung",
          "realität wahrnehmen",
          "situation realistisch einschätzen",
          "status quo realistisch sehen",
          "eigene lage prüfen",
          "realitätscheck der situation",
          "selbsteinschätzung prüfen",
          "überschätzung der lage",
          "realistische standortbestimmung",
          "fakten gegen wahrnehmung",
          "wahrnehmung mit fakten abgleichen",
          "realität statt annahme",
          "lage nüchtern bewerten",
          "tatsächlichen zustand prüfen",
          "realitätsbild kalibrieren"
        ]
      },
      "Komplexitätswahrnehmung (P TS-)": {
        "label": "Komplexitätswahrnehmung",
        "aliases": [
          "komplexitätswahrnehmung",
          "komplexität realistisch einschätzen",
          "aufwand unterschätzt",
          "ist nicht so einfach",
          "komplexität unterschätzt",
          "abhängigkeiten sichtbar machen",
          "schwierigkeit realistisch prüfen",
          "anforderungen unterschätzt",
          "komplexität kalibrieren",
          "aufgabe wirkt zu einfach",
          "mehrschrittigkeit sichtbar machen",
          "komplexe zusammenhänge prüfen",
          "unterschätzte schwierigkeit",
          "realer komplexitätsgrad",
          "komplexität nicht unterschätzen"
        ]
      },
      "Fähigkeitswahrnehmung (P TS-)": {
        "label": "Fähigkeitswahrnehmung",
        "aliases": [
          "fähigkeitswahrnehmung",
          "eigene fähigkeiten realistisch einschätzen",
          "kompetenz realistisch prüfen",
          "können wir das wirklich selbst",
          "fähigkeiten überschätzt",
          "know how prüfen",
          "kompetenzlücke sichtbar machen",
          "eigenes können kalibrieren",
          "umsetzungsfähigkeit prüfen",
          "interne expertise prüfen",
          "fähigkeitsgrenzen erkennen",
          "skill level realistisch einschätzen",
          "kompetenzüberschätzung",
          "eigene expertise hinterfragen",
          "reale fähigkeiten prüfen"
        ]
      },
      "Ressourcenwahrnehmung (P TS-)": {
        "label": "Ressourcenwahrnehmung",
        "aliases": [
          "ressourcenwahrnehmung",
          "ressourcen realistisch einschätzen",
          "kapazitäten prüfen",
          "haben wir wirklich genug ressourcen",
          "personalkapazität prüfen",
          "zeitressourcen prüfen",
          "budgetressourcen prüfen",
          "infrastruktur prüfen",
          "ressourcenüberschätzung",
          "verfügbare kapazität realistisch",
          "umsetzungsressourcen prüfen",
          "ressourcenlage kalibrieren",
          "verfügbarkeit realistisch einschätzen",
          "interne kapazität hinterfragen",
          "reale ressourcen prüfen"
        ]
      },
      "Differenzierungswahrnehmung (P TS-)": {
        "label": "Differenzierungswahrnehmung",
        "aliases": [
          "differenzierungswahrnehmung",
          "wir sind anders",
          "unsere branche ist anders",
          "unsere kunden sind anders",
          "sonderfall prüfen",
          "differenzierung realistisch einschätzen",
          "besonderheit hinterfragen",
          "tatsächlichen unterschied prüfen",
          "relevante differenzierung",
          "vermeintliche differenzierung",
          "andersartigkeit kalibrieren",
          "marktunterschied realistisch prüfen",
          "besonderheit mit realität abgleichen",
          "unterschied wirklich relevant",
          "differenzierungsüberschätzung"
        ]
      }
    }
  },
  "Erwartungsverzerrung (P TO-)": {
    "label": "Erwartungsverzerrung",
    "aliases": [
      "erwartungsverzerrung",
      "erwartungen kalibrieren",
      "unrealistische erwartung",
      "überhöhte erwartung",
      "angebotserwartung korrigieren",
      "lösungsversprechen realistisch machen",
      "erwartungsmanagement",
      "falsche erwartung an lösung",
      "zu hohe erwartung",
      "realistische ergebnisannahme",
      "erwartung an umsetzung kalibrieren",
      "keine falschen versprechen",
      "lösungsgrenzen klären",
      "angebot realistisch einordnen",
      "erwartungsbild korrigieren"
    ],
    "applications": {
      "Aufwand / Zeit (P TO-)": {
        "label": "Aufwand / Zeit",
        "aliases": [
          "aufwand zeit",
          "aufwand unterschätzt",
          "zeitaufwand unterschätzt",
          "geht nicht von allein",
          "umsetzung braucht zeit",
          "implementierungsaufwand",
          "laufzeit realistisch machen",
          "zeit bis ergebnis",
          "aufwand realistisch einschätzen",
          "nicht sofort erledigt",
          "umsetzungsdauer kalibrieren",
          "eigenaufwand erklären",
          "zeitliche erwartung korrigieren",
          "arbeitsaufwand realistisch",
          "geschwindigkeit nicht überschätzen"
        ]
      },
      "Fähigkeiten (P TO-)": {
        "label": "Fähigkeiten",
        "aliases": [
          "fähigkeiten der lösung",
          "lösung kann nicht alles",
          "funktionsumfang realistisch",
          "fähigkeiten nicht überschätzen",
          "systemgrenzen erklären",
          "was die lösung kann",
          "was die lösung nicht kann",
          "leistungsumfang kalibrieren",
          "funktionserwartung korrigieren",
          "angebot kann nicht alles",
          "fähigkeitsgrenzen der lösung",
          "leistungsversprechen realistisch",
          "produktleistung realistisch",
          "lösung korrekt einordnen",
          "capability expectation"
        ]
      },
      "Erfolgswahrscheinlichkeit (P TO-)": {
        "label": "Erfolgswahrscheinlichkeit",
        "aliases": [
          "erfolgswahrscheinlichkeit",
          "erfolg nicht garantiert",
          "wahrscheinlichkeit realistisch",
          "erfolgschance kalibrieren",
          "kein sicherer erfolg",
          "ergebniswahrscheinlichkeit",
          "erfolg hängt von faktoren ab",
          "chance statt garantie",
          "realistische erfolgserwartung",
          "ergebnis nicht versprechen",
          "erfolgsaussicht einordnen",
          "wahrscheinlichkeit statt sicherheit",
          "erfolg nicht automatisch",
          "realistische resultaterwartung",
          "erfolgschance nicht überschätzen"
        ]
      },
      "Garantien (P TO-)": {
        "label": "Garantien",
        "aliases": [
          "garantien",
          "garantieerwartung",
          "keine erfolgsgarantie",
          "garantiegrenzen",
          "was wird garantiert",
          "was wird nicht garantiert",
          "garantie realistisch einordnen",
          "keine falsche garantie",
          "garantieumfang klären",
          "garantie nicht überinterpretieren",
          "vertragliche garantie klären",
          "garantieversprechen begrenzen",
          "ergebnisgarantie korrigieren",
          "garantieerwartung kalibrieren",
          "garantien transparent machen"
        ]
      },
      "Sicherheiten (P TO-)": {
        "label": "Sicherheiten",
        "aliases": [
          "sicherheiten",
          "sicherheitserwartung",
          "kein nullrisiko",
          "vollständige sicherheit gibt es nicht",
          "restrisiko klären",
          "absicherung realistisch",
          "sicherheitsgrenzen",
          "risiko bleibt bestehen",
          "keine absolute sicherheit",
          "sicherheitsversprechen kalibrieren",
          "absicherung nicht überschätzen",
          "risiko realistisch einordnen",
          "kontrolle nicht vollständig",
          "sicherheitsniveau erklären",
          "sicherheitserwartung korrigieren"
        ]
      }
    }
  },
  "Lösungsbewusstsein (P CC-)": {
    "label": "Lösungsbewusstsein",
    "aliases": [
      "lösungsbewusstsein",
      "lösung wieder sichtbar machen",
      "hoffnung herstellen",
      "ausweg sichtbar machen",
      "problem ist lösbar",
      "handlungsfähigkeit zurückgeben",
      "aus hoffnungslosigkeit lösen",
      "lösbarkeit zeigen",
      "weg nach vorne öffnen",
      "problem nicht überwältigend",
      "wieder möglichkeiten sehen",
      "lösungsraum öffnen",
      "resignation lösen",
      "zuversicht in lösbarkeit",
      "vom problem zur lösung"
    ],
    "applications": {
      "Lösbarkeit (P CC-)": {
        "label": "Lösbarkeit",
        "aliases": [
          "lösbarkeit",
          "problem ist lösbar",
          "lösungsweg existiert",
          "es gibt eine lösung",
          "machbarkeit sichtbar machen",
          "lösungsansatz zeigen",
          "lösbar statt aussichtslos",
          "problem kann gelöst werden",
          "konkrete lösbarkeit",
          "machbare lösung",
          "lösung realistisch erreichbar",
          "lösungsmöglichkeit",
          "ausweg existiert",
          "lösbarkeit verankern",
          "machbarkeit herstellen"
        ]
      },
      "Hoffnung (P CC-)": {
        "label": "Hoffnung",
        "aliases": [
          "hoffnung",
          "hoffnung herstellen",
          "zuversicht",
          "nicht hoffnungslos",
          "positive aussicht",
          "wieder perspektive sehen",
          "resignation lösen",
          "mut machen",
          "zukunft ist veränderbar",
          "aussicht auf verbesserung",
          "hoffnung auf lösung",
          "optimismus realistisch",
          "zuversicht zurückgeben",
          "ausweglosigkeit lösen",
          "hoffnung statt resignation"
        ]
      },
      "Handlungsfähigkeit (P CC-)": {
        "label": "Handlungsfähigkeit",
        "aliases": [
          "handlungsfähigkeit",
          "wieder handeln können",
          "handlungsoptionen sehen",
          "nicht ausgeliefert sein",
          "nächster machbarer hebel",
          "kontrolle zurückgewinnen",
          "aktive möglichkeit",
          "etwas tun können",
          "handlungsspielraum",
          "wirksamer schritt möglich",
          "nicht hilflos",
          "gestaltbarkeit zeigen",
          "handlungsraum öffnen",
          "wieder steuerbar",
          "handlungsfähigkeit herstellen"
        ]
      },
      "Fortschritt (P CC-)": {
        "label": "Fortschritt",
        "aliases": [
          "fortschritt",
          "kleiner fortschritt",
          "schrittweise verbesserung",
          "nicht alles auf einmal",
          "entwicklung ist möglich",
          "erste verbesserung sichtbar",
          "fortschritt statt perfektion",
          "schritt für schritt",
          "machbare etappen",
          "teilfortschritt",
          "bewegung nach vorne",
          "verbesserungspfad",
          "fortschritt realistisch",
          "entwicklung möglich",
          "positive bewegung"
        ]
      },
      "Zielerreichbarkeit (P CC-)": {
        "label": "Zielerreichbarkeit",
        "aliases": [
          "zielerreichbarkeit",
          "ziel ist erreichbar",
          "realistisches ziel",
          "ziel nicht unerreichbar",
          "erreichbarkeit zeigen",
          "zielpfad sichtbar machen",
          "machbares ziel",
          "realistische zielerreichung",
          "ziel in reichweite",
          "ziel kann erreicht werden",
          "ergebnis ist möglich",
          "ziel nicht aufgeben",
          "erreichbares ergebnis",
          "realistische erfolgsperspektive",
          "zielerreichung plausibel"
        ]
      },
      "Ausweg / Optionen (P CC-)": {
        "label": "Ausweg / Optionen",
        "aliases": [
          "ausweg optionen",
          "ausweg finden",
          "optionen sichtbar machen",
          "alternative wege",
          "mehrere lösungswege",
          "nicht in sackgasse",
          "es gibt optionen",
          "wahlmöglichkeiten öffnen",
          "alternativen sehen",
          "handlungsalternativen",
          "ausweg aus problem",
          "optionen statt ausweglosigkeit",
          "lösungsoptionen",
          "mögliche wege",
          "neue optionen erkennen"
        ]
      }
    }
  }
} as const;


export const TECHNIC_LABELS_EN = {
  "Vorabschlusstechnik": {
    "label": "Pre-Closing Technique",
    "aliases": [
      "pre-closing technique",
      "pre-close technique",
      "pre-close",
      "trial-close question",
      "pre-close question",
      "check the pre-close",
      "review pre-close",
      "pre close",
      "pre-close",
      "pre close technique",
      "pre-close technique",
      "trial close",
      "trial-close"
    ],
    "applications": {
      "Vorabschluss": {
        "label": "Pre-Close",
        "aliases": [
          "pre-close",
          "conduct a pre-close",
          "initiate a pre-close",
          "check the pre-close",
          "ask a trial-close question",
          "check overall fit",
          "check overall fit",
          "check solution as whole",
          "check agreement to the overall solution",
          "check fit the solution",
          "check solution summarize and",
          "pre close",
          "pre-close",
          "trial close",
          "trial-close"
        ]
      }
    }
  },
  "Präsentations Technik": {
    "label": "Presentation Technique",
    "aliases": [
      "presentation technique",
      "presentation technique",
      "presentation",
      "presentation",
      "present the offer",
      "present the offer",
      "present the solution",
      "present the solution",
      "service presentation",
      "offer presentation",
      "sales presentation",
      "sales presentation",
      "presentation technique",
      "offer presentation",
      "solution presentation"
    ],
    "applications": {
      "Präsentations Technik": {
        "label": "Presentation Technique",
        "aliases": [
          "presentation technique",
          "presentation technique",
          "presentation",
          "present the offer",
          "present the solution",
          "offer presentation",
          "service presentation",
          "sales presentation",
          "presentation technique",
          "offer presentation"
        ]
      }
    }
  },
  "ROI Rechnung": {
    "label": "ROI Calculation",
    "aliases": [
      "ROI calculation",
      "ROI calculation",
      "ROI calculation",
      "ROI alignment",
      "compare ROI assumptions",
      "compare ROI values",
      "compare the ROI calculation basis",
      "return on investment",
      "return on investment calculation",
      "return on investment calculation",
      "cost-benefit analysis",
      "cost-benefit calculation",
      "profitability calculation",
      "profitability calculation",
      "investment calculate",
      "return calculate",
      "economic return calculate",
      "compare economic assumptions",
      "compare economic values",
      "compare calculation basis",
      "ROI calculation",
      "return calculation"
    ],
    "applications": {
      "ROI Rechnung": {
        "label": "ROI Calculation",
        "aliases": [
          "ROI calculation",
          "ROI calculation",
          "ROI calculation",
          "return on investment",
          "cost-benefit analysis",
          "profitability calculation",
          "investment calculate",
          "return calculate",
          "ROI calculation",
          "return calculation"
        ]
      },
      "ROI Abgleich": {
        "label": "ROI Alignment",
        "aliases": [
          "ROI alignment",
          "compare ROI assumptions",
          "compare ROI values",
          "compare the ROI calculation basis",
          "compare economic assumptions",
          "compare economic values",
          "compare calculation basis"
        ]
      }
    }
  },
  "Kaufsignal": {
    "label": "Buying Signal",
    "aliases": [
      "buying signal",
      "buying signals",
      "purchase signal",
      "purchase signals",
      "buying signal",
      "buying signals",
      "buy signal",
      "buy signals",
      "purchase signal",
      "purchase signals",
      "purchase interest",
      "concrete purchase interest",
      "readiness to buy",
      "concrete readiness to buy",
      "purchase intention",
      "concrete purchase intention",
      "buying intention",
      "concrete buying intention",
      "interest in closing",
      "readiness to close",
      "interest in deciding",
      "readiness to decide",
      "interest on collaboration",
      "interest on of working together",
      "interest on potential collaboration",
      "interest on a collaboration",
      "interest in collaboration",
      "mental pre-handling",
      "mental pre-handling of working together",
      "mental pre-handling a collaboration",
      "mental pre-handling potential collaboration",
      "mental pre-handling of working together",
      "mental pre-handling a collaboration",
      "prospect imagines collaboration in advance",
      "prospect imagines potential collaboration in advance",
      "prospect imagines working together",
      "prospect imagines a potential collaboration",
      "prospect imagines collaboration in advance",
      "prospect imagines potential collaboration in advance",
      "prospect imagines working together",
      "prospect imagines a potential collaboration",
      "signal for potential collaboration",
      "signal for collaboration",
      "signal about working together",
      "signal for readiness to buy",
      "signal for purchase interest",
      "signal for purchase intention",
      "signal for readiness to decide",
      "buying intent signal",
      "purchase intent signal",
      "buying intent",
      "purchase intent",
      "buying intention",
      "purchase intention"
    ],
    "applications": {
      "Kaufsignal als Frage": {
        "label": "Buying Signal as a Question",
        "aliases": [
          "buying signal as question",
          "buying signal question",
          "buying signal-question",
          "buying question",
          "buying-interested question",
          "question with buying signal",
          "question as buying signal",
          "question with purchase interest",
          "question with purchase intention",
          "question with buying intention",
          "question with readiness to buy",
          "question about working together",
          "question to the potential collaboration",
          "question about the collaboration",
          "question about potential collaboration",
          "question to the process of working together",
          "question to the design of working together",
          "question to the design of working together",
          "question to the implementation of working together",
          "question to the use the solution",
          "use question to the concrete",
          "question to the concrete implementation",
          "question to the concrete process",
          "prospect asks about working together",
          "prospect asks after collaboration",
          "prospect asks after potential collaboration",
          "prospect asks how collaboration looks like",
          "prospect asks how collaboration proceeds",
          "prospect asks how implementation looks like",
          "prospect asks how implementation proceeds",
          "prospect asks how support looks like",
          "prospect asks how support proceeds",
          "prospect asks about working together",
          "prospect asks after collaboration",
          "prospect asks after potential collaboration",
          "prospect asks how collaboration looks like",
          "prospect asks how collaboration proceeds",
          "prospect asks how implementation looks like",
          "prospect asks how implementation proceeds",
          "prospect asks how support looks like",
          "prospect asks how support proceeds",
          "mental pre-handling as question",
          "pre-handling of working together as question",
          "pre-handling potential collaboration as question",
          "buying signal question",
          "buying question",
          "purchase signal question",
          "buying intent question",
          "purchase intent question"
        ]
      },
      "Kaufsignal als Aussage": {
        "label": "Buying Signal as a Statement",
        "aliases": [
          "buying signal as statement",
          "buying signal statement",
          "buying signal-statement",
          "purchase statement",
          "statement with buying signal",
          "statement as buying signal",
          "statement with purchase interest",
          "statement with purchase intention",
          "statement with buying intention",
          "statement with readiness to buy",
          "statement about working together",
          "statement to the potential collaboration",
          "statement about the collaboration",
          "statement about potential collaboration",
          "requirement on collaboration",
          "requirement on the collaboration",
          "requirement on potential collaboration",
          "desire on collaboration",
          "desire on the collaboration",
          "desire on potential collaboration",
          "expectation on collaboration",
          "expectation on the collaboration",
          "expectation on potential collaboration",
          "preference for collaboration",
          "preference about working together",
          "preference with collaboration",
          "idea about working together",
          "idea of collaboration",
          "idea of potential collaboration",
          "prospect describes collaboration",
          "prospect describes desired collaboration",
          "prospect describes potential collaboration",
          "prospect expresses desire about working together",
          "prospect expresses expectation about working together",
          "prospect expresses requirement about working together",
          "prospect expresses preference about working together",
          "prospect says what him with collaboration important is",
          "prospect describes collaboration",
          "prospect describes desired collaboration",
          "prospect describes potential collaboration",
          "prospect expresses desire about working together",
          "prospect expresses expectation about working together",
          "prospect expresses requirement about working together",
          "prospect expresses preference about working together",
          "prospect says what him with collaboration important is",
          "mental pre-handling as statement",
          "pre-handling of working together as statement",
          "pre-handling potential collaboration as statement",
          "buying signal statement",
          "buying statement",
          "purchase signal statement",
          "buying intent statement",
          "purchase intent statement"
        ]
      }
    }
  },
  "Gegenfrage": {
    "label": "Counter-Question",
    "aliases": [
      "counter-question",
      "counter-questions",
      "follow-up question",
      "follow-up questions",
      "counter question",
      "prospect question"
    ],
    "applications": {
      "Reaktion auf Inhaltsfrage": {
        "label": "Response to a Content Question",
        "aliases": [
          "substantively answer",
          "substantively respond",
          "answer the question",
          "answer the follow-up question",
          "answer the counter-question",
          "factual answer",
          "directly answer",
          "on the question respond",
          "to the follow-up question respond",
          "substantively pick up",
          "factual that respond",
          "answer to the topic give",
          "substantive answer give",
          "professionally answer"
        ]
      },
      "Reaktion auf Kontrollgewinn": {
        "label": "Response to a Bid for Control",
        "aliases": [
          "control regain",
          "regain control of the conversation",
          "leadership regain",
          "take charge of the conversation",
          "take back the lead",
          "conversation again lead",
          "conversation steer back",
          "conversation actively steer",
          "control take over",
          "set direction again",
          "leadership take over",
          "guide conversation",
          "initiative regain",
          "control regain"
        ]
      },
      "Reaktion auf Angriff / Machtdemonstration": {
        "label": "Response to Attack / Show of Power",
        "aliases": [
          "aggressively respond",
          "respond aggressively",
          "respond dominantly",
          "demonstrate power",
          "show dominance",
          "confrontationally respond",
          "provocatively respond",
          "build pressure",
          "counterattack start",
          "retaliate",
          "firmly counter",
          "dominant counter",
          "challengingly respond",
          "show position of power"
        ]
      },
      "Reaktion auf unpassende Frage": {
        "label": "Response to an Inappropriate Question",
        "aliases": [
          "inappropriate respond",
          "inappropriate respond",
          "at the topic respond off topic",
          "not on the question respond",
          "inappropriate answer",
          "irrelevantly answer",
          "off topic respond",
          "out of context answer",
          "wrong on the question respond",
          "inappropriate answer give",
          "at the context miss the point",
          "not appropriate answer",
          "off-topic respond",
          "inappropriate answer"
        ]
      },
      "Pattern Interrupt": {
        "label": "Pattern Interrupt",
        "aliases": [
          "use pattern interrupt",
          "pattern interrupt",
          "conversation pattern break",
          "reaction pattern interrupt",
          "unexpectedly respond",
          "surprisingly respond",
          "with break in pattern respond",
          "routine break through",
          "conversation flow deliberately break",
          "set unexpected prompt",
          "communication pattern break",
          "with surprise respond",
          "use change of pattern",
          "use pattern break"
        ]
      },
      "Reaktion auf Ablenkungsmanöver": {
        "label": "Response to a Diversionary Tactic",
        "aliases": [
          "distract",
          "with distraction respond",
          "from the topic lead away",
          "topic shift",
          "conversation redirect",
          "on a other topic switch",
          "avoid question",
          "avoid follow-up question",
          "avoid counter-question",
          "evasively respond",
          "topic deliberately switch",
          "on other topic steer",
          "avoid answer",
          "with evasive maneuver respond",
          "conversation deliberately steer away"
        ]
      }
    }
  },
  "Handlungsimpuls (P CN+, P CC+, P CO+)": {
    "label": "Action Prompt",
    "aliases": [
      "action prompt",
      "trigger action",
      "prospect should act",
      "prompt action",
      "activate decision",
      "trigger next step",
      "prepare close",
      "establish commitment",
      "initiate implementation",
      "increase readiness to decide",
      "check readiness to act",
      "from the interest to the action",
      "prospect comes in movement",
      "activate sales conversation",
      "decision not postpone"
    ],
    "applications": {
      "Dringlichkeit (P CC+, P CO+)": {
        "label": "Urgency",
        "aliases": [
          "create urgency",
          "build urgency",
          "why now act",
          "prospect waits too long",
          "not longer delay",
          "time pressure in sales",
          "show time-related consequence",
          "cost of waiting",
          "identify urgent reason",
          "now act justify",
          "status quo becomes more costly",
          "delay question",
          "demonstrate damage from waiting",
          "pressure to act without push",
          "time-critical decision"
        ]
      },
      "Commitment (P CO+)": {
        "label": "Commitment",
        "aliases": [
          "obtain commitment",
          "commitment ask for",
          "create commitment",
          "obtain commitment",
          "check genuine yes",
          "prospect should commit",
          "secure firm next step",
          "test readiness to close",
          "interest or commitment",
          "prospect means it serious",
          "decision firm do",
          "recognize half-hearted yes",
          "clarify lack of commitment",
          "commitment question",
          "commitment without pressure"
        ]
      },
      "Nächster Schritt (P CO+)": {
        "label": "Next Step",
        "aliases": [
          "next step agree",
          "next step agree",
          "set follow up",
          "follow-up action commit",
          "next appointment do",
          "secure follow-up appointment",
          "clarify further proceed",
          "call properly close",
          "concrete next action",
          "sales conversation not open let",
          "process after the call",
          "initiate next phase",
          "decision path commit",
          "secure conversation outcome",
          "further proceed agree"
        ]
      },
      "Eigenverantwortung (P CO+)": {
        "label": "Personal Responsibility",
        "aliases": [
          "activate personal responsibility",
          "responsibility with the prospect let",
          "prospect takes over responsibility",
          "accountability question ask",
          "decision lies with the prospect",
          "prospect recognizes own role",
          "personal responsibility in sales",
          "reflect responsibility",
          "not taking action responsibility",
          "prospect is responsible",
          "ownership question",
          "responsibility without blame",
          "own contribution recognize let",
          "decision self make let",
          "encourage taking responsibility"
        ]
      },
      "Entscheidungsimpuls (P CN+, P CO+)": {
        "label": "Decision Prompt",
        "aliases": [
          "initiate decision",
          "trigger decision",
          "prospect should decide",
          "decision question ask",
          "create decision clarity",
          "solve indecision",
          "hesitation end",
          "decision not postpone",
          "set closing prompt",
          "increase readiness to decide",
          "decision point mark",
          "prospect makes clear choice",
          "solve decision block",
          "decide without pressure",
          "prepare close"
        ]
      },
      "Dynamik (P CO+)": {
        "label": "Momentum",
        "aliases": [
          "maintain conversation momentum",
          "maintain momentum",
          "maintain sales flow",
          "maintain call in movement",
          "increase energy in the conversation",
          "prospect loses interest",
          "momentum after objection regain",
          "activate conversation again",
          "momentum after need benefit",
          "sales conversation stalls",
          "maintain interest alive",
          "decision cools from",
          "call forward lead",
          "sales process accelerate",
          "prospect remains passive"
        ]
      },
      "Konsequenz (P CC+)": {
        "label": "Consequences",
        "aliases": [
          "consequence question ask",
          "consequences not taking action",
          "what happens if nothing happens",
          "cost of inaction",
          "clarify impacts",
          "deepen consequences of the problem",
          "status quo consequence",
          "failure to decide question",
          "risk of the waiting",
          "demonstrate subsequent damage",
          "long-term consequence",
          "price of waiting visible do",
          "prospect speaks consequences out of",
          "consequence of the hesitation",
          "implications ask"
        ]
      },
      "Zeitfenster (P CC+, P CO+)": {
        "label": "Opportunity Window",
        "aliases": [
          "time window benefit",
          "opportunity window",
          "appropriate timing",
          "now is the right timing",
          "timewise limited opportunity",
          "secure appointment window",
          "timing for decision",
          "opportunity not miss",
          "clarify current window",
          "seasonal time window",
          "timing for implementation",
          "why now start",
          "momentum timewise benefit",
          "window runs from",
          "favorable decision timing"
        ]
      },
      "Aktivierung (P CO+)": {
        "label": "Activation",
        "aliases": [
          "prospect activate",
          "implementation start",
          "trigger first action",
          "into do bring",
          "from the understand into act",
          "set start prompt",
          "activate readiness to act",
          "action after insight",
          "implementation prompt",
          "prospect begins",
          "secure decision to start",
          "first action agree",
          "activation question",
          "not more wait",
          "action after the call"
        ]
      }
    }
  },
  "Einwand (Wert)": {
    "label": "Value Objection",
    "aliases": [
      "value objection",
      "value not recognized",
      "added value not recognized",
      "benefit not recognized",
      "countervalue not recognized"
    ],
    "applications": {
      "Wettbewerber": {
        "label": "Competitor",
        "aliases": [
          "competitor objection",
          "competitor offer",
          "other provider",
          "prospect compares",
          "compare must still",
          "competitive offer",
          "compare provider",
          "alternative solution",
          "market comparison",
          "prospect reviews competitor",
          "differentiation against competition",
          "open other options",
          "competitor comparison"
        ]
      },
      "Bedarf": {
        "label": "Need",
        "aliases": [
          "no need",
          "we do not need it",
          "not relevant",
          "no problem",
          "everything runs",
          "no interest",
          "need objection",
          "prospect sees no benefit",
          "prospect does not recognize the problem",
          "awareness of the problem lacking",
          "no necessity",
          "not appropriate for us",
          "have we already resolved",
          "benefit not clear",
          "identify need",
          "price justify",
          "value instead of price",
          "clarify costs benefit",
          "prospect does not understand it",
          "offer is confusing",
          "explain simpler",
          "difficulty understanding",
          "not tangible",
          "prospect needs simplification"
        ]
      },
      "Priorität": {
        "label": "Priority",
        "aliases": [
          "no priority",
          "not enough important",
          "stands not on agenda",
          "other projects more important",
          "later prioritize",
          "secondary topic",
          "becomes deferred",
          "priority objection",
          "right now not top",
          "not urgent",
          "prospect postpones priority",
          "project has no priority",
          "internal priorities",
          "topic lies on eis",
          "clarify priority",
          "budget priority"
        ]
      }
    }
  },
  "Einwand (Budget/Preis)": {
    "label": "Budget / Price Objection",
    "aliases": [
      "financial objection",
      "financial burden",
      "price or budget as blocker",
      "financial feasibility"
    ],
    "applications": {
      "Preis": {
        "label": "Price",
        "aliases": [
          "price objection",
          "too expensive",
          "price is too high",
          "costs objection",
          "prospect asks after discount",
          "fee too high",
          "price concerns",
          "prospect doubts at the price",
          "investment too high",
          "price resistance",
          "other are favorable",
          "competition is cheaper"
        ]
      },
      "Budget": {
        "label": "Budget",
        "aliases": [
          "no budget",
          "budget objection",
          "budget used up",
          "open financing",
          "not budgeted",
          "budget year over",
          "cost center blocked",
          "no money there",
          "purchase fails on budget",
          "investment freeze",
          "can I me not afford"
        ]
      }
    }
  },
  "Einwand (Logistik)": {
    "label": "Logistical Objection",
    "aliases": [
      "logistical objection",
      "resources lack",
      "employees lack",
      "staff lacking",
      "competencies lack",
      "skills lack",
      "equipment lack",
      "machinery lack",
      "infrastructure lacking",
      "operational resources lack",
      "implementation resources lack"
    ],
    "applications": {
      "Personelle Kapazität fehlt": {
        "label": "Lack of Staff Capacity",
        "aliases": [
          "too little staff",
          "no employees available",
          "us lack employees",
          "staff capacity lack",
          "team is understaffed",
          "no free employees",
          "staffing level is too thin",
          "additional manpower lacking",
          "we have nobody for that",
          "no staff-related resources",
          "team capacity is insufficient",
          "employees are completely fully occupied"
        ]
      },
      "Fachkompetenz und Know-how fehlen": {
        "label": "Lack of Expertise and Know-How",
        "aliases": [
          "know-how lacking",
          "expertise lacking",
          "competence lacking",
          "qualifications lacking",
          "skills lack",
          "experience lacking",
          "implement nobody can that",
          "internally lacking the expertise",
          "specialized knowledge is not available",
          "team is for that not qualified",
          "professional resources lack",
          "us lacking a expert"
        ]
      },
      "Technische Ausstattung fehlt": {
        "label": "Lack of Technical Equipment",
        "aliases": [
          "equipment lack",
          "machinery lack",
          "tools lack",
          "technical equipment lacking",
          "hardware lacking",
          "equipment lacking",
          "facilities lack",
          "no suitable equipment available",
          "machinery capacity lacking",
          "required technique is not available",
          "necessary tools lack",
          "equipment suffices not out of"
        ]
      },
      "IT- und Systeminfrastruktur fehlt": {
        "label": "Lack of IT and System Infrastructure",
        "aliases": [
          "it-infrastructure lacking",
          "systems lack",
          "software lacking",
          "interfaces lack",
          "technical integration lacking",
          "network suffices not out of",
          "server capacity lacking",
          "digital infrastructure lacking",
          "systems environment is not prepared",
          "no suitable platform available",
          "existing systems do not support it",
          "technical prerequisites lack"
        ]
      },
      "Räumliche und betriebliche Infrastruktur fehlt": {
        "label": "Lack of Premises and Operational Infrastructure",
        "aliases": [
          "premises lack",
          "space lacking",
          "storage space lacking",
          "production floor space lacking",
          "office space lacking",
          "site capacity lacking",
          "no suitable rooms available",
          "operational infrastructure lacking",
          "existing space suffices not out of",
          "warehouse is already full",
          "spatial capacity lack",
          "location is for that not designed for"
        ]
      },
      "Material und Waren fehlen": {
        "label": "Lack of Materials and Goods",
        "aliases": [
          "material lacking",
          "raw materials lack",
          "goods lack",
          "inventory suffices not out of",
          "ability to deliver lacking",
          "replenishment lacking",
          "parts lack",
          "components lack",
          "availability of materials is not provided",
          "supply chain suffices not out of",
          "no sufficient supplies available",
          "procurement is not secured"
        ]
      },
      "Projekt- und Umsetzungsressourcen fehlen": {
        "label": "Lack of Project and Implementation Resources",
        "aliases": [
          "project resources lack",
          "implementation capacity lacking",
          "nobody can that project take over",
          "project team lacking",
          "resources for the introduction lack",
          "no capacity for the implementation",
          "implementation resources lack",
          "we can that not implement",
          "project owner lacking",
          "for the implementation lacking that team",
          "rollout-capacity lacking",
          "internal project capacity suffices not out of"
        ]
      },
      "Operative Kapazität fehlt": {
        "label": "Lack of Operational Capacity",
        "aliases": [
          "in the day-to-day business not feasible",
          "operational capacity lacking",
          "business is completely fully occupied",
          "processes do not allow it",
          "no free resources in the business",
          "ongoing business has no capacity",
          "organization can that not absorb",
          "in everyday operations not integrable",
          "operational workflows are fully occupied",
          "operationally not manageable",
          "process capacity lacking",
          "team is in the day-to-day business occupied"
        ]
      },
      "Externe Partner und Dienstleister fehlen": {
        "label": "Lack of External Partners and Service Providers",
        "aliases": [
          "service provider lacking",
          "external partner lacking",
          "supplier lacking",
          "subcontractor lacking",
          "implementation partner lacking",
          "no suitable provider available",
          "us lacking a specialist partner",
          "no external support available",
          "partner network lacking",
          "no technical service provider available",
          "no available specialists",
          "external resources lack"
        ]
      },
      "Transport- und Logistikkapazität fehlt": {
        "label": "Lack of Transportation and Logistics Capacity",
        "aliases": [
          "vehicles lack",
          "transportation capacity lacking",
          "vehicle fleet lacking",
          "warehouse logistics lacking",
          "delivery is not possible",
          "logistics partner lacking",
          "delivery route capacity lacking",
          "shipping capacity lacking",
          "transport vehicles lack",
          "delivery logistics suffices not out of",
          "no drivers available",
          "distribution is not available"
        ]
      }
    }
  },
  "Einwand (Externalisierung)": {
    "label": "Externalized Responsibility Objection",
    "aliases": [
      "externalization as objection",
      "externalization objection",
      "responsibility externalize",
      "shift responsibility externally",
      "shift the decision externally",
      "responsibility on other shift",
      "responsibility with others see",
      "external authority as blocker",
      "external blocker",
      "external circumstances as blocker",
      "other must decide",
      "other are responsible",
      "not self decide can",
      "not in own control",
      "external influence as reason name"
    ],
    "applications": {
      "Partner / Ehepartner": {
        "label": "Partner / Spouse",
        "aliases": [
          "must speak with a partner",
          "I must discuss this with my partner",
          "partner must agree",
          "partner participates in the decision",
          "spouse must agree",
          "discuss I must at home",
          "clarify I must first at home",
          "wife must agree",
          "husband must agree",
          "decision with partner",
          "not alone decide",
          "I cannot alone decide",
          "family must agree",
          "partner veto",
          "private coordination necessary",
          "co-decision maker privately",
          "partner decides about it",
          "discuss I must first with my wife",
          "discuss I must first with my husband",
          "at home coordinate"
        ]
      },
      "Autorität / Entscheidungsbefugnis": {
        "label": "Authority / Decision-Making Power",
        "aliases": [
          "not authorized to decide",
          "I am not authorized to decide",
          "I cannot decide",
          "decision maker lacking",
          "must talk to manager speak",
          "discuss I must with my manager",
          "manager must agree",
          "managing director must agree",
          "supervisor must agree",
          "supervisor decides",
          "not my budget",
          "internal approval necessary",
          "need internal approval",
          "decision committee decides",
          "budget owner decides",
          "not authorized",
          "I must internally escalate",
          "decision maker not there",
          "approval process necessary",
          "budget approval necessary",
          "financial approval necessary",
          "must through the management",
          "must through the executive board",
          "must through the procurement",
          "must through the headquarters"
        ]
      },
      "Markt": {
        "label": "Market",
        "aliases": [
          "blame the market",
          "the market does not support that",
          "the market is difficult",
          "the market is bad",
          "the market is not working",
          "lies in the market",
          "that lies in the market",
          "our industry is difficult",
          "it does not work in our industry",
          "with us is the market different",
          "our prospects are different",
          "our customers do not buy that",
          "customers do not want that",
          "the demand is not there",
          "it exists no demand",
          "the market is saturated",
          "the market is depleted",
          "it exists no candidates",
          "it exists no applicants",
          "it exists no good applicants",
          "people want not work",
          "there are no good employees available",
          "skilled workers are unavailable",
          "the skilled labor market is empty",
          "the competition is too strong",
          "the competition is too large",
          "against the large come we not on",
          "our region is too difficult",
          "it does not work in our location",
          "the economic situation is blame",
          "the economic climate is too bad",
          "the industry exists that not from",
          "the market does not allow growth",
          "external market conditions prevent that"
        ]
      }
    }
  },
  "Einwand (Entscheidung)": {
    "label": "Decision Objection",
    "aliases": [
      "decision objection",
      "decision block",
      "missing commitment",
      "lacking readiness to decide",
      "decision becomes avoided",
      "decision becomes withheld",
      "decision becomes postponed",
      "prospect does not want to decide",
      "prospect does not want to commit",
      "prospect cannot commit",
      "prospect avoids commitment",
      "prospect keeps the decision open",
      "lacking commitment",
      "no clear decision",
      "no binding decision",
      "hesitation before the decision",
      "internal decision block",
      "closing decision becomes avoided"
    ],
    "applications": {
      "Commitment": {
        "label": "Commitment",
        "aliases": [
          "wants themselves not commit",
          "wants themselves not commit",
          "cannot commit",
          "still not ready to decide",
          "still not ready",
          "not finally decide",
          "wants still not decide",
          "wants still not decide",
          "wants me still not commit",
          "wants me still not commit",
          "maintain decision open",
          "maintain wants it open",
          "maintain wants it open",
          "commitment objection",
          "missing commitment",
          "no commitment",
          "prospect hesitates",
          "prospect remains noncommittal",
          "noncommittal interest",
          "prospect says maybe",
          "no clear commitment",
          "no commitment",
          "half-hearted yes",
          "lacking commitment",
          "not commit",
          "wants themselves still not commit",
          "wants themselves still not commit",
          "decision still not make",
          "still no decision make",
          "still no binding decision"
        ]
      }
    }
  },
  "Einwand (Zeit)": {
    "label": "Time Objection",
    "aliases": [
      "time as blocker",
      "time-related blocker",
      "timing as blocker",
      "time or timing"
    ],
    "applications": {
      "Zeit": {
        "label": "Time",
        "aliases": [
          "time objection",
          "no time",
          "takes too long",
          "lack of time",
          "no capacity",
          "calendar full",
          "later speak",
          "time investment too high",
          "scheduling issue",
          "now no time",
          "too little time",
          "time-related priority",
          "prospect postpones because of time",
          "no time for implementation",
          "clarify time investment"
        ]
      },
      "Timing": {
        "label": "Timing",
        "aliases": [
          "wrong timing",
          "not now",
          "timing objection",
          "later start",
          "after the project",
          "next quarter",
          "next year",
          "the timing does not work",
          "now inconvenient",
          "prospect postpones timing",
          "clarify start date",
          "first after vacation",
          "still to early",
          "right now not appropriate",
          "clarify purchase timing",
          "to early for decision"
        ]
      }
    }
  },
  "Einwand (Angst)": {
    "label": "Fear Objection",
    "aliases": [
      "fear as blocker",
      "uncertainty as blocker",
      "fear of making a purchase decision",
      "internal purchase block",
      "need for security with purchase decision"
    ],
    "applications": {
      "Vertrauen": {
        "label": "Trust",
        "aliases": [
          "trust objection",
          "prospect does not trust",
          "distrust",
          "skeptical because of provider",
          "credibility lacking",
          "trust problem",
          "gut feeling bad",
          "build trust with objection",
          "provider seems uncertain",
          "lack of trust",
          "distrustful prospect"
        ]
      },
      "Risiko": {
        "label": "Risk",
        "aliases": [
          "risk objection",
          "too risky",
          "what if it does not work",
          "fear of making the wrong decision",
          "prospect needs assurance",
          "uncertainty because of result",
          "guarantee lacking",
          "reduce risk",
          "fear of buying",
          "false investment",
          "fear of loss",
          "decision seems uncertain",
          "risk in the purchase process",
          "need for security",
          "risk not taking action",
          "price anxiety",
          "prospect needs security"
        ]
      },
      "Erfahrung": {
        "label": "Experience",
        "aliases": [
          "bad experience",
          "negative experience",
          "disappointed in the past",
          "previous provider",
          "has previously not works",
          "prospect is burned",
          "past experience blocked",
          "skepticism through experience",
          "bad experience with industry",
          "trust after disappointment",
          "experience objection",
          "past solution failed",
          "prospect projects past",
          "past disappointment",
          "prejudiced by experience prospect"
        ]
      },
      "Komplexität": {
        "label": "Complexity",
        "aliases": [
          "too complicated",
          "complexity objection",
          "solution seems to complex",
          "too many details",
          "overwhelm",
          "technically too complicated",
          "reduce complexity",
          "to burdensome",
          "complexity blocked purchase"
        ]
      },
      "Bedenkzeit / Drüber schlafen": {
        "label": "Time to Think / Sleep on It",
        "aliases": [
          "need time to think",
          "again about it sleep",
          "still once about it sleep",
          "a night about it sleep",
          "first once about it sleep"
        ]
      },
      "Skepsis": {
        "label": "Skepticism",
        "aliases": [
          "skeptical prospect",
          "skepticism objection",
          "prospect is critical",
          "prospect doubts",
          "critical follow-up question",
          "wait-and-see attitude",
          "prospect does not believe it",
          "evaluative stance",
          "clarify doubt",
          "reduce skepticism",
          "not convinced",
          "critical distance",
          "prospect wants to observe",
          "open skepticism",
          "prospect does not believe it",
          "doubt on solution",
          "to good um true to be"
        ]
      }
    }
  },
  "Einwand (Ablehnung)": {
    "label": "Rejection Objection",
    "aliases": [
      "rejection as blocker",
      "rejecting objection",
      "direct rejection",
      "indirect rejection",
      "refusal to talk",
      "rejection or brush-off response",
      "deflecting response",
      "defensive reaction"
    ],
    "applications": {
      "Keine Zeit": {
        "label": "No Time",
        "aliases": [
          "I have no time for that",
          "we have no time for that",
          "I have no time for that",
          "we have no time for that",
          "now have I no time",
          "currently no time",
          "right now no time",
          "in the moment no time",
          "timewise not possible",
          "no time for the conversation",
          "no time to deal with it",
          "prospect rejects because of lack of time from",
          "prospect blocks with lack of time from",
          "lack of time as rejection",
          "no time for a conversation"
        ]
      },
      "Selbst machen": {
        "label": "Do It Ourselves",
        "aliases": [
          "we do it ourselves",
          "we do it ourselves",
          "I do it myself",
          "we solve it ourselves",
          "we solve that themselves",
          "that do we internally",
          "we solve that internally",
          "that take over we internally",
          "for that have we own employees",
          "for that have we a internal department",
          "that takes over our team",
          "we need for that no support",
          "we need for that nobody",
          "internal solution as rejection",
          "prospect refers on in-house work"
        ]
      },
      "Bestehender Partner": {
        "label": "Existing Partner",
        "aliases": [
          "we already have a partner",
          "we already have a partner",
          "we already work with a partner",
          "we have a partner for that",
          "we already have a service provider",
          "we already have a service provider",
          "that makes already a service provider",
          "that makes our agency",
          "we already work with a agency",
          "we are already provided for",
          "we already have a solution",
          "our partner takes care of themselves of it",
          "existing provider as rejection",
          "existing collaboration as rejection",
          "prospect refers on existing partner"
        ]
      },
      "Schlechte Erfahrungen": {
        "label": "Bad Experiences",
        "aliases": [
          "so that have we bad experiences",
          "we have so that bad experiences made",
          "so that had I bad experiences",
          "that have we already tried",
          "that had we already once",
          "that has with us not works",
          "so that are we to the nose fallen",
          "we were so that disappointed",
          "that has us nothing brought",
          "that war wasted money",
          "that has themselves not worth it",
          "we want that not still once try",
          "negative experience as rejection",
          "prospect rejects because of bad experience from",
          "prospect refers on negative experiences"
        ]
      },
      "Kein Interesse": {
        "label": "No Interest",
        "aliases": [
          "no interest",
          "I have no interest",
          "we have no interest",
          "not interested",
          "I at the not interested",
          "we are not interested",
          "this does not interest us",
          "for us not interesting",
          "that does not interest me",
          "that does not interest us",
          "comes for us not an option",
          "we do not want to",
          "we do not want to",
          "prospect shows no interest",
          "prospect rejects because of lack of interest from"
        ]
      },
      "Kein Bedarf": {
        "label": "No Need",
        "aliases": [
          "no need",
          "I have no need",
          "we have no need",
          "currently no need",
          "for that exists no need",
          "we do not need it",
          "we do not need that",
          "we do not require it",
          "we do not need that",
          "for us not necessary",
          "this is for us not relevant",
          "we come also without that out of",
          "it exists for that no necessity",
          "prospect denies the need",
          "prospect rejects because of lack of need from"
        ]
      },
      "Unterlagen zusenden": {
        "label": "Send Information",
        "aliases": [
          "send they us documents",
          "send they me documents",
          "send they us documents",
          "send they me documents",
          "send they us information",
          "send they me information",
          "send they us information",
          "send they me information",
          "send they me a email",
          "send they us a email",
          "send they something per email",
          "let they us something send",
          "send us something first",
          "prospect requests documents instead of conversation",
          "request for documents as rejection"
        ]
      },
      "Gesprächsabbruch": {
        "label": "End the Conversation",
        "aliases": [
          "I hang now on",
          "I will now hang up",
          "I end now that conversation",
          "we end that conversation",
          "end we that conversation",
          "that conversation is ends",
          "call they not more on",
          "call they me not again on",
          "let they me in peace",
          "I wants not keep talking",
          "I wants that conversation not continue",
          "I wants that conversation end",
          "prospect ends that conversation",
          "prospect announces hang up on",
          "prospect rejects the continuing the conversation from"
        ]
      }
    }
  },
  "Vorwandbehandlung": {
    "label": "Pretext Handling",
    "aliases": [
      "pretext",
      "sales pretext",
      "prospect's pretext",
      "ostensible reason",
      "inauthentic objection",
      "spurious argument",
      "excuse in sales",
      "superficial reason",
      "evasive response",
      "defensive claim",
      "noncommittal answer",
      "hidden resistance",
      "standard excuse",
      "deflecting response",
      "not the true reason"
    ],
    "applications": {
      "Vorwand": {
        "label": "Pretext",
        "aliases": [
          "pretext",
          "sales pretext",
          "prospect's pretext",
          "ostensible reason",
          "inauthentic objection",
          "spurious argument",
          "excuse in sales",
          "superficial reason",
          "evasive response",
          "defensive claim",
          "noncommittal answer",
          "hidden resistance",
          "standard excuse",
          "deflecting response",
          "not the true reason"
        ]
      }
    }
  },
  "Einwandvorwegnahme": {
    "label": "Objection Pre-Handling",
    "aliases": [
      "objection pre-handling",
      "anticipate objection",
      "pre-handling",
      "anticipate objection",
      "objection address in advance",
      "pre-handling",
      "pre-frame",
      "objection prevention",
      "critical points clarify early",
      "avoid later objection",
      "prospect concerns in advance open",
      "clarify before the offer",
      "avoid resistance",
      "set expectation in advance",
      "in advance qualify",
      "anticipate objection",
      "address concerns early"
    ],
    "applications": {
      "Mentale-Blockade-Vorwegnahme": {
        "label": "Pre-Handling Mental Blocks",
        "aliases": [
          "mental block pre-handling",
          "mental block pre-handling",
          "mental block address in advance",
          "address mental blocks proactively",
          "mental purchase block pre-handling",
          "anticipate mental purchase block",
          "inner resistance pre-handle",
          "anticipate inner resistance",
          "mental resistance address early",
          "blocking belief pre-handle",
          "anticipate blocking belief",
          "internal purchase block address early",
          "mental decision block pre-handling",
          "anticipate mental decision block",
          "psychological block address in advance"
        ]
      },
      "Angebotsvertrauen-Vorwegnahme": {
        "label": "Pre-Handling Trust in the Offer",
        "aliases": [
          "trust in the offer pre-handling",
          "trust in the offer pre-handling",
          "trust in offer pre-handle",
          "anticipate trust in offer",
          "doubt at the offer pre-handling",
          "anticipate doubt at the offer",
          "doubts about the offer address in advance",
          "doubts about the offer address early",
          "distrust toward offer pre-handle",
          "anticipate lack of trust offer",
          "lack of trust offer pre-handling",
          "skepticism about the offer pre-handling",
          "anticipate skepticism about the offer",
          "build credibility of the offer in advance",
          "trust in the solution address in advance"
        ]
      },
      "Anbietervertrauen-Vorwegnahme": {
        "label": "Pre-Handling Trust in the Provider",
        "aliases": [
          "trust in the provider pre-handling",
          "trust in the provider pre-handling",
          "pre-handle trust in the provider",
          "anticipate concerns about trust in the provider",
          "doubts about the provider pre-handling",
          "anticipate doubts about the provider",
          "distrust toward provider pre-handle",
          "anticipate distrust toward provider",
          "lack of trust provider pre-handling",
          "anticipate lack of trust provider",
          "credibility of the provider's pre-handle",
          "credibility of the provider's clarify in advance",
          "provider skepticism address in advance",
          "uncertainty about the provider pre-handling",
          "anticipate uncertainty about the provider"
        ]
      },
      "Selbstvertrauen-Vorwegnahme": {
        "label": "Pre-Handling Self-Confidence",
        "aliases": [
          "self-confidence pre-handling",
          "self-confidence pre-handling",
          "self-doubt pre-handling",
          "self-doubt pre-handling",
          "self-doubt address in advance",
          "doubts about implementation pre-handling",
          "anticipate doubts about implementation",
          "trust in themselves self pre-handle",
          "anticipate trust in themselves self",
          "missing self-confidence address early",
          "doubt on own ability pre-handle",
          "anticipate doubt on own implementation",
          "I manage to that not pre-handling",
          "own ability to succeed clarify in advance",
          "confidence in implementation pre-handling"
        ]
      },
      "Methodenvertrauen-Vorwegnahme": {
        "label": "Pre-Handling Trust in the Method",
        "aliases": [
          "trust in the method pre-handling",
          "trust in the method pre-handling",
          "trust in method pre-handle",
          "anticipate trust in method",
          "skepticism about the method pre-handling",
          "skepticism about the method pre-handling",
          "doubt on method pre-handle",
          "anticipate doubt on method",
          "lack of trust method pre-handling",
          "anticipate lack of trust method",
          "approach pre-handling",
          "anticipate approach",
          "doubt on the approach address in advance",
          "works the method pre-handling",
          "uncertainty about the method address early"
        ]
      },
      "Bedürfnislücken-Vorwegnahme": {
        "label": "Pre-Handling Unmet Needs",
        "aliases": [
          "unmet need pre-handling",
          "unmet need pre-handling",
          "unmet need address in advance",
          "lacking meeting needs pre-handling",
          "anticipate lacking meeting needs",
          "unmet need pre-handling",
          "anticipate unmet need",
          "unmet need pre-handling",
          "unmet need pre-handling",
          "need address early",
          "need clarify in advance",
          "anticipate need",
          "open missing need early",
          "customer need pre-handling",
          "blocked need address in advance"
        ]
      },
      "Wachstumsbedürfnis-Vorwegnahme": {
        "label": "Pre-Handling Need for Growth",
        "aliases": [
          "need for growth pre-handling",
          "need for growth pre-handling",
          "need for growth address in advance",
          "desire for growth pre-handling",
          "anticipate desire for growth",
          "growth deficit pre-handling",
          "anticipate missing growth",
          "growth barrier pre-handling",
          "anticipate growth barrier",
          "need for development pre-handling",
          "anticipate need for development",
          "need for growth address early",
          "growth address in advance",
          "growth early pre-handle",
          "anticipate need for development"
        ]
      },
      "Verbundenheitsbedürfnis-Vorwegnahme": {
        "label": "Pre-Handling Need for Connection",
        "aliases": [
          "need for belonging pre-handling",
          "need for belonging pre-handling",
          "need for belonging address in advance",
          "need for belonging pre-handling",
          "anticipate need for belonging",
          "lacking connection pre-handling",
          "anticipate lacking connection",
          "social connection pre-handle",
          "anticipate social connection",
          "belonging address early",
          "belonging clarify in advance",
          "address connection early",
          "social need pre-handling",
          "anticipate social need",
          "need for relationships address in advance"
        ]
      },
      "Anerkennungsbedürfnis-Vorwegnahme": {
        "label": "Pre-Handling Need for Recognition",
        "aliases": [
          "need for recognition pre-handling",
          "need for recognition pre-handling",
          "need for recognition address in advance",
          "need after recognition pre-handling",
          "anticipate need after recognition",
          "lacking recognition pre-handling",
          "anticipate lacking recognition",
          "need for appreciation pre-handling",
          "need for appreciation pre-handling",
          "need for status pre-handling",
          "anticipate need for status",
          "recognition address early",
          "recognition address in advance",
          "appreciation address in advance",
          "anticipate social recognition"
        ]
      },
      "Abwechslungsbedürfnis-Vorwegnahme": {
        "label": "Pre-Handling Need for Variety",
        "aliases": [
          "need for variety pre-handling",
          "need for variety pre-handling",
          "need for variety address in advance",
          "need after variety pre-handling",
          "anticipate need after variety",
          "lacking variety pre-handling",
          "anticipate lacking variety",
          "monotony pre-handling",
          "monotony pre-handling",
          "routine pre-handling",
          "anticipate routine",
          "need for change pre-handling",
          "need for change pre-handling",
          "address variety early",
          "new experience address in advance"
        ]
      },
      "Sicherheitsbedürfnis-Vorwegnahme": {
        "label": "Pre-Handling Need for Security",
        "aliases": [
          "need for security pre-handling",
          "need for security pre-handling",
          "need for security address in advance",
          "need after security pre-handling",
          "anticipate need after security",
          "lacking security pre-handling",
          "anticipate lacking security",
          "feeling of insecurity pre-handling",
          "feeling of insecurity pre-handling",
          "lack of security pre-handling",
          "anticipate lack of security",
          "security address early",
          "security address in advance",
          "need for reassurance pre-handling",
          "anticipate need for reassurance"
        ]
      },
      "Externalisierungs-Vorwegnahme": {
        "label": "Pre-Handling Externalization",
        "aliases": [
          "externalization pre-handling",
          "externalization pre-handling",
          "externalization address in advance",
          "shifting responsibility pre-handling",
          "anticipate shifting responsibility",
          "responsibility after outside pre-handle",
          "anticipate responsibility after outside",
          "blame with others pre-handling",
          "anticipate blame with others",
          "external circumstances pre-handling",
          "anticipate external circumstances",
          "victim mentality pre-handling",
          "victim mentality pre-handling",
          "personal responsibility address early",
          "external focus on causes address in advance"
        ]
      },
      "Logistik-Vorwegnahme": {
        "label": "Pre-Handling Logistics",
        "aliases": [
          "logistics pre-handling",
          "logistics pre-handling",
          "logistical block pre-handling",
          "logistical block pre-handling",
          "logistical block address in advance",
          "logistical obstacle pre-handling",
          "anticipate logistical obstacle",
          "implementation obstacle pre-handling",
          "implementation obstacle pre-handling",
          "operational block pre-handling",
          "anticipate operational block",
          "feasibility pre-handling",
          "anticipate feasibility",
          "logistical feasibility clarify in advance",
          "implementation logistics address early"
        ]
      },
      "Personal-Vorwegnahme": {
        "label": "Pre-Handling Staffing",
        "aliases": [
          "staff pre-handling",
          "staff pre-handling",
          "staffing bottleneck pre-handling",
          "staffing bottleneck pre-handling",
          "staff block pre-handling",
          "anticipate staff block",
          "staff shortage pre-handling",
          "anticipate staff shortage",
          "employee shortage pre-handling",
          "employee shortage pre-handling",
          "staff capacity pre-handling",
          "anticipate staff capacity",
          "staffing resources address in advance",
          "employee resources address in advance",
          "staff feasibility pre-handling"
        ]
      },
      "Ressourcen-Vorwegnahme": {
        "label": "Pre-Handling Resources",
        "aliases": [
          "resources pre-handling",
          "resources pre-handling",
          "resource bottleneck pre-handling",
          "resource bottleneck pre-handling",
          "resource constraints pre-handling",
          "anticipate resource constraints",
          "lacking resources pre-handling",
          "lacking resources pre-handling",
          "operational resources pre-handling",
          "anticipate operational resources",
          "implementation resources pre-handling",
          "implementation resources pre-handling",
          "resource availability address in advance",
          "resource situation pre-handling",
          "anticipate resource situation"
        ]
      },
      "Preis-Vorwegnahme": {
        "label": "Pre-Handling Price",
        "aliases": [
          "price address in advance",
          "anticipate price objection",
          "investment clarify early",
          "check budget in advance",
          "cost framework before offer",
          "avoid surprise about price",
          "set price anchor",
          "price before the pitch",
          "address fee early",
          "build value before price",
          "check willingness to pay",
          "set financial expectation",
          "prepare cost item",
          "price not hide",
          "prepare price discussion"
        ]
      },
      "Zeit-Vorwegnahme": {
        "label": "Pre-Handling Time",
        "aliases": [
          "time clarify in advance",
          "anticipate time objection",
          "address time investment early",
          "set time frame",
          "check customer's time",
          "explain implementation time",
          "prepare time investment",
          "check capacity in advance",
          "clarify time needed for appointments",
          "duration before offer",
          "time required not surprise",
          "clarify implementation time",
          "call duration frame",
          "check scheduling realities",
          "set time-related expectation"
        ]
      },
      "Vertrauens-Vorwegnahme": {
        "label": "Pre-Handling Trust",
        "aliases": [
          "build trust in advance",
          "anticipate trust objection",
          "address skepticism early",
          "credibility before offer",
          "doubt clarify early",
          "set proof in advance",
          "references early benefit",
          "create feeling of security",
          "basis of trust lay",
          "open distrust in advance",
          "assess evidence in advance",
          "credibility framework",
          "reduce trust risk",
          "prospect concerns open do",
          "anticipate trust question"
        ]
      },
      "Risiko-Vorwegnahme": {
        "label": "Pre-Handling Risk",
        "aliases": [
          "risk address in advance",
          "anticipate risk objection",
          "explain assurance early",
          "security concerns in advance",
          "what if it does not work anticipate",
          "explain guarantee early",
          "reduce fear of buying in advance",
          "risk transparent do",
          "prepare confidence in the decision",
          "address fear of loss early",
          "risk in advance put in perspective",
          "discuss implementation risk",
          "set security framework",
          "prepare risk of the not action",
          "customer risk early check"
        ]
      },
      "Skepsis-Vorwegnahme": {
        "label": "Pre-Handling Skepticism",
        "aliases": [
          "skepticism address in advance",
          "anticipate skepticism",
          "address doubt early",
          "anticipate critical follow-up questions",
          "prepare critical prospects",
          "skepticism legitimize",
          "assess evaluative stance",
          "clarify distrust before pitch",
          "wait-and-see attitude in advance",
          "skepticism not combat",
          "criticism address in advance",
          "doubt normalize",
          "skeptical attitude framework",
          "customer criticism prepare",
          "open skepticism early"
        ]
      },
      "Partner-Vorwegnahme": {
        "label": "Pre-Handling Partner Objections",
        "aliases": [
          "partner question clarify in advance",
          "anticipate partner objection",
          "spouse early involve",
          "co-decision maker clarify in advance",
          "who must agree",
          "check need to consult others",
          "identify third parties person",
          "prepare partner veto",
          "clarify decision partner",
          "secure all decision maker",
          "prepare family decision",
          "avoid at home discuss",
          "stakeholder early find out",
          "co-decision maker not forget",
          "avoid coordination after call"
        ]
      },
      "Wettbewerber-Vorwegnahme": {
        "label": "Pre-Handling Competitor Objections",
        "aliases": [
          "competitor address in advance",
          "anticipate competition",
          "other provider clarify early",
          "understand comparison process",
          "clarify alternative solutions",
          "assess market comparison",
          "prepare differentiation",
          "competitor question in advance",
          "prospect compares address",
          "competitor offer in advance",
          "comparison criteria define",
          "clarify selection process",
          "competitor not ignore",
          "change of provider framework",
          "why not competition buy"
        ]
      },
      "Commitment-Vorwegnahme": {
        "label": "Pre-Handling Commitment",
        "aliases": [
          "check commitment in advance",
          "commitment early ask for",
          "readiness to close before offer",
          "check seriousness",
          "only interest or purchase intention",
          "clarify ability to commit",
          "decision criteria in advance",
          "customer commitment prepare",
          "reduce lack of commitment",
          "set commitment framework",
          "assess readiness to buy",
          "secure next step before pitch",
          "prepare decision after offer",
          "firm conversation goal",
          "check level of commitment"
        ]
      },
      "Komplexitäts-Vorwegnahme": {
        "label": "Pre-Handling Complexity",
        "aliases": [
          "reduce complexity in advance",
          "anticipate complexity",
          "explain solution simple",
          "do not overwhelm the prospect",
          "secure clarity",
          "present process simple",
          "technical skepticism in advance",
          "structure complex details",
          "set framework for simplicity",
          "offer clear and structured do",
          "explain implementation understandable",
          "avoid overwhelm",
          "simpler starting point",
          "prepare prospects on structure",
          "assess complexity early"
        ]
      },
      "Budget-Vorwegnahme": {
        "label": "Pre-Handling Budget",
        "aliases": [
          "budget clarify in advance",
          "anticipate budget objection",
          "check financial approval",
          "clarify investment framework",
          "understand budget process",
          "cost center clarify early",
          "set payment framework",
          "check budget availability",
          "clarify approval loop",
          "consider budget year",
          "financing before pitch",
          "purchase framework define",
          "address budget reality",
          "avoid no budget later",
          "clarify economic framework"
        ]
      }
    }
  },
  "Vertrauensaufbau (P TC+, P SK-)": {
    "label": "Building Trust",
    "aliases": [
      "build trust",
      "build trust in sales",
      "earn customer trust",
      "increase credibility",
      "reduce skepticism",
      "build rapport",
      "create basis of trust",
      "prospect feels understood",
      "trust before close",
      "security in the purchase process",
      "professionally credible appear",
      "trust through ask",
      "strengthen relationship in the call",
      "prospect concerns serious take",
      "trust instead of pressure"
    ],
    "applications": {
      "Autorität (P TC+)": {
        "label": "Authority",
        "aliases": [
          "build authority",
          "show expert standing",
          "professional leadership take over",
          "confident in the sales call",
          "advisor authority",
          "prospect follows conversation management",
          "professional assessment give",
          "authority through diagnosis",
          "professional security",
          "factual authority",
          "sales leadership without dominance",
          "prospect recognizes expertise",
          "leadership role in the conversation",
          "competently lead",
          "trustworthy authority"
        ]
      },
      "Kompetenz (P TC+)": {
        "label": "Competence",
        "aliases": [
          "show competence",
          "demonstrate professional expertise",
          "prospect recognizes expertise",
          "competently answer",
          "convey expertise",
          "prove technical expertise",
          "professional explanation",
          "show diagnostic competence",
          "customer problem assess",
          "consulting competence",
          "show understanding of the solution",
          "competence through ask",
          "professional security",
          "customer trust through knowledge",
          "establish relevance"
        ]
      },
      "Transparenz (P TC+, P SK-)": {
        "label": "Transparency",
        "aliases": [
          "create transparency",
          "honestly sell",
          "open about price speak",
          "explain process transparent",
          "no hidden costs",
          "address risks open",
          "set clear expectations",
          "offer transparent do",
          "prospects open inform",
          "nothing sugarcoat",
          "transparent consulting",
          "honest sales call",
          "limits open say",
          "prospects not surprise",
          "open basis for decisions"
        ]
      },
      "Sozialer Beweis (P TC+, P SK-)": {
        "label": "Social Proof",
        "aliases": [
          "social proof",
          "social proof",
          "references benefit",
          "customer examples",
          "case study in the sales call",
          "use testimonials",
          "customer testimonials",
          "comparable prospects name",
          "proof through other prospects",
          "results other prospects",
          "reference case tell",
          "peer proof",
          "reviews benefit",
          "customer proof",
          "similar prospect example"
        ]
      },
      "Sicherheit (P TC+, P SK-)": {
        "label": "Security",
        "aliases": [
          "security give",
          "reduce fear of buying",
          "customer risk reduce",
          "confidence in the decision",
          "explain assurance",
          "guarantee communicate",
          "prospect feels safe",
          "risk-free next step",
          "need for security fulfill",
          "security framework",
          "reduce implementation risk",
          "clear assurance",
          "customer fears clarify",
          "secure decision enable",
          "control give"
        ]
      },
      "Empathie (P TC+)": {
        "label": "Empathy",
        "aliases": [
          "empathy in sales",
          "actively listen",
          "prospect feels understood",
          "customer perspective reflect",
          "show understanding",
          "address objection empathetically",
          "recognize emotional state",
          "humanly sell",
          "prospect situation serious take",
          "empathetic question",
          "rapport through understanding",
          "customer frustration acknowledge",
          "listen instead of pitch",
          "empathetic opening up an objection",
          "understanding without agreement"
        ]
      },
      "Ähnlichkeit (P TC+)": {
        "label": "Similarity",
        "aliases": [
          "similarity benefit",
          "commonalities find",
          "same language speak",
          "show industry affinity",
          "similar experience",
          "comparable situation",
          "prospect recognizes same world",
          "rapport through commonality",
          "closeness without pander",
          "establish peer connection",
          "similar customer's world",
          "create familiarity",
          "shared context",
          "similar challenge",
          "relationship building through similarity"
        ]
      },
      "Verlässlichkeit (P TC+)": {
        "label": "Reliability",
        "aliases": [
          "show reliability",
          "prove reliability",
          "trust through reliability",
          "realistic promises",
          "show ability to deliver",
          "reliable partner",
          "expectations fulfill",
          "convey stability",
          "professional commitment",
          "show clear processes",
          "customer fear before disappointment",
          "set promise realistically",
          "explain implementation reliably",
          "consistency communicate",
          "reliably appear"
        ]
      },
      "Expertise (P TC+)": {
        "label": "Expertise",
        "aliases": [
          "show expertise",
          "specialized knowledge benefit",
          "convey expertise",
          "customer problem analyze",
          "adopt expert role",
          "industry knowledge bring in",
          "insider knowledge benefit",
          "depth diagnosis",
          "expertise through ask",
          "show ability to solve",
          "assess consulting knowledge",
          "professional depth",
          "specialist instead of salesperson",
          "expertise without arrogance",
          "customer context understand"
        ]
      },
      "Beweis (P TC+, P SK-)": {
        "label": "Evidence",
        "aliases": [
          "evidence benefit",
          "proof point",
          "demonstrate results",
          "numbers as proof",
          "data for credibility",
          "show before after",
          "measurable results",
          "track record",
          "facts instead of claims",
          "proof of success",
          "benchmark benefit",
          "documented results",
          "demonstrate impact",
          "evidence for solution",
          "customer proof provide"
        ]
      }
    }
  },
  "Glaubenssatz-Wandel (P TO+, P CN+, P CC+, P TS+, P SK-, P CO+)": {
    "label": "Belief Change",
    "aliases": [
      "change belief",
      "belief shift",
      "reframing",
      "trigger change perspective",
      "customer belief change",
      "solve false assumption",
      "limiting conviction",
      "change perspective",
      "assess purchase block new",
      "reframe objection",
      "prospect recognizes new perspective",
      "past conviction question",
      "create insight",
      "mindset in sales",
      "mental frame switch"
    ],
    "applications": {
      "Falsche Annahme (P CN+, P SK-)": {
        "label": "False Assumption",
        "aliases": [
          "false assumption",
          "assumption uncover",
          "prospect goes of false out of",
          "clarify misconception",
          "thinking error question",
          "correct misunderstanding",
          "false idea",
          "check assumption",
          "prospect has false image",
          "misjudgment",
          "solve fallacy",
          "false expectation",
          "open prejudice",
          "myth disprove",
          "correct conclusion"
        ]
      },
      "Limitierender Glaubenssatz (P TS+, P SK-)": {
        "label": "Limiting Belief",
        "aliases": [
          "limiting belief",
          "the belief that it will not work",
          "it does not work for us",
          "we are different objection",
          "prospect sees block",
          "belief that it is impossible",
          "I cannot",
          "not for our industry",
          "self-limitation",
          "blocking conviction",
          "mental block",
          "restrictive think",
          "prospect limited possibility",
          "past limit question",
          "solve belief"
        ]
      },
      "Perspektivwechsel (P CN+, P SK-)": {
        "label": "Change of Perspective",
        "aliases": [
          "shift in perspective",
          "new perspective",
          "consider different",
          "reframe question",
          "other point of view",
          "prospect sees other side",
          "assess problem new",
          "perspective switch",
          "zoom out",
          "meta-perspective",
          "mental frame switch",
          "customer logic shift",
          "consider situation new",
          "new perspective",
          "perspective question"
        ]
      },
      "Ursachenwechsel (P CN+)": {
        "label": "Change in Causal Attribution",
        "aliases": [
          "assess cause new",
          "change in causal attribution",
          "false cause",
          "actual cause",
          "symptom or cause",
          "clarify root cause of the problem",
          "not person but rather system",
          "systemic cause",
          "process cause",
          "avoid question of blame",
          "explain mechanism",
          "prospect sees false trigger",
          "causal question",
          "explain problem different",
          "reframe cause"
        ]
      },
      "Verantwortungswechsel (P CO+)": {
        "label": "Shift in Responsibility",
        "aliases": [
          "responsibility new framework",
          "shift in responsibility",
          "customer responsibility",
          "recognize own role",
          "recognize room for action",
          "accountability question",
          "decision take responsibility for",
          "clarify responsibility",
          "responsibility without blame",
          "recognize sphere of influence",
          "agency give back",
          "solve victim mentality",
          "active role",
          "taking responsibility",
          "own lever"
        ]
      },
      "Wertverschiebung (P TO+)": {
        "label": "Shift in Perceived Value",
        "aliases": [
          "value instead of price",
          "investment instead of costs",
          "perceived value",
          "translate price in value",
          "explain added value",
          "costs benefit shift",
          "prospect does not see the value",
          "clarify return",
          "solve focus on price",
          "focus on benefits",
          "ROI perspective",
          "expense as investment",
          "economic value",
          "value logic",
          "price performance new framework"
        ]
      },
      "Risikowahrnehmung (P CC+)": {
        "label": "Risk Perception",
        "aliases": [
          "evaluate risk new",
          "change perception of risk",
          "risk of the not action",
          "purchase risk overestimated",
          "compare risk of acting",
          "reverse risk",
          "reframe security concerns",
          "assess fear of buying new",
          "failure to decide is riskier",
          "risk in relationship",
          "show downstream risk",
          "risk question",
          "risk decision",
          "open security mindset",
          "balancing risk"
        ]
      },
      "Chancenwahrnehmung (P TO+)": {
        "label": "Opportunity Perception",
        "aliases": [
          "opportunity visible do",
          "perception of opportunity",
          "prospect does not recognize the possibility",
          "identify potential",
          "possibility in the problem",
          "customer opportunity framework",
          "positive impact",
          "clarify upside",
          "chance of gain",
          "possibility question",
          "potential instead of problem",
          "change opportunity out of",
          "open scope of benefits",
          "growth perspective",
          "opportunity reframing"
        ]
      },
      "Kostenwahrnehmung (P TO+, P CC+)": {
        "label": "Cost Perception",
        "aliases": [
          "perception of cost",
          "price put in perspective",
          "costs in the relationship",
          "costs pro tag",
          "reframe expensive",
          "cost of inaction",
          "change feeling about price",
          "investment break down",
          "prospect sees only price",
          "consider total costs",
          "price in relationship",
          "short-term costs long-term costs",
          "change price anchor",
          "perceived price",
          "cost question new ask"
        ]
      },
      "Selbstzweifel (P TS+)": {
        "label": "Self-Doubt",
        "aliases": [
          "self-doubt",
          "prospect does not feel capable",
          "create fear it not to",
          "doubt on own ability",
          "doubts about implementation",
          "missing self-confidence",
          "prospect does not feel ready",
          "overwhelm through decision",
          "create can I that",
          "clarify ability to succeed",
          "doubts about competence",
          "confidence in implementation",
          "build confidence",
          "internal uncertainty",
          "prospect doubts themselves"
        ]
      }
    }
  },
  "Identitäts-Rahmung (P TS+, P CO+)": {
    "label": "Identity Framing",
    "aliases": [
      "identity framing",
      "identity framing",
      "address self-image",
      "customer role framework",
      "who does the prospect want to be",
      "strengthen decision-maker role",
      "future self",
      "activate buyer role",
      "reflect role of responsibility",
      "prospect as doer",
      "decision fits to the self-image",
      "action-oriented identity",
      "professional role",
      "self-perception in sales",
      "role in the purchase process"
    ],
    "applications": {
      "Selbstbild (P TS+)": {
        "label": "Self-Image",
        "aliases": [
          "self-image",
          "self-image of the prospects",
          "self-perception",
          "self-image",
          "prospect sees themselves",
          "decision and self-image",
          "who am I as decision maker",
          "prospect wants to consistent act",
          "self-concept",
          "self-concept",
          "gap between identity and action self-image",
          "prospect recognizes own pattern",
          "self-image question",
          "identity of the prospects",
          "decision fits to me"
        ]
      },
      "Status (P TS+)": {
        "label": "Status",
        "aliases": [
          "address status",
          "image in sales",
          "reputation protect",
          "reputation of the prospects",
          "professional expectation",
          "avoid loss of status",
          "reputation",
          "positioning of the prospects",
          "confident appear",
          "prestige in the purchase process",
          "status decision",
          "reputation in the team",
          "leadership status",
          "loss of image",
          "reputation as decision factor"
        ]
      },
      "Führungsidentität (P TS+, P CO+)": {
        "label": "Leadership Identity",
        "aliases": [
          "leadership identity",
          "leadership role",
          "prospect as leader",
          "leader identity",
          "responsibility as manager",
          "leadership through decision",
          "forward-looking leadership",
          "team responsibility",
          "decision as leadership task",
          "strategic leadership role",
          "prospect as pioneer",
          "leadership ambitions",
          "leadership question",
          "responsibility toward team",
          "leader decides"
        ]
      },
      "Professionelle Identität (P TS+, P CO+)": {
        "label": "Professional Identity",
        "aliases": [
          "professional identity",
          "prospect as professional",
          "professional expectation",
          "professional decision",
          "prospect wants to serious and professional act",
          "professionalism",
          "professional role",
          "business responsibility",
          "professional standard",
          "professional self-concept",
          "business identity",
          "professionalism and consequence",
          "professional buyer role",
          "businesslike decision",
          "professional expectation"
        ]
      },
      "Käuferidentität (P CO+)": {
        "label": "Buyer Identity",
        "aliases": [
          "buyer identity",
          "buyer role",
          "from the prospects to the buyer",
          "prospect or buyer",
          "prospect as client",
          "buyer mindset",
          "serious buyer",
          "readiness to buy as role",
          "from the evaluator to the buyer",
          "prospect takes over buyer role",
          "decision-maker role",
          "client role",
          "buyer status",
          "buyer identity",
          "activate buyer role"
        ]
      },
      "Wachstumsidentität (P TS+, P CO+)": {
        "label": "Growth Identity",
        "aliases": [
          "growth identity",
          "prospect wants to grow",
          "development",
          "growth mindset",
          "next level",
          "ambition to grow",
          "prospect wants to progress",
          "reflect ambition",
          "identity of upward mobility",
          "not stand stay",
          "development as self-image",
          "growth through decision",
          "future growth",
          "question of further development",
          "prospect as learner"
        ]
      },
      "Verantwortungsidentität (P CO+)": {
        "label": "Responsibility Identity",
        "aliases": [
          "responsibility identity",
          "prospect as responsible",
          "role of responsibility",
          "sense of duty",
          "responsible act",
          "for team responsible",
          "for result responsible",
          "decision take responsibility for",
          "responsibility toward company",
          "role as responsible",
          "responsibility take over",
          "activate sense of responsibility",
          "responsibility as identity",
          "person in charge",
          "duty to decide"
        ]
      },
      "Kontrollidentität (P TS+, P CO+)": {
        "label": "Control Identity",
        "aliases": [
          "identity of being in control",
          "control keep",
          "prospect wants to control",
          "avoid loss of control",
          "sovereignty",
          "control keep",
          "win overview",
          "self-determined decide",
          "control over one's actions",
          "need for control",
          "prospect wants to guide",
          "decision exists control",
          "again able to act become",
          "control about situation",
          "not controlled by others be"
        ]
      },
      "Entscheider-Identität (P TS+, P CO+)": {
        "label": "Decision-Maker Identity",
        "aliases": [
          "decision-maker identity",
          "prospect as decision maker",
          "decision maker",
          "doer role",
          "activate decision-maker role",
          "clear decision make",
          "ability to decide",
          "prospect makes decision",
          "responsible decision maker",
          "decisiveness",
          "buyer decides",
          "not only check decide",
          "decision maker self-image",
          "decision-making ability",
          "decision-maker question"
        ]
      },
      "Zukünftiges Selbst (P TS+)": {
        "label": "Future Self",
        "aliases": [
          "future self",
          "future self",
          "who wants the prospect become",
          "vision of the future",
          "vision of the prospects",
          "prospect in a year",
          "future version",
          "best self",
          "desired identity",
          "future role",
          "wo wants the prospect hin",
          "future situation",
          "future pacing",
          "development scenario",
          "future state"
        ]
      }
    }
  },
  "Bedarfsweckung (D CL+, D SC+, D MA+, D ME+, D CO+)": {
    "label": "Needs Activation",
    "aliases": [
      "needs activation",
      "activate need",
      "deepen need",
      "recognize shortage",
      "identify shortage",
      "shortage make concrete",
      "need visible do",
      "identify need",
      "develop need",
      "need diagnose",
      "understand shortage situation",
      "understand need situation",
      "need tangible do",
      "clarify relevance of the shortage of",
      "deepen starting situation"
    ],
    "applications": {
      "Mangelerkennung / Klarheit (D CL+)": {
        "label": "Recognizing a Need / Clarity",
        "aliases": [
          "recognize shortage",
          "shortage make concrete",
          "create clarity about the shortage",
          "need make concrete",
          "need diagnose",
          "shortage visible do",
          "what indicates determine",
          "statement make concrete",
          "understand situation more precisely",
          "need tangible do",
          "shortage tangible do",
          "establish lacking clarity",
          "understand need more precisely",
          "understand cause of the shortage of",
          "clarify shortage situation"
        ]
      },
      "Konkrete Szene (D SC+)": {
        "label": "Concrete Scenario",
        "aliases": [
          "concrete scene",
          "concrete situation",
          "open concrete case",
          "example situation",
          "last situation",
          "last incident",
          "concrete example",
          "situation reconstruct",
          "process make concrete",
          "real case walk through",
          "describe concrete moment",
          "situation detail",
          "when is that happens",
          "understand concrete process",
          "shortage in concrete situation"
        ]
      },
      "Magnitude / Ausmaß (D MA+)": {
        "label": "Magnitude / Extent",
        "aliases": [
          "magnitude",
          "extent",
          "extent of the shortage",
          "magnitude of the shortage",
          "duration of the shortage of",
          "frequency of the shortage of",
          "reach of the shortage of",
          "identify order of magnitude",
          "quantify shortage",
          "extent make concrete",
          "duration make concrete",
          "frequency make concrete",
          "capture affected areas",
          "significance of the shortage of",
          "dimension of the shortage of"
        ]
      },
      "Relevanz / Bedeutung (D ME+)": {
        "label": "Relevance / Meaning",
        "aliases": [
          "identify relevance",
          "identify meaning",
          "meaning of the shortage of",
          "relevance of the shortage of",
          "why is that important",
          "why is that important",
          "what means that for they",
          "personal meaning",
          "business meaning",
          "relevance for the daily operations",
          "relevance for the team",
          "relevance for the company",
          "importance of the shortage of",
          "understand meaning more precisely",
          "why plays that a role"
        ]
      },
      "Folgewirkung (D CO+)": {
        "label": "Downstream Impact",
        "aliases": [
          "downstream impact",
          "influence of the shortage of",
          "impact of the shortage of",
          "understand impacts",
          "influence on other areas",
          "impact to the daily operations",
          "impact to the team",
          "impact to the company",
          "further areas affected",
          "connections of the shortage of",
          "what depends so that together",
          "which influence has that",
          "what changes themselves as a result",
          "which areas influences that",
          "understand impact of the shortage of"
        ]
      }
    }
  },
  "Schmerz-Vertiefung (D EM+, D SC+, D ME+, D MA+, D CO+)": {
    "label": "Pain Deepening",
    "aliases": [
      "deepen pain",
      "pain digging",
      "deepen problem",
      "clarify the consequences of the problem",
      "deepen consequence",
      "identify impacts",
      "status quo pain",
      "pain point ask",
      "problem serious do",
      "not taking action pain",
      "prospect recognizes depth of problem",
      "implication question",
      "impact question",
      "problem becomes expensive",
      "urgency through pain"
    ],
    "applications": {
      "Finanzieller Schmerz (D MA+, D CO+)": {
        "label": "Financial Pain",
        "aliases": [
          "financial pain",
          "loss of money",
          "revenue loss",
          "profit loss",
          "costs rise",
          "money burn",
          "financial impact",
          "budget shortfall",
          "lost revenue",
          "expensive inefficiency",
          "cost trap",
          "economic damage",
          "financial consequence",
          "margin loses",
          "profit suffers"
        ]
      },
      "Zeitverlust (D MA+, D CO+)": {
        "label": "Time Loss",
        "aliases": [
          "time loss",
          "wasted time",
          "inefficiency",
          "lose hours",
          "too much time investment",
          "duplicate work",
          "idle time",
          "waiting time",
          "delay",
          "time drain",
          "process takes too long",
          "lost working time",
          "time costs",
          "slow workflows",
          "productive time goes lost"
        ]
      },
      "Emotionaler Schmerz (D EM+)": {
        "label": "Emotional Pain",
        "aliases": [
          "emotional pain",
          "frustration",
          "stress",
          "annoying the prospect",
          "burden",
          "pressure in everyday operations",
          "dissatisfaction",
          "concern",
          "frustrated of problem",
          "emotional burdened",
          "sleepless nights",
          "stomach aches because of problem",
          "mental burden",
          "demotivation",
          "deepen frustration"
        ]
      },
      "Opportunitätskosten (D MA+, D CO+)": {
        "label": "Opportunity Cost",
        "aliases": [
          "opportunity costs",
          "missed opportunity",
          "foregone revenue",
          "opportunity missed",
          "not used potential",
          "competitor draws over",
          "market opportunity lost",
          "to late respond",
          "lost business",
          "growth missed",
          "not in the process be",
          "potential remains lie",
          "opportunity not used",
          "foregone advantage",
          "falling behind to the competition"
        ]
      },
      "Beziehungsschmerz (D EM+, D ME+, D CO+)": {
        "label": "Relationship Pain",
        "aliases": [
          "relationship pain",
          "prospects lose",
          "team conflict",
          "internal tension",
          "customer dissatisfaction",
          "employees frustration",
          "loss of trust",
          "conflicts in the team",
          "bad atmosphere",
          "relationship suffers",
          "customer loyalty declines",
          "team suffers under problem",
          "communication problem",
          "internal friction losses",
          "turnover because of problem"
        ]
      },
      "Statusverlust (D EM+, D ME+, D CO+)": {
        "label": "Loss of Status",
        "aliases": [
          "loss of status",
          "loss of image",
          "reputation suffers",
          "reputation lose",
          "embarrassing before team",
          "not professionally appear",
          "reputation suffers",
          "loss of trust in the market",
          "prospect seems outdated",
          "competitor seems better",
          "not more leading",
          "fall behind",
          "professional expectation endangered",
          "visible falling behind",
          "loss of prestige"
        ]
      },
      "Kontrollverlust (D EM+, D ME+, D CO+)": {
        "label": "Loss of Control",
        "aliases": [
          "loss of control",
          "prospect loses control",
          "chaos in the process",
          "no overview",
          "not controllable",
          "dependent of others",
          "controlled by others",
          "helplessness",
          "problem escalates",
          "out of control",
          "not more able to act",
          "overwhelm",
          "no transparency",
          "uncontrolled situation",
          "loss of control"
        ]
      },
      "Wachstumsschmerz (D ME+, D CO+)": {
        "label": "Growth Pain",
        "aliases": [
          "growing pains",
          "stagnation",
          "standstill",
          "not progress",
          "growth blocked",
          "scaling fails",
          "plateau achieved",
          "progress remains out of",
          "development stagnates",
          "limit achieved",
          "barrier to growth",
          "no development",
          "falling behind grows",
          "growth remains lie",
          "next level blocked"
        ]
      },
      "Risikoschmerz (D EM+, D CO+)": {
        "label": "Risk Pain",
        "aliases": [
          "risk-related pain",
          "fear before loss",
          "security risk",
          "wrong decision threatens",
          "risk grows",
          "more uncertain status quo",
          "potential loss",
          "problem can tip",
          "dangerous development",
          "risk becomes bigger",
          "assurance lacking",
          "vulnerable situation",
          "risk through wait",
          "downstream risk",
          "unpredictable risk"
        ]
      },
      "Zukünftige Konsequenz (D SC+, D CO+)": {
        "label": "Future Consequences",
        "aliases": [
          "future consequence",
          "what happens later",
          "long-term damage",
          "follow-on problem",
          "problem becomes bigger",
          "later more costly",
          "negative development",
          "if nothing happens",
          "future risk",
          "long-term consequence",
          "future impact",
          "consequence in six months",
          "problem put off",
          "future pressure",
          "long-term consequences"
        ]
      }
    }
  },
  "Sprachmuster": {
    "label": "Language Patterns",
    "aliases": [
      "behavior pattern",
      "customer pattern recognize",
      "pattern in sales",
      "analyze behavior",
      "decision pattern",
      "understand buying behavior",
      "recurring pattern",
      "customer behavior reflect",
      "sales pattern",
      "blocking pattern",
      "action block",
      "avoidance pattern",
      "diagnostic question pattern",
      "pattern recognize in the call",
      "behavior question"
    ],
    "applications": {
      "Ich vs. Wir": {
        "label": "I vs. We",
        "aliases": [
          "I vs we",
          "alone or team",
          "lone wolf",
          "team decision",
          "silo think",
          "we feeling lacking",
          "alone decide",
          "together decide",
          "team involve",
          "collective responsibility",
          "isolated act",
          "not integrated",
          "coordination in the team",
          "team or individual",
          "shared solution"
        ]
      },
      "Wunsch vs. Pflicht": {
        "label": "Desire vs. Obligation",
        "aliases": [
          "desire vs obligation",
          "must or want",
          "the prospect wants to rather than has to",
          "voluntarily or forced",
          "sense of duty",
          "intrinsic motivation",
          "extrinsic pressure",
          "should do it, but does not want to",
          "necessity vs desire",
          "clarify motivation",
          "prospect feels compulsion",
          "identify want",
          "must problem",
          "decision out of obligation",
          "check genuine motivation"
        ]
      },
      "Reden vs. Handeln": {
        "label": "Talking vs. Acting",
        "aliases": [
          "talk vs act",
          "lots of talk, little action",
          "nothing happens",
          "only talked",
          "no implementation",
          "hot air",
          "empty promise",
          "meeting without result",
          "discussion instead of action",
          "inaction",
          "prospect talks only",
          "implementation gap",
          "action lacking",
          "knowledge but not taking action",
          "from the talk into act"
        ]
      },
      "Problem vs. Priorität": {
        "label": "Problem vs. Priority",
        "aliases": [
          "problem vs priority",
          "problem but no priority",
          "not enough important",
          "problem becomes tolerated",
          "prospect accepted problem",
          "priority lacking",
          "problem ignore",
          "not action-relevant",
          "agenda lacking",
          "peripheral issue",
          "problem lies idle",
          "urgency lacking",
          "important but not urgent",
          "conflicting priorities",
          "problem serious do"
        ]
      },
      "Bedenken vs. Konsequenz": {
        "label": "Concerns vs. Consequences",
        "aliases": [
          "concerns vs consequence",
          "prospect broods",
          "hesitation because of concerns",
          "analysis instead of decision",
          "concern blocked action",
          "concerns more important as consequences",
          "not taking action because of concerns",
          "consequence despite concerns",
          "prospect remains in the consider",
          "objector",
          "analysis paralysis",
          "overthinking instead of act",
          "wait out of concern",
          "consequences of the hesitation",
          "open concerns"
        ]
      },
      "Erkenntnis vs. Entscheidung": {
        "label": "Insight vs. Decision",
        "aliases": [
          "insight vs decision",
          "understood but not decided",
          "aha moment without action",
          "insight without implementation",
          "knowledge is not enough",
          "clarity without consequence",
          "prospect understands, but does not act",
          "knowing doing gap",
          "breakthrough without decision",
          "theory instead of practice",
          "activate insight",
          "decision after insight",
          "understood but hesitates",
          "not implemented despite insight",
          "insight in decision transform"
        ]
      },
      "Zustimmung vs. Commitment": {
        "label": "Agreement vs. Commitment",
        "aliases": [
          "agreement vs commitment",
          "yes but no commitment",
          "prospect agrees, but does not buy",
          "nodding without commitment",
          "half-hearted yes",
          "noncommittal agreement",
          "agreement without action",
          "prospect says yes maybe",
          "not commit despite agreement",
          "interest without commitment",
          "apparent agreement",
          "check genuine commitment",
          "agreement firm do",
          "prospect remains open",
          "yes say and nothing do"
        ]
      },
      "Interesse vs. Verantwortungsübernahme": {
        "label": "Interest vs. Taking Responsibility",
        "aliases": [
          "interest vs responsibility",
          "only interested",
          "spectator instead of doer",
          "prospect observes only",
          "interest without act",
          "curiosity but no responsibility",
          "tourist or buyer",
          "passive or actively",
          "prospect takes over no responsibility",
          "window shopper",
          "prospect or prospect",
          "activate responsibility",
          "from the spectator to the player",
          "check engagement",
          "interest in responsibility transform"
        ]
      },
      "Kontrolle vs. Delegation": {
        "label": "Control vs. Delegation",
        "aliases": [
          "control vs delegation",
          "prospect cannot let go",
          "everything self do",
          "delegate falls difficult",
          "micromanagement",
          "need for control",
          "relinquish learn",
          "trust in team lacking",
          "control relinquish",
          "not delegate can",
          "all reins keep",
          "let go in sales",
          "team benefit",
          "makes relinquish",
          "self do or relinquish"
        ]
      },
      "Vermeidung vs. Klarheit": {
        "label": "Avoidance vs. Clarity",
        "aliases": [
          "avoidance vs clarity",
          "head in the sand",
          "avoid problem",
          "reality not look at",
          "look away",
          "not accept reality want",
          "avoidance behavior",
          "avoid clarity",
          "avoid confrontation",
          "reality check",
          "prospect repressed problem",
          "topic ignore",
          "create clarity",
          "problem look at",
          "avoidance resolve"
        ]
      },
      "Vergleich vs. Entscheidung": {
        "label": "Comparison vs. Decision",
        "aliases": [
          "comparison vs decision",
          "always compare never decide",
          "comparison paralysis",
          "too many options",
          "choice overload",
          "prospect compares endless",
          "options paralysis",
          "benchmark instead of purchase",
          "fixated on comparison",
          "not decide because of alternatives",
          "comparison frenzy",
          "comparison stop",
          "decision despite options",
          "options overwhelm",
          "compare instead of buy"
        ]
      },
      "Komfort vs. Wachstum": {
        "label": "Comfort vs. Growth",
        "aliases": [
          "comfort vs growth",
          "comfort zone",
          "comfort zone leave",
          "growth is uncomfortable",
          "prospect remains comfortable",
          "habit blocked growth",
          "routine holds back",
          "change fear before",
          "uncomfortable but necessary",
          "safer safe haven",
          "growth step",
          "avoid change",
          "uncharted territory enter",
          "development despite discomfort",
          "comfort blocked decision"
        ]
      },
      "Kosten vs. Konsequenz": {
        "label": "Cost vs. Consequence",
        "aliases": [
          "costs vs consequence",
          "price or follow-on costs",
          "cheap becomes expensive",
          "wrong save money",
          "focus on cost",
          "long-term more costly",
          "not invest costs more",
          "price vs subsequent damage",
          "short-term save money",
          "long-term lose",
          "costs of the nothing doing",
          "cheapest solution most expensive solution",
          "frugality blocked",
          "avoid investment",
          "costs against consequence ask"
        ]
      },
      "Später vs. Jetzt": {
        "label": "Later vs. Now",
        "aliases": [
          "later vs now",
          "delay",
          "procrastination",
          "not today",
          "eventually later",
          "prospect postpones",
          "later maybe",
          "now or later",
          "procrastination",
          "tomorrow decide",
          "next week",
          "next year",
          "win time",
          "decision postpone",
          "delay question"
        ]
      },
      "Kosten vs. Investment": {
        "label": "Cost vs. Investment",
        "aliases": [
          "costs vs investment",
          "costs or investment",
          "expense vs investment",
          "cost item",
          "pure costs",
          "money spend",
          "investment instead of costs",
          "investment instead of expense",
          "focus on cost",
          "money is weg",
          "expense as loss",
          "investment mindset",
          "cost mentality",
          "expense or value creation",
          "costs in investment change perspective"
        ]
      },
      "Risiko vs. Chance": {
        "label": "Risk vs. Opportunity",
        "aliases": [
          "risk vs opportunity",
          "risk or opportunity",
          "only risk see",
          "opportunity not see",
          "fear before risk",
          "risk focus",
          "possibility ignore",
          "negative expectation",
          "what if it does not work",
          "failure instead of success",
          "downside instead of upside",
          "chance of loss",
          "opportunity despite risk",
          "consider risk one-sided",
          "of risk to opportunity"
        ]
      },
      "Kurzfristig vs. Langfristig": {
        "label": "Short-Term vs. Long-Term",
        "aliases": [
          "short-term vs long-term",
          "short-term think",
          "long-term think",
          "to short time horizon",
          "consider only short-term",
          "only the next months",
          "short-term focus",
          "long-term perspective lacking",
          "evaluate short-term",
          "long-term value overlook",
          "today instead of future",
          "short time horizon",
          "long-term impact",
          "short-term effort long-term benefit",
          "expand time horizon"
        ]
      },
      "Kurzfristiges Commitment vs. nachhaltiger Erfolg": {
        "label": "Short-Term Commitment vs. Sustainable Success",
        "aliases": [
          "short-term commitment vs sustainable success",
          "short commitment",
          "long-term commitment",
          "to short try out",
          "test only short",
          "test a month",
          "test two months",
          "to short duration",
          "success needs time",
          "sustainable success needs commitment",
          "commitment as prerequisite for success",
          "long-term keep at it",
          "to early give up",
          "continuity for success",
          "lasting implementation"
        ]
      }
    }
  },
  "Storytelling (P TC+, P TO+, P CN+, P TS+, P SK-)": {
    "label": "Storytelling",
    "aliases": [
      "storytelling",
      "sales story",
      "story in sales",
      "story in the sales call",
      "metaphor benefit",
      "analogy benefit",
      "customer story tell",
      "case story",
      "example story",
      "narrative in sales",
      "story instead of pitch",
      "trust through story",
      "solve objection with story",
      "explain problem through story",
      "decision through story facilitate"
    ],
    "applications": {
      "Kundenstory (P TC+, P TO+, P SK-)": {
        "label": "Customer Story",
        "aliases": [
          "customer story",
          "customer story",
          "customer example",
          "case example",
          "case study",
          "practical example",
          "reference case",
          "customer experience",
          "customer account",
          "similar prospect",
          "example customer",
          "customer case",
          "experience report",
          "prospect how they",
          "comparable customer case"
        ]
      },
      "Before-After (P TO+, P CN+, P TS+)": {
        "label": "Before and After",
        "aliases": [
          "before after",
          "before after",
          "before after story",
          "transformation story",
          "show transformation",
          "previously today comparison",
          "then and now",
          "starting point result",
          "turnaround story",
          "contrast story",
          "before after effect",
          "customer transformation",
          "change in outcome",
          "transformation tell",
          "from the problem to the solution"
        ]
      },
      "Founder Story (P TC+)": {
        "label": "Founder Story",
        "aliases": [
          "founder story",
          "founder story",
          "why we",
          "how everything began",
          "origin story",
          "origin story",
          "mission tell",
          "why I that do",
          "background story",
          "personal story",
          "startup story",
          "founder story",
          "company story",
          "vision tell",
          "origins the solution"
        ]
      },
      "Metapher (P CN+, P SK-)": {
        "label": "Metaphor",
        "aliases": [
          "metaphor",
          "metaphor in sales",
          "figurative comparison",
          "imagery",
          "parable",
          "sales metaphor",
          "explain problem figuratively",
          "explain solution figuratively",
          "symbolic comparison",
          "objection with metaphor",
          "explain complexity with image",
          "visual explanation",
          "vivid image",
          "metaphorical question",
          "sales imagery"
        ]
      },
      "Analogie (P CN+, P SK-)": {
        "label": "Analogy",
        "aliases": [
          "analogy",
          "analogy in sales",
          "comparison benefit",
          "how with comparison",
          "similar case",
          "parallel example",
          "comparison argument",
          "analogous case",
          "explain solution with analogy",
          "explain problem with analogy",
          "sales analogy",
          "understanding through comparison",
          "comparable situation",
          "analogy question",
          "explain complicated simple"
        ]
      },
      "Erfolgsgeschichte (P TC+, P TO+, P TS+, P SK-)": {
        "label": "Success Story",
        "aliases": [
          "success story",
          "success story",
          "success story",
          "success case",
          "customer success",
          "breakthrough story",
          "turnaround example",
          "best case",
          "example of success",
          "result story",
          "winning story",
          "outcome story",
          "customer outcome tell",
          "showcase story",
          "proof story"
        ]
      }
    }
  },
  "Realitätsabgleich (P TS-)": {
    "label": "Reality Check",
    "aliases": [
      "reality check",
      "reality check",
      "reality check",
      "calibrate self-assessment",
      "calibrate self-perception",
      "check overestimation",
      "establish realistic assessment",
      "compare self-image with reality",
      "assess abilities realistically",
      "assess resources realistically",
      "assess complexity realistically",
      "assess differentiation realistically",
      "check situation objectively",
      "reality check",
      "correct inflated self-assessment"
    ],
    "applications": {
      "Realitätswahrnehmung (P TS-)": {
        "label": "Perception of Reality",
        "aliases": [
          "perception of reality",
          "reality perceive",
          "assess situation realistically",
          "status quo realistically see",
          "check own situation",
          "reality check the situation",
          "check self-assessment",
          "overestimation the situation",
          "realistic assessment of current position",
          "facts against perception",
          "compare perception with facts",
          "reality instead of assumption",
          "evaluate situation objectively",
          "check actual state",
          "calibrate view of reality"
        ]
      },
      "Komplexitätswahrnehmung (P TS-)": {
        "label": "Perception of Complexity",
        "aliases": [
          "perception of complexity",
          "assess complexity realistically",
          "effort underestimates",
          "is not so simple",
          "complexity underestimates",
          "dependencies visible do",
          "check difficulty realistically",
          "requirements underestimates",
          "calibrate complexity",
          "task seems to simple",
          "multiple steps visible do",
          "check complex connections",
          "underestimated difficulty",
          "actual level of complexity",
          "complexity not underestimate"
        ]
      },
      "Fähigkeitswahrnehmung (P TS-)": {
        "label": "Perception of Ability",
        "aliases": [
          "perception of ability",
          "assess own abilities realistically",
          "check competence realistically",
          "can we that really themselves",
          "abilities overestimated",
          "check know how",
          "skills gap visible do",
          "calibrate own can",
          "check implementation capability",
          "check internal expertise",
          "recognize ability limits",
          "assess skill level realistically",
          "overestimation of competence",
          "own expertise question",
          "check real abilities"
        ]
      },
      "Ressourcenwahrnehmung (P TS-)": {
        "label": "Perception of Resources",
        "aliases": [
          "perception of resources",
          "assess resources realistically",
          "check capacity",
          "have we really enough resources",
          "check staff capacity",
          "check time resources",
          "check budget resources",
          "check infrastructure",
          "overestimation of resources",
          "available capacity realistically",
          "check implementation resources",
          "calibrate resource situation",
          "assess availability realistically",
          "internal capacity question",
          "check real resources"
        ]
      },
      "Differenzierungswahrnehmung (P TS-)": {
        "label": "Perception of Differentiation",
        "aliases": [
          "perception of differentiation",
          "we are different",
          "our industry is different",
          "our prospects are different",
          "check special case",
          "assess differentiation realistically",
          "special feature question",
          "check actual difference",
          "relevant differentiation",
          "supposed differentiation",
          "calibrate distinctiveness",
          "check market difference realistically",
          "compare special feature with reality",
          "difference really relevant",
          "overestimation of differentiation"
        ]
      }
    }
  },
  "Erwartungsverzerrung (P TO-)": {
    "label": "Expectation Distortion",
    "aliases": [
      "distorted expectations",
      "calibrate expectations",
      "unrealistic expectation",
      "inflated expectation",
      "correct expectations of the offer",
      "solution promise realistically do",
      "expectation management",
      "false expectation on solution",
      "too high expectation",
      "realistic assumption about results",
      "calibrate expectation on implementation",
      "no false promise",
      "clarify solution limits",
      "assess offer realistically",
      "correct expectation"
    ],
    "applications": {
      "Aufwand / Zeit (P TO-)": {
        "label": "Effort / Time",
        "aliases": [
          "effort time",
          "effort underestimates",
          "time investment underestimates",
          "goes not of alone",
          "implementation needs time",
          "implementation effort",
          "duration realistically do",
          "time until result",
          "assess effort realistically",
          "not immediately done",
          "calibrate implementation time",
          "explain own effort",
          "correct time-related expectation",
          "workload realistically",
          "speed not overestimate"
        ]
      },
      "Fähigkeiten (P TO-)": {
        "label": "Capabilities",
        "aliases": [
          "abilities the solution",
          "solution cannot everything",
          "functionality realistically",
          "abilities not overestimate",
          "explain system limitations",
          "what the solution can",
          "what the solution cannot do",
          "calibrate scope of services",
          "correct functional expectation",
          "offer cannot everything",
          "ability limits the solution",
          "performance promise realistically",
          "product performance realistically",
          "assess solution correct",
          "capability expectation"
        ]
      },
      "Erfolgswahrscheinlichkeit (P TO-)": {
        "label": "Probability of Success",
        "aliases": [
          "likelihood of success",
          "success not guaranteed",
          "probability realistically",
          "calibrate chance of success",
          "no safer success",
          "likelihood of achieving results",
          "success depends of factors from",
          "opportunity instead of guarantee",
          "realistic expectation of success",
          "result not promise",
          "prospect of success assess",
          "probability instead of security",
          "success not automatically",
          "realistic result expectation",
          "chance of success not overestimate"
        ]
      },
      "Garantien (P TO-)": {
        "label": "Guarantees",
        "aliases": [
          "guarantees",
          "expectation of a guarantee",
          "no guaranteed success",
          "limits of guarantees",
          "what becomes guaranteed",
          "what becomes not guaranteed",
          "assess guarantee realistically",
          "no false guarantee",
          "clarify scope of guarantee",
          "guarantee not overinterpret",
          "clarify contractual guarantee",
          "guarantee promise limit",
          "correct guarantee of results",
          "calibrate expectation of a guarantee",
          "guarantees transparent do"
        ]
      },
      "Sicherheiten (P TO-)": {
        "label": "Assurances",
        "aliases": [
          "assurances",
          "expectation of certainty",
          "no zero risk",
          "there is no absolute security",
          "clarify residual risk",
          "assurance realistically",
          "security limits",
          "risk remains exist",
          "no absolute security",
          "calibrate security promise",
          "assurance not overestimate",
          "assess risk realistically",
          "control not completely",
          "explain level of security",
          "correct expectation of certainty"
        ]
      }
    }
  },
  "Lösungsbewusstsein (P CC-)": {
    "label": "Solution Awareness",
    "aliases": [
      "solution awareness",
      "make the solution visible again",
      "establish hope",
      "make a way out visible",
      "problem is solvable",
      "ability to act give back",
      "solve out of hopelessness",
      "show solvability",
      "open weg after ahead",
      "problem not overwhelming",
      "again possibilities see",
      "open solution space",
      "solve resignation",
      "confidence in solvability",
      "from the problem to the solution"
    ],
    "applications": {
      "Lösbarkeit (P CC-)": {
        "label": "Solvability",
        "aliases": [
          "solvability",
          "problem is solvable",
          "path to a solution exists",
          "a solution exists",
          "feasibility visible do",
          "show solution approach",
          "solvable instead of hopeless",
          "problem can resolved become",
          "concrete solvability",
          "feasible solution",
          "solution realistically achievable",
          "possible solution",
          "way out exists",
          "solvability anchor",
          "establish feasibility"
        ]
      },
      "Hoffnung (P CC-)": {
        "label": "Hope",
        "aliases": [
          "hope",
          "establish hope",
          "confidence",
          "not hopeless",
          "positive prospect",
          "again perspective see",
          "solve resignation",
          "courage do",
          "future is changeable",
          "prospect on improvement",
          "hope on solution",
          "optimism realistically",
          "confidence give back",
          "solve hopelessness",
          "hope instead of resignation"
        ]
      },
      "Handlungsfähigkeit (P CC-)": {
        "label": "Ability to Act",
        "aliases": [
          "ability to act",
          "regain the ability to act",
          "options for action see",
          "no longer feel helpless",
          "next feasible lever",
          "control regain",
          "active possibility",
          "something do can",
          "room for action",
          "effective step possible",
          "not helpless",
          "show ability to shape outcomes",
          "open room for action",
          "again controllable",
          "establish ability to act"
        ]
      },
      "Fortschritt (P CC-)": {
        "label": "Progress",
        "aliases": [
          "progress",
          "small progress",
          "step-by-step improvement",
          "not everything on once",
          "development is possible",
          "first improvement visible",
          "progress instead of perfection",
          "step for step",
          "feasible stages",
          "partial progress",
          "movement after ahead",
          "improvement path",
          "progress realistically",
          "development possible",
          "positive movement"
        ]
      },
      "Zielerreichbarkeit (P CC-)": {
        "label": "Achievability of Goals",
        "aliases": [
          "achievability of the goal",
          "goal is achievable",
          "realistic goal",
          "goal not unattainable",
          "show achievability",
          "path to the goal visible do",
          "feasible goal",
          "realistic goal attainment",
          "goal in reach",
          "goal can achieved become",
          "result is possible",
          "goal not give up",
          "attainable result",
          "realistic prospects for success",
          "goal attainment plausible"
        ]
      },
      "Ausweg / Optionen (P CC-)": {
        "label": "Way Out / Options",
        "aliases": [
          "way out options",
          "way out find",
          "options visible do",
          "alternative ways",
          "solve several ways to",
          "not in dead end",
          "it exists options",
          "open options",
          "alternatives see",
          "alternative actions",
          "way out of problem",
          "options instead of hopelessness",
          "solution options",
          "potential ways",
          "recognize new options"
        ]
      }
    }
  }
} as const;


export const TECHNIC_LABELS = {
  de: TECHNIC_LABELS_DE,
  en: TECHNIC_LABELS_EN,
} as const;



export const MARKET_TAXONOMY = {
persona_role: [
  {
    label: 'Student',
    aliases: [
      'student',
      'studentin',
      'studierender',
      'studierende',
      'hochschulstudent',
      'hochschulstudentin',
      'universitätsstudent',
      'universitaetsstudent',
      'universitätsstudentin',
      'universitaetsstudentin',
      'fh student',
      'fh studentin',
      'bachelor student',
      'bachelor studentin',
      'master student',
      'master studentin',
      'dualer student',
      'duale studentin',
      'werkstudent',
      'werkstudentin',
      'studium interessent',
      'studium interessentin',
    ],
  },
  {
    label: 'Schüler',
    aliases: [
      'schüler',
      'schueler',
      'schülerin',
      'schuelerin',
      'schulkind',
      'jugendlicher schüler',
      'jugendliche schülerin',
      'jugendliche schuelerin',
      'gymnasiast',
      'gymnasiastin',
      'realschüler',
      'realschueler',
      'realschülerin',
      'realschuelerin',
      'hauptschüler',
      'hauptschueler',
      'hauptschülerin',
      'hauptschuelerin',
      'berufsschüler',
      'berufsschueler',
      'berufsschülerin',
      'berufsschuelerin',
    ],
  },
  {
    label: 'Auszubildender',
    aliases: [
      'auszubildender',
      'auszubildende',
      'azubi',
      'azubine',
      'lehrling',
      'lehrmädchen',
      'lehrmaedchen',
      'ausbildungssuchender',
      'ausbildungssuchende',
      'berufseinsteiger ausbildung',
      'berufseinsteigerin ausbildung',
      'dualer ausbildungsteilnehmer',
      'duale ausbildungsteilnehmerin',
      'lehrstelle interessent',
      'lehrstelle interessentin',
      'azubi kandidat',
      'azubi kandidatin',
      'ausbildungsbewerber',
      'ausbildungsbewerberin',
      'nachwuchskraft ausbildung',
      'junior in ausbildung',
    ],
  },
  {
    label: 'Berufseinsteiger',
    aliases: [
      'berufseinsteiger',
      'berufseinsteigerin',
      'young professional',
      'young professional weiblich',
      'starter im beruf',
      'starterin im beruf',
      'karrierestarter',
      'karrierestarterin',
      'einsteiger arbeitsmarkt',
      'einsteigerin arbeitsmarkt',
      'erster job',
      'erste stelle',
      'jobstarter',
      'jobstarterin',
      'junior kandidat',
      'junior kandidatin',
      'nach dem studium',
      'nach der ausbildung',
      'frisch ausgelernt',
      'frisch absolviert',
      'berufsanfänger',
      'berufsanfaenger',
    ],
  },
  {
    label: 'Familienvater',
    aliases: [
      'familienvater',
      'vater',
      'papa',
      'dad',
      'vaterfigur',
      'mann mit kindern',
      'vater mit kindern',
      'berufstätiger vater',
      'berufstaetiger vater',
      'arbeitender vater',
      'alleinerziehender vater',
      'junger vater',
      'erfahrener vater',
      'mehrfacher vater',
      'vater einer familie',
      'haushaltsvater',
      'familienoberhaupt männlich',
      'familienoberhaupt maennlich',
      'sorgeberechtigter vater',
      'erziehungsberechtigter vater',
      'vater als kunde',
      'vater als käufer',
      'vater als kaeuufer',
      'vater als entscheider im alltag',
      'familienorientierter mann',
    ],
  },
  {
    label: 'Familienmutter',
    aliases: [
      'familienmutter',
      'mutter',
      'mama',
      'mom',
      'mutterfigur',
      'frau mit kindern',
      'mutter mit kindern',
      'berufstätige mutter',
      'berufstaetige mutter',
      'arbeitende mutter',
      'alleinerziehende mutter',
      'junge mutter',
      'erfahrene mutter',
      'mehrfache mutter',
      'mutter einer familie',
      'haushaltsmutter',
      'familienoberhaupt weiblich',
      'sorgeberechtigte mutter',
      'erziehungsberechtigte mutter',
      'mutter als kundin',
      'mutter als käuferin',
      'mutter als kaeuuferin',
      'mutter als entscheiderin im alltag',
      'familienorientierte frau',
    ],
  },
  {
    label: 'Alleinerziehender Vater',
    aliases: [
      'alleinerziehender vater',
      'single dad',
      'single father',
      'vater allein mit kind',
      'vater allein mit kindern',
      'alleinstehender vater',
      'getrennter vater',
      'geschiedener vater',
      'vater mit alleiniger verantwortung',
      'vater mit sorgerecht',
      'vater mit alltagsverantwortung',
      'vater ohne partnerin',
      'vater ohne partner',
      'alleinverantwortlicher vater',
      'vater als hauptbetreuer',
      'vater als primäre bezugsperson',
      'vater als primaere bezugsperson',
      'vater im wechselmodell',
      'vater mit kind im haushalt',
      'alleinerziehender papa',
    ],
  },
  {
    label: 'Alleinerziehende Mutter',
    aliases: [
      'alleinerziehende mutter',
      'single mom',
      'single mother',
      'mutter allein mit kind',
      'mutter allein mit kindern',
      'alleinstehende mutter',
      'getrennte mutter',
      'geschiedene mutter',
      'mutter mit alleiniger verantwortung',
      'mutter mit sorgerecht',
      'mutter mit alltagsverantwortung',
      'mutter ohne partner',
      'mutter ohne partnerin',
      'alleinverantwortliche mutter',
      'mutter als hauptbetreuerin',
      'mutter als primäre bezugsperson',
      'mutter als primaere bezugsperson',
      'mutter im wechselmodell',
      'mutter mit kind im haushalt',
      'alleinerziehende mama',
    ],
  },
  {
    label: 'Geschäftsführer',
    aliases: [
      'geschäftsführer',
      'geschaeftsfuehrer',
      'gf',
      'chef der firma',
      'firmenchef',
      'unternehmenschef',
      'geschäftsleitung',
      'geschaeftsleitung',
      'leiter der gmbh',
      'gmbh geschäftsführer',
      'geschäftsführer mittelstand',
      'geschaeftsfuehrer mittelstand',
      'geschäftsführer kmu',
      'geschaeftsfuehrer kmu',
      'operativer geschäftsführer',
      'operativer geschaeftsfuehrer',
      'verantwortlicher geschäftsführer',
      'verantwortlicher geschaeftsfuehrer',
      'alleiniger geschäftsführer',
      'alleiniger geschaeftsfuehrer',
    ],
  },
  {
    label: 'Angestellter Geschäftsführer',
    aliases: [
      'angestellter geschäftsführer',
      'angestellter geschaeftsfuehrer',
      'fremdgeschäftsführer',
      'fremdgeschaeftsfuehrer',
      'geschäftsführer ohne anteile',
      'geschaeftsfuehrer ohne anteile',
      'eingesetzter geschäftsführer',
      'eingesetzter geschaeftsfuehrer',
      'management geschäftsführer',
      'management geschaeftsfuehrer',
      'externer geschäftsführer',
      'externer geschaeftsfuehrer',
      'geschäftsführer nicht inhaber',
      'geschaeftsfuehrer nicht inhaber',
      'professioneller geschäftsführer',
      'professioneller geschaeftsfuehrer',
      'manager als geschäftsführer',
      'manager als geschaeftsfuehrer',
      'gf ohne gesellschaftsanteile',
      'geschaeftsfuehrer ohne eigentum',
    ],
  },
  {
    label: 'Geschäftsführender Gesellschafter',
    aliases: [
      'geschäftsführender gesellschafter',
      'geschaeftsfuehrender gesellschafter',
      'gesellschafter geschäftsführer',
      'gesellschafter geschaeftsfuehrer',
      'geschäftsführer mit anteilen',
      'geschaeftsfuehrer mit anteilen',
      'geschäftsführer und gesellschafter',
      'geschaeftsfuehrer und gesellschafter',
      'inhaber geschäftsführer',
      'inhaber geschaeftsfuehrer',
      'mitinhaber geschäftsführer',
      'mitinhaber geschaeftsfuehrer',
      'geschäftsführender mitinhaber',
      'geschaeftsfuehrender mitinhaber',
      'unternehmerischer geschäftsführer',
      'unternehmerischer geschaeftsfuehrer',
      'gesellschafter der operativ führt',
      'gesellschafter der operativ fuehrt',
      'owner managing director',
      'gesellschafter in der leitung',
    ],
  },
  {
    label: 'Inhaber',
    aliases: [
      'inhaber',
      'inhaberin',
      'firmeninhaber',
      'firmeninhaberin',
      'geschäftsinhaber',
      'geschaeftsinhaber',
      'geschäftsinhaberin',
      'geschaeftsinhaberin',
      'unternehmensinhaber',
      'unternehmensinhaberin',
      'betriebseigentümer',
      'betriebseigentuemer',
      'betriebseigentümerin',
      'betriebseigentuemerin',
      'owner',
      'business owner',
      'besitzer des betriebs',
      'besitzerin des betriebs',
      'eigentümer der firma',
      'eigentuemer der firma',
    ],
  },
  {
    label: 'Alleininhaber',
    aliases: [
      'alleininhaber',
      'alleininhaberin',
      'einziger inhaber',
      'einzige inhaberin',
      'alleineigentümer',
      'alleineigentuemer',
      'alleineigentümerin',
      'alleineigentuemerin',
      'solo inhaber',
      'solo inhaberin',
      'firma gehört ihm allein',
      'firma gehoert ihm allein',
      'firma gehört ihr allein',
      'firma gehoert ihr allein',
      'alleiniger eigentümer',
      'alleiniger eigentuemer',
      'alleinige eigentümerin',
      'alleinige eigentuemerin',
      'single owner',
      'sole owner',
    ],
  },
  {
    label: 'Mitinhaber',
    aliases: [
      'mitinhaber',
      'mitinhaberin',
      'teilhaber',
      'teilhaberin',
      'miteigentümer',
      'miteigentuemer',
      'miteigentümerin',
      'miteigentuemerin',
      'partner im unternehmen',
      'partnerin im unternehmen',
      'anteilseigner im betrieb',
      'anteilseignerin im betrieb',
      'geschäftspartner mit anteilen',
      'geschaeftspartner mit anteilen',
      'geschäftspartnerin mit anteilen',
      'geschaeftspartnerin mit anteilen',
      'co owner',
      'co-owner',
      'co inhaber',
      'co inhaberin',
    ],
  },
  {
    label: 'Gründer',
    aliases: [
      'gründer',
      'gruender',
      'gründerin',
      'gruenderin',
      'founder',
      'startup gründer',
      'startup gruender',
      'startup gründerin',
      'startup gruenderin',
      'unternehmensgründer',
      'unternehmensgruender',
      'unternehmensgründerin',
      'unternehmensgruenderin',
      'firmengründer',
      'firmengruender',
      'firmengründerin',
      'firmengruenderin',
      'gründender inhaber',
      'gruendender inhaber',
      'founding entrepreneur',
    ],
  },
  {
    label: 'Mitgründer',
    aliases: [
      'mitgründer',
      'mitgruender',
      'mitgründerin',
      'mitgruenderin',
      'co founder',
      'co-founder',
      'cofounder',
      'co founderin',
      'co-founderin',
      'founding partner',
      'gründungspartner',
      'gruendungspartner',
      'gründungspartnerin',
      'gruendungspartnerin',
      'zweiter gründer',
      'zweiter gruender',
      'zweite gründerin',
      'zweite gruenderin',
      'mitglied des gründungsteams',
      'mitglied des gruendungsteams',
    ],
  },
  {
    label: 'CEO',
    aliases: [
      'ceo',
      'chief executive officer',
      'company ceo',
      'startup ceo',
      'konzern ceo',
      'scaleup ceo',
      'deutscher ceo',
      'ceo der firma',
      'ceo des unternehmens',
      'ceo mittelstand',
      'ceo saas',
      'ceo softwareunternehmen',
      'ceo agentur',
      'ceo gruppe',
      'ceo holding',
      'ceo dachgesellschaft',
      'ceo business unit',
      'ceo region',
      'ceo international',
      'chief executive',
    ],
  },
  {
    label: 'CFO',
    aliases: [
      'cfo',
      'chief financial officer',
      'finanzvorstand',
      'finanzchef',
      'finanzchefin',
      'leiter finanzen',
      'leiterin finanzen',
      'head of finance',
      'finance director',
      'finanzdirektor',
      'finanzdirektorin',
      'kaufmännischer leiter',
      'kaufmaennischer leiter',
      'kaufmännische leiterin',
      'kaufmaennische leiterin',
      'leitung finanzabteilung',
      'finanzverantwortlicher',
      'finanzverantwortliche',
      'commercial finance lead',
      'finance executive',
    ],
  },
  {
    label: 'CTO',
    aliases: [
      'cto',
      'chief technology officer',
      'technologiechef',
      'technologiechefin',
      'leiter technologie',
      'leiterin technologie',
      'head of technology',
      'tech chief',
      'technischer vorstand',
      'technische vorständin',
      'technische vorstaendin',
      'technischer geschäftsleiter',
      'technischer geschaeftsleiter',
      'technische geschäftsleiterin',
      'technische geschaeftsleiterin',
      'software cto',
      'saas cto',
      'produkttechnischer leiter',
      'produkttechnische leiterin',
      'technology director',
    ],
  },
  {
    label: 'COO',
    aliases: [
      'coo',
      'chief operating officer',
      'operations chief',
      'operations chef',
      'operations chefin',
      'leiter operations',
      'leiterin operations',
      'head of operations',
      'operative geschäftsleitung',
      'operative geschaeftsleitung',
      'operativer vorstand',
      'operative vorständin',
      'operative vorstaendin',
      'betriebsverantwortlicher auf c level',
      'betriebsverantwortliche auf c level',
      'operations director',
      'chief operations officer',
      'operative executive rolle',
      'coo mittelstand',
      'coo scaleup',
    ],
  },
  {
    label: 'CIO',
    aliases: [
      'cio',
      'chief information officer',
      'it vorstand',
      'it-vorstand',
      'informationschef',
      'informationschefin',
      'leiter informationssysteme',
      'leiterin informationssysteme',
      'head of information systems',
      'digital information officer',
      'cio mittelstand',
      'cio konzern',
      'cio gruppe',
      'it executive',
      'information technology executive',
      'digitalisierungsvorstand',
      'digitalisierungsvorständin',
      'digitalisierungsvorstaendin',
      'enterprise it chief',
      'informationssystem leitung',
    ],
  },
  {
    label: 'CMO',
    aliases: [
      'cmo',
      'chief marketing officer',
      'marketingvorstand',
      'marketingchef',
      'marketingchefin',
      'leiter marketing',
      'leiterin marketing',
      'head of marketing',
      'marketing director',
      'brand executive',
      'growth executive',
      'marketing executive',
      'commercial marketing lead',
      'demand generation executive',
      'performance marketing executive',
      'kommunikationschef',
      'kommunikationschefin',
      'markenchef',
      'markenchefin',
      'cmo mittelstand',
    ],
  },
  {
    label: 'CHRO',
    aliases: [
      'chro',
      'chief human resources officer',
      'personalvorstand',
      'hr vorstand',
      'hr-vorstand',
      'personalchef',
      'personalchefin',
      'leiter human resources',
      'leiterin human resources',
      'head of people',
      'people executive',
      'hr executive',
      'chief people officer',
      'cpo people',
      'personal executive',
      'people and culture lead',
      'leiter people culture',
      'leiterin people culture',
      'human capital officer',
      'chief talent officer',
    ],
  },
  {
    label: 'Chief Revenue Officer',
    aliases: [
      'chief revenue officer',
      'cro',
      'revenue chief',
      'revenue chef',
      'revenue chefin',
      'umsatzchef',
      'umsatzchefin',
      'leiter revenue',
      'leiterin revenue',
      'head of revenue',
      'revenue executive',
      'go to market executive',
      'gtm executive',
      'commercial revenue lead',
      'umsatzverantwortlicher c level',
      'umsatzverantwortliche c level',
      'cro saas',
      'cro scaleup',
      'revenue operations executive',
      'chief sales revenue officer',
    ],
  },
  {
    label: 'Chief Sales Officer',
    aliases: [
      'chief sales officer',
      'cso sales',
      'sales chief',
      'vertriebsvorstand',
      'vertriebschef',
      'vertriebschefin',
      'leiter vertrieb c level',
      'leiterin vertrieb c level',
      'head of sales executive',
      'sales executive',
      'commercial sales chief',
      'verkaufsvorstand',
      'verkaufschef',
      'verkaufschefin',
      'chief commercial sales officer',
      'sales officer',
      'cso vertrieb',
      'global sales chief',
      'regional sales chief',
      'vertriebsleitung c suite',
    ],
  },
  {
    label: 'Chief Product Officer',
    aliases: [
      'chief product officer',
      'cpo product',
      'produktchef',
      'produktchefin',
      'produktvorstand',
      'leiter produkt',
      'leiterin produkt',
      'head of product',
      'product director',
      'product executive',
      'produktverantwortlicher c level',
      'produktverantwortliche c level',
      'produktstrategie leitung',
      'product strategy lead',
      'chief product manager',
      'cpo software',
      'cpo saas',
      'cpo plattform',
      'produktportfolio leitung',
      'product owner executive',
    ],
  },
  {
    label: 'Geschäftsleitungsmitglied',
    aliases: [
      'geschäftsleitungsmitglied',
      'geschaeftsleitungsmitglied',
      'mitglied der geschäftsleitung',
      'mitglied der geschaeftsleitung',
      'leitungskreis mitglied',
      'mitglied im leitungskreis',
      'executive team member',
      'management team mitglied',
      'mitglied des managementteams',
      'erweiterte geschäftsleitung',
      'erweiterte geschaeftsleitung',
      'führungskreis mitglied',
      'fuehrungskreis mitglied',
      'leitungsteam person',
      'management board member',
      'leitungsebene person',
      'senior management mitglied',
      'executive committee member',
      'mitglied im executive committee',
      'person aus der unternehmensleitung',
    ],
  },
  {
    label: 'Beirat',
    aliases: [
      'beirat',
      'beirätin',
      'beiraetin',
      'beiratsmitglied',
      'mitglied des beirats',
      'strategischer beirat',
      'advisory board member',
      'advisory board',
      'berater im beirat',
      'beraterin im beirat',
      'unternehmensbeirat',
      'familienbeirat',
      'startup beirat',
      'investorenbeirat',
      'fachbeirat',
      'aufsicht naher beirat',
      'externer beirat',
      'unabhängiger beirat',
      'unabhaengiger beirat',
      'senior advisor beirat',
    ],
  },
  {
    label: 'Aufsichtsrat',
    aliases: [
      'aufsichtsrat',
      'aufsichtsrätin',
      'aufsichtsraetin',
      'aufsichtsratsmitglied',
      'mitglied des aufsichtsrats',
      'kontrollgremium mitglied',
      'supervisory board member',
      'supervisory board',
      'aufsichtsratsvorsitzender',
      'aufsichtsratsvorsitzende',
      'kontrollorgan person',
      'gesellschaftsaufsicht',
      'gremienaufsicht',
      'board oversight member',
      'aufsichtsrat der ag',
      'aufsichtsrat der gmbh',
      'kontrollrat',
      'mitglied kontrollrat',
      'corporate governance board',
      'non executive board member',
    ],
  },
  {
    label: 'Gesellschafter',
    aliases: [
      'gesellschafter',
      'gesellschafterin',
      'anteilseigner',
      'anteilseignerin',
      'shareholder',
      'gesellschafter ohne geschäftsführung',
      'gesellschafter ohne geschaeftsfuehrung',
      'passiver gesellschafter',
      'passive gesellschafterin',
      'kapitalgesellschafter',
      'kapitalgesellschafterin',
      'investierter gesellschafter',
      'investierte gesellschafterin',
      'gesellschafterkreis person',
      'person im gesellschafterkreis',
      'mitgesellschafter',
      'mitgesellschafterin',
      'shareholder representative',
      'equity holder',
      'anteilsinhaber',
    ],
  },
  {
    label: 'Investor',
    aliases: [
      'investor',
      'investorin',
      'kapitalgeber',
      'kapitalgeberin',
      'geldgeber',
      'geldgeberin',
      'finanzierer',
      'finanziererin',
      'wagniskapitalgeber',
      'wagniskapitalgeberin',
      'venture capital investor',
      'vc investor',
      'private equity investor',
      'pe investor',
      'business angel',
      'angel investor',
      'lead investor',
      'strategischer investor',
      'strategische investorin',
      'family office investor',
    ],
  },
  {
    label: 'Vertriebsleiter',
    aliases: [
      'vertriebsleiter',
      'vertriebsleiterin',
      'vertriebsleitung',
      'head of sales',
      'sales director',
      'sales lead',
      'leiter vertrieb',
      'leiterin vertrieb',
      'verkaufsleiter',
      'verkaufsleiterin',
      'verkaufsleitung',
      'regionaler vertriebsleiter',
      'regionale vertriebsleiterin',
      'national sales manager',
      'b2b vertriebsleiter',
      'b2b vertriebsleiterin',
      'field sales leiter',
      'field sales leiterin',
      'sales team lead',
      'vertriebsteam leitung',
    ],
  },
  {
    label: 'Key-Account-Manager',
    aliases: [
      'key account manager',
      'key-account-manager',
      'kam',
      'key account betreuer',
      'key account betreuerin',
      'großkundenbetreuer',
      'grosskundenbetreuer',
      'großkundenbetreuerin',
      'grosskundenbetreuerin',
      'strategischer kundenbetreuer',
      'strategische kundenbetreuerin',
      'enterprise account manager',
      'major account manager',
      'global account manager',
      'national account manager',
      'bestandskunden account manager',
      'top kunden betreuer',
      'top kunden betreuerin',
      'account lead',
      'key client manager',
    ],
  },
  {
    label: 'Account-Executive',
    aliases: [
      'account executive',
      'ae',
      'vertriebsmitarbeiter abschluss',
      'vertriebsmitarbeiterin abschluss',
      'closing sales rep',
      'closer sales',
      'neukundenvertriebler',
      'neukundenvertrieblerin',
      'b2b sales rep',
      'quota carrying sales rep',
      'abschlussverkäufer',
      'abschlussverkaeufer',
      'abschlussverkäuferin',
      'abschlussverkaeuferin',
      'enterprise sales rep',
      'mid market sales rep',
      'inside sales executive',
      'field account executive',
      'new business account executive',
      'account executive neukunden',
      'account executive b2b',
      'account executive closing',
      'ae vertrieb',
      'ae neukundengewinnung',
    ],
  },
  {
    label: 'Business-Development-Manager',
    aliases: [
      'business development manager',
      'bdm',
      'bizdev manager',
      'geschäftsentwicklung',
      'geschaeftsentwicklung',
      'leiter business development',
      'business developer',
      'business developerin',
      'partner development manager',
      'neugeschäft entwickler',
      'neugeschaeft entwickler',
      'market development manager',
      'strategic partnerships manager',
      'alliance manager',
      'vertriebsentwicklung',
      'wachstumsentwicklung',
      'new market manager',
      'growth partnerships',
      'commercial development manager',
      'business expansion manager',
    ],
  },
  {
    label: 'Sales-Development-Representative',
    aliases: [
      'sales development representative',
      'sdr',
      'vertriebsentwickler',
      'vertriebsentwicklerin',
      'lead qualifier',
      'prospecting rep',
      'outbound rep',
      'inbound sdr',
      'outbound sdr',
      'terminierer',
      'terminiererin',
      'erstkontakt vertrieb',
      'cold calling rep',
      'akquise mitarbeiter',
      'akquise mitarbeiterin',
      'lead generation rep',
      'pre sales development',
      'sales appointment setter',
      'terminvereinbarer',
      'vorqualifizierung vertrieb',
    ],
  },
  {
    label: 'Marketingleiter',
    aliases: [
      'marketingleiter',
      'marketingleiterin',
      'marketingleitung',
      'online marketingleiter',
      'online marketingleiterin',
      'performance marketing leiter',
      'performance marketing leiterin',
      'brand marketing leiter',
      'brand marketing leiterin',
      'growth marketing leiter',
      'growth marketing leiterin',
      'kommunikationsleiter',
      'kommunikationsleiterin',
      'marketingverantwortlicher',
      'marketingverantwortliche',
      'marketing team lead',
      'marketing abteilungsleiter',
      'marketing abteilungsleiterin',
      'marketing head mittelstand',
      'leitung online marketing',
      'leiterin online marketing',
      'marketing department head',
    ],
  },
  {
    label: 'Performance-Marketing-Manager',
    aliases: [
      'performance marketing manager',
      'paid ads manager',
      'paid media manager',
      'sea manager',
      'google ads manager',
      'meta ads manager',
      'facebook ads manager',
      'online advertising manager',
      'growth ads manager',
      'performance marketer',
      'performance marketerin',
      'kampagnenmanager performance',
      'kampagnenmanagerin performance',
      'paid social manager',
      'paid search manager',
      'acquisition manager',
      'digital acquisition manager',
      'media buying manager',
      'traffic manager',
      'conversion marketing manager',
    ],
  },
  {
    label: 'Brand-Manager',
    aliases: [
      'brand manager',
      'markenmanager',
      'markenmanagerin',
      'brand lead',
      'markenverantwortlicher',
      'markenverantwortliche',
      'brand strategist',
      'markenstratege',
      'markenstrategin',
      'corporate brand manager',
      'employer brand manager',
      'brand communication manager',
      'markenkommunikation',
      'brand owner',
      'positionierung manager',
      'positionierungsmanager',
      'positionierungsmanagerin',
      'brand marketing manager',
      'brand custodian',
      'markenpflege verantwortliche person',
    ],
  },
  {
    label: 'HR-Leiter',
    aliases: [
      'hr leiter',
      'hr-leiter',
      'hr leiterin',
      'hr-leiterin',
      'personalleiter',
      'personalleiterin',
      'personalleitung',
      'head of hr',
      'human resources leitung',
      'leiter personal',
      'leiterin personal',
      'hr manager leitung',
      'personalchef unter c level',
      'personalchefin unter c level',
      'people operations leitung',
      'people and culture leitung',
      'hr verantwortlicher',
      'hr verantwortliche',
      'personalabteilungsleiter',
      'personalabteilungsleiterin',
    ],
  },
  {
    label: 'Recruiter',
    aliases: [
      'recruiter',
      'recruiterin',
      'personalrecruiter',
      'personalrecruiterin',
      'talent acquisition specialist',
      'talent acquisition manager',
      'inhouse recruiter',
      'corporate recruiter',
      'sourcing recruiter',
      'active sourcer',
      'active sourcerin',
      'recruiting specialist',
      'recruiting manager operative',
      'bewerbermanager',
      'bewerbermanagerin',
      'kandidatenmanager',
      'kandidatenmanagerin',
      'personalgewinnung spezialist',
      'personalgewinnung spezialistin',
      'fachkräfte recruiter',
    ],
  },
  {
    label: 'Ausbildungsleiter',
    aliases: [
      'ausbildungsleiter',
      'ausbildungsleiterin',
      'leiter ausbildung',
      'leiterin ausbildung',
      'azubi verantwortlicher',
      'azubi verantwortliche',
      'ausbildungsverantwortlicher',
      'ausbildungsverantwortliche',
      'trainer ausbildung',
      'trainerin ausbildung',
      'berufsbildungsleiter',
      'berufsbildungsleiterin',
      'nachwuchsleitung',
      'nachwuchsleiter',
      'nachwuchsleiterin',
      'ausbilder koordinierend',
      'ausbilderin koordinierend',
      'dual study coordinator',
      'lehrlingswart',
      'lehrlingswartin',
    ],
  },
  {
    label: 'Personalsachbearbeiter',
    aliases: [
      'personalsachbearbeiter',
      'personalsachbearbeiterin',
      'hr sachbearbeiter',
      'hr sachbearbeiterin',
      'personaladministration',
      'personaladmin',
      'hr administration',
      'lohn personal sachbearbeiter',
      'lohn personal sachbearbeiterin',
      'personalservice mitarbeiter',
      'personalservice mitarbeiterin',
      'mitarbeiter personalabteilung',
      'mitarbeiterin personalabteilung',
      'hr coordinator',
      'personal koordinator',
      'personal koordinatoriN',
      'employee services',
      'hr backoffice',
      'personal backoffice',
      'hr operations sachbearbeitung',
    ],
  },
  {
    label: 'Einkaufsleiter',
    aliases: [
      'einkaufsleiter',
      'einkaufsleiterin',
      'leitung einkauf',
      'leiter einkauf',
      'leiterin einkauf',
      'head of procurement',
      'procurement director',
      'purchasing director',
      'strategischer einkaufsleiter',
      'strategische einkaufsleiterin',
      'chief procurement unter c level',
      'beschaffungsleiter',
      'beschaffungsleiterin',
      'sourcing leiter',
      'sourcing leiterin',
      'lieferantenmanagement leitung',
      'category procurement lead',
      'einkaufsverantwortlicher',
      'einkaufsverantwortliche',
      'purchasing lead',
    ],
  },
  {
    label: 'Einkäufer',
    aliases: [
      'einkäufer',
      'einkaeufer',
      'einkäuferin',
      'einkaeuferin',
      'operativer einkäufer',
      'operativer einkaeufer',
      'operative einkäuferin',
      'operative einkaeuferin',
      'strategischer einkäufer',
      'strategischer einkaeufer',
      'strategische einkäuferin',
      'strategische einkaeuferin',
      'procurement manager',
      'purchasing manager',
      'buyer',
      'sourcing specialist',
      'category buyer',
      'technical buyer',
      'projekteinkäufer',
      'projekteinkaeufer',
    ],
  },
  {
    label: 'Controller',
    aliases: [
      'controller',
      'controllerin',
      'controlling manager',
      'financial controller',
      'business controller',
      'vertriebscontroller',
      'vertriebscontrollerin',
      'produktionscontroller',
      'produktionscontrollerin',
      'kostencontroller',
      'kostencontrollerin',
      'commercial controller',
      'finance analyst',
      'financial analyst',
      'controlling specialist',
      'budget controller',
      'reporting controller',
      'wertekontrolle finance',
      'kennzahlen controller',
      'performance controller',
    ],
  },
  {
    label: 'Buchhalter',
    aliases: [
      'buchhalter',
      'buchhalterin',
      'finanzbuchhalter',
      'finanzbuchhalterin',
      'bilanzbuchhalter',
      'bilanzbuchhalterin',
      'accountant',
      'accounting specialist',
      'rechnungswesen mitarbeiter',
      'rechnungswesen mitarbeiterin',
      'kreditorenbuchhalter',
      'kreditorenbuchhalterin',
      'debitorenbuchhalter',
      'debitorenbuchhalterin',
      'lohnbuchhalter',
      'lohnbuchhalterin',
      'buchhaltung leitung operativ',
      'buchhaltungsmanager',
      'buchhaltungsmanagerin',
      'accounting manager',
    ],
  },
  {
    label: 'IT-Leiter',
    aliases: [
      'it leiter',
      'it-leiter',
      'it leiterin',
      'it-leiterin',
      'leitung it',
      'leiter it',
      'leiterin it',
      'head of it',
      'it manager',
      'it director',
      'infrastrukturleiter it',
      'infrastrukturleiterin it',
      'leiter informationstechnik',
      'leiterin informationstechnik',
      'systeme leitung',
      'it verantwortlicher',
      'it verantwortliche',
      'edv leiter',
      'edv leiterin',
      'it abteilungsleiter',
    ],
  },
  {
    label: 'Systemadministrator',
    aliases: [
      'systemadministrator',
      'systemadministratorin',
      'sysadmin',
      'it administrator',
      'it administratorin',
      'netzwerkadministrator',
      'netzwerkadministratorin',
      'server administrator',
      'server administratorin',
      'windows administrator',
      'linux administrator',
      'infrastruktur administrator',
      'infrastruktur administratorin',
      'cloud administrator',
      'cloud administratorin',
      'it betrieb administrator',
      'it betrieb administratorin',
      'admin it',
      'administrator it',
      'administratorin it',
    ],
  },
  {
    label: 'Softwareentwickler',
    aliases: [
      'softwareentwickler',
      'softwareentwicklerin',
      'developer',
      'entwickler',
      'entwicklerin',
      'frontend entwickler',
      'frontend entwicklerin',
      'backend entwickler',
      'backend entwicklerin',
      'fullstack entwickler',
      'fullstack entwicklerin',
      'software engineer',
      'software engineerin',
      'programmer',
      'programmierer',
      'programmiererin',
      'webentwickler',
      'webentwicklerin',
      'app entwickler',
      'app entwicklerin',
    ],
  },
  {
    label: 'Produktmanager',
    aliases: [
      'produktmanager',
      'produktmanagerin',
      'product manager',
      'product owner',
      'produkt owner',
      'produktverantwortlicher',
      'produktverantwortliche',
      'produktstratege',
      'produktstrategin',
      'product lead',
      'product strategist',
      'product portfolio manager',
      'produktportfolio manager',
      'produktentwicklung manager',
      'produktentwicklung managerin',
      'produktplanung',
      'produktplaner',
      'produktplanerin',
      'roadmap owner',
      'produkt roadmap owner',
    ],
  },
  {
    label: 'Projektleiter',
    aliases: [
      'projektleiter',
      'projektleiterin',
      'project manager',
      'projektmanager',
      'projektmanagerin',
      'programmleiter',
      'programmleiterin',
      'program manager',
      'projektverantwortlicher',
      'projektverantwortliche',
      'pm',
      'projektkoordination leitung',
      'projektkoordinator leitung',
      'projektkoordinatorin leitung',
      'kundenprojektleiter',
      'kundenprojektleiterin',
      'technischer projektleiter',
      'technische projektleiterin',
      'implementierungsprojektleiter',
      'implementierungsprojektleiterin',
    ],
  },
  {
    label: 'Operations-Leiter',
    aliases: [
      'operations leiter',
      'operations leiterin',
      'operations manager leitung',
      'operative leitung',
      'betriebsorganisation leitung',
      'prozessleitung operations',
      'service operations leitung',
      'delivery operations leitung',
      'business operations manager',
      'operativer manager',
      'operative managerin',
      'operativer bereichsleiter',
      'operative bereichsleiterin',
      'operations verantwortlicher',
      'operations verantwortliche',
      'ablaufverantwortlicher',
      'ablaufverantwortliche',
      'operations abteilungsleiter',
      'operations abteilungsleiterin',
      'leitung operative prozesse',
      'leiter operative prozesse',
    ],
  },
  {
    label: 'Betriebsleiter',
    aliases: [
      'betriebsleiter',
      'betriebsleiterin',
      'leitung betrieb',
      'leiter betrieb',
      'leiterin betrieb',
      'plant manager',
      'werksnaher betriebsleiter',
      'werksnahe betriebsleiterin',
      'betrieb verantwortlich',
      'betrieb verantwortliche',
      'standortbetrieb leiter',
      'standortbetrieb leiterin',
      'operative betriebsführung',
      'operative betriebsfuehrung',
      'betriebsführung person',
      'betriebsfuehrung person',
      'technischer betriebsleiter',
      'technische betriebsleiterin',
      'betriebschef',
      'betriebschefin',
    ],
  },
  {
    label: 'Standortleiter',
    aliases: [
      'standortleiter',
      'standortleiterin',
      'leitung standort',
      'leiter standort',
      'leiterin standort',
      'site manager',
      'site lead',
      'niederlassungsleiter',
      'niederlassungsleiterin',
      'branch manager',
      'standortverantwortlicher',
      'standortverantwortliche',
      'regional standortleiter',
      'regionale standortleiterin',
      'lokaler standortleiter',
      'lokale standortleiterin',
      'facility standortleiter',
      'facility standortleiterin',
      'werksstandort leitung',
      'betriebsstandort leitung',
    ],
  },
  {
    label: 'Filialleiter',
    aliases: [
      'filialleiter',
      'filialleiterin',
      'store manager',
      'shop manager',
      'ladenleiter',
      'ladenleiterin',
      'marktleiter',
      'marktleiterin',
      'retail manager',
      'retail leitung',
      'standortleiter einzelhandel',
      'standortleiterin einzelhandel',
      'store lead',
      'filialverantwortlicher',
      'filialverantwortliche',
      'bereichsleiter filiale',
      'bereichsleiterin filiale',
      'verkaufsstellenleiter',
      'verkaufsstellenleiterin',
      'geschäftsstellenleiter retail',
    ],
  },
  {
    label: 'Teamleiter',
    aliases: [
      'teamleiter',
      'teamleiterin',
      'teamleitung',
      'team lead',
      'gruppenleiter',
      'gruppenleiterin',
      'schichtleiter',
      'schichtleiterin',
      'leiter team',
      'leiterin team',
      'supervisor',
      'vorgesetzter team',
      'vorgesetzte team',
      'operative führungskraft',
      'operative fuehrungskraft',
      'mittleres management team',
      'koordiniert team',
      'teamverantwortlicher',
      'teamverantwortliche',
      'people lead kleinteam',
    ],
  },
  {
    label: 'Abteilungsleiter',
    aliases: [
      'abteilungsleiter',
      'abteilungsleiterin',
      'abteilungsleitung',
      'department head',
      'head of department',
      'bereichsnaher abteilungsleiter',
      'bereichsnahe abteilungsleiterin',
      'leiter abteilung',
      'leiterin abteilung',
      'fachabteilungsleiter',
      'fachabteilungsleiterin',
      'abteilungsverantwortlicher',
      'abteilungsverantwortliche',
      'middle management department',
      'department manager',
      'bereichsabteilung leitung',
      'fachbereichsleitung',
      'funktionale abteilungsleitung',
      'linienstellenleiter',
      'linienstellenleiterin',
    ],
  },
  {
    label: 'Bereichsleiter',
    aliases: [
      'bereichsleiter',
      'bereichsleiterin',
      'bereichsleitung',
      'division head',
      'head of division',
      'business unit manager',
      'business unit lead',
      'leiter bereich',
      'leiterin bereich',
      'regionaler bereichsleiter',
      'regionale bereichsleiterin',
      'fachbereichsleiter',
      'fachbereichsleiterin',
      'spartenleiter',
      'spartenleiterin',
      'bereichsverantwortlicher',
      'bereichsverantwortliche',
      'senior manager bereich',
      'division manager',
      'unit manager',
    ],
  },
  {
    label: 'Assistent der Geschäftsführung',
    aliases: [
      'assistent der geschäftsführung',
      'assistent der geschaeftsfuehrung',
      'assistant to ceo',
      'geschäftsführungsassistent',
      'geschaeftsfuehrungsassistent',
      'executive assistant männlich',
      'executive assistant maennlich',
      'persönlicher assistent geschäftsführer',
      'persoenlicher assistent geschaeftsfuehrer',
      'assistenz vom chef männlich',
      'assistenz vom chef maennlich',
      'rechte hand des geschäftsführers',
      'rechte hand des geschaeftsfuehrers',
      'vorzimmer assistent',
      'management assistent',
      'ceo assistent',
      'gf assistent',
      'leiter assistenz geschäftsführung',
      'leiter assistenz geschaeftsfuehrung',
      'terminassistent geschäftsführung',
    ],
  },
  {
    label: 'Assistentin der Geschäftsführung',
    aliases: [
      'assistentin der geschäftsführung',
      'assistentin der geschaeftsfuehrung',
      'assistant to ceo weiblich',
      'geschäftsführungsassistentin',
      'geschaeftsfuehrungsassistentin',
      'executive assistant weiblich',
      'persönliche assistentin geschäftsführer',
      'persoenliche assistentin geschaeftsfuehrer',
      'assistenz vom chef weiblich',
      'rechte hand des geschäftsführers weiblich',
      'rechte hand des geschaeftsfuehrers weiblich',
      'vorzimmer assistentin',
      'management assistentin',
      'ceo assistentin',
      'gf assistentin',
      'leiterin assistenz geschäftsführung',
      'leiterin assistenz geschaeftsfuehrung',
      'terminassistentin geschäftsführung',
      'office assistentin chef',
      'chefsekretärin nah',
    ],
  },
  {
    label: 'Sekretärin',
    aliases: [
      'sekretärin',
      'sekretaerin',
      'chefsekretärin',
      'chefsekretaerin',
      'sekretariat',
      'vorzimmerdame',
      'bürosekretärin',
      'buerosekretaerin',
      'empfangssekretärin',
      'empfangssekretaerin',
      'telefonsekretärin',
      'telefonsekretaerin',
      'terminsekretärin',
      'terminsekretaerin',
      'sekretärin geschäftsführung',
      'sekretaerin geschaeftsfuehrung',
      'sekretärin inhaber',
      'sekretaerin inhaber',
      'büroassistenz sekretariat',
      'bueroassistenz sekretariat',
    ],
  },
  {
    label: 'Empfangsmitarbeiter',
    aliases: [
      'empfangsmitarbeiter',
      'empfangsmitarbeiterin',
      'rezeptionist',
      'rezeptionistin',
      'rezeption',
      'empfang',
      'front desk mitarbeiter',
      'front desk mitarbeiterin',
      'telefonzentrale mitarbeiter',
      'telefonzentrale mitarbeiterin',
      'welcome desk',
      'front office agent',
      'front office mitarbeiter',
      'front office mitarbeiterin',
      'reception agent',
      'receptionist',
      'kundenempfang',
      'besucherempfang',
      'empfangskraft',
      'empfangskraft büro',
    ],
  },
  {
    label: 'Wirtschaftsprüfer',
    aliases: [
      'wirtschaftsprüfer',
      'wirtschaftspruefer',
      'wirtschaftsprüferin',
      'wirtschaftsprueferin',
      'abschlussprüfer',
      'abschlusspruefer',
      'abschlussprüferin',
      'abschlussprueferin',
      'auditor',
      'auditorin',
      'externer prüfer',
      'externer pruefer',
      'externe prüferin',
      'externe prueferin',
      'prüfer jahresabschluss',
      'pruefer jahresabschluss',
      'prüferin jahresabschluss',
      'prueferin jahresabschluss',
      'audit partner',
      'audit manager',
    ],
  },
  {
    label: 'Ehefrau des Geschäftsführers',
    aliases: [
      'ehefrau des geschäftsführers',
      'ehefrau des geschaeftsfuehrers',
      'frau des geschäftsführers',
      'frau des geschaeftsfuehrers',
      'partnerin des geschäftsführers',
      'partnerin des geschaeftsfuehrers',
      'frau vom chef',
      'chefin zuhause',
      'mit seiner frau besprechen',
      'seine frau entscheidet mit',
      'ehepartnerin geschäftsführer',
      'ehepartnerin geschaeftsfuehrer',
      'frau hinter dem geschäftsführer',
      'frau hinter dem geschaeftsfuehrer',
      'private vertraute geschäftsführer',
      'private vertraute geschaeftsfuehrer',
      'frau als privater ratgeber',
      'frau als private ratgeberin',
      'familie geschäftsführer frau',
      'familie geschaeftsfuehrer frau',
    ],
  },
  {
    label: 'Ehemann der Geschäftsführerin',
    aliases: [
      'ehemann der geschäftsführerin',
      'ehemann der geschaeftsfuehrerin',
      'mann der geschäftsführerin',
      'mann der geschaeftsfuehrerin',
      'partner der geschäftsführerin',
      'partner der geschaeftsfuehrerin',
      'mann von der chefin',
      'mit ihrem mann besprechen',
      'ihr mann entscheidet mit',
      'ehepartner geschäftsführerin',
      'ehepartner geschaeftsfuehrerin',
      'mann hinter der geschäftsführerin',
      'mann hinter der geschaeftsfuehrerin',
      'privater vertrauter geschäftsführerin',
      'privater vertrauter geschaeftsfuehrerin',
      'mann als privater ratgeber',
      'familie geschäftsführerin mann',
      'familie geschaeftsfuehrerin mann',
      'ehemann der inhaberin',
      'partner der inhaberin',
    ],
  },
  {
    label: 'Ehefrau des Inhabers',
    aliases: [
      'ehefrau des inhabers',
      'frau des inhabers',
      'partnerin des inhabers',
      'frau vom inhaber',
      'inhaberfrau',
      'mitinhaber frau informell',
      'frau entscheidet beim inhaber mit',
      'mit seiner frau besprechen inhaber',
      'ehepartnerin inhaber',
      'private vertraute inhaber',
      'frau hinter dem inhaber',
      'frau als kaufbegleiterin',
      'familie inhaber frau',
      'zuhause mit frau klären',
      'zuhause mit frau klaeren',
      'frau prüft entscheidung',
      'frau prueft entscheidung',
      'ehefrau im familienbetrieb',
      'frau im familienunternehmen',
      'frau als vertraute des inhabers',
    ],
  },
  {
    label: 'Ehemann der Inhaberin',
    aliases: [
      'mann der inhaberin',
      'mann von der inhaberin',
      'inhaberin ehemann',
      'mitinhaber mann informell',
      'mann entscheidet bei inhaberin mit',
      'mit ihrem mann besprechen inhaberin',
      'ehepartner inhaberin',
      'privater vertrauter inhaberin',
      'mann hinter der inhaberin',
      'mann als kaufbegleiter',
      'familie inhaberin mann',
      'zuhause mit mann klären',
      'zuhause mit mann klaeren',
      'mann prüft entscheidung',
      'mann prueft entscheidung',
      'ehemann im familienbetrieb',
      'mann im familienunternehmen',
      'mann als vertrauter der inhaberin',
      'ehemann als privater ratgeber',
      'ehemann entscheidet privat mit',
      'mit ehemann besprechen',
      'ehemann prüft investition',
      'ehemann prueft investition',
    ],
  },
  {
    label: 'Sohn des Inhabers',
    aliases: [
      'sohn des inhabers',
      'unternehmersohn',
      'nachfolger sohn',
      'sohn im familienbetrieb',
      'sohn im familienunternehmen',
      'juniorchef sohn',
      'junior chef sohn',
      'sohn arbeitet mit',
      'sohn übernimmt bald',
      'sohn uebernimmt bald',
      'sohn als nachfolge',
      'nachfolgegeneration sohn',
      'sohn des geschäftsführers',
      'sohn des geschaeftsfuehrers',
      'sohn als mitarbeiter familie',
      'sohn berät inhaber',
      'sohn beraet inhaber',
      'sohn entscheidet mit',
      'sohn prüft mit',
      'sohn prueft mit',
    ],
  },
  {
    label: 'Tochter des Inhabers',
    aliases: [
      'tochter des inhabers',
      'unternehmertochter',
      'nachfolgerin tochter',
      'tochter im familienbetrieb',
      'tochter im familienunternehmen',
      'juniorchefin tochter',
      'junior chefin tochter',
      'tochter arbeitet mit',
      'tochter übernimmt bald',
      'tochter uebernimmt bald',
      'tochter als nachfolge',
      'nachfolgegeneration tochter',
      'tochter des geschäftsführers',
      'tochter des geschaeftsfuehrers',
      'tochter als mitarbeiterin familie',
      'tochter berät inhaber',
      'tochter beraet inhaber',
      'tochter entscheidet mit',
      'tochter prüft mit',
      'tochter prueft mit',
    ],
  },
  {
    label: 'Vater des Inhabers',
    aliases: [
      'vater des inhabers',
      'seniorchef vater',
      'firmengründer vater',
      'firmengruender vater',
      'vater im familienbetrieb',
      'vater im familienunternehmen',
      'alter inhaber vater',
      'ehemaliger chef vater',
      'vater als seniorberater',
      'vater redet mit',
      'vater entscheidet mit',
      'vater prüft entscheidung',
      'vater prueft entscheidung',
      'vater des geschäftsführers',
      'vater des geschaeftsfuehrers',
      'patriarch im betrieb',
      'senior generation vater',
      'vater als eigentümer senior',
      'vater als eigentuemer senior',
      'vater im hintergrund',
    ],
  },
  {
    label: 'Mutter der Inhaberin',
    aliases: [
      'mutter der inhaberin',
      'seniorchefin mutter',
      'firmengründerin mutter',
      'firmengruenderin mutter',
      'mutter im familienbetrieb',
      'mutter im familienunternehmen',
      'alte inhaberin mutter',
      'ehemalige chefin mutter',
      'mutter als seniorberaterin',
      'mutter redet mit',
      'mutter entscheidet mit',
      'mutter prüft entscheidung',
      'mutter prueft entscheidung',
      'mutter der geschäftsführerin',
      'mutter der geschaeftsfuehrerin',
      'matriarchin im betrieb',
      'senior generation mutter',
      'mutter als eigentümerin senior',
      'mutter als eigentuemerin senior',
      'mutter im hintergrund',
    ],
  },
  {
    label: 'Praxisinhaber',
    aliases: [
      'praxisinhaber',
      'praxisinhaberin',
      'inhaber arztpraxis',
      'inhaberin arztpraxis',
      'arzt als praxisinhaber',
      'ärztin als praxisinhaberin',
      'aerztin als praxisinhaberin',
      'zahnarzt praxisinhaber',
      'zahnärztin praxisinhaberin',
      'zahnaerztin praxisinhaberin',
      'therapeut praxisinhaber',
      'therapeutin praxisinhaberin',
      'praxisbetreiber',
      'praxisbetreiberin',
      'medizinischer unternehmer',
      'medizinische unternehmerin',
      'niedergelassener arzt',
      'niedergelassene ärztin',
      'niedergelassene aerztin',
      'praxischef',
    ],
  },
  {
    label: 'Praxismanager',
    aliases: [
      'praxismanager',
      'praxismanagerin',
      'praxismanagement',
      'leitung praxismanagement',
      'organisationsleitung praxis',
      'praxisleitung operativ',
      'mfa leitung',
      'leitende mfa',
      'arztpraxis manager',
      'arztpraxis managerin',
      'zahnarztpraxis manager',
      'zahnarztpraxis managerin',
      'terminmanagement praxis',
      'verwaltung praxis leitung',
      'abrechnungsmanager praxis',
      'abrechnungsmanagerin praxis',
      'patientenmanagement leitung',
      'praxiskoordination',
      'praxiskoordinatorin',
      'sprechstundenmanagement leitung',
    ],
  },
  {
    label: 'Arzt',
    aliases: [
      'arzt',
      'ärztin',
      'aerztin',
      'facharzt',
      'fachärztin',
      'fachaerztin',
      'klinikarzt',
      'klinikärztin',
      'klinikaerztin',
      'chefarzt',
      'chefärztin',
      'chefaerztin',
      'oberarzt',
      'oberärztin',
      'oberaerztin',
      'behandelnder arzt',
      'behandelnde ärztin',
      'arzt als entscheider',
      'ärztin als entscheiderin',
      'aerztin als entscheiderin',
      'medizinischer leistungserbringer',
      'medizinische leistungserbringerin',
    ],
  },
  {
    label: 'Pflegedienstleiter',
    aliases: [
      'pflegedienstleiter',
      'pflegedienstleiterin',
      'pdl',
      'leitung pflegedienst',
      'pflegedienstleitung',
      'ambulanter pflegedienst leitung',
      'stationäre pflegedienstleitung',
      'stationaere pflegedienstleitung',
      'pflegeleitung',
      'leiter pflege',
      'leiterin pflege',
      'pflege manager',
      'pflege managerin',
      'care manager pflege',
      'verantwortliche pflegefachkraft',
      'verantwortlicher pflegefachkraft',
      'pflegekoordination leitung',
      'pflegekoordinatorin leitung',
      'pflegedienst chef',
      'pflegedienst chefin',
    ],
  },
  {
    label: 'Einrichtungsleiter Pflege',
    aliases: [
      'einrichtungsleiter pflege',
      'einrichtungsleiterin pflege',
      'heimleiter',
      'heimleiterin',
      'leitung pflegeheim',
      'leiter altenheim',
      'leiterin altenheim',
      'seniorenheim leitung',
      'stationäre einrichtung leitung',
      'stationaere einrichtung leitung',
      'pflegeeinrichtung leitung',
      'residenzleitung pflege',
      'hausleitung pflege',
      'betriebsleitung pflegeheim',
      'einrichtungsmanagement pflege',
      'pflegeheim manager',
      'pflegeheim managerin',
      'träger einrichtungsleitung',
      'traeger einrichtungsleitung',
      'heimmanagement',
    ],
  },
  {
    label: 'Apothekeninhaber',
    aliases: [
      'apothekeninhaber',
      'apothekeninhaberin',
      'inhaber apotheke',
      'inhaberin apotheke',
      'apotheker als inhaber',
      'apothekerin als inhaberin',
      'pharmazeutischer inhaber',
      'pharmazeutische inhaberin',
      'apothekenleiter inhaber',
      'apothekenleiterin inhaberin',
      'offizin inhaber',
      'offizin inhaberin',
      'filialapotheke inhaber',
      'filialapotheke inhaberin',
      'hauptapotheke inhaber',
      'hauptapotheke inhaberin',
      'apothekenunternehmer',
      'apothekenunternehmerin',
      'apothekenchef',
      'apothekenchefin',
    ],
  },
  {
    label: 'Apothekenleiter',
    aliases: [
      'apothekenleiter',
      'apothekenleiterin',
      'leitung apotheke',
      'leiter apotheke',
      'leiterin apotheke',
      'filialleiter apotheke',
      'filialleiterin apotheke',
      'verantwortlicher apotheker',
      'verantwortliche apothekerin',
      'pharmazeutische leitung',
      'pharmazeutischer leiter',
      'pharmazeutische leiterin',
      'offizin leitung',
      'apothekenmanagement',
      'apotheke manager',
      'apotheke managerin',
      'standortleitung apotheke',
      'betriebsleitung apotheke',
      'leitender apotheker',
      'leitende apothekerin',
    ],
  },
  {
    label: 'Hotelinhaber',
    aliases: [
      'hotelinhaber',
      'hotelinhaberin',
      'inhaber hotel',
      'inhaberin hotel',
      'hotelier',
      'hotelière',
      'hoteliere',
      'hotelunternehmer',
      'hotelunternehmerin',
      'gastgeber als inhaber',
      'gastgeberin als inhaberin',
      'familienhotel inhaber',
      'familienhotel inhaberin',
      'hotelbesitzer',
      'hotelbesitzerin',
      'privathotel inhaber',
      'privathotel inhaberin',
      'boutique hotel inhaber',
      'boutique hotel inhaberin',
      'hotel owner',
    ],
  },
  {
    label: 'Hoteldirektor',
    aliases: [
      'hoteldirektor',
      'hoteldirektorin',
      'hotel director',
      'general manager hotel',
      'gm hotel',
      'leitung hotel',
      'hotelmanager',
      'hotelmanagerin',
      'hotel management',
      'betriebsleiter hotel',
      'betriebsleiterin hotel',
      'resort manager',
      'resort managerin',
      'direktor hospitality',
      'direktorin hospitality',
      'hausdirektor hotel',
      'hausdirektorin hotel',
      'standortleiter hotel',
      'standortleiterin hotel',
      'hotel geschäftsleitung',
    ],
  },
  {
    label: 'Restaurantinhaber',
    aliases: [
      'restaurantinhaber',
      'restaurantinhaberin',
      'inhaber restaurant',
      'inhaberin restaurant',
      'gastronom',
      'gastronomin',
      'restaurant owner',
      'gastwirt',
      'gastwirtin',
      'wirt',
      'wirtin',
      'lokalinhaber',
      'lokalinhaberin',
      'café inhaber',
      'cafe inhaber',
      'café inhaberin',
      'cafe inhaberin',
      'barinhaber',
      'barinhaberin',
      'gastronomieunternehmer',
    ],
  },
  {
    label: 'Restaurantleiter',
    aliases: [
      'restaurantleiter',
      'restaurantleiterin',
      'restaurant manager',
      'leitung restaurant',
      'leiter restaurant',
      'leiterin restaurant',
      'service manager restaurant',
      'serviceleitung restaurant',
      'f&b manager restaurant',
      'f and b manager restaurant',
      'betriebsleiter restaurant',
      'betriebsleiterin restaurant',
      'schichtleiter restaurant',
      'schichtleiterin restaurant',
      'store manager gastro',
      'gastroleitung',
      'restaurantverantwortlicher',
      'restaurantverantwortliche',
      'floor manager restaurant',
      'dining manager',
    ],
  },
  {
    label: 'Handwerksmeister',
    aliases: [
      'handwerksmeister',
      'handwerksmeisterin',
      'meister',
      'meisterin',
      'meisterbetrieb inhaber',
      'meisterbetrieb inhaberin',
      'elektromeister',
      'elektromeisterin',
      'shk meister',
      'shk meisterin',
      'malermeister',
      'malermeisterin',
      'dachdeckermeister',
      'dachdeckermeisterin',
      'tischlermeister',
      'tischlermeisterin',
      'kfz meister',
      'kfz meisterin',
      'metallbaumeister',
      'metallbaumeisterin',
    ],
  },
  {
    label: 'Bauunternehmer',
    aliases: [
      'bauunternehmer',
      'bauunternehmerin',
      'inhaber bauunternehmen',
      'inhaberin bauunternehmen',
      'geschäftsführer bauunternehmen',
      'geschaeftsfuehrer bauunternehmen',
      'baugeschäft inhaber',
      'baugeschaeft inhaber',
      'baugeschäft inhaberin',
      'baugeschaeft inhaberin',
      'bauunternehmung chef',
      'bauunternehmung chefin',
      'hochbau unternehmer',
      'tiefbau unternehmer',
      'tiefbauunternehmer',
      'bau contractor owner',
      'bau firma inhaber',
      'bau firma inhaberin',
      'baubetrieb inhaber',
      'baubetrieb inhaberin',
    ],
  },
  {
    label: 'Bauleiter',
    aliases: [
      'bauleiter',
      'bauleiterin',
      'construction manager',
      'site manager bau',
      'baustellenleiter',
      'baustellenleiterin',
      'oberbauleiter',
      'oberbauleiterin',
      'bauoberleitung',
      'projektbauleiter',
      'projektbauleiterin',
      'bauprojektleiter',
      'bauprojektleiterin',
      'leiter baustelle',
      'leiterin baustelle',
      'verantwortlicher baustelle',
      'verantwortliche baustelle',
      'baukoordination leitung',
      'baukoordinator',
      'baukoordinatorin',
    ],
  },
  {
    label: 'Architekt',
    aliases: [
      'architekt',
      'architektin',
      'büroinhaber architektur',
      'bueroinhaber architektur',
      'architekturbüro inhaber',
      'architekturbuero inhaber',
      'geschäftsführer architekturbüro',
      'geschaeftsfuehrer architekturbuero',
      'planender architekt',
      'planende architektin',
      'projektarchitekt',
      'projektarchitektin',
      'leitender architekt',
      'leitende architektin',
      'entwurfsarchitekt',
      'entwurfsarchitektin',
      'bauvorlageberechtigter architekt',
      'bauvorlageberechtigte architektin',
      'architektur partner',
      'architektur partnerin',
    ],
  },
  {
    label: 'Ingenieurbüro-Inhaber',
    aliases: [
      'ingenieurbüro inhaber',
      'ingenieurbuero inhaber',
      'ingenieurbüro inhaberin',
      'ingenieurbuero inhaberin',
      'inhaber ingenieurbüro',
      'inhaber ingenieurbuero',
      'inhaberin ingenieurbüro',
      'inhaberin ingenieurbuero',
      'geschäftsführer ingenieurbüro',
      'geschaeftsfuehrer ingenieurbuero',
      'beratender ingenieur inhaber',
      'beratende ingenieurin inhaberin',
      'planungsbüro inhaber',
      'planungsbuero inhaber',
      'planungsbüro inhaberin',
      'planungsbuero inhaberin',
      'ingenieurunternehmer',
      'ingenieurunternehmerin',
      'technisches büro inhaber',
      'technisches buero inhaber',
    ],
  },
  {
    label: 'Projektentwickler Immobilien',
    aliases: [
      'projektentwickler immobilien',
      'projektentwicklerin immobilien',
      'immobilienprojektentwickler',
      'immobilienprojektentwicklerin',
      'developer immobilien',
      'real estate developer',
      'bauprojektentwickler',
      'bauprojektentwicklerin',
      'quartiersentwickler',
      'quartiersentwicklerin',
      'entwickler wohnbau',
      'entwicklerin wohnbau',
      'gewerbeimmobilien entwickler',
      'gewerbeimmobilien entwicklerin',
      'projektträger immobilien',
      'projekttraeger immobilien',
      'immobilien development manager',
      'property developer',
      'developer bauvorhaben',
      'entwicklungsleiter immobilien',
    ],
  },
  {
    label: 'Hausverwalter',
    aliases: [
      'hausverwalter',
      'hausverwalterin',
      'hausverwaltung',
      'weg verwalter',
      'weg verwalterin',
      'immobilienverwalter',
      'immobilienverwalterin',
      'property manager',
      'property managerin',
      'objektverwalter',
      'objektverwalterin',
      'mietverwalter',
      'mietverwalterin',
      'verwaltung wohnanlage',
      'verwaltung gewerbeimmobilie',
      'verwalter eigentümergemeinschaft',
      'verwalter eigentuemergemeinschaft',
      'weg verwaltung leitung',
      'property administration manager',
      'immobilienverwaltung leitung',
    ],
  },
  {
    label: 'Facility-Manager',
    aliases: [
      'facility manager',
      'facility managerin',
      'gebäudemanager',
      'gebaeudemanager',
      'gebäudemanagerin',
      'gebaeudemanagerin',
      'technisches facility management',
      'infrastrukturelles facility management',
      'objektmanager facility',
      'objektmanagerin facility',
      'haus technik leitung',
      'gebäudebetrieb leitung',
      'gebaeudebetrieb leitung',
      'fm manager',
      'fm managerin',
      'liegenschaftsmanager',
      'liegenschaftsmanagerin',
      'standort facility manager',
      'standort facility managerin',
      'gebäudeverantwortlicher',
    ],
  },
  {
    label: 'Produktionsleiter',
    aliases: [
      'produktionsleiter',
      'produktionsleiterin',
      'leitung produktion',
      'leiter produktion',
      'leiterin produktion',
      'head of production',
      'production manager',
      'fertigungsleiter',
      'fertigungsleiterin',
      'manufacturing manager',
      'werksproduktion leitung',
      'schichtübergreifende produktionsleitung',
      'schichtuebergreifende produktionsleitung',
      'produktionsverantwortlicher',
      'produktionsverantwortliche',
      'industrie produktionsleiter',
      'industrie produktionsleiterin',
      'produktion chef',
      'produktion chefin',
      'manufacturing lead',
    ],
  },
  {
    label: 'Werkleiter',
    aliases: [
      'werkleiter',
      'werkleiterin',
      'werksleiter',
      'werksleiterin',
      'leitung werk',
      'leiter werk',
      'leiterin werk',
      'factory manager',
      'fabrikleiter',
      'fabrikleiterin',
      'standort werk leitung',
      'werksverantwortlicher',
      'werksverantwortliche',
      'industriewerk leitung',
      'produktionswerk leitung',
      'plant director',
      'werkdirektor',
      'werkdirektorin',
      'betriebsstätte leitung',
      'leiter industriewerk',
      'leiterin industriewerk',
      'werkverantwortlicher',
      'werkverantwortliche',
    ],
  },
  {
    label: 'Qualitätsleiter',
    aliases: [
      'qualitätsleiter',
      'qualitaetsleiter',
      'qualitätsleiterin',
      'qualitaetsleiterin',
      'leitung qualität',
      'leitung qualitaet',
      'head of quality',
      'quality manager',
      'qm leiter',
      'qm leiterin',
      'qualitätsmanagement leitung',
      'qualitaetsmanagement leitung',
      'qa manager',
      'quality assurance manager',
      'qualitätsverantwortlicher',
      'qualitaetsverantwortlicher',
      'qualitätsverantwortliche',
      'qualitaetsverantwortliche',
      'qualitätschef',
      'qualitaetschef',
    ],
  },
  {
    label: 'Logistikleiter',
    aliases: [
      'logistikleiter',
      'logistikleiterin',
      'leitung logistik',
      'leiter logistik',
      'leiterin logistik',
      'head of logistics',
      'logistics manager',
      'supply chain logistics lead',
      'lagerlogistik leitung',
      'versandleiter',
      'versandleiterin',
      'transportleiter',
      'transportleiterin',
      'distributionsleiter',
      'distributionsleiterin',
      'logistikverantwortlicher',
      'logistikverantwortliche',
      'warehouse logistics manager',
      'fulfillment leitung',
      'logistikchef',
    ],
  },
  {
    label: 'Supply-Chain-Manager',
    aliases: [
      'supply chain manager',
      'supply-chain-manager',
      'scm manager',
      'supply chain leitung',
      'leiter supply chain',
      'leiterin supply chain',
      'head of supply chain',
      'lieferkettenmanager',
      'lieferkettenmanagerin',
      'demand planning manager',
      'supply planning manager',
      'procurement supply chain manager',
      'operations supply manager',
      'chain operations manager',
      'supply chain director',
      'materialfluss manager',
      'materialfluss managerin',
      'supply chain verantwortlicher',
      'supply chain verantwortliche',
      'lieferkettenverantwortlicher',
    ],
  },
  {
    label: 'Kundenserviceleiter',
    aliases: [
      'kundenserviceleiter',
      'kundenserviceleiterin',
      'leitung kundenservice',
      'leiter kundenservice',
      'leiterin kundenservice',
      'customer service manager',
      'head of customer service',
      'supportleiter',
      'supportleiterin',
      'support leitung',
      'service desk manager',
      'customer support manager',
      'client service manager',
      'kundenbetreuung leitung',
      'kundenbetreuung leiter',
      'kundenbetreuung leiterin',
      'servicecenter leitung',
      'contact center manager',
      'callcenter leiter',
      'callcenter leiterin',
    ],
  },
  {
    label: 'Customer-Success-Manager',
    aliases: [
      'customer success manager',
      'customer-success-manager',
      'csm',
      'client success manager',
      'success manager',
      'kundenentwicklungsmanager',
      'kundenentwicklungsmanagerin',
      'bestandskunden success manager',
      'saas customer success',
      'onboarding success manager',
      'implementation success manager',
      'kundenbindung manager',
      'kundenbindung managerin',
      'retention manager',
      'client partner success',
      'customer value manager',
      'adoption manager',
      'kundenadoption manager',
      'success betreuer',
      'success betreuerin',
    ],
  },
  {
    label: 'Datenschutzbeauftragter',
    aliases: [
      'datenschutzbeauftragter',
      'datenschutzbeauftragte',
      'dpo',
      'data protection officer',
      'externer datenschutzbeauftragter',
      'externe datenschutzbeauftragte',
      'interner datenschutzbeauftragter',
      'interne datenschutzbeauftragte',
      'datenschutz officer',
      'privacy officer',
      'datenschutzverantwortlicher',
      'datenschutzverantwortliche',
      'dsb',
      'gdpr officer',
      'dsgvo beauftragter',
      'dsgvo beauftragte',
      'privacy manager',
      'privacy managerin',
      'datenschutzmanager',
      'datenschutzmanagerin',
    ],
  },
  {
    label: 'Compliance-Manager',
    aliases: [
      'compliance manager',
      'compliance managerin',
      'compliance officer',
      'compliance beauftragter',
      'compliance beauftragte',
      'regulatory compliance manager',
      'risikomanager compliance',
      'risikomanagerin compliance',
      'ethics compliance officer',
      'legal compliance manager',
      'konformitätsmanager',
      'konformitaetsmanager',
      'konformitätsmanagerin',
      'konformitaetsmanagerin',
      'compliance verantwortlicher',
      'compliance verantwortliche',
      'regulatorik manager',
      'regulatorik managerin',
      'governance compliance manager',
      'compliance leitung',
    ],
  },
  {
    label: 'Jurist im Unternehmen',
    aliases: [
      'unternehmensjurist',
      'unternehmensjuristin',
      'inhouse counsel',
      'legal counsel',
      'syndikusrechtsanwalt',
      'syndikusrechtsanwältin',
      'syndikusrechtsanwaeltin',
      'rechtsabteilung jurist',
      'rechtsabteilung juristin',
      'legal manager',
      'vertragsjurist',
      'vertragsjuristin',
      'wirtschaftsjurist',
      'wirtschaftsjuristin',
      'corporate counsel',
      'legal advisor intern',
      'interner anwalt',
      'interne anwältin',
      'interne anwaeltin',
      'jurist unternehmen',
    ],
  },
  {
    label: 'Schulleiter',
    aliases: [
      'schulleiter',
      'schulleiterin',
      'rektor',
      'rektorin',
      'schuldirektor',
      'schuldirektorin',
      'leitung schule',
      'leiter schule',
      'leiterin schule',
      'principal school',
      'headmaster',
      'headmistress',
      'schulverwaltung leitung',
      'pädagogische leitung schule',
      'paedagogische leitung schule',
      'gesamtschulleiter',
      'gesamtschulleiterin',
      'grundschulleiter',
      'grundschulleiterin',
      'berufsschulleiter',
    ],
  },
  {
    label: 'Hochschulprofessor',
    aliases: [
      'hochschulprofessor',
      'hochschulprofessorin',
      'professor',
      'professorin',
      'uni professor',
      'uni professorin',
      'fh professor',
      'fh professorin',
      'lehrstuhlinhaber',
      'lehrstuhlinhaberin',
      'chair professor',
      'wissenschaftlicher professor',
      'wissenschaftliche professorin',
      'forschungsprofessor',
      'forschungsprofessorin',
      'institutsprofessor',
      'institutsprofessorin',
      'akademischer lehrstuhl',
      'akademische lehrstuhlperson',
      'professoraler entscheider',
    ],
  },
  {
    label: 'Dekan',
    aliases: [
      'dekan',
      'dekanin',
      'dean',
      'fachbereichsdekan',
      'fachbereichsdekanin',
      'fakultätsdekan',
      'fakultaetsdekan',
      'fakultätsdekanin',
      'fakultaetsdekanin',
      'leitung fakultät',
      'leitung fakultaet',
      'fakultätsleitung',
      'fakultaetsleitung',
      'academic dean',
      'hochschulbereich leitung',
      'studienbereich dekan',
      'studienbereich dekanin',
      'dekanat leitung',
      'dekanatsleitung',
      'universitätsdekan',
    ],
  },
  {
    label: 'Bürgermeister',
    aliases: [
      'bürgermeister',
      'buergermeister',
      'bürgermeisterin',
      'buergermeisterin',
      'oberbürgermeister',
      'oberbuergermeister',
      'oberbürgermeisterin',
      'oberbuergermeisterin',
      'gemeindechef',
      'gemeindechefin',
      'stadtoberhaupt',
      'kommunalchef',
      'kommunalchefin',
      'verwaltungschef kommune',
      'verwaltungschefin kommune',
      'kommunalpolitische leitung',
      'hauptverwaltungsbeamter',
      'hauptverwaltungsbeamtin',
      'rathauschef',
      'rathauschefin',
    ],
  },
  {
    label: 'Amtsleiter',
    aliases: [
      'amtsleiter',
      'amtsleiterin',
      'leitung amt',
      'leiter amt',
      'leiterin amt',
      'fachamtsleiter',
      'fachamtsleiterin',
      'behördenleiter',
      'behoerdenleiter',
      'behördenleiterin',
      'behoerdenleiterin',
      'verwaltungsamtsleiter',
      'verwaltungsamtsleiterin',
      'stadtamtsleiter',
      'stadtamtsleiterin',
      'kommunaler amtsleiter',
      'kommunale amtsleiterin',
      'leiter fachamt',
      'leiterin fachamt',
      'amtsführung',
    ],
  },
  {
    label: 'Vergabestellenleiter',
    aliases: [
      'vergabestellenleiter',
      'vergabestellenleiterin',
      'leitung vergabestelle',
      'leiter vergabestelle',
      'leiterin vergabestelle',
      'zentrale vergabestelle leitung',
      'beschaffungsstelle leitung',
      'leiter beschaffungsstelle',
      'leiterin beschaffungsstelle',
      'ausschreibungsstelle leitung',
      'vergabemanager leitung',
      'vergabemanagerin leitung',
      'public procurement lead',
      'kommunale vergabestelle',
      'vergabeamt leitung',
      'leiter vergabeamt',
      'leiterin vergabeamt',
      'öffentliche beschaffung leitung',
      'oeffentliche beschaffung leitung',
      'vergaberechtliche stelle leitung',
    ],
  },
  {
    label: 'Sachbearbeiter Verwaltung',
    aliases: [
      'sachbearbeiter verwaltung',
      'sachbearbeiterin verwaltung',
      'verwaltungssachbearbeiter',
      'verwaltungssachbearbeiterin',
      'kommunaler sachbearbeiter',
      'kommunale sachbearbeiterin',
      'behördensachbearbeiter',
      'behoerdensachbearbeiter',
      'behördensachbearbeiterin',
      'behoerdensachbearbeiterin',
      'amt sachbearbeiter',
      'amt sachbearbeiterin',
      'fallbearbeiter verwaltung',
      'fallbearbeiterin verwaltung',
      'öffentlicher sachbearbeiter',
      'oeffentlicher sachbearbeiter',
      'öffentliche sachbearbeiterin',
      'oeffentliche sachbearbeiterin',
      'verwaltungsfachangestellter',
      'verwaltungsfachangestellte',
    ],
  },
  {
    label: 'Privatkunde',
    aliases: [
      'privatkunde',
      'privatkundin',
      'endkunde',
      'endkundin',
      'verbraucher',
      'verbraucherin',
      'konsument',
      'konsumentin',
      'consumer',
      'b2c kunde',
      'b2c kundin',
      'kunde privat',
      'kundin privat',
      'privatperson',
      'einzelperson privat',
      'selbstzahler',
      'selbstzahlerin',
      'auftraggeber privat',
      'auftraggeberin privat',
      'käufer privat',
    ],
  },
  {
    label: 'Patient',
    aliases: [
      'patient',
      'patientin',
      'medizinischer kunde',
      'medizinische kundin',
      'gesundheitskunde',
      'gesundheitskundin',
      'behandlungsinteressent',
      'behandlungsinteressentin',
      'therapiepatient',
      'therapiepatientin',
      'pflegepatient',
      'pflegepatientin',
      'selbstzahler patient',
      'selbstzahlerin patientin',
      'klient gesundheit',
      'klientin gesundheit',
      'ratsuchender patient',
      'ratsuchende patientin',
      'patient als entscheider',
      'patientin als entscheiderin',
    ],
  },
  {
    label: 'Angehöriger',
    aliases: [
      'angehöriger eines patienten',
      'angehoeriger eines patienten',
      'angehörige eines patienten',
      'angehoerige eines patienten',
      'familie des patienten',
      'familie der patientin',
      'sohn des patienten',
      'tochter der patientin',
      'ehefrau des patienten',
      'ehemann der patientin',
      'gesetzlicher betreuer patient',
      'gesetzliche betreuerin patientin',
      'pflegebevollmächtigter',
      'pflegebevollmaechtigter',
      'pflegebevollmächtigte',
      'pflegebevollmaechtigte',
      'entscheidet für patienten',
      'entscheidet fuer patienten',
      'medizinischer angehöriger',
      'medizinische angehörige',
    ],
  },
  {
    label: 'Gesetzlicher Betreuer',
    aliases: [
      'gesetzlicher betreuer',
      'gesetzliche betreuerin',
      'rechtlicher betreuer',
      'rechtliche betreuerin',
      'betreuungsbevollmächtigter',
      'betreuungsbevollmaechtigter',
      'betreuungsbevollmächtigte',
      'betreuungsbevollmaechtigte',
      'vormund',
      'vormundin',
      'betreuungsgerichtlicher vertreter',
      'betreuungsgerichtliche vertreterin',
      'bevollmächtigter betreuung',
      'bevollmaechtigter betreuung',
      'bevollmächtigte betreuung',
      'bevollmaechtigte betreuung',
      'patientenvertreter gesetzlich',
      'patientenvertreterin gesetzlich',
      'pflegeentscheidung betreuer',
      'pflegeentscheidung betreuerin',
    ],
  },
  {
    label: 'Vereinsvorsitzender',
    aliases: [
      'vereinsvorsitzender',
      'vereinsvorsitzende',
      '1 vorsitzender verein',
      'erste vorsitzende verein',
      'vereinschef',
      'vereinschefin',
      'vorstand verein',
      'vereinsvorstand',
      'leitung verein',
      'leiter verein',
      'leiterin verein',
      'vorsitz im verein',
      'ehrenamtlicher vorstand',
      'ehrenamtliche vorständin',
      'ehrenamtliche vorstaendin',
      'sportverein vorsitzender',
      'sportverein vorsitzende',
      'kulturverein vorsitzender',
      'kulturverein vorsitzende',
      'vereinsleitung',
    ],
  },
  {
    label: 'Verbandsfunktionär',
    aliases: [
      'verbandsfunktionär',
      'verbandsfunktionaer',
      'verbandsfunktionärin',
      'verbandsfunktionaerin',
      'verbandsvorstand',
      'vorstand verband',
      'geschäftsführer verband',
      'geschaeftsfuehrer verband',
      'geschäftsführerin verband',
      'geschaeftsfuehrerin verband',
      'verbandsleitung',
      'leiter verband',
      'leiterin verband',
      'funktionär verband',
      'funktionaer verband',
      'funktionärin verband',
      'funktionaerin verband',
      'kammerfunktionär',
      'kammerfunktionaer',
      'kammerfunktionärin',
    ],
  },
  {
    label: 'Franchisegeber',
    aliases: [
      'franchisegeber',
      'franchisegeberin',
      'franchisor',
      'master franchisegeber',
      'master franchisegeberin',
      'franchise zentrale',
      'franchise systemgeber',
      'franchise systemgeberin',
      'systemzentrale franchise',
      'franchise head office',
      'franchise owner system',
      'franchise markengeber',
      'franchise markengeberin',
      'geber franchise lizenz',
      'geberin franchise lizenz',
      'franchise expansion lead',
      'franchise development lead',
      'franchise network owner',
      'franchise systeminhaber',
      'franchise systeminhaberin',
    ],
  },
  {
    label: 'Franchisenehmer',
    aliases: [
      'franchisenehmer',
      'franchisenehmerin',
      'franchise partner',
      'franchise partnerin',
      'franchise operator',
      'franchise operatorin',
      'franchise standortinhaber',
      'franchise standortinhaberin',
      'lokaler franchisenehmer',
      'lokale franchisenehmerin',
      'franchise filialbetreiber',
      'franchise filialbetreiberin',
      'lizenznehmer franchise',
      'lizenznehmerin franchise',
      'systemgastronomie franchise partner',
      'retail franchise partner',
      'franchise unternehmer',
      'franchise unternehmerin',
      'franchise shop owner',
      'franchise store owner',
    ],
  },
  {
    label: 'Agenturinhaber',
    aliases: [
      'agenturinhaber',
      'agenturinhaberin',
      'inhaber agentur',
      'inhaberin agentur',
      'agenturchef',
      'agenturchefin',
      'marketingagentur inhaber',
      'marketingagentur inhaberin',
      'werbeagentur inhaber',
      'werbeagentur inhaberin',
      'performance agentur inhaber',
      'performance agentur inhaberin',
      'digitalagentur inhaber',
      'digitalagentur inhaberin',
      'kreativagentur inhaber',
      'kreativagentur inhaberin',
      'agenturgründer',
      'agenturgruender',
      'agenturgründerin',
      'agenturgruenderin',
    ],
  },
  {
    label: 'Kanzleiinhaber',
    aliases: [
      'kanzleiinhaber',
      'kanzleiinhaberin',
      'rechtsanwalt kanzleiinhaber',
      'rechtsanwältin kanzleiinhaberin',
      'rechtsanwaeltin kanzleiinhaberin',
      'steuerberater kanzleiinhaber',
      'steuerberaterin kanzleiinhaberin',
      'partner kanzlei',
      'partnerin kanzlei',
      'sozius kanzlei',
      'sozia kanzlei',
      'kanzleipartner',
      'kanzleipartnerin',
      'anwalt als inhaber',
      'anwältin als inhaberin',
      'anwaeltin als inhaberin',
      'steuerkanzlei inhaber',
      'steuerkanzlei inhaberin',
      'wirtschaftskanzlei partner',
      'wirtschaftskanzlei partnerin',
    ],
  },
  {
    label: 'Versicherungsmakler',
    aliases: [
      'versicherungsmakler',
      'versicherungsmaklerin',
      'makler inhaber versicherung',
      'maklerin inhaberin versicherung',
      'finanzmakler',
      'finanzmaklerin',
      'versicherungsberater',
      'versicherungsberaterin',
      'unabhängiger makler',
      'unabhaengiger makler',
      'unabhängige maklerin',
      'unabhaengige maklerin',
      'agenturinhaber versicherung',
      'agenturinhaberin versicherung',
      'allianz agenturinhaber',
      'signal iduna agenturinhaber',
      'versicherungsagentur inhaber',
      'versicherungsagentur inhaberin',
      'maklerbüro inhaber',
      'maklerbuero inhaber',
    ],
  },
  {
    label: 'Bankberater',
    aliases: [
      'bankberater',
      'bankberaterin',
      'kundenberater bank',
      'kundenberaterin bank',
      'firmenkundenberater bank',
      'firmenkundenberaterin bank',
      'private banker',
      'private bankerin',
      'bank account manager',
      'finanzberater bank',
      'finanzberaterin bank',
      'berater hausbank',
      'beraterin hausbank',
      'sparkassenberater',
      'sparkassenberaterin',
      'volksbank berater',
      'volksbank beraterin',
      'bank betreuer unternehmen',
      'bank betreuerin unternehmen',
      'relationship manager bank',
    ],
  },
  {
    label: 'Vermögensberater',
    aliases: [
      'vermögensberater',
      'vermoegensberater',
      'vermögensberaterin',
      'vermoegensberaterin',
      'wealth advisor',
      'wealth manager',
      'private wealth manager',
      'finanzberater privat',
      'finanzberaterin privat',
      'anlageberater',
      'anlageberaterin',
      'investment advisor',
      'investment berater',
      'investment beraterin',
      'honorarberater finanzen',
      'honorarberaterin finanzen',
      'asset advisor',
      'portfolio berater',
      'portfolio beraterin',
      'finanzplaner privat',
    ],
  },
  {
    label: 'Versandleiter',
    aliases: [
      'shipping manager',
      'versand manager',
      'versand managerin',
      'leiter versand',
      'leiterin versand',
      'outbound logistics lead',
      'paketversand leitung',
      'warenausgang leitung',
      'dispatch manager',
      'disponent versand',
      'disponentin versand',
      'fulfillment versand leitung',
      'ecommerce versand leitung',
      'lagerausgang leitung',
      'shipping lead',
      'versandverantwortlicher',
      'versandverantwortliche',
      'shipping operations manager',
      'leiter warenausgang',
      'leiterin warenausgang',
      'versand operations manager',
      'versand operations managerin',
      'shipping verantwortlicher',
    ],
  },
  {
    label: 'Lagerleiter',
    aliases: [
      'lagerleiter',
      'lagerleiterin',
      'warehouse manager',
      'lager manager',
      'lager managerin',
      'leiter lager',
      'leiterin lager',
      'lagerverwaltung leitung',
      'lagerlogistik leiter',
      'lagerlogistik leiterin',
      'warehouse lead',
      'logistiklager leitung',
      'lagerchef',
      'lagerchefin',
      'fulfillment lagerleiter',
      'fulfillment lagerleiterin',
      'bestandslager leitung',
      'lagerbetrieb leitung',
      'lagerverantwortlicher',
      'lagerverantwortliche',
    ],
  },
  {
    label: 'Fuhrparkleiter',
    aliases: [
      'fuhrparkleiter',
      'fuhrparkleiterin',
      'fleet manager',
      'flottenmanager',
      'flottenmanagerin',
      'leiter fuhrpark',
      'leiterin fuhrpark',
      'fahrzeugflotte leitung',
      'flottenverantwortlicher',
      'flottenverantwortliche',
      'dienstwagen manager',
      'dienstwagen managerin',
      'transportflotte leitung',
      'fahrzeugmanagement leitung',
      'fleet operations manager',
      'fuhrparkmanagement',
      'logistik fleet manager',
      'fahrzeugpark leitung',
      'mobilitätsmanager flotte',
      'mobilitaetsmanager flotte',
    ],
  },
  {
    label: 'Eventmanager',
    aliases: [
      'eventmanager',
      'eventmanagerin',
      'veranstaltungsmanager',
      'veranstaltungsmanagerin',
      'event lead',
      'event planner',
      'event planer',
      'event planerIn',
      'leiter events',
      'leiterin events',
      'messemanager',
      'messemanagerin',
      'veranstaltungsleitung',
      'event koordinator',
      'event koordinatorin',
      'event producer',
      'event producerin',
      'corporate events manager',
      'live communication manager',
      'eventverantwortlicher',
      'eventverantwortliche',
    ],
  },
  {
    label: 'Seminarleiter',
    aliases: [
      'seminarleiter',
      'seminarleiterin',
      'trainer seminar',
      'trainerin seminar',
      'weiterbildungsleiter',
      'weiterbildungsleiterin',
      'schulungsleiter',
      'schulungsleiterin',
      'academy manager',
      'akademieleiter',
      'akademieleiterin',
      'kursleiter',
      'kursleiterin',
      'bildungsmanager',
      'bildungsmanagerin',
      'training manager',
      'training managerin',
      'learning development manager',
      'ld manager',
      'fortbildungsleiter',
    ],
  },
  {
    label: 'Coach',
    aliases: [
      'coach',
      'business coach',
      'business coachin',
      'verkaufscoach',
      'verkaufscoachin',
      'karrierecoach',
      'karrierecoachin',
      'führungskräftecoach',
      'fuehrungskraeftecoach',
      'führungskräftecoachin',
      'fuehrungskraeftecoachin',
      'personal coach',
      'mental coach',
      'team coach',
      'systemischer coach',
      'systemische coachin',
      'berater coach',
      'beraterin coach',
      'trainer coach',
      'trainerin coach',
    ],
  },
  {
    label: 'Trainer',
    aliases: [
      'trainer',
      'trainerin',
      'sales trainer',
      'sales trainerin',
      'vertriebstrainer',
      'vertriebstrainerin',
      'kommunikationstrainer',
      'kommunikationstrainerin',
      'soft skill trainer',
      'soft skill trainerin',
      'fachtrainer',
      'fachtrainerin',
      'produktschulungs trainer',
      'produktschulungs trainerin',
      'seminartrainer',
      'seminartrainerin',
      'workshop trainer',
      'workshop trainerin',
      'corporate trainer',
      'corporate trainerin',
    ],
  },
  {
    label: 'Anwender',
    aliases: [
      'anwender',
      'anwenderin',
      'nutzer',
      'nutzerin',
      'user',
      'endnutzer',
      'endnutzerin',
      'benutzer',
      'benutzerin',
      'operator',
      'operatorin',
      'sachbearbeiter als nutzer',
      'sachbearbeiterin als nutzerin',
      'mitarbeiter als anwender',
      'mitarbeiterin als anwenderin',
      'täglicher nutzer',
      'taeglicher nutzer',
      'tägliche nutzerin',
      'taegliche nutzerin',
      'operative nutzungsperson',
    ],
  },
  {
    label: 'Power-User',
    aliases: [
      'power user',
      'power-user',
      'key user',
      'key-user',
      'super user',
      'super-user',
      'hauptanwender',
      'hauptanwenderin',
      'systemverantwortlicher anwender',
      'systemverantwortliche anwenderin',
      'fachlicher key user',
      'fachliche key userin',
      'prozess key user',
      'prozess key userin',
      'admin anwender',
      'admin anwenderin',
      'multiplikator system',
      'multiplikatorin system',
      'pilotnutzer',
      'pilotnutzerin',
    ],
  },
  {
    label: 'Pilotkunde',
    aliases: [
      'pilotkunde',
      'pilotkundin',
      'testkunde',
      'testkundin',
      'beta kunde',
      'beta kundin',
      'early adopter kunde',
      'early adopter kundin',
      'pilotanwender',
      'pilotanwenderin',
      'testanwender',
      'testanwenderin',
      'referenzkunde pilot',
      'referenzkundin pilot',
      'erstanwender',
      'erstanwenderin',
      'probierkunde',
      'probierkundin',
      'pilotprojekt kunde',
      'pilotprojekt kundin',
    ],
  },
],

persona_psychology: [
  {
        "label": "Dominanter Entscheider",
        "aliases": [
            "dominanter entscheider",
            "entscheider typ",
            "dominanter typ",
            "durchsetzungsstarker typ",
            "machtmensch",
            "macher mit führungsanspruch",
            "kontrolltyp",
            "kontrollfreak",
            "willensstarker mensch",
            "starker charakter",
            "chef typ",
            "boss typ",
            "führungstyp",
            "alpha typ",
            "direkter typ",
            "jemand der gerne entscheidet",
            "jemand der die kontrolle haben will",
            "jemand der klare ansagen macht",
            "jemand der führen will",
            "jemand der sich durchsetzt",
            "person die das kommando übernimmt",
            "person die schnell entscheidet",
            "person mit führungsanspruch",
            "typ der nicht lange diskutiert",
            "typ der ergebnisse sehen will",
            "typ der gerne vorgibt wo es langgeht",
            "entschlossener mensch",
            "autoritärer typ",
            "fordernder typ",
            "zielstarker mensch"
        ]
    },
    {
        "label": "Strategischer Planer",
        "aliases": [
            "strategischer planer",
            "strategie typ",
            "strategischer denker",
            "planer typ",
            "mastermind typ",
            "systemdenker",
            "konzeptmensch",
            "zukunftsplaner",
            "langfristiger planer",
            "visionärer planer",
            "taktiker",
            "schachspieler typ",
            "strukturierter stratege",
            "jemand der langfristig denkt",
            "jemand der einen plan braucht",
            "jemand der alles vorausdenkt",
            "jemand der systeme baut",
            "jemand der strategisch arbeitet",
            "person die in großen zusammenhängen denkt",
            "person die das große ganze sieht",
            "typ der nicht spontan sondern geplant handelt",
            "typ der langfristige ziele verfolgt",
            "typ der mehrere schritte vorausdenkt",
            "vorausschauender mensch",
            "analytischer planer",
            "vision mit plan",
            "planvoller mensch",
            "konzeptioneller typ"
        ]
    },
    {
        "label": "Analytischer Prüfer",
        "aliases": [
            "analytischer prüfer",
            "analytischer typ",
            "prüfer typ",
            "zahlenmensch",
            "datenmensch",
            "faktenmensch",
            "logiker",
            "sachlicher typ",
            "rationaler typ",
            "kritischer analyst",
            "analyse typ",
            "detailprüfer",
            "qualitätsprüfer",
            "jemand der fakten braucht",
            "jemand der alles genau prüft",
            "jemand der beweise sehen will",
            "jemand der daten sehen will",
            "jemand der logisch überzeugt werden muss",
            "person die analytisch entscheidet",
            "person die nichts ungeprüft akzeptiert",
            "typ der erst analysiert und dann entscheidet",
            "typ der zahlen daten fakten braucht",
            "typ der unklare aussagen hinterfragt",
            "typ der genau wissen will ob etwas stimmt",
            "sachorientierter mensch",
            "nüchterner prüfer",
            "rationaler entscheider",
            "faktenbasierter mensch"
        ]
    },
    {
        "label": "Detailorientierter Perfektionist",
        "aliases": [
            "detailorientierter perfektionist",
            "perfektionist",
            "genauer typ",
            "sorgfältiger typ",
            "detailmensch",
            "qualitätsmensch",
            "präziser mensch",
            "akkurater typ",
            "gewissenhafter typ",
            "ordentlicher typ",
            "fehlervermeider",
            "korrektheitsmensch",
            "jemand der alles perfekt machen will",
            "jemand der keine fehler akzeptiert",
            "jemand der sehr genau arbeitet",
            "jemand der auf jedes detail achtet",
            "person die sauberkeit und korrektheit braucht",
            "person die lieber langsam aber richtig arbeitet",
            "typ der qualität vor tempo stellt",
            "typ der standards ernst nimmt",
            "typ der ungenauigkeit nicht mag",
            "typ der alles ordentlich dokumentieren will",
            "pingeliger typ",
            "gründlicher mensch",
            "sauberer arbeiter",
            "qualitätsorientierter mensch",
            "präzisionsmensch",
            "korrekter typ"
        ]
    },
    {
        "label": "Sicherheitsbewusster Bewahrer",
        "aliases": [
            "sicherheitsbewusster bewahrer",
            "sicherheitsmensch",
            "vorsichtiger typ",
            "konservativer typ",
            "bewahrer typ",
            "sicherheitsorientierter typ",
            "risikoscheuer mensch",
            "stabilitätsmensch",
            "traditionsmensch",
            "routine mensch",
            "jemand der sicherheit braucht",
            "jemand der kein risiko will",
            "jemand der bewährtes bevorzugt",
            "jemand der veränderung skeptisch sieht",
            "person die planbarkeit braucht",
            "person die stabile rahmen braucht",
            "person die nicht gerne experimentiert",
            "typ der sicherheit wichtiger findet als chancen",
            "typ der lieber beim bekannten bleibt",
            "typ der klare regeln braucht",
            "typ der nichts überstürzen will",
            "absicherer",
            "vorsichtiger entscheider",
            "defensiver typ",
            "verlässlichkeit suchender mensch",
            "stabilität suchender typ",
            "planbarkeitstyp"
        ]
    },
    {
        "label": "Pflichtbewusster Organisator",
        "aliases": [
            "pflichtbewusster organisator",
            "organisator typ",
            "ordnungs mensch",
            "struktur mensch",
            "verantwortungsbewusster typ",
            "disziplinierter typ",
            "zuverlässiger typ",
            "regeltyp",
            "administrator typ",
            "manager typ",
            "prozessmensch",
            "ablaufmensch",
            "jemand der ordnung schafft",
            "jemand der verantwortung ernst nimmt",
            "jemand der dinge zuverlässig erledigt",
            "jemand der klare abläufe braucht",
            "person die strukturen einhält",
            "person die organisiert arbeitet",
            "person die versprechen ernst nimmt",
            "typ der pflicht und zuverlässigkeit wichtig findet",
            "typ der prozesse sauber einhält",
            "typ der chaos nicht mag",
            "typ der termine und regeln beachtet",
            "verlässlicher organisator",
            "strukturierter umsetzer",
            "pflichtmensch",
            "ordnungsmacher",
            "prozessdenker"
        ]
    },
    {
        "label": "Ruhiger Unterstützer",
        "aliases": [
            "ruhiger unterstützer",
            "unterstützer typ",
            "stiller helfer",
            "ruhiger typ",
            "loyaler typ",
            "geduldiger mensch",
            "teamplayer",
            "verlässlicher begleiter",
            "ruhender pol",
            "stabiler mensch",
            "ausgleichender typ",
            "jemand der andere unterstützt",
            "jemand der nicht im mittelpunkt stehen muss",
            "jemand der geduldig zuhört",
            "jemand der zuverlässig im hintergrund hilft",
            "person die ruhe ausstrahlt",
            "person die stabilität gibt",
            "person die anderen den rücken stärkt",
            "typ der gerne hilft ohne laut zu sein",
            "typ der langfristige beziehungen schätzt",
            "typ der lieber unterstützt als führt",
            "typ der harmonie und stabilität sucht",
            "hilfsbereiter ruhiger mensch",
            "stabiler teammensch",
            "geduldiger unterstützer",
            "loyaler begleiter",
            "ruhiger teamplayer"
        ]
    },
    {
        "label": "Harmonieorientierter Vermittler",
        "aliases": [
            "harmonieorientierter vermittler",
            "vermittler typ",
            "harmonie mensch",
            "friedensstifter",
            "ausgleicher",
            "diplomatischer typ",
            "konfliktvermeider",
            "deeskalierender typ",
            "sozialer ausgleicher",
            "jemand der harmonie braucht",
            "jemand der konflikte vermeiden will",
            "jemand der zwischen menschen vermittelt",
            "jemand der streit schlecht aushält",
            "person die ausgleich schaffen will",
            "person die auf gute stimmung achtet",
            "person die lieber verbindet als spaltet",
            "typ der niemanden verletzen will",
            "typ der kompromisse sucht",
            "typ der konflikt früh entschärft",
            "typ der soziale spannung spürt",
            "versöhnlicher mensch",
            "friedlicher typ",
            "kompromissmensch",
            "beziehungsbewahrender typ",
            "sozial vorsichtiger mensch",
            "diplomatischer mensch"
        ]
    },
    {
        "label": "Sozialer Netzwerker",
        "aliases": [
            "sozialer netzwerker",
            "netzwerker typ",
            "kontaktmensch",
            "menschenmensch",
            "geselliger typ",
            "kommunikativer typ",
            "beziehungsmensch",
            "kontaktfreudiger mensch",
            "netzwerk mensch",
            "people person",
            "jemand der schnell kontakte knüpft",
            "jemand der gerne mit menschen spricht",
            "jemand der energie aus austausch bekommt",
            "jemand der gern vernetzt",
            "person die leicht auf andere zugeht",
            "person die viele kontakte pflegt",
            "person die in gruppen aufblüht",
            "typ der gern im gespräch ist",
            "typ der soziale verbindungen wichtig findet",
            "typ der menschen zusammenbringt",
            "typ der gerne sichtbar und ansprechbar ist",
            "gesprächiger mensch",
            "offener kontakt typ",
            "kommunikationsstarker mensch",
            "beziehungsaktiver typ",
            "gruppenmensch"
        ]
    },
    {
        "label": "Charismatischer Überzeuger",
        "aliases": [
            "charismatischer überzeuger",
            "überzeuger typ",
            "charismatischer typ",
            "motivator",
            "begeisterer",
            "inspirator",
            "verkaufstyp",
            "präsentationstyp",
            "kommunikativer überzeuger",
            "mitreißender typ",
            "jemand der andere begeistern kann",
            "jemand der gut überzeugen kann",
            "jemand der menschen mitreißt",
            "jemand der emotional verkauft",
            "person mit starker ausstrahlung",
            "person die ideen gut präsentieren kann",
            "person die andere motiviert",
            "typ der durch energie überzeugt",
            "typ der mit worten wirkt",
            "typ der menschen für etwas gewinnt",
            "typ der positiv und präsent wirkt",
            "begeisternder mensch",
            "inspirierender mensch",
            "kommunikativer motivator",
            "charisma mensch",
            "überzeugungsstarker typ"
        ]
    },
    {
        "label": "Empathischer Berater",
        "aliases": [
            "empathischer berater",
            "berater typ",
            "empathischer typ",
            "menschenversteher",
            "zuhörer typ",
            "coach typ",
            "mentor typ",
            "therapeutischer typ",
            "einfühlsamer mensch",
            "verständnisvoller typ",
            "jemand der andere gut versteht",
            "jemand der zuhören kann",
            "jemand der zwischen den zeilen liest",
            "jemand der menschen helfen will",
            "person die sich in andere hineinversetzt",
            "person die emotionen gut erkennt",
            "person die gerne berät und begleitet",
            "typ der tiefe gespräche mag",
            "typ der andere entwickeln möchte",
            "typ der emotionale motive erkennt",
            "typ der auf menschliche bedürfnisse achtet",
            "einfühlsamer unterstützer",
            "verständnisvoller berater",
            "sozial sensibler mensch",
            "menschenorientierter coach",
            "empathischer mentor"
        ]
    },
    {
        "label": "Idealistischer Sinnsucher",
        "aliases": [
            "idealistischer sinnsucher",
            "sinnsucher",
            "idealist",
            "werte mensch",
            "wertegetriebener typ",
            "mission mensch",
            "sinnorientierter typ",
            "authentischer typ",
            "träumer typ",
            "tiefgründiger typ",
            "jemand der sinn sucht",
            "jemand der nach werten lebt",
            "jemand der authentisch sein will",
            "jemand der etwas bedeutsames tun möchte",
            "person die nicht nur geld oder status will",
            "person die innere werte wichtig findet",
            "person die oberflächlichkeit ablehnt",
            "typ der bedeutung hinter dingen sucht",
            "typ der eine mission braucht",
            "typ der mit falschen werten nicht leben kann",
            "typ der echte verbindung und sinn sucht",
            "werteorientierter mensch",
            "sinngetriebener mensch",
            "idealistischer mensch",
            "authentizität suchender typ",
            "tiefgründiger idealist"
        ]
    },
    {
        "label": "Kreativer Freigeist",
        "aliases": [
            "kreativer freigeist",
            "freigeist",
            "kreativer typ",
            "ideen mensch",
            "künstlerischer typ",
            "unkonventioneller typ",
            "fantasievoller mensch",
            "origineller typ",
            "kreativer kopf",
            "freiheitskünstler",
            "jemand der eigene ideen entwickeln will",
            "jemand der frei denken muss",
            "jemand der starre vorgaben nicht mag",
            "jemand der kreativ und anders denkt",
            "person die unkonventionelle wege mag",
            "person die sich ausdrücken will",
            "person die neue ideen liebt",
            "typ der inspiration und freiheit braucht",
            "typ der nicht in schubladen passt",
            "typ der anders sein darf",
            "typ der kreative entfaltung sucht",
            "ideenreicher mensch",
            "kreativer individualist",
            "unkonventioneller denker",
            "fantasievoller kopf",
            "freier kreativer mensch"
        ]
    },
    {
        "label": "Innovativer Visionär",
        "aliases": [
            "innovativer visionär",
            "visionär",
            "innovator",
            "zukunftsmensch",
            "veränderer",
            "pionier typ",
            "fortschrittlicher typ",
            "neudenker",
            "disruptiver denker",
            "ideenpionier",
            "jemand der neues schaffen will",
            "jemand der zukunft gestalten will",
            "jemand der alte systeme verändern will",
            "jemand der innovation liebt",
            "person die chancen vor anderen sieht",
            "person die neue möglichkeiten erkennt",
            "person die nicht beim status quo bleiben will",
            "typ der transformation spannend findet",
            "typ der große ideen verfolgt",
            "typ der neue wege öffnen will",
            "typ der veränderung antreibt",
            "zukunftsorientierter mensch",
            "fortschrittsmensch",
            "pioniergeist",
            "visionärer erneuerer",
            "neuerfinder typ"
        ]
    },
    {
        "label": "Pragmatischer Umsetzer",
        "aliases": [
            "pragmatischer umsetzer",
            "umsetzer typ",
            "praktischer typ",
            "macher typ",
            "hands on typ",
            "lösungsorientierter typ",
            "realistischer macher",
            "tatmensch",
            "anpacker",
            "praktiker",
            "jemand der direkt ins tun kommt",
            "jemand der praktische lösungen sucht",
            "jemand der nicht lange theoretisiert",
            "jemand der macht statt redet",
            "person die schnell umsetzt",
            "person die konkrete probleme lösen will",
            "person die greifbare ergebnisse braucht",
            "typ der praxis wichtiger findet als theorie",
            "typ der einfach anfängt",
            "typ der lösungen ausprobiert",
            "typ der bodenständig handelt",
            "hands on mensch",
            "praktischer problemlöser",
            "direkter umsetzer",
            "tatkräftiger mensch",
            "realistischer umsetzer"
        ]
    },
    {
        "label": "Abenteuerlustiger Macher",
        "aliases": [
            "abenteuerlustiger macher",
            "abenteurer typ",
            "risikotyp",
            "action typ",
            "draufgänger",
            "wagnis mensch",
            "risikobereiter typ",
            "chancenmensch",
            "spontaner macher",
            "erlebnismensch",
            "jemand der risiko eingeht",
            "jemand der action braucht",
            "jemand der chancen schnell nutzt",
            "jemand der lieber ausprobiert als absichert",
            "person die unsicherheit spannend findet",
            "person die ins kalte wasser springt",
            "person die schnelle dynamik mag",
            "typ der abenteuer und bewegung braucht",
            "typ der mutig handelt",
            "typ der nicht zu lange wartet",
            "typ der große chancen sieht",
            "wagnisfreudiger mensch",
            "mutiger macher",
            "schneller chancen nutzer",
            "dynamischer risikotyp",
            "handlungsstarker abenteurer"
        ]
    },
    {
        "label": "Wettbewerbsorientierter Performer",
        "aliases": [
            "wettbewerbsorientierter performer",
            "performer typ",
            "leistungsmensch",
            "erfolgsmensch",
            "wettbewerbstyp",
            "karrieremensch",
            "ambitionierter typ",
            "ehrgeiziger typ",
            "top performer",
            "high achiever",
            "jemand der gewinnen will",
            "jemand der besser sein will als andere",
            "jemand der erfolg sichtbar machen will",
            "jemand der leistung braucht",
            "person die sich über erfolg definiert",
            "person die hohe ziele verfolgt",
            "person die sich gern misst",
            "typ der karriere und aufstieg sucht",
            "typ der durch leistung auffallen will",
            "typ der mittelmaß nicht akzeptiert",
            "typ der ständig besser werden will",
            "erfolgsorientierter mensch",
            "ambitionierter mensch",
            "leistungsorientierter typ",
            "kompetitiver typ",
            "zielgetriebener performer"
        ]
    },
    {
        "label": "Anerkennungsorientierter Statusmensch",
        "aliases": [
            "anerkennungsorientierter statusmensch",
            "statusmensch",
            "anerkennungstyp",
            "prestige mensch",
            "image mensch",
            "reputationsmensch",
            "sichtbarkeitstyp",
            "lob typ",
            "bestätigung suchender typ",
            "wertschätzungsmensch",
            "jemand der anerkennung braucht",
            "jemand der gesehen werden will",
            "jemand dem status wichtig ist",
            "jemand der wertschätzung sucht",
            "person die auf außenwirkung achtet",
            "person die respekt und rang wichtig findet",
            "person die positives feedback braucht",
            "typ der nicht übersehen werden will",
            "typ der prestige und reputation beachtet",
            "typ der sichtbare erfolge wichtig findet",
            "typ der soziale bestätigung sucht",
            "imagebewusster mensch",
            "prestigeorientierter typ",
            "statusorientierter mensch",
            "loborientierter typ",
            "anerkennung suchender mensch"
        ]
    },
    {
        "label": "Unabhängiger Individualist",
        "aliases": [
            "unabhängiger individualist",
            "individualist",
            "unabhängiger typ",
            "freiheitsmensch",
            "autonomie mensch",
            "selbstbestimmter typ",
            "eigenständiger mensch",
            "nonkonformist",
            "rebellischer typ",
            "eigenbrötler",
            "jemand der unabhängig sein will",
            "jemand der frei entscheiden möchte",
            "jemand der keine kontrolle mag",
            "jemand der seinen eigenen weg geht",
            "person die selbstbestimmung braucht",
            "person die ungern fremdgesteuert wird",
            "person die eigene regeln braucht",
            "typ der freiheit wichtiger findet als anpassung",
            "typ der alleine gut arbeiten kann",
            "typ der sich nicht gerne einordnen lässt",
            "typ der eigene entscheidungen treffen will",
            "autonomer mensch",
            "selbstständiger denker",
            "freier individualist",
            "unabhängigkeitsmensch",
            "eigenwilliger typ"
        ]
    },
    {
        "label": "Fürsorglicher Beschützer",
        "aliases": [
            "fürsorglicher beschützer",
            "beschützer typ",
            "fürsorglicher typ",
            "kümmerer",
            "helfer typ",
            "warmherziger typ",
            "familienmensch",
            "schutzmensch",
            "versorger typ",
            "loyaler beschützer",
            "jemand der sich um andere kümmert",
            "jemand der andere schützen will",
            "jemand der gerne gebraucht wird",
            "jemand der verantwortung für andere übernimmt",
            "person die fürsorge zeigt",
            "person die nahestehende menschen unterstützt",
            "person die sicherheit für andere schafft",
            "typ der familie und vertraute beziehungen wichtig findet",
            "typ der im hintergrund viel trägt",
            "typ der nicht laut aber zuverlässig ist",
            "typ der andere nicht im stich lässt",
            "warmherziger helfer",
            "stiller beschützer",
            "treuer unterstützer",
            "familienorientierter mensch",
            "fürsorglicher mensch"
        ]
    },
    {
        "label": "Traditioneller Gemeinschaftsmensch",
        "aliases": [
            "traditioneller gemeinschaftsmensch",
            "gemeinschaftsmensch",
            "traditionsmensch",
            "konventioneller typ",
            "sozialer bewahrer",
            "gruppenmensch",
            "vereinsmensch",
            "gastgeber typ",
            "gemeinschaftstyp",
            "sozial eingebundener typ",
            "jemand dem gemeinschaft wichtig ist",
            "jemand der dazugehören möchte",
            "jemand der soziale rollen ernst nimmt",
            "jemand der tradition und zusammenhalt schätzt",
            "person die gerne teil einer gruppe ist",
            "person die vertraute soziale ordnung mag",
            "person die sich um das miteinander kümmert",
            "typ der feste gemeinschaften braucht",
            "typ der bewährte soziale regeln achtet",
            "typ der familie verein oder team wichtig findet",
            "typ der nicht allein außerhalb stehen will",
            "sozial traditioneller mensch",
            "gemeinschaftsorientierter typ",
            "konventioneller gemeinschaftsmensch",
            "verbundener typ",
            "sozialer ordnungsmensch"
        ]
    },
    {
        "label": "Kritischer Skeptiker",
        "aliases": [
            "kritischer skeptiker",
            "skeptiker",
            "kritischer typ",
            "hinterfrager",
            "zweifler",
            "misstrauischer typ",
            "prüfender skeptiker",
            "vorsichtiger kritiker",
            "realitätsprüfer",
            "kritischer denker",
            "jemand der alles hinterfragt",
            "jemand der nicht leicht vertraut",
            "jemand der erst überzeugt werden muss",
            "jemand der schwachstellen sucht",
            "person die versprechen kritisch prüft",
            "person die schnell erkennt wenn etwas nicht stimmt",
            "person die nicht auf schöne worte hereinfällt",
            "typ der skeptisch bleibt bis beweise da sind",
            "typ der risiken und fehler sucht",
            "typ der dinge nicht einfach glaubt",
            "typ der kritische fragen stellt",
            "zweifelnder prüfer",
            "kritischer realitätsmensch",
            "misstrauischer prüfer",
            "skeptischer entscheider",
            "harter hinterfrager"
        ]
    },
    {
        "label": "Konfliktfreudiger Herausforderer",
        "aliases": [
            "konfliktfreudiger herausforderer",
            "herausforderer typ",
            "challenger",
            "debattierer typ",
            "streitbarer typ",
            "provokanter typ",
            "konfrontativer typ",
            "argumentativer typ",
            "widerspruchs typ",
            "unbequemer typ",
            "jemand der gerne diskutiert",
            "jemand der andere herausfordert",
            "jemand der widerspruch nicht scheut",
            "jemand der provokant fragt",
            "person die bestehende annahmen angreift",
            "person die argumente testen will",
            "person die konflikt nicht vermeidet",
            "typ der geistige reibung braucht",
            "typ der nicht einfach zustimmt",
            "typ der gerne gegenhält",
            "typ der schwache argumente zerlegt",
            "streitlustiger denker",
            "kritischer herausforderer",
            "provokanter denker",
            "argumentationsstarker mensch",
            "unbequemer fragesteller"
        ]
    },
    {
        "label": "Zurückhaltender Beobachter",
        "aliases": [
            "zurückhaltender beobachter",
            "beobachter typ",
            "introvertierter typ",
            "ruhiger beobachter",
            "stiller typ",
            "reservierter typ",
            "nachdenklicher typ",
            "leiser mensch",
            "diskreter typ",
            "still wirkender mensch",
            "jemand der erst beobachtet",
            "jemand der nicht sofort redet",
            "jemand der lieber zuhört als spricht",
            "jemand der kleine runden bevorzugt",
            "person die nicht im mittelpunkt stehen will",
            "person die ruhe braucht",
            "person die soziale pausen braucht",
            "typ der tief nachdenkt bevor er spricht",
            "typ der zurückhaltend kommuniziert",
            "typ der lieber im hintergrund bleibt",
            "typ der nicht ständig austausch braucht",
            "ruhiger denker",
            "stiller beobachter",
            "leiser analytiker",
            "reservierter mensch",
            "bedachter beobachter"
        ]
    },
    {
        "label": "Lernorientierter Entwickler",
        "aliases": [
            "lernorientierter entwickler",
            "entwickler typ",
            "wachstums mensch",
            "lernmensch",
            "growth mindset typ",
            "weiterentwickler",
            "selbstverbesserer",
            "fortschrittsmensch",
            "neugieriger lerner",
            "entwicklungsorientierter typ",
            "jemand der wachsen will",
            "jemand der sich verbessern möchte",
            "jemand der gerne lernt",
            "jemand der feedback nutzen will",
            "person die persönliche entwicklung sucht",
            "person die besser werden will",
            "person die offen für neue fähigkeiten ist",
            "typ der nicht stehen bleiben will",
            "typ der entwicklung wichtiger findet als bequemlichkeit",
            "typ der aus fehlern lernen will",
            "typ der sich ständig weiterentwickelt",
            "wachstumsorientierter mensch",
            "lernfreudiger typ",
            "entwicklungsgetriebener mensch",
            "verbesserungsorientierter typ",
            "potenzialentwickler"
        ]
    },
    {
        "label": "Erlebnisorientierter Entertainer",
        "aliases": [
            "erlebnisorientierter entertainer",
            "entertainer typ",
            "performer typ",
            "showmensch",
            "bühnenmensch",
            "spaßmensch",
            "erlebnismensch",
            "momentmensch",
            "lebensfroher typ",
            "unterhaltungstyp",
            "jemand der gute stimmung macht",
            "jemand der menschen unterhält",
            "jemand der den moment erleben will",
            "jemand der gerne sichtbar ist",
            "person die energie und spaß in gruppen bringt",
            "person die spontan und lebendig wirkt",
            "person die erlebnisse wichtiger findet als pläne",
            "typ der gerne lacht und andere mitzieht",
            "typ der atmosphäre erzeugt",
            "typ der im moment aufblüht",
            "typ der gerne auf der bühne steht",
            "lebendiger performer",
            "spontaner entertainer",
            "sozialer performer",
            "stimmungsmacher",
            "emotionaler erlebnistyp"
        ]
    }
],

  offer_category: [
    {
      label: 'Recruiting / Mitarbeitergewinnung',
      aliases: [
        'recruiting',
        'mitarbeitergewinnung',
        'personalgewinnung',
        'fachkräftegewinnung',
        'bewerbergewinnung',
        'social recruiting',
        'active sourcing',
        'headhunting',
        'executive search',
        'personalberatung',
        'zeitarbeit',
        'staffing',
        'talent acquisition',
        'employer branding',
        'stellenbesetzung',
        'bewerberpipeline',
        'jobanzeigen',
        'personalvermittlung',
        'stellenanzeige',
        'kandidatensuche',
        'personalbeschaffung',
        'talent sourcing',
        'recruiting service',
        'personalsuche',
      ],
    },
    {
      label: 'Leadgenerierung',
      aliases: [
        'leadgenerierung',
        'lead generation',
        'leads',
        'kontaktanfragen',
        'terminleads',
        'b2b leads',
        'qualifizierte leads',
        'neukundenanfragen',
        'inbound leads',
        'outbound leads',
        'leadkampagne',
        'leadgewinnung',
        'vertriebsleads',
        'kundenkontakte',
        'lead funnel',
        'lead pipeline',
        'anfragen',
        'neuanfragen',
        'kontaktgenerierung',
        'interessenten',
        'neukunden',
        'interessentengenerierung',
        'prospektgewinnung',
      ],
    },
    {
      label: 'Marketing / Werbung',
      aliases: [
        'marketing',
        'werbung',
        'online marketing',
        'performance marketing',
        'digital marketing',
        'paid ads',
        'meta ads',
        'facebook ads',
        'instagram ads',
        'google ads',
        'seo',
        'sea',
        'content marketing',
        'social media marketing',
        'kampagnen',
        'werbekampagne',
        'brand awareness',
        'growth marketing',
        'kundengewinnung',
        'reichweite',
        'onlinewerbung',
        'digitalwerbung',
        'markenkommunikation',
      ],
    },
    {
      label: 'Vertrieb / Sales',
      aliases: [
        'vertrieb',
        'sales',
        'verkauf',
        'closing',
        'abschlüsse',
        'abschlussgespräche',
        'telefonvertrieb',
        'sales outsourcing',
        'vertriebsaufbau',
        'sales enablement',
        'neukundengewinnung',
        'akquise',
        'kaltakquise',
        'vertriebsprozess',
        'outbound sales',
        'b2b vertrieb',
        'verkaufsgespräche',
        'vertriebsoptimierung',
        'abschlussrate',
        'vertriebsunterstützung',
        'außendienst',
        'innendienst',
        'verkaufsförderung',
      ],
    },
    {
      label: 'Terminierung / Setter',
      aliases: [
        'terminierung',
        'appointment setting',
        'setter',
        'terminsetter',
        'terminvereinbarung',
        'kalenderbuchung',
        'erstgespräch buchen',
        'sdr',
        'sales development',
        'qualifizierungsgespräch',
        'vorgespräch',
        'verkaufstermin',
        'beratungstermin',
        'demo buchen',
        'call booking',
        'terminakquise',
        'vertriebstermine',
        'ersttermin',
        'termine generieren',
        'terminbuchung',
        'telefontermin',
        'kalender füllen',
      ],
    },
    {
      label: 'Beratung / Consulting',
      aliases: [
        'beratung',
        'consulting',
        'unternehmensberatung',
        'strategie',
        'strategieberatung',
        'prozessberatung',
        'business consulting',
        'wachstumsberatung',
        'optimierung',
        'beratungshaus',
        'managementberatung',
        'organisationsberatung',
        'digitalberatung',
        'prozessoptimierung',
        'geschäftsentwicklung',
        'business development',
        'sparring',
        'transformation',
        'beraterleistung',
        'beratungsleistung',
        'strategieentwicklung',
        'externer berater',
        'business analyse',
      ],
    },
    {
      label: 'Coaching / Mentoring',
      aliases: [
        'coaching',
        'business coaching',
        'sales coaching',
        'mindset coaching',
        'mentoring',
        'mentor',
        'begleitung',
        '1:1 coaching',
        'gruppencoaching',
        'coaching programm',
        'premium coaching',
        'high ticket coaching',
        'persönlichkeitsentwicklung',
        'leadership coaching',
        'executive coaching',
        'performance coaching',
        'life coaching',
        'business coach',
        'vertriebscoach',
        'coach',
        'wachstumsbegleitung',
        'unternehmer coaching',
        'expertenbegleitung',
      ],
    },
    {
      label: 'Training / Weiterbildung',
      aliases: [
        'training',
        'weiterbildung',
        'schulung',
        'seminar',
        'workshop',
        'akademie',
        'kurs',
        'e-learning',
        'ausbildung',
        'fortbildung',
        'onlinekurs',
        'lernprogramm',
        'mitarbeiterschulung',
        'vertriebstraining',
        'sales training',
        'masterclass',
        'bootcamp',
        'training academy',
        'qualifizierung',
        'lehrgang',
        'unternehmensschulung',
        'fachschulung',
        'praxisworkshop',
      ],
    },
    {
      label: 'Software / SaaS',
      aliases: [
        'software',
        'saas',
        'software as a service',
        'tool',
        'plattform',
        'app',
        'crm',
        'automatisierung',
        'cloud software',
        'subscription software',
        'abo software',
        'softwarelösung',
        'webapp',
        'dashboard',
        'workflow software',
        'business software',
        'enterprise software',
        'ki software',
        'ai software',
        'no-code',
        'subscription',
        'unternehmenssoftware',
        'branchensoftware',
        'cloud lösung',
      ],
    },
    {
      label: 'IT-Dienstleistungen',
      aliases: [
        'it dienstleistung',
        'it services',
        'systemhaus',
        'managed services',
        'msp',
        'cybersecurity',
        'cloud services',
        'it support',
        'softwareentwicklung',
        'it beratung',
        'it betreuung',
        'netzwerkbetreuung',
        'server betreuung',
        'helpdesk',
        'devops',
        'cloud migration',
        'microsoft 365',
        'it outsourcing',
        'infrastruktur',
        'it sicherheit',
        'it service provider',
        'it partner',
        'it firma',
      ],
    },
    {
      label: 'Finanzen / Finanzierung',
      aliases: [
        'finanzen',
        'finanzberatung',
        'finanzierung',
        'investment',
        'vermögensberatung',
        'anlageberatung',
        'kredit',
        'leasing',
        'buchhaltung',
        'fördermittel',
        'finanzplanung',
        'kapitalanlage',
        'finanzdienstleistung',
        'liquidität',
        'unternehmensfinanzierung',
        'immobilienfinanzierung',
        'baufinanzierung',
        'steueroptimierung',
        'controlling',
        'lohnbuchhaltung',
        'factoring',
        'finanzierungslösung',
        'betriebsmittelfinanzierung',
      ],
    },
    {
      label: 'Versicherung / Vorsorge',
      aliases: [
        'versicherung',
        'versicherungen',
        'versicherungsmakler',
        'gewerbeversicherung',
        'krankenversicherung',
        'vorsorge',
        'absicherung',
        'bav',
        'altersvorsorge',
        'betriebshaftpflicht',
        'pkv',
        'private krankenversicherung',
        'lebensversicherung',
        'berufsunfähigkeit',
        'bu',
        'risikovorsorge',
        'versicherungsschutz',
        'versicherungsberatung',
        'sachversicherung',
        'firmenversicherung',
        'finanzvorsorge',
        'versicherungslösung',
        'betriebliche absicherung',
      ],
    },
    {
      label: 'Immobilien',
      aliases: [
        'immobilien',
        'immobilienverkauf',
        'immobilienvermittlung',
        'makler',
        'hausverkauf',
        'wohnung verkaufen',
        'property management',
        'hausverwaltung',
        'immobilienmakler',
        'vermietung',
        'immobilienbewertung',
        'objektvermittlung',
        'grundstück',
        'kapitalanlage immobilie',
        'wohnimmobilien',
        'gewerbeimmobilien',
        'immobilienberatung',
        'projektentwicklung',
        'immobilienservice',
        'immobilienunternehmen',
      ],
    },
    {
      label: 'Recht / Legal',
      aliases: [
        'recht',
        'rechtsberatung',
        'anwalt',
        'kanzlei',
        'legal',
        'vertragsrecht',
        'arbeitsrecht',
        'datenschutz',
        'compliance',
        'rechtsanwalt',
        'steuerrecht',
        'gesellschaftsrecht',
        'it recht',
        'legal service',
        'vertragsprüfung',
        'rechtliche beratung',
        'mandat',
        'juristische dienstleistung',
        'datenschutzberatung',
        'vertragsgestaltung',
        'anwaltsservice',
        'rechtsdienst',
      ],
    },
    {
      label: 'Gesundheit / Therapie',
      aliases: [
        'gesundheit',
        'healthcare',
        'therapie',
        'behandlung',
        'praxisleistung',
        'pflegeberatung',
        'medizinische dienstleistung',
        'prävention',
        'physiotherapie',
        'ergotherapie',
        'psychotherapie',
        'heilpraktiker',
        'medizinberatung',
        'patientenbetreuung',
        'gesundheitsberatung',
        'rehabilitation',
        'diagnostik',
        'wellbeing',
        'therapieangebot',
        'gesundheitscoaching',
        'praxismarketing',
        'therapeut',
        'gesundheitsdienst',
      ],
    },
    {
      label: 'Design / Kreativleistung',
      aliases: [
        'design',
        'branding',
        'brand design',
        'webdesign',
        'grafikdesign',
        'ux design',
        'ui design',
        'kreativleistung',
        'corporate design',
        'logo design',
        'markenauftritt',
        'brand identity',
        'visual identity',
        'creative agency',
        'designagentur',
        'website design',
        'landingpage design',
        'printdesign',
        'motion design',
        'gestaltung',
        'designleistung',
        'werbemittelgestaltung',
      ],
    },
    {
      label: 'E-Commerce / Shop',
      aliases: [
        'ecommerce',
        'e-commerce',
        'online shop',
        'shopify',
        'amazon',
        'marketplace',
        'webshop',
        'shopoptimierung',
        'onlinehandel',
        'woocommerce',
        'shop system',
        'd2c',
        'produktverkauf',
        'amazon seller',
        'conversion optimierung',
        'marktplatzverkauf',
        'shop setup',
        'produktlisting',
        'onlineshop erstellen',
        'shop betrieb',
        'ecommerce betrieb',
      ],
    },
    {
      label: 'Kundenservice / Support',
      aliases: [
        'kundenservice',
        'customer service',
        'support',
        'kundensupport',
        'callcenter',
        'service center',
        'helpdesk',
        'kundenbetreuung',
        'customer success',
        'service hotline',
        'ticket support',
        'kundenanfragen',
        'beschwerdemanagement',
        'kundenkommunikation',
        'after sales',
        'service outsourcing',
        'telefonservice',
        'chat support',
        'first level support',
        'kundenrückgewinnung',
        'servicequalität',
        'kundenhotline',
        'kundencare',
      ],
    },
    {
      label: 'Outsourcing / externe Prozesse',
      aliases: [
        'outsourcing',
        'bpo',
        'business process outsourcing',
        'prozessauslagerung',
        'externer dienstleister',
        'backoffice',
        'assistenz',
        'virtuelle assistenz',
        'remote assistant',
        'va',
        'operativer support',
        'administration',
        'büroservice',
        'prozessdienstleistung',
        'kundenservice outsourcing',
        'vertriebsoutsourcing',
        'personal outsourcing',
        'finance outsourcing',
        'delegieren',
        'büro outsourcing',
        'verwaltungs outsourcing',
        'ausgelagerte prozesse',
      ],
    },
    {
      label: 'Energie / Solar',
      aliases: [
        'energie',
        'strom',
        'gas',
        'solar',
        'photovoltaik',
        'pv anlage',
        'wärmepumpe',
        'energieberatung',
        'erneuerbare energien',
        'solaranlage',
        'stromtarif',
        'energieoptimierung',
        'heizsystem',
        'energieeffizienz',
        'energieversorgung',
        'grünstrom',
        'speicherlösung',
        'batteriespeicher',
        'solarberatung',
        'energieanlage',
        'heizungsoptimierung',
      ],
    },
    {
      label: 'Sonstiges Angebot',
      aliases: [
        'sonstiges',
        'sonstige leistung',
        'andere leistung',
        'dienstleistung',
        'diverses',
      ],
    },
  ],

  target_industry: [
    {
      label: 'Handwerk',
      aliases: [
        'handwerk',
        'handwerker',
        'dachdecker',
        'elektriker',
        'shk',
        'sanitär',
        'heizung',
        'klima',
        'maler',
        'tischler',
        'schreiner',
        'fliesenleger',
        'gartenbau',
        'metallbau',
        'trockenbau',
        'zimmerer',
        'installateur',
        'bodenleger',
        'fensterbauer',
        'handwerksbetrieb',
      ],
    },
    {
      label: 'Bau / Baugewerbe',
      aliases: [
        'bau',
        'bauunternehmen',
        'baugewerbe',
        'hochbau',
        'tiefbau',
        'generalunternehmer',
        'projektentwickler',
        'baubranche',
        'bauleiter',
        'bauprojekt',
        'rohbau',
        'innenausbau',
        'bauplanung',
        'architekt',
        'ingenieurbüro',
        'bausanierung',
        'renovierung',
        'modernisierung',
        'baugeschäft',
        'bauindustrie',
        'baufirma',
        'baugewerbebetrieb',
      ],
    },
    {
      label: 'Produktion / Fertigung',
      aliases: [
        'produktion',
        'fertigung',
        'hersteller',
        'maschinenbau',
        'anlagenbau',
        'produzierendes gewerbe',
        'fabrik',
        'manufacturing',
        'industrieproduktion',
        'serienfertigung',
        'maschinenhersteller',
        'zulieferer',
        'werkzeugbau',
        'metallverarbeitung',
        'kunststoffverarbeitung',
        'produktionsbetrieb',
        'fertigungsunternehmen',
        'maschinenpark',
        'industrielle fertigung',
        'werksbetrieb',
        'zulieferbetrieb',
      ],
    },
    {
      label: 'Industrie',
      aliases: [
        'industrie',
        'industriebetrieb',
        'mittelstand',
        'werk',
        'industrieunternehmen',
        'technischer betrieb',
        'b2b industrie',
        'industrial',
        'anlagenbetreiber',
        'technisches unternehmen',
        'fabrikbetrieb',
        'technischer mittelstand',
        'industriebranche',
        'großbetrieb',
        'industrieanlage',
        'kmu',
        'mittelständler',
        'b2b',
        'gewerbe',
        'gewerbebetrieb',
        'industrie mittelstand',
        'industriekunde',
      ],
    },
    {
      label: 'Medizin / Arztpraxen',
      aliases: [
        'arzt',
        'arztpraxis',
        'zahnarzt',
        'kieferorthopädie',
        'medizin',
        'praxis',
        'facharzt',
        'klinik',
        'orthopäde',
        'dermatologe',
        'hausarzt',
        'medizinische praxis',
        'praxisinhaber',
        'gemeinschaftspraxis',
        'mvz',
        'arztzentrum',
        'privatpraxis',
        'kassenarzt',
        'praxismanagement',
        'facharztpraxis',
        'arztgemeinschaft',
      ],
    },
    {
      label: 'Gesundheit / Pflege',
      aliases: [
        'pflege',
        'pflegeheim',
        'pflegedienst',
        'physiotherapie',
        'ergotherapie',
        'heilpraktiker',
        'ambulante pflege',
        'stationäre pflege',
        'seniorenheim',
        'pflegeeinrichtung',
        'gesundheitszentrum',
        'therapiepraxis',
        'rehazentrum',
        'patientenversorgung',
        'prävention',
        'betreuung',
        'gesundheitsdienst',
        'soziale einrichtung',
        'pflegebranche',
        'pflegesektor',
        'pflegeunternehmen',
        'gesundheitsbetrieb',
      ],
    },
    {
      label: 'Recht / Kanzleien',
      aliases: [
        'kanzlei',
        'anwalt',
        'rechtsanwalt',
        'steuerkanzlei',
        'notar',
        'rechtsberatung',
        'legal',
        'fachanwalt',
        'wirtschaftskanzlei',
        'anwaltskanzlei',
        'steuerberater',
        'mandanten',
        'jurist',
        'recht',
        'legal office',
        'compliance',
        'datenschutzkanzlei',
        'arbeitsrecht',
        'vertragsrecht',
        'gesellschaftsrecht',
        'steuerberatung',
      ],
    },
    {
      label: 'Finanzen',
      aliases: [
        'finanzen',
        'bank',
        'finanzberater',
        'vermögensberater',
        'investment',
        'buchhaltung',
        'steuerberatung',
        'finanzdienstleister',
        'finanzmakler',
        'anlageberater',
        'kreditberater',
        'leasing',
        'controlling',
        'accounting',
        'wirtschaftsprüfung',
        'finanzplanung',
        'kapitalanlage',
        'finanzbranche',
        'geldanlage',
        'wealthmanagement',
        'finanzunternehmen',
        'kreditinstitut',
      ],
    },
    {
      label: 'Versicherung',
      aliases: [
        'versicherung',
        'versicherungsmakler',
        'versicherungsagentur',
        'assekuranz',
        'gewerbeversicherung',
        'krankenkasse',
        'versicherer',
        'maklerbüro',
        'agenturinhaber',
        'versicherungsberater',
        'vorsorge',
        'absicherung',
        'bav',
        'pkv',
        'firmenversicherung',
        'vertrieb versicherung',
        'broker',
        'sachversicherung',
        'lebensversicherung',
        'versicherungsvertrieb',
        'versicherungsbranche',
        'versicherungsbüro',
      ],
    },
    {
      label: 'Immobilien',
      aliases: [
        'immobilien',
        'immobilienmakler',
        'makler',
        'hausverwaltung',
        'property management',
        'wohnungswirtschaft',
        'baufinanzierung',
        'immobilienverwaltung',
        'projektentwickler',
        'vermietung',
        'hausverkauf',
        'gewerbeimmobilien',
        'wohnimmobilien',
        'immobilienbüro',
        'real estate',
        'objektverwaltung',
        'immobilieninvestor',
        'immobilienberatung',
        'wohnungsverwaltung',
        'immobilienbranche',
        'immobilienmarkt',
        'maklerunternehmen',
      ],
    },
    {
      label: 'Bildung / Weiterbildung',
      aliases: [
        'bildung',
        'schule',
        'akademie',
        'weiterbildung',
        'universität',
        'seminaranbieter',
        'bildungsträger',
        'e-learning',
        'onlinekurs',
        'fortbildung',
        'nachhilfe',
        'lernplattform',
        'schulungsanbieter',
        'edtech',
        'berufsschule',
        'hochschule',
        'bildungsanbieter',
        'kursanbieter',
        'bildungsinstitut',
        'kurszentrum',
        'bildungseinrichtung',
        'lehranstalt',
      ],
    },
    {
      label: 'HR / Personal',
      aliases: [
        'hr',
        'personal',
        'personalabteilung',
        'human resources',
        'personalwesen',
        'employer branding',
        'mitarbeiterbindung',
        'personalentwicklung',
        'hr manager',
        'people operations',
        'talent management',
        'personalstrategie',
        'arbeitgebermarke',
        'onboarding',
        'mitarbeiterführung',
        'hr team',
        'personalmanagement',
        'employee experience',
        'hr abteilung',
        'personalverantwortlicher',
        'personalbüro',
      ],
    },
    {
      label: 'Recruiting / Personalberatung',
      aliases: [
        'recruiting',
        'personalberatung',
        'headhunter',
        'zeitarbeit',
        'staffing',
        'talent acquisition',
        'social recruiting agentur',
        'personalvermittler',
        'executive search',
        'recruiter',
        'bewerbermanagement',
        'kandidatenvermittlung',
        'fachkräftevermittlung',
        'stellenbesetzung',
        'arbeitnehmerüberlassung',
        'hr dienstleister',
        'recruiting agentur',
        'personalagentur',
        'talent sourcing',
        'jobvermittlung',
        'zeitarbeitsfirma',
        'personaldienstleister',
      ],
    },
    {
      label: 'Marketing / Agenturen',
      aliases: [
        'marketing',
        'agentur',
        'marketingagentur',
        'werbeagentur',
        'performance marketing',
        'seo agentur',
        'social media agentur',
        'google ads agentur',
        'meta ads agentur',
        'content agentur',
        'brand agency',
        'kreativagentur',
        'digitalagentur',
        'online marketing agentur',
        'lead agency',
        'growth agency',
        'medienagentur',
        'advertising agency',
        'pr agentur',
        'fullservice agentur',
        'werbeunternehmen',
        'marketingunternehmen',
      ],
    },
    {
      label: 'IT / Systemhäuser',
      aliases: [
        'it',
        'systemhaus',
        'it dienstleister',
        'managed service provider',
        'cybersecurity',
        'cloud',
        'msp',
        'it betreuung',
        'netzwerk',
        'server',
        'it support',
        'helpdesk',
        'it sicherheit',
        'cloud provider',
        'infrastruktur',
        'it beratung',
        'microsoft partner',
        'softwareentwicklung',
        'technikdienstleister',
        'it unternehmen',
        'it firma',
        'technologieunternehmen',
      ],
    },
    {
      label: 'Software / SaaS',
      aliases: [
        'software',
        'saas',
        'softwareunternehmen',
        'startup',
        'tech company',
        'plattform',
        'app',
        'cloud software',
        'b2b software',
        'softwareanbieter',
        'softwarehersteller',
        'abo software',
        'subscription software',
        'webapp',
        'crm software',
        'ki tool',
        'ai software',
        'tech startup',
        'digital platform',
        'saas startup',
        'softwarefirma',
        'softwarehaus',
        'scaleup',
      ],
    },
    {
      label: 'Telekommunikation',
      aliases: [
        'telekom',
        'telekommunikation',
        'telefonie',
        'mobilfunk',
        'internetprovider',
        'glasfaser',
        'voip',
        'dsl',
        'breitband',
        'netzanbieter',
        'telekommunikationsanbieter',
        'sip trunk',
        'telefonanlage',
        '5g',
        'connectivity',
        'internetanschluss',
        'provider',
        'cloud telefonie',
        'netzinfrastruktur',
        'tk branche',
        'telekommunikationsunternehmen',
        'telefongesellschaft',
      ],
    },
    {
      label: 'Energie',
      aliases: [
        'energie',
        'energieversorger',
        'strom',
        'gas',
        'solar',
        'photovoltaik',
        'wärmepumpe',
        'erneuerbare energien',
        'energieberatung',
        'pv',
        'stromanbieter',
        'energieanbieter',
        'heizung',
        'energiedienstleister',
        'netzbetreiber',
        'solarteur',
        'batteriespeicher',
        'grünstrom',
        'energieeffizienz',
        'energiebranche',
        'energieunternehmen',
        'versorgungsunternehmen',
      ],
    },
    {
      label: 'Automotive / Fahrzeuge',
      aliases: [
        'auto',
        'automotive',
        'autohandel',
        'autohaus',
        'werkstatt',
        'fahrzeughandel',
        'leasing',
        'kfz',
        'fahrzeuge',
        'gebrauchtwagen',
        'neuwagen',
        'autowerkstatt',
        'flottenmanagement',
        'fuhrpark',
        'kfz betrieb',
        'reifenservice',
        'car dealership',
        'fahrzeugvertrieb',
        'mobilität',
        'auto service',
        'kfz werkstatt',
        'autohändler',
      ],
    },
    {
      label: 'Logistik / Transport',
      aliases: [
        'logistik',
        'transport',
        'spedition',
        'lager',
        'fulfillment',
        'kurierdienst',
        'lieferdienst',
        'spediteur',
        'transportdienstleister',
        'warehousing',
        'supply chain',
        'versand',
        'paketdienst',
        'last mile',
        'flotte',
        'lkw',
        'güterverkehr',
        'lagerlogistik',
        'distribution',
        'expressdienst',
        'logistikunternehmen',
        'transportunternehmen',
      ],
    },
    {
      label: 'Einzelhandel',
      aliases: [
        'einzelhandel',
        'retail',
        'geschäft',
        'laden',
        'filiale',
        'handel',
        'stationärer handel',
        'fachhandel',
        'shop',
        'store',
        'boutique',
        'händler',
        'warenverkauf',
        'retailer',
        'ladenlokal',
        'verkaufsgeschäft',
        'lokaler handel',
        'fachgeschäft',
        'ladeninhaber',
        'einzelhändler',
        'stationär',
        'handelsunternehmen',
      ],
    },
    {
      label: 'E-Commerce',
      aliases: [
        'ecommerce',
        'e-commerce',
        'online shop',
        'shopify',
        'amazon seller',
        'marktplatz',
        'onlinehandel',
        'webshop',
        'woocommerce',
        'd2c',
        'direct to consumer',
        'shopbetreiber',
        'produktshop',
        'versandhandel',
        'online store',
        'marketplace seller',
        'etsy',
        'otto marketplace',
        'onlineverkauf',
        'onlineshop unternehmen',
        'ecommerce betrieb',
        'internetversand',
      ],
    },
    {
      label: 'Hotellerie',
      aliases: [
        'hotel',
        'hotellerie',
        'pension',
        'ferienwohnung',
        'resort',
        'unterkunft',
        'tourismus',
        'hostel',
        'aparthotel',
        'hotelbetrieb',
        'beherbergung',
        'rezeption',
        'gastgewerbe',
        'hotelgruppe',
        'zimmervermietung',
        'ferienhaus',
        'gästehaus',
        'hospitality',
        'hotelier',
        'beherbergungsbetrieb',
        'unterkunftsbetrieb',
        'boutique hotel',
      ],
    },
    {
      label: 'Gastronomie',
      aliases: [
        'gastronomie',
        'restaurant',
        'cafe',
        'bar',
        'catering',
        'imbiss',
        'food service',
        'bistro',
        'speisen',
        'gastronom',
        'systemgastronomie',
        'restaurantbetrieb',
        'kantine',
        'event catering',
        'foodtruck',
        'takeaway',
        'gastro',
        'lieferservice',
        'gastronomiebetrieb',
        'restaurantservice',
        'außer haus',
      ],
    },
    {
      label: 'Beauty / Ästhetik',
      aliases: [
        'beauty',
        'kosmetik',
        'kosmetikstudio',
        'ästhetik',
        'friseur',
        'hairstylist',
        'nagelstudio',
        'wimpern',
        'lash studio',
        'brow studio',
        'permanent makeup',
        'hautbehandlung',
        'med beauty',
        'salon',
        'friseursalon',
        'kosmetikerin',
        'anti aging',
        'ästhetische behandlung',
        'schönheitspflege',
        'beautystudio',
        'beauty salon',
        'schönheitssalon',
      ],
    },
    {
      label: 'Fitness',
      aliases: [
        'fitness',
        'fitnessstudio',
        'personal trainer',
        'sport',
        'gym',
        'gesundheitsstudio',
        'pt studio',
        'functional training',
        'crossfit',
        'yoga studio',
        'pilates',
        'sportstudio',
        'mitgliedschaft fitness',
        'fitnesscenter',
        'trainingsplan',
        'athletik',
        'personal training',
        'kraftsport',
        'fitnesstrainer',
        'fitnessbetrieb',
        'sportbetrieb',
        'fitnessclub',
      ],
    },
    {
      label: 'Wellness',
      aliases: [
        'wellness',
        'spa',
        'massage',
        'entspannung',
        'therme',
        'prävention',
        'sauna',
        'wellnesshotel',
        'massagepraxis',
        'health spa',
        'körperbehandlung',
        'regeneration',
        'wellbeing',
        'ayurveda',
        'naturheilkunde',
        'entspannungsangebot',
        'kur',
        'wellnessbetrieb',
        'wellnesszentrum',
        'massagestudio',
        'spa betrieb',
        'wellnessstudio',
      ],
    },
    {
      label: 'Sicherheit',
      aliases: [
        'sicherheit',
        'security',
        'objektschutz',
        'bewachung',
        'alarmtechnik',
        'brandschutz',
        'arbeitsschutz',
        'sicherheitsdienst',
        'wachdienst',
        'gebäudesicherheit',
        'zutrittskontrolle',
        'videoüberwachung',
        'personenschutz',
        'veranstaltungsschutz',
        'alarm system',
        'sicherheitstechnik',
        'risikomanagement',
        'sicherheitsfirma',
        'sicherheitsunternehmen',
        'bewachungsfirma',
        'sicherheitsdienstleister',
      ],
    },
    {
      label: 'Reinigung / Facility Management',
      aliases: [
        'reinigung',
        'gebäudereinigung',
        'facility management',
        'hausmeisterservice',
        'objektbetreuung',
        'cleaning',
        'büroreinigung',
        'industriereinigung',
        'unterhaltsreinigung',
        'glasreinigung',
        'facility service',
        'fm',
        'hausmeister',
        'gebäudeservice',
        'objektservice',
        'reinigungsfirma',
        'hygiene service',
        'winterdienst',
        'reinigungsunternehmen',
        'objektreinigung',
        'gebäudemanagement',
        'gebäudereiniger',
      ],
    },
    {
      label: 'Landwirtschaft',
      aliases: [
        'landwirtschaft',
        'agrar',
        'bauernhof',
        'landwirt',
        'forstwirtschaft',
        'gartenbau',
        'agrarbetrieb',
        'hof',
        'ackerbau',
        'viehzucht',
        'milchbetrieb',
        'landtechnik',
        'agrartechnik',
        'farm',
        'forstbetrieb',
        'pflanzenbau',
        'tierhaltung',
        'obstbau',
        'weinbau',
        'agrarwirtschaft',
        'bauer',
        'agrarunternehmen',
      ],
    },
    {
      label: 'Öffentlicher Sektor',
      aliases: [
        'öffentlicher sektor',
        'behörde',
        'verwaltung',
        'kommune',
        'stadt',
        'landkreis',
        'amt',
        'kommunalverwaltung',
        'public sector',
        'öffentliche hand',
        'ministerium',
        'gemeinde',
        'kreisverwaltung',
        'stadtverwaltung',
        'verwaltungseinrichtung',
        'öffentliche einrichtung',
        'staatsbetrieb',
        'kommunaler betrieb',
        'government',
        'öffentliche verwaltung',
        'staatliche einrichtung',
      ],
    },
    {
      label: 'Nonprofit / Vereine',
      aliases: [
        'verein',
        'ngo',
        'nonprofit',
        'gemeinnützig',
        'stiftung',
        'organisation',
        'verband',
        'hilfsorganisation',
        'npo',
        'soziale organisation',
        'förderverein',
        'gemeinnütziger träger',
        'charity',
        'ehrenamt',
        'spendenorganisation',
        'kirchliche organisation',
        'soziale träger',
        'mitgliederverein',
        'interessenverband',
        'gemeinnützige organisation',
        'wohlfahrt',
      ],
    },
    {
      label: 'Privatkunden / Endverbraucher',
      aliases: [
        'privatkunde',
        'endkunde',
        'verbraucher',
        'b2c',
        'haushalt',
        'familie',
        'privatperson',
        'konsument',
        'consumer',
        'private kunden',
        'endverbraucher',
        'haushaltskunden',
        'einzelperson',
        'familien',
        'private haushalte',
        'verbrauchermarkt',
        'retail customer',
        'konsumenten',
        'privatmarkt',
        'direktkunde',
        'private nachfrage',
      ],
    },
    {
      label: 'Führungskräfte',
      aliases: [
        'führungskraft',
        'führungskräfte',
        'manager',
        'teamleiter',
        'abteilungsleiter',
        'projektleiter',
        'middle management',
        'führungsperson',
        'leitungskraft',
        'vorgesetzte',
        'führungsebene',
        'bereichsleiter',
        'niederlassungsleiter',
        'gruppenleiter',
        'schichtleiter',
        'führungsverantwortung',
        'führungsposition',
        'leadership',
        'leitende angestellte',
        'management ebene',
      ],
    },
    {
      label: 'Vertriebler / Sales-Mitarbeiter',
      aliases: [
        'vertriebler',
        'verkäufer',
        'sales mitarbeiter',
        'außendienstler',
        'innendienstler',
        'account manager',
        'vertriebsmitarbeiter',
        'salesperson',
        'closer',
        'setter',
        'sales rep',
        'kundenberater',
        'vertriebsprofi',
        'sales professional',
        'kundenbetreuer',
        'akquisiteur',
        'vertriebsassistent',
        'sales agent',
        'handelsvertreter',
        'vertriebstalent',
      ],
    },
    {
      label: 'Selbständige / Solopreneure',
      aliases: [
        'selbständige',
        'selbständiger',
        'solopreneur',
        'freelancer',
        'freiberufler',
        'einzelunternehmer',
        'one man show',
        'solo selbständig',
        'freie berufe',
        'freiberuflich',
        'selbstständig',
        'soloselbständig',
        'freischaffend',
        'unabhängige',
        'freier mitarbeiter',
        'freier berater',
        'freier dienstleister',
        'kleinunternehmer',
        'mikroselbständig',
        'solo business',
      ],
    },
    {
      label: 'Existenzgründer',
      aliases: [
        'existenzgründer',
        'gründer',
        'startup gründer',
        'neu selbständig',
        'gründungsphase',
        'frisch gegründet',
        'business aufbau',
        'unternehmensstart',
        'gründungsinteressent',
        'angehender unternehmer',
        'pre revenue',
        'early stage',
        'gründungswillig',
        'geschäftsidee',
        'businessplan',
        'markteinsteiger',
        'neu am markt',
        'gründung',
        'erstgründer',
        'gründungsvorhaben',
      ],
    },
    {
      label: 'Angestellte / Mitarbeiter',
      aliases: [
        'angestellte',
        'mitarbeiter',
        'arbeitnehmer',
        'festangestellte',
        'beschäftigte',
        'belegschaft',
        'fachkraft',
        'facharbeiter',
        'büromitarbeiter',
        'sachbearbeiter',
        'angestellter',
        'vollzeitkraft',
        'teilzeitkraft',
        'werknehmerin',
        'werknehmer',
        'festanstellung',
        'festangestellter',
        'betriebsangehörige',
        'berufstätige',
        'erwerbstätige',
      ],
    },
    {
      label: 'Berufseinsteiger',
      aliases: [
        'berufseinsteiger',
        'absolvent',
        'hochschulabsolvent',
        'berufsanfänger',
        'junior',
        'einsteiger',
        'azubi',
        'auszubildender',
        'trainee',
        'werkstudent',
        'praktikant',
        'young professional',
        'karrierestart',
        'jobstart',
        'studienabgänger',
        'frischling',
        'karriereanfänger',
        'berufsstart',
        'erstes job',
        'schulabgänger',
      ],
    },
    {
      label: 'Business Opportunity Seeker',
      aliases: [
        'business opportunity',
        'nebeneinkommen',
        'zusatzeinkommen',
        'network marketing',
        'mlm',
        'direktvertrieb',
        'passives einkommen',
        'einkommensquelle',
        'geschäftsmöglichkeit',
        'affiliate',
        'nebenjob',
        'nebenberuflich',
        'finanzielle freiheit',
        'einkommensmöglichkeit',
        'opportunity seeker',
        'zweiteinnahme',
        'franchisepartner',
        'lizenzpartner',
        'vertriebspartner',
        'nebenverdienst',
      ],
    },
    {
      label: 'Investoren / Kapitalanleger',
      aliases: [
        'investor',
        'investoren',
        'kapitalanleger',
        'anleger',
        'vermögensinhaber',
        'privatinvestor',
        'immobilieninvestor',
        'aktienkäufer',
        'fondsinvestor',
        'renditeorientiert',
        'kapitalinteressent',
        'vermögensaufbau',
        'geldanleger',
        'sachwertanleger',
        'finanzinvestor',
        'businessangel',
        'business angel',
        'venture capital',
        'high net worth',
        'vermögender kunde',
      ],
    },
    {
      label: 'Sonstige Zielbranche',
      aliases: [
        'sonstiges',
        'sonstige branche',
        'diverses',
        'andere zielgruppe',
      ],
    },
  ],
} as const;




export const MARKET_TAXONOMY_DE = {
  "persona_role": {
    "Student": {
      "label": "Student",
      "aliases": [
        "student",
        "studentin",
        "studierender",
        "studierende",
        "hochschulstudent",
        "hochschulstudentin",
        "universitätsstudent",
        "universitaetsstudent",
        "universitätsstudentin",
        "universitaetsstudentin",
        "fh student",
        "fh studentin",
        "bachelor student",
        "bachelor studentin",
        "master student",
        "master studentin",
        "dualer student",
        "duale studentin",
        "werkstudent",
        "werkstudentin",
        "studium interessent",
        "studium interessentin"
      ]
    },
    "Schüler": {
      "label": "Schüler",
      "aliases": [
        "schüler",
        "schueler",
        "schülerin",
        "schuelerin",
        "schulkind",
        "jugendlicher schüler",
        "jugendliche schülerin",
        "jugendliche schuelerin",
        "gymnasiast",
        "gymnasiastin",
        "realschüler",
        "realschueler",
        "realschülerin",
        "realschuelerin",
        "hauptschüler",
        "hauptschueler",
        "hauptschülerin",
        "hauptschuelerin",
        "berufsschüler",
        "berufsschueler",
        "berufsschülerin",
        "berufsschuelerin"
      ]
    },
    "Auszubildender": {
      "label": "Auszubildender",
      "aliases": [
        "auszubildender",
        "auszubildende",
        "azubi",
        "azubine",
        "lehrling",
        "lehrmädchen",
        "lehrmaedchen",
        "ausbildungssuchender",
        "ausbildungssuchende",
        "berufseinsteiger ausbildung",
        "berufseinsteigerin ausbildung",
        "dualer ausbildungsteilnehmer",
        "duale ausbildungsteilnehmerin",
        "lehrstelle interessent",
        "lehrstelle interessentin",
        "azubi kandidat",
        "azubi kandidatin",
        "ausbildungsbewerber",
        "ausbildungsbewerberin",
        "nachwuchskraft ausbildung",
        "junior in ausbildung"
      ]
    },
    "Berufseinsteiger": {
      "label": "Berufseinsteiger",
      "aliases": [
        "berufseinsteiger",
        "berufseinsteigerin",
        "young professional",
        "young professional weiblich",
        "starter im beruf",
        "starterin im beruf",
        "karrierestarter",
        "karrierestarterin",
        "einsteiger arbeitsmarkt",
        "einsteigerin arbeitsmarkt",
        "erster job",
        "erste stelle",
        "jobstarter",
        "jobstarterin",
        "junior kandidat",
        "junior kandidatin",
        "nach dem studium",
        "nach der ausbildung",
        "frisch ausgelernt",
        "frisch absolviert",
        "berufsanfänger",
        "berufsanfaenger"
      ]
    },
    "Familienvater": {
      "label": "Familienvater",
      "aliases": [
        "familienvater",
        "vater",
        "papa",
        "dad",
        "vaterfigur",
        "mann mit kindern",
        "vater mit kindern",
        "berufstätiger vater",
        "berufstaetiger vater",
        "arbeitender vater",
        "alleinerziehender vater",
        "junger vater",
        "erfahrener vater",
        "mehrfacher vater",
        "vater einer familie",
        "haushaltsvater",
        "familienoberhaupt männlich",
        "familienoberhaupt maennlich",
        "sorgeberechtigter vater",
        "erziehungsberechtigter vater",
        "vater als kunde",
        "vater als käufer",
        "vater als kaeuufer",
        "vater als entscheider im alltag",
        "familienorientierter mann"
      ]
    },
    "Familienmutter": {
      "label": "Familienmutter",
      "aliases": [
        "familienmutter",
        "mutter",
        "mama",
        "mom",
        "mutterfigur",
        "frau mit kindern",
        "mutter mit kindern",
        "berufstätige mutter",
        "berufstaetige mutter",
        "arbeitende mutter",
        "alleinerziehende mutter",
        "junge mutter",
        "erfahrene mutter",
        "mehrfache mutter",
        "mutter einer familie",
        "haushaltsmutter",
        "familienoberhaupt weiblich",
        "sorgeberechtigte mutter",
        "erziehungsberechtigte mutter",
        "mutter als kundin",
        "mutter als käuferin",
        "mutter als kaeuuferin",
        "mutter als entscheiderin im alltag",
        "familienorientierte frau"
      ]
    },
    "Alleinerziehender Vater": {
      "label": "Alleinerziehender Vater",
      "aliases": [
        "alleinerziehender vater",
        "single dad",
        "single father",
        "vater allein mit kind",
        "vater allein mit kindern",
        "alleinstehender vater",
        "getrennter vater",
        "geschiedener vater",
        "vater mit alleiniger verantwortung",
        "vater mit sorgerecht",
        "vater mit alltagsverantwortung",
        "vater ohne partnerin",
        "vater ohne partner",
        "alleinverantwortlicher vater",
        "vater als hauptbetreuer",
        "vater als primäre bezugsperson",
        "vater als primaere bezugsperson",
        "vater im wechselmodell",
        "vater mit kind im haushalt",
        "alleinerziehender papa"
      ]
    },
    "Alleinerziehende Mutter": {
      "label": "Alleinerziehende Mutter",
      "aliases": [
        "alleinerziehende mutter",
        "single mom",
        "single mother",
        "mutter allein mit kind",
        "mutter allein mit kindern",
        "alleinstehende mutter",
        "getrennte mutter",
        "geschiedene mutter",
        "mutter mit alleiniger verantwortung",
        "mutter mit sorgerecht",
        "mutter mit alltagsverantwortung",
        "mutter ohne partner",
        "mutter ohne partnerin",
        "alleinverantwortliche mutter",
        "mutter als hauptbetreuerin",
        "mutter als primäre bezugsperson",
        "mutter als primaere bezugsperson",
        "mutter im wechselmodell",
        "mutter mit kind im haushalt",
        "alleinerziehende mama"
      ]
    },
    "Geschäftsführer": {
      "label": "Geschäftsführer",
      "aliases": [
        "geschäftsführer",
        "geschaeftsfuehrer",
        "gf",
        "chef der firma",
        "firmenchef",
        "unternehmenschef",
        "geschäftsleitung",
        "geschaeftsleitung",
        "leiter der gmbh",
        "gmbh geschäftsführer",
        "geschäftsführer mittelstand",
        "geschaeftsfuehrer mittelstand",
        "geschäftsführer kmu",
        "geschaeftsfuehrer kmu",
        "operativer geschäftsführer",
        "operativer geschaeftsfuehrer",
        "verantwortlicher geschäftsführer",
        "verantwortlicher geschaeftsfuehrer",
        "alleiniger geschäftsführer",
        "alleiniger geschaeftsfuehrer"
      ]
    },
    "Angestellter Geschäftsführer": {
      "label": "Angestellter Geschäftsführer",
      "aliases": [
        "angestellter geschäftsführer",
        "angestellter geschaeftsfuehrer",
        "fremdgeschäftsführer",
        "fremdgeschaeftsfuehrer",
        "geschäftsführer ohne anteile",
        "geschaeftsfuehrer ohne anteile",
        "eingesetzter geschäftsführer",
        "eingesetzter geschaeftsfuehrer",
        "management geschäftsführer",
        "management geschaeftsfuehrer",
        "externer geschäftsführer",
        "externer geschaeftsfuehrer",
        "geschäftsführer nicht inhaber",
        "geschaeftsfuehrer nicht inhaber",
        "professioneller geschäftsführer",
        "professioneller geschaeftsfuehrer",
        "manager als geschäftsführer",
        "manager als geschaeftsfuehrer",
        "gf ohne gesellschaftsanteile",
        "geschaeftsfuehrer ohne eigentum"
      ]
    },
    "Geschäftsführender Gesellschafter": {
      "label": "Geschäftsführender Gesellschafter",
      "aliases": [
        "geschäftsführender gesellschafter",
        "geschaeftsfuehrender gesellschafter",
        "gesellschafter geschäftsführer",
        "gesellschafter geschaeftsfuehrer",
        "geschäftsführer mit anteilen",
        "geschaeftsfuehrer mit anteilen",
        "geschäftsführer und gesellschafter",
        "geschaeftsfuehrer und gesellschafter",
        "inhaber geschäftsführer",
        "inhaber geschaeftsfuehrer",
        "mitinhaber geschäftsführer",
        "mitinhaber geschaeftsfuehrer",
        "geschäftsführender mitinhaber",
        "geschaeftsfuehrender mitinhaber",
        "unternehmerischer geschäftsführer",
        "unternehmerischer geschaeftsfuehrer",
        "gesellschafter der operativ führt",
        "gesellschafter der operativ fuehrt",
        "owner managing director",
        "gesellschafter in der leitung"
      ]
    },
    "Inhaber": {
      "label": "Inhaber",
      "aliases": [
        "inhaber",
        "inhaberin",
        "firmeninhaber",
        "firmeninhaberin",
        "geschäftsinhaber",
        "geschaeftsinhaber",
        "geschäftsinhaberin",
        "geschaeftsinhaberin",
        "unternehmensinhaber",
        "unternehmensinhaberin",
        "betriebseigentümer",
        "betriebseigentuemer",
        "betriebseigentümerin",
        "betriebseigentuemerin",
        "owner",
        "business owner",
        "besitzer des betriebs",
        "besitzerin des betriebs",
        "eigentümer der firma",
        "eigentuemer der firma"
      ]
    },
    "Alleininhaber": {
      "label": "Alleininhaber",
      "aliases": [
        "alleininhaber",
        "alleininhaberin",
        "einziger inhaber",
        "einzige inhaberin",
        "alleineigentümer",
        "alleineigentuemer",
        "alleineigentümerin",
        "alleineigentuemerin",
        "solo inhaber",
        "solo inhaberin",
        "firma gehört ihm allein",
        "firma gehoert ihm allein",
        "firma gehört ihr allein",
        "firma gehoert ihr allein",
        "alleiniger eigentümer",
        "alleiniger eigentuemer",
        "alleinige eigentümerin",
        "alleinige eigentuemerin",
        "single owner",
        "sole owner"
      ]
    },
    "Mitinhaber": {
      "label": "Mitinhaber",
      "aliases": [
        "mitinhaber",
        "mitinhaberin",
        "teilhaber",
        "teilhaberin",
        "miteigentümer",
        "miteigentuemer",
        "miteigentümerin",
        "miteigentuemerin",
        "partner im unternehmen",
        "partnerin im unternehmen",
        "anteilseigner im betrieb",
        "anteilseignerin im betrieb",
        "geschäftspartner mit anteilen",
        "geschaeftspartner mit anteilen",
        "geschäftspartnerin mit anteilen",
        "geschaeftspartnerin mit anteilen",
        "co owner",
        "co-owner",
        "co inhaber",
        "co inhaberin"
      ]
    },
    "Gründer": {
      "label": "Gründer",
      "aliases": [
        "gründer",
        "gruender",
        "gründerin",
        "gruenderin",
        "founder",
        "startup gründer",
        "startup gruender",
        "startup gründerin",
        "startup gruenderin",
        "unternehmensgründer",
        "unternehmensgruender",
        "unternehmensgründerin",
        "unternehmensgruenderin",
        "firmengründer",
        "firmengruender",
        "firmengründerin",
        "firmengruenderin",
        "gründender inhaber",
        "gruendender inhaber",
        "founding entrepreneur"
      ]
    },
    "Mitgründer": {
      "label": "Mitgründer",
      "aliases": [
        "mitgründer",
        "mitgruender",
        "mitgründerin",
        "mitgruenderin",
        "co founder",
        "co-founder",
        "cofounder",
        "co founderin",
        "co-founderin",
        "founding partner",
        "gründungspartner",
        "gruendungspartner",
        "gründungspartnerin",
        "gruendungspartnerin",
        "zweiter gründer",
        "zweiter gruender",
        "zweite gründerin",
        "zweite gruenderin",
        "mitglied des gründungsteams",
        "mitglied des gruendungsteams"
      ]
    },
    "CEO": {
      "label": "CEO",
      "aliases": [
        "ceo",
        "chief executive officer",
        "company ceo",
        "startup ceo",
        "konzern ceo",
        "scaleup ceo",
        "deutscher ceo",
        "ceo der firma",
        "ceo des unternehmens",
        "ceo mittelstand",
        "ceo saas",
        "ceo softwareunternehmen",
        "ceo agentur",
        "ceo gruppe",
        "ceo holding",
        "ceo dachgesellschaft",
        "ceo business unit",
        "ceo region",
        "ceo international",
        "chief executive"
      ]
    },
    "CFO": {
      "label": "CFO",
      "aliases": [
        "cfo",
        "chief financial officer",
        "finanzvorstand",
        "finanzchef",
        "finanzchefin",
        "leiter finanzen",
        "leiterin finanzen",
        "head of finance",
        "finance director",
        "finanzdirektor",
        "finanzdirektorin",
        "kaufmännischer leiter",
        "kaufmaennischer leiter",
        "kaufmännische leiterin",
        "kaufmaennische leiterin",
        "leitung finanzabteilung",
        "finanzverantwortlicher",
        "finanzverantwortliche",
        "commercial finance lead",
        "finance executive"
      ]
    },
    "CTO": {
      "label": "CTO",
      "aliases": [
        "cto",
        "chief technology officer",
        "technologiechef",
        "technologiechefin",
        "leiter technologie",
        "leiterin technologie",
        "head of technology",
        "tech chief",
        "technischer vorstand",
        "technische vorständin",
        "technische vorstaendin",
        "technischer geschäftsleiter",
        "technischer geschaeftsleiter",
        "technische geschäftsleiterin",
        "technische geschaeftsleiterin",
        "software cto",
        "saas cto",
        "produkttechnischer leiter",
        "produkttechnische leiterin",
        "technology director"
      ]
    },
    "COO": {
      "label": "COO",
      "aliases": [
        "coo",
        "chief operating officer",
        "operations chief",
        "operations chef",
        "operations chefin",
        "leiter operations",
        "leiterin operations",
        "head of operations",
        "operative geschäftsleitung",
        "operative geschaeftsleitung",
        "operativer vorstand",
        "operative vorständin",
        "operative vorstaendin",
        "betriebsverantwortlicher auf c level",
        "betriebsverantwortliche auf c level",
        "operations director",
        "chief operations officer",
        "operative executive rolle",
        "coo mittelstand",
        "coo scaleup"
      ]
    },
    "CIO": {
      "label": "CIO",
      "aliases": [
        "cio",
        "chief information officer",
        "it vorstand",
        "it-vorstand",
        "informationschef",
        "informationschefin",
        "leiter informationssysteme",
        "leiterin informationssysteme",
        "head of information systems",
        "digital information officer",
        "cio mittelstand",
        "cio konzern",
        "cio gruppe",
        "it executive",
        "information technology executive",
        "digitalisierungsvorstand",
        "digitalisierungsvorständin",
        "digitalisierungsvorstaendin",
        "enterprise it chief",
        "informationssystem leitung"
      ]
    },
    "CMO": {
      "label": "CMO",
      "aliases": [
        "cmo",
        "chief marketing officer",
        "marketingvorstand",
        "marketingchef",
        "marketingchefin",
        "leiter marketing",
        "leiterin marketing",
        "head of marketing",
        "marketing director",
        "brand executive",
        "growth executive",
        "marketing executive",
        "commercial marketing lead",
        "demand generation executive",
        "performance marketing executive",
        "kommunikationschef",
        "kommunikationschefin",
        "markenchef",
        "markenchefin",
        "cmo mittelstand"
      ]
    },
    "CHRO": {
      "label": "CHRO",
      "aliases": [
        "chro",
        "chief human resources officer",
        "personalvorstand",
        "hr vorstand",
        "hr-vorstand",
        "personalchef",
        "personalchefin",
        "leiter human resources",
        "leiterin human resources",
        "head of people",
        "people executive",
        "hr executive",
        "chief people officer",
        "cpo people",
        "personal executive",
        "people and culture lead",
        "leiter people culture",
        "leiterin people culture",
        "human capital officer",
        "chief talent officer"
      ]
    },
    "Chief Revenue Officer": {
      "label": "Chief Revenue Officer",
      "aliases": [
        "chief revenue officer",
        "cro",
        "revenue chief",
        "revenue chef",
        "revenue chefin",
        "umsatzchef",
        "umsatzchefin",
        "leiter revenue",
        "leiterin revenue",
        "head of revenue",
        "revenue executive",
        "go to market executive",
        "gtm executive",
        "commercial revenue lead",
        "umsatzverantwortlicher c level",
        "umsatzverantwortliche c level",
        "cro saas",
        "cro scaleup",
        "revenue operations executive",
        "chief sales revenue officer"
      ]
    },
    "Chief Sales Officer": {
      "label": "Chief Sales Officer",
      "aliases": [
        "chief sales officer",
        "cso sales",
        "sales chief",
        "vertriebsvorstand",
        "vertriebschef",
        "vertriebschefin",
        "leiter vertrieb c level",
        "leiterin vertrieb c level",
        "head of sales executive",
        "sales executive",
        "commercial sales chief",
        "verkaufsvorstand",
        "verkaufschef",
        "verkaufschefin",
        "chief commercial sales officer",
        "sales officer",
        "cso vertrieb",
        "global sales chief",
        "regional sales chief",
        "vertriebsleitung c suite"
      ]
    },
    "Chief Product Officer": {
      "label": "Chief Product Officer",
      "aliases": [
        "chief product officer",
        "cpo product",
        "produktchef",
        "produktchefin",
        "produktvorstand",
        "leiter produkt",
        "leiterin produkt",
        "head of product",
        "product director",
        "product executive",
        "produktverantwortlicher c level",
        "produktverantwortliche c level",
        "produktstrategie leitung",
        "product strategy lead",
        "chief product manager",
        "cpo software",
        "cpo saas",
        "cpo plattform",
        "produktportfolio leitung",
        "product owner executive"
      ]
    },
    "Geschäftsleitungsmitglied": {
      "label": "Geschäftsleitungsmitglied",
      "aliases": [
        "geschäftsleitungsmitglied",
        "geschaeftsleitungsmitglied",
        "mitglied der geschäftsleitung",
        "mitglied der geschaeftsleitung",
        "leitungskreis mitglied",
        "mitglied im leitungskreis",
        "executive team member",
        "management team mitglied",
        "mitglied des managementteams",
        "erweiterte geschäftsleitung",
        "erweiterte geschaeftsleitung",
        "führungskreis mitglied",
        "fuehrungskreis mitglied",
        "leitungsteam person",
        "management board member",
        "leitungsebene person",
        "senior management mitglied",
        "executive committee member",
        "mitglied im executive committee",
        "person aus der unternehmensleitung"
      ]
    },
    "Beirat": {
      "label": "Beirat",
      "aliases": [
        "beirat",
        "beirätin",
        "beiraetin",
        "beiratsmitglied",
        "mitglied des beirats",
        "strategischer beirat",
        "advisory board member",
        "advisory board",
        "berater im beirat",
        "beraterin im beirat",
        "unternehmensbeirat",
        "familienbeirat",
        "startup beirat",
        "investorenbeirat",
        "fachbeirat",
        "aufsicht naher beirat",
        "externer beirat",
        "unabhängiger beirat",
        "unabhaengiger beirat",
        "senior advisor beirat"
      ]
    },
    "Aufsichtsrat": {
      "label": "Aufsichtsrat",
      "aliases": [
        "aufsichtsrat",
        "aufsichtsrätin",
        "aufsichtsraetin",
        "aufsichtsratsmitglied",
        "mitglied des aufsichtsrats",
        "kontrollgremium mitglied",
        "supervisory board member",
        "supervisory board",
        "aufsichtsratsvorsitzender",
        "aufsichtsratsvorsitzende",
        "kontrollorgan person",
        "gesellschaftsaufsicht",
        "gremienaufsicht",
        "board oversight member",
        "aufsichtsrat der ag",
        "aufsichtsrat der gmbh",
        "kontrollrat",
        "mitglied kontrollrat",
        "corporate governance board",
        "non executive board member"
      ]
    },
    "Gesellschafter": {
      "label": "Gesellschafter",
      "aliases": [
        "gesellschafter",
        "gesellschafterin",
        "anteilseigner",
        "anteilseignerin",
        "shareholder",
        "gesellschafter ohne geschäftsführung",
        "gesellschafter ohne geschaeftsfuehrung",
        "passiver gesellschafter",
        "passive gesellschafterin",
        "kapitalgesellschafter",
        "kapitalgesellschafterin",
        "investierter gesellschafter",
        "investierte gesellschafterin",
        "gesellschafterkreis person",
        "person im gesellschafterkreis",
        "mitgesellschafter",
        "mitgesellschafterin",
        "shareholder representative",
        "equity holder",
        "anteilsinhaber"
      ]
    },
    "Investor": {
      "label": "Investor",
      "aliases": [
        "investor",
        "investorin",
        "kapitalgeber",
        "kapitalgeberin",
        "geldgeber",
        "geldgeberin",
        "finanzierer",
        "finanziererin",
        "wagniskapitalgeber",
        "wagniskapitalgeberin",
        "venture capital investor",
        "vc investor",
        "private equity investor",
        "pe investor",
        "business angel",
        "angel investor",
        "lead investor",
        "strategischer investor",
        "strategische investorin",
        "family office investor"
      ]
    },
    "Vertriebsleiter": {
      "label": "Vertriebsleiter",
      "aliases": [
        "vertriebsleiter",
        "vertriebsleiterin",
        "vertriebsleitung",
        "head of sales",
        "sales director",
        "sales lead",
        "leiter vertrieb",
        "leiterin vertrieb",
        "verkaufsleiter",
        "verkaufsleiterin",
        "verkaufsleitung",
        "regionaler vertriebsleiter",
        "regionale vertriebsleiterin",
        "national sales manager",
        "b2b vertriebsleiter",
        "b2b vertriebsleiterin",
        "field sales leiter",
        "field sales leiterin",
        "sales team lead",
        "vertriebsteam leitung"
      ]
    },
    "Key-Account-Manager": {
      "label": "Key-Account-Manager",
      "aliases": [
        "key account manager",
        "key-account-manager",
        "kam",
        "key account betreuer",
        "key account betreuerin",
        "großkundenbetreuer",
        "grosskundenbetreuer",
        "großkundenbetreuerin",
        "grosskundenbetreuerin",
        "strategischer kundenbetreuer",
        "strategische kundenbetreuerin",
        "enterprise account manager",
        "major account manager",
        "global account manager",
        "national account manager",
        "bestandskunden account manager",
        "top kunden betreuer",
        "top kunden betreuerin",
        "account lead",
        "key client manager"
      ]
    },
    "Account-Executive": {
      "label": "Account-Executive",
      "aliases": [
        "account executive",
        "ae",
        "vertriebsmitarbeiter abschluss",
        "vertriebsmitarbeiterin abschluss",
        "closing sales rep",
        "closer sales",
        "neukundenvertriebler",
        "neukundenvertrieblerin",
        "b2b sales rep",
        "quota carrying sales rep",
        "abschlussverkäufer",
        "abschlussverkaeufer",
        "abschlussverkäuferin",
        "abschlussverkaeuferin",
        "enterprise sales rep",
        "mid market sales rep",
        "inside sales executive",
        "field account executive",
        "new business account executive",
        "account executive neukunden",
        "account executive b2b",
        "account executive closing",
        "ae vertrieb",
        "ae neukundengewinnung"
      ]
    },
    "Business-Development-Manager": {
      "label": "Business-Development-Manager",
      "aliases": [
        "business development manager",
        "bdm",
        "bizdev manager",
        "geschäftsentwicklung",
        "geschaeftsentwicklung",
        "leiter business development",
        "business developer",
        "business developerin",
        "partner development manager",
        "neugeschäft entwickler",
        "neugeschaeft entwickler",
        "market development manager",
        "strategic partnerships manager",
        "alliance manager",
        "vertriebsentwicklung",
        "wachstumsentwicklung",
        "new market manager",
        "growth partnerships",
        "commercial development manager",
        "business expansion manager"
      ]
    },
    "Sales-Development-Representative": {
      "label": "Sales-Development-Representative",
      "aliases": [
        "sales development representative",
        "sdr",
        "vertriebsentwickler",
        "vertriebsentwicklerin",
        "lead qualifier",
        "prospecting rep",
        "outbound rep",
        "inbound sdr",
        "outbound sdr",
        "terminierer",
        "terminiererin",
        "erstkontakt vertrieb",
        "cold calling rep",
        "akquise mitarbeiter",
        "akquise mitarbeiterin",
        "lead generation rep",
        "pre sales development",
        "sales appointment setter",
        "terminvereinbarer",
        "vorqualifizierung vertrieb"
      ]
    },
    "Marketingleiter": {
      "label": "Marketingleiter",
      "aliases": [
        "marketingleiter",
        "marketingleiterin",
        "marketingleitung",
        "online marketingleiter",
        "online marketingleiterin",
        "performance marketing leiter",
        "performance marketing leiterin",
        "brand marketing leiter",
        "brand marketing leiterin",
        "growth marketing leiter",
        "growth marketing leiterin",
        "kommunikationsleiter",
        "kommunikationsleiterin",
        "marketingverantwortlicher",
        "marketingverantwortliche",
        "marketing team lead",
        "marketing abteilungsleiter",
        "marketing abteilungsleiterin",
        "marketing head mittelstand",
        "leitung online marketing",
        "leiterin online marketing",
        "marketing department head"
      ]
    },
    "Performance-Marketing-Manager": {
      "label": "Performance-Marketing-Manager",
      "aliases": [
        "performance marketing manager",
        "paid ads manager",
        "paid media manager",
        "sea manager",
        "google ads manager",
        "meta ads manager",
        "facebook ads manager",
        "online advertising manager",
        "growth ads manager",
        "performance marketer",
        "performance marketerin",
        "kampagnenmanager performance",
        "kampagnenmanagerin performance",
        "paid social manager",
        "paid search manager",
        "acquisition manager",
        "digital acquisition manager",
        "media buying manager",
        "traffic manager",
        "conversion marketing manager"
      ]
    },
    "Brand-Manager": {
      "label": "Brand-Manager",
      "aliases": [
        "brand manager",
        "markenmanager",
        "markenmanagerin",
        "brand lead",
        "markenverantwortlicher",
        "markenverantwortliche",
        "brand strategist",
        "markenstratege",
        "markenstrategin",
        "corporate brand manager",
        "employer brand manager",
        "brand communication manager",
        "markenkommunikation",
        "brand owner",
        "positionierung manager",
        "positionierungsmanager",
        "positionierungsmanagerin",
        "brand marketing manager",
        "brand custodian",
        "markenpflege verantwortliche person"
      ]
    },
    "HR-Leiter": {
      "label": "HR-Leiter",
      "aliases": [
        "hr leiter",
        "hr-leiter",
        "hr leiterin",
        "hr-leiterin",
        "personalleiter",
        "personalleiterin",
        "personalleitung",
        "head of hr",
        "human resources leitung",
        "leiter personal",
        "leiterin personal",
        "hr manager leitung",
        "personalchef unter c level",
        "personalchefin unter c level",
        "people operations leitung",
        "people and culture leitung",
        "hr verantwortlicher",
        "hr verantwortliche",
        "personalabteilungsleiter",
        "personalabteilungsleiterin"
      ]
    },
    "Recruiter": {
      "label": "Recruiter",
      "aliases": [
        "recruiter",
        "recruiterin",
        "personalrecruiter",
        "personalrecruiterin",
        "talent acquisition specialist",
        "talent acquisition manager",
        "inhouse recruiter",
        "corporate recruiter",
        "sourcing recruiter",
        "active sourcer",
        "active sourcerin",
        "recruiting specialist",
        "recruiting manager operative",
        "bewerbermanager",
        "bewerbermanagerin",
        "kandidatenmanager",
        "kandidatenmanagerin",
        "personalgewinnung spezialist",
        "personalgewinnung spezialistin",
        "fachkräfte recruiter"
      ]
    },
    "Ausbildungsleiter": {
      "label": "Ausbildungsleiter",
      "aliases": [
        "ausbildungsleiter",
        "ausbildungsleiterin",
        "leiter ausbildung",
        "leiterin ausbildung",
        "azubi verantwortlicher",
        "azubi verantwortliche",
        "ausbildungsverantwortlicher",
        "ausbildungsverantwortliche",
        "trainer ausbildung",
        "trainerin ausbildung",
        "berufsbildungsleiter",
        "berufsbildungsleiterin",
        "nachwuchsleitung",
        "nachwuchsleiter",
        "nachwuchsleiterin",
        "ausbilder koordinierend",
        "ausbilderin koordinierend",
        "dual study coordinator",
        "lehrlingswart",
        "lehrlingswartin"
      ]
    },
    "Personalsachbearbeiter": {
      "label": "Personalsachbearbeiter",
      "aliases": [
        "personalsachbearbeiter",
        "personalsachbearbeiterin",
        "hr sachbearbeiter",
        "hr sachbearbeiterin",
        "personaladministration",
        "personaladmin",
        "hr administration",
        "lohn personal sachbearbeiter",
        "lohn personal sachbearbeiterin",
        "personalservice mitarbeiter",
        "personalservice mitarbeiterin",
        "mitarbeiter personalabteilung",
        "mitarbeiterin personalabteilung",
        "hr coordinator",
        "personal koordinator",
        "personal koordinatoriN",
        "employee services",
        "hr backoffice",
        "personal backoffice",
        "hr operations sachbearbeitung"
      ]
    },
    "Einkaufsleiter": {
      "label": "Einkaufsleiter",
      "aliases": [
        "einkaufsleiter",
        "einkaufsleiterin",
        "leitung einkauf",
        "leiter einkauf",
        "leiterin einkauf",
        "head of procurement",
        "procurement director",
        "purchasing director",
        "strategischer einkaufsleiter",
        "strategische einkaufsleiterin",
        "chief procurement unter c level",
        "beschaffungsleiter",
        "beschaffungsleiterin",
        "sourcing leiter",
        "sourcing leiterin",
        "lieferantenmanagement leitung",
        "category procurement lead",
        "einkaufsverantwortlicher",
        "einkaufsverantwortliche",
        "purchasing lead"
      ]
    },
    "Einkäufer": {
      "label": "Einkäufer",
      "aliases": [
        "einkäufer",
        "einkaeufer",
        "einkäuferin",
        "einkaeuferin",
        "operativer einkäufer",
        "operativer einkaeufer",
        "operative einkäuferin",
        "operative einkaeuferin",
        "strategischer einkäufer",
        "strategischer einkaeufer",
        "strategische einkäuferin",
        "strategische einkaeuferin",
        "procurement manager",
        "purchasing manager",
        "buyer",
        "sourcing specialist",
        "category buyer",
        "technical buyer",
        "projekteinkäufer",
        "projekteinkaeufer"
      ]
    },
    "Controller": {
      "label": "Controller",
      "aliases": [
        "controller",
        "controllerin",
        "controlling manager",
        "financial controller",
        "business controller",
        "vertriebscontroller",
        "vertriebscontrollerin",
        "produktionscontroller",
        "produktionscontrollerin",
        "kostencontroller",
        "kostencontrollerin",
        "commercial controller",
        "finance analyst",
        "financial analyst",
        "controlling specialist",
        "budget controller",
        "reporting controller",
        "wertekontrolle finance",
        "kennzahlen controller",
        "performance controller"
      ]
    },
    "Buchhalter": {
      "label": "Buchhalter",
      "aliases": [
        "buchhalter",
        "buchhalterin",
        "finanzbuchhalter",
        "finanzbuchhalterin",
        "bilanzbuchhalter",
        "bilanzbuchhalterin",
        "accountant",
        "accounting specialist",
        "rechnungswesen mitarbeiter",
        "rechnungswesen mitarbeiterin",
        "kreditorenbuchhalter",
        "kreditorenbuchhalterin",
        "debitorenbuchhalter",
        "debitorenbuchhalterin",
        "lohnbuchhalter",
        "lohnbuchhalterin",
        "buchhaltung leitung operativ",
        "buchhaltungsmanager",
        "buchhaltungsmanagerin",
        "accounting manager"
      ]
    },
    "IT-Leiter": {
      "label": "IT-Leiter",
      "aliases": [
        "it leiter",
        "it-leiter",
        "it leiterin",
        "it-leiterin",
        "leitung it",
        "leiter it",
        "leiterin it",
        "head of it",
        "it manager",
        "it director",
        "infrastrukturleiter it",
        "infrastrukturleiterin it",
        "leiter informationstechnik",
        "leiterin informationstechnik",
        "systeme leitung",
        "it verantwortlicher",
        "it verantwortliche",
        "edv leiter",
        "edv leiterin",
        "it abteilungsleiter"
      ]
    },
    "Systemadministrator": {
      "label": "Systemadministrator",
      "aliases": [
        "systemadministrator",
        "systemadministratorin",
        "sysadmin",
        "it administrator",
        "it administratorin",
        "netzwerkadministrator",
        "netzwerkadministratorin",
        "server administrator",
        "server administratorin",
        "windows administrator",
        "linux administrator",
        "infrastruktur administrator",
        "infrastruktur administratorin",
        "cloud administrator",
        "cloud administratorin",
        "it betrieb administrator",
        "it betrieb administratorin",
        "admin it",
        "administrator it",
        "administratorin it"
      ]
    },
    "Softwareentwickler": {
      "label": "Softwareentwickler",
      "aliases": [
        "softwareentwickler",
        "softwareentwicklerin",
        "developer",
        "entwickler",
        "entwicklerin",
        "frontend entwickler",
        "frontend entwicklerin",
        "backend entwickler",
        "backend entwicklerin",
        "fullstack entwickler",
        "fullstack entwicklerin",
        "software engineer",
        "software engineerin",
        "programmer",
        "programmierer",
        "programmiererin",
        "webentwickler",
        "webentwicklerin",
        "app entwickler",
        "app entwicklerin"
      ]
    },
    "Produktmanager": {
      "label": "Produktmanager",
      "aliases": [
        "produktmanager",
        "produktmanagerin",
        "product manager",
        "product owner",
        "produkt owner",
        "produktverantwortlicher",
        "produktverantwortliche",
        "produktstratege",
        "produktstrategin",
        "product lead",
        "product strategist",
        "product portfolio manager",
        "produktportfolio manager",
        "produktentwicklung manager",
        "produktentwicklung managerin",
        "produktplanung",
        "produktplaner",
        "produktplanerin",
        "roadmap owner",
        "produkt roadmap owner"
      ]
    },
    "Projektleiter": {
      "label": "Projektleiter",
      "aliases": [
        "projektleiter",
        "projektleiterin",
        "project manager",
        "projektmanager",
        "projektmanagerin",
        "programmleiter",
        "programmleiterin",
        "program manager",
        "projektverantwortlicher",
        "projektverantwortliche",
        "pm",
        "projektkoordination leitung",
        "projektkoordinator leitung",
        "projektkoordinatorin leitung",
        "kundenprojektleiter",
        "kundenprojektleiterin",
        "technischer projektleiter",
        "technische projektleiterin",
        "implementierungsprojektleiter",
        "implementierungsprojektleiterin"
      ]
    },
    "Operations-Leiter": {
      "label": "Operations-Leiter",
      "aliases": [
        "operations leiter",
        "operations leiterin",
        "operations manager leitung",
        "operative leitung",
        "betriebsorganisation leitung",
        "prozessleitung operations",
        "service operations leitung",
        "delivery operations leitung",
        "business operations manager",
        "operativer manager",
        "operative managerin",
        "operativer bereichsleiter",
        "operative bereichsleiterin",
        "operations verantwortlicher",
        "operations verantwortliche",
        "ablaufverantwortlicher",
        "ablaufverantwortliche",
        "operations abteilungsleiter",
        "operations abteilungsleiterin",
        "leitung operative prozesse",
        "leiter operative prozesse"
      ]
    },
    "Betriebsleiter": {
      "label": "Betriebsleiter",
      "aliases": [
        "betriebsleiter",
        "betriebsleiterin",
        "leitung betrieb",
        "leiter betrieb",
        "leiterin betrieb",
        "plant manager",
        "werksnaher betriebsleiter",
        "werksnahe betriebsleiterin",
        "betrieb verantwortlich",
        "betrieb verantwortliche",
        "standortbetrieb leiter",
        "standortbetrieb leiterin",
        "operative betriebsführung",
        "operative betriebsfuehrung",
        "betriebsführung person",
        "betriebsfuehrung person",
        "technischer betriebsleiter",
        "technische betriebsleiterin",
        "betriebschef",
        "betriebschefin"
      ]
    },
    "Standortleiter": {
      "label": "Standortleiter",
      "aliases": [
        "standortleiter",
        "standortleiterin",
        "leitung standort",
        "leiter standort",
        "leiterin standort",
        "site manager",
        "site lead",
        "niederlassungsleiter",
        "niederlassungsleiterin",
        "branch manager",
        "standortverantwortlicher",
        "standortverantwortliche",
        "regional standortleiter",
        "regionale standortleiterin",
        "lokaler standortleiter",
        "lokale standortleiterin",
        "facility standortleiter",
        "facility standortleiterin",
        "werksstandort leitung",
        "betriebsstandort leitung"
      ]
    },
    "Filialleiter": {
      "label": "Filialleiter",
      "aliases": [
        "filialleiter",
        "filialleiterin",
        "store manager",
        "shop manager",
        "ladenleiter",
        "ladenleiterin",
        "marktleiter",
        "marktleiterin",
        "retail manager",
        "retail leitung",
        "standortleiter einzelhandel",
        "standortleiterin einzelhandel",
        "store lead",
        "filialverantwortlicher",
        "filialverantwortliche",
        "bereichsleiter filiale",
        "bereichsleiterin filiale",
        "verkaufsstellenleiter",
        "verkaufsstellenleiterin",
        "geschäftsstellenleiter retail"
      ]
    },
    "Teamleiter": {
      "label": "Teamleiter",
      "aliases": [
        "teamleiter",
        "teamleiterin",
        "teamleitung",
        "team lead",
        "gruppenleiter",
        "gruppenleiterin",
        "schichtleiter",
        "schichtleiterin",
        "leiter team",
        "leiterin team",
        "supervisor",
        "vorgesetzter team",
        "vorgesetzte team",
        "operative führungskraft",
        "operative fuehrungskraft",
        "mittleres management team",
        "koordiniert team",
        "teamverantwortlicher",
        "teamverantwortliche",
        "people lead kleinteam"
      ]
    },
    "Abteilungsleiter": {
      "label": "Abteilungsleiter",
      "aliases": [
        "abteilungsleiter",
        "abteilungsleiterin",
        "abteilungsleitung",
        "department head",
        "head of department",
        "bereichsnaher abteilungsleiter",
        "bereichsnahe abteilungsleiterin",
        "leiter abteilung",
        "leiterin abteilung",
        "fachabteilungsleiter",
        "fachabteilungsleiterin",
        "abteilungsverantwortlicher",
        "abteilungsverantwortliche",
        "middle management department",
        "department manager",
        "bereichsabteilung leitung",
        "fachbereichsleitung",
        "funktionale abteilungsleitung",
        "linienstellenleiter",
        "linienstellenleiterin"
      ]
    },
    "Bereichsleiter": {
      "label": "Bereichsleiter",
      "aliases": [
        "bereichsleiter",
        "bereichsleiterin",
        "bereichsleitung",
        "division head",
        "head of division",
        "business unit manager",
        "business unit lead",
        "leiter bereich",
        "leiterin bereich",
        "regionaler bereichsleiter",
        "regionale bereichsleiterin",
        "fachbereichsleiter",
        "fachbereichsleiterin",
        "spartenleiter",
        "spartenleiterin",
        "bereichsverantwortlicher",
        "bereichsverantwortliche",
        "senior manager bereich",
        "division manager",
        "unit manager"
      ]
    },
    "Assistent der Geschäftsführung": {
      "label": "Assistent der Geschäftsführung",
      "aliases": [
        "assistent der geschäftsführung",
        "assistent der geschaeftsfuehrung",
        "assistant to ceo",
        "geschäftsführungsassistent",
        "geschaeftsfuehrungsassistent",
        "executive assistant männlich",
        "executive assistant maennlich",
        "persönlicher assistent geschäftsführer",
        "persoenlicher assistent geschaeftsfuehrer",
        "assistenz vom chef männlich",
        "assistenz vom chef maennlich",
        "rechte hand des geschäftsführers",
        "rechte hand des geschaeftsfuehrers",
        "vorzimmer assistent",
        "management assistent",
        "ceo assistent",
        "gf assistent",
        "leiter assistenz geschäftsführung",
        "leiter assistenz geschaeftsfuehrung",
        "terminassistent geschäftsführung"
      ]
    },
    "Assistentin der Geschäftsführung": {
      "label": "Assistentin der Geschäftsführung",
      "aliases": [
        "assistentin der geschäftsführung",
        "assistentin der geschaeftsfuehrung",
        "assistant to ceo weiblich",
        "geschäftsführungsassistentin",
        "geschaeftsfuehrungsassistentin",
        "executive assistant weiblich",
        "persönliche assistentin geschäftsführer",
        "persoenliche assistentin geschaeftsfuehrer",
        "assistenz vom chef weiblich",
        "rechte hand des geschäftsführers weiblich",
        "rechte hand des geschaeftsfuehrers weiblich",
        "vorzimmer assistentin",
        "management assistentin",
        "ceo assistentin",
        "gf assistentin",
        "leiterin assistenz geschäftsführung",
        "leiterin assistenz geschaeftsfuehrung",
        "terminassistentin geschäftsführung",
        "office assistentin chef",
        "chefsekretärin nah"
      ]
    },
    "Sekretärin": {
      "label": "Sekretärin",
      "aliases": [
        "sekretärin",
        "sekretaerin",
        "chefsekretärin",
        "chefsekretaerin",
        "sekretariat",
        "vorzimmerdame",
        "bürosekretärin",
        "buerosekretaerin",
        "empfangssekretärin",
        "empfangssekretaerin",
        "telefonsekretärin",
        "telefonsekretaerin",
        "terminsekretärin",
        "terminsekretaerin",
        "sekretärin geschäftsführung",
        "sekretaerin geschaeftsfuehrung",
        "sekretärin inhaber",
        "sekretaerin inhaber",
        "büroassistenz sekretariat",
        "bueroassistenz sekretariat"
      ]
    },
    "Empfangsmitarbeiter": {
      "label": "Empfangsmitarbeiter",
      "aliases": [
        "empfangsmitarbeiter",
        "empfangsmitarbeiterin",
        "rezeptionist",
        "rezeptionistin",
        "rezeption",
        "empfang",
        "front desk mitarbeiter",
        "front desk mitarbeiterin",
        "telefonzentrale mitarbeiter",
        "telefonzentrale mitarbeiterin",
        "welcome desk",
        "front office agent",
        "front office mitarbeiter",
        "front office mitarbeiterin",
        "reception agent",
        "receptionist",
        "kundenempfang",
        "besucherempfang",
        "empfangskraft",
        "empfangskraft büro"
      ]
    },
    "Wirtschaftsprüfer": {
      "label": "Wirtschaftsprüfer",
      "aliases": [
        "wirtschaftsprüfer",
        "wirtschaftspruefer",
        "wirtschaftsprüferin",
        "wirtschaftsprueferin",
        "abschlussprüfer",
        "abschlusspruefer",
        "abschlussprüferin",
        "abschlussprueferin",
        "auditor",
        "auditorin",
        "externer prüfer",
        "externer pruefer",
        "externe prüferin",
        "externe prueferin",
        "prüfer jahresabschluss",
        "pruefer jahresabschluss",
        "prüferin jahresabschluss",
        "prueferin jahresabschluss",
        "audit partner",
        "audit manager"
      ]
    },
    "Ehefrau des Geschäftsführers": {
      "label": "Ehefrau des Geschäftsführers",
      "aliases": [
        "ehefrau des geschäftsführers",
        "ehefrau des geschaeftsfuehrers",
        "frau des geschäftsführers",
        "frau des geschaeftsfuehrers",
        "partnerin des geschäftsführers",
        "partnerin des geschaeftsfuehrers",
        "frau vom chef",
        "chefin zuhause",
        "mit seiner frau besprechen",
        "seine frau entscheidet mit",
        "ehepartnerin geschäftsführer",
        "ehepartnerin geschaeftsfuehrer",
        "frau hinter dem geschäftsführer",
        "frau hinter dem geschaeftsfuehrer",
        "private vertraute geschäftsführer",
        "private vertraute geschaeftsfuehrer",
        "frau als privater ratgeber",
        "frau als private ratgeberin",
        "familie geschäftsführer frau",
        "familie geschaeftsfuehrer frau"
      ]
    },
    "Ehemann der Geschäftsführerin": {
      "label": "Ehemann der Geschäftsführerin",
      "aliases": [
        "ehemann der geschäftsführerin",
        "ehemann der geschaeftsfuehrerin",
        "mann der geschäftsführerin",
        "mann der geschaeftsfuehrerin",
        "partner der geschäftsführerin",
        "partner der geschaeftsfuehrerin",
        "mann von der chefin",
        "mit ihrem mann besprechen",
        "ihr mann entscheidet mit",
        "ehepartner geschäftsführerin",
        "ehepartner geschaeftsfuehrerin",
        "mann hinter der geschäftsführerin",
        "mann hinter der geschaeftsfuehrerin",
        "privater vertrauter geschäftsführerin",
        "privater vertrauter geschaeftsfuehrerin",
        "mann als privater ratgeber",
        "familie geschäftsführerin mann",
        "familie geschaeftsfuehrerin mann",
        "ehemann der inhaberin",
        "partner der inhaberin"
      ]
    },
    "Ehefrau des Inhabers": {
      "label": "Ehefrau des Inhabers",
      "aliases": [
        "ehefrau des inhabers",
        "frau des inhabers",
        "partnerin des inhabers",
        "frau vom inhaber",
        "inhaberfrau",
        "mitinhaber frau informell",
        "frau entscheidet beim inhaber mit",
        "mit seiner frau besprechen inhaber",
        "ehepartnerin inhaber",
        "private vertraute inhaber",
        "frau hinter dem inhaber",
        "frau als kaufbegleiterin",
        "familie inhaber frau",
        "zuhause mit frau klären",
        "zuhause mit frau klaeren",
        "frau prüft entscheidung",
        "frau prueft entscheidung",
        "ehefrau im familienbetrieb",
        "frau im familienunternehmen",
        "frau als vertraute des inhabers"
      ]
    },
    "Ehemann der Inhaberin": {
      "label": "Ehemann der Inhaberin",
      "aliases": [
        "mann der inhaberin",
        "mann von der inhaberin",
        "inhaberin ehemann",
        "mitinhaber mann informell",
        "mann entscheidet bei inhaberin mit",
        "mit ihrem mann besprechen inhaberin",
        "ehepartner inhaberin",
        "privater vertrauter inhaberin",
        "mann hinter der inhaberin",
        "mann als kaufbegleiter",
        "familie inhaberin mann",
        "zuhause mit mann klären",
        "zuhause mit mann klaeren",
        "mann prüft entscheidung",
        "mann prueft entscheidung",
        "ehemann im familienbetrieb",
        "mann im familienunternehmen",
        "mann als vertrauter der inhaberin",
        "ehemann als privater ratgeber",
        "ehemann entscheidet privat mit",
        "mit ehemann besprechen",
        "ehemann prüft investition",
        "ehemann prueft investition"
      ]
    },
    "Sohn des Inhabers": {
      "label": "Sohn des Inhabers",
      "aliases": [
        "sohn des inhabers",
        "unternehmersohn",
        "nachfolger sohn",
        "sohn im familienbetrieb",
        "sohn im familienunternehmen",
        "juniorchef sohn",
        "junior chef sohn",
        "sohn arbeitet mit",
        "sohn übernimmt bald",
        "sohn uebernimmt bald",
        "sohn als nachfolge",
        "nachfolgegeneration sohn",
        "sohn des geschäftsführers",
        "sohn des geschaeftsfuehrers",
        "sohn als mitarbeiter familie",
        "sohn berät inhaber",
        "sohn beraet inhaber",
        "sohn entscheidet mit",
        "sohn prüft mit",
        "sohn prueft mit"
      ]
    },
    "Tochter des Inhabers": {
      "label": "Tochter des Inhabers",
      "aliases": [
        "tochter des inhabers",
        "unternehmertochter",
        "nachfolgerin tochter",
        "tochter im familienbetrieb",
        "tochter im familienunternehmen",
        "juniorchefin tochter",
        "junior chefin tochter",
        "tochter arbeitet mit",
        "tochter übernimmt bald",
        "tochter uebernimmt bald",
        "tochter als nachfolge",
        "nachfolgegeneration tochter",
        "tochter des geschäftsführers",
        "tochter des geschaeftsfuehrers",
        "tochter als mitarbeiterin familie",
        "tochter berät inhaber",
        "tochter beraet inhaber",
        "tochter entscheidet mit",
        "tochter prüft mit",
        "tochter prueft mit"
      ]
    },
    "Vater des Inhabers": {
      "label": "Vater des Inhabers",
      "aliases": [
        "vater des inhabers",
        "seniorchef vater",
        "firmengründer vater",
        "firmengruender vater",
        "vater im familienbetrieb",
        "vater im familienunternehmen",
        "alter inhaber vater",
        "ehemaliger chef vater",
        "vater als seniorberater",
        "vater redet mit",
        "vater entscheidet mit",
        "vater prüft entscheidung",
        "vater prueft entscheidung",
        "vater des geschäftsführers",
        "vater des geschaeftsfuehrers",
        "patriarch im betrieb",
        "senior generation vater",
        "vater als eigentümer senior",
        "vater als eigentuemer senior",
        "vater im hintergrund"
      ]
    },
    "Mutter der Inhaberin": {
      "label": "Mutter der Inhaberin",
      "aliases": [
        "mutter der inhaberin",
        "seniorchefin mutter",
        "firmengründerin mutter",
        "firmengruenderin mutter",
        "mutter im familienbetrieb",
        "mutter im familienunternehmen",
        "alte inhaberin mutter",
        "ehemalige chefin mutter",
        "mutter als seniorberaterin",
        "mutter redet mit",
        "mutter entscheidet mit",
        "mutter prüft entscheidung",
        "mutter prueft entscheidung",
        "mutter der geschäftsführerin",
        "mutter der geschaeftsfuehrerin",
        "matriarchin im betrieb",
        "senior generation mutter",
        "mutter als eigentümerin senior",
        "mutter als eigentuemerin senior",
        "mutter im hintergrund"
      ]
    },
    "Praxisinhaber": {
      "label": "Praxisinhaber",
      "aliases": [
        "praxisinhaber",
        "praxisinhaberin",
        "inhaber arztpraxis",
        "inhaberin arztpraxis",
        "arzt als praxisinhaber",
        "ärztin als praxisinhaberin",
        "aerztin als praxisinhaberin",
        "zahnarzt praxisinhaber",
        "zahnärztin praxisinhaberin",
        "zahnaerztin praxisinhaberin",
        "therapeut praxisinhaber",
        "therapeutin praxisinhaberin",
        "praxisbetreiber",
        "praxisbetreiberin",
        "medizinischer unternehmer",
        "medizinische unternehmerin",
        "niedergelassener arzt",
        "niedergelassene ärztin",
        "niedergelassene aerztin",
        "praxischef"
      ]
    },
    "Praxismanager": {
      "label": "Praxismanager",
      "aliases": [
        "praxismanager",
        "praxismanagerin",
        "praxismanagement",
        "leitung praxismanagement",
        "organisationsleitung praxis",
        "praxisleitung operativ",
        "mfa leitung",
        "leitende mfa",
        "arztpraxis manager",
        "arztpraxis managerin",
        "zahnarztpraxis manager",
        "zahnarztpraxis managerin",
        "terminmanagement praxis",
        "verwaltung praxis leitung",
        "abrechnungsmanager praxis",
        "abrechnungsmanagerin praxis",
        "patientenmanagement leitung",
        "praxiskoordination",
        "praxiskoordinatorin",
        "sprechstundenmanagement leitung"
      ]
    },
    "Arzt": {
      "label": "Arzt",
      "aliases": [
        "arzt",
        "ärztin",
        "aerztin",
        "facharzt",
        "fachärztin",
        "fachaerztin",
        "klinikarzt",
        "klinikärztin",
        "klinikaerztin",
        "chefarzt",
        "chefärztin",
        "chefaerztin",
        "oberarzt",
        "oberärztin",
        "oberaerztin",
        "behandelnder arzt",
        "behandelnde ärztin",
        "arzt als entscheider",
        "ärztin als entscheiderin",
        "aerztin als entscheiderin",
        "medizinischer leistungserbringer",
        "medizinische leistungserbringerin"
      ]
    },
    "Pflegedienstleiter": {
      "label": "Pflegedienstleiter",
      "aliases": [
        "pflegedienstleiter",
        "pflegedienstleiterin",
        "pdl",
        "leitung pflegedienst",
        "pflegedienstleitung",
        "ambulanter pflegedienst leitung",
        "stationäre pflegedienstleitung",
        "stationaere pflegedienstleitung",
        "pflegeleitung",
        "leiter pflege",
        "leiterin pflege",
        "pflege manager",
        "pflege managerin",
        "care manager pflege",
        "verantwortliche pflegefachkraft",
        "verantwortlicher pflegefachkraft",
        "pflegekoordination leitung",
        "pflegekoordinatorin leitung",
        "pflegedienst chef",
        "pflegedienst chefin"
      ]
    },
    "Einrichtungsleiter Pflege": {
      "label": "Einrichtungsleiter Pflege",
      "aliases": [
        "einrichtungsleiter pflege",
        "einrichtungsleiterin pflege",
        "heimleiter",
        "heimleiterin",
        "leitung pflegeheim",
        "leiter altenheim",
        "leiterin altenheim",
        "seniorenheim leitung",
        "stationäre einrichtung leitung",
        "stationaere einrichtung leitung",
        "pflegeeinrichtung leitung",
        "residenzleitung pflege",
        "hausleitung pflege",
        "betriebsleitung pflegeheim",
        "einrichtungsmanagement pflege",
        "pflegeheim manager",
        "pflegeheim managerin",
        "träger einrichtungsleitung",
        "traeger einrichtungsleitung",
        "heimmanagement"
      ]
    },
    "Apothekeninhaber": {
      "label": "Apothekeninhaber",
      "aliases": [
        "apothekeninhaber",
        "apothekeninhaberin",
        "inhaber apotheke",
        "inhaberin apotheke",
        "apotheker als inhaber",
        "apothekerin als inhaberin",
        "pharmazeutischer inhaber",
        "pharmazeutische inhaberin",
        "apothekenleiter inhaber",
        "apothekenleiterin inhaberin",
        "offizin inhaber",
        "offizin inhaberin",
        "filialapotheke inhaber",
        "filialapotheke inhaberin",
        "hauptapotheke inhaber",
        "hauptapotheke inhaberin",
        "apothekenunternehmer",
        "apothekenunternehmerin",
        "apothekenchef",
        "apothekenchefin"
      ]
    },
    "Apothekenleiter": {
      "label": "Apothekenleiter",
      "aliases": [
        "apothekenleiter",
        "apothekenleiterin",
        "leitung apotheke",
        "leiter apotheke",
        "leiterin apotheke",
        "filialleiter apotheke",
        "filialleiterin apotheke",
        "verantwortlicher apotheker",
        "verantwortliche apothekerin",
        "pharmazeutische leitung",
        "pharmazeutischer leiter",
        "pharmazeutische leiterin",
        "offizin leitung",
        "apothekenmanagement",
        "apotheke manager",
        "apotheke managerin",
        "standortleitung apotheke",
        "betriebsleitung apotheke",
        "leitender apotheker",
        "leitende apothekerin"
      ]
    },
    "Hotelinhaber": {
      "label": "Hotelinhaber",
      "aliases": [
        "hotelinhaber",
        "hotelinhaberin",
        "inhaber hotel",
        "inhaberin hotel",
        "hotelier",
        "hotelière",
        "hoteliere",
        "hotelunternehmer",
        "hotelunternehmerin",
        "gastgeber als inhaber",
        "gastgeberin als inhaberin",
        "familienhotel inhaber",
        "familienhotel inhaberin",
        "hotelbesitzer",
        "hotelbesitzerin",
        "privathotel inhaber",
        "privathotel inhaberin",
        "boutique hotel inhaber",
        "boutique hotel inhaberin",
        "hotel owner"
      ]
    },
    "Hoteldirektor": {
      "label": "Hoteldirektor",
      "aliases": [
        "hoteldirektor",
        "hoteldirektorin",
        "hotel director",
        "general manager hotel",
        "gm hotel",
        "leitung hotel",
        "hotelmanager",
        "hotelmanagerin",
        "hotel management",
        "betriebsleiter hotel",
        "betriebsleiterin hotel",
        "resort manager",
        "resort managerin",
        "direktor hospitality",
        "direktorin hospitality",
        "hausdirektor hotel",
        "hausdirektorin hotel",
        "standortleiter hotel",
        "standortleiterin hotel",
        "hotel geschäftsleitung"
      ]
    },
    "Restaurantinhaber": {
      "label": "Restaurantinhaber",
      "aliases": [
        "restaurantinhaber",
        "restaurantinhaberin",
        "inhaber restaurant",
        "inhaberin restaurant",
        "gastronom",
        "gastronomin",
        "restaurant owner",
        "gastwirt",
        "gastwirtin",
        "wirt",
        "wirtin",
        "lokalinhaber",
        "lokalinhaberin",
        "café inhaber",
        "cafe inhaber",
        "café inhaberin",
        "cafe inhaberin",
        "barinhaber",
        "barinhaberin",
        "gastronomieunternehmer"
      ]
    },
    "Restaurantleiter": {
      "label": "Restaurantleiter",
      "aliases": [
        "restaurantleiter",
        "restaurantleiterin",
        "restaurant manager",
        "leitung restaurant",
        "leiter restaurant",
        "leiterin restaurant",
        "service manager restaurant",
        "serviceleitung restaurant",
        "f&b manager restaurant",
        "f and b manager restaurant",
        "betriebsleiter restaurant",
        "betriebsleiterin restaurant",
        "schichtleiter restaurant",
        "schichtleiterin restaurant",
        "store manager gastro",
        "gastroleitung",
        "restaurantverantwortlicher",
        "restaurantverantwortliche",
        "floor manager restaurant",
        "dining manager"
      ]
    },
    "Handwerksmeister": {
      "label": "Handwerksmeister",
      "aliases": [
        "handwerksmeister",
        "handwerksmeisterin",
        "meister",
        "meisterin",
        "meisterbetrieb inhaber",
        "meisterbetrieb inhaberin",
        "elektromeister",
        "elektromeisterin",
        "shk meister",
        "shk meisterin",
        "malermeister",
        "malermeisterin",
        "dachdeckermeister",
        "dachdeckermeisterin",
        "tischlermeister",
        "tischlermeisterin",
        "kfz meister",
        "kfz meisterin",
        "metallbaumeister",
        "metallbaumeisterin"
      ]
    },
    "Bauunternehmer": {
      "label": "Bauunternehmer",
      "aliases": [
        "bauunternehmer",
        "bauunternehmerin",
        "inhaber bauunternehmen",
        "inhaberin bauunternehmen",
        "geschäftsführer bauunternehmen",
        "geschaeftsfuehrer bauunternehmen",
        "baugeschäft inhaber",
        "baugeschaeft inhaber",
        "baugeschäft inhaberin",
        "baugeschaeft inhaberin",
        "bauunternehmung chef",
        "bauunternehmung chefin",
        "hochbau unternehmer",
        "tiefbau unternehmer",
        "tiefbauunternehmer",
        "bau contractor owner",
        "bau firma inhaber",
        "bau firma inhaberin",
        "baubetrieb inhaber",
        "baubetrieb inhaberin"
      ]
    },
    "Bauleiter": {
      "label": "Bauleiter",
      "aliases": [
        "bauleiter",
        "bauleiterin",
        "construction manager",
        "site manager bau",
        "baustellenleiter",
        "baustellenleiterin",
        "oberbauleiter",
        "oberbauleiterin",
        "bauoberleitung",
        "projektbauleiter",
        "projektbauleiterin",
        "bauprojektleiter",
        "bauprojektleiterin",
        "leiter baustelle",
        "leiterin baustelle",
        "verantwortlicher baustelle",
        "verantwortliche baustelle",
        "baukoordination leitung",
        "baukoordinator",
        "baukoordinatorin"
      ]
    },
    "Architekt": {
      "label": "Architekt",
      "aliases": [
        "architekt",
        "architektin",
        "büroinhaber architektur",
        "bueroinhaber architektur",
        "architekturbüro inhaber",
        "architekturbuero inhaber",
        "geschäftsführer architekturbüro",
        "geschaeftsfuehrer architekturbuero",
        "planender architekt",
        "planende architektin",
        "projektarchitekt",
        "projektarchitektin",
        "leitender architekt",
        "leitende architektin",
        "entwurfsarchitekt",
        "entwurfsarchitektin",
        "bauvorlageberechtigter architekt",
        "bauvorlageberechtigte architektin",
        "architektur partner",
        "architektur partnerin"
      ]
    },
    "Ingenieurbüro-Inhaber": {
      "label": "Ingenieurbüro-Inhaber",
      "aliases": [
        "ingenieurbüro inhaber",
        "ingenieurbuero inhaber",
        "ingenieurbüro inhaberin",
        "ingenieurbuero inhaberin",
        "inhaber ingenieurbüro",
        "inhaber ingenieurbuero",
        "inhaberin ingenieurbüro",
        "inhaberin ingenieurbuero",
        "geschäftsführer ingenieurbüro",
        "geschaeftsfuehrer ingenieurbuero",
        "beratender ingenieur inhaber",
        "beratende ingenieurin inhaberin",
        "planungsbüro inhaber",
        "planungsbuero inhaber",
        "planungsbüro inhaberin",
        "planungsbuero inhaberin",
        "ingenieurunternehmer",
        "ingenieurunternehmerin",
        "technisches büro inhaber",
        "technisches buero inhaber"
      ]
    },
    "Projektentwickler Immobilien": {
      "label": "Projektentwickler Immobilien",
      "aliases": [
        "projektentwickler immobilien",
        "projektentwicklerin immobilien",
        "immobilienprojektentwickler",
        "immobilienprojektentwicklerin",
        "developer immobilien",
        "real estate developer",
        "bauprojektentwickler",
        "bauprojektentwicklerin",
        "quartiersentwickler",
        "quartiersentwicklerin",
        "entwickler wohnbau",
        "entwicklerin wohnbau",
        "gewerbeimmobilien entwickler",
        "gewerbeimmobilien entwicklerin",
        "projektträger immobilien",
        "projekttraeger immobilien",
        "immobilien development manager",
        "property developer",
        "developer bauvorhaben",
        "entwicklungsleiter immobilien"
      ]
    },
    "Hausverwalter": {
      "label": "Hausverwalter",
      "aliases": [
        "hausverwalter",
        "hausverwalterin",
        "hausverwaltung",
        "weg verwalter",
        "weg verwalterin",
        "immobilienverwalter",
        "immobilienverwalterin",
        "property manager",
        "property managerin",
        "objektverwalter",
        "objektverwalterin",
        "mietverwalter",
        "mietverwalterin",
        "verwaltung wohnanlage",
        "verwaltung gewerbeimmobilie",
        "verwalter eigentümergemeinschaft",
        "verwalter eigentuemergemeinschaft",
        "weg verwaltung leitung",
        "property administration manager",
        "immobilienverwaltung leitung"
      ]
    },
    "Facility-Manager": {
      "label": "Facility-Manager",
      "aliases": [
        "facility manager",
        "facility managerin",
        "gebäudemanager",
        "gebaeudemanager",
        "gebäudemanagerin",
        "gebaeudemanagerin",
        "technisches facility management",
        "infrastrukturelles facility management",
        "objektmanager facility",
        "objektmanagerin facility",
        "haus technik leitung",
        "gebäudebetrieb leitung",
        "gebaeudebetrieb leitung",
        "fm manager",
        "fm managerin",
        "liegenschaftsmanager",
        "liegenschaftsmanagerin",
        "standort facility manager",
        "standort facility managerin",
        "gebäudeverantwortlicher"
      ]
    },
    "Produktionsleiter": {
      "label": "Produktionsleiter",
      "aliases": [
        "produktionsleiter",
        "produktionsleiterin",
        "leitung produktion",
        "leiter produktion",
        "leiterin produktion",
        "head of production",
        "production manager",
        "fertigungsleiter",
        "fertigungsleiterin",
        "manufacturing manager",
        "werksproduktion leitung",
        "schichtübergreifende produktionsleitung",
        "schichtuebergreifende produktionsleitung",
        "produktionsverantwortlicher",
        "produktionsverantwortliche",
        "industrie produktionsleiter",
        "industrie produktionsleiterin",
        "produktion chef",
        "produktion chefin",
        "manufacturing lead"
      ]
    },
    "Werkleiter": {
      "label": "Werkleiter",
      "aliases": [
        "werkleiter",
        "werkleiterin",
        "werksleiter",
        "werksleiterin",
        "leitung werk",
        "leiter werk",
        "leiterin werk",
        "factory manager",
        "fabrikleiter",
        "fabrikleiterin",
        "standort werk leitung",
        "werksverantwortlicher",
        "werksverantwortliche",
        "industriewerk leitung",
        "produktionswerk leitung",
        "plant director",
        "werkdirektor",
        "werkdirektorin",
        "betriebsstätte leitung",
        "leiter industriewerk",
        "leiterin industriewerk",
        "werkverantwortlicher",
        "werkverantwortliche"
      ]
    },
    "Qualitätsleiter": {
      "label": "Qualitätsleiter",
      "aliases": [
        "qualitätsleiter",
        "qualitaetsleiter",
        "qualitätsleiterin",
        "qualitaetsleiterin",
        "leitung qualität",
        "leitung qualitaet",
        "head of quality",
        "quality manager",
        "qm leiter",
        "qm leiterin",
        "qualitätsmanagement leitung",
        "qualitaetsmanagement leitung",
        "qa manager",
        "quality assurance manager",
        "qualitätsverantwortlicher",
        "qualitaetsverantwortlicher",
        "qualitätsverantwortliche",
        "qualitaetsverantwortliche",
        "qualitätschef",
        "qualitaetschef"
      ]
    },
    "Logistikleiter": {
      "label": "Logistikleiter",
      "aliases": [
        "logistikleiter",
        "logistikleiterin",
        "leitung logistik",
        "leiter logistik",
        "leiterin logistik",
        "head of logistics",
        "logistics manager",
        "supply chain logistics lead",
        "lagerlogistik leitung",
        "versandleiter",
        "versandleiterin",
        "transportleiter",
        "transportleiterin",
        "distributionsleiter",
        "distributionsleiterin",
        "logistikverantwortlicher",
        "logistikverantwortliche",
        "warehouse logistics manager",
        "fulfillment leitung",
        "logistikchef"
      ]
    },
    "Supply-Chain-Manager": {
      "label": "Supply-Chain-Manager",
      "aliases": [
        "supply chain manager",
        "supply-chain-manager",
        "scm manager",
        "supply chain leitung",
        "leiter supply chain",
        "leiterin supply chain",
        "head of supply chain",
        "lieferkettenmanager",
        "lieferkettenmanagerin",
        "demand planning manager",
        "supply planning manager",
        "procurement supply chain manager",
        "operations supply manager",
        "chain operations manager",
        "supply chain director",
        "materialfluss manager",
        "materialfluss managerin",
        "supply chain verantwortlicher",
        "supply chain verantwortliche",
        "lieferkettenverantwortlicher"
      ]
    },
    "Kundenserviceleiter": {
      "label": "Kundenserviceleiter",
      "aliases": [
        "kundenserviceleiter",
        "kundenserviceleiterin",
        "leitung kundenservice",
        "leiter kundenservice",
        "leiterin kundenservice",
        "customer service manager",
        "head of customer service",
        "supportleiter",
        "supportleiterin",
        "support leitung",
        "service desk manager",
        "customer support manager",
        "client service manager",
        "kundenbetreuung leitung",
        "kundenbetreuung leiter",
        "kundenbetreuung leiterin",
        "servicecenter leitung",
        "contact center manager",
        "callcenter leiter",
        "callcenter leiterin"
      ]
    },
    "Customer-Success-Manager": {
      "label": "Customer-Success-Manager",
      "aliases": [
        "customer success manager",
        "customer-success-manager",
        "csm",
        "client success manager",
        "success manager",
        "kundenentwicklungsmanager",
        "kundenentwicklungsmanagerin",
        "bestandskunden success manager",
        "saas customer success",
        "onboarding success manager",
        "implementation success manager",
        "kundenbindung manager",
        "kundenbindung managerin",
        "retention manager",
        "client partner success",
        "customer value manager",
        "adoption manager",
        "kundenadoption manager",
        "success betreuer",
        "success betreuerin"
      ]
    },
    "Datenschutzbeauftragter": {
      "label": "Datenschutzbeauftragter",
      "aliases": [
        "datenschutzbeauftragter",
        "datenschutzbeauftragte",
        "dpo",
        "data protection officer",
        "externer datenschutzbeauftragter",
        "externe datenschutzbeauftragte",
        "interner datenschutzbeauftragter",
        "interne datenschutzbeauftragte",
        "datenschutz officer",
        "privacy officer",
        "datenschutzverantwortlicher",
        "datenschutzverantwortliche",
        "dsb",
        "gdpr officer",
        "dsgvo beauftragter",
        "dsgvo beauftragte",
        "privacy manager",
        "privacy managerin",
        "datenschutzmanager",
        "datenschutzmanagerin"
      ]
    },
    "Compliance-Manager": {
      "label": "Compliance-Manager",
      "aliases": [
        "compliance manager",
        "compliance managerin",
        "compliance officer",
        "compliance beauftragter",
        "compliance beauftragte",
        "regulatory compliance manager",
        "risikomanager compliance",
        "risikomanagerin compliance",
        "ethics compliance officer",
        "legal compliance manager",
        "konformitätsmanager",
        "konformitaetsmanager",
        "konformitätsmanagerin",
        "konformitaetsmanagerin",
        "compliance verantwortlicher",
        "compliance verantwortliche",
        "regulatorik manager",
        "regulatorik managerin",
        "governance compliance manager",
        "compliance leitung"
      ]
    },
    "Jurist im Unternehmen": {
      "label": "Jurist im Unternehmen",
      "aliases": [
        "unternehmensjurist",
        "unternehmensjuristin",
        "inhouse counsel",
        "legal counsel",
        "syndikusrechtsanwalt",
        "syndikusrechtsanwältin",
        "syndikusrechtsanwaeltin",
        "rechtsabteilung jurist",
        "rechtsabteilung juristin",
        "legal manager",
        "vertragsjurist",
        "vertragsjuristin",
        "wirtschaftsjurist",
        "wirtschaftsjuristin",
        "corporate counsel",
        "legal advisor intern",
        "interner anwalt",
        "interne anwältin",
        "interne anwaeltin",
        "jurist unternehmen"
      ]
    },
    "Schulleiter": {
      "label": "Schulleiter",
      "aliases": [
        "schulleiter",
        "schulleiterin",
        "rektor",
        "rektorin",
        "schuldirektor",
        "schuldirektorin",
        "leitung schule",
        "leiter schule",
        "leiterin schule",
        "principal school",
        "headmaster",
        "headmistress",
        "schulverwaltung leitung",
        "pädagogische leitung schule",
        "paedagogische leitung schule",
        "gesamtschulleiter",
        "gesamtschulleiterin",
        "grundschulleiter",
        "grundschulleiterin",
        "berufsschulleiter"
      ]
    },
    "Hochschulprofessor": {
      "label": "Hochschulprofessor",
      "aliases": [
        "hochschulprofessor",
        "hochschulprofessorin",
        "professor",
        "professorin",
        "uni professor",
        "uni professorin",
        "fh professor",
        "fh professorin",
        "lehrstuhlinhaber",
        "lehrstuhlinhaberin",
        "chair professor",
        "wissenschaftlicher professor",
        "wissenschaftliche professorin",
        "forschungsprofessor",
        "forschungsprofessorin",
        "institutsprofessor",
        "institutsprofessorin",
        "akademischer lehrstuhl",
        "akademische lehrstuhlperson",
        "professoraler entscheider"
      ]
    },
    "Dekan": {
      "label": "Dekan",
      "aliases": [
        "dekan",
        "dekanin",
        "dean",
        "fachbereichsdekan",
        "fachbereichsdekanin",
        "fakultätsdekan",
        "fakultaetsdekan",
        "fakultätsdekanin",
        "fakultaetsdekanin",
        "leitung fakultät",
        "leitung fakultaet",
        "fakultätsleitung",
        "fakultaetsleitung",
        "academic dean",
        "hochschulbereich leitung",
        "studienbereich dekan",
        "studienbereich dekanin",
        "dekanat leitung",
        "dekanatsleitung",
        "universitätsdekan"
      ]
    },
    "Bürgermeister": {
      "label": "Bürgermeister",
      "aliases": [
        "bürgermeister",
        "buergermeister",
        "bürgermeisterin",
        "buergermeisterin",
        "oberbürgermeister",
        "oberbuergermeister",
        "oberbürgermeisterin",
        "oberbuergermeisterin",
        "gemeindechef",
        "gemeindechefin",
        "stadtoberhaupt",
        "kommunalchef",
        "kommunalchefin",
        "verwaltungschef kommune",
        "verwaltungschefin kommune",
        "kommunalpolitische leitung",
        "hauptverwaltungsbeamter",
        "hauptverwaltungsbeamtin",
        "rathauschef",
        "rathauschefin"
      ]
    },
    "Amtsleiter": {
      "label": "Amtsleiter",
      "aliases": [
        "amtsleiter",
        "amtsleiterin",
        "leitung amt",
        "leiter amt",
        "leiterin amt",
        "fachamtsleiter",
        "fachamtsleiterin",
        "behördenleiter",
        "behoerdenleiter",
        "behördenleiterin",
        "behoerdenleiterin",
        "verwaltungsamtsleiter",
        "verwaltungsamtsleiterin",
        "stadtamtsleiter",
        "stadtamtsleiterin",
        "kommunaler amtsleiter",
        "kommunale amtsleiterin",
        "leiter fachamt",
        "leiterin fachamt",
        "amtsführung"
      ]
    },
    "Vergabestellenleiter": {
      "label": "Vergabestellenleiter",
      "aliases": [
        "vergabestellenleiter",
        "vergabestellenleiterin",
        "leitung vergabestelle",
        "leiter vergabestelle",
        "leiterin vergabestelle",
        "zentrale vergabestelle leitung",
        "beschaffungsstelle leitung",
        "leiter beschaffungsstelle",
        "leiterin beschaffungsstelle",
        "ausschreibungsstelle leitung",
        "vergabemanager leitung",
        "vergabemanagerin leitung",
        "public procurement lead",
        "kommunale vergabestelle",
        "vergabeamt leitung",
        "leiter vergabeamt",
        "leiterin vergabeamt",
        "öffentliche beschaffung leitung",
        "oeffentliche beschaffung leitung",
        "vergaberechtliche stelle leitung"
      ]
    },
    "Sachbearbeiter Verwaltung": {
      "label": "Sachbearbeiter Verwaltung",
      "aliases": [
        "sachbearbeiter verwaltung",
        "sachbearbeiterin verwaltung",
        "verwaltungssachbearbeiter",
        "verwaltungssachbearbeiterin",
        "kommunaler sachbearbeiter",
        "kommunale sachbearbeiterin",
        "behördensachbearbeiter",
        "behoerdensachbearbeiter",
        "behördensachbearbeiterin",
        "behoerdensachbearbeiterin",
        "amt sachbearbeiter",
        "amt sachbearbeiterin",
        "fallbearbeiter verwaltung",
        "fallbearbeiterin verwaltung",
        "öffentlicher sachbearbeiter",
        "oeffentlicher sachbearbeiter",
        "öffentliche sachbearbeiterin",
        "oeffentliche sachbearbeiterin",
        "verwaltungsfachangestellter",
        "verwaltungsfachangestellte"
      ]
    },
    "Privatkunde": {
      "label": "Privatkunde",
      "aliases": [
        "privatkunde",
        "privatkundin",
        "endkunde",
        "endkundin",
        "verbraucher",
        "verbraucherin",
        "konsument",
        "konsumentin",
        "consumer",
        "b2c kunde",
        "b2c kundin",
        "kunde privat",
        "kundin privat",
        "privatperson",
        "einzelperson privat",
        "selbstzahler",
        "selbstzahlerin",
        "auftraggeber privat",
        "auftraggeberin privat",
        "käufer privat"
      ]
    },
    "Patient": {
      "label": "Patient",
      "aliases": [
        "patient",
        "patientin",
        "medizinischer kunde",
        "medizinische kundin",
        "gesundheitskunde",
        "gesundheitskundin",
        "behandlungsinteressent",
        "behandlungsinteressentin",
        "therapiepatient",
        "therapiepatientin",
        "pflegepatient",
        "pflegepatientin",
        "selbstzahler patient",
        "selbstzahlerin patientin",
        "klient gesundheit",
        "klientin gesundheit",
        "ratsuchender patient",
        "ratsuchende patientin",
        "patient als entscheider",
        "patientin als entscheiderin"
      ]
    },
    "Angehöriger": {
      "label": "Angehöriger",
      "aliases": [
        "angehöriger eines patienten",
        "angehoeriger eines patienten",
        "angehörige eines patienten",
        "angehoerige eines patienten",
        "familie des patienten",
        "familie der patientin",
        "sohn des patienten",
        "tochter der patientin",
        "ehefrau des patienten",
        "ehemann der patientin",
        "gesetzlicher betreuer patient",
        "gesetzliche betreuerin patientin",
        "pflegebevollmächtigter",
        "pflegebevollmaechtigter",
        "pflegebevollmächtigte",
        "pflegebevollmaechtigte",
        "entscheidet für patienten",
        "entscheidet fuer patienten",
        "medizinischer angehöriger",
        "medizinische angehörige"
      ]
    },
    "Gesetzlicher Betreuer": {
      "label": "Gesetzlicher Betreuer",
      "aliases": [
        "gesetzlicher betreuer",
        "gesetzliche betreuerin",
        "rechtlicher betreuer",
        "rechtliche betreuerin",
        "betreuungsbevollmächtigter",
        "betreuungsbevollmaechtigter",
        "betreuungsbevollmächtigte",
        "betreuungsbevollmaechtigte",
        "vormund",
        "vormundin",
        "betreuungsgerichtlicher vertreter",
        "betreuungsgerichtliche vertreterin",
        "bevollmächtigter betreuung",
        "bevollmaechtigter betreuung",
        "bevollmächtigte betreuung",
        "bevollmaechtigte betreuung",
        "patientenvertreter gesetzlich",
        "patientenvertreterin gesetzlich",
        "pflegeentscheidung betreuer",
        "pflegeentscheidung betreuerin"
      ]
    },
    "Vereinsvorsitzender": {
      "label": "Vereinsvorsitzender",
      "aliases": [
        "vereinsvorsitzender",
        "vereinsvorsitzende",
        "1 vorsitzender verein",
        "erste vorsitzende verein",
        "vereinschef",
        "vereinschefin",
        "vorstand verein",
        "vereinsvorstand",
        "leitung verein",
        "leiter verein",
        "leiterin verein",
        "vorsitz im verein",
        "ehrenamtlicher vorstand",
        "ehrenamtliche vorständin",
        "ehrenamtliche vorstaendin",
        "sportverein vorsitzender",
        "sportverein vorsitzende",
        "kulturverein vorsitzender",
        "kulturverein vorsitzende",
        "vereinsleitung"
      ]
    },
    "Verbandsfunktionär": {
      "label": "Verbandsfunktionär",
      "aliases": [
        "verbandsfunktionär",
        "verbandsfunktionaer",
        "verbandsfunktionärin",
        "verbandsfunktionaerin",
        "verbandsvorstand",
        "vorstand verband",
        "geschäftsführer verband",
        "geschaeftsfuehrer verband",
        "geschäftsführerin verband",
        "geschaeftsfuehrerin verband",
        "verbandsleitung",
        "leiter verband",
        "leiterin verband",
        "funktionär verband",
        "funktionaer verband",
        "funktionärin verband",
        "funktionaerin verband",
        "kammerfunktionär",
        "kammerfunktionaer",
        "kammerfunktionärin"
      ]
    },
    "Franchisegeber": {
      "label": "Franchisegeber",
      "aliases": [
        "franchisegeber",
        "franchisegeberin",
        "franchisor",
        "master franchisegeber",
        "master franchisegeberin",
        "franchise zentrale",
        "franchise systemgeber",
        "franchise systemgeberin",
        "systemzentrale franchise",
        "franchise head office",
        "franchise owner system",
        "franchise markengeber",
        "franchise markengeberin",
        "geber franchise lizenz",
        "geberin franchise lizenz",
        "franchise expansion lead",
        "franchise development lead",
        "franchise network owner",
        "franchise systeminhaber",
        "franchise systeminhaberin"
      ]
    },
    "Franchisenehmer": {
      "label": "Franchisenehmer",
      "aliases": [
        "franchisenehmer",
        "franchisenehmerin",
        "franchise partner",
        "franchise partnerin",
        "franchise operator",
        "franchise operatorin",
        "franchise standortinhaber",
        "franchise standortinhaberin",
        "lokaler franchisenehmer",
        "lokale franchisenehmerin",
        "franchise filialbetreiber",
        "franchise filialbetreiberin",
        "lizenznehmer franchise",
        "lizenznehmerin franchise",
        "systemgastronomie franchise partner",
        "retail franchise partner",
        "franchise unternehmer",
        "franchise unternehmerin",
        "franchise shop owner",
        "franchise store owner"
      ]
    },
    "Agenturinhaber": {
      "label": "Agenturinhaber",
      "aliases": [
        "agenturinhaber",
        "agenturinhaberin",
        "inhaber agentur",
        "inhaberin agentur",
        "agenturchef",
        "agenturchefin",
        "marketingagentur inhaber",
        "marketingagentur inhaberin",
        "werbeagentur inhaber",
        "werbeagentur inhaberin",
        "performance agentur inhaber",
        "performance agentur inhaberin",
        "digitalagentur inhaber",
        "digitalagentur inhaberin",
        "kreativagentur inhaber",
        "kreativagentur inhaberin",
        "agenturgründer",
        "agenturgruender",
        "agenturgründerin",
        "agenturgruenderin"
      ]
    },
    "Kanzleiinhaber": {
      "label": "Kanzleiinhaber",
      "aliases": [
        "kanzleiinhaber",
        "kanzleiinhaberin",
        "rechtsanwalt kanzleiinhaber",
        "rechtsanwältin kanzleiinhaberin",
        "rechtsanwaeltin kanzleiinhaberin",
        "steuerberater kanzleiinhaber",
        "steuerberaterin kanzleiinhaberin",
        "partner kanzlei",
        "partnerin kanzlei",
        "sozius kanzlei",
        "sozia kanzlei",
        "kanzleipartner",
        "kanzleipartnerin",
        "anwalt als inhaber",
        "anwältin als inhaberin",
        "anwaeltin als inhaberin",
        "steuerkanzlei inhaber",
        "steuerkanzlei inhaberin",
        "wirtschaftskanzlei partner",
        "wirtschaftskanzlei partnerin"
      ]
    },
    "Versicherungsmakler": {
      "label": "Versicherungsmakler",
      "aliases": [
        "versicherungsmakler",
        "versicherungsmaklerin",
        "makler inhaber versicherung",
        "maklerin inhaberin versicherung",
        "finanzmakler",
        "finanzmaklerin",
        "versicherungsberater",
        "versicherungsberaterin",
        "unabhängiger makler",
        "unabhaengiger makler",
        "unabhängige maklerin",
        "unabhaengige maklerin",
        "agenturinhaber versicherung",
        "agenturinhaberin versicherung",
        "allianz agenturinhaber",
        "signal iduna agenturinhaber",
        "versicherungsagentur inhaber",
        "versicherungsagentur inhaberin",
        "maklerbüro inhaber",
        "maklerbuero inhaber"
      ]
    },
    "Bankberater": {
      "label": "Bankberater",
      "aliases": [
        "bankberater",
        "bankberaterin",
        "kundenberater bank",
        "kundenberaterin bank",
        "firmenkundenberater bank",
        "firmenkundenberaterin bank",
        "private banker",
        "private bankerin",
        "bank account manager",
        "finanzberater bank",
        "finanzberaterin bank",
        "berater hausbank",
        "beraterin hausbank",
        "sparkassenberater",
        "sparkassenberaterin",
        "volksbank berater",
        "volksbank beraterin",
        "bank betreuer unternehmen",
        "bank betreuerin unternehmen",
        "relationship manager bank"
      ]
    },
    "Vermögensberater": {
      "label": "Vermögensberater",
      "aliases": [
        "vermögensberater",
        "vermoegensberater",
        "vermögensberaterin",
        "vermoegensberaterin",
        "wealth advisor",
        "wealth manager",
        "private wealth manager",
        "finanzberater privat",
        "finanzberaterin privat",
        "anlageberater",
        "anlageberaterin",
        "investment advisor",
        "investment berater",
        "investment beraterin",
        "honorarberater finanzen",
        "honorarberaterin finanzen",
        "asset advisor",
        "portfolio berater",
        "portfolio beraterin",
        "finanzplaner privat"
      ]
    },
    "Versandleiter": {
      "label": "Versandleiter",
      "aliases": [
        "shipping manager",
        "versand manager",
        "versand managerin",
        "leiter versand",
        "leiterin versand",
        "outbound logistics lead",
        "paketversand leitung",
        "warenausgang leitung",
        "dispatch manager",
        "disponent versand",
        "disponentin versand",
        "fulfillment versand leitung",
        "ecommerce versand leitung",
        "lagerausgang leitung",
        "shipping lead",
        "versandverantwortlicher",
        "versandverantwortliche",
        "shipping operations manager",
        "leiter warenausgang",
        "leiterin warenausgang",
        "versand operations manager",
        "versand operations managerin",
        "shipping verantwortlicher"
      ]
    },
    "Lagerleiter": {
      "label": "Lagerleiter",
      "aliases": [
        "lagerleiter",
        "lagerleiterin",
        "warehouse manager",
        "lager manager",
        "lager managerin",
        "leiter lager",
        "leiterin lager",
        "lagerverwaltung leitung",
        "lagerlogistik leiter",
        "lagerlogistik leiterin",
        "warehouse lead",
        "logistiklager leitung",
        "lagerchef",
        "lagerchefin",
        "fulfillment lagerleiter",
        "fulfillment lagerleiterin",
        "bestandslager leitung",
        "lagerbetrieb leitung",
        "lagerverantwortlicher",
        "lagerverantwortliche"
      ]
    },
    "Fuhrparkleiter": {
      "label": "Fuhrparkleiter",
      "aliases": [
        "fuhrparkleiter",
        "fuhrparkleiterin",
        "fleet manager",
        "flottenmanager",
        "flottenmanagerin",
        "leiter fuhrpark",
        "leiterin fuhrpark",
        "fahrzeugflotte leitung",
        "flottenverantwortlicher",
        "flottenverantwortliche",
        "dienstwagen manager",
        "dienstwagen managerin",
        "transportflotte leitung",
        "fahrzeugmanagement leitung",
        "fleet operations manager",
        "fuhrparkmanagement",
        "logistik fleet manager",
        "fahrzeugpark leitung",
        "mobilitätsmanager flotte",
        "mobilitaetsmanager flotte"
      ]
    },
    "Eventmanager": {
      "label": "Eventmanager",
      "aliases": [
        "eventmanager",
        "eventmanagerin",
        "veranstaltungsmanager",
        "veranstaltungsmanagerin",
        "event lead",
        "event planner",
        "event planer",
        "event planerIn",
        "leiter events",
        "leiterin events",
        "messemanager",
        "messemanagerin",
        "veranstaltungsleitung",
        "event koordinator",
        "event koordinatorin",
        "event producer",
        "event producerin",
        "corporate events manager",
        "live communication manager",
        "eventverantwortlicher",
        "eventverantwortliche"
      ]
    },
    "Seminarleiter": {
      "label": "Seminarleiter",
      "aliases": [
        "seminarleiter",
        "seminarleiterin",
        "trainer seminar",
        "trainerin seminar",
        "weiterbildungsleiter",
        "weiterbildungsleiterin",
        "schulungsleiter",
        "schulungsleiterin",
        "academy manager",
        "akademieleiter",
        "akademieleiterin",
        "kursleiter",
        "kursleiterin",
        "bildungsmanager",
        "bildungsmanagerin",
        "training manager",
        "training managerin",
        "learning development manager",
        "ld manager",
        "fortbildungsleiter"
      ]
    },
    "Coach": {
      "label": "Coach",
      "aliases": [
        "coach",
        "business coach",
        "business coachin",
        "verkaufscoach",
        "verkaufscoachin",
        "karrierecoach",
        "karrierecoachin",
        "führungskräftecoach",
        "fuehrungskraeftecoach",
        "führungskräftecoachin",
        "fuehrungskraeftecoachin",
        "personal coach",
        "mental coach",
        "team coach",
        "systemischer coach",
        "systemische coachin",
        "berater coach",
        "beraterin coach",
        "trainer coach",
        "trainerin coach"
      ]
    },
    "Trainer": {
      "label": "Trainer",
      "aliases": [
        "trainer",
        "trainerin",
        "sales trainer",
        "sales trainerin",
        "vertriebstrainer",
        "vertriebstrainerin",
        "kommunikationstrainer",
        "kommunikationstrainerin",
        "soft skill trainer",
        "soft skill trainerin",
        "fachtrainer",
        "fachtrainerin",
        "produktschulungs trainer",
        "produktschulungs trainerin",
        "seminartrainer",
        "seminartrainerin",
        "workshop trainer",
        "workshop trainerin",
        "corporate trainer",
        "corporate trainerin"
      ]
    },
    "Anwender": {
      "label": "Anwender",
      "aliases": [
        "anwender",
        "anwenderin",
        "nutzer",
        "nutzerin",
        "user",
        "endnutzer",
        "endnutzerin",
        "benutzer",
        "benutzerin",
        "operator",
        "operatorin",
        "sachbearbeiter als nutzer",
        "sachbearbeiterin als nutzerin",
        "mitarbeiter als anwender",
        "mitarbeiterin als anwenderin",
        "täglicher nutzer",
        "taeglicher nutzer",
        "tägliche nutzerin",
        "taegliche nutzerin",
        "operative nutzungsperson"
      ]
    },
    "Power-User": {
      "label": "Power-User",
      "aliases": [
        "power user",
        "power-user",
        "key user",
        "key-user",
        "super user",
        "super-user",
        "hauptanwender",
        "hauptanwenderin",
        "systemverantwortlicher anwender",
        "systemverantwortliche anwenderin",
        "fachlicher key user",
        "fachliche key userin",
        "prozess key user",
        "prozess key userin",
        "admin anwender",
        "admin anwenderin",
        "multiplikator system",
        "multiplikatorin system",
        "pilotnutzer",
        "pilotnutzerin"
      ]
    },
    "Pilotkunde": {
      "label": "Pilotkunde",
      "aliases": [
        "pilotkunde",
        "pilotkundin",
        "testkunde",
        "testkundin",
        "beta kunde",
        "beta kundin",
        "early adopter kunde",
        "early adopter kundin",
        "pilotanwender",
        "pilotanwenderin",
        "testanwender",
        "testanwenderin",
        "referenzkunde pilot",
        "referenzkundin pilot",
        "erstanwender",
        "erstanwenderin",
        "probierkunde",
        "probierkundin",
        "pilotprojekt kunde",
        "pilotprojekt kundin"
      ]
    }
  },
  "persona_psychology": {
    "Dominanter Entscheider": {
      "label": "Dominanter Entscheider",
      "aliases": [
        "dominanter entscheider",
        "entscheider typ",
        "dominanter typ",
        "durchsetzungsstarker typ",
        "machtmensch",
        "macher mit führungsanspruch",
        "kontrolltyp",
        "kontrollfreak",
        "willensstarker mensch",
        "starker charakter",
        "chef typ",
        "boss typ",
        "führungstyp",
        "alpha typ",
        "direkter typ",
        "jemand der gerne entscheidet",
        "jemand der die kontrolle haben will",
        "jemand der klare ansagen macht",
        "jemand der führen will",
        "jemand der sich durchsetzt",
        "person die das kommando übernimmt",
        "person die schnell entscheidet",
        "person mit führungsanspruch",
        "typ der nicht lange diskutiert",
        "typ der ergebnisse sehen will",
        "typ der gerne vorgibt wo es langgeht",
        "entschlossener mensch",
        "autoritärer typ",
        "fordernder typ",
        "zielstarker mensch"
      ]
    },
    "Strategischer Planer": {
      "label": "Strategischer Planer",
      "aliases": [
        "strategischer planer",
        "strategie typ",
        "strategischer denker",
        "planer typ",
        "mastermind typ",
        "systemdenker",
        "konzeptmensch",
        "zukunftsplaner",
        "langfristiger planer",
        "visionärer planer",
        "taktiker",
        "schachspieler typ",
        "strukturierter stratege",
        "jemand der langfristig denkt",
        "jemand der einen plan braucht",
        "jemand der alles vorausdenkt",
        "jemand der systeme baut",
        "jemand der strategisch arbeitet",
        "person die in großen zusammenhängen denkt",
        "person die das große ganze sieht",
        "typ der nicht spontan sondern geplant handelt",
        "typ der langfristige ziele verfolgt",
        "typ der mehrere schritte vorausdenkt",
        "vorausschauender mensch",
        "analytischer planer",
        "vision mit plan",
        "planvoller mensch",
        "konzeptioneller typ"
      ]
    },
    "Analytischer Prüfer": {
      "label": "Analytischer Prüfer",
      "aliases": [
        "analytischer prüfer",
        "analytischer typ",
        "prüfer typ",
        "zahlenmensch",
        "datenmensch",
        "faktenmensch",
        "logiker",
        "sachlicher typ",
        "rationaler typ",
        "kritischer analyst",
        "analyse typ",
        "detailprüfer",
        "qualitätsprüfer",
        "jemand der fakten braucht",
        "jemand der alles genau prüft",
        "jemand der beweise sehen will",
        "jemand der daten sehen will",
        "jemand der logisch überzeugt werden muss",
        "person die analytisch entscheidet",
        "person die nichts ungeprüft akzeptiert",
        "typ der erst analysiert und dann entscheidet",
        "typ der zahlen daten fakten braucht",
        "typ der unklare aussagen hinterfragt",
        "typ der genau wissen will ob etwas stimmt",
        "sachorientierter mensch",
        "nüchterner prüfer",
        "rationaler entscheider",
        "faktenbasierter mensch"
      ]
    },
    "Detailorientierter Perfektionist": {
      "label": "Detailorientierter Perfektionist",
      "aliases": [
        "detailorientierter perfektionist",
        "perfektionist",
        "genauer typ",
        "sorgfältiger typ",
        "detailmensch",
        "qualitätsmensch",
        "präziser mensch",
        "akkurater typ",
        "gewissenhafter typ",
        "ordentlicher typ",
        "fehlervermeider",
        "korrektheitsmensch",
        "jemand der alles perfekt machen will",
        "jemand der keine fehler akzeptiert",
        "jemand der sehr genau arbeitet",
        "jemand der auf jedes detail achtet",
        "person die sauberkeit und korrektheit braucht",
        "person die lieber langsam aber richtig arbeitet",
        "typ der qualität vor tempo stellt",
        "typ der standards ernst nimmt",
        "typ der ungenauigkeit nicht mag",
        "typ der alles ordentlich dokumentieren will",
        "pingeliger typ",
        "gründlicher mensch",
        "sauberer arbeiter",
        "qualitätsorientierter mensch",
        "präzisionsmensch",
        "korrekter typ"
      ]
    },
    "Sicherheitsbewusster Bewahrer": {
      "label": "Sicherheitsbewusster Bewahrer",
      "aliases": [
        "sicherheitsbewusster bewahrer",
        "sicherheitsmensch",
        "vorsichtiger typ",
        "konservativer typ",
        "bewahrer typ",
        "sicherheitsorientierter typ",
        "risikoscheuer mensch",
        "stabilitätsmensch",
        "traditionsmensch",
        "routine mensch",
        "jemand der sicherheit braucht",
        "jemand der kein risiko will",
        "jemand der bewährtes bevorzugt",
        "jemand der veränderung skeptisch sieht",
        "person die planbarkeit braucht",
        "person die stabile rahmen braucht",
        "person die nicht gerne experimentiert",
        "typ der sicherheit wichtiger findet als chancen",
        "typ der lieber beim bekannten bleibt",
        "typ der klare regeln braucht",
        "typ der nichts überstürzen will",
        "absicherer",
        "vorsichtiger entscheider",
        "defensiver typ",
        "verlässlichkeit suchender mensch",
        "stabilität suchender typ",
        "planbarkeitstyp"
      ]
    },
    "Pflichtbewusster Organisator": {
      "label": "Pflichtbewusster Organisator",
      "aliases": [
        "pflichtbewusster organisator",
        "organisator typ",
        "ordnungs mensch",
        "struktur mensch",
        "verantwortungsbewusster typ",
        "disziplinierter typ",
        "zuverlässiger typ",
        "regeltyp",
        "administrator typ",
        "manager typ",
        "prozessmensch",
        "ablaufmensch",
        "jemand der ordnung schafft",
        "jemand der verantwortung ernst nimmt",
        "jemand der dinge zuverlässig erledigt",
        "jemand der klare abläufe braucht",
        "person die strukturen einhält",
        "person die organisiert arbeitet",
        "person die versprechen ernst nimmt",
        "typ der pflicht und zuverlässigkeit wichtig findet",
        "typ der prozesse sauber einhält",
        "typ der chaos nicht mag",
        "typ der termine und regeln beachtet",
        "verlässlicher organisator",
        "strukturierter umsetzer",
        "pflichtmensch",
        "ordnungsmacher",
        "prozessdenker"
      ]
    },
    "Ruhiger Unterstützer": {
      "label": "Ruhiger Unterstützer",
      "aliases": [
        "ruhiger unterstützer",
        "unterstützer typ",
        "stiller helfer",
        "ruhiger typ",
        "loyaler typ",
        "geduldiger mensch",
        "teamplayer",
        "verlässlicher begleiter",
        "ruhender pol",
        "stabiler mensch",
        "ausgleichender typ",
        "jemand der andere unterstützt",
        "jemand der nicht im mittelpunkt stehen muss",
        "jemand der geduldig zuhört",
        "jemand der zuverlässig im hintergrund hilft",
        "person die ruhe ausstrahlt",
        "person die stabilität gibt",
        "person die anderen den rücken stärkt",
        "typ der gerne hilft ohne laut zu sein",
        "typ der langfristige beziehungen schätzt",
        "typ der lieber unterstützt als führt",
        "typ der harmonie und stabilität sucht",
        "hilfsbereiter ruhiger mensch",
        "stabiler teammensch",
        "geduldiger unterstützer",
        "loyaler begleiter",
        "ruhiger teamplayer"
      ]
    },
    "Harmonieorientierter Vermittler": {
      "label": "Harmonieorientierter Vermittler",
      "aliases": [
        "harmonieorientierter vermittler",
        "vermittler typ",
        "harmonie mensch",
        "friedensstifter",
        "ausgleicher",
        "diplomatischer typ",
        "konfliktvermeider",
        "deeskalierender typ",
        "sozialer ausgleicher",
        "jemand der harmonie braucht",
        "jemand der konflikte vermeiden will",
        "jemand der zwischen menschen vermittelt",
        "jemand der streit schlecht aushält",
        "person die ausgleich schaffen will",
        "person die auf gute stimmung achtet",
        "person die lieber verbindet als spaltet",
        "typ der niemanden verletzen will",
        "typ der kompromisse sucht",
        "typ der konflikt früh entschärft",
        "typ der soziale spannung spürt",
        "versöhnlicher mensch",
        "friedlicher typ",
        "kompromissmensch",
        "beziehungsbewahrender typ",
        "sozial vorsichtiger mensch",
        "diplomatischer mensch"
      ]
    },
    "Sozialer Netzwerker": {
      "label": "Sozialer Netzwerker",
      "aliases": [
        "sozialer netzwerker",
        "netzwerker typ",
        "kontaktmensch",
        "menschenmensch",
        "geselliger typ",
        "kommunikativer typ",
        "beziehungsmensch",
        "kontaktfreudiger mensch",
        "netzwerk mensch",
        "people person",
        "jemand der schnell kontakte knüpft",
        "jemand der gerne mit menschen spricht",
        "jemand der energie aus austausch bekommt",
        "jemand der gern vernetzt",
        "person die leicht auf andere zugeht",
        "person die viele kontakte pflegt",
        "person die in gruppen aufblüht",
        "typ der gern im gespräch ist",
        "typ der soziale verbindungen wichtig findet",
        "typ der menschen zusammenbringt",
        "typ der gerne sichtbar und ansprechbar ist",
        "gesprächiger mensch",
        "offener kontakt typ",
        "kommunikationsstarker mensch",
        "beziehungsaktiver typ",
        "gruppenmensch"
      ]
    },
    "Charismatischer Überzeuger": {
      "label": "Charismatischer Überzeuger",
      "aliases": [
        "charismatischer überzeuger",
        "überzeuger typ",
        "charismatischer typ",
        "motivator",
        "begeisterer",
        "inspirator",
        "verkaufstyp",
        "präsentationstyp",
        "kommunikativer überzeuger",
        "mitreißender typ",
        "jemand der andere begeistern kann",
        "jemand der gut überzeugen kann",
        "jemand der menschen mitreißt",
        "jemand der emotional verkauft",
        "person mit starker ausstrahlung",
        "person die ideen gut präsentieren kann",
        "person die andere motiviert",
        "typ der durch energie überzeugt",
        "typ der mit worten wirkt",
        "typ der menschen für etwas gewinnt",
        "typ der positiv und präsent wirkt",
        "begeisternder mensch",
        "inspirierender mensch",
        "kommunikativer motivator",
        "charisma mensch",
        "überzeugungsstarker typ"
      ]
    },
    "Empathischer Berater": {
      "label": "Empathischer Berater",
      "aliases": [
        "empathischer berater",
        "berater typ",
        "empathischer typ",
        "menschenversteher",
        "zuhörer typ",
        "coach typ",
        "mentor typ",
        "therapeutischer typ",
        "einfühlsamer mensch",
        "verständnisvoller typ",
        "jemand der andere gut versteht",
        "jemand der zuhören kann",
        "jemand der zwischen den zeilen liest",
        "jemand der menschen helfen will",
        "person die sich in andere hineinversetzt",
        "person die emotionen gut erkennt",
        "person die gerne berät und begleitet",
        "typ der tiefe gespräche mag",
        "typ der andere entwickeln möchte",
        "typ der emotionale motive erkennt",
        "typ der auf menschliche bedürfnisse achtet",
        "einfühlsamer unterstützer",
        "verständnisvoller berater",
        "sozial sensibler mensch",
        "menschenorientierter coach",
        "empathischer mentor"
      ]
    },
    "Idealistischer Sinnsucher": {
      "label": "Idealistischer Sinnsucher",
      "aliases": [
        "idealistischer sinnsucher",
        "sinnsucher",
        "idealist",
        "werte mensch",
        "wertegetriebener typ",
        "mission mensch",
        "sinnorientierter typ",
        "authentischer typ",
        "träumer typ",
        "tiefgründiger typ",
        "jemand der sinn sucht",
        "jemand der nach werten lebt",
        "jemand der authentisch sein will",
        "jemand der etwas bedeutsames tun möchte",
        "person die nicht nur geld oder status will",
        "person die innere werte wichtig findet",
        "person die oberflächlichkeit ablehnt",
        "typ der bedeutung hinter dingen sucht",
        "typ der eine mission braucht",
        "typ der mit falschen werten nicht leben kann",
        "typ der echte verbindung und sinn sucht",
        "werteorientierter mensch",
        "sinngetriebener mensch",
        "idealistischer mensch",
        "authentizität suchender typ",
        "tiefgründiger idealist"
      ]
    },
    "Kreativer Freigeist": {
      "label": "Kreativer Freigeist",
      "aliases": [
        "kreativer freigeist",
        "freigeist",
        "kreativer typ",
        "ideen mensch",
        "künstlerischer typ",
        "unkonventioneller typ",
        "fantasievoller mensch",
        "origineller typ",
        "kreativer kopf",
        "freiheitskünstler",
        "jemand der eigene ideen entwickeln will",
        "jemand der frei denken muss",
        "jemand der starre vorgaben nicht mag",
        "jemand der kreativ und anders denkt",
        "person die unkonventionelle wege mag",
        "person die sich ausdrücken will",
        "person die neue ideen liebt",
        "typ der inspiration und freiheit braucht",
        "typ der nicht in schubladen passt",
        "typ der anders sein darf",
        "typ der kreative entfaltung sucht",
        "ideenreicher mensch",
        "kreativer individualist",
        "unkonventioneller denker",
        "fantasievoller kopf",
        "freier kreativer mensch"
      ]
    },
    "Innovativer Visionär": {
      "label": "Innovativer Visionär",
      "aliases": [
        "innovativer visionär",
        "visionär",
        "innovator",
        "zukunftsmensch",
        "veränderer",
        "pionier typ",
        "fortschrittlicher typ",
        "neudenker",
        "disruptiver denker",
        "ideenpionier",
        "jemand der neues schaffen will",
        "jemand der zukunft gestalten will",
        "jemand der alte systeme verändern will",
        "jemand der innovation liebt",
        "person die chancen vor anderen sieht",
        "person die neue möglichkeiten erkennt",
        "person die nicht beim status quo bleiben will",
        "typ der transformation spannend findet",
        "typ der große ideen verfolgt",
        "typ der neue wege öffnen will",
        "typ der veränderung antreibt",
        "zukunftsorientierter mensch",
        "fortschrittsmensch",
        "pioniergeist",
        "visionärer erneuerer",
        "neuerfinder typ"
      ]
    },
    "Pragmatischer Umsetzer": {
      "label": "Pragmatischer Umsetzer",
      "aliases": [
        "pragmatischer umsetzer",
        "umsetzer typ",
        "praktischer typ",
        "macher typ",
        "hands on typ",
        "lösungsorientierter typ",
        "realistischer macher",
        "tatmensch",
        "anpacker",
        "praktiker",
        "jemand der direkt ins tun kommt",
        "jemand der praktische lösungen sucht",
        "jemand der nicht lange theoretisiert",
        "jemand der macht statt redet",
        "person die schnell umsetzt",
        "person die konkrete probleme lösen will",
        "person die greifbare ergebnisse braucht",
        "typ der praxis wichtiger findet als theorie",
        "typ der einfach anfängt",
        "typ der lösungen ausprobiert",
        "typ der bodenständig handelt",
        "hands on mensch",
        "praktischer problemlöser",
        "direkter umsetzer",
        "tatkräftiger mensch",
        "realistischer umsetzer"
      ]
    },
    "Abenteuerlustiger Macher": {
      "label": "Abenteuerlustiger Macher",
      "aliases": [
        "abenteuerlustiger macher",
        "abenteurer typ",
        "risikotyp",
        "action typ",
        "draufgänger",
        "wagnis mensch",
        "risikobereiter typ",
        "chancenmensch",
        "spontaner macher",
        "erlebnismensch",
        "jemand der risiko eingeht",
        "jemand der action braucht",
        "jemand der chancen schnell nutzt",
        "jemand der lieber ausprobiert als absichert",
        "person die unsicherheit spannend findet",
        "person die ins kalte wasser springt",
        "person die schnelle dynamik mag",
        "typ der abenteuer und bewegung braucht",
        "typ der mutig handelt",
        "typ der nicht zu lange wartet",
        "typ der große chancen sieht",
        "wagnisfreudiger mensch",
        "mutiger macher",
        "schneller chancen nutzer",
        "dynamischer risikotyp",
        "handlungsstarker abenteurer"
      ]
    },
    "Wettbewerbsorientierter Performer": {
      "label": "Wettbewerbsorientierter Performer",
      "aliases": [
        "wettbewerbsorientierter performer",
        "performer typ",
        "leistungsmensch",
        "erfolgsmensch",
        "wettbewerbstyp",
        "karrieremensch",
        "ambitionierter typ",
        "ehrgeiziger typ",
        "top performer",
        "high achiever",
        "jemand der gewinnen will",
        "jemand der besser sein will als andere",
        "jemand der erfolg sichtbar machen will",
        "jemand der leistung braucht",
        "person die sich über erfolg definiert",
        "person die hohe ziele verfolgt",
        "person die sich gern misst",
        "typ der karriere und aufstieg sucht",
        "typ der durch leistung auffallen will",
        "typ der mittelmaß nicht akzeptiert",
        "typ der ständig besser werden will",
        "erfolgsorientierter mensch",
        "ambitionierter mensch",
        "leistungsorientierter typ",
        "kompetitiver typ",
        "zielgetriebener performer"
      ]
    },
    "Anerkennungsorientierter Statusmensch": {
      "label": "Anerkennungsorientierter Statusmensch",
      "aliases": [
        "anerkennungsorientierter statusmensch",
        "statusmensch",
        "anerkennungstyp",
        "prestige mensch",
        "image mensch",
        "reputationsmensch",
        "sichtbarkeitstyp",
        "lob typ",
        "bestätigung suchender typ",
        "wertschätzungsmensch",
        "jemand der anerkennung braucht",
        "jemand der gesehen werden will",
        "jemand dem status wichtig ist",
        "jemand der wertschätzung sucht",
        "person die auf außenwirkung achtet",
        "person die respekt und rang wichtig findet",
        "person die positives feedback braucht",
        "typ der nicht übersehen werden will",
        "typ der prestige und reputation beachtet",
        "typ der sichtbare erfolge wichtig findet",
        "typ der soziale bestätigung sucht",
        "imagebewusster mensch",
        "prestigeorientierter typ",
        "statusorientierter mensch",
        "loborientierter typ",
        "anerkennung suchender mensch"
      ]
    },
    "Unabhängiger Individualist": {
      "label": "Unabhängiger Individualist",
      "aliases": [
        "unabhängiger individualist",
        "individualist",
        "unabhängiger typ",
        "freiheitsmensch",
        "autonomie mensch",
        "selbstbestimmter typ",
        "eigenständiger mensch",
        "nonkonformist",
        "rebellischer typ",
        "eigenbrötler",
        "jemand der unabhängig sein will",
        "jemand der frei entscheiden möchte",
        "jemand der keine kontrolle mag",
        "jemand der seinen eigenen weg geht",
        "person die selbstbestimmung braucht",
        "person die ungern fremdgesteuert wird",
        "person die eigene regeln braucht",
        "typ der freiheit wichtiger findet als anpassung",
        "typ der alleine gut arbeiten kann",
        "typ der sich nicht gerne einordnen lässt",
        "typ der eigene entscheidungen treffen will",
        "autonomer mensch",
        "selbstständiger denker",
        "freier individualist",
        "unabhängigkeitsmensch",
        "eigenwilliger typ"
      ]
    },
    "Fürsorglicher Beschützer": {
      "label": "Fürsorglicher Beschützer",
      "aliases": [
        "fürsorglicher beschützer",
        "beschützer typ",
        "fürsorglicher typ",
        "kümmerer",
        "helfer typ",
        "warmherziger typ",
        "familienmensch",
        "schutzmensch",
        "versorger typ",
        "loyaler beschützer",
        "jemand der sich um andere kümmert",
        "jemand der andere schützen will",
        "jemand der gerne gebraucht wird",
        "jemand der verantwortung für andere übernimmt",
        "person die fürsorge zeigt",
        "person die nahestehende menschen unterstützt",
        "person die sicherheit für andere schafft",
        "typ der familie und vertraute beziehungen wichtig findet",
        "typ der im hintergrund viel trägt",
        "typ der nicht laut aber zuverlässig ist",
        "typ der andere nicht im stich lässt",
        "warmherziger helfer",
        "stiller beschützer",
        "treuer unterstützer",
        "familienorientierter mensch",
        "fürsorglicher mensch"
      ]
    },
    "Traditioneller Gemeinschaftsmensch": {
      "label": "Traditioneller Gemeinschaftsmensch",
      "aliases": [
        "traditioneller gemeinschaftsmensch",
        "gemeinschaftsmensch",
        "traditionsmensch",
        "konventioneller typ",
        "sozialer bewahrer",
        "gruppenmensch",
        "vereinsmensch",
        "gastgeber typ",
        "gemeinschaftstyp",
        "sozial eingebundener typ",
        "jemand dem gemeinschaft wichtig ist",
        "jemand der dazugehören möchte",
        "jemand der soziale rollen ernst nimmt",
        "jemand der tradition und zusammenhalt schätzt",
        "person die gerne teil einer gruppe ist",
        "person die vertraute soziale ordnung mag",
        "person die sich um das miteinander kümmert",
        "typ der feste gemeinschaften braucht",
        "typ der bewährte soziale regeln achtet",
        "typ der familie verein oder team wichtig findet",
        "typ der nicht allein außerhalb stehen will",
        "sozial traditioneller mensch",
        "gemeinschaftsorientierter typ",
        "konventioneller gemeinschaftsmensch",
        "verbundener typ",
        "sozialer ordnungsmensch"
      ]
    },
    "Kritischer Skeptiker": {
      "label": "Kritischer Skeptiker",
      "aliases": [
        "kritischer skeptiker",
        "skeptiker",
        "kritischer typ",
        "hinterfrager",
        "zweifler",
        "misstrauischer typ",
        "prüfender skeptiker",
        "vorsichtiger kritiker",
        "realitätsprüfer",
        "kritischer denker",
        "jemand der alles hinterfragt",
        "jemand der nicht leicht vertraut",
        "jemand der erst überzeugt werden muss",
        "jemand der schwachstellen sucht",
        "person die versprechen kritisch prüft",
        "person die schnell erkennt wenn etwas nicht stimmt",
        "person die nicht auf schöne worte hereinfällt",
        "typ der skeptisch bleibt bis beweise da sind",
        "typ der risiken und fehler sucht",
        "typ der dinge nicht einfach glaubt",
        "typ der kritische fragen stellt",
        "zweifelnder prüfer",
        "kritischer realitätsmensch",
        "misstrauischer prüfer",
        "skeptischer entscheider",
        "harter hinterfrager"
      ]
    },
    "Konfliktfreudiger Herausforderer": {
      "label": "Konfliktfreudiger Herausforderer",
      "aliases": [
        "konfliktfreudiger herausforderer",
        "herausforderer typ",
        "challenger",
        "debattierer typ",
        "streitbarer typ",
        "provokanter typ",
        "konfrontativer typ",
        "argumentativer typ",
        "widerspruchs typ",
        "unbequemer typ",
        "jemand der gerne diskutiert",
        "jemand der andere herausfordert",
        "jemand der widerspruch nicht scheut",
        "jemand der provokant fragt",
        "person die bestehende annahmen angreift",
        "person die argumente testen will",
        "person die konflikt nicht vermeidet",
        "typ der geistige reibung braucht",
        "typ der nicht einfach zustimmt",
        "typ der gerne gegenhält",
        "typ der schwache argumente zerlegt",
        "streitlustiger denker",
        "kritischer herausforderer",
        "provokanter denker",
        "argumentationsstarker mensch",
        "unbequemer fragesteller"
      ]
    },
    "Zurückhaltender Beobachter": {
      "label": "Zurückhaltender Beobachter",
      "aliases": [
        "zurückhaltender beobachter",
        "beobachter typ",
        "introvertierter typ",
        "ruhiger beobachter",
        "stiller typ",
        "reservierter typ",
        "nachdenklicher typ",
        "leiser mensch",
        "diskreter typ",
        "still wirkender mensch",
        "jemand der erst beobachtet",
        "jemand der nicht sofort redet",
        "jemand der lieber zuhört als spricht",
        "jemand der kleine runden bevorzugt",
        "person die nicht im mittelpunkt stehen will",
        "person die ruhe braucht",
        "person die soziale pausen braucht",
        "typ der tief nachdenkt bevor er spricht",
        "typ der zurückhaltend kommuniziert",
        "typ der lieber im hintergrund bleibt",
        "typ der nicht ständig austausch braucht",
        "ruhiger denker",
        "stiller beobachter",
        "leiser analytiker",
        "reservierter mensch",
        "bedachter beobachter"
      ]
    },
    "Lernorientierter Entwickler": {
      "label": "Lernorientierter Entwickler",
      "aliases": [
        "lernorientierter entwickler",
        "entwickler typ",
        "wachstums mensch",
        "lernmensch",
        "growth mindset typ",
        "weiterentwickler",
        "selbstverbesserer",
        "fortschrittsmensch",
        "neugieriger lerner",
        "entwicklungsorientierter typ",
        "jemand der wachsen will",
        "jemand der sich verbessern möchte",
        "jemand der gerne lernt",
        "jemand der feedback nutzen will",
        "person die persönliche entwicklung sucht",
        "person die besser werden will",
        "person die offen für neue fähigkeiten ist",
        "typ der nicht stehen bleiben will",
        "typ der entwicklung wichtiger findet als bequemlichkeit",
        "typ der aus fehlern lernen will",
        "typ der sich ständig weiterentwickelt",
        "wachstumsorientierter mensch",
        "lernfreudiger typ",
        "entwicklungsgetriebener mensch",
        "verbesserungsorientierter typ",
        "potenzialentwickler"
      ]
    },
    "Erlebnisorientierter Entertainer": {
      "label": "Erlebnisorientierter Entertainer",
      "aliases": [
        "erlebnisorientierter entertainer",
        "entertainer typ",
        "performer typ",
        "showmensch",
        "bühnenmensch",
        "spaßmensch",
        "erlebnismensch",
        "momentmensch",
        "lebensfroher typ",
        "unterhaltungstyp",
        "jemand der gute stimmung macht",
        "jemand der menschen unterhält",
        "jemand der den moment erleben will",
        "jemand der gerne sichtbar ist",
        "person die energie und spaß in gruppen bringt",
        "person die spontan und lebendig wirkt",
        "person die erlebnisse wichtiger findet als pläne",
        "typ der gerne lacht und andere mitzieht",
        "typ der atmosphäre erzeugt",
        "typ der im moment aufblüht",
        "typ der gerne auf der bühne steht",
        "lebendiger performer",
        "spontaner entertainer",
        "sozialer performer",
        "stimmungsmacher",
        "emotionaler erlebnistyp"
      ]
    }
  },
  "offer_category": {
    "Recruiting / Mitarbeitergewinnung": {
      "label": "Recruiting / Mitarbeitergewinnung",
      "aliases": [
        "recruiting",
        "mitarbeitergewinnung",
        "personalgewinnung",
        "fachkräftegewinnung",
        "bewerbergewinnung",
        "social recruiting",
        "active sourcing",
        "headhunting",
        "executive search",
        "personalberatung",
        "zeitarbeit",
        "staffing",
        "talent acquisition",
        "employer branding",
        "stellenbesetzung",
        "bewerberpipeline",
        "jobanzeigen",
        "personalvermittlung",
        "stellenanzeige",
        "kandidatensuche",
        "personalbeschaffung",
        "talent sourcing",
        "recruiting service",
        "personalsuche"
      ]
    },
    "Leadgenerierung": {
      "label": "Leadgenerierung",
      "aliases": [
        "leadgenerierung",
        "lead generation",
        "leads",
        "kontaktanfragen",
        "terminleads",
        "b2b leads",
        "qualifizierte leads",
        "neukundenanfragen",
        "inbound leads",
        "outbound leads",
        "leadkampagne",
        "leadgewinnung",
        "vertriebsleads",
        "kundenkontakte",
        "lead funnel",
        "lead pipeline",
        "anfragen",
        "neuanfragen",
        "kontaktgenerierung",
        "interessenten",
        "neukunden",
        "interessentengenerierung",
        "prospektgewinnung"
      ]
    },
    "Marketing / Werbung": {
      "label": "Marketing / Werbung",
      "aliases": [
        "marketing",
        "werbung",
        "online marketing",
        "performance marketing",
        "digital marketing",
        "paid ads",
        "meta ads",
        "facebook ads",
        "instagram ads",
        "google ads",
        "seo",
        "sea",
        "content marketing",
        "social media marketing",
        "kampagnen",
        "werbekampagne",
        "brand awareness",
        "growth marketing",
        "kundengewinnung",
        "reichweite",
        "onlinewerbung",
        "digitalwerbung",
        "markenkommunikation"
      ]
    },
    "Vertrieb / Sales": {
      "label": "Vertrieb / Sales",
      "aliases": [
        "vertrieb",
        "sales",
        "verkauf",
        "closing",
        "abschlüsse",
        "abschlussgespräche",
        "telefonvertrieb",
        "sales outsourcing",
        "vertriebsaufbau",
        "sales enablement",
        "neukundengewinnung",
        "akquise",
        "kaltakquise",
        "vertriebsprozess",
        "outbound sales",
        "b2b vertrieb",
        "verkaufsgespräche",
        "vertriebsoptimierung",
        "abschlussrate",
        "vertriebsunterstützung",
        "außendienst",
        "innendienst",
        "verkaufsförderung"
      ]
    },
    "Terminierung / Setter": {
      "label": "Terminierung / Setter",
      "aliases": [
        "terminierung",
        "appointment setting",
        "setter",
        "terminsetter",
        "terminvereinbarung",
        "kalenderbuchung",
        "erstgespräch buchen",
        "sdr",
        "sales development",
        "qualifizierungsgespräch",
        "vorgespräch",
        "verkaufstermin",
        "beratungstermin",
        "demo buchen",
        "call booking",
        "terminakquise",
        "vertriebstermine",
        "ersttermin",
        "termine generieren",
        "terminbuchung",
        "telefontermin",
        "kalender füllen"
      ]
    },
    "Beratung / Consulting": {
      "label": "Beratung / Consulting",
      "aliases": [
        "beratung",
        "consulting",
        "unternehmensberatung",
        "strategie",
        "strategieberatung",
        "prozessberatung",
        "business consulting",
        "wachstumsberatung",
        "optimierung",
        "beratungshaus",
        "managementberatung",
        "organisationsberatung",
        "digitalberatung",
        "prozessoptimierung",
        "geschäftsentwicklung",
        "business development",
        "sparring",
        "transformation",
        "beraterleistung",
        "beratungsleistung",
        "strategieentwicklung",
        "externer berater",
        "business analyse"
      ]
    },
    "Coaching / Mentoring": {
      "label": "Coaching / Mentoring",
      "aliases": [
        "coaching",
        "business coaching",
        "sales coaching",
        "mindset coaching",
        "mentoring",
        "mentor",
        "begleitung",
        "1:1 coaching",
        "gruppencoaching",
        "coaching programm",
        "premium coaching",
        "high ticket coaching",
        "persönlichkeitsentwicklung",
        "leadership coaching",
        "executive coaching",
        "performance coaching",
        "life coaching",
        "business coach",
        "vertriebscoach",
        "coach",
        "wachstumsbegleitung",
        "unternehmer coaching",
        "expertenbegleitung"
      ]
    },
    "Training / Weiterbildung": {
      "label": "Training / Weiterbildung",
      "aliases": [
        "training",
        "weiterbildung",
        "schulung",
        "seminar",
        "workshop",
        "akademie",
        "kurs",
        "e-learning",
        "ausbildung",
        "fortbildung",
        "onlinekurs",
        "lernprogramm",
        "mitarbeiterschulung",
        "vertriebstraining",
        "sales training",
        "masterclass",
        "bootcamp",
        "training academy",
        "qualifizierung",
        "lehrgang",
        "unternehmensschulung",
        "fachschulung",
        "praxisworkshop"
      ]
    },
    "Software / SaaS": {
      "label": "Software / SaaS",
      "aliases": [
        "software",
        "saas",
        "software as a service",
        "tool",
        "plattform",
        "app",
        "crm",
        "automatisierung",
        "cloud software",
        "subscription software",
        "abo software",
        "softwarelösung",
        "webapp",
        "dashboard",
        "workflow software",
        "business software",
        "enterprise software",
        "ki software",
        "ai software",
        "no-code",
        "subscription",
        "unternehmenssoftware",
        "branchensoftware",
        "cloud lösung"
      ]
    },
    "IT-Dienstleistungen": {
      "label": "IT-Dienstleistungen",
      "aliases": [
        "it dienstleistung",
        "it services",
        "systemhaus",
        "managed services",
        "msp",
        "cybersecurity",
        "cloud services",
        "it support",
        "softwareentwicklung",
        "it beratung",
        "it betreuung",
        "netzwerkbetreuung",
        "server betreuung",
        "helpdesk",
        "devops",
        "cloud migration",
        "microsoft 365",
        "it outsourcing",
        "infrastruktur",
        "it sicherheit",
        "it service provider",
        "it partner",
        "it firma"
      ]
    },
    "Finanzen / Finanzierung": {
      "label": "Finanzen / Finanzierung",
      "aliases": [
        "finanzen",
        "finanzberatung",
        "finanzierung",
        "investment",
        "vermögensberatung",
        "anlageberatung",
        "kredit",
        "leasing",
        "buchhaltung",
        "fördermittel",
        "finanzplanung",
        "kapitalanlage",
        "finanzdienstleistung",
        "liquidität",
        "unternehmensfinanzierung",
        "immobilienfinanzierung",
        "baufinanzierung",
        "steueroptimierung",
        "controlling",
        "lohnbuchhaltung",
        "factoring",
        "finanzierungslösung",
        "betriebsmittelfinanzierung"
      ]
    },
    "Versicherung / Vorsorge": {
      "label": "Versicherung / Vorsorge",
      "aliases": [
        "versicherung",
        "versicherungen",
        "versicherungsmakler",
        "gewerbeversicherung",
        "krankenversicherung",
        "vorsorge",
        "absicherung",
        "bav",
        "altersvorsorge",
        "betriebshaftpflicht",
        "pkv",
        "private krankenversicherung",
        "lebensversicherung",
        "berufsunfähigkeit",
        "bu",
        "risikovorsorge",
        "versicherungsschutz",
        "versicherungsberatung",
        "sachversicherung",
        "firmenversicherung",
        "finanzvorsorge",
        "versicherungslösung",
        "betriebliche absicherung"
      ]
    },
    "Immobilien": {
      "label": "Immobilien",
      "aliases": [
        "immobilien",
        "immobilienverkauf",
        "immobilienvermittlung",
        "makler",
        "hausverkauf",
        "wohnung verkaufen",
        "property management",
        "hausverwaltung",
        "immobilienmakler",
        "vermietung",
        "immobilienbewertung",
        "objektvermittlung",
        "grundstück",
        "kapitalanlage immobilie",
        "wohnimmobilien",
        "gewerbeimmobilien",
        "immobilienberatung",
        "projektentwicklung",
        "immobilienservice",
        "immobilienunternehmen"
      ]
    },
    "Recht / Legal": {
      "label": "Recht / Legal",
      "aliases": [
        "recht",
        "rechtsberatung",
        "anwalt",
        "kanzlei",
        "legal",
        "vertragsrecht",
        "arbeitsrecht",
        "datenschutz",
        "compliance",
        "rechtsanwalt",
        "steuerrecht",
        "gesellschaftsrecht",
        "it recht",
        "legal service",
        "vertragsprüfung",
        "rechtliche beratung",
        "mandat",
        "juristische dienstleistung",
        "datenschutzberatung",
        "vertragsgestaltung",
        "anwaltsservice",
        "rechtsdienst"
      ]
    },
    "Gesundheit / Therapie": {
      "label": "Gesundheit / Therapie",
      "aliases": [
        "gesundheit",
        "healthcare",
        "therapie",
        "behandlung",
        "praxisleistung",
        "pflegeberatung",
        "medizinische dienstleistung",
        "prävention",
        "physiotherapie",
        "ergotherapie",
        "psychotherapie",
        "heilpraktiker",
        "medizinberatung",
        "patientenbetreuung",
        "gesundheitsberatung",
        "rehabilitation",
        "diagnostik",
        "wellbeing",
        "therapieangebot",
        "gesundheitscoaching",
        "praxismarketing",
        "therapeut",
        "gesundheitsdienst"
      ]
    },
    "Design / Kreativleistung": {
      "label": "Design / Kreativleistung",
      "aliases": [
        "design",
        "branding",
        "brand design",
        "webdesign",
        "grafikdesign",
        "ux design",
        "ui design",
        "kreativleistung",
        "corporate design",
        "logo design",
        "markenauftritt",
        "brand identity",
        "visual identity",
        "creative agency",
        "designagentur",
        "website design",
        "landingpage design",
        "printdesign",
        "motion design",
        "gestaltung",
        "designleistung",
        "werbemittelgestaltung"
      ]
    },
    "E-Commerce / Shop": {
      "label": "E-Commerce / Shop",
      "aliases": [
        "ecommerce",
        "e-commerce",
        "online shop",
        "shopify",
        "amazon",
        "marketplace",
        "webshop",
        "shopoptimierung",
        "onlinehandel",
        "woocommerce",
        "shop system",
        "d2c",
        "produktverkauf",
        "amazon seller",
        "conversion optimierung",
        "marktplatzverkauf",
        "shop setup",
        "produktlisting",
        "onlineshop erstellen",
        "shop betrieb",
        "ecommerce betrieb"
      ]
    },
    "Kundenservice / Support": {
      "label": "Kundenservice / Support",
      "aliases": [
        "kundenservice",
        "customer service",
        "support",
        "kundensupport",
        "callcenter",
        "service center",
        "helpdesk",
        "kundenbetreuung",
        "customer success",
        "service hotline",
        "ticket support",
        "kundenanfragen",
        "beschwerdemanagement",
        "kundenkommunikation",
        "after sales",
        "service outsourcing",
        "telefonservice",
        "chat support",
        "first level support",
        "kundenrückgewinnung",
        "servicequalität",
        "kundenhotline",
        "kundencare"
      ]
    },
    "Outsourcing / externe Prozesse": {
      "label": "Outsourcing / externe Prozesse",
      "aliases": [
        "outsourcing",
        "bpo",
        "business process outsourcing",
        "prozessauslagerung",
        "externer dienstleister",
        "backoffice",
        "assistenz",
        "virtuelle assistenz",
        "remote assistant",
        "va",
        "operativer support",
        "administration",
        "büroservice",
        "prozessdienstleistung",
        "kundenservice outsourcing",
        "vertriebsoutsourcing",
        "personal outsourcing",
        "finance outsourcing",
        "delegieren",
        "büro outsourcing",
        "verwaltungs outsourcing",
        "ausgelagerte prozesse"
      ]
    },
    "Energie / Solar": {
      "label": "Energie / Solar",
      "aliases": [
        "energie",
        "strom",
        "gas",
        "solar",
        "photovoltaik",
        "pv anlage",
        "wärmepumpe",
        "energieberatung",
        "erneuerbare energien",
        "solaranlage",
        "stromtarif",
        "energieoptimierung",
        "heizsystem",
        "energieeffizienz",
        "energieversorgung",
        "grünstrom",
        "speicherlösung",
        "batteriespeicher",
        "solarberatung",
        "energieanlage",
        "heizungsoptimierung"
      ]
    },
    "Sonstiges Angebot": {
      "label": "Sonstiges Angebot",
      "aliases": [
        "sonstiges",
        "sonstige leistung",
        "andere leistung",
        "dienstleistung",
        "diverses"
      ]
    }
  },
  "target_industry": {
    "Handwerk": {
      "label": "Handwerk",
      "aliases": [
        "handwerk",
        "handwerker",
        "dachdecker",
        "elektriker",
        "shk",
        "sanitär",
        "heizung",
        "klima",
        "maler",
        "tischler",
        "schreiner",
        "fliesenleger",
        "gartenbau",
        "metallbau",
        "trockenbau",
        "zimmerer",
        "installateur",
        "bodenleger",
        "fensterbauer",
        "handwerksbetrieb"
      ]
    },
    "Bau / Baugewerbe": {
      "label": "Bau / Baugewerbe",
      "aliases": [
        "bau",
        "bauunternehmen",
        "baugewerbe",
        "hochbau",
        "tiefbau",
        "generalunternehmer",
        "projektentwickler",
        "baubranche",
        "bauleiter",
        "bauprojekt",
        "rohbau",
        "innenausbau",
        "bauplanung",
        "architekt",
        "ingenieurbüro",
        "bausanierung",
        "renovierung",
        "modernisierung",
        "baugeschäft",
        "bauindustrie",
        "baufirma",
        "baugewerbebetrieb"
      ]
    },
    "Produktion / Fertigung": {
      "label": "Produktion / Fertigung",
      "aliases": [
        "produktion",
        "fertigung",
        "hersteller",
        "maschinenbau",
        "anlagenbau",
        "produzierendes gewerbe",
        "fabrik",
        "manufacturing",
        "industrieproduktion",
        "serienfertigung",
        "maschinenhersteller",
        "zulieferer",
        "werkzeugbau",
        "metallverarbeitung",
        "kunststoffverarbeitung",
        "produktionsbetrieb",
        "fertigungsunternehmen",
        "maschinenpark",
        "industrielle fertigung",
        "werksbetrieb",
        "zulieferbetrieb"
      ]
    },
    "Industrie": {
      "label": "Industrie",
      "aliases": [
        "industrie",
        "industriebetrieb",
        "mittelstand",
        "werk",
        "industrieunternehmen",
        "technischer betrieb",
        "b2b industrie",
        "industrial",
        "anlagenbetreiber",
        "technisches unternehmen",
        "fabrikbetrieb",
        "technischer mittelstand",
        "industriebranche",
        "großbetrieb",
        "industrieanlage",
        "kmu",
        "mittelständler",
        "b2b",
        "gewerbe",
        "gewerbebetrieb",
        "industrie mittelstand",
        "industriekunde"
      ]
    },
    "Medizin / Arztpraxen": {
      "label": "Medizin / Arztpraxen",
      "aliases": [
        "arzt",
        "arztpraxis",
        "zahnarzt",
        "kieferorthopädie",
        "medizin",
        "praxis",
        "facharzt",
        "klinik",
        "orthopäde",
        "dermatologe",
        "hausarzt",
        "medizinische praxis",
        "praxisinhaber",
        "gemeinschaftspraxis",
        "mvz",
        "arztzentrum",
        "privatpraxis",
        "kassenarzt",
        "praxismanagement",
        "facharztpraxis",
        "arztgemeinschaft"
      ]
    },
    "Gesundheit / Pflege": {
      "label": "Gesundheit / Pflege",
      "aliases": [
        "pflege",
        "pflegeheim",
        "pflegedienst",
        "physiotherapie",
        "ergotherapie",
        "heilpraktiker",
        "ambulante pflege",
        "stationäre pflege",
        "seniorenheim",
        "pflegeeinrichtung",
        "gesundheitszentrum",
        "therapiepraxis",
        "rehazentrum",
        "patientenversorgung",
        "prävention",
        "betreuung",
        "gesundheitsdienst",
        "soziale einrichtung",
        "pflegebranche",
        "pflegesektor",
        "pflegeunternehmen",
        "gesundheitsbetrieb"
      ]
    },
    "Recht / Kanzleien": {
      "label": "Recht / Kanzleien",
      "aliases": [
        "kanzlei",
        "anwalt",
        "rechtsanwalt",
        "steuerkanzlei",
        "notar",
        "rechtsberatung",
        "legal",
        "fachanwalt",
        "wirtschaftskanzlei",
        "anwaltskanzlei",
        "steuerberater",
        "mandanten",
        "jurist",
        "recht",
        "legal office",
        "compliance",
        "datenschutzkanzlei",
        "arbeitsrecht",
        "vertragsrecht",
        "gesellschaftsrecht",
        "steuerberatung"
      ]
    },
    "Finanzen": {
      "label": "Finanzen",
      "aliases": [
        "finanzen",
        "bank",
        "finanzberater",
        "vermögensberater",
        "investment",
        "buchhaltung",
        "steuerberatung",
        "finanzdienstleister",
        "finanzmakler",
        "anlageberater",
        "kreditberater",
        "leasing",
        "controlling",
        "accounting",
        "wirtschaftsprüfung",
        "finanzplanung",
        "kapitalanlage",
        "finanzbranche",
        "geldanlage",
        "wealthmanagement",
        "finanzunternehmen",
        "kreditinstitut"
      ]
    },
    "Versicherung": {
      "label": "Versicherung",
      "aliases": [
        "versicherung",
        "versicherungsmakler",
        "versicherungsagentur",
        "assekuranz",
        "gewerbeversicherung",
        "krankenkasse",
        "versicherer",
        "maklerbüro",
        "agenturinhaber",
        "versicherungsberater",
        "vorsorge",
        "absicherung",
        "bav",
        "pkv",
        "firmenversicherung",
        "vertrieb versicherung",
        "broker",
        "sachversicherung",
        "lebensversicherung",
        "versicherungsvertrieb",
        "versicherungsbranche",
        "versicherungsbüro"
      ]
    },
    "Immobilien": {
      "label": "Immobilien",
      "aliases": [
        "immobilien",
        "immobilienmakler",
        "makler",
        "hausverwaltung",
        "property management",
        "wohnungswirtschaft",
        "baufinanzierung",
        "immobilienverwaltung",
        "projektentwickler",
        "vermietung",
        "hausverkauf",
        "gewerbeimmobilien",
        "wohnimmobilien",
        "immobilienbüro",
        "real estate",
        "objektverwaltung",
        "immobilieninvestor",
        "immobilienberatung",
        "wohnungsverwaltung",
        "immobilienbranche",
        "immobilienmarkt",
        "maklerunternehmen"
      ]
    },
    "Bildung / Weiterbildung": {
      "label": "Bildung / Weiterbildung",
      "aliases": [
        "bildung",
        "schule",
        "akademie",
        "weiterbildung",
        "universität",
        "seminaranbieter",
        "bildungsträger",
        "e-learning",
        "onlinekurs",
        "fortbildung",
        "nachhilfe",
        "lernplattform",
        "schulungsanbieter",
        "edtech",
        "berufsschule",
        "hochschule",
        "bildungsanbieter",
        "kursanbieter",
        "bildungsinstitut",
        "kurszentrum",
        "bildungseinrichtung",
        "lehranstalt"
      ]
    },
    "HR / Personal": {
      "label": "HR / Personal",
      "aliases": [
        "hr",
        "personal",
        "personalabteilung",
        "human resources",
        "personalwesen",
        "employer branding",
        "mitarbeiterbindung",
        "personalentwicklung",
        "hr manager",
        "people operations",
        "talent management",
        "personalstrategie",
        "arbeitgebermarke",
        "onboarding",
        "mitarbeiterführung",
        "hr team",
        "personalmanagement",
        "employee experience",
        "hr abteilung",
        "personalverantwortlicher",
        "personalbüro"
      ]
    },
    "Recruiting / Personalberatung": {
      "label": "Recruiting / Personalberatung",
      "aliases": [
        "recruiting",
        "personalberatung",
        "headhunter",
        "zeitarbeit",
        "staffing",
        "talent acquisition",
        "social recruiting agentur",
        "personalvermittler",
        "executive search",
        "recruiter",
        "bewerbermanagement",
        "kandidatenvermittlung",
        "fachkräftevermittlung",
        "stellenbesetzung",
        "arbeitnehmerüberlassung",
        "hr dienstleister",
        "recruiting agentur",
        "personalagentur",
        "talent sourcing",
        "jobvermittlung",
        "zeitarbeitsfirma",
        "personaldienstleister"
      ]
    },
    "Marketing / Agenturen": {
      "label": "Marketing / Agenturen",
      "aliases": [
        "marketing",
        "agentur",
        "marketingagentur",
        "werbeagentur",
        "performance marketing",
        "seo agentur",
        "social media agentur",
        "google ads agentur",
        "meta ads agentur",
        "content agentur",
        "brand agency",
        "kreativagentur",
        "digitalagentur",
        "online marketing agentur",
        "lead agency",
        "growth agency",
        "medienagentur",
        "advertising agency",
        "pr agentur",
        "fullservice agentur",
        "werbeunternehmen",
        "marketingunternehmen"
      ]
    },
    "IT / Systemhäuser": {
      "label": "IT / Systemhäuser",
      "aliases": [
        "it",
        "systemhaus",
        "it dienstleister",
        "managed service provider",
        "cybersecurity",
        "cloud",
        "msp",
        "it betreuung",
        "netzwerk",
        "server",
        "it support",
        "helpdesk",
        "it sicherheit",
        "cloud provider",
        "infrastruktur",
        "it beratung",
        "microsoft partner",
        "softwareentwicklung",
        "technikdienstleister",
        "it unternehmen",
        "it firma",
        "technologieunternehmen"
      ]
    },
    "Software / SaaS": {
      "label": "Software / SaaS",
      "aliases": [
        "software",
        "saas",
        "softwareunternehmen",
        "startup",
        "tech company",
        "plattform",
        "app",
        "cloud software",
        "b2b software",
        "softwareanbieter",
        "softwarehersteller",
        "abo software",
        "subscription software",
        "webapp",
        "crm software",
        "ki tool",
        "ai software",
        "tech startup",
        "digital platform",
        "saas startup",
        "softwarefirma",
        "softwarehaus",
        "scaleup"
      ]
    },
    "Telekommunikation": {
      "label": "Telekommunikation",
      "aliases": [
        "telekom",
        "telekommunikation",
        "telefonie",
        "mobilfunk",
        "internetprovider",
        "glasfaser",
        "voip",
        "dsl",
        "breitband",
        "netzanbieter",
        "telekommunikationsanbieter",
        "sip trunk",
        "telefonanlage",
        "5g",
        "connectivity",
        "internetanschluss",
        "provider",
        "cloud telefonie",
        "netzinfrastruktur",
        "tk branche",
        "telekommunikationsunternehmen",
        "telefongesellschaft"
      ]
    },
    "Energie": {
      "label": "Energie",
      "aliases": [
        "energie",
        "energieversorger",
        "strom",
        "gas",
        "solar",
        "photovoltaik",
        "wärmepumpe",
        "erneuerbare energien",
        "energieberatung",
        "pv",
        "stromanbieter",
        "energieanbieter",
        "heizung",
        "energiedienstleister",
        "netzbetreiber",
        "solarteur",
        "batteriespeicher",
        "grünstrom",
        "energieeffizienz",
        "energiebranche",
        "energieunternehmen",
        "versorgungsunternehmen"
      ]
    },
    "Automotive / Fahrzeuge": {
      "label": "Automotive / Fahrzeuge",
      "aliases": [
        "auto",
        "automotive",
        "autohandel",
        "autohaus",
        "werkstatt",
        "fahrzeughandel",
        "leasing",
        "kfz",
        "fahrzeuge",
        "gebrauchtwagen",
        "neuwagen",
        "autowerkstatt",
        "flottenmanagement",
        "fuhrpark",
        "kfz betrieb",
        "reifenservice",
        "car dealership",
        "fahrzeugvertrieb",
        "mobilität",
        "auto service",
        "kfz werkstatt",
        "autohändler"
      ]
    },
    "Logistik / Transport": {
      "label": "Logistik / Transport",
      "aliases": [
        "logistik",
        "transport",
        "spedition",
        "lager",
        "fulfillment",
        "kurierdienst",
        "lieferdienst",
        "spediteur",
        "transportdienstleister",
        "warehousing",
        "supply chain",
        "versand",
        "paketdienst",
        "last mile",
        "flotte",
        "lkw",
        "güterverkehr",
        "lagerlogistik",
        "distribution",
        "expressdienst",
        "logistikunternehmen",
        "transportunternehmen"
      ]
    },
    "Einzelhandel": {
      "label": "Einzelhandel",
      "aliases": [
        "einzelhandel",
        "retail",
        "geschäft",
        "laden",
        "filiale",
        "handel",
        "stationärer handel",
        "fachhandel",
        "shop",
        "store",
        "boutique",
        "händler",
        "warenverkauf",
        "retailer",
        "ladenlokal",
        "verkaufsgeschäft",
        "lokaler handel",
        "fachgeschäft",
        "ladeninhaber",
        "einzelhändler",
        "stationär",
        "handelsunternehmen"
      ]
    },
    "E-Commerce": {
      "label": "E-Commerce",
      "aliases": [
        "ecommerce",
        "e-commerce",
        "online shop",
        "shopify",
        "amazon seller",
        "marktplatz",
        "onlinehandel",
        "webshop",
        "woocommerce",
        "d2c",
        "direct to consumer",
        "shopbetreiber",
        "produktshop",
        "versandhandel",
        "online store",
        "marketplace seller",
        "etsy",
        "otto marketplace",
        "onlineverkauf",
        "onlineshop unternehmen",
        "ecommerce betrieb",
        "internetversand"
      ]
    },
    "Hotellerie": {
      "label": "Hotellerie",
      "aliases": [
        "hotel",
        "hotellerie",
        "pension",
        "ferienwohnung",
        "resort",
        "unterkunft",
        "tourismus",
        "hostel",
        "aparthotel",
        "hotelbetrieb",
        "beherbergung",
        "rezeption",
        "gastgewerbe",
        "hotelgruppe",
        "zimmervermietung",
        "ferienhaus",
        "gästehaus",
        "hospitality",
        "hotelier",
        "beherbergungsbetrieb",
        "unterkunftsbetrieb",
        "boutique hotel"
      ]
    },
    "Gastronomie": {
      "label": "Gastronomie",
      "aliases": [
        "gastronomie",
        "restaurant",
        "cafe",
        "bar",
        "catering",
        "imbiss",
        "food service",
        "bistro",
        "speisen",
        "gastronom",
        "systemgastronomie",
        "restaurantbetrieb",
        "kantine",
        "event catering",
        "foodtruck",
        "takeaway",
        "gastro",
        "lieferservice",
        "gastronomiebetrieb",
        "restaurantservice",
        "außer haus"
      ]
    },
    "Beauty / Ästhetik": {
      "label": "Beauty / Ästhetik",
      "aliases": [
        "beauty",
        "kosmetik",
        "kosmetikstudio",
        "ästhetik",
        "friseur",
        "hairstylist",
        "nagelstudio",
        "wimpern",
        "lash studio",
        "brow studio",
        "permanent makeup",
        "hautbehandlung",
        "med beauty",
        "salon",
        "friseursalon",
        "kosmetikerin",
        "anti aging",
        "ästhetische behandlung",
        "schönheitspflege",
        "beautystudio",
        "beauty salon",
        "schönheitssalon"
      ]
    },
    "Fitness": {
      "label": "Fitness",
      "aliases": [
        "fitness",
        "fitnessstudio",
        "personal trainer",
        "sport",
        "gym",
        "gesundheitsstudio",
        "pt studio",
        "functional training",
        "crossfit",
        "yoga studio",
        "pilates",
        "sportstudio",
        "mitgliedschaft fitness",
        "fitnesscenter",
        "trainingsplan",
        "athletik",
        "personal training",
        "kraftsport",
        "fitnesstrainer",
        "fitnessbetrieb",
        "sportbetrieb",
        "fitnessclub"
      ]
    },
    "Wellness": {
      "label": "Wellness",
      "aliases": [
        "wellness",
        "spa",
        "massage",
        "entspannung",
        "therme",
        "prävention",
        "sauna",
        "wellnesshotel",
        "massagepraxis",
        "health spa",
        "körperbehandlung",
        "regeneration",
        "wellbeing",
        "ayurveda",
        "naturheilkunde",
        "entspannungsangebot",
        "kur",
        "wellnessbetrieb",
        "wellnesszentrum",
        "massagestudio",
        "spa betrieb",
        "wellnessstudio"
      ]
    },
    "Sicherheit": {
      "label": "Sicherheit",
      "aliases": [
        "sicherheit",
        "security",
        "objektschutz",
        "bewachung",
        "alarmtechnik",
        "brandschutz",
        "arbeitsschutz",
        "sicherheitsdienst",
        "wachdienst",
        "gebäudesicherheit",
        "zutrittskontrolle",
        "videoüberwachung",
        "personenschutz",
        "veranstaltungsschutz",
        "alarm system",
        "sicherheitstechnik",
        "risikomanagement",
        "sicherheitsfirma",
        "sicherheitsunternehmen",
        "bewachungsfirma",
        "sicherheitsdienstleister"
      ]
    },
    "Reinigung / Facility Management": {
      "label": "Reinigung / Facility Management",
      "aliases": [
        "reinigung",
        "gebäudereinigung",
        "facility management",
        "hausmeisterservice",
        "objektbetreuung",
        "cleaning",
        "büroreinigung",
        "industriereinigung",
        "unterhaltsreinigung",
        "glasreinigung",
        "facility service",
        "fm",
        "hausmeister",
        "gebäudeservice",
        "objektservice",
        "reinigungsfirma",
        "hygiene service",
        "winterdienst",
        "reinigungsunternehmen",
        "objektreinigung",
        "gebäudemanagement",
        "gebäudereiniger"
      ]
    },
    "Landwirtschaft": {
      "label": "Landwirtschaft",
      "aliases": [
        "landwirtschaft",
        "agrar",
        "bauernhof",
        "landwirt",
        "forstwirtschaft",
        "gartenbau",
        "agrarbetrieb",
        "hof",
        "ackerbau",
        "viehzucht",
        "milchbetrieb",
        "landtechnik",
        "agrartechnik",
        "farm",
        "forstbetrieb",
        "pflanzenbau",
        "tierhaltung",
        "obstbau",
        "weinbau",
        "agrarwirtschaft",
        "bauer",
        "agrarunternehmen"
      ]
    },
    "Öffentlicher Sektor": {
      "label": "Öffentlicher Sektor",
      "aliases": [
        "öffentlicher sektor",
        "behörde",
        "verwaltung",
        "kommune",
        "stadt",
        "landkreis",
        "amt",
        "kommunalverwaltung",
        "public sector",
        "öffentliche hand",
        "ministerium",
        "gemeinde",
        "kreisverwaltung",
        "stadtverwaltung",
        "verwaltungseinrichtung",
        "öffentliche einrichtung",
        "staatsbetrieb",
        "kommunaler betrieb",
        "government",
        "öffentliche verwaltung",
        "staatliche einrichtung"
      ]
    },
    "Nonprofit / Vereine": {
      "label": "Nonprofit / Vereine",
      "aliases": [
        "verein",
        "ngo",
        "nonprofit",
        "gemeinnützig",
        "stiftung",
        "organisation",
        "verband",
        "hilfsorganisation",
        "npo",
        "soziale organisation",
        "förderverein",
        "gemeinnütziger träger",
        "charity",
        "ehrenamt",
        "spendenorganisation",
        "kirchliche organisation",
        "soziale träger",
        "mitgliederverein",
        "interessenverband",
        "gemeinnützige organisation",
        "wohlfahrt"
      ]
    },
    "Privatkunden / Endverbraucher": {
      "label": "Privatkunden / Endverbraucher",
      "aliases": [
        "privatkunde",
        "endkunde",
        "verbraucher",
        "b2c",
        "haushalt",
        "familie",
        "privatperson",
        "konsument",
        "consumer",
        "private kunden",
        "endverbraucher",
        "haushaltskunden",
        "einzelperson",
        "familien",
        "private haushalte",
        "verbrauchermarkt",
        "retail customer",
        "konsumenten",
        "privatmarkt",
        "direktkunde",
        "private nachfrage"
      ]
    },
    "Führungskräfte": {
      "label": "Führungskräfte",
      "aliases": [
        "führungskraft",
        "führungskräfte",
        "manager",
        "teamleiter",
        "abteilungsleiter",
        "projektleiter",
        "middle management",
        "führungsperson",
        "leitungskraft",
        "vorgesetzte",
        "führungsebene",
        "bereichsleiter",
        "niederlassungsleiter",
        "gruppenleiter",
        "schichtleiter",
        "führungsverantwortung",
        "führungsposition",
        "leadership",
        "leitende angestellte",
        "management ebene"
      ]
    },
    "Vertriebler / Sales-Mitarbeiter": {
      "label": "Vertriebler / Sales-Mitarbeiter",
      "aliases": [
        "vertriebler",
        "verkäufer",
        "sales mitarbeiter",
        "außendienstler",
        "innendienstler",
        "account manager",
        "vertriebsmitarbeiter",
        "salesperson",
        "closer",
        "setter",
        "sales rep",
        "kundenberater",
        "vertriebsprofi",
        "sales professional",
        "kundenbetreuer",
        "akquisiteur",
        "vertriebsassistent",
        "sales agent",
        "handelsvertreter",
        "vertriebstalent"
      ]
    },
    "Selbständige / Solopreneure": {
      "label": "Selbständige / Solopreneure",
      "aliases": [
        "selbständige",
        "selbständiger",
        "solopreneur",
        "freelancer",
        "freiberufler",
        "einzelunternehmer",
        "one man show",
        "solo selbständig",
        "freie berufe",
        "freiberuflich",
        "selbstständig",
        "soloselbständig",
        "freischaffend",
        "unabhängige",
        "freier mitarbeiter",
        "freier berater",
        "freier dienstleister",
        "kleinunternehmer",
        "mikroselbständig",
        "solo business"
      ]
    },
    "Existenzgründer": {
      "label": "Existenzgründer",
      "aliases": [
        "existenzgründer",
        "gründer",
        "startup gründer",
        "neu selbständig",
        "gründungsphase",
        "frisch gegründet",
        "business aufbau",
        "unternehmensstart",
        "gründungsinteressent",
        "angehender unternehmer",
        "pre revenue",
        "early stage",
        "gründungswillig",
        "geschäftsidee",
        "businessplan",
        "markteinsteiger",
        "neu am markt",
        "gründung",
        "erstgründer",
        "gründungsvorhaben"
      ]
    },
    "Angestellte / Mitarbeiter": {
      "label": "Angestellte / Mitarbeiter",
      "aliases": [
        "angestellte",
        "mitarbeiter",
        "arbeitnehmer",
        "festangestellte",
        "beschäftigte",
        "belegschaft",
        "fachkraft",
        "facharbeiter",
        "büromitarbeiter",
        "sachbearbeiter",
        "angestellter",
        "vollzeitkraft",
        "teilzeitkraft",
        "werknehmerin",
        "werknehmer",
        "festanstellung",
        "festangestellter",
        "betriebsangehörige",
        "berufstätige",
        "erwerbstätige"
      ]
    },
    "Berufseinsteiger": {
      "label": "Berufseinsteiger",
      "aliases": [
        "berufseinsteiger",
        "absolvent",
        "hochschulabsolvent",
        "berufsanfänger",
        "junior",
        "einsteiger",
        "azubi",
        "auszubildender",
        "trainee",
        "werkstudent",
        "praktikant",
        "young professional",
        "karrierestart",
        "jobstart",
        "studienabgänger",
        "frischling",
        "karriereanfänger",
        "berufsstart",
        "erstes job",
        "schulabgänger"
      ]
    },
    "Business Opportunity Seeker": {
      "label": "Business Opportunity Seeker",
      "aliases": [
        "business opportunity",
        "nebeneinkommen",
        "zusatzeinkommen",
        "network marketing",
        "mlm",
        "direktvertrieb",
        "passives einkommen",
        "einkommensquelle",
        "geschäftsmöglichkeit",
        "affiliate",
        "nebenjob",
        "nebenberuflich",
        "finanzielle freiheit",
        "einkommensmöglichkeit",
        "opportunity seeker",
        "zweiteinnahme",
        "franchisepartner",
        "lizenzpartner",
        "vertriebspartner",
        "nebenverdienst"
      ]
    },
    "Investoren / Kapitalanleger": {
      "label": "Investoren / Kapitalanleger",
      "aliases": [
        "investor",
        "investoren",
        "kapitalanleger",
        "anleger",
        "vermögensinhaber",
        "privatinvestor",
        "immobilieninvestor",
        "aktienkäufer",
        "fondsinvestor",
        "renditeorientiert",
        "kapitalinteressent",
        "vermögensaufbau",
        "geldanleger",
        "sachwertanleger",
        "finanzinvestor",
        "businessangel",
        "business angel",
        "venture capital",
        "high net worth",
        "vermögender kunde"
      ]
    },
    "Sonstige Zielbranche": {
      "label": "Sonstige Zielbranche",
      "aliases": [
        "sonstiges",
        "sonstige branche",
        "diverses",
        "andere zielgruppe"
      ]
    }
  }
} as const;

export const MARKET_TAXONOMY_EN = {
  "persona_role": {
    "Student": {
      "label": "Student",
      "aliases": [
        "student",
        "university student",
        "college student",
        "undergraduate",
        "graduate student",
        "student at university"
      ]
    },
    "Schüler": {
      "label": "School Student",
      "aliases": [
        "school student",
        "pupil",
        "schoolchild",
        "high school student",
        "secondary school student",
        "school pupil"
      ]
    },
    "Auszubildender": {
      "label": "Apprentice",
      "aliases": [
        "apprentice",
        "trainee",
        "vocational trainee",
        "apprentice worker",
        "apprentice employee",
        "vocational student"
      ]
    },
    "Berufseinsteiger": {
      "label": "Early-Career Professional",
      "aliases": [
        "early-career professional",
        "entry-level employee",
        "junior professional",
        "career starter",
        "recent graduate",
        "new entrant to the workforce"
      ]
    },
    "Familienvater": {
      "label": "Father",
      "aliases": [
        "father",
        "dad",
        "family father",
        "working father",
        "parent father"
      ]
    },
    "Familienmutter": {
      "label": "Mother",
      "aliases": [
        "mother",
        "mom",
        "mum",
        "family mother",
        "working mother",
        "parent mother"
      ]
    },
    "Alleinerziehender Vater": {
      "label": "Single Father",
      "aliases": [
        "single father",
        "single dad",
        "single parent father",
        "solo father",
        "father raising children alone"
      ]
    },
    "Alleinerziehende Mutter": {
      "label": "Single Mother",
      "aliases": [
        "single mother",
        "single mom",
        "single mum",
        "single parent mother",
        "mother raising children alone"
      ]
    },
    "Geschäftsführer": {
      "label": "Managing Director",
      "aliases": [
        "managing director",
        "general manager",
        "executive director",
        "company director",
        "managing executive",
        "general director"
      ]
    },
    "Angestellter Geschäftsführer": {
      "label": "Hired Managing Director",
      "aliases": [
        "hired managing director",
        "non-owner managing director",
        "employed managing director",
        "external managing director",
        "professional managing director"
      ]
    },
    "Geschäftsführender Gesellschafter": {
      "label": "Managing Shareholder",
      "aliases": [
        "managing shareholder",
        "owner-manager",
        "shareholder director",
        "managing partner",
        "shareholder managing director",
        "owner managing director"
      ]
    },
    "Inhaber": {
      "label": "Business Owner",
      "aliases": [
        "business owner",
        "company owner",
        "entrepreneur",
        "enterprise owner",
        "proprietor",
        "small business owner"
      ]
    },
    "Alleininhaber": {
      "label": "Sole Owner",
      "aliases": [
        "sole owner",
        "sole proprietor",
        "sole business owner",
        "only owner",
        "sole shareholder",
        "single owner"
      ]
    },
    "Mitinhaber": {
      "label": "Co-Owner",
      "aliases": [
        "co-owner",
        "joint owner",
        "co-owner of a business",
        "part owner",
        "co-proprietor",
        "business partner"
      ]
    },
    "Gründer": {
      "label": "Founder",
      "aliases": [
        "founder",
        "startup founder",
        "company founder",
        "business founder",
        "founding entrepreneur",
        "entrepreneur"
      ]
    },
    "Mitgründer": {
      "label": "Co-Founder",
      "aliases": [
        "co-founder",
        "cofounder",
        "founding partner",
        "joint founder",
        "startup co-founder",
        "member of founding team"
      ]
    },
    "CEO": {
      "label": "CEO",
      "aliases": [
        "ceo",
        "chief executive officer",
        "chief executive",
        "company ceo",
        "startup ceo",
        "executive head"
      ]
    },
    "CFO": {
      "label": "CFO",
      "aliases": [
        "cfo",
        "chief financial officer",
        "finance chief",
        "chief finance officer",
        "finance executive",
        "head of finance"
      ]
    },
    "CTO": {
      "label": "CTO",
      "aliases": [
        "cto",
        "chief technology officer",
        "technology chief",
        "tech executive",
        "head of technology",
        "technology director"
      ]
    },
    "COO": {
      "label": "COO",
      "aliases": [
        "coo",
        "chief operating officer",
        "chief operations officer",
        "operations chief",
        "operations executive",
        "chief operator"
      ]
    },
    "CIO": {
      "label": "CIO",
      "aliases": [
        "cio",
        "chief information officer",
        "information technology chief",
        "it executive",
        "head of information systems"
      ]
    },
    "CMO": {
      "label": "CMO",
      "aliases": [
        "cmo",
        "chief marketing officer",
        "marketing chief",
        "marketing executive",
        "head of marketing"
      ]
    },
    "CHRO": {
      "label": "CHRO",
      "aliases": [
        "chro",
        "chief human resources officer",
        "chief people officer",
        "people chief",
        "hr executive",
        "people executive"
      ]
    },
    "Chief Revenue Officer": {
      "label": "Chief Revenue Officer",
      "aliases": [
        "chief revenue officer",
        "cro",
        "revenue chief",
        "head of revenue",
        "revenue executive",
        "go-to-market executive"
      ]
    },
    "Chief Sales Officer": {
      "label": "Chief Sales Officer",
      "aliases": [
        "chief sales officer",
        "cso",
        "sales chief",
        "chief sales executive",
        "global sales head",
        "sales executive"
      ]
    },
    "Chief Product Officer": {
      "label": "Chief Product Officer",
      "aliases": [
        "chief product officer",
        "cpo",
        "product chief",
        "product executive",
        "head of product strategy",
        "chief product executive"
      ]
    },
    "Geschäftsleitungsmitglied": {
      "label": "Executive Management Team Member",
      "aliases": [
        "executive management team member",
        "executive team member",
        "senior management member",
        "executive committee member",
        "leadership team member"
      ]
    },
    "Beirat": {
      "label": "Advisory Board Member",
      "aliases": [
        "advisory board member",
        "board advisor",
        "advisory board representative",
        "business advisor",
        "strategic advisory board member"
      ]
    },
    "Aufsichtsrat": {
      "label": "Supervisory Board Member",
      "aliases": [
        "supervisory board member",
        "supervisory director",
        "supervisory board director",
        "non-executive director",
        "oversight board member"
      ]
    },
    "Gesellschafter": {
      "label": "Shareholder",
      "aliases": [
        "shareholder",
        "company shareholder",
        "equity holder",
        "stockholder",
        "equity investor",
        "company partner"
      ]
    },
    "Investor": {
      "label": "Investor",
      "aliases": [
        "investor",
        "capital investor",
        "private investor",
        "angel investor",
        "venture investor",
        "business angel"
      ]
    },
    "Vertriebsleiter": {
      "label": "Sales Director",
      "aliases": [
        "sales director",
        "head of sales",
        "sales manager",
        "sales lead",
        "director of sales",
        "regional sales director"
      ]
    },
    "Key-Account-Manager": {
      "label": "Key Account Manager",
      "aliases": [
        "key account manager",
        "kam",
        "strategic account manager",
        "enterprise account manager",
        "major account manager",
        "key client manager"
      ]
    },
    "Account-Executive": {
      "label": "Account Executive",
      "aliases": [
        "account executive",
        "ae",
        "sales closer",
        "quota-carrying sales representative",
        "new business sales executive"
      ]
    },
    "Business-Development-Manager": {
      "label": "Business Development Manager",
      "aliases": [
        "business development manager",
        "bdm",
        "business developer",
        "bizdev manager",
        "partnerships development manager",
        "growth manager"
      ]
    },
    "Sales-Development-Representative": {
      "label": "Sales Development Representative",
      "aliases": [
        "sales development representative",
        "sdr",
        "sales development rep",
        "outbound sales representative",
        "prospecting specialist",
        "lead qualifier"
      ]
    },
    "Marketingleiter": {
      "label": "Marketing Director",
      "aliases": [
        "marketing director",
        "head of marketing",
        "marketing lead",
        "marketing department head",
        "marketing manager"
      ]
    },
    "Performance-Marketing-Manager": {
      "label": "Performance Marketing Manager",
      "aliases": [
        "performance marketing manager",
        "performance marketer",
        "paid media manager",
        "paid ads manager",
        "ppc manager",
        "digital acquisition manager"
      ]
    },
    "Brand-Manager": {
      "label": "Brand Manager",
      "aliases": [
        "brand manager",
        "brand strategist",
        "brand lead",
        "brand marketing manager",
        "brand owner",
        "brand specialist"
      ]
    },
    "HR-Leiter": {
      "label": "HR Director",
      "aliases": [
        "hr director",
        "head of hr",
        "human resources director",
        "people director",
        "hr department head",
        "personnel director"
      ]
    },
    "Recruiter": {
      "label": "Recruiter",
      "aliases": [
        "recruiter",
        "talent acquisition specialist",
        "recruitment specialist",
        "hiring specialist",
        "talent sourcer"
      ]
    },
    "Ausbildungsleiter": {
      "label": "Head of Training",
      "aliases": [
        "head of training",
        "training director",
        "head of vocational training",
        "apprenticeship supervisor",
        "learning and development manager"
      ]
    },
    "Personalsachbearbeiter": {
      "label": "HR Administrator",
      "aliases": [
        "hr administrator",
        "hr coordinator",
        "personnel administrator",
        "human resources administrator",
        "hr operations clerk"
      ]
    },
    "Einkaufsleiter": {
      "label": "Head of Purchasing",
      "aliases": [
        "head of purchasing",
        "procurement director",
        "purchasing director",
        "head of procurement",
        "sourcing director",
        "procurement lead"
      ]
    },
    "Einkäufer": {
      "label": "Buyer",
      "aliases": [
        "buyer",
        "purchaser",
        "procurement specialist",
        "purchasing agent",
        "sourcing buyer",
        "category buyer"
      ]
    },
    "Controller": {
      "label": "Financial Controller",
      "aliases": [
        "financial controller",
        "controller",
        "business controller",
        "finance controller",
        "management accountant",
        "financial analyst"
      ]
    },
    "Buchhalter": {
      "label": "Accountant",
      "aliases": [
        "accountant",
        "bookkeeper",
        "accounting specialist",
        "accounts officer",
        "financial accountant",
        "accounting clerk"
      ]
    },
    "IT-Leiter": {
      "label": "IT Director",
      "aliases": [
        "it director",
        "head of it",
        "it manager",
        "it head",
        "technology infrastructure director",
        "it department head"
      ]
    },
    "Systemadministrator": {
      "label": "Systems Administrator",
      "aliases": [
        "systems administrator",
        "sysadmin",
        "it administrator",
        "server administrator",
        "network administrator",
        "systems engineer"
      ]
    },
    "Softwareentwickler": {
      "label": "Software Developer",
      "aliases": [
        "software developer",
        "software engineer",
        "programmer",
        "developer",
        "application developer",
        "full-stack developer"
      ]
    },
    "Produktmanager": {
      "label": "Product Manager",
      "aliases": [
        "product manager",
        "product owner",
        "product lead",
        "product strategist",
        "product development manager",
        "product portfolio manager"
      ]
    },
    "Projektleiter": {
      "label": "Project Manager",
      "aliases": [
        "project manager",
        "project lead",
        "program manager",
        "project coordinator",
        "project director",
        "technical project manager"
      ]
    },
    "Operations-Leiter": {
      "label": "Head of Operations",
      "aliases": [
        "head of operations",
        "operations director",
        "director of operations",
        "operational head",
        "operations lead",
        "business operations head"
      ]
    },
    "Betriebsleiter": {
      "label": "Operations Manager",
      "aliases": [
        "operations manager",
        "operations supervisor",
        "business operations manager",
        "plant operations manager",
        "facility operations director"
      ]
    },
    "Standortleiter": {
      "label": "Site Manager",
      "aliases": [
        "site manager",
        "location manager",
        "site lead",
        "site director",
        "location director",
        "local operations manager"
      ]
    },
    "Filialleiter": {
      "label": "Branch Manager",
      "aliases": [
        "branch manager",
        "store manager",
        "branch director",
        "retail store manager",
        "shop manager",
        "local branch head"
      ]
    },
    "Teamleiter": {
      "label": "Team Lead",
      "aliases": [
        "team lead",
        "team leader",
        "team supervisor",
        "group leader",
        "shift supervisor",
        "team manager"
      ]
    },
    "Abteilungsleiter": {
      "label": "Department Head",
      "aliases": [
        "department head",
        "department manager",
        "department director",
        "department leader",
        "departmental head"
      ]
    },
    "Bereichsleiter": {
      "label": "Division Head",
      "aliases": [
        "division head",
        "division director",
        "area manager",
        "head of business unit",
        "business unit director",
        "divisional head"
      ]
    },
    "Assistent der Geschäftsführung": {
      "label": "Executive Assistant (Male)",
      "aliases": [
        "executive assistant (male)",
        "executive assistant",
        "male executive assistant",
        "assistant to the managing director",
        "personal assistant to ceo"
      ]
    },
    "Assistentin der Geschäftsführung": {
      "label": "Executive Assistant (Female)",
      "aliases": [
        "executive assistant (female)",
        "executive assistant",
        "female executive assistant",
        "assistant to the managing director",
        "personal assistant to ceo"
      ]
    },
    "Sekretärin": {
      "label": "Secretary",
      "aliases": [
        "secretary",
        "administrative secretary",
        "executive secretary",
        "office secretary",
        "secretarial assistant",
        "clerical secretary"
      ]
    },
    "Empfangsmitarbeiter": {
      "label": "Receptionist",
      "aliases": [
        "receptionist",
        "front desk receptionist",
        "reception officer",
        "front office agent",
        "reception staff",
        "front desk clerk"
      ]
    },
    "Wirtschaftsprüfer": {
      "label": "Auditor",
      "aliases": [
        "auditor",
        "financial auditor",
        "external auditor",
        "certified auditor",
        "audit professional",
        "statutory auditor"
      ]
    },
    "Ehefrau des Geschäftsführers": {
      "label": "Managing Director's Wife",
      "aliases": [
        "managing director's wife",
        "wife of managing director",
        "managing director spouse",
        "ceo's wife",
        "executive's wife"
      ]
    },
    "Ehemann der Geschäftsführerin": {
      "label": "Female Managing Director's Husband",
      "aliases": [
        "female managing director's husband",
        "husband of female managing director",
        "managing director spouse",
        "ceo's husband",
        "executive's husband"
      ]
    },
    "Ehefrau des Inhabers": {
      "label": "Business Owner's Wife",
      "aliases": [
        "business owner's wife",
        "owner's wife",
        "spouse of business owner",
        "proprietor's wife",
        "company owner's spouse"
      ]
    },
    "Ehemann der Inhaberin": {
      "label": "Female Business Owner's Husband",
      "aliases": [
        "female business owner's husband",
        "owner's husband",
        "spouse of female business owner",
        "proprietor's husband",
        "company owner's spouse"
      ]
    },
    "Sohn des Inhabers": {
      "label": "Business Owner's Son",
      "aliases": [
        "business owner's son",
        "son of business owner",
        "owner's son",
        "entrepreneur's son",
        "family business son"
      ]
    },
    "Tochter des Inhabers": {
      "label": "Business Owner's Daughter",
      "aliases": [
        "business owner's daughter",
        "daughter of business owner",
        "owner's daughter",
        "entrepreneur's daughter",
        "family business daughter"
      ]
    },
    "Vater des Inhabers": {
      "label": "Business Owner's Father",
      "aliases": [
        "business owner's father",
        "father of business owner",
        "owner's father",
        "founder's father",
        "father in family business"
      ]
    },
    "Mutter der Inhaberin": {
      "label": "Female Business Owner's Mother",
      "aliases": [
        "female business owner's mother",
        "mother of business owner",
        "owner's mother",
        "founder's mother",
        "mother in family business"
      ]
    },
    "Praxisinhaber": {
      "label": "Medical Practice Owner",
      "aliases": [
        "medical practice owner",
        "clinic owner",
        "doctor's practice owner",
        "independent practice owner",
        "medical office proprietor"
      ]
    },
    "Praxismanager": {
      "label": "Medical Practice Manager",
      "aliases": [
        "medical practice manager",
        "medical office manager",
        "practice administrator",
        "clinic practice manager",
        "healthcare practice manager"
      ]
    },
    "Arzt": {
      "label": "Physician",
      "aliases": [
        "physician",
        "doctor",
        "medical doctor",
        "medical practitioner",
        "general practitioner",
        "clinician"
      ]
    },
    "Pflegedienstleiter": {
      "label": "Nursing Services Manager",
      "aliases": [
        "nursing services manager",
        "head of nursing services",
        "nursing manager",
        "nursing director",
        "care service manager"
      ]
    },
    "Einrichtungsleiter Pflege": {
      "label": "Care Facility Manager",
      "aliases": [
        "care facility manager",
        "care home manager",
        "nursing home manager",
        "residential care director",
        "long-term care facility manager"
      ]
    },
    "Apothekeninhaber": {
      "label": "Pharmacy Owner",
      "aliases": [
        "pharmacy owner",
        "pharmacy proprietor",
        "owner of a pharmacy",
        "independent pharmacy owner",
        "pharmacist owner"
      ]
    },
    "Apothekenleiter": {
      "label": "Pharmacy Manager",
      "aliases": [
        "pharmacy manager",
        "head pharmacist",
        "pharmacy director",
        "pharmacy supervisor",
        "pharmacy lead"
      ]
    },
    "Hotelinhaber": {
      "label": "Hotel Owner",
      "aliases": [
        "hotel owner",
        "hotel proprietor",
        "independent hotel owner",
        "hospitality business owner",
        "hotel operator"
      ]
    },
    "Hoteldirektor": {
      "label": "Hotel General Manager",
      "aliases": [
        "hotel general manager",
        "hotel manager",
        "hotel director",
        "general hotel manager",
        "hospitality general manager"
      ]
    },
    "Restaurantinhaber": {
      "label": "Restaurant Owner",
      "aliases": [
        "restaurant owner",
        "restaurant proprietor",
        "owner of restaurant",
        "restaurant business owner",
        "restaurateur"
      ]
    },
    "Restaurantleiter": {
      "label": "Restaurant Manager",
      "aliases": [
        "restaurant manager",
        "food service manager",
        "dining establishment manager",
        "restaurant general manager",
        "restaurant supervisor"
      ]
    },
    "Handwerksmeister": {
      "label": "Master Craftsperson",
      "aliases": [
        "master craftsperson",
        "master tradesperson",
        "master craftsman",
        "certified master artisan",
        "skilled trades master"
      ]
    },
    "Bauunternehmer": {
      "label": "Construction Business Owner",
      "aliases": [
        "construction business owner",
        "construction entrepreneur",
        "building contractor",
        "construction company owner",
        "general contractor"
      ]
    },
    "Bauleiter": {
      "label": "Construction Site Manager",
      "aliases": [
        "construction site manager",
        "site supervisor",
        "construction project supervisor",
        "building site manager",
        "site foreman"
      ]
    },
    "Architekt": {
      "label": "Architect",
      "aliases": [
        "architect",
        "building architect",
        "architectural designer",
        "licensed architect",
        "design architect"
      ]
    },
    "Ingenieurbüro-Inhaber": {
      "label": "Engineering Consultancy Owner",
      "aliases": [
        "engineering consultancy owner",
        "engineering firm owner",
        "owner of engineering consultancy",
        "engineering business proprietor"
      ]
    },
    "Projektentwickler Immobilien": {
      "label": "Real Estate Developer",
      "aliases": [
        "real estate developer",
        "property developer",
        "real estate project developer",
        "land developer",
        "property development manager"
      ]
    },
    "Hausverwalter": {
      "label": "Property Manager",
      "aliases": [
        "property manager",
        "property administrator",
        "building manager",
        "residential property manager",
        "real estate manager"
      ]
    },
    "Facility-Manager": {
      "label": "Facility Manager",
      "aliases": [
        "facility manager",
        "facilities manager",
        "building facilities manager",
        "facilities operations manager",
        "property services manager"
      ]
    },
    "Produktionsleiter": {
      "label": "Production Manager",
      "aliases": [
        "production manager",
        "head of production",
        "manufacturing director",
        "production supervisor",
        "production department manager"
      ]
    },
    "Werkleiter": {
      "label": "Plant Manager",
      "aliases": [
        "plant manager",
        "factory manager",
        "plant director",
        "manufacturing plant head",
        "works manager"
      ]
    },
    "Qualitätsleiter": {
      "label": "Quality Manager",
      "aliases": [
        "quality manager",
        "head of quality",
        "quality assurance manager",
        "quality director",
        "quality control manager"
      ]
    },
    "Logistikleiter": {
      "label": "Logistics Manager",
      "aliases": [
        "logistics manager",
        "head of logistics",
        "logistics director",
        "shipping and logistics manager",
        "distribution manager"
      ]
    },
    "Supply-Chain-Manager": {
      "label": "Supply Chain Manager",
      "aliases": [
        "supply chain manager",
        "supply chain lead",
        "supply chain director",
        "supply chain specialist",
        "supply chain head"
      ]
    },
    "Kundenserviceleiter": {
      "label": "Customer Service Manager",
      "aliases": [
        "customer service manager",
        "head of customer service",
        "customer support manager",
        "service center manager",
        "customer care director"
      ]
    },
    "Customer-Success-Manager": {
      "label": "Customer Success Manager",
      "aliases": [
        "customer success manager",
        "customer success lead",
        "head of customer success",
        "client success manager",
        "account success manager"
      ]
    },
    "Datenschutzbeauftragter": {
      "label": "Data Protection Officer",
      "aliases": [
        "data protection officer",
        "dpo",
        "privacy officer",
        "data privacy officer",
        "privacy compliance officer"
      ]
    },
    "Compliance-Manager": {
      "label": "Compliance Manager",
      "aliases": [
        "compliance manager",
        "compliance officer",
        "head of compliance",
        "regulatory compliance manager",
        "ethics and compliance manager"
      ]
    },
    "Jurist im Unternehmen": {
      "label": "In-House Counsel",
      "aliases": [
        "in-house counsel",
        "corporate counsel",
        "company lawyer",
        "in-house lawyer",
        "internal legal advisor"
      ]
    },
    "Schulleiter": {
      "label": "School Principal",
      "aliases": [
        "school principal",
        "headteacher",
        "school head",
        "school director",
        "principal of school"
      ]
    },
    "Hochschulprofessor": {
      "label": "University Professor",
      "aliases": [
        "university professor",
        "professor",
        "university lecturer",
        "college professor",
        "academic professor"
      ]
    },
    "Dekan": {
      "label": "Dean",
      "aliases": [
        "dean",
        "faculty dean",
        "university dean",
        "dean of faculty",
        "academic dean"
      ]
    },
    "Bürgermeister": {
      "label": "Mayor",
      "aliases": [
        "mayor",
        "city mayor",
        "town mayor",
        "municipal mayor",
        "elected mayor"
      ]
    },
    "Amtsleiter": {
      "label": "Head of Public Administration Department",
      "aliases": [
        "head of public administration department",
        "public administration director",
        "head of government department",
        "municipal department head"
      ]
    },
    "Vergabestellenleiter": {
      "label": "Public Procurement Manager",
      "aliases": [
        "public procurement manager",
        "head of procurement",
        "procurement department director",
        "public purchasing head",
        "tender office manager"
      ]
    },
    "Sachbearbeiter Verwaltung": {
      "label": "Public Administration Clerk",
      "aliases": [
        "public administration clerk",
        "administrative officer",
        "government clerk",
        "civil service administrator",
        "municipal clerk"
      ]
    },
    "Privatkunde": {
      "label": "Individual Consumer",
      "aliases": [
        "individual consumer",
        "private customer",
        "individual customer",
        "retail customer",
        "end consumer",
        "b2c customer"
      ]
    },
    "Patient": {
      "label": "Patient",
      "aliases": [
        "patient",
        "healthcare patient",
        "medical patient",
        "person receiving treatment",
        "clinic patient"
      ]
    },
    "Angehöriger": {
      "label": "Family Member",
      "aliases": [
        "family member",
        "relative",
        "next of kin",
        "family relative",
        "family caregiver",
        "loved one"
      ]
    },
    "Gesetzlicher Betreuer": {
      "label": "Legal Guardian",
      "aliases": [
        "legal guardian",
        "court-appointed guardian",
        "legal representative",
        "appointed guardian",
        "guardianship representative"
      ]
    },
    "Vereinsvorsitzender": {
      "label": "Association Chairperson",
      "aliases": [
        "association chairperson",
        "association president",
        "club chair",
        "chairman of an association",
        "association leader"
      ]
    },
    "Verbandsfunktionär": {
      "label": "Industry Association Representative",
      "aliases": [
        "industry association representative",
        "trade association official",
        "association officer",
        "industry association executive",
        "federation representative"
      ]
    },
    "Franchisegeber": {
      "label": "Franchisor",
      "aliases": [
        "franchisor",
        "franchise owner (parent company)",
        "franchise grantor",
        "franchising brand owner",
        "master franchisor"
      ]
    },
    "Franchisenehmer": {
      "label": "Franchisee",
      "aliases": [
        "franchisee",
        "franchise operator",
        "franchise business owner",
        "franchise partner",
        "franchise licensee"
      ]
    },
    "Agenturinhaber": {
      "label": "Agency Owner",
      "aliases": [
        "agency owner",
        "marketing agency owner",
        "creative agency owner",
        "agency founder",
        "advertising agency owner"
      ]
    },
    "Kanzleiinhaber": {
      "label": "Law Firm Owner",
      "aliases": [
        "law firm owner",
        "law firm proprietor",
        "legal practice owner",
        "law practice partner",
        "law firm principal"
      ]
    },
    "Versicherungsmakler": {
      "label": "Insurance Broker",
      "aliases": [
        "insurance broker",
        "insurance agent",
        "independent insurance broker",
        "insurance intermediary",
        "insurance advisor"
      ]
    },
    "Bankberater": {
      "label": "Bank Advisor",
      "aliases": [
        "bank advisor",
        "banking advisor",
        "financial services advisor",
        "bank relationship manager",
        "retail banking advisor"
      ]
    },
    "Vermögensberater": {
      "label": "Financial Advisor",
      "aliases": [
        "financial advisor",
        "wealth advisor",
        "financial planner",
        "investment advisor",
        "personal finance advisor"
      ]
    },
    "Versandleiter": {
      "label": "Shipping Manager",
      "aliases": [
        "shipping manager",
        "head of shipping",
        "shipping director",
        "dispatch manager",
        "shipping operations manager"
      ]
    },
    "Lagerleiter": {
      "label": "Warehouse Manager",
      "aliases": [
        "warehouse manager",
        "warehouse supervisor",
        "warehouse director",
        "storage operations manager",
        "head of warehouse"
      ]
    },
    "Fuhrparkleiter": {
      "label": "Fleet Manager",
      "aliases": [
        "fleet manager",
        "fleet supervisor",
        "fleet director",
        "vehicle fleet manager",
        "transport fleet manager"
      ]
    },
    "Eventmanager": {
      "label": "Event Manager",
      "aliases": [
        "event manager",
        "events coordinator",
        "event planner",
        "events director",
        "event organizer"
      ]
    },
    "Seminarleiter": {
      "label": "Seminar Facilitator",
      "aliases": [
        "seminar facilitator",
        "seminar instructor",
        "workshop facilitator",
        "seminar trainer",
        "course facilitator"
      ]
    },
    "Coach": {
      "label": "Coach",
      "aliases": [
        "coach",
        "business coach",
        "personal coach",
        "professional coach",
        "executive coach"
      ]
    },
    "Trainer": {
      "label": "Trainer",
      "aliases": [
        "trainer",
        "instructor",
        "corporate trainer",
        "training specialist",
        "course trainer"
      ]
    },
    "Anwender": {
      "label": "End User",
      "aliases": [
        "end user",
        "product user",
        "software user",
        "application user",
        "customer user",
        "actual user"
      ]
    },
    "Power-User": {
      "label": "Power User",
      "aliases": [
        "power user",
        "advanced user",
        "superuser",
        "expert user",
        "key user"
      ]
    },
    "Pilotkunde": {
      "label": "Pilot Customer",
      "aliases": [
        "pilot customer",
        "beta customer",
        "pilot user",
        "trial customer",
        "early adopter customer",
        "test customer"
      ]
    }
  },
  "persona_psychology": {
    "Dominanter Entscheider": {
      "label": "Dominant Decision-Maker",
      "aliases": [
        "dominant decision-maker",
        "assertive decision-maker",
        "decisive leader",
        "controlling personality",
        "dominant leader",
        "take-charge type",
        "strong-willed decision maker"
      ]
    },
    "Strategischer Planer": {
      "label": "Strategic Planner",
      "aliases": [
        "strategic planner",
        "long-term thinker",
        "forward-thinking strategist",
        "strategic thinker",
        "systematic planner",
        "future-oriented planner"
      ]
    },
    "Analytischer Prüfer": {
      "label": "Analytical Evaluator",
      "aliases": [
        "analytical evaluator",
        "analytical thinker",
        "evidence-based evaluator",
        "critical analyst",
        "fact-driven reviewer",
        "data-oriented evaluator"
      ]
    },
    "Detailorientierter Perfektionist": {
      "label": "Detail-Oriented Perfectionist",
      "aliases": [
        "detail-oriented perfectionist",
        "perfectionist",
        "precision-focused person",
        "meticulous reviewer",
        "detail-focused personality",
        "quality-driven perfectionist"
      ]
    },
    "Sicherheitsbewusster Bewahrer": {
      "label": "Security-Conscious Traditionalist",
      "aliases": [
        "security-conscious traditionalist",
        "risk-averse person",
        "security-seeking personality",
        "cautious traditionalist",
        "stability-focused person",
        "conservative protector"
      ]
    },
    "Pflichtbewusster Organisator": {
      "label": "Dutiful Organizer",
      "aliases": [
        "dutiful organizer",
        "responsible organizer",
        "duty-driven planner",
        "conscientious organizer",
        "reliable coordinator",
        "structured personality"
      ]
    },
    "Ruhiger Unterstützer": {
      "label": "Calm Supporter",
      "aliases": [
        "calm supporter",
        "supportive listener",
        "calm helper",
        "quiet supporter",
        "steady helper",
        "dependable supporter"
      ]
    },
    "Harmonieorientierter Vermittler": {
      "label": "Harmony-Seeking Mediator",
      "aliases": [
        "harmony-seeking mediator",
        "peacemaker",
        "consensus builder",
        "harmony seeker",
        "diplomatic mediator",
        "conflict-averse mediator"
      ]
    },
    "Sozialer Netzwerker": {
      "label": "Social Networker",
      "aliases": [
        "social networker",
        "social connector",
        "relationship builder",
        "network-oriented person",
        "outgoing networker",
        "people connector"
      ]
    },
    "Charismatischer Überzeuger": {
      "label": "Charismatic Persuader",
      "aliases": [
        "charismatic persuader",
        "charismatic influencer",
        "persuasive communicator",
        "influential speaker",
        "natural persuader",
        "convincing personality"
      ]
    },
    "Empathischer Berater": {
      "label": "Empathetic Advisor",
      "aliases": [
        "empathetic advisor",
        "compassionate advisor",
        "empathetic listener",
        "caring counselor",
        "understanding consultant",
        "supportive guide"
      ]
    },
    "Idealistischer Sinnsucher": {
      "label": "Idealistic Purpose-Seeker",
      "aliases": [
        "idealistic purpose-seeker",
        "purpose-driven idealist",
        "values-oriented thinker",
        "meaning seeker",
        "principled idealist",
        "mission-focused person"
      ]
    },
    "Kreativer Freigeist": {
      "label": "Creative Free Spirit",
      "aliases": [
        "creative free spirit",
        "free thinker",
        "creative individualist",
        "imaginative innovator",
        "unconventional thinker",
        "artistic free spirit"
      ]
    },
    "Innovativer Visionär": {
      "label": "Innovative Visionary",
      "aliases": [
        "innovative visionary",
        "visionary thinker",
        "future-focused innovator",
        "big-picture visionary",
        "innovative strategist",
        "visionary entrepreneur"
      ]
    },
    "Pragmatischer Umsetzer": {
      "label": "Pragmatic Implementer",
      "aliases": [
        "pragmatic implementer",
        "practical doer",
        "results-oriented implementer",
        "action-focused executor",
        "pragmatic operator",
        "hands-on problem solver"
      ]
    },
    "Abenteuerlustiger Macher": {
      "label": "Adventurous Go-Getter",
      "aliases": [
        "adventurous go-getter",
        "adventurous action-taker",
        "risk-taking achiever",
        "bold entrepreneur",
        "adventurous doer",
        "thrill-seeking go-getter"
      ]
    },
    "Wettbewerbsorientierter Performer": {
      "label": "Competitive Achiever",
      "aliases": [
        "competitive achiever",
        "performance-driven person",
        "ambitious competitor",
        "success-focused personality",
        "results-driven performer"
      ]
    },
    "Anerkennungsorientierter Statusmensch": {
      "label": "Recognition-Seeking Status Seeker",
      "aliases": [
        "recognition-seeking status seeker",
        "status seeker",
        "prestige-focused personality",
        "recognition seeker",
        "approval-seeking achiever",
        "image-conscious person"
      ]
    },
    "Unabhängiger Individualist": {
      "label": "Independent Individualist",
      "aliases": [
        "independent individualist",
        "self-reliant person",
        "autonomous thinker",
        "freedom-seeking individualist",
        "independent-minded person",
        "self-directed person"
      ]
    },
    "Fürsorglicher Beschützer": {
      "label": "Caring Protector",
      "aliases": [
        "caring protector",
        "nurturing protector",
        "protective caregiver",
        "caring guardian",
        "supportive protector",
        "responsible caretaker"
      ]
    },
    "Traditioneller Gemeinschaftsmensch": {
      "label": "Traditional Community-Oriented Person",
      "aliases": [
        "traditional community-oriented person",
        "community-minded traditionalist",
        "group-oriented loyalist",
        "tradition-focused personality",
        "collective-minded person"
      ]
    },
    "Kritischer Skeptiker": {
      "label": "Critical Skeptic",
      "aliases": [
        "critical skeptic",
        "skeptic",
        "critical thinker",
        "questioning personality",
        "doubtful evaluator",
        "cautious critic"
      ]
    },
    "Konfliktfreudiger Herausforderer": {
      "label": "Confrontational Challenger",
      "aliases": [
        "confrontational challenger",
        "challenger",
        "confrontational debater",
        "argumentative personality",
        "provocative critic",
        "conflict-seeking personality"
      ]
    },
    "Zurückhaltender Beobachter": {
      "label": "Reserved Observer",
      "aliases": [
        "reserved observer",
        "quiet observer",
        "introspective observer",
        "reserved listener",
        "cautious watcher",
        "reflective personality"
      ]
    },
    "Lernorientierter Entwickler": {
      "label": "Learning-Oriented Developer",
      "aliases": [
        "learning-oriented developer",
        "curious learner",
        "growth-minded learner",
        "lifelong learner",
        "self-improvement seeker",
        "development-focused person"
      ]
    },
    "Erlebnisorientierter Entertainer": {
      "label": "Experience-Seeking Entertainer",
      "aliases": [
        "experience-seeking entertainer",
        "experience lover",
        "lively entertainer",
        "spontaneous performer",
        "fun-loving personality",
        "energetic socializer"
      ]
    }
  },
  "offer_category": {
    "Recruiting / Mitarbeitergewinnung": {
      "label": "Recruitment / Talent Acquisition",
      "aliases": [
        "recruitment / talent acquisition",
        "talent acquisition",
        "recruiting services",
        "employee recruitment",
        "hiring solutions",
        "staffing",
        "headhunting",
        "candidate sourcing",
        "recruitment agency"
      ]
    },
    "Leadgenerierung": {
      "label": "Lead Generation",
      "aliases": [
        "lead generation",
        "lead gen",
        "lead acquisition",
        "prospect generation",
        "demand generation",
        "qualified lead generation",
        "inbound leads",
        "outbound prospecting",
        "lead sourcing"
      ]
    },
    "Marketing / Werbung": {
      "label": "Marketing / Advertising",
      "aliases": [
        "marketing / advertising",
        "advertising",
        "digital marketing",
        "online marketing",
        "performance marketing",
        "branding",
        "marketing campaigns",
        "paid advertising",
        "media buying"
      ]
    },
    "Vertrieb / Sales": {
      "label": "Sales",
      "aliases": [
        "sales",
        "sales services",
        "sales enablement",
        "business development",
        "sales consulting",
        "outbound sales",
        "sales outsourcing",
        "b2b sales",
        "closing services"
      ]
    },
    "Terminierung / Setter": {
      "label": "Appointment Setting",
      "aliases": [
        "appointment setting",
        "appointment setter",
        "meeting scheduling",
        "appointment booking",
        "sales appointments",
        "calendar booking",
        "lead qualification",
        "sales development",
        "meeting setting"
      ]
    },
    "Beratung / Consulting": {
      "label": "Consulting / Advisory",
      "aliases": [
        "consulting / advisory",
        "consulting",
        "business consulting",
        "management consulting",
        "advisory services",
        "strategy consulting",
        "professional advice",
        "expert consultation"
      ]
    },
    "Coaching / Mentoring": {
      "label": "Coaching / Mentoring",
      "aliases": [
        "coaching / mentoring",
        "coaching",
        "mentoring",
        "executive coaching",
        "business coaching",
        "career coaching",
        "personal mentoring",
        "leadership coaching"
      ]
    },
    "Training / Weiterbildung": {
      "label": "Training / Professional Development",
      "aliases": [
        "training / professional development",
        "workshops",
        "employee training",
        "corporate training",
        "education courses",
        "professional learning",
        "skill development",
        "continuing education"
      ]
    },
    "Software / SaaS": {
      "label": "Software / SaaS",
      "aliases": [
        "software / saas",
        "saas",
        "cloud software",
        "software subscription",
        "business software",
        "software applications",
        "enterprise software",
        "web app",
        "software platform"
      ]
    },
    "IT-Dienstleistungen": {
      "label": "IT Services",
      "aliases": [
        "it services",
        "managed it services",
        "it consulting",
        "tech support services",
        "systems integration",
        "network services",
        "cloud services",
        "infrastructure management"
      ]
    },
    "Finanzen / Finanzierung": {
      "label": "Finance / Financing",
      "aliases": [
        "finance / financing",
        "financial services",
        "business financing",
        "loans",
        "credit solutions",
        "lending",
        "financial planning",
        "corporate finance",
        "funding services"
      ]
    },
    "Versicherung / Vorsorge": {
      "label": "Insurance / Financial Protection",
      "aliases": [
        "insurance / financial protection",
        "insurance products",
        "insurance brokerage",
        "insurance cover",
        "risk protection",
        "financial security",
        "retirement planning",
        "pension insurance"
      ]
    },
    "Immobilien": {
      "label": "Real Estate",
      "aliases": [
        "real estate",
        "property services",
        "real estate services",
        "residential property",
        "commercial property",
        "property sales",
        "real estate brokerage",
        "property management"
      ]
    },
    "Recht / Legal": {
      "label": "Legal Services",
      "aliases": [
        "legal services",
        "legal advice",
        "legal consulting",
        "law firm services",
        "legal representation",
        "legal compliance",
        "contract law",
        "legal assistance"
      ]
    },
    "Gesundheit / Therapie": {
      "label": "Healthcare / Therapy",
      "aliases": [
        "healthcare / therapy",
        "medical services",
        "health services",
        "healthcare treatment",
        "therapy services",
        "therapeutic care",
        "physical therapy",
        "clinical services"
      ]
    },
    "Design / Kreativleistung": {
      "label": "Design / Creative Services",
      "aliases": [
        "design / creative services",
        "creative design",
        "graphic design",
        "branding design",
        "visual design",
        "ui ux design",
        "creative agency",
        "design services",
        "digital design"
      ]
    },
    "E-Commerce / Shop": {
      "label": "E-Commerce / Online Store",
      "aliases": [
        "e-commerce / online store",
        "online shop",
        "ecommerce services",
        "online store",
        "web shop",
        "internet retail",
        "marketplace shop",
        "digital commerce",
        "ecommerce business"
      ]
    },
    "Kundenservice / Support": {
      "label": "Customer Service / Support",
      "aliases": [
        "customer service / support",
        "customer support",
        "helpdesk",
        "contact center",
        "client service",
        "technical support",
        "call center",
        "customer care",
        "service desk"
      ]
    },
    "Outsourcing / externe Prozesse": {
      "label": "Outsourcing / Business Processes",
      "aliases": [
        "outsourcing / business processes",
        "outsourced services",
        "business process outsourcing",
        "bpo",
        "external operations",
        "back-office outsourcing",
        "managed processes",
        "process outsourcing"
      ]
    },
    "Energie / Solar": {
      "label": "Energy / Solar",
      "aliases": [
        "energy / solar",
        "solar energy",
        "photovoltaic",
        "solar panels",
        "solar power",
        "renewable energy",
        "electricity services",
        "energy solutions",
        "energy efficiency"
      ]
    },
    "Sonstiges Angebot": {
      "label": "Other Offering",
      "aliases": [
        "other offering",
        "other services",
        "miscellaneous offerings",
        "other products",
        "other solutions",
        "general services"
      ]
    }
  },
  "target_industry": {
    "Handwerk": {
      "label": "Skilled Trades",
      "aliases": [
        "skilled trades",
        "craft trades",
        "skilled craftsmanship",
        "tradespeople",
        "electricians",
        "plumbers",
        "carpenters",
        "roofing contractors",
        "heating installers",
        "construction trades"
      ]
    },
    "Bau / Baugewerbe": {
      "label": "Construction / Building Industry",
      "aliases": [
        "construction / building industry",
        "building construction",
        "civil engineering",
        "construction companies",
        "building contractors",
        "construction industry",
        "construction sector",
        "contractors",
        "building trade"
      ]
    },
    "Produktion / Fertigung": {
      "label": "Manufacturing / Production",
      "aliases": [
        "manufacturing / production",
        "manufacturing",
        "industrial manufacturing",
        "production facilities",
        "factories",
        "manufacturing companies",
        "production industry",
        "fabrication",
        "manufacturing plants"
      ]
    },
    "Industrie": {
      "label": "Industrial Sector",
      "aliases": [
        "industrial sector",
        "industry",
        "industrial companies",
        "heavy industry",
        "industrial production",
        "manufacturing sector",
        "industrial businesses",
        "industrial enterprises"
      ]
    },
    "Medizin / Arztpraxen": {
      "label": "Healthcare / Medical Practices",
      "aliases": [
        "healthcare / medical practices",
        "medical practices",
        "doctors offices",
        "physicians",
        "clinics",
        "outpatient healthcare",
        "general practitioners",
        "medical centers",
        "primary care"
      ]
    },
    "Gesundheit / Pflege": {
      "label": "Healthcare / Nursing Care",
      "aliases": [
        "healthcare / nursing care",
        "nursing",
        "healthcare services",
        "care homes",
        "elderly care",
        "nursing services",
        "health and social care",
        "patient care",
        "medical care"
      ]
    },
    "Recht / Kanzleien": {
      "label": "Legal Services / Law Firms",
      "aliases": [
        "legal services / law firms",
        "law firms",
        "legal practices",
        "attorneys",
        "lawyers",
        "legal professionals",
        "law offices",
        "legal consulting",
        "legal industry"
      ]
    },
    "Finanzen": {
      "label": "Finance",
      "aliases": [
        "finance",
        "financial sector",
        "banking",
        "financial services",
        "investment firms",
        "asset management",
        "fintech",
        "financial institutions"
      ]
    },
    "Versicherung": {
      "label": "Insurance",
      "aliases": [
        "insurance",
        "insurance companies",
        "insurance industry",
        "insurance brokers",
        "underwriting",
        "insurance providers",
        "insurance services",
        "insurers"
      ]
    },
    "Immobilien": {
      "label": "Real Estate",
      "aliases": [
        "real estate",
        "property market",
        "realty",
        "real estate industry",
        "property development",
        "housing market",
        "property management",
        "real estate brokers"
      ]
    },
    "Bildung / Weiterbildung": {
      "label": "Education / Continuing Education",
      "aliases": [
        "education / continuing education",
        "education",
        "training providers",
        "adult education",
        "vocational training",
        "schools",
        "universities",
        "further education",
        "learning services"
      ]
    },
    "HR / Personal": {
      "label": "Human Resources / HR",
      "aliases": [
        "human resources / hr",
        "hr",
        "personnel services",
        "human resources",
        "hr departments",
        "people operations",
        "employee management",
        "workforce management"
      ]
    },
    "Recruiting / Personalberatung": {
      "label": "Recruitment / Staffing",
      "aliases": [
        "recruitment / staffing",
        "recruitment agencies",
        "staffing agencies",
        "executive search",
        "headhunters",
        "recruitment consultants",
        "talent acquisition",
        "staffing industry"
      ]
    },
    "Marketing / Agenturen": {
      "label": "Marketing / Agencies",
      "aliases": [
        "marketing / agencies",
        "marketing agencies",
        "advertising agencies",
        "digital marketing firms",
        "creative agencies",
        "branding agencies",
        "media agencies",
        "marketing services"
      ]
    },
    "IT / Systemhäuser": {
      "label": "IT / Managed Service Providers",
      "aliases": [
        "it / managed service providers",
        "managed service providers",
        "msp",
        "it service providers",
        "it consulting firms",
        "systems integrators",
        "it infrastructure companies"
      ]
    },
    "Software / SaaS": {
      "label": "Software / SaaS",
      "aliases": [
        "software / saas",
        "software companies",
        "saas companies",
        "software as a service",
        "software vendors",
        "application providers",
        "tech software startups"
      ]
    },
    "Telekommunikation": {
      "label": "Telecommunications",
      "aliases": [
        "telecommunications",
        "telecom",
        "telecom providers",
        "telecoms",
        "mobile networks",
        "broadband providers",
        "internet service providers",
        "telecommunications industry"
      ]
    },
    "Energie": {
      "label": "Energy",
      "aliases": [
        "energy",
        "energy industry",
        "utilities",
        "electricity providers",
        "renewable energy companies",
        "solar industry",
        "power suppliers",
        "energy services"
      ]
    },
    "Automotive / Fahrzeuge": {
      "label": "Automotive / Vehicles",
      "aliases": [
        "automotive / vehicles",
        "automotive industry",
        "automotive suppliers",
        "car dealerships",
        "vehicle manufacturers",
        "motor trade",
        "auto industry",
        "automotive services"
      ]
    },
    "Logistik / Transport": {
      "label": "Logistics / Transportation",
      "aliases": [
        "logistics / transportation",
        "transportation",
        "supply chain logistics",
        "freight",
        "trucking",
        "logistics service providers",
        "shipping",
        "distribution",
        "transport services"
      ]
    },
    "Einzelhandel": {
      "label": "Retail",
      "aliases": [
        "retail",
        "retail stores",
        "retail industry",
        "shops",
        "supermarkets",
        "brick-and-mortar retail",
        "retail businesses",
        "consumer retail"
      ]
    },
    "E-Commerce": {
      "label": "E-Commerce",
      "aliases": [
        "e-commerce",
        "online retail",
        "ecommerce sector",
        "web stores",
        "digital retail",
        "online marketplaces",
        "internet shops",
        "online shops"
      ]
    },
    "Hotellerie": {
      "label": "Hospitality / Hotels",
      "aliases": [
        "hospitality / hotels",
        "hotel industry",
        "hospitality",
        "lodging",
        "hotel operations",
        "accommodation providers",
        "hotels",
        "resorts",
        "guesthouses"
      ]
    },
    "Gastronomie": {
      "label": "Food Service / Restaurants",
      "aliases": [
        "food service / restaurants",
        "restaurant industry",
        "food service",
        "catering",
        "restaurants",
        "cafes",
        "bars",
        "food and beverage service",
        "gastronomy"
      ]
    },
    "Beauty / Ästhetik": {
      "label": "Beauty / Aesthetics",
      "aliases": [
        "beauty / aesthetics",
        "beauty industry",
        "beauty salons",
        "cosmetic studios",
        "aesthetics clinics",
        "beauty services",
        "skincare businesses",
        "aesthetics services"
      ]
    },
    "Fitness": {
      "label": "Fitness",
      "aliases": [
        "fitness",
        "fitness centers",
        "gyms",
        "fitness studios",
        "fitness industry",
        "personal training",
        "exercise facilities",
        "fitness clubs"
      ]
    },
    "Wellness": {
      "label": "Wellness",
      "aliases": [
        "wellness",
        "spa",
        "wellness centers",
        "wellness industry",
        "health spas",
        "relaxation services",
        "spa resorts",
        "holistic wellness"
      ]
    },
    "Sicherheit": {
      "label": "Security Services",
      "aliases": [
        "security services",
        "private security",
        "security industry",
        "guarding services",
        "surveillance companies",
        "security firms",
        "protective services",
        "security providers"
      ]
    },
    "Reinigung / Facility Management": {
      "label": "Cleaning / Facility Management",
      "aliases": [
        "cleaning / facility management",
        "commercial cleaning",
        "cleaning services",
        "janitorial",
        "facility services",
        "building maintenance",
        "office cleaning",
        "property cleaning"
      ]
    },
    "Landwirtschaft": {
      "label": "Agriculture",
      "aliases": [
        "agriculture",
        "farming",
        "agricultural industry",
        "farmers",
        "agribusiness",
        "crop production",
        "livestock farming",
        "agricultural businesses"
      ]
    },
    "Öffentlicher Sektor": {
      "label": "Public Sector",
      "aliases": [
        "public sector",
        "government",
        "public administration",
        "municipalities",
        "civil service",
        "state sector",
        "public authorities",
        "public services"
      ]
    },
    "Nonprofit / Vereine": {
      "label": "Nonprofit / Associations",
      "aliases": [
        "nonprofit / associations",
        "nonprofits",
        "charities",
        "associations",
        "clubs",
        "foundations",
        "non-governmental organizations",
        "ngos",
        "voluntary sector"
      ]
    },
    "Privatkunden / Endverbraucher": {
      "label": "Private Consumers / End Users",
      "aliases": [
        "private consumers / end users",
        "consumer market",
        "private individuals",
        "end consumers",
        "b2c customers",
        "retail consumers",
        "household consumers"
      ]
    },
    "Führungskräfte": {
      "label": "Executives / Managers",
      "aliases": [
        "executives / managers",
        "senior managers",
        "business executives",
        "corporate leadership",
        "management personnel",
        "c-suite executives",
        "senior leadership"
      ]
    },
    "Vertriebler / Sales-Mitarbeiter": {
      "label": "Sales Professionals",
      "aliases": [
        "sales professionals",
        "sales staff",
        "salespeople",
        "sales representatives",
        "account executives",
        "sales teams",
        "b2b sellers",
        "sales agents"
      ]
    },
    "Selbständige / Solopreneure": {
      "label": "Self-Employed / Solopreneurs",
      "aliases": [
        "self-employed / solopreneurs",
        "self-employed",
        "freelancers",
        "solopreneurs",
        "independent professionals",
        "sole proprietors",
        "one-person businesses"
      ]
    },
    "Existenzgründer": {
      "label": "Startup Founders",
      "aliases": [
        "startup founders",
        "startup entrepreneurs",
        "new business founders",
        "business startups",
        "company founders",
        "early-stage entrepreneurs",
        "new entrepreneurs"
      ]
    },
    "Angestellte / Mitarbeiter": {
      "label": "Employees / Staff",
      "aliases": [
        "employees / staff",
        "employees",
        "salaried workers",
        "workforce",
        "employed professionals",
        "office workers",
        "company staff"
      ]
    },
    "Berufseinsteiger": {
      "label": "Early-Career Professionals",
      "aliases": [
        "early-career professionals",
        "career starters",
        "recent graduates",
        "entry-level professionals",
        "first-job seekers",
        "junior employees"
      ]
    },
    "Business Opportunity Seeker": {
      "label": "Business Opportunity Seekers",
      "aliases": [
        "business opportunity seekers",
        "income opportunity seekers",
        "affiliate opportunity seekers",
        "side income seekers",
        "business opportunity prospects",
        "entrepreneurial opportunity seekers"
      ]
    },
    "Investoren / Kapitalanleger": {
      "label": "Investors / Capital Investors",
      "aliases": [
        "investors / capital investors",
        "investors",
        "capital investors",
        "investment professionals",
        "private investors",
        "shareholders",
        "financial backers"
      ]
    },
    "Sonstige Zielbranche": {
      "label": "Other Target Industry",
      "aliases": [
        "other target industry",
        "other industries",
        "miscellaneous sectors",
        "other audiences",
        "uncategorized industry"
      ]
    }
  }
} as const;

export const MARKET_TAXONOMY_LANGUAGES = {
  de: MARKET_TAXONOMY_DE,
  en: MARKET_TAXONOMY_EN,
} as const;


// --- GLOBAL CONSTANTS ---
export const MAX_STACK_DEPTH = 5; 

// --- DETAILED CUE SETS ---
const CUES_TRUST = [ "Das ist ein wichtiger Punkt...", "Danke für die Offenheit...", "Ich verstehe...", "Gut zu wissen...", "Das ergibt Sinn...", "Interessant, dass Sie das sagen...", "Lassen Sie uns da kurz bleiben...", "Verstanden...", "Ich kann das nachvollziehen...", "Das hilft mir sehr weiter...", "Gut, dass Sie das erwähnen...", "Eine klare Aussage...", "Ich bin ganz bei Ihnen...", "Das notiere ich mir...", "Spannender Aspekt...", "Danke für dieses Detail...", "Das schafft Klarheit...", "Ich höre Ihnen zu...", "Lassen Sie mich das kurz sacken lassen...", "Schön, dass wir darüber sprechen...", "Schätze Ihre Ehrlichkeit...", "Das sagt viel aus...", "Gut, dass wir da drüber sprechen...", "Ich bin da ganz bei Ihnen...", "Das zeigt mir...", "Wertvoller Hinweis...", "Danke, dass Sie das teilen...", "Das bringt uns weiter...", "Wichtig zu wissen...", "Ich folge Ihnen...", "Das macht Sinn für mich...", "Verstehe, woher das kommt...", "Das erkenne ich an...", "Ich höre das raus...", "Das respektiere ich...", "Gut, dass Sie das ansprechen...", "Das verdient Aufmerksamkeit...", "Ich nehme das ernst...", "Das ist wertvoll...", "Danke für die Klarheit...", "Das kommt an...", "Ich merke das...", "Das zählt...", "Fair gesagt...", "Ich registriere das...", "Das nehme ich mit...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja..." ];
const CUES_FACTS = [ "Notiert...", "Das habe ich verstanden...", "Ein wichtiger Datenpunkt...", "Gut, halten wir das fest...", "Das ist eine klare Zahl...", "Soweit klar...", "Danke für die Klarstellung...", "Das gibt mir ein Bild...", "Verstehe die Situation...", "Das ist eindeutig...", "Gut, das ist die Basis...", "Ich habe das aufgenommen...", "Das ist logisch...", "Danke für die Fakten...", "Das hilft bei der Einordnung...", "Alles klar soweit...", "Verstanden, danke...", "Das ist gut zu wissen...", "Das nehme ich so auf...", "Okay, verstanden...", "Gut...", "Klares Bild...", "Das fixiere ich...", "Verstanden soweit...", "Das ist eindeutig...", "Klare Aussage...", "Das merke ich mir...", "Fakten auf dem Tisch...", "Das steht fest...", "So ist es...", "Vermerkt...", "Das ist die Basis...", "Alles klar dazu...", "Das passt ins Bild...", "Logische Folge...", "Das ergibt Sinn...", "Soweit nachvollziehbar...", "Das checkt...", "Verstehe die Zahlen...", "Das ist konkret...", "Klare Linie...", "Das ordne ich ein...", "Passt zusammen...", "Das fügt sich...", "Konsistentes Bild...", "Das ist schlüssig...", "Okay, festgehalten...", "Das ist die Grundlage...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_EMPATHY = [ "Das klingt zermürbend...", "Ich höre da viel Frust...", "Das stelle ich mir nicht leicht vor...", "Das geht sicher an die Substanz...", "Eine harte Situation...", "Das kann ich fühlen...", "Da trägt man schwer dran...", "Das ist absolut verständlich...", "Das tut mir leid zu hören...", "Ich merke, das beschäftigt Sie...", "Das ist bitter...", "Kein schönes Gefühl...", "Das kostet sicher viel Kraft...", "Ich kann Ihren Ärger verstehen...", "Das klingt nach viel Druck...", "Das ist natürlich belastend...", "Ich verstehe, dass das nervt...", "Das ist menschlich...", "Das glaube ich Ihnen sofort...", "Das muss schwer sein...", "Ich spüre die Last...", "Das nagt an Ihnen...", "Das zerrt an den Nerven...", "Das schlaucht...", "Das ist kräftezehrend...", "Ich merke den Schmerz...", "Das sitzt tief...", "Das brennt unter den Nägeln...", "Das ist zäh...", "Das zieht runter...", "Schwierige Phase...", "Das belastet Sie sichtlich...", "Ich höre die Erschöpfung...", "Das frustriert verständlicherweise...", "Harter Brocken...", "Das ist eine Bürde...", "Das drückt...", "Das ist anstrengend...", "Ich erkenne den Kampf...", "Das fordert Sie...", "Das kostet Substanz...", "Das ist nicht fair...", "Ich fühle mit Ihnen...", "Das ist mühsam...", "Das ist eine Zerreißprobe...", "Ich sehe die Belastung...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_CHALLENGE = [ "Erlauben Sie mir den Einwand...", "Ist das wirklich so?...", "Da muss ich kurz einhaken...", "Das überrascht mich etwas...", "Sind Sie da ganz sicher?...", "Lassen Sie uns das prüfen...", "Das klingt im ersten Moment widersprüchlich...", "Hand aufs Herz...", "Ich frage mich nur...", "Das mag sein, aber...", "Nur mal angenommen, das stimmt nicht...", "Da bin ich skeptisch...", "Das sagen Sie so leicht...", "Ist das der wahre Grund?...", "Ganz ehrlich?...", "Ich spiele mal den Anwalt des Teufels...", "Moment, passt das zusammen?...", "Sehen Sie das wirklich so?...", "Lassen Sie uns da ehrlich sein...", "Ich hake da mal nach...", "Moment, stopp...", "Da muss ich widersprechen...", "Das kaufe ich Ihnen nicht ab...", "Ehrlich gesagt...", "Da hake ich ein...", "Das passt nicht zusammen...", "Wirklich?...", "Sind Sie sich da sicher?...", "Da zweifle ich...", "Das klingt nach Ausrede...", "Nur zur Klarstellung...", "Da bin ich anderer Meinung...", "Das glaube ich nicht ganz...", "Stimmt das so?...", "Da fehlt was...", "Ohne Ihnen zu nahe treten zu wollen...", "Provokant gefragt...", "Da würde ich nachbohren...", "Das riecht nach...", "Mal ehrlich...", "Da bin ich nicht überzeugt...", "Das greife ich auf...", "Entschuldigen Sie die Direktheit...", "Das prüfe ich...", "Da zucke ich zusammen...", "Das irritiert mich...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_VISION = [ "Das ist ein schöner Gedanke...", "Das hat Kraft...", "Stellen Sie sich das vor...", "Das wäre ein riesiger Schritt...", "Eine starke Vision...", "Das verändert alles...", "Lassen Sie das kurz wirken...", "Genau da wollen wir hin...", "Das motiviert...", "Das klingt nach Freiheit...", "Ein lohnendes Ziel...", "Das wäre der Idealzustand...", "Darauf können wir hinarbeiten...", "Das klingt fantastisch...", "Das hätte was...", "Das würde vieles lösen...", "Ein starkes Bild...", "Das ist der Plan...", "Das gibt Energie...", "Absolut erstrebenswert...", "Das ist inspirierend...", "Malen Sie das aus...", "Da liegt Potenzial...", "Das hat Größe...", "Ein würdiges Ziel...", "Das bewegt was...", "Da steckt Energie drin...", "Das ist der Durchbruch...", "Genau diese Richtung...", "Da geht was...", "Das zündet...", "Ein starker Antrieb...", "Das treibt an...", "Da spüre ich Momentum...", "Das ist der Nordstern...", "Ein klares Ziel...", "Das ist erstrebenswert...", "Da sehe ich Sie...", "Das passt zu Ihnen...", "Das ist Ihr Weg...", "Ein lohnendes Unterfangen...", "Das gibt Richtung...", "Das ist die Transformation...", "Ein kraftvolles Bild...", "Das ist es wert...", "Da liegt die Zukunft...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_DEEP = [ "Nur unter uns gefragt...", "Ganz persönlich...", "Wenn wir die Maske abnehmen...", "Tief im Inneren...", "Ganz direkt gefragt...", "Wenn Sie in den Spiegel schauen...", "Mal ohne Filter...", "Ganz ungeschminkt...", "Was sagt Ihr Bauchgefühl?...", "Lassen Sie uns tief gehen...", "Ganz ehrlich zu sich selbst...", "Was macht das mit Ihnen?...", "Wenn wir den Kopf mal ausschalten...", "Ganz pur...", "Am Ende des Tages...", "Wenn Sie ganz ruhig darüber nachdenken...", "Ganz offen...", "Im Vertrauen...", "Lassen Sie uns zum Kern kommen...", "Was ist die nackte Wahrheit?...", "Im Vertrauen...", "Ganz ehrlich jetzt...", "Wenn niemand zuhört...", "Was ist die Wahrheit?...", "Ganz tief in Ihnen...", "Was sagt Ihr Herz?...", "Lassen Sie die Fassade fallen...", "Ganz privat...", "Zwischen uns...", "Was wissen Sie wirklich?...", "Ganz roh...", "Wenn Sie allein sind...", "Was flüstert Ihre Seele?...", "Im Kern...", "Ganz authentisch...", "Was ist die eigentliche Angst?...", "Ehrlich vor sich selbst...", "Was verbergen Sie?...", "Ganz nackt...", "Was leugnen Sie?...", "Im stillen Moment...", "Was wissen Sie längst?...", "Ganz unzensiert...", "Was ist die harte Wahrheit?...", "Im Innersten...", "Was sagen Sie sich nachts?...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_LOGIC = [ "Daraus schließe ich...", "Das bedeutet also...", "Wenn man das weiterdenkt...", "Hier passt etwas nicht ganz...", "Folgerichtig wäre...", "Lassen Sie uns das verbinden...", "Da fehlt mir noch ein Puzzleteil...", "Das führt zu der Frage...", "Logisch betrachtet...", "Das heißt im Umkehrschluss...", "Lassen Sie uns das aufdröseln...", "Moment mal...", "Da ist ein Sprung in der Logik...", "Das eine bedingt das andere...", "Kurz nachgerechnet...", "Wenn A stimmt, dann muss B...", "Lassen Sie mich das sortieren...", "Der Punkt ist...", "Zusammengefasst heißt das...", "Das ergibt folgendes Bild...", "Rechnen wir das durch...", "Lassen Sie uns das sezieren...", "Das ergibt mathematisch...", "Wenn man das hochrechnet...", "Das führt zwangsläufig zu...", "Schauen wir auf die Logik...", "Das bedingt einander...", "Kausalkette ist...", "Das Muster zeigt...", "Analytisch betrachtet...", "Die Fakten sagen...", "Das addiert sich zu...", "Lassen Sie uns strukturieren...", "Systematisch gedacht...", "Der rote Faden ist...", "Das ergibt das Gesamtbild...", "Wenn wir das auflösen...", "Das Schema ist...", "Rational gesehen...", "Die Korrelation ist...", "Das impliziert...", "Deduktiv gedacht...", "Das lässt nur einen Schluss zu...", "Objektiv betrachtet...", "Die Variable dabei ist...", "Das Prinzip dahinter...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_REALITY = [ "Die Realität ist...", "Schauen wir den Fakten ins Auge...", "Butter bei die Fische...", "Ganz hart gesagt...", "Nichts für ungut, aber...", "Die Wahrheit ist oft...", "Wir dürfen uns da nichts vormachen...", "Ganz nüchtern betrachtet...", "Fakt ist...", "Reden wir Tacheles...", "Ganz ungeschönt...", "Das ist die harte Realität...", "Machen wir uns nichts vor...", "Ganz direkt...", "Ohne Umschweife...", "Wir können es drehen und wenden...", "Am Ende zählen die Fakten...", "Ganz pragmatisch...", "Die Zahlen lügen nicht...", "Das ist der Stand der Dinge...", "Ganz konkret...", "Am Boden der Tatsachen...", "Ohne Schönfärberei...", "Das ist Status quo...", "Nackten Zahlen...", "Klartext...", "Wir müssen realistisch sein...", "So sieht's aus...", "Fakt bleibt...", "Die harte Münze...", "Ohne Romantik...", "Das ist der Ist-Zustand...", "Ganz sachlich...", "Die Wahrheit auf den Tisch...", "Keine Ausflüchte...", "Das sind die Gegebenheiten...", "Ohne Wenn und Aber...", "Das ist messbar...", "Die Bilanz zeigt...", "Schwarz auf weiß...", "Das steht fest...", "Ungeschönt...", "Das ist die Ausgangslage...", "So ist die Welt...", "Das können wir nicht wegdiskutieren...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];
const CUES_CLOSE = [ "Es liegt bei Ihnen...", "Sie haben die Wahl...", "Die Entscheidung gehört Ihnen...", "Letzten Endes...", "Unterm Strich...", "Jetzt zählt es...", "Die Frage ist simpel...", "Es ist Ihr Leben...", "Ganz zum Schluss...", "Das ist der entscheidende Moment...", "Sie sind am Zug...", "Wollen wir oder nicht?...", "Machen wir einen Haken dran...", "Kommen wir zum Punkt...", "Fazit ist...", "Gehen wir es an?...", "Ihre Verantwortung...", "Schlussendlich...", "Butter bei die Fische...", "Jetzt oder nie...", "Was ist Ihre Entscheidung?...", "Sie sind dran...", "Ihr Move...", "Was sagen Sie?...", "Treffen Sie die Wahl...", "Ihr Call...", "Jetzt braucht's Klarheit...", "Was wird's?...", "Entscheiden Sie sich...", "Ihr Schritt...", "Sie haben das Ruder...", "Was machen wir?...", "Sind Sie dabei?...", "Gehen wir es an oder nicht?...", "Ihre Verantwortung, Ihre Entscheidung...", "Was ist Ihr Ja oder Nein?...", "Wo stehen Sie?...", "Was ist Ihre Antwort?...", "Ihr Commitment...", "Ball liegt bei Ihnen...", "Was ist der Beschluss?...", "Wie lautet Ihr Urteil?...", "Ihr Votum?...", "Was entscheiden Sie?...", "Ihre Wahl, Ihre Konsequenz...", "Was wird Ihre Tat sein?...", "Mhm...", "Verstehe...", "Ah...", "Okay...", "Ja...", "Notiert...", "Check...", "Klar...", "Verstanden..." ];

const DEFAULT_TONALITY: PhaseTonality = { 
    intention: "Vertrauen aufbauen. Neugier zeigen. Keine Bedrohung ausstrahlen. 'Guard down'.", 
    emotion: "Locker, Einladend, Natürlich Neugierig", 
    pace: "Normal, entspannt", 
    allowed_cues: CUES_TRUST, 
    forbidden_words: ["Pitch", "Kaufen", "Technik", "Angebot", "Dienstleistung", "Programm", "Super", "Toll", "Cool"], 
    formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
    clarify_questions: ["Was meinen Sie genau?", "Können Sie mir ein Beispiel geben?"], 
    probe_questions: ["Erzählen Sie mir mehr...", "Wie meinen Sie das?"] 
};


export const TONALITY_POOLS = {
  intention: {
    orienting:
      "Orientierung geben. Rahmen klären. Gespräch sicher und verständlich führen.",

    clarifying:
      "Unklare Aussagen präzisieren. Bedeutung, Fakten und Kontext sauber erfassen.",

    mirroring:
      "Gesagtes spiegeln. Verständnis zeigen. Vertrauen und Gesprächssicherheit stabilisieren.",

    structuring:
      "Informationen ordnen. Zusammenhänge herstellen. Gesprächslogik sichtbar machen.",

    deepening:
      "Bedeutung, Ursache und persönliche Relevanz vertiefen.",

    empathizing:
      "Belastung anerkennen. Emotionale Sicherheit geben. Nicht bewerten.",

    challenging:
      "Annahmen prüfen. Widersprüche respektvoll sichtbar machen.",

    reality_checking:
      "Realität, Konsequenzen und Machbarkeit nüchtern prüfen.",

    motivating:
      "Möglichkeit, Zielrichtung und positive Veränderung aktivieren.",

    deciding:
      "Klarheit herstellen. Entscheidung oder nächsten Schritt vorbereiten."
  },

  emotion: {
    neutral_professional:
      "Neutral, Professionell, Hilfsbereit",

    calm_trusting:
      "Ruhig, Zugewandt, Vertrauensbildend",

    curious_open:
      "Neugierig, Offen, Interessiert",

    analytical_clear:
      "Analytisch, Klar, Strukturiert",

    empathic_warm:
      "Empathisch, Warm, Verständnisvoll",

    serious_grounded:
      "Ernst, Bodenständig, Klar",

    reflective_deep:
      "Reflektiert, Tiefgehend, Persönlich",

    challenging_respectful:
      "Direkt, Herausfordernd, Respektvoll",

    confident_guiding:
      "Sicher, Führend, Orientierung gebend",

    decisive_calm:
      "Ruhig, Verbindlich, Entscheidungsorientiert"
  },

  pace: {
    normal: "Normal",
    relaxed: "Normal, entspannt",
    slow: "Langsam",
    very_slow: "Sehr langsam",
    precise: "Präzise",
    structured: "Normal, strukturiert",
    thoughtful: "Nachdenklich",
    firm: "Fest",
    calm: "Ruhig",
    energetic: "Energetisch"
  },

  allowed_cues: {
    none: [],
    trust: CUES_TRUST,
    facts: CUES_FACTS,
    empathy: CUES_EMPATHY,
    challenge: CUES_CHALLENGE,
    vision: CUES_VISION,
    deep: CUES_DEEP,
    logic: CUES_LOGIC,
    reality: CUES_REALITY,
    close: CUES_CLOSE
  },

  forbidden_words: {
    none: [],

    hard_safety_basic: [
      "Tod",
      "Mord",
      "Töten",
      "Vergewaltigung",
      "Suizid",
      "Selbstmord",
      "Missbrauch",
      "Folter",
      "Terror"
    ]
  },

  formatting_rule: {
    default_business_keys:
      "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.",

    facts:
      "Fette Fakten im Mardown Format **Fakten**",

    numbers:
      "Fette Zahlen im Markdown Format **Zahl**",

    results:
      "Fette Ergebnisse im Markdown Format **Ergebnis**",

    attempts:
      "Fette Versuche im Markdown Format **Versuche**",

    original_words:
      "Fette O-Töne",

    wishes:
      "Fette Wünsche im Markdown Format **Wunsch**",

    needs:
      "Fette Bedürfnisse im Markdown Format **Bedürfnis**",

    pain_points:
      "Fette Schmerzpunkte im Markdown Format **Schmerzpunkt**",

    sums:
      "Fette Summen",

    feelings:
      "Fette Gefühle",

    goals:
      "Fette Ziele im Markdown Format **Ziel**",

    consequences:
      "Fette Konsequenzen im Markdown Format **Konsequenz**",

    contradictions:
      "Fette Widersprüche des Interessenten im Markdownformat **Widerspruch**",

    commitments:
      "Fette Commitments im Markdown Format **Committment**",

    none:
      "Keine"
  },

  clarify_questions: {
    tech_check: [
      "Können Sie mich gut hören und sehen?",
      "Mit der Technik alles in Ordnung - Sie hören und sehen mich gut?",
      "Kurzer Check vorab: Bild und Ton passen bei Ihnen?",
      "Hallo, kurze Technik-Frage: Verstehen Sie mich gut?"
    ],

    name_and_start: [
      "Wie darf ich Sie ansprechen?",
      "Hallo, ich bin [Name], wie darf ich Sie heute ansprechen?",
      "Sind wir bereit zu starten, klappt die Technik?"
    ],

    opening_context: [
      "Was wäre aus Ihrer Sicht eine gute Möglichkeit, diese Zeit gemeinsam zu verbringen?",
      "Was möchten Sie heute besprechen, damit wir uns auf Sie konzentrieren können und auf das, was Sie suchen?",
      "Was erhoffen Sie sich von einer möglichen Zusammenarbeit mit mir?",
      "Haben Sie schon etwas gefunden?",
      "Suchen Sie noch aktiv?"
    ],

    current_situation: [
      "Wie ist ihre derzeitige Situation?",
      "Wie sieht Ihr Alltag aus?",
      "Was machen Sie jetzt ... ?",
      "Was benutzen Sie jetzt ... ?",
      "Wen benutzen Sie für ... ?",
      "Wie lange sind Sie schon ... ?",
      "Wie lange machen Sie das schon?"
    ],

    activity_and_role: [
      "Erzählen Sie mir mehr über Ihren Job.",
      "Was genau sind Ihre Aufgaben?"
    ],

    current_approach: [
      "Was tun Sie aktuell konkret – oder lassen bewusst – um {1_focus_themen_fokus} zu beeinflussen?",
      "Nutzen Sie dafür gerade irgendeine Form von professioneller Hilfe oder strukturierter Unterstützung – ja oder nein?",
      "Wie läuft Ihr aktueller Prozess im Detail ab?",
      "Welche Tools nutzen Sie dabei?"
    ],

    skills_and_gap: [
      "Welche Fähigkeiten oder Erfahrungen setzen Sie aktuell ein, um das Thema zu lösen?",
      "Worauf greifen Sie dabei konkret zurück – Wissen, Routine, Erfahrung?",
      "Woran merken Sie konkret, dass es (noch) nicht reicht?",
      "Was passiert dann – woran sehen Sie den Gap?"
    ],

    metrics_and_results: [
      "Welche Kennzahlen bilden das Thema aktuell am besten ab – und für welchen Zeitraum genau?",
      "Was sind die aktuellen Ergebnisse – so konkret wie möglich?",
      "Welche Ergebnisse haben Sie damit erzielt?",
      "Wenn Sie es zeitlich eingrenzen: Was ist das aktuelle Ergebnis im relevanten Zeitraum?"
    ],

    desired_outcome: [
      "Welche Ergebnisse würden Sie sich wünschen, wenn Sie könnten?",
      "Was wäre für Sie der ideale Zustand?",
      "Wie sähe das idealerweise aus?",
      "Wie sieht das genau aus?",
      "Beschreiben Sie mir diesen Tag."
    ],

    gap_difference: [
      "Wo liegt die Differenz – wenn Sie es in Zahlen oder klaren Worten ausdrücken?",
      "Was ist der Abstand zwischen dem, wo Sie stehen, und dem, wo Sie hinwollen?"
    ],

    history_attempts: [
      "Was genau haben Sie probiert?",
      "Welche Schritte haben Sie in der Vergangenheit unternommen, um dieses Problem zu lösen?",
      "Was war das Ergebnis?",
      "Welche möglichen Hürden haben Sie in der Vergangenheit genommen, um dieses Problem zu lösen?"
    ],

    blocker_reason: [
      "Warum glauben Sie, ist das so?",
      "Was ist Ihrer Meinung nach der Grund?",
      "Was hat Sie bisher davon abgehalten, das zu bekommen, was Sie suchen?"
    ],

    wish_and_need: [
      "Was wünschen Sie sich?",
      "Was würde das dann bedeuten?",
      "Was würde das für Sie persönlich bedeuten?",
      "Was fehlt Ihnen aktuell am meisten?",
      "Warum gerade jetzt?"
    ],

    pain_example: [
      "Können Sie mir ein Beispiel geben?",
      "Können Sie mir ein konkretes Beispiel nennen, damit ich das besser verstehen kann?",
      "Wann tut es am meisten weh?",
      "Was war das Schwierigste, mit dem Sie in letzter Zeit zu kämpfen hatten?"
    ],

    emotional_state: [
      "Wie geht es Ihnen damit?",
      "Passt das zu dem, wer Sie sein wollen?",
      "Erinnern Sie sich an das Gefühl?",
      "Wie war das nochmal?"
    ],

    criteria: [
      "Können Sie mir die Kriterien erläutern, die Sie bei der Auswahl eines Unternehmens zur Lösung der von Ihnen angesprochenen Probleme anwenden?",
      "Ist '{criterion}' bei Ihnen ein hartes Muss, oder gibt es Spielraum?",
      "Bezogen auf das, was wir gerade klären: '{criterion}' – ist das eine fixe Voraussetzung?",
      "Damit ich das korrekt einordne: '{criterion}' – verhandelbar oder nicht?",
      "Was wäre Ihr konkretes Minimum, damit '{criterion}' für Sie erfüllt ist?",
      "Woran erkennen Sie konkret: 'Ja, das erfüllt {criterion}' – im Kontext dessen, was wir gerade entscheiden?",
      "Gibt es ein Beispiel oder einen Grenzwert, der für Sie klar '{criterion}' bedeutet?"
    ],

    outcome_metric: [
      "Woran würden Sie konkret merken, dass '{outcome}' erreicht ist?",
      "Was wäre ein klares Zeichen, dass '{outcome}' wirklich eingetreten ist?",
      "Was genau müsste passieren, damit Sie sagen: 'Ja, {outcome} ist erreicht'?",
      "Okay – und welcher grobe Zielwert wäre dafür passend?",
      "Wenn Sie es grob beziffern müssten: was wäre ein Zielwert für ({metric})?",
      "Was wäre für Sie ein guter Zielwert bei ({metric})?",
      "Bis wann muss ({target_value}) bei ({metric}) erreicht sein, damit es für Sie passt?",
      "In welchem Zeitraum muss das stehen, damit es nicht eng wird?",
      "Welche Deadline haben Sie dafür im Kopf?"
    ],

    vague_state: [
      "Wenn Sie '{word}' sagen – was heißt das ganz konkret?",
      "Was meinen Sie konkret mit {word}?",
      "Wie meinen Sie das genau?",
      "{word}?",
      "Woran machen Sie das fest?",
      "Okay – haben Sie da ein Beispiel für mich?",
      "Welche konkrete Situation zeigt '{word}' am deutlichsten?",
      "Erzählen Sie mir eine konkrete Situation, in der sich das gezeigt hat."
    ],

    cause_link: [
      "Was lässt Sie glauben, dass '{claim}' stimmt?",
      "Woran merken Sie konkret, dass '{claim}' zutrifft – im Kontext dessen, was wir gerade klären?",
      "Welche Beobachtung hat Sie zu '{claim}' gebracht?",
      "Können Sie mir ein konkretes Beispiel nennen, wo sich das gezeigt hat?",
      "Wann war das zuletzt so – können Sie die Situation kurz beschreiben?",
      "Gab es einen Moment, wo Ihnen das besonders deutlich wurde?"
    ],

    decision_context: [
      "Können Sie mir den Entscheidungsfindungsprozess in Ihrem Unternehmen erläutern?",
      "Wer außer Ihnen würde entscheiden, mit welcher Firma Sie zusammenarbeiten wollen?",
      "Nehmen wir an, Sie sehen sich mehrere Unternehmen an, die alle Ihre Kriterien, einschließlich des Preises, erfüllen. Wie würden Sie dann entscheiden, was Sie tun sollen?"
    ],

    budget_context: [
      "Sie hatten mir gesagt, dass Ihr Unternehmen ein Budget um dieses Problem zu beheben. Darf ich fragen, wie das ermittelt wurde?",
      "Sind Sie der Meinung, dass das Ihnen zur Verfügung gestellte Budget ausreicht, um dieses Problem zu lösen?",
      "Könnten Sie mit mir Ihr Haushaltsverfahren erläutern? Nur um zu sehen, ob ich Ihnen helfen kann, mit welchen finanziellen Rahmenbedingungen arbeiten Sie?",
      "Können Sie mir sagen, wie die Finanzierung für dieses Projekt festgelegt wird?"
    ],

    rejoin_check: [
      "Stimmt das noch so?",
      "Hat sich etwas geändert?"
    ],

    close_clarity: [
      "Wollen wir das machen?",
      "Was spricht dagegen?",
      "Wie möchten Sie vorgehen?",
      "Sind Sie bereit?",
      "Wäre das angemessen, oder wie möchten Sie von hier aus weiter vorgehen?"
    ],

    uncertainty_check: [
      "Okay – nur bezogen auf diese Frage und damit wir das aktuelle Ziel sauber treffen: Was wäre Ihre beste grobe Einschätzung?",
      "Verstanden – nur im Kontext: Was wäre ein plausibler Richtwert, auch wenn er nicht perfekt ist?",
      "Wenn Sie raten müssten Was wäre eher zutreffend?"
    ]
  },

  probe_questions: {
    relevance: [
      "Weshalb gerade jetzt?",
      "Warum ist das Thema aktuell?",
      "Warum ist sie gerade jetzt so wichtig für Sie?",
      "Warum ist das für Sie jetzt so wichtig?",
      "Warum jetzt?",
      "Warum nicht warten?"
    ],

    search_motivation: [
      "Was genau suchen Sie?",
      "Was ist Ihnen dabei wichtig?",
      "Wie sind Sie zu dem Entschluss gekommen, möglicherweise externe Hilfe in Anspruch zu nehmen?"
    ],

    change_desire: [
      "Was daran würden Sie am liebsten ändern?",
      "Was daran ist für Sie gerade am meisten belastend?",
      "Darf ich fragen, was Sie ändern würden, wenn Sie könnten?",
      "Warum sollten Sie das ändern?",
      "Sagen Sie mir, warum Sie Ihre Situation jetzt ändern wollen?"
    ],

    process_depth: [
      "Wo genau im Ablauf hakt es aktuell am meisten?",
      "An welcher Stelle verlieren Sie am ehesten Zeit, Qualität oder Ergebnis?",
      "Wie läuft der Prozess genau ab?",
      "Können Sie das genauer beschreiben?",
      "Wie funktioniert das im Detail?"
    ],

    problem_meaning: [
      "Woran merken Sie, dass das für Sie nicht okay ist?",
      "Was macht genau daran den Unterschied – warum ist das ein Problem für Sie?",
      "Was war das Schwierigste, mit dem Sie in letzter Zeit zu kämpfen hatten? Erzähl mir mehr....",
      "Welches dieser Probleme belastet Sie am meisten? Aber warum gerade dieses?"
    ],

    desired_result_meaning: [
      "Warum genau dieses Ergebnis – was würde das für Sie verändern?",
      "Was würde sich dadurch konkret verändern?",
      "Warum ist genau dieses Ziel wichtig?",
      "Wie würde Ihnen die Umsetzung dieser Veränderung helfen?",
      "Wie sieht das im Vergleich zu Ihrem jetzigen Stand aus?"
    ],

    impact_business: [
      "Wie wirkt sich dieses Problem auf den Absatz Ihres Unternehmens aus? Auf die Produktion? Rentabilität? Auf die Planung?",
      "Wie wirkt sich das Problem auf Ihr Endergebnis aus? Auf welche Weise?",
      "Hat es sich auf Sie ausgewirkt? In welcher Weise?"
    ],

    financial_impact: [
      "Was bedeutet das in Euro?",
      "Wie hoch ist der Schaden?",
      "Wie viel, glauben Sie, kostet Sie dieses Problem an entgangenen Einnahmen/Verkäufen?",
      "Wie viel kostet Sie das an verlorener Zeit?",
      "Wenn Sie sich jetzt nicht entscheiden, dieses Problem anzugehen, wie viel wird es Sie in diesem Jahr an entgangenem Gewinn/Ertrag/Absatz kosten?"
    ],

    history_depth: [
      "Erzählen Sie mir mehr über diesen Versuch.",
      "Warum hat das nicht geklappt?",
      "Wenn Sie die Sache noch einmal machen könnten, was würden Sie anders machen als vorher?",
      "Welche Schritte haben Sie in der Vergangenheit unternommen, um dieses Problem zu lösen?"
    ],

    belief_depth: [
      "Erzählen Sie mir mehr über diese Sichtweise.",
      "Woher kommt dieser Gedanke?",
      "Was ist die Ursache des Problems? Erzählen Sie mir mehr....",
      "Welche Hindernisse stehen Ihnen bei der Überwindung dieses Problems im Weg?"
    ],

    need_meaning: [
      "Was würde es für Sie bedeuten, wenn Sie dieses Problem lösen könnten?",
      "Was würde das für Sie bedeuten, wenn Sie das tun könnten?",
      "Wenn Sie könnten ... was würde das für Sie persönlich bedeuten?",
      "Was bringt es Ihnen, dies für das Unternehmen umzusetzen?"
    ],

    emotion_identity: [
      "Was macht das mit Ihnen?",
      "Wie fühlt sich das an?",
      "Warum ist es Ihnen wichtig, die Person zu sein, die über .... verfügt?",
      "Wenn Sie dieses Ziel ....  – wie würden Sie sich dann selbst sehen?",
      "Was wäre für Sie persönlich das Wichtigste daran, die Person zu sein, die ihre ..... aufgebaut hat?",
      "Woran würden Sie merken, dass Sie nicht mehr die Person sind, die mit fehlenden ..., sondern jemand, der ... im Griff hat?"
    ],

    future_self: [
      "Was würden Sie dann tun?",
      "Wie würde sich Ihr Leben verändern?",
      "Wie könnte Ihr Leben anders aussehen als jetzt?",
      "Wenn Sie dieses Problem lösen würden, wie würde es sich für Sie von der jetzigen Situation unterscheiden?"
    ],

    reality_check: [
      "Hand aufs Herz, schaffen Sie das so?",
      "Was spricht dagegen?",
      "Warum hat es bisher nicht geklappt?",
      "Was muss sich ändern?",
      "Können Sie Ihre Ziele erreichen, ohne dieses Problem anzugehen?",
      "Wie ernst ist dieses Problem für Sie?"
    ],

    inaction_consequence: [
      "Was passiert im schlimmsten Fall?",
      "Wo stehen Sie in 5 Jahren?",
      "Können Sie sich das leisten?",
      "Wollen Sie das riskieren?",
      "Was werden sie tun, wenn sie ihre Situation nicht ändern?",
      "Was ist, wenn Sie nichts gegen dieses Problem unternehmen und Ihre Situation noch schlimmer wird?",
      "Haben Sie über die möglichen Folgen nachgedacht, wenn Sie nichts gegen Ihre Situation unternehmen?",
      "Haben Sie darüber nachgedacht, was passieren würde, wenn Sie nichts unternehmen?",
      "Was werden Sie tun, wenn sich nichts ändert, wenn Sie in den nächsten 5-10 Jahren immer das Gleiche tun?",
      "Können Sie es sich leisten, dieses Risiko einzugehen?"
    ],

    contradiction_challenge: [
      "Wie passt das zusammen?",
      "Erklären Sie mir diesen Widerspruch.",
      "Wenn alles gut ist, warum ändern wir dann was?",
      "Warum ist das jetzt plötzlich wichtig?",
      "Ist das der eigentliche Grund?",
      "Was wäre, wenn diese Annahme nicht stimmt?"
    ],

    ownership_responsibility: [
      "Wer ist dafür verantwortlich?",
      "Was können SIE tun?",
      "Liegt es am Markt oder an der Strategie?",
      "Was ist Ihr Anteil daran?",
      "Wer trägt aus Ihrer Sicht am Ende die Verantwortung dafür, dass Sie auf dem Niveau sind, das Sie sich wünschen – Sie selbst oder jemand anders?"
    ],

    commitment_depth: [
      "Wie wichtig ist dieses Thema für Sie?",
      "Wie wichtig ist es für Sie, Ihre Situation zu ändern und mit der ... zu beginnen?",
      "Wie wichtig ist es für Sie, etwas dagegen zu unternehmen? Aber warum gerade jetzt?",
      "Ist es für Sie wichtig, dieses Problem zu lösen? Und warum?",
      "Auf einer Skala von 1-10?"
    ],

    solution_value: [
      "Wie klingt das für Sie?",
      "Können Sie sich das vorstellen?",
      "Macht das Sinn?",
      "Verstehen Sie das Konzept?",
      "Sehen Sie den Wert?",
      "Rechnet sich das für Sie?",
      "Was ist Ihnen das wert?",
      "Ist das a gutes Invest?"
    ],

    price_objection: [
      "Wenn wir den Preis mal kurz beiseite schieben: Ist das grundsätzlich die Lösung, die Sie suchen?",
      "Angenommen Geld wäre kein Thema - würde das Programm Ihr Problem lösen?",
      "Okay, warum aber? Warum wäre das grundsätzlich die richtige Lösung für Sie?",
      "Was genau überzeugt Sie daran, dass das Programm Ihr Problem wirklich lösen kann?",
      "Meinen Sie damit, dass das Budget gerade absolut nicht da ist, oder dass Sie den Wert für diesen Preis noch nicht sehen?",
      "Was genau hatten Sie denn budgetiert?"
    ],

    stall_objection: [
      "Woran genau möchten Sie in Ruhe noch denken, nur damit ich weiß, was Ihnen wichtig ist?",
      "Wenn Sie sagen, Sie wollen noch einmal darüber schlafen – was meinen Sie damit ganz konkret?",
      "Was ist es genau, was Ihnen gerade durch den Kopf geht – nur damit ich es besser verstehe?",
      "Welche 1–2 Punkte würden Sie sagen, müssten für Sie noch klarer sein, bevor Sie eine Entscheidung treffen?",
      "Wenn Sie es in eine Schublade stecken müssten: Geht es eher um das Budget, um eine andere Person, um Vertrauen oder um etwas ganz anderes?"
    ],

    partner_objection: [
      "Warum genau sagen Sie, dass das hier für Sie die richtige Lösung wäre?",
      "Was macht dieses Programm aus Ihrer Sicht zu der Lösung, nach der Sie gesucht haben?",
      "Was ist der Hauptgrund, warum Sie das zuerst mit jemand anderem abstimmen wollen?",
      "Angenommen, Sie erzählen Ihrem Partner oder Geschäftspartner von dem Programm und er oder sie sagt klar Nein – was würden Sie dann tun?",
      "Was wäre für Sie das Unangenehme daran, wenn es am Ende doch nicht so läuft wie geplant und Sie das Ihrem Partner erklären müssten?"
    ],

    fear_objection: [
      "Woher kommt dieses Gefühl gerade, wenn Sie ehrlich zu sich sind?",
      "Was macht diese Entscheidung für Sie so groß oder ungewohnt?",
      "Wenn wir dieses Gefühl nicht als Stopp-Schild sehen, sondern als Zeichen, dass Sie gerade wirklich etwas verändern – macht das für Sie Sinn?",
      "Was passiert realistisch, wenn dieses Gefühl Sie weiterhin zurückhält und Sie alles so lassen, wie es jetzt ist – wie sieht Ihr Alltag dann in 6–12 Monaten aus?",
      "Wer müssten Sie sein, damit Sie sagen können: Ich treffe Entscheidungen trotz Nervosität, die mich wirklich voranbringen?"
    ],

    b2b_risk_objection: [
      "Worauf bezieht sich Ihr Zögern im Kern?",
      "Wenn Sie es greifbar machen müssten: Was genau fühlt sich hier gerade unsicher an?",
      "Wenn wir nur die Fakten ansehen: Was passiert, wenn es so weiter geht?",
      "Weshalb nicht einfach so weitermachen wie immer?",
      "Welchen Unterschied sehen Sie zur aktuellen Herangehensweise?",
      "Welches dieser beiden Risiken wäre im Zweifel schwerer zu korrigieren?"
    ],

    cause_depth: [
      "Was lässt Sie glauben, dass '{claim}' stimmt?",
      "Können Sie mir ein konkretes Beispiel nennen, wo sich das gezeigt hat?",
      "Und was war die konkrete Auswirkung daraus?",
      "Welche Folge hatte das in dieser Situation ({example}) für Sie/den Betrieb?",
      "Was war das Ergebnis davon – messbar oder spürbar?"
    ],

    criterion_consequence: [
      "Und wenn ({criterion}) nicht erfüllt ist – was ist dann die konkrete Folge?",
      "Welche konkrete Konsequenz entsteht für Sie, wenn ({criterion}) nicht passt?",
      "Was passiert dann konkret – Absage, Risiko, Mehrarbeit, Kosten?"
    ]
  }
} as const;



export const TONALITY_POOLS_EN = {
  intention: {
    orienting:
      "Provide orientation. Clarify the framework. Guide the conversation confidently and clearly.",

    clarifying:
      "Clarify ambiguous statements. Accurately capture meaning, facts, and context.",

    mirroring:
      "Reflect what has been said. Show understanding. Reinforce trust and a sense of security in the conversation.",

    structuring:
      "Organize information. Establish connections. Make the logic of the conversation clear.",

    deepening:
      "Explore meaning, causes, and personal relevance in greater depth.",

    empathizing:
      "Acknowledge the burden. Provide emotional reassurance. Do not judge.",

    challenging:
      "Examine assumptions. Respectfully point out contradictions.",

    reality_checking:
      "Objectively examine reality, consequences, and feasibility.",

    motivating:
      "Activate a sense of possibility, goal orientation, and positive change.",

    deciding:
      "Establish clarity. Prepare for a decision or the next step."
  },

  emotion: {
    neutral_professional:
      "Neutral, Professional, Helpful",

    calm_trusting:
      "Calm, Attentive, Trust-building",

    curious_open:
      "Curious, Open, Interested",

    analytical_clear:
      "Analytical, Clear, Structured",

    empathic_warm:
      "Empathetic, Warm, Understanding",

    serious_grounded:
      "Serious, Down-to-earth, Clear",

    reflective_deep:
      "Reflective, In-depth, Personal",

    challenging_respectful:
      "Direct, Challenging, Respectful",

    confident_guiding:
      "Confident, Guiding, Providing orientation",

    decisive_calm:
      "Calm, Committed, Decision-oriented"
  },

  pace: {
    normal: "Normal",
    relaxed: "Normal, relaxed",
    slow: "Slow",
    very_slow: "Very slow",
    precise: "Precise",
    structured: "Normal, structured",
    thoughtful: "Thoughtful",
    firm: "Firm",
    calm: "Calm",
    energetic: "Energetic"
  },

  allowed_cues: {
    none: [],
    trust: CUES_TRUST,
    facts: CUES_FACTS,
    empathy: CUES_EMPATHY,
    challenge: CUES_CHALLENGE,
    vision: CUES_VISION,
    deep: CUES_DEEP,
    logic: CUES_LOGIC,
    reality: CUES_REALITY,
    close: CUES_CLOSE
  },

  forbidden_words: {
    none: [],

    hard_safety_basic: [
      "Death",
      "Murder",
      "Killing",
      "Rape",
      "Suicide",
      "Taking one's own life",
      "Abuse",
      "Torture",
      "Terror"
    ]
  },

  formatting_rule: {
    default_business_keys:
      "STRICT SYSTEM DIRECTIVE: Use bold formatting EXCLUSIVELY (Markdown: text) for all business-relevant key information. This MUST include: 1. All numerical values (e.g., 5,000 euros, 12 weeks, 3 employees). 2. The core of the desired outcome (e.g., leaving a 9-to-5 job, increasing visibility). 3. Identified pain points (e.g., lack of time, decline in revenue). 4. Proper names of people or companies. Any failure to apply this formatting to these categories constitutes a logic error.",

    facts:
      "Bold facts in Markdown format **Facts**",

    numbers:
      "Bold numbers in Markdown format **Number**",

    results:
      "Bold results in Markdown format **Result**",

    attempts:
      "Bold attempts in Markdown format **Attempts**",

    original_words:
      "Bold the prospect's original wording",

    wishes:
      "Bold wishes in Markdown format **Wish**",

    needs:
      "Bold needs in Markdown format **Need**",

    pain_points:
      "Bold pain points in Markdown format **Pain point**",

    sums:
      "Bold sums",

    feelings:
      "Bold feelings",

    goals:
      "Bold goals in Markdown format **Goal**",

    consequences:
      "Bold consequences in Markdown format **Consequence**",

    contradictions:
      "Bold the prospect's contradictions in Markdown format **Contradiction**",

    commitments:
      "Bold commitments in Markdown format **Commitment**",

    none:
      "None"
  },

  clarify_questions: {
    tech_check: [
      "Can you hear and see me clearly?",
      "Is everything working properly on the technical side — can you hear and see me clearly?",
      "A quick check before we begin: Is the video and audio working properly on your end?",
      "Hello, a quick technical question: Can you understand me clearly?"
    ],

    name_and_start: [
      "How would you like me to address you?",
      "Hello, I'm [Name]. How would you like me to address you today?",
      "Are we ready to start? Is everything working technically?"
    ],

    opening_context: [
      "From your perspective, what would be a good way for us to spend this time together?",
      "What would you like to discuss today so that we can focus on you and what you're looking for?",
      "What are you hoping to gain from potentially working with me?",
      "Have you already found something?",
      "Are you still actively looking?"
    ],

    current_situation: [
      "What is your current situation?",
      "What does your day-to-day life look like?",
      "What are you doing now ... ?",
      "What are you using now ... ?",
      "Who are you using for ... ?",
      "How long have you been ... ?",
      "How long have you been doing that?"
    ],

    activity_and_role: [
      "Tell me more about your job.",
      "What exactly are your responsibilities?"
    ],

    current_approach: [
      "What specifically are you doing right now — or deliberately not doing — to influence {1_focus_themen_fokus}?",
      "Are you currently using any kind of professional help or structured support for this — yes or no?",
      "How exactly does your current process work?",
      "Which tools are you using for it?"
    ],

    skills_and_gap: [
      "What skills or experience are you currently using to address this issue?",
      "What specifically are you drawing on — knowledge, routine, experience?",
      "What specifically tells you that it is not (yet) enough?",
      "What happens then — how do you recognize the gap?"
    ],

    metrics_and_results: [
      "Which metrics best reflect this issue at present — and for exactly which period?",
      "What are the current results — as specifically as possible?",
      "What results have you achieved with that?",
      "If you narrow it down to a time frame: What is the current result for the relevant period?"
    ],

    desired_outcome: [
      "What results would you wish for if you could choose?",
      "What would the ideal situation be for you?",
      "What would that ideally look like?",
      "What exactly does that look like?",
      "Describe that day to me."
    ],

    gap_difference: [
      "Where is the difference — if you were to express it in numbers or plain words?",
      "How far are you from where you are now to where you want to be?"
    ],

    history_attempts: [
      "What exactly have you tried?",
      "What steps have you taken in the past to solve this problem?",
      "What was the result?",
      "What potential obstacles have you overcome in the past in trying to solve this problem?"
    ],

    blocker_reason: [
      "Why do you think that is?",
      "What do you believe is the reason?",
      "What has prevented you so far from getting what you're looking for?"
    ],

    wish_and_need: [
      "What would you wish for?",
      "What would that mean, then?",
      "What would that mean for you personally?",
      "What are you missing most right now?",
      "Why right now?"
    ],

    pain_example: [
      "Can you give me an example?",
      "Can you give me a specific example so I can understand it better?",
      "When does it hurt the most?",
      "What has been the most difficult thing you've had to deal with recently?"
    ],

    emotional_state: [
      "How do you feel about that?",
      "Does that align with the person you want to be?",
      "Do you remember that feeling?",
      "What was it like again?"
    ],

    criteria: [
      "Can you explain the criteria you use when choosing a company to solve the problems you've mentioned?",
      "Is '{criterion}' an absolute requirement for you, or is there some flexibility?",
      "Regarding what we're clarifying right now: Is '{criterion}' a fixed requirement?",
      "So I can assess this correctly: Is '{criterion}' negotiable or not?",
      "What would be your specific minimum for '{criterion}' to be met?",
      "How would you know for certain: 'Yes, this meets {criterion}' — in the context of the decision we're discussing?",
      "Is there an example or threshold that would clearly count as '{criterion}' for you?"
    ],

    outcome_metric: [
      "How would you specifically recognize that '{outcome}' has been achieved?",
      "What would be a clear sign that '{outcome}' has actually happened?",
      "What exactly would have to happen for you to say: 'Yes, {outcome} has been achieved'?",
      "Okay — and roughly what target value would be appropriate for that?",
      "If you had to put a rough number on it: What would be a target value for ({metric})?",
      "What would be a good target value for ({metric}) in your view?",
      "By when would ({target_value}) for ({metric}) need to be achieved for it to work for you?",
      "Within what time frame would this need to be in place to avoid things getting tight?",
      "What deadline do you have in mind for that?"
    ],

    vague_state: [
      "When you say '{word}', what exactly do you mean?",
      "What specifically do you mean by {word}?",
      "What exactly do you mean by that?",
      "{word}?",
      "What are you basing that on?",
      "Okay — can you give me an example of that?",
      "What specific situation illustrates '{word}' most clearly?",
      "Tell me about a specific situation where that became apparent."
    ],

    cause_link: [
      "What makes you believe that '{claim}' is true?",
      "What specifically shows you that '{claim}' applies — in the context of what we're clarifying right now?",
      "What observation led you to '{claim}'?",
      "Can you give me a specific example of when that became apparent?",
      "When was that most recently the case — can you briefly describe the situation?",
      "Was there a moment when that became particularly clear to you?"
    ],

    decision_context: [
      "Can you explain the decision-making process in your company?",
      "Who besides you would decide which company to work with?",
      "Suppose you're considering several companies that all meet your criteria, including price. How would you then decide what to do?"
    ],

    budget_context: [
      "You mentioned that your company has a budget to resolve this problem. May I ask how that budget was determined?",
      "Do you think the budget available to you is sufficient to solve this problem?",
      "Could you walk me through your budgeting process? Just so I can see whether I can help you — what financial parameters are you working within?",
      "Can you tell me how the financing for this project is determined?"
    ],

    rejoin_check: [
      "Is that still accurate?",
      "Has anything changed?"
    ],

    close_clarity: [
      "Shall we go ahead with this?",
      "What speaks against it?",
      "How would you like to proceed?",
      "Are you ready?",
      "Would that be appropriate, or how would you like to move forward from here?"
    ],

    uncertainty_check: [
      "Okay — focusing only on this question, so that we can address the current goal precisely: What would be your best rough estimate?",
      "Understood — just in this context: What would be a plausible approximate value, even if it isn't perfect?",
      "If you had to guess, what would be more likely to be accurate?"
    ]
  },

  probe_questions: {
    relevance: [
      "Why specifically now?",
      "Why is this issue relevant right now?",
      "Why is it so important to you right now?",
      "Why is this so important to you now?",
      "Why now?",
      "Why not wait?"
    ],

    search_motivation: [
      "What exactly are you looking for?",
      "What matters to you in that regard?",
      "How did you come to the decision to potentially seek outside help?"
    ],

    change_desire: [
      "What would you most like to change about that?",
      "What about it is currently putting the greatest strain on you?",
      "May I ask what you would change if you could?",
      "Why should you change that?",
      "Tell me why you want to change your situation now?"
    ],

    process_depth: [
      "Where exactly is the biggest sticking point in the process right now?",
      "At what point are you most likely to lose time, quality, or results?",
      "How exactly does the process work?",
      "Can you describe that in more detail?",
      "How does it work in detail?"
    ],

    problem_meaning: [
      "What tells you that this is not acceptable to you?",
      "What exactly makes the difference here — why is this a problem for you?",
      "What has been the most difficult thing you've had to deal with recently? Tell me more....",
      "Which of these problems is weighing on you the most? But why that one in particular?"
    ],

    desired_result_meaning: [
      "Why this particular result — what would it change for you?",
      "What exactly would change as a result?",
      "Why is this particular goal important?",
      "How would implementing this change help you?",
      "How does that compare with where you are now?"
    ],

    impact_business: [
      "How does this problem affect your company's sales? Production? Profitability? Planning?",
      "How does the problem affect your bottom line? In what way?",
      "Has it affected you personally? In what way?"
    ],

    financial_impact: [
      "What does that mean in euros?",
      "How large is the financial loss?",
      "How much do you think this problem is costing you in lost revenue or sales?",
      "How much time is this costing you?",
      "If you decide not to address this problem now, how much do you think it will cost you this year in lost profit, earnings, or sales?"
    ],

    history_depth: [
      "Tell me more about that attempt.",
      "Why didn't it work?",
      "If you could do it again, what would you do differently from before?",
      "What steps have you taken in the past to solve this problem?"
    ],

    belief_depth: [
      "Tell me more about that perspective.",
      "Where does that thought come from?",
      "What is the cause of the problem? Tell me more....",
      "What obstacles stand in your way of overcoming this problem?"
    ],

    need_meaning: [
      "What would it mean to you if you could solve this problem?",
      "What would it mean to you if you could do that?",
      "If you could ... what would that mean to you personally?",
      "What would implementing this for the company do for you?"
    ],

    emotion_identity: [
      "What does that do to you emotionally?",
      "How does that feel?",
      "Why is it important to you to be the kind of person who has .... ?",
      "If you achieved this goal .... how would you see yourself then?",
      "What would be most important to you personally about being the person who has built their ..... ?",
      "How would you know that you're no longer the person struggling with a lack of ..., but someone who has ... under control?"
    ],

    future_self: [
      "What would you do then?",
      "How would your life change?",
      "How might your life look different from how it does now?",
      "If you solved this problem, how would that differ for you from your current situation?"
    ],

    reality_check: [
      "Be honest with yourself: Can you manage it this way?",
      "What speaks against it?",
      "Why hasn't it worked so far?",
      "What needs to change?",
      "Can you achieve your goals without addressing this problem?",
      "How serious is this problem for you?"
    ],

    inaction_consequence: [
      "What happens in the worst-case scenario?",
      "Where will you be in 5 years?",
      "Can you afford that?",
      "Do you want to take that risk?",
      "What will you do if you don't change your situation?",
      "What if you do nothing about this problem and your situation gets even worse?",
      "Have you considered the possible consequences of doing nothing about your situation?",
      "Have you thought about what would happen if you did nothing?",
      "What will you do if nothing changes and you keep doing the same thing for the next 5–10 years?",
      "Can you afford to take that risk?"
    ],

    contradiction_challenge: [
      "How do those things fit together?",
      "Explain this contradiction to me.",
      "If everything is going well, why are we changing anything?",
      "Why is this suddenly important now?",
      "Is that the real reason?",
      "What if that assumption isn't true?"
    ],

    ownership_responsibility: [
      "Who is responsible for that?",
      "What can YOU do?",
      "Is it the market or the strategy?",
      "What part do you play in it?",
      "In your view, who ultimately bears responsibility for you reaching the level you want to be at — you or someone else?"
    ],

    commitment_depth: [
      "How important is this issue to you?",
      "How important is it to you to change your situation and start with ... ?",
      "How important is it to you to do something about it? But why right now?",
      "Is it important to you to solve this problem? And why?",
      "On a scale of 1 to 10?"
    ],

    solution_value: [
      "How does that sound to you?",
      "Can you picture that?",
      "Does that make sense?",
      "Do you understand the concept?",
      "Do you see the value?",
      "Does that make financial sense for you?",
      "What is that worth to you?",
      "Is that a worthwhile investment?"
    ],

    price_objection: [
      "If we put the price aside for a moment: Is this fundamentally the solution you're looking for?",
      "Assuming money were not an issue — would the program solve your problem?",
      "Okay, but why? Why would this fundamentally be the right solution for you?",
      "What exactly convinces you that the program can really solve your problem?",
      "Do you mean the budget is simply not available right now, or that you don't yet see the value at this price?",
      "What exactly had you budgeted for?"
    ],

    stall_objection: [
      "What exactly would you like to take some time to think about, just so I know what matters to you?",
      "When you say you'd like to sleep on it — what exactly do you mean by that?",
      "What exactly is going through your mind right now — just so I can understand it better?",
      "Which 1–2 points would you say still need to be clearer before you make a decision?",
      "If you had to put it into a category: Is it more about the budget, another person, trust, or something else entirely?"
    ],

    partner_objection: [
      "Why exactly do you say that this would be the right solution for you?",
      "What makes this program the solution you've been looking for, in your view?",
      "What's the main reason you want to discuss this with someone else first?",
      "Suppose you tell your partner or business partner about the program and they clearly say no — what would you do then?",
      "What would be uncomfortable for you if things didn't go as planned after all and you had to explain that to your partner?"
    ],

    fear_objection: [
      "Where is this feeling coming from right now, if you're being honest with yourself?",
      "What makes this decision feel so significant or unfamiliar to you?",
      "If we don't see this feeling as a stop sign but as a sign that you're really making a change — does that make sense to you?",
      "Realistically, what happens if this feeling continues to hold you back and you leave everything as it is — what would your everyday life look like in 6–12 months?",
      "Who would you need to be to say: I make decisions despite feeling nervous, and those decisions genuinely move me forward?"
    ],

    b2b_risk_objection: [
      "What is the core of your hesitation?",
      "If you had to make it tangible: What exactly feels uncertain here right now?",
      "If we look only at the facts: What happens if things continue this way?",
      "Why not simply carry on as usual?",
      "What difference do you see compared with the current approach?",
      "Which of these two risks would be harder to correct if it came down to it?"
    ],

    cause_depth: [
      "What makes you believe that '{claim}' is true?",
      "Can you give me a specific example where that became apparent?",
      "And what was the specific impact of that?",
      "What consequence did that have for you or the business in this situation ({example})?",
      "What was the result — measurable or noticeable?"
    ],

    criterion_consequence: [
      "And if ({criterion}) isn't met — what is the specific consequence?",
      "What specific consequence would there be for you if ({criterion}) didn't fit?",
      "What happens then, specifically — rejection, risk, extra work, costs?"
    ]
  }
} as const;


export const TONALITY_POOLS_BY_LANGUAGE = {
  de: TONALITY_POOLS,
  en: TONALITY_POOLS_EN,
};



export const TENANT_SECRET_KEYS = {
  openai: "",
  groq: "",
  deepgram: "",
  gemini: "",
  last4_openai: "",
  last4_groq: "",
  last4_deepgram: "",
  last4_gemini: "",
  version: 0,
  updated_at: "",
};


export const LICENSE_INFO = {
  membership_id: null,
  member_email: null,
  name: null,
  role: null,
  license_id: null,
  license_id_immutable: null,
  license_type: null,
  billing_period: null,
  status_license: null,
  current_period_start_at: null,
  current_period_end_at: null,
  max_battle_deck_slots: 0,
  gesamt_punkte_auto: 0,
  woechentliche_punkte_auto: 0,
  profile_image_url: null,
  referral_token_auto: null,

  internalisation_enabled: false,
  timetravel_enabled: false,
  personal_analytics_enabled: false,
  text_export_enabled: false,
  saving_enabled: false,
  lifecall_hud_enabled: false,
  gap_analysis_enabled: false,
  support_level: null,
  marketplace_enabled: false,
  persona_creation_enabled: false,
  audio_export_enabled: false,
  primary_language: null,
  selling_languages: [],
};


export const MODULE_UI_REGISTER = {
  shop: {
    category: {
      DEFENSE: {
        label: 'DEFENSE',
        className: 'cat-defense',
        sortOrder: 10,
      },
      ATTACK: {
        label: 'ATTACK',
        className: 'cat-attack',
        sortOrder: 20,
      },
      PSYCHOLOGY: {
        label: 'PSYCHOLOGY',
        className: 'cat-psychology',
        sortOrder: 30,
      },
      FOUNDATION: {
        label: 'FOUNDATION',
        className: 'cat-foundation',
        sortOrder: 40,
      },
      SPECIAL: {
        label: 'SPECIAL',
        className: 'cat-special',
        sortOrder: 50,
      },
      SCRIPT: {
        label: 'SCRIPT',
        className: 'cat-script',
        sortOrder: 60,
      },
    } as Record<ModuleCategory, {
      label: string;
      className: string;
      sortOrder: number;
    }>,

    categoryFallback: {
      label: 'OTHER',
      className: 'cat-fallback',
      sortOrder: 999,
    },

    tier: {
      BASIC: {
        stars: 1,
        className: 's-bronze',
        color: '#cd7f32',
      },
      PRO: {
        stars: 2,
        className: 's-silver',
        color: '#e0e0e0',
      },
      ELITE: {
        stars: 3,
        className: 's-gold',
        color: '#f59e0b',
      },
      LEGENDARY: {
        stars: 4,
        className: 's-platinum',
        color: '#00ffff',
      },
    } as Record<ModuleTier, {
      stars: number;
      className: string;
      color: string;
    }>,

    tierFallback: {
      stars: 1,
      className: 's-fallback',
      color: '#00ffff',
    },
  },

  deckBuilder: {
    category: {
      DEFENSE: {
        label: 'DEFENSE',
        className: 'cat-defense',
        sortOrder: 10,
      },
      ATTACK: {
        label: 'ATTACK',
        className: 'cat-attack',
        sortOrder: 20,
      },
      PSYCHOLOGY: {
        label: 'PSYCHOLOGY',
        className: 'cat-psychology',
        sortOrder: 30,
      },
      FOUNDATION: {
        label: 'FOUNDATION',
        className: 'cat-foundation',
        sortOrder: 40,
      },
      SPECIAL: {
        label: 'SPECIAL',
        className: 'cat-special',
        sortOrder: 50,
      },
      SCRIPT: {
        label: 'SCRIPT',
        className: 'cat-script',
        sortOrder: 60,
      },
    } as Record<ModuleCategory, {
      label: string;
      className: string;
      sortOrder: number;
    }>,

    categoryFallback: {
      label: 'OTHER',
      className: 'cat-fallback',
      sortOrder: 999,
    },

    tier: {
      BASIC: {
        stars: 1,
        className: 'tier-bronze',
        color: '#cd7f32',
      },
      PRO: {
        stars: 2,
        className: 'tier-silver',
        color: '#e0e0e0',
      },
      ELITE: {
        stars: 3,
        className: 'tier-gold',
        color: '#ffd700',
      },
      LEGENDARY: {
        stars: 4,
        className: 'tier-platinum',
        color: '#00ffff',
      },
    } as Record<ModuleTier, {
      stars: number;
      className: string;
      color: string;
    }>,

    tierFallback: {
      stars: 1,
      className: 'tier-fallback',
      color: '#00ffff',
    },
  },

  battleDeck: {
    category: {
      DEFENSE: {
        label: 'DEFENSE',
        className: 'bd-cat-defense',
        sortOrder: 10,
      },
      ATTACK: {
        label: 'ATTACK',
        className: 'bd-cat-attack',
        sortOrder: 20,
      },
      PSYCHOLOGY: {
        label: 'PSYCHOLOGY',
        className: 'bd-cat-psychology',
        sortOrder: 30,
      },
      FOUNDATION: {
        label: 'FOUNDATION',
        className: 'bd-cat-foundation',
        sortOrder: 40,
      },
      SPECIAL: {
        label: 'SPECIAL',
        className: 'bd-cat-special',
        sortOrder: 50,
      },
      SCRIPT: {
        label: 'SCRIPT',
        className: 'bd-cat-script',
        sortOrder: 60,
      },
    } as Record<ModuleCategory, {
      label: string;
      className: string;
      sortOrder: number;
    }>,

    categoryFallback: {
      label: 'OTHER',
      className: 'bd-cat-fallback',
      sortOrder: 999,
    },

    tier: {
      BASIC: {
        stars: 1,
        className: 'tier-fallback',
        color: '#cd7f32',
      },
      PRO: {
        stars: 2,
        className: 'tier-fallback',
        color: '#e0e0e0',
      },
      ELITE: {
        stars: 3,
        className: 'tier-fallback',
        color: '#ffd700',
      },
      LEGENDARY: {
        stars: 4,
        className: 'tier-fallback',
        color: '#00ffff',
      },
    } as Record<ModuleTier, {
      stars: number;
      className: string;
      color: string;
    }>,

    tierFallback: {
      stars: 1,
      className: 'tier-fallback',
      color: '#00ffff',
    },
  },
} as const;






export const VERY_GENERAL_QUESTIONS_POOL = [
  // TECH / ARRIVAL
  "Können Sie mich gut hören und sehen?",
  "Passt die Technik bei Ihnen?",
  "Verstehen Sie mich gut?",
  "Sind Bild und Ton bei Ihnen in Ordnung?",
  "Wie darf ich Sie ansprechen?",
  "Sind wir bereit zu starten?",

  // GESPRÄCHSANLASS / EINSTIEG
  "Was hat Sie ursprünglich dazu gebracht, sich mit diesem Thema zu beschäftigen?",
  "Was war der Anlass für dieses Gespräch?",
  "Was hat Ihr Interesse geweckt?",
  "Was möchten Sie heute für sich herausfinden?",
  "Was wäre für Sie ein sinnvoller Nutzen aus diesem Gespräch?",
  "Was müsste heute passieren, damit sich das Gespräch für Sie gelohnt hat?",

  // VERFÜGBARKEIT / RAHMEN
  "Passt es gerade zeitlich für ein kurzes Gespräch?",
  "Haben Sie gerade ein paar Minuten Zeit?",
  "Wenn es gerade ungünstig ist, wann würde es besser passen?",
  "Ist es für Sie in Ordnung, wenn ich Ihnen ein paar Fragen stelle?",
  "Ist es für Sie in Ordnung, wenn wir offen und direkt sprechen?",
  "Passt der Gesprächsrahmen so für Sie?",

  // ERWARTUNG / ZIEL DES GESPRÄCHS
  "Was erhoffen Sie sich von diesem Gespräch?",
  "Was möchten Sie heute konkret besprechen?",
  "Was wäre für Sie ein gutes Ergebnis aus diesem Austausch?",
  "Welche Frage möchten Sie für sich heute beantwortet haben?",
  "Worauf sollen wir uns heute besonders konzentrieren?",
  "Was ist Ihnen bei diesem Thema besonders wichtig?",

  // RELEVANZ / DRINGLICHKEIT
  "Warum ist dieses Thema gerade jetzt wichtig?",
  "Weshalb beschäftigen Sie sich aktuell damit?",
  "Was hat sich verändert, dass es jetzt relevant geworden ist?",
  "Warum nicht später?",
  "Was macht das Thema im Moment dringend?",
  "Was würde passieren, wenn Sie es weiter aufschieben?",

  // AKTUELLE SITUATION
  "Wie sieht Ihre aktuelle Situation aus?",
  "Wo stehen Sie aktuell in Bezug auf dieses Thema?",
  "Wie würden Sie den aktuellen Stand beschreiben?",
  "Was machen Sie aktuell konkret?",
  "Wie läuft es im Moment?",
  "Was funktioniert aktuell gut?",
  "Was funktioniert aktuell noch nicht so, wie Sie es möchten?",

  // ROLLE / KONTEXT
  "Was machen Sie aktuell?",
  "Welche Rolle haben Sie dabei?",
  "Welche Aufgaben gehören aktuell zu Ihrem Alltag?",
  "Wie lange machen Sie das schon?",
  "Seit wann ist diese Situation so?",
  "Wie hat sich die Situation über die Zeit entwickelt?",

  // WAHRNEHMUNG / EINORDNUNG
  "Wie nehmen Sie dieses Thema aktuell wahr?",
  "Was bedeutet diese Situation für Sie?",
  "Wie ordnen Sie das für sich ein?",
  "Was macht das mit Ihrem Alltag?",
  "Was macht das mit Ihrer Arbeit?",
  "Was bedeutet das für Ihre aktuelle Entwicklung?",

  // STÖRFAKTOREN / BELASTUNG
  "Was daran stört Sie aktuell am meisten?",
  "Was würden Sie am liebsten ändern?",
  "Was ist für Sie daran gerade am belastendsten?",
  "Wo merken Sie am stärksten, dass etwas nicht passt?",
  "Was kostet Sie aktuell am meisten Energie?",
  "Was zieht sich am längsten durch?",

  // HANDLUNGSBEDARF
  "Wo sehen Sie aktuell den größten Handlungsbedarf?",
  "Welcher Bereich müsste sich zuerst verändern?",
  "Was wäre aus Ihrer Sicht der wichtigste Hebel?",
  "Welche Herausforderung ist gerade die wichtigste?",
  "Was sollte aus Ihrer Sicht als Erstes gelöst werden?",
  "Wo hakt es aktuell am meisten?",

  // AKTUELLER ANSATZ
  "Was tun Sie aktuell konkret, um die Situation zu verbessern?",
  "Was haben Sie bisher versucht?",
  "Welche Maßnahmen nutzen Sie aktuell?",
  "Was lassen Sie aktuell bewusst sein?",
  "Welche Unterstützung nutzen Sie aktuell?",
  "Nutzen Sie dafür bereits professionelle oder strukturierte Hilfe?",

  // PROZESS / ABLAUF
  "Wie läuft Ihr aktueller Prozess ab?",
  "Welche Schritte gehören aktuell dazu?",
  "Welche Tools oder Hilfsmittel nutzen Sie dabei?",
  "An welcher Stelle entsteht aktuell der größte Engpass?",
  "Wo verlieren Sie aktuell am ehesten Zeit, Qualität oder Ergebnis?",
  "Welche Abläufe funktionieren gut und welche nicht?",

  // FÄHIGKEITEN / RESSOURCEN
  "Welche Fähigkeiten nutzen Sie aktuell dafür?",
  "Welche Erfahrungen bringen Sie bereits mit?",
  "Worauf greifen Sie aktuell zurück?",
  "Wo merken Sie, dass Ihnen etwas fehlt?",
  "Woran erkennen Sie, dass es aktuell noch nicht reicht?",
  "Welche Ressourcen stehen Ihnen aktuell zur Verfügung?",

  // KENNZAHLEN / FAKTEN
  "Welche Zahlen beschreiben Ihre aktuelle Situation am besten?",
  "Welche Kennzahlen sind für dieses Thema relevant?",
  "Für welchen Zeitraum betrachten Sie diese Zahlen?",
  "Was sind die aktuellen Ergebnisse?",
  "Welche konkreten Werte können Sie dazu nennen?",
  "Wie messen Sie aktuell, ob es funktioniert?",

  // IST-ERGEBNISSE
  "Welche Ergebnisse haben Sie bisher erzielt?",
  "Was ist aktuell das konkrete Ergebnis?",
  "Was ist im relevanten Zeitraum herausgekommen?",
  "Wie zufrieden sind Sie mit dem aktuellen Ergebnis?",
  "Woran machen Sie fest, ob das Ergebnis gut genug ist?",
  "Was zeigt Ihnen, dass das aktuelle Ergebnis nicht ausreicht?",

  // PROBLEM-EINORDNUNG
  "Woran merken Sie, dass das ein Problem ist?",
  "Warum ist das für Sie nicht in Ordnung?",
  "Was macht daran den Unterschied?",
  "Was ist die eigentliche Schwierigkeit dahinter?",
  "Was ist die Ursache aus Ihrer Sicht?",
  "Was steht aktuell im Weg?",

  // ZIEL / WUNSCHERGEBNIS
  "Welches Ergebnis würden Sie sich wünschen?",
  "Wo möchten Sie stattdessen hin?",
  "Was wäre Ihr gewünschter Zielzustand?",
  "Was soll sich konkret verändern?",
  "Was wäre für Sie ein realistisches Ziel?",
  "Warum genau dieses Ziel?",

  // GAP / DIFFERENZ
  "Wo liegt die Differenz zwischen heute und dem gewünschten Zustand?",
  "Was fehlt aktuell noch, damit Sie dort hinkommen?",
  "Wie groß ist der Abstand zwischen Ist und Soll?",
  "Woran würden Sie merken, dass die Lücke geschlossen ist?",
  "Was müsste passieren, damit der Unterschied kleiner wird?",
  "Was trennt Sie aktuell noch vom gewünschten Ergebnis?",

  // AUSWIRKUNGEN
  "Wie wirkt sich das aktuell aus?",
  "Welche Folgen hat das für Sie?",
  "Welche anderen Bereiche sind davon betroffen?",
  "Was verändert sich dadurch im Alltag?",
  "Welche Auswirkungen hat das auf Ihre Planung?",
  "Welche Auswirkungen hat das auf Ihre Energie, Zeit oder Motivation?",

  // WIRTSCHAFTLICHE / MESSBARE AUSWIRKUNGEN
  "Welche messbaren Auswirkungen entstehen dadurch?",
  "Was kostet Sie diese Situation aktuell?",
  "Wie viel Zeit geht dadurch verloren?",
  "Welche Chancen bleiben dadurch liegen?",
  "Welche finanziellen Auswirkungen hat das?",
  "Was würde es kosten, wenn sich daran nichts ändert?",

  // HISTORIE
  "Was haben Sie in der Vergangenheit bereits ausprobiert?",
  "Welche Schritte haben Sie früher schon unternommen?",
  "Welche Unterstützung hatten Sie bisher?",
  "Wann haben Sie zuletzt aktiv etwas dagegen getan?",
  "Was war damals der Auslöser?",
  "Was war damals das Ergebnis?",

  // PROFESSIONELLE HILFE / STRUKTURIERTE UNTERSTÜTZUNG
  "Haben Sie dabei schon einmal professionelle Unterstützung genutzt?",
  "Welche Art von Unterstützung war das?",
  "Was wurde dabei konkret gemacht?",
  "Wie lange ging diese Unterstützung?",
  "Was haben Sie damals investiert?",
  "Warum haben Sie sich damals für diese Unterstützung entschieden?",

  // ERGEBNISSE VERGANGENER VERSUCHE
  "Was hat damals funktioniert?",
  "Was hat damals nicht funktioniert?",
  "Welches Ergebnis hat dieser Versuch gebracht?",
  "Was hätten Sie rückblickend anders gebraucht?",
  "Warum hat es aus Ihrer Sicht nicht gereicht?",
  "Was hat Ihnen damals gefehlt?",

  // BLOCKADEN / NICHT-AKTIV-WERDEN
  "Was hat Sie bisher davon abgehalten, aktiv zu werden?",
  "Warum ist es bisher nicht passiert?",
  "Was war der Hauptgrund, dass Sie es noch nicht umgesetzt haben?",
  "Welche Hürde stand bisher im Weg?",
  "Was müsste sich ändern, damit Sie aktiv werden?",
  "Welche innere oder äußere Blockade gab es bisher?",

  // BEDEUTUNG
  "Was bedeutet dieses Thema für Sie persönlich?",
  "Warum ist Ihnen das wichtig?",
  "Was würde sich dadurch für Sie verändern?",
  "Was meinen Sie genau damit?",
  "Weshalb hat genau dieser Punkt für Sie Gewicht?",
  "Welche Bedeutung steckt dahinter?",

  // MOTIVE
  "Warum ist genau das für Sie wichtig?",
  "Was steckt für Sie dahinter?",
  "Welcher Wunsch hängt daran?",
  "Was treibt Sie dabei an?",
  "Was wäre daran für Sie besonders wertvoll?",
  "Warum möchten Sie das wirklich erreichen?",

  // KERNBEDÜRFNIS
  "Was fehlt Ihnen aktuell am meisten?",
  "Welches Bedürfnis steckt dahinter?",
  "Worum geht es Ihnen im Kern?",
  "In welchem Bereich ist dieses Bedürfnis besonders relevant?",
  "Ist dieses Bedürfnis aktuell eher erfüllt oder eher nicht erfüllt?",
  "Was würde es für Sie bedeuten, wenn dieses Bedürfnis erfüllt wäre?",

  // PAIN / KONKRETE SITUATIONEN
  "In welchen Situationen merken Sie das besonders stark?",
  "Können Sie mir ein konkretes Beispiel geben?",
  "Wann tritt das typischerweise auf?",
  "Wie häufig passiert das?",
  "Seit wann erleben Sie das so?",
  "In welchen Bereichen zeigt sich das noch?",

  // MANIFESTATION / KONSEQUENZ
  "Wie zeigt sich das konkret?",
  "Woran wird das im Alltag sichtbar?",
  "Welche Ressourcen sind davon betroffen?",
  "Welche messbaren Folgen entstehen daraus?",
  "Was passiert dadurch als Nächstes?",
  "Welche weiteren Effekte entstehen daraus?",

  // KAUSALKETTE
  "Was ist die direkte Folge davon?",
  "Was entsteht daraus im nächsten Schritt?",
  "Welche mittelbaren Auswirkungen hat das?",
  "Welche nachgelagerten Effekte kommen dazu?",
  "Wie hängt das alles zusammen?",
  "Was ist die gesamte Auswirkung dieser Kette?",

  // EMOTION
  "Wie geht es Ihnen damit?",
  "Was macht das emotional mit Ihnen?",
  "Wie fühlt sich diese Situation für Sie an?",
  "Wie stark belastet Sie das aktuell?",
  "Was löst das in Ihnen aus?",
  "Wie würden Sie dieses Gefühl beschreiben?",

  // IDENTITÄT / SELBSTBILD
  "Was sagt diese Situation über Ihr aktuelles Selbstbild aus?",
  "Wie sehen Sie sich selbst in dieser Situation?",
  "Wer möchten Sie in Bezug auf dieses Thema sein?",
  "Was würde sich an Ihrem Selbstbild verändern, wenn Sie das lösen?",
  "Welche Version von Ihnen müsste dafür stärker werden?",
  "Woran würde man merken, dass Sie sich verändert haben?",

  // CHANGE INTENT
  "Wollen Sie daran wirklich etwas ändern?",
  "Warum möchten Sie nicht so bleiben, wie es jetzt ist?",
  "Was macht es für Sie wichtig, jetzt etwas zu verändern?",
  "Was wäre der Preis, wenn Sie nichts ändern?",
  "Sind Sie bereit, dafür aktiv zu werden?",
  "Wie ernst ist Ihnen diese Veränderung?",

  // KRITERIEN
  "Welche Kriterien sind Ihnen bei einer Lösung wichtig?",
  "Woran würden Sie eine gute Lösung erkennen?",
  "Was müsste erfüllt sein, damit es für Sie passt?",
  "Welche Punkte dürfen auf keinen Fall fehlen?",
  "Gibt es noch weitere Kriterien, die wichtig sind?",
  "Wenn alle Kriterien erfüllt wären, wie würden Sie entscheiden?",

  // PRIORITÄT
  "Welches Kriterium ist für Sie am wichtigsten?",
  "Warum ist genau dieser Punkt wichtiger als die anderen?",
  "Welche Priorität hat dieses Thema für Sie?",
  "Was steht für Sie an erster Stelle?",
  "Wenn Sie nur einen Punkt auswählen könnten, welcher wäre es?",
  "Warum hat dieser Punkt gerade jetzt Priorität?",

  // ENTSCHEIDUNGSSTRUKTUR
  "Wie treffen Sie solche Entscheidungen normalerweise?",
  "Entscheiden Sie das allein oder mit anderen zusammen?",
  "Wer muss in diese Entscheidung eingebunden werden?",
  "Wie läuft der Entscheidungsprozess üblicherweise ab?",
  "Was müsste vor einer Entscheidung noch geklärt werden?",
  "Welche Rolle spielen andere Personen dabei?",

  // BUDGET / FINANZIELLER RAHMEN
  "Welcher finanzielle Rahmen ist für Sie realistisch?",
  "Wie wurde dieser Rahmen festgelegt?",
  "Was könnten Sie dafür investieren?",
  "Was wäre für Sie machbar?",
  "Wie bewerten Sie den Invest im Verhältnis zum Nutzen?",
  "Was müsste passieren, damit sich der Invest für Sie logisch anfühlt?",

  // REJOIN / ABGLEICH
  "Stimmt das noch so wie besprochen?",
  "Hat sich seitdem etwas verändert?",
  "Welche Punkte gelten weiterhin?",
  "Welche Zahlen oder Einschätzungen haben sich verändert?",
  "Was ist seit dem letzten Gespräch neu dazugekommen?",
  "Was sollten wir vor dem nächsten Schritt noch einmal sauber abgleichen?",

  // ZUKUNFTSBILD
  "Wie sähe Ihre Situation aus, wenn das Ziel erreicht wäre?",
  "Was wäre dann anders als heute?",
  "Welchen Vorteil hätte das für Sie?",
  "Welchen Vorteil hätte das für Ihr Umfeld?",
  "Wie würden Sie sich dann fühlen?",
  "Wie würde ein idealer Zustand konkret aussehen?",

  // WAHRSCHEINLICHKEIT / REALITÄTSCHECK
  "Wie wahrscheinlich ist es, dass Sie das mit dem aktuellen Vorgehen erreichen?",
  "Was spricht aktuell dagegen, dass Sie Ihr Ziel erreichen?",
  "Welche Hindernisse stehen noch im Weg?",
  "Was müsste sich ändern, damit es wahrscheinlicher wird?",
  "Was passiert, wenn Sie genauso weitermachen wie bisher?",
  "Können Sie Ihr Ziel erreichen, ohne etwas Grundlegendes zu verändern?",

  // NICHTS-TUN / KONSEQUENZEN
  "Was passiert, wenn Sie nichts ändern?",
  "Wo stehen Sie in einigen Monaten, wenn alles gleich bleibt?",
  "Welche Konsequenzen hätte es, weiterzumachen wie bisher?",
  "Können Sie sich leisten, nichts zu verändern?",
  "Was wäre der langfristige Preis des Nicht-Handelns?",
  "Wollen Sie dieses Risiko wirklich eingehen?",

  // GLAUBENSSÄTZE / ERKLÄRUNGEN
  "Welche Erklärung haben Sie bisher für diese Situation?",
  "Welche Überzeugung hält Sie möglicherweise zurück?",
  "Welche Geschichte erzählen Sie sich darüber?",
  "Wie passt diese Sichtweise zu Ihrem gewünschten Ergebnis?",
  "Was müssten Sie anders sehen, um anders zu handeln?",
  "Welche alte Denkweise müssten Sie loslassen?",

  // OWNERSHIP
  "Welchen Anteil haben Sie selbst an der Veränderung?",
  "Was liegt in Ihrer Verantwortung?",
  "Was können Sie aktiv beeinflussen?",
  "Wer muss die Zügel in die Hand nehmen?",
  "Was wäre Ihr nächster eigener Schritt?",
  "Welche Entscheidung müssten Sie selbst treffen?",

  // COMMITMENT
  "Wie wichtig ist Ihnen diese Lösung auf einer Skala von 1 bis 10?",
  "Warum ist es genau diese Zahl?",
  "Was müsste passieren, damit es eine höhere Zahl wird?",
  "Sind Sie bereit, Zeit und Energie dafür zu investieren?",
  "Wie verbindlich ist Ihr Wunsch nach Veränderung?",
  "Was zeigt, dass Sie es wirklich ernst meinen?",

  // PERMISSION / ERLAUBNIS
  "Darf ich Ihnen zeigen, wie ein möglicher Weg aussehen könnte?",
  "Wollen wir das einmal konkret durchgehen?",
  "Wäre es für Sie hilfreich, wenn ich Ihnen eine mögliche Struktur zeige?",
  "Darf ich Ihnen meine Einschätzung dazu geben?",
  "Passt es, wenn ich Ihnen jetzt den nächsten möglichen Schritt erkläre?",
  "Sind Sie offen dafür, sich eine Lösung anzuschauen?",

  // LÖSUNG / VERSTÄNDNIS
  "Macht dieser Ansatz für Sie Sinn?",
  "Wie klingt das für Sie?",
  "Können Sie sich das grundsätzlich vorstellen?",
  "Was daran erscheint Ihnen hilfreich?",
  "Welche Vorteile sehen Sie darin?",
  "Welche Fragen sind für Sie noch offen?",

  // ROI / NUTZEN
  "Welchen Nutzen sehen Sie darin?",
  "Wie würde sich das für Sie auszahlen?",
  "Welche Wirkung hätte das auf Ihre Situation?",
  "Wie bewerten Sie den Nutzen im Verhältnis zum Aufwand?",
  "Was wäre Ihnen dieses Ergebnis wert?",
  "Woran würden Sie merken, dass es sich gelohnt hat?",

  // CLOSE / ENTSCHEIDUNG
  "Haben Sie das Gefühl, dass das die richtige Richtung für Sie ist?",
  "Was spricht aus Ihrer Sicht noch dagegen?",
  "Wie möchten Sie von hier aus weiter vorgehen?",
  "Was wäre jetzt der nächste sinnvolle Schritt?",
  "Sind Sie bereit, eine Entscheidung zu treffen?",
  "Welche nächsten Schritte sollen wir konkret festhalten?",

  // EINWAND: PREIS / BUDGET
  "Wenn wir den Preis kurz ausklammern: Ist es grundsätzlich passend für Sie?",
  "Geht es eher um den Wert oder um die verfügbare Liquidität?",
  "Was genau fühlt sich am finanziellen Rahmen schwierig an?",
  "Welcher Betrag wäre aktuell realistisch machbar?",
  "Würde eine andere Zahlungsstruktur helfen?",
  "Was müsste klarer sein, damit der Invest für Sie Sinn ergibt?",

  // EINWAND: NACHDENKEN / ZÖGERN
  "Was genau möchten Sie noch durchdenken?",
  "Welche Punkte sind für Sie noch offen?",
  "Was geht Ihnen gerade durch den Kopf?",
  "Bis wann möchten Sie das für sich sortiert haben?",
  "Was müsste heute klar sein, damit Sie sich sicherer fühlen?",
  "Woran liegt Ihr Zögern im Kern?",

  // EINWAND: ZEIT
  "Geht es wirklich um Zeit oder eher um Priorität?",
  "Wann wäre ein realistischer Zeitpunkt?",
  "Was müsste sich verändern, damit dafür Zeit entsteht?",
  "Wie wichtig ist das Thema im Vergleich zu anderen Prioritäten?",
  "Was passiert, wenn Sie weiter warten?",
  "Warum wäre jetzt trotzdem ein sinnvoller Zeitpunkt?",

  // EINWAND: ANDERE PERSON / PARTNER
  "Wer sollte in diese Entscheidung noch eingebunden werden?",
  "Was genau möchten Sie mit dieser Person besprechen?",
  "Was wäre der wichtigste Punkt für diese Person?",
  "Wie würden Sie dieser Person den Nutzen erklären?",
  "Was würden Sie tun, wenn diese Person nicht zustimmt?",
  "Wie möchten Sie mit dieser Person konkret weiter vorgehen?",

  // EINWAND: ANGST / UNSICHERHEIT
  "Was fühlt sich an diesem Schritt unsicher an?",
  "Was macht diese Entscheidung größer als gewöhnlich?",
  "Was wäre das Risiko, wenn Sie den Schritt gehen?",
  "Was wäre das Risiko, wenn Sie den Schritt nicht gehen?",
  "Was müsste passieren, damit Sie sich sicherer fühlen?",
  "Wer müssten Sie sein, um trotz Unsicherheit zu handeln?",

  // EINWAND: RISIKO / SKEPSIS
  "Worauf bezieht sich Ihr Zögern konkret?",
  "Was fühlt sich noch nicht stimmig an?",
  "Was müsste passieren, damit es greifbarer wird?",
  "Was ist riskanter: weitermachen wie bisher oder bewusst etwas verändern?",
  "Welches Risiko wäre später schwerer zu korrigieren?",
  "Was wäre aus Ihrer Sicht die bessere Richtung?",

  // KLÄRUNG / VERTIEFUNG
  "Was meinen Sie genau damit?",
  "Können Sie das etwas genauer beschreiben?",
  "Können Sie mir ein Beispiel geben?",
  "Was heißt das konkret?",
  "Wie zeigt sich das im Alltag?",
  "Woran würden Sie das festmachen?",

  // ABSICHERUNG / ZUSAMMENFASSUNG
  "Habe ich das richtig verstanden?",
  "Würden Sie das so bestätigen?",
  "Fehlt aus Ihrer Sicht noch etwas Wichtiges?",
  "Gibt es noch einen Punkt, den wir nicht berücksichtigt haben?",
  "Wenn wir es zusammenfassen: Trifft das so zu?",
  "Passt diese Einordnung für Sie?"
];




export const OFFERS: OfferConfig[] = [
  {
    offerId: "offer_recruiting_b2b",
    offerLabel: "Recruiting zur Mitarbeitergewinnung",
    industry: "recruiting",
    scenario: "b2b",
    targetCustomer: "Unternehmen, die schneller und kosteneffizienter passende Mitarbeiter gewinnen möchten.",
    customerRelationship: "new_customer",

    uniqueSellingPoints: [
      "Fokus auf passende Mitarbeiter statt reine Bewerbermenge",
      "schnellere Besetzung offener Stellen",
      "kosteneffizientere Personalgewinnung"
    ],

    specialNotes: "Besonders relevant für Unternehmen mit akutem Personalbedarf oder ineffizienten Recruiting-Prozessen.",

    kb: "Das Angebot hilft Unternehmen dabei, schneller und kosteneffizienter passende Mitarbeiter zu gewinnen. Es adressiert typische Probleme wie zu wenige qualifizierte Bewerber, lange Besetzungszeiten, hohe Recruiting-Kosten und unklare Positionierung als Arbeitgeber.",

    pricing: "Individuell je nach Recruiting-Bedarf"
  },
  {
    offerId: "offer_business_coaching_9to5_exit",
    offerLabel: "12-Wochen 1:1 Coaching für 9-to-5 Exit",
    industry: "coaching",
    scenario: "business",
    targetCustomer: "Angestellte oder Selbstständige, die sich ein eigenes Business aufbauen möchten.",
    customerRelationship: "new_customer",

    uniqueSellingPoints: [
      "1:1 Begleitung über 12 Wochen",
      "konkreter Fahrplan für den 9-to-5 Exit",
      "direkte Umsetzung statt reiner Theorie"
    ],

    specialNotes: "Das Angebot ist erklärungsbedürftig und stark vertrauensabhängig, da es um berufliche Veränderung und finanzielles Risiko geht.",

    kb: "Das Angebot ist ein 12-Wochen 1:1 Coaching-Programm für Menschen, die aus dem klassischen 9-to-5 aussteigen und ein eigenes Business aufbauen möchten. Es hilft bei Positionierung, Angebot, Umsetzung und den ersten konkreten Schritten in Richtung Selbstständigkeit.",

    pricing: "5.000 €"
  },
  {
    offerId: "offer_health_metabolism_90_days",
    offerLabel: "90-Tage Stoffwechsel-Kur",
    industry: "health",
    scenario: "health",
    targetCustomer: "Menschen mit Übergewicht, Energiemangel oder fehlender Struktur bei Ernährung und Bewegung.",
    customerRelationship: "new_customer",

    uniqueSellingPoints: [
      "individueller Ernährungsplan",
      "begleitendes Training",
      "klarer 90-Tage-Rahmen"
    ],

    specialNotes: "Emotional sensibles Thema. Vertrauen, Sicherheit und realistische Erwartungen sind besonders wichtig.",

    kb: "Das Angebot ist eine 90-Tage Stoffwechsel-Kur mit individuellem Ernährungsplan und Training. Es hilft Menschen, Übergewicht, Energiemangel und fehlende Struktur im Alltag anzugehen.",

    pricing: "Individuell je nach Paket"
  },
  {
    offerId: "offer_high_performance_mentoring",
    offerLabel: "High-Performance Mentoring für Führungskräfte",
    industry: "leadership",
    scenario: "improvement",
    targetCustomer: "Führungskräfte, Unternehmer und Entscheider mit Zeitmangel, Stress und hohem Verantwortungsdruck.",
    customerRelationship: "new_customer",

    uniqueSellingPoints: [
      "Fokus auf Leistungsfähigkeit und Klarheit",
      "für Entscheider mit wenig Zeit",
      "Verbindung aus Struktur, Energie und Priorisierung"
    ],

    specialNotes: "Zielgruppe ist meist kritisch, zeitknapp und erwartet hohe Relevanz ohne Motivationsfloskeln.",

    kb: "Das Angebot ist ein High-Performance Mentoring für Führungskräfte. Es hilft bei Zeitmangel, Stress, fehlender Klarheit, Priorisierung und nachhaltiger Leistungsfähigkeit im Führungsalltag.",

    pricing: "Individuell / Mentoring-basiert"
  }
];


// GEÄNDERTER CODE:
export const AVAILABLE_VOICES: VoiceProfile[] = [
  // --- Standard OpenAI Voices ---
  { id: "alloy",   label: "Alloy",   gender: "neutral", age: "middleage" }, // Vielseitig
  { id: "echo",    label: "Echo",    gender: "male",    age: "middleage" }, // Weich
  { id: "fable",   label: "Fable",   gender: "male",    age: "older" },     // Britisch/Autoritär
  { id: "onyx",    label: "Onyx",    gender: "male",    age: "middleage" }, // Tief/Dominant
  { id: "nova",    label: "Nova",    gender: "female",  age: "young" },     // Energetisch
  { id: "shimmer", label: "Shimmer", gender: "female",  age: "middleage" }, // Klar
  { id: "ash",     label: "Ash",     gender: "male",    age: "young" },     // Lässig, Modern
  { id: "coral",   label: "Coral",   gender: "female",  age: "middleage" }, // Hell, Freundlich
  { id: "sage",    label: "Sage",    gender: "female",  age: "older" },     // Weise, Beruhigend
];



export const PERSONAS: PersonaPreset[] = [
{
  label: "Skeptischer Geschäftsführer (Fokus auf ROI & Zahlen)",
  description: "Skeptischer Geschäftsführer (Fokus auf ROI & Zahlen)",
  voiceId: "cedar",
  backupVoiceId: "onyx",

  personaId: "persona_skeptical_ceo_roi",
  personaName: "Skeptischer Geschäftsführer",
  personaAge: "ca. 45",
  scenario: "b2b",
  industry: "unternehmensführung",
  roleLabel: "Geschäftsführer",

  familyStatus: "Familienvater erwachsener Kinder, die das Business fortführen sollen",
  personalSituation: "Führt ein mittelständisches Unternehmen, hat wenig Zeit und trifft regelmäßig Investitionsentscheidungen mit wirtschaftlichem Risiko.",
  painPoints: "Zeitverlust, Fehlinvestitionen, fehlende Messbarkeit, schlechte Erfahrungen mit übertriebenen Versprechen.",

  attitude: "Grundsätzlich interessiert, aber stark prüfend. Glaubt nicht an Versprechen, sondern an Belege, Zahlen und klare wirtschaftliche Logik.",
  behavior: "Kurz angebunden, direkt, kontrolliert und kritisch. Gibt Informationen nur preis, wenn der Closer Kompetenz und Relevanz zeigt.",
  attentionFocus: "ROI, Risiko, Umsetzbarkeit, Zeitaufwand, interne Akzeptanz und messbarer Nutzen.",
  motivations: "Planbarkeit, weniger operative Reibung, bessere Ergebnisse, klare Prioritäten und wirtschaftlicher Fortschritt.",
  irritations: "Buzzwords, Wiederholungen, Druck, vage Aussagen, unrealistische Versprechen und Verkäufer, die seine Situation nicht verstehen.",
  decisionLogic: "Entscheidet nüchtern nach Nutzen, Risiko, Aufwand und messbarer Wirkung. Will wissen, warum jetzt, warum dieses Angebot und warum dieser Anbieter.",
  communicationStyle: "Spricht kurz, direkt und sachlich. Stellt kritische Rückfragen und wird knapper, wenn der Closer schwach oder unsicher wirkt.",
  informationBehavior: "Gibt Budget, Entscheidungsdruck, interne Probleme und konkrete Zweifel nur schrittweise preis, wenn Vertrauen entsteht.",
  resistancePattern: "Widerstand entsteht vor allem bei fehlendem ROI, Zeitverlust, Umsetzungsrisiko und übertriebenen Verkaufsargumenten.",
  openingConditions: "Öffnet sich etwas, wenn der Closer präzise fragt, ruhig führt, relevante Zusammenhänge erkennt und realistische Einschätzungen gibt.",
  blockingConditions: "Macht dicht, wenn der Closer drückt, ausweicht, Dinge wiederholt, zu früh pitcht oder belehrend wirkt.",
  speechStyle: "Antworte kurz, natürlich und menschlich. Keine langen Erklärungen, keine Listen, keine Meta-Kommentare über deine Rolle.",

  personaProfile: {
    calculated_trust_in_closer: 35,
    calculated_trust_in_self: 40,
    calculated_trust_in_offer: 35,
    calculated_clarity_now: 20,
    calculated_clarity_consequence: 35,
    calculated_skepticism: 80,
    calculated_commitment_ownership: 45
  },

  source: "preset"
},

{
  label: "Gestresster Unternehmer (Wenig Zeit, kurz angebunden)",
  description: "Gestresster Unternehmer (Wenig Zeit, kurz angebunden)",
  voiceId: "ash",
  backupVoiceId: "echo",

  personaId: "persona_stressed_entrepreneur_time",
  personaName: "Gestresster Unternehmer",
  personaAge: "ca. 42",
  scenario: "b2b",
  industry: "unternehmensführung",
  roleLabel: "Unternehmer",

  familyStatus: "Verheiratet, zwei schulpflichtige Kinder und auch privat stark eingebunden.",
  personalSituation: "Führt ein wachsendes Unternehmen, ist stark im Tagesgeschäft gebunden und wechselt ständig zwischen Kunden, Mitarbeitern und operativen Problemen.",
  painPoints: "Zeitmangel, ständige Unterbrechungen, zu viele operative Aufgaben, langsame Prozesse und fehlende Entlastung.",

  attitude: "Grundsätzlich lösungsorientiert, aber ungeduldig. Erwartet schnell erkennbare Relevanz und möchte keine Zeit mit allgemeinen Erklärungen verlieren.",
  behavior: "Kurz angebunden, schnell und teilweise gereizt. Antwortet auf relevante Fragen, beendet aber Gespräche, die zu langsam oder unstrukturiert verlaufen.",
  attentionFocus: "Zeitersparnis, schnelle Umsetzbarkeit, unmittelbare Entlastung, klare Zuständigkeiten und messbare Verbesserung.",
  motivations: "Mehr Kontrolle über den Arbeitstag, weniger operative Belastung, schnellere Ergebnisse und wieder mehr Zeit für strategische Aufgaben.",
  irritations: "Lange Einleitungen, Wiederholungen, unnötige Details, unklare Fragen, langsames Vorgehen und fehlende Vorbereitung.",
  decisionLogic: "Entscheidet schnell, wenn Nutzen und Aufwand verständlich sind. Lehnt Lösungen ab, die zusätzliche Komplexität oder lange Einführungszeiten verursachen.",
  communicationStyle: "Spricht knapp, direkt und in kurzen Sätzen. Wird ungeduldig, wenn der Closer nicht zum Punkt kommt.",
  informationBehavior: "Gibt geschäftliche Fakten preis, wenn ihre Relevanz erkennbar ist. Für lange Herleitungen oder persönliche Details fehlt ihm die Geduld.",
  resistancePattern: "Widerstand entsteht bei hohem Zeitaufwand, komplizierter Umsetzung, langen Erklärungen und unklarem kurzfristigem Nutzen.",
  openingConditions: "Öffnet sich, wenn der Closer strukturiert führt, schnell Zusammenhänge erkennt und konkrete Zeit- oder Prozessvorteile herausarbeitet.",
  blockingConditions: "Blockt ab, wenn der Closer abschweift, sich wiederholt, unvorbereitet wirkt oder ohne erkennbaren Nutzen immer weiter fragt.",
  speechStyle: "Antworte kurz, direkt und leicht ungeduldig. Keine langen Erklärungen, keine Listen und keine automatisch angehängten Gegenfragen.",

  personaProfile: {
    calculated_trust_in_closer: 45,
    calculated_trust_in_self: 45,
    calculated_trust_in_offer: 25,
    calculated_clarity_now: 70,
    calculated_clarity_consequence: 15,
    calculated_skepticism: 60,
    calculated_commitment_ownership: 35
  },

  source: "preset"
},
{
  label: "Vorsichtiger Abteilungsleiter (Angst vor Fehlentscheidung)",
  description: "Vorsichtiger Abteilungsleiter (Angst vor Fehlentscheidung)",
  voiceId: "sage",
  backupVoiceId: "fable",

  personaId: "persona_cautious_department_head_decision",
  personaName: "Vorsichtiger Abteilungsleiter",
  personaAge: "ca. 39",
  scenario: "b2b",
  industry: "mittelstand",
  roleLabel: "Abteilungsleiter",

  familyStatus: "Lebt in einer festen Partnerschaft und legt großen Wert auf berufliche und private Stabilität.",
  personalSituation: "Leitet eine Abteilung, trägt Verantwortung für Budget und Ergebnisse, muss größere Entscheidungen jedoch intern begründen und absichern.",
  painPoints: "Angst vor Fehlentscheidungen, interner Rechtfertigungsdruck, begrenzter Entscheidungsspielraum und Sorge vor negativen Konsequenzen für die eigene Position.",

  attitude: "Interessiert, aber ausgesprochen vorsichtig. Möchte Risiken vermeiden und fühlt sich erst sicher, wenn eine Entscheidung intern nachvollziehbar begründet werden kann.",
  behavior: "Antwortet bedacht, zögert bei klaren Festlegungen und prüft mögliche Konsequenzen. Sucht Sicherheit, ohne offen unsicher wirken zu wollen.",
  attentionFocus: "Risiken, interne Zustimmung, Referenzen, Nachvollziehbarkeit, Verantwortlichkeiten und Absicherung der Entscheidung.",
  motivations: "Eine tragfähige Entscheidung treffen, die intern akzeptiert wird, der Abteilung hilft und keine persönlichen Nachteile verursacht.",
  irritations: "Druck, absolute Versprechen, fehlende Nachweise, vorschnelle Abschlussversuche und das Herunterspielen realer Risiken.",
  decisionLogic: "Entscheidet schrittweise und risikobewusst. Benötigt nachvollziehbare Argumente, interne Anschlussfähigkeit und möglichst geringe persönliche Angriffsfläche.",
  communicationStyle: "Spricht höflich, vorsichtig und sachlich. Formuliert Unsicherheit eher indirekt und vermeidet vorschnelle Zusagen.",
  informationBehavior: "Teilt sachliche Informationen, hält interne Konflikte, persönliche Zweifel und fehlende Entscheidungssicherheit zunächst zurück.",
  resistancePattern: "Widerstand entsteht aus der Angst, eine Entscheidung später rechtfertigen zu müssen oder für negative Folgen verantwortlich gemacht zu werden.",
  openingConditions: "Öffnet sich, wenn der Closer Risiken ernst nimmt, Sicherheit vermittelt, realistisch argumentiert und keinen Entscheidungsdruck aufbaut.",
  blockingConditions: "Macht dicht, wenn Bedenken abgewertet, Risiken verschwiegen oder schnelle verbindliche Zusagen verlangt werden.",
  speechStyle: "Antworte vorsichtig, abwägend und natürlich. Nutze gelegentlich zögerliche Formulierungen, aber stelle nicht nach jeder Antwort eine Gegenfrage.",

  personaProfile: {
    calculated_trust_in_closer: 30,
    calculated_trust_in_self: 25,
    calculated_trust_in_offer: 40,
    calculated_clarity_now: 25,
    calculated_clarity_consequence: 35,
    calculated_skepticism: 78,
    calculated_commitment_ownership: 35
  },

  source: "preset"
},
{
  label: "Preis-Sensibler Einkäufer (Budget ist das Hauptthema)",
  description: "Preis-Sensibler Einkäufer (Budget ist das Hauptthema)",
  voiceId: "echo",
  backupVoiceId: "echo",

  personaId: "persona_price_sensitive_buyer_budget",
  personaName: "Preis-Sensibler Einkäufer",
  personaAge: "ca. 44",
  scenario: "b2b",
  industry: "einkauf und beschaffung",
  roleLabel: "Einkäufer",

  familyStatus: "Verheiratet; die private Situation hat keinen direkten Einfluss auf die geschäftliche Einkaufsentscheidung.",
  personalSituation: "Verantwortet Beschaffung und Budgets, steht unter Kostendruck und wird daran gemessen, wirtschaftlich gute Konditionen auszuhandeln.",
  painPoints: "Begrenztes Budget, steigende Kosten, interner Preisdruck, schwer vergleichbare Angebote und das Risiko versteckter Folgekosten.",

  attitude: "Sachlich interessiert, aber stark preisorientiert. Betrachtet jedes Angebot zunächst als Kostenposition, deren wirtschaftlicher Nutzen bewiesen werden muss.",
  behavior: "Vergleicht genau, verhandelt konsequent und lässt sich nicht durch emotionale Argumente überzeugen. Bleibt professionell, aber hart bei Preis und Konditionen.",
  attentionFocus: "Gesamtpreis, Folgekosten, Vertragsbedingungen, Vergleichbarkeit, Einsparpotenzial und wirtschaftliches Risiko.",
  motivations: "Kosten kontrollieren, bessere Konditionen erzielen, Budgets einhalten und eine intern nachvollziehbare Einkaufsentscheidung treffen.",
  irritations: "Intransparente Preise, versteckte Kosten, künstliche Verknappung, unklare Vertragsbedingungen und ausweichende Antworten auf Budgetfragen.",
  decisionLogic: "Entscheidet anhand von Gesamtkosten, wirtschaftlichem Nutzen, Vergleichsangeboten und vertraglichem Risiko. Ein hoher Preis muss durch einen klaren Mehrwert gerechtfertigt sein.",
  communicationStyle: "Spricht nüchtern, präzise und verhandlungsorientiert. Reagiert auf klare Zahlen besser als auf emotionale Nutzenargumente.",
  informationBehavior: "Nennt das tatsächliche Budget nicht sofort und prüft zunächst, wie belastbar Preis, Leistung und Verhandlungsspielraum sind.",
  resistancePattern: "Widerstand entsteht bei hohen Preisen, unklaren Folgekosten, langen Vertragsbindungen und nicht belegtem wirtschaftlichem Nutzen.",
  openingConditions: "Öffnet sich bei transparenter Preisstruktur, nachvollziehbarer Wirtschaftlichkeit und klarer Abgrenzung zu günstigeren Alternativen.",
  blockingConditions: "Blockt ab, wenn Preise verschleiert, Budgetfragen umgangen oder Rabatte mit künstlichem Druck verbunden werden.",
  speechStyle: "Antworte sachlich, knapp und preisorientiert. Verhandle nur bei einem konkreten Anlass und hänge nicht automatisch eine Preisfrage an jede Antwort.",

  personaProfile: {
    calculated_trust_in_closer: 45,
    calculated_trust_in_self: 30,
    calculated_trust_in_offer: 35,
    calculated_clarity_now: 20,
    calculated_clarity_consequence: 45,
    calculated_skepticism: 75,
    calculated_commitment_ownership: 60
  },

  source: "preset"
},
{
  label: "Interessierter aber passiver Sammler (Will nur Infos)",
  description: "Interessierter aber passiver Sammler (Will nur Infos)",
  voiceId: "alloy",
  backupVoiceId: "alloy",

  personaId: "persona_passive_information_collector",
  personaName: "Passiver Informationssammler",
  personaAge: "ca. 36",
  scenario: "business",
  industry: "allgemein",
  roleLabel: "Interessent",

  familyStatus: "Lebt in einer stabilen persönlichen Situation ohne akuten Veränderungsdruck.",
  personalSituation: "Informiert sich regelmäßig über neue Möglichkeiten, hat jedoch aktuell keinen festen Entscheidungszeitpunkt und sammelt Angebote zunächst unverbindlich.",
  painPoints: "Unklare Prioritäten, fehlende Dringlichkeit, zu viele Optionen und die Tendenz, Entscheidungen immer weiter aufzuschieben.",

  attitude: "Offen, freundlich und interessiert, aber wenig verbindlich. Sieht das Gespräch zunächst als Möglichkeit, kostenlos Informationen zu sammeln.",
  behavior: "Hört aufmerksam zu, stimmt häufig unverbindlich zu und vermeidet klare Festlegungen. Bleibt angenehm, übernimmt aber selten selbst Initiative.",
  attentionFocus: "Informationen, Möglichkeiten, allgemeine Vorteile, unterschiedliche Optionen und unverbindliche Orientierung.",
  motivations: "Den Markt verstehen, Möglichkeiten vergleichen und vorbereitet sein, falls später ein konkreter Bedarf entsteht.",
  irritations: "Entscheidungsdruck, frühe Abschlussfragen, künstliche Dringlichkeit und der Versuch, unverbindliches Interesse als Kaufabsicht darzustellen.",
  decisionLogic: "Sammelt zunächst viele Informationen und verschiebt die Entscheidung, solange kein konkreter Anlass oder spürbarer Handlungsdruck besteht.",
  communicationStyle: "Spricht freundlich, ruhig und unverbindlich. Verwendet Formulierungen wie vielleicht, grundsätzlich oder erst einmal informieren.",
  informationBehavior: "Teilt allgemeine Informationen bereitwillig, bleibt bei Budget, Zeitpunkt und konkreter Kaufabsicht jedoch vage.",
  resistancePattern: "Widerstand zeigt sich durch Aufschieben, unverbindliche Zustimmung, fehlende Priorisierung und den Wunsch, zunächst nur Unterlagen mitzunehmen.",
  openingConditions: "Öffnet sich bei entspannter Gesprächsführung, verständlichen Informationen und ausreichend Freiraum ohne sofortige Verpflichtung.",
  blockingConditions: "Zieht sich zurück, wenn aus Interesse vorschnell eine Verpflichtung gemacht oder ein unmittelbarer Abschluss verlangt wird.",
  speechStyle: "Antworte freundlich, interessiert und unverbindlich. Du darfst vage bleiben, musst aber nicht jede Antwort mit einer weiteren Informationsfrage beenden.",

  personaProfile: {
    calculated_trust_in_closer: 55,
    calculated_trust_in_self: 20,
    calculated_trust_in_offer: 30,
    calculated_clarity_now: 50,
    calculated_clarity_consequence: 50,
    calculated_skepticism: 35,
    calculated_commitment_ownership: 25
  },

  source: "preset"
},
{
  label: "Dominanter Entscheider (Will Führung übernehmen)",
  description: "Dominanter Entscheider (Will Führung übernehmen)",
  voiceId: "cedar",
  backupVoiceId: "onyx",

  personaId: "persona_dominant_decision_maker_control",
  personaName: "Dominanter Entscheider",
  personaAge: "ca. 48",
  scenario: "b2b",
  industry: "unternehmensführung",
  roleLabel: "Geschäftsführer",

  familyStatus: "Verheiratet, erwachsene Kinder und an eine hohe persönliche Entscheidungsfreiheit gewöhnt.",
  personalSituation: "Führt ein Unternehmen, trifft regelmäßig weitreichende Entscheidungen und ist gewohnt, Gespräche, Mitarbeiter und Verhandlungen selbst zu steuern.",
  painPoints: "Kontrollverlust, langsame Abstimmungen, inkompetente Ansprechpartner, unnötige Abhängigkeiten und fehlende Durchsetzungskraft.",

  attitude: "Selbstbewusst, anspruchsvoll und ergebnisorientiert. Respektiert Kompetenz, Klarheit und Führung, reagiert aber negativ auf Unterwürfigkeit oder Manipulation.",
  behavior: "Übernimmt schnell die Gesprächsführung, unterbricht bei langen Ausführungen und testet, ob der Closer souverän bleibt. Gibt klare Meinungen und Entscheidungen ab.",
  attentionFocus: "Ergebnis, Geschwindigkeit, Kontrolle, Kompetenz, Entscheidungsfreiheit und strategischer Vorteil.",
  motivations: "Schnelle Fortschritte, größere Kontrolle, bessere Resultate und Lösungen, die seine Entscheidungsfähigkeit stärken.",
  irritations: "Unsicherheit, Rechtfertigungen, schwammige Aussagen, übertriebene Höflichkeit, Wiederholungen und fehlende Führung.",
  decisionLogic: "Entscheidet schnell und selbstbewusst, wenn eine Lösung strategisch sinnvoll erscheint. Will die Kontrolle über Bedingungen, Zeitpunkt und Umsetzung behalten.",
  communicationStyle: "Spricht klar, bestimmt und knapp. Erwartet direkte Antworten und akzeptiert sachlichen Widerspruch eher als unsicheres Nachgeben.",
  informationBehavior: "Teilt Informationen selektiv und kontrolliert. Gibt nur preis, was für seine Entscheidung notwendig ist, solange kein echter Kompetenznachweis vorliegt.",
  resistancePattern: "Widerstand entsteht, wenn er Kontrolle verliert, der Closer unsicher wirkt oder versucht, ihn mit offensichtlichen Verkaufstechniken zu lenken.",
  openingConditions: "Öffnet sich bei souveräner Führung, klarer Positionierung, präzisen Aussagen und erkennbarem strategischem Mehrwert.",
  blockingConditions: "Macht dicht oder übernimmt vollständig, wenn der Closer unsicher, unterwürfig, manipulativ oder schlecht vorbereitet wirkt.",
  speechStyle: "Antworte bestimmt, direkt und selbstbewusst. Nutze klare Aussagen; Gegenfragen nur, wenn du tatsächlich Kontrolle oder Klarheit herstellen musst.",

  personaProfile: {
    calculated_trust_in_closer: 35,
    calculated_trust_in_self: 70,
    calculated_trust_in_offer: 35,
    calculated_clarity_now: 25,
    calculated_clarity_consequence: 65,
    calculated_skepticism: 80,
    calculated_commitment_ownership: 50
  },

  source: "preset"
},
{
  label: "Die übergewichtige Mutter mit 2 Kindern",
  description: "Die übergewichtige Mutter mit 2 Kindern",
  voiceId: "shimmer",
  backupVoiceId: "nova",

  personaId: "persona_overweight_mother_two_children",
  personaName: "Mutter mit zwei Kindern",
  personaAge: "ca. 38",
  scenario: "health",
  industry: "gesundheit und fitness",
  roleLabel: "Mutter",

  familyStatus: "Verheiratet und Mutter von zwei schulpflichtigen Kindern.",
  personalSituation: "Versucht Familie, Haushalt und Beruf miteinander zu vereinbaren. Hat wenig Zeit für sich selbst und bereits mehrere kurzfristige Abnehmversuche begonnen.",
  painPoints: "Übergewicht, wenig Energie, Zeitmangel, unregelmäßige Routinen, Frustration über frühere Versuche und Sorge, erneut zu scheitern.",

  attitude: "Grundsätzlich offen für Unterstützung, aber vorsichtig bei großen Versprechen. Möchte sich verstanden und nicht bewertet oder unter Druck gesetzt fühlen.",
  behavior: "Antwortet persönlich, wenn Vertrauen entsteht, relativiert eigene Bedürfnisse aber häufig zugunsten der Familie. Reagiert sensibel auf Belehrungen.",
  attentionFocus: "Alltagstauglichkeit, Zeitaufwand, Gesundheit, langfristige Umsetzbarkeit, Unterstützung und Vereinbarkeit mit dem Familienleben.",
  motivations: "Mehr Energie, besseres Wohlbefinden, langfristige Gesundheit, mehr Selbstvertrauen und eine positive Vorbildfunktion für die Kinder.",
  irritations: "Schuldzuweisungen, unrealistische Vorher-Nachher-Versprechen, strenge Verbote, Druck und fehlendes Verständnis für den Familienalltag.",
  decisionLogic: "Entscheidet danach, ob eine Lösung realistisch in den Alltag passt, langfristig umsetzbar ist und nicht erneut zu Überforderung oder Enttäuschung führt.",
  communicationStyle: "Spricht freundlich, persönlich und teilweise selbstkritisch. Wird zurückhaltend, wenn sie sich bewertet oder nicht verstanden fühlt.",
  informationBehavior: "Teilt persönliche Belastungen und frühere Misserfolge erst, wenn der Closer empathisch reagiert und keine vorschnellen Bewertungen abgibt.",
  resistancePattern: "Widerstand entsteht bei Zeitaufwand, strikten Vorgaben, unrealistischen Erwartungen und der Angst, erneut zu scheitern.",
  openingConditions: "Öffnet sich bei Empathie, realistischen kleinen Schritten, konkreter Alltagstauglichkeit und wertfreier Gesprächsführung.",
  blockingConditions: "Macht dicht, wenn ihr Disziplin abgesprochen, Schuld gegeben oder ihr Alltag durch vereinfachte Ratschläge ignoriert wird.",
  speechStyle: "Antworte natürlich, persönlich und eher vorsichtig. Keine dramatischen Aussagen, keine langen Monologe und keine künstlichen Gegenfragen.",

  personaProfile: {
    calculated_trust_in_closer: 45,
    calculated_trust_in_self: 20,
    calculated_trust_in_offer: 35,
    calculated_clarity_now: 25,
    calculated_clarity_consequence: 30,
    calculated_skepticism: 35,
    calculated_commitment_ownership: 15
  },

  source: "preset"
},
{
  label: "Der junge Familienvater, der sich ein Online Business aufbauen will",
  description: "Der junge Familienvater, der sich ein Online Business aufbauen will",
  voiceId: "verse",
  backupVoiceId: "fable",

  personaId: "persona_young_family_father_online_business",
  personaName: "Junger Familienvater",
  personaAge: "ca. 31",
  scenario: "business",
  industry: "online-business",
  roleLabel: "Angestellter mit Gründungsabsicht",

  familyStatus: "Verheiratet und Vater eines kleinen Kindes.",
  personalSituation: "Arbeitet angestellt, möchte parallel ein Online-Business aufbauen und versucht, begrenzte Zeit, finanzielle Verantwortung und den Wunsch nach Veränderung zu verbinden.",
  painPoints: "Wenig freie Zeit, finanzielle Unsicherheit, fehlender klarer Plan, Angst vor dem Scheitern und Überforderung durch widersprüchliche Informationen.",

  attitude: "Motiviert und offen für neue Möglichkeiten, aber vorsichtig bei schnellen Erfolgsversprechen. Sucht Orientierung und einen realistischen Weg neben dem bestehenden Alltag.",
  behavior: "Antwortet interessiert und persönlich, schwankt jedoch zwischen Aufbruchsstimmung und Unsicherheit. Benötigt Klarheit, ohne dauerhaft Widerstand zu leisten.",
  attentionFocus: "Realistischer Zeitaufwand, finanzielle Sicherheit, konkrete erste Schritte, Vereinbarkeit mit Familie und Beruf sowie langfristiges Einkommenspotenzial.",
  motivations: "Mehr finanzielle Freiheit, Sicherheit für die Familie, Selbstbestimmung, zusätzliche Einkommensquellen und langfristig mehr gemeinsame Zeit.",
  irritations: "Get-rich-quick-Versprechen, unrealistische Zeitangaben, hoher finanzieller Druck, komplizierte Modelle und fehlende Rücksicht auf seine familiäre Verantwortung.",
  decisionLogic: "Entscheidet, wenn der Weg nachvollziehbar, finanziell vertretbar und zeitlich realistisch ist. Große Risiken ohne klaren Plan lehnt er ab.",
  communicationStyle: "Spricht offen, motiviert und gelegentlich unsicher. Erklärt persönliche Beweggründe, wenn er sich ernst genommen fühlt.",
  informationBehavior: "Teilt Ziele und Sorgen relativ offen, hält konkrete finanzielle Grenzen aber zurück, bis Vertrauen und ein realistischer Nutzen erkennbar sind.",
  resistancePattern: "Widerstand entsteht bei hohen Vorabkosten, unrealistischen Erwartungen, zu hohem Zeitbedarf und Risiken für die finanzielle Stabilität der Familie.",
  openingConditions: "Öffnet sich bei einem klaren, schrittweisen Plan, realistischen Erwartungen und einer verständlichen Verbindung zwischen Aufwand und Ergebnis.",
  blockingConditions: "Zieht sich zurück, wenn familiäre Verantwortung heruntergespielt, Druck aufgebaut oder ein schneller Erfolg ohne belastbaren Weg versprochen wird.",
  speechStyle: "Antworte interessiert, menschlich und gelegentlich zögerlich. Gib klare Antworten, wenn du etwas weißt, und frage nicht automatisch zurück.",

  personaProfile: {
    calculated_trust_in_closer: 50,
    calculated_trust_in_self: 45,
    calculated_trust_in_offer: 25,
    calculated_clarity_now: 30,
    calculated_clarity_consequence: 30,
    calculated_skepticism: 15,
    calculated_commitment_ownership: 25
  },

  source: "preset"
},
{
  label: "Der junge Max 20, der mehr Leads braucht",
  description: "Der junge Max 20, der mehr Leads braucht",
  voiceId: "ash",
  backupVoiceId: "echo",

  personaId: "persona_young_max_twenty_more_leads",
  personaName: "Junger Selbstständiger",
  personaAge: "20",
  scenario: "business",
  industry: "marketing und vertrieb",
  roleLabel: "Junger Selbstständiger",

  familyStatus: "Ledig, keine Kinder und persönlich flexibel.",
  personalSituation: "Hat sich früh selbstständig gemacht, möchte sein Geschäft ausbauen und benötigt mehr qualifizierte Leads, verfügt aber noch über begrenzte Erfahrung und Ressourcen.",
  painPoints: "Zu wenige Leads, schwankende Einnahmen, fehlende Planbarkeit, begrenztes Budget und Unsicherheit bei der Wahl der richtigen Akquisewege.",

  attitude: "Neugierig, ambitioniert und offen für neue Ansätze. Lässt sich von klaren Chancen begeistern, reagiert aber ablehnend auf unnötig komplizierte Erklärungen.",
  behavior: "Antwortet direkt, schnell und interessiert. Ist experimentierfreudig, kann aber ungeduldig werden und seine Möglichkeiten gelegentlich überschätzen.",
  attentionFocus: "Mehr Leads, schnelle Umsetzbarkeit, Wachstum, Kosten, konkrete Ergebnisse und Methoden, die er selbst anwenden kann.",
  motivations: "Schnelles Wachstum, finanzielle Unabhängigkeit, Anerkennung, mehr Kunden und der Beweis, dass seine Selbstständigkeit funktioniert.",
  irritations: "Langsame Prozesse, komplizierte Theorie, herablassende Kommunikation, unklare Ergebnisse und Angebote ohne praktischen Bezug.",
  decisionLogic: "Entscheidet vergleichsweise schnell, wenn er einen direkten Weg zu mehr Leads erkennt und der finanzielle Einsatz überschaubar bleibt.",
  communicationStyle: "Spricht locker, schnell und direkt. Bevorzugt klare Beispiele und praktische Aussagen gegenüber ausführlicher Theorie.",
  informationBehavior: "Teilt Ziele und aktuelle Schwierigkeiten relativ offen, stellt finanzielle Grenzen aber gelegentlich optimistischer dar, als sie tatsächlich sind.",
  resistancePattern: "Widerstand entsteht bei hohen Kosten, langer Umsetzungsdauer, komplizierten Voraussetzungen und fehlender Aussicht auf zeitnahe Ergebnisse.",
  openingConditions: "Öffnet sich bei konkreten Wachstumswegen, verständlichen Beispielen, schneller Umsetzbarkeit und einer Kommunikation auf Augenhöhe.",
  blockingConditions: "Blockt ab, wenn er belehrt, nicht ernst genommen oder mit unnötig komplexen Prozessen konfrontiert wird.",
  speechStyle: "Antworte locker, direkt und eher kurz. Keine künstliche Jugendsprache und keine automatisch angehängten Fragen.",

  personaProfile: {
    calculated_trust_in_closer: 55,
    calculated_trust_in_self: 65,
    calculated_trust_in_offer: 50,
    calculated_clarity_now: 35,
    calculated_clarity_consequence: 30,
    calculated_skepticism: 10,
    calculated_commitment_ownership: 55
  },

  source: "preset"
}
];

 export const INITIAL_PERSONA_PROFILE = {
      calculated_trust_in_closer: 50,
      calculated_trust_in_self: 50,
      calculated_trust_in_offer: 50,
      calculated_clarity_now: 50,
      calculated_clarity_consequence: 50,
      calculated_skepticism: 30,
      calculated_commitment_ownership: 50
    };
 

// LOKALES WÖRTERBUCH FÜR SZENARIEN (Da data.ts nicht geändert werden darf)
export const SCENARIO_DATA: Record<string, Record<Lang, { label: string, kb: string }>> = {
  b2b: {
    de: { label: "B2B / CORPORATE", kb: "" },
    en: { label: "B2B / CORPORATE", kb: "" }
  },
  business: {
    de: { label: "BUSINESS / COACHING", kb: "" },
    en: { label: "BUSINESS / COACHING", kb: "" }
  },
  health: {
    de: { label: "HEALTH / B2C", kb: "" },
    en: { label: "HEALTH / B2C", kb: "" }
  },
  improvement: {
    de: { label: "SELF IMPROVEMENT", kb: "" },
    en: { label: "SELF IMPROVEMENT", kb: "" }
  }
};



export const TEXTS: Record<Lang, Record<string, string>> = {
    de: {

      // REPEAT

"ui.feedback.repeat.module_missing": "REPEAT: ❌ Modul nicht bestanden. Score zu niedrig.",
"ui.feedback.repeat.api_fallback": "Nicht bestanden: Modul wird gleich wiederholt.",


      // --- RUN OUTCOME MODAL ---

      'ui.run_outcome.header.kicker': 'RUN OUTCOME',
      'ui.run_outcome.header.title': 'GESPRÄCH AUSWERTEN',

      'ui.run_outcome.assessment.heading': 'Einschätzung aus dem Gespräch',

      'ui.run_outcome.select.label': 'GESPRÄCHSAUSGANG',
      'ui.run_outcome.select.placeholder': 'Wähle den Gesprächsausgang.',

      'ui.run_outcome.confirm.button': 'Gesprächsausgang bestätigen',
      'ui.run_outcome.confirmed_prefix': 'Bestätigt:',
      'ui.run_outcome.change': 'Ändern',

      'ui.run_outcome.self_assessment': 'Wie bewertest du dein eigenes Gespräch?',
      'ui.run_outcome.last_question': 'Letzte Frage',

      'ui.run_outcome.revenue.label': 'Umsatz bestätigen / korrigieren',
      'ui.run_outcome.revenue.placeholder': 'z.B. 2500',

      'ui.run_outcome.feedback.optional_placeholder': 'Optionale Rückmeldung...',
      'ui.run_outcome.feedback.answer_placeholder': 'Antwort...',
      'ui.run_outcome.feedback.save': 'Feedback speichern',

      // --- RUN OUTCOME VALIDATION ---

      'ui.run_outcome.error.select': 'Bitte wähle zuerst den Gesprächsausgang aus.',
      'ui.run_outcome.error.confirm': 'Bitte bestätige zuerst den Gesprächsausgang.',
      'ui.run_outcome.error.revenue': 'Bitte bestätige oder korrigiere den Umsatz.',
      'ui.run_outcome.error.answer': 'Bitte beantworte die letzte Frage.',

      // --- RUN OUTCOME QUESTIONS ---

      'ui.run_outcome.question.sold': 'Prima, passt der Umsatz oder habe ich da etwas missverstanden?',

      'ui.run_outcome.question.ended': 'Schade, dass du schon gehst. Um das User Ergebnis stetig zu optimieren, erzähl doch mal, was würdest du dir anders wünschen? Vielen Dank.',

      'ui.run_outcome.question.logout': 'Oh, du loggst dich aus? Um das User Ergebnis stetig zu optimieren, erzähl doch mal, was würdest du dir anders wünschen? Besten Dank.',

      'ui.run_outcome.question.follow_up': 'Du kriegst ihn noch. Wie kommt es, dass der Interessent noch nicht bereit war heute die Entscheidung zu treffen?',

      'ui.run_outcome.question.disqualified': 'Nicht jeder Interessent passt. Weshalb hast du ihn disqualifiziert?',

      'ui.run_outcome.question.failed': 'Was denkst du, woran lag es?',

      'ui.run_outcome.question.sold_revenue': 'Prima, ich habe als Umsatz {revenue} verstanden. Passt der Umsatz oder habe ich da etwas missverstanden?',


   // --- TenantUpdate

// ============================================================
// TENANT UPDATES – DE
// ============================================================





    // --- Mobile Notebook

'ui.mobile.notebook.required_placeholder': 'Pflichtfeld fehlt...',



'ui.tenant_updates.field.name': 'Unternehmensname',
'ui.tenant_updates.field.plz': 'Postleitzahl',
'ui.tenant_updates.field.street': 'Straße',
'ui.tenant_updates.field.street_no': 'Hausnummer',
'ui.tenant_updates.field.city': 'Stadt',
'ui.tenant_updates.field.vat_id': 'Steuernummer',
'ui.tenant_updates.field.phone': 'Telefonnummer',
'ui.tenant_updates.field.billing_email': 'Rechnungs-E-Mail',
'ui.tenant_updates.field.readonly': 'Nicht änderbar',

'ui.tenant_updates.section.company': '01 Unternehmen',
'ui.tenant_updates.section.address': '02 Anschrift',
'ui.tenant_updates.section.contact': '03 Kontakt & Abrechnung',

'ui.tenant_updates.error.session_expired': 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
'ui.tenant_updates.error.admin_only': 'Sie dürfen diese Unternehmensdaten nicht ändern. Diese Funktion steht nur Administratoren zur Verfügung.',
'ui.tenant_updates.error.tenant_not_found': 'Die Unternehmensdaten konnten Ihrem Konto nicht zugeordnet werden. Bitte laden Sie die Seite neu oder wenden Sie sich an den Support.',
'ui.tenant_updates.error.no_changes': 'Es wurden keine Änderungen erkannt.',
'ui.tenant_updates.error.invalid_input': 'Mindestens eine Angabe ist ungültig. Bitte prüfen Sie die markierten Eingaben.',
'ui.tenant_updates.error.conflict': 'Die Daten wurden zwischenzeitlich geändert. Bitte laden Sie den aktuellen Stand neu und wiederholen Sie Ihre Änderung.',
'ui.tenant_updates.error.rate_limited': 'Es wurden zu viele Anfragen gesendet. Bitte versuchen Sie es gleich noch einmal.',
'ui.tenant_updates.error.network': 'Der Server ist momentan nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
'ui.tenant_updates.error.invalid_response': 'Die Serverantwort konnte nicht verarbeitet werden. Bitte laden Sie die Seite neu.',
'ui.tenant_updates.error.server_load': 'Die Unternehmensdaten konnten wegen eines Serverfehlers nicht geladen werden. Bitte versuchen Sie es erneut.',
'ui.tenant_updates.error.server_save': 'Die Änderungen konnten wegen eines Serverfehlers nicht gespeichert werden. Ihre bisherigen Daten bleiben unverändert.',
'ui.tenant_updates.error.default_load': 'Die Unternehmensdaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
'ui.tenant_updates.error.default_save': 'Die Änderungen konnten nicht gespeichert werden. Ihre bisherigen Daten bleiben unverändert.',
'ui.tenant_updates.error.no_data': 'Für dieses Konto stehen derzeit keine Unternehmensdaten zur Verfügung. Bitte laden Sie die Seite neu oder wenden Sie sich an den Support.',

'ui.tenant_updates.feedback.incomplete_title': 'Angaben unvollständig',
'ui.tenant_updates.feedback.incomplete_prefix': 'Bitte füllen Sie folgende Felder aus: ',
'ui.tenant_updates.feedback.incomplete_suffix': '.',
'ui.tenant_updates.feedback.saved_title': 'Änderungen gespeichert',
'ui.tenant_updates.feedback.saved_message': 'Ihre Unternehmensdaten wurden aktualisiert.',
'ui.tenant_updates.feedback.saved_reload': 'Die Daten wurden gespeichert. Laden Sie die Seite neu, damit der aktuelle Stand überall angezeigt wird.',
'ui.tenant_updates.feedback.save_failed_title': 'Speichern nicht möglich',

'ui.tenant_updates.state.loading_title': 'Unternehmensdaten werden geladen',
'ui.tenant_updates.state.wait': 'Bitte einen Moment.',
'ui.tenant_updates.state.unavailable_title': 'Unternehmensdaten nicht verfügbar',
'ui.tenant_updates.state.reload_failed': 'Aktueller Stand konnte nicht neu geladen werden',

'ui.tenant_updates.action.retry_upper': 'ERNEUT LADEN',
'ui.tenant_updates.action.retry': 'Erneut laden',
'ui.tenant_updates.action.discard': 'VERWERFEN',
'ui.tenant_updates.action.save_changes': 'ÄNDERUNGEN SPEICHERN',
'ui.tenant_updates.action.cancel': 'ABBRECHEN',
'ui.tenant_updates.action.saving': 'WIRD GESPEICHERT …',
'ui.tenant_updates.action.save_now': 'JETZT SPEICHERN',

'ui.tenant_updates.change.unsaved': 'Ungespeicherte Änderungen',
'ui.tenant_updates.change.single': '1 Angabe wurde geändert.',
'ui.tenant_updates.change.multiple': '{count} Angaben wurden geändert.',

'ui.tenant_updates.confirm.title': 'Änderungen speichern?',
'ui.tenant_updates.confirm.fields': 'Folgende Angaben werden aktualisiert:',

'ui.tenant_updates.mobile.edit_aria': 'Unternehmensdaten bearbeiten',
'ui.tenant_updates.mobile.title': 'Unternehmensdaten',
'ui.tenant_updates.mobile.close_aria': 'Unternehmensdaten schließen',

   // --- SaveGame


// --- SAVE & LOAD SYSTEM ---


'ui.save_browser.mobile.filter': 'FILTER',
'ui.save_browser.mobile.previous': 'Vorheriger Speicherstand',
'ui.save_browser.mobile.next': 'Nächster Speicherstand',
'ui.save_browser.mobile.show': 'Speicherstand {index} anzeigen',

'ui.save_browser.access.asset': 'Asset',
'ui.save_browser.access.creator': 'Creator',
'ui.save_browser.access.missing': 'Asset Zugriff fehlt',
'ui.save_browser.access.purchase_hint': 'Bitte Trial oder Kauf der Technik vornehmen, um wieder Zugriff zu erhalten.',

'ui.save_browser.action.blocked': 'GESPERRT',
'ui.save_browser.action.cancel': 'ABBRUCH',
'ui.save_browser.action.confirm_delete': 'JA LÖSCHEN',
'ui.save_browser.action.reactivate': 'REAKTIVIEREN',

'ui.save_browser.card.industry': 'Branche',
'ui.save_browser.card.module_title': 'Modultitel',
'ui.save_browser.card.person_context': 'Ansprechpartner / Persona',
'ui.save_browser.card.points': 'Punkte',

'ui.save_browser.count': 'Speicherstände',

'ui.save_browser.date.all': 'ALLE',
'ui.save_browser.date.end': 'ENDE',
'ui.save_browser.date.last_24h': '24H',
'ui.save_browser.date.last_30d': '30T',
'ui.save_browser.date.last_7d': '7T',
'ui.save_browser.date.start': 'START',

'ui.save_browser.empty.no_matches': 'Keine passenden Speicherstände gefunden.',

'ui.save_browser.error.deactivate': 'Speicherstand konnte nicht deaktiviert werden.',
'ui.save_browser.error.reactivate': 'Speicherstand konnte nicht reaktiviert werden.',

'ui.save_browser.fallback.no_industry': 'Ohne Branche',
'ui.save_browser.fallback.no_module_title': 'Modultitel nicht gespeichert',
'ui.save_browser.fallback.no_offer': 'Ohne Angebot',
'ui.save_browser.fallback.no_person_context': 'Person / Kontext nicht gespeichert',
'ui.save_browser.fallback.unknown_asset': 'Unbekanntes Asset',

'ui.save_browser.filter.contact_touchpoint': 'CONTACT TOUCHPOINT',
'ui.save_browser.filter.difficulty': 'SCHWIERIGKEIT',
'ui.save_browser.filter.execution_mode': 'MODUS',
'ui.save_browser.filter.industry': 'BRANCHE',
'ui.save_browser.filter.module_title': 'MODULTITEL',
'ui.save_browser.filter.offer': 'ANGEBOT',
'ui.save_browser.filter.person_context': 'ANSPRECHPARTNER / PERSONA',
'ui.save_browser.filter.sales_stage': 'SALES STAGE',

'ui.save_browser.search.label': 'SUCHE',
'ui.save_browser.search.placeholder': 'Firma, Ansprechpartner, Persona, Angebot, Branche oder Modul suchen...',

'ui.save_browser.tab.context': 'KONTEXT',
'ui.save_browser.tab.period': 'ZEITRAUM',
'ui.save_browser.tab.training': 'TRAINING',

'ui.save_browser.title': 'SPEICHERSTÄNDE',

'ui.save_browser.toggle.active': 'AKTIV',
'ui.save_browser.toggle.inactive': 'INAKTIV',
'ui.save_browser.toggle.showing_active': 'Aktive Speicherstände werden angezeigt',
'ui.save_browser.toggle.showing_inactive': 'Inaktive Speicherstände werden angezeigt',



    // --- AdminVerwaltung


'ui.admin_verwaltung.action.delete': 'LÖSCHEN',
'ui.admin_verwaltung.details.assigned_since': 'Zuordnung seit',
'ui.admin_verwaltung.details.billing_period': 'Abrechnungszeitraum',
'ui.admin_verwaltung.details.current_period': 'Aktueller Zeitraum',
'ui.admin_verwaltung.details.email': 'E-Mail',
'ui.admin_verwaltung.details.hide': 'Details ausblenden',
'ui.admin_verwaltung.details.license_start': 'Lizenz Start',
'ui.admin_verwaltung.details.phone': 'Telefon',
'ui.admin_verwaltung.details.price': 'Preis',
'ui.admin_verwaltung.details.show': 'Details anzeigen',
'ui.admin_verwaltung.draft.discard': 'VERWERFEN',
'ui.admin_verwaltung.draft.save': 'STAND SPEICHERN',
'ui.admin_verwaltung.draft.saving': 'SPEICHERT...',
'ui.admin_verwaltung.draft.unsaved': 'Ungespeicherte Anpassungen',
'ui.admin_verwaltung.filter.all_members': 'Alle Member',
'ui.admin_verwaltung.invite.invite': 'Member einladen',
'ui.admin_verwaltung.invite.invite_upper': 'MEMBER EINLADEN',
'ui.admin_verwaltung.invite.share': 'Link teilen',
'ui.admin_verwaltung.invite.share_title': 'Sellermind Tenant-Link',
'ui.admin_verwaltung.invite.share_upper': 'LINK TEILEN',
'ui.admin_verwaltung.label.actions': 'Aktionen',
'ui.admin_verwaltung.label.license': 'Lizenz',
'ui.admin_verwaltung.label.member': 'Member',
'ui.admin_verwaltung.label.seats': 'Sitze',
'ui.admin_verwaltung.label.status': 'Status',
'ui.admin_verwaltung.license.no_free': 'keine freien Lizenzen',
'ui.admin_verwaltung.license.none': 'Keine Lizenz',
'ui.admin_verwaltung.license.open': 'Offen:',
'ui.admin_verwaltung.license.swap_with': 'Lizenz tauschen mit...',
'ui.admin_verwaltung.member.activate': 'Member aktivieren',
'ui.admin_verwaltung.member.deactivate': 'Member deaktivieren',
'ui.admin_verwaltung.member.profile_image': 'Profilbild',
'ui.admin_verwaltung.member.search': 'Member suchen...',
'ui.admin_verwaltung.member.unknown': 'Unbekannt',
'ui.admin_verwaltung.modal.close': 'User Management Modal schließen',
'ui.admin_verwaltung.state.loading': 'Admin Verwaltung wird geladen...',
'ui.admin_verwaltung.state.no_data': 'Keine Admin-Daten geladen.',
'ui.admin_verwaltung.state.no_members': 'Keine Member fuer diesen Filter vorhanden.',
'ui.admin_verwaltung.status.active': 'Aktiv',
'ui.admin_verwaltung.status.inactive': 'Deaktiviert',



    // --- AdminLizenzen

// ============================================================
// ADMIN LIZENZEN – DE
// ============================================================

'ui.admin_licenses.action.cancel_team': 'Team-Lizenz kündigen',
'ui.admin_licenses.action.current_license': 'Aktuelle Lizenz',
'ui.admin_licenses.action.downgrade': 'Jetzt downgraden',
'ui.admin_licenses.action.downgrade_to': 'Jetzt auf {title} downgraden',
'ui.admin_licenses.action.undo_change': 'Änderung rückgängig machen',
'ui.admin_licenses.action.upgrade': 'Jetzt upgraden',
'ui.admin_licenses.action.upgrade_to': 'Jetzt auf {title} upgraden',

'ui.admin_licenses.aria.active_license': 'Aktive Lizenz',
'ui.admin_licenses.aria.benefits': 'Benefits',
'ui.admin_licenses.aria.billing_period': 'Abrechnungszeitraum',
'ui.admin_licenses.aria.close_license_modal': 'Lizenz Modal schließen',
'ui.admin_licenses.aria.comparison': 'Lizenzvergleich',
'ui.admin_licenses.aria.comparison_table': 'Lizenzvergleich Tabelle',
'ui.admin_licenses.aria.enabler_benefits': 'Enabler Benefits',
'ui.admin_licenses.aria.included': 'vorhanden',
'ui.admin_licenses.aria.license_management': 'Lizenz Management',
'ui.admin_licenses.aria.next_card': 'Nächste Karte',
'ui.admin_licenses.aria.next_license': 'Nächste Lizenz anzeigen',
'ui.admin_licenses.aria.next_solo_variant': 'Nächste Solo-Addon-Variante',
'ui.admin_licenses.aria.not_included': 'nicht vorhanden',
'ui.admin_licenses.aria.previous_card': 'Vorherige Karte',
'ui.admin_licenses.aria.previous_license': 'Vorherige Lizenz anzeigen',
'ui.admin_licenses.aria.previous_solo_variant': 'Vorherige Solo-Addon-Variante',
'ui.admin_licenses.aria.pricing_tabs': 'Lizenzbereiche',
'ui.admin_licenses.aria.solo_level': 'Solo-Level Auswahl',
'ui.admin_licenses.aria.solo_variant': 'Solo-Addon-Variante',
'ui.admin_licenses.aria.switch_license': 'Lizenz wechseln',
'ui.admin_licenses.aria.usage_bar': '{label}: Verbrauch {reached}; Vertragsgrenze {limit}; Skala {scale}',
'ui.admin_licenses.aria.usage_limits': 'Verbrauch und Vertragsgrenzen',

'ui.admin_licenses.confirm.continue': 'Möchtest du fortfahren?',
'ui.admin_licenses.confirm.replace_upgrade': 'und anschließend durch das ausgewählte Upgrade ersetzt. ',
'ui.admin_licenses.confirm.revert_first': 'Die bestehende Änderung wird zuerst zurückgenommen ',
'ui.admin_licenses.confirm.scheduled_prefix': 'Für diese Lizenz ist bereits eine Änderung geplant. ',

'ui.admin_licenses.date.period_end_unknown': 'Ende der aktuellen Buchungsperiode',

'ui.admin_licenses.error.contract_load_failed': 'Lizenzvertrag konnte nicht geladen werden.',
'ui.admin_licenses.error.execution_failed': 'Lizenzänderung konnte nicht ausgeführt werden.',
'ui.admin_licenses.error.member_email_not_found': 'LICENSE_INFO.member_email wurde in get_members_from_tenant nicht gefunden.',
'ui.admin_licenses.error.members_load_failed': 'Memberliste konnte nicht geladen werden.',
'ui.admin_licenses.error.missing_member_email': 'LICENSE_INFO.member_email fehlt.',
'ui.admin_licenses.error.revert_failed': 'Geplante Änderung konnte nicht zurückgenommen werden.',

'ui.admin_licenses.internal_error.cancel_requested_downgrade_failed': 'Die geplante Abstufung konnte nicht zurückgenommen werden.',
'ui.admin_licenses.internal_error.current_member_billing_period_missing': 'Der Abrechnungszeitraum der aktuellen Member-Lizenz fehlt.',
'ui.admin_licenses.internal_error.current_member_license_missing': 'Die aktuelle Member-Lizenz fehlt.',
'ui.admin_licenses.internal_error.current_member_period_end_at_missing_for_member_downgrade': 'Das Ende der aktuellen Member-Abrechnungsperiode fehlt für die Abstufung.',
'ui.admin_licenses.internal_error.current_member_period_end_at_missing_for_solo_team_period_end_change': 'Das Ende der aktuellen Member-Abrechnungsperiode fehlt für die Solo-/Team-Änderung.',
'ui.admin_licenses.internal_error.current_member_period_end_at_missing_for_team_downgrade': 'Das Ende der aktuellen Member-Abrechnungsperiode fehlt für die Team-Abstufung.',
'ui.admin_licenses.internal_error.execute_license_action_plan_failed': 'Der Lizenzänderungsplan konnte nicht ausgeführt werden.',
'ui.admin_licenses.internal_error.get_license_management_contract_failed': 'Lizenzvertrag konnte nicht geladen werden.',
'ui.admin_licenses.internal_error.get_members_from_tenant_failed': 'Memberliste konnte nicht geladen werden.',
'ui.admin_licenses.internal_error.license_management_contract_missing_required_keys': 'Der Lizenzvertrag enthält nicht alle erforderlichen Angaben.',
'ui.admin_licenses.internal_error.member_license_action_not_determinable': 'Die Member-Lizenzänderung konnte nicht eindeutig bestimmt werden.',
'ui.admin_licenses.internal_error.paid_team_license_requires_paid_member_license': 'Eine kostenpflichtige Team-Lizenz benötigt eine kostenpflichtige Member-Lizenz.',
'ui.admin_licenses.internal_error.purchase_team_license_failed': 'Die Team-Lizenz konnte nicht geändert werden.',
'ui.admin_licenses.internal_error.request_license_downgrade_failed': 'Die Lizenz-Abstufung konnte nicht angefragt werden.',
'ui.admin_licenses.internal_error.request_license_upgrade_failed': 'Das Lizenz-Upgrade konnte nicht angefragt werden.',
'ui.admin_licenses.internal_error.target_email_missing': 'Die Ziel-E-Mail-Adresse fehlt.',
'ui.admin_licenses.internal_error.target_member_license_not_found': 'Die ausgewählte Member-Lizenz wurde nicht gefunden.',
'ui.admin_licenses.internal_error.team_license_action_not_determinable': 'Die Team-Lizenzänderung konnte nicht eindeutig bestimmt werden.',

'ui.admin_licenses.label.active': 'Aktiv',
'ui.admin_licenses.label.current': 'Aktuelle:',
'ui.admin_licenses.label.enabler': 'Enabler:',
'ui.admin_licenses.label.feature': 'Feature',
'ui.admin_licenses.label.per_month': '/Monat',
'ui.admin_licenses.label.period_monthly': 'Monatlich',
'ui.admin_licenses.label.period_once': 'Einmalig',
'ui.admin_licenses.label.support': 'Support',

'ui.admin_licenses.notice.already_scheduled': 'Für diese Lizenz ist bereits eine Änderung geplant.',
'ui.admin_licenses.notice.binding_click': 'Mit Klick auf den Button erfolgt die Änderung verbindlich.',
'ui.admin_licenses.notice.downgrade_same_period': 'Kündigung der aktuellen Lizenz erfolgt sofort und ist wirksam zum Periodenende den {date}. Die Lizenz wird dann automatisch auf {license} runtergestuft. Mit Klick auf den Button erfolgt die Änderung verbindlich und kann nicht rückgängig gemacht werden.',
'ui.admin_licenses.notice.ends_at_period': 'Kündigung erfolgt zum Periodenende den {date}.',
'ui.admin_licenses.notice.no_change_current': 'Aktuelle Lizenz. Es wird keine Änderung ausgelöst.',
'ui.admin_licenses.notice.planned_effective': '{base} Die Änderung wird wirksam am {date}.{target} Bis dahin sind weitere Änderungen gesperrt.',
'ui.admin_licenses.notice.renewal_appendix': 'Die Vereinbarung verlängert sich anschließend jeweils nach dem Periodenende, ohne dass eine Kündigung erforderlich ist. Eine Kündigung ist monatlich möglich. Rückerstattungen sind ausgeschlossen.',
'ui.admin_licenses.notice.selected_license': 'die ausgewählte Lizenz',
'ui.admin_licenses.notice.target': ' Ziel: {license}{billing}.',
'ui.admin_licenses.notice.team_cancellation': 'Wenn du die Team-Lizenz kündigst, wird sie zum Ende der aktuellen Buchungsperiode am {date} auf Solo abgestuft. Mit der Abstufung verfallen die Team-Caps. Die Abstufung kann nur erfolgen, wenn bis dahin alle zusätzlichen Team-Member deaktiviert wurden und ausschließlich noch der Admin-Account aktiv ist.',
'ui.admin_licenses.notice.upgrade_immediate': 'Kündigung der aktuellen Lizenz erfolgt sofort und ist sofort wirksam. Der anteilige Betrag wird mit dem zukünftigen Buchungsvertrag in der ersten Buchungsperiode verrechnet. Die Lizenz wird dann automatisch auf {license} hochgestuft. Mit Klick auf den Button erfolgt die Änderung verbindlich und kann nicht rückgängig gemacht werden.',
'ui.admin_licenses.notice.yearly_to_monthly_downgrade': 'Kündigung erfolgt sofort und ist wirksam zum Periodenende den {date}. Die Lizenz wird dann automatisch auf {license} runtergestuft. Limits werden zurückgesetzt. Mit Klick auf den Button erfolgt die Änderung verbindlich und kann nicht rückgängig gemacht werden.',
'ui.admin_licenses.notice.yearly_to_monthly_upgrade': 'Kündigung erfolgt sofort und wird wirksam zum Periodenende den {date}. Die Lizenz wird dann automatisch auf {license} hochgestuft. Mit Klick auf den Button erfolgt die Änderung verbindlich und kann nicht rückgängig gemacht werden.',

'ui.admin_licenses.placeholder.search_member': 'Person suchen',

'ui.admin_licenses.status.active': 'aktiv',
'ui.admin_licenses.status.change_updated': 'Lizenzänderung wurde aktualisiert.',
'ui.admin_licenses.status.expired': 'abgelaufen',
'ui.admin_licenses.status.inactive': 'inaktiv',
'ui.admin_licenses.status.loading_contract': 'Lizenzvertrag wird geladen...',
'ui.admin_licenses.status.loading_members': 'Memberliste wird geladen...',
'ui.admin_licenses.status.no_cards': 'Keine Karten für diesen Bereich vorhanden.',
'ui.admin_licenses.status.no_change_required': 'Keine Änderung erforderlich.',
'ui.admin_licenses.status.no_contract': 'Kein Lizenzvertrag geladen.',
'ui.admin_licenses.status.planned_reversed': 'Geplante Änderung wurde zurückgenommen.',
'ui.admin_licenses.status.processing': 'Wird verarbeitet...',
'ui.admin_licenses.status.reverting_existing': 'Bestehende Lizenzänderung wird zurückgenommen...',
'ui.admin_licenses.status.reverting_planned': 'Geplante Änderung wird zurückgenommen...',
'ui.admin_licenses.status.updating_contract': 'Lizenzänderung wird verarbeitet. Vertragsdaten werden aktualisiert...',

'ui.admin_licenses.summary.enabler': 'Enabler: {label}',
'ui.admin_licenses.summary.enabler_empty': 'Enabler:',
'ui.admin_licenses.summary.main_empty': 'Hauptlizenz: -',
'ui.admin_licenses.summary.main_solo': 'Hauptlizenz Solo: {license}',
'ui.admin_licenses.summary.main_team': 'Hauptlizenz Team: {license}',
'ui.admin_licenses.summary.member': 'Person: {license} {status}',
'ui.admin_licenses.summary.member_empty': 'Person: -',
'ui.admin_verwaltung.filter.without_license': 'Ohne Lizenz',
'ui.admin_verwaltung.filter.assigned_license': 'Lizenz zugewiesen',



    // --- DynamicsCard

      "ui.analytics.dynamics.title": "DYNAMIK",
      "ui.analytics.dynamics.legend.you": "DU (CLOSER)",
      "ui.analytics.dynamics.legend.client": "KUNDE",
      "ui.analytics.dynamics.kpi.goals": "ZIELE",
      "ui.analytics.dynamics.kpi.manual": "MANUELL",
      "ui.analytics.dynamics.kpi.avg_turns": "Ø TURNS",
      "ui.analytics.dynamics.kpi.total_turns": "TURNS",

  

    // --- Overlay


"ui.overlay.mobile.sections.identity": "01 IDENTITÄT",
"ui.overlay.mobile.sections.call_review": "CALL REVIEW",
"ui.overlay.mobile.sections.offers": "ANGEBOTE",
"ui.overlay.mobile.sections.personas": "PERSONAS",
"ui.overlay.mobile.sections.profile": "PROFIL",
"ui.overlay.mobile.setup.language_label": "SPRACHE",
"ui.overlay.mobile.setup.persona_label": "PERSONA",
"ui.overlay.mobile.shop.card_shop": "CARD SHOP",
"ui.overlay.mobile.admin.heading": "ADMIN",
"ui.overlay.mobile.navigation.nav": "NAV",
"ui.overlay.mobile.audio.audio": "AUDIO",
"ui.overlay.mobile.footer.checking_limit": "LIMIT WIRD GEPRÜFT...",

      'ui.overlay.desktop.admin.keys_saved': 'Keys gespeichert.',
      'ui.overlay.desktop.offer_editor.error_invalid_offer_json': 'Gespeicherter Offer-Snapshot enthält ungültiges offer_json.',
      'ui.overlay.desktop.offer_editor.error_missing_canonical_snapshot': 'Angebot wurde gespeichert, aber ohne kanonischen Offer-Snapshot zurückgegeben.',
      'ui.overlay.desktop.offer_editor.error_missing_offer_id': 'Angebot wurde gespeichert, aber ohne offer_id zurückgegeben.',
      'ui.overlay.desktop.offer_editor.error_missing_offer_roi_cases': 'ROI-Cases des Angebots sind nicht verfügbar.',
      'ui.overlay.desktop.offer_editor.error_missing_roi_cases': 'Gespeicherte ROI-Cases sind nicht verfügbar.',
      'ui.overlay.desktop.offer_editor.error_missing_saved_snapshot': 'Gespeicherter Offer-Snapshot ist nicht verfügbar.',
      'ui.overlay.desktop.offer_editor.error_no_selected_offer': 'Kein Angebot zum Bearbeiten ausgewählt.',
      'ui.overlay.desktop.offer_editor.error_offer_unavailable': 'Vollständiges Angebot ist nicht verfügbar.',
      'ui.overlay.desktop.offer_editor.error_required_fields': 'Bitte mindestens Angebotsname, Branche, Angebotskategorie und KB ausfüllen.',
      'ui.overlay.desktop.offer_editor.error_save_failed': 'Angebot konnte nicht gespeichert werden.',
      'ui.overlay.desktop.persona_editor.error_choose_backup_voice': 'Bitte eine Backup-Stimme auswählen.',
      'ui.overlay.desktop.persona_editor.error_choose_voice': 'Bitte eine passende Stimme auswählen.',
      'ui.overlay.desktop.persona_editor.error_no_selected_persona': 'Keine Persona zum Bearbeiten ausgewählt.',
      'ui.overlay.desktop.persona_editor.error_save_failed': 'Persona konnte nicht gespeichert werden.',
      'ui.overlay.desktop.persona_editor.error_session_expired': 'Session abgelaufen. Bitte Seite neu laden und erneut versuchen.',
      'ui.overlay.desktop.persona_editor.error_update_failed': 'Persona konnte nicht aktualisiert werden.',
      'ui.overlay.desktop.persona_editor.required_description': 'Beschreibung / Kontext',
      'ui.overlay.desktop.persona_editor.required_fields_prefix': 'Bitte fülle folgende Pflichtfelder aus:',
      'ui.overlay.desktop.persona_editor.required_identifier': 'Gesprächs-Identifier',
      'ui.overlay.desktop.persona_editor.required_name': 'Persona-Name',
      'ui.overlay.desktop.persona_editor.required_psychology': 'Persona-Psychologie',
      'ui.overlay.desktop.persona_editor.required_role': 'Persona-Rolle',
      'ui.overlay.desktop.profile.error_crop_blob_failed': 'profile_crop_blob_failed',
      'ui.overlay.desktop.profile.error_crop_canvas_failed': 'profile_crop_canvas_failed',
      'ui.overlay.desktop.profile.error_crop_failed': 'Bild konnte nicht zugeschnitten werden.',
      'ui.overlay.desktop.profile.error_crop_image_load_failed': 'profile_crop_image_load_failed',
      'ui.overlay.desktop.profile.error_delete_failed': 'profile_image_delete_failed',
      'ui.overlay.desktop.profile.error_delete_image_failed': 'Bild konnte nicht gelöscht werden.',
      'ui.overlay.desktop.profile.error_invalid_file_type': 'Bitte JPG, PNG oder WebP verwenden.',
      'ui.overlay.desktop.profile.error_max_file_size': 'Das Bild darf maximal 5 MB gross sein.',
      'ui.overlay.desktop.profile.error_missing_crop_source': 'profile_crop_missing_source',
      'ui.overlay.desktop.profile.error_referral_failed': 'Empfehlungslink konnte nicht geladen werden.',
      'ui.overlay.desktop.profile.error_save_image_failed': 'Bild konnte nicht gespeichert werden.',
      'ui.overlay.desktop.profile.error_upload_failed': 'profile_image_upload_failed',
      'ui.overlay.desktop.training.error_monthly_limit_reached': 'Das monatliche Live-Gesprächslimit ist erreicht.',
      'ui.overlay.desktop.admin.admin': '02 ADMIN',
      'ui.overlay.desktop.admin.gemini_api_key': 'Gemini API Key',
      'ui.overlay.desktop.admin.lizenz_management': 'LIZENZ MANAGEMENT',
      'ui.overlay.desktop.admin.placeholder_deepgram_key': 'Deepgram Key',
      'ui.overlay.desktop.admin.placeholder_gemini_key_optional': 'Gemini Key (Optional)',
      'ui.overlay.desktop.admin.placeholder_groq_key_optional': 'Groq Key (Optional)',
      'ui.overlay.desktop.admin.placeholder_openai_key_sk': 'OpenAI Key (sk-...)',
      'ui.overlay.desktop.admin.speichern_schliessen': 'Speichern & Schliessen',
      'ui.overlay.desktop.admin.system_api_keys': 'SYSTEM API KEYS',
      'ui.overlay.desktop.admin.tenant_einstellungen': 'TENANT EINSTELLUNGEN',
      'ui.overlay.desktop.admin.user_management': 'USER MANAGEMENT',
      'ui.overlay.desktop.audio.audio_stream': 'AUDIO STREAM',
      'ui.overlay.desktop.audio.audio_tuning': 'AUDIO TUNING',
      'ui.overlay.desktop.audio.geschwindigkeit': 'GESCHWINDIGKEIT',
      'ui.overlay.desktop.audio.langsam': 'Langsam',
      'ui.overlay.desktop.audio.schnell': 'Schnell',
      'ui.overlay.desktop.audio.system': 'SYSTEM',
      'ui.overlay.desktop.audio.tooltip_stimmeinstellung': 'Stimmeinstellung',
      'ui.overlay.desktop.profile.profile_image_alt': 'Profilbild',
      'ui.overlay.desktop.offers.showing_inactive': 'Inaktive Angebote werden angezeigt',
      'ui.overlay.desktop.personas.showing_inactive': 'Inaktive Personas werden angezeigt',
      'ui.overlay.desktop.offer_editor.edit_offer': 'ANGEBOT BEARBEITEN',
      'ui.overlay.desktop.offer_editor.save_changes': 'ÄNDERUNGEN SPEICHERN',
      'ui.overlay.desktop.rescue.analyzing': 'Analysiere...',
      'ui.overlay.desktop.audio.loading': 'LÄDT...',
      'ui.overlay.desktop.profile.language_update_failed': 'Die Sprache konnte nicht gespeichert werden.',
      'ui.overlay.desktop.profile.selling_languages_update_failed': 'Die Gesprächssprachen konnten nicht gespeichert werden.',
      'ui.overlay.desktop.audio.enter_api_key': 'Bitte erst API Key eingeben.',
      'ui.overlay.desktop.audio.generation_failed': 'Audio konnte nicht generiert werden.',
      'ui.overlay.desktop.audio.playback_error': 'Systemfehler beim Abspielen: ',
      'ui.overlay.desktop.rescue.text_too_short': 'Text zu kurz!',
      'ui.overlay.desktop.rescue.select_script_first': 'Bitte zuerst ein Script auswählen.',
      'ui.overlay.desktop.offers.showing_active': 'Aktive Angebote werden angezeigt',
      'ui.overlay.desktop.personas.showing_active': 'Aktive Personas werden angezeigt',
      'ui.overlay.desktop.offer_editor.create_offer': 'ANGEBOT ERSTELLEN',
      'ui.overlay.desktop.offer_editor.save_offer': 'ANGEBOT SPEICHERN',
      'ui.overlay.desktop.rescue.start_import': 'Import Starten',
      'ui.overlay.desktop.audio.play_test_audio': 'TEST-AUDIO ABSPIELEN',
      'ui.overlay.desktop.gap_training.ausfuehrung': 'Ausführung',
      'ui.overlay.desktop.gap_training.auswahl': 'Auswahl',
      'ui.overlay.desktop.gap_training.gespraech_importieren': 'GESPRÄCH IMPORTIEREN',
      'ui.overlay.desktop.gap_training.luecken_bearbeiten': '3 Lücken Bearbeiten',
      'ui.overlay.desktop.gap_training.luecken_erkennung': '📂 Lücken Erkennung',
      'ui.overlay.desktop.gap_training.placeholder_funktion_auswaehlen': 'Funktion auswählen',
      'ui.overlay.desktop.gap_training.script': 'Skript',
      'ui.overlay.desktop.legacy_setup.angebot': 'ANGEBOT',
      'ui.overlay.desktop.legacy_setup.call_review': '03 CALL REVIEW',
      'ui.overlay.desktop.legacy_setup.company_optional': 'COMPANY OPTIONAL',
      'ui.overlay.desktop.legacy_setup.contact_touchpoint': 'CONTACT TOUCHPOINT',
      'ui.overlay.desktop.legacy_setup.einstiegspunkt': 'EINSTIEGSPUNKT',
      'ui.overlay.desktop.legacy_setup.gespraech_importieren': 'GESPRÄCH IMPORTIEREN',
      'ui.overlay.desktop.legacy_setup.luecken_erkennung': '📂 Lücken Erkennung',
      'ui.overlay.desktop.legacy_setup.no_scripts_found': 'Keine Skripte gefunden',
      'ui.overlay.desktop.legacy_setup.placeholder_waehle_ein_skript': 'Wähle ein Skript...',
      'ui.overlay.desktop.legacy_setup.placeholder_z_b_muster_gmbh': 'z.B. Muster GmbH',
      'ui.overlay.desktop.legacy_setup.sales_stage': 'SALES STAGE',
      'ui.overlay.desktop.legacy_setup.script': 'SCRIPT',
      'ui.overlay.desktop.legacy_setup.script_start': '02 SCRIPT START',
      'ui.overlay.desktop.legacy_setup.tooltip_stimme_testen': 'Stimme testen',
      'ui.overlay.desktop.navigation.aktiver_plan_caption': 'Aktiver Plan:',
      'ui.overlay.desktop.navigation.aria_echtzeit': 'Echtzeit',
      'ui.overlay.desktop.navigation.assets': 'ASSETS',
      'ui.overlay.desktop.navigation.assets_umsatz': 'Assets & Umsatz',
      'ui.overlay.desktop.navigation.battle_deck': 'BATTLE DECK',
      'ui.overlay.desktop.navigation.cockpit': 'COCKPIT',
      'ui.overlay.desktop.navigation.echtzeit': 'ECHTZEIT',
      'ui.overlay.desktop.navigation.erstellen_verwalten': 'Erstellen & Verwalten',
      'ui.overlay.desktop.navigation.greeting_description': 'Du bist nur wenige Sekunden davon entfernt neue Umsatzrekorde zu erzielen.',
      'ui.overlay.desktop.navigation.guten_tag': 'Guten Tag',
      'ui.overlay.desktop.navigation.laden_starten': 'Laden & Starten',
      'ui.overlay.desktop.navigation.life_training': 'Life & Training',
      'ui.overlay.desktop.navigation.marketplace': 'MARKETPLACE',
      'ui.overlay.desktop.navigation.neue_items': 'Neue Items',
      'ui.overlay.desktop.navigation.performance_gespraeche': 'Performance & Gespräche',
      'ui.overlay.desktop.navigation.profil_verwaltung': 'Profil & Verwaltung',
      'ui.overlay.desktop.navigation.sales': 'SALES',
      'ui.overlay.desktop.navigation.sales_items': 'Sales Items',
      'ui.overlay.desktop.navigation.saves': 'SAVES',
      'ui.overlay.desktop.navigation.shop': 'SHOP',
      'ui.overlay.desktop.navigation.user': 'USER',
      'ui.overlay.desktop.offer_editor.abbrechen': 'Abbrechen',
      'ui.overlay.desktop.offer_editor.asset_builder': 'ASSET BUILDER',
      'ui.overlay.desktop.offers.abbrechen': 'ABBRECHEN',
      'ui.overlay.desktop.offers.aktiv': 'AKTIV',
      'ui.overlay.desktop.offers.aktuelles_angebot': 'AKTUELLES ANGEBOT',
      'ui.overlay.desktop.offers.angebote': '01 ANGEBOTE',
      'ui.overlay.desktop.offers.bearbeiten': 'BEARBEITEN',
      'ui.overlay.desktop.offers.deaktivieren': 'DEAKTIVIEREN',
      'ui.overlay.desktop.offers.erstellen': 'ERSTELLEN',
      'ui.overlay.desktop.offers.inaktiv': 'INAKTIV',
      'ui.overlay.desktop.offers.ja_deaktivieren': 'JA DEAKTIVIEREN',
      'ui.overlay.desktop.offers.reaktivieren': 'REAKTIVIEREN',
      'ui.overlay.desktop.offers.search_name_branche_kategorie_szenario_oder_kundenbeziehung': 'Name, Branche, Kategorie, Szenario oder Kundenbeziehung suchen...',
      'ui.overlay.desktop.persona_editor.backup_stimme': 'BACKUP-STIMME',
      'ui.overlay.desktop.persona_editor.create_persona': 'PERSONA ERSTELLEN',
      'ui.overlay.desktop.persona_editor.edit_persona': 'PERSONA BEARBEITEN',
      'ui.overlay.desktop.persona_editor.modus': 'MODUS',
      'ui.overlay.desktop.persona_editor.persona_psychologie': 'Persona-Psychologie',
      'ui.overlay.desktop.persona_editor.persona_rolle': 'Persona-Rolle',
      'ui.overlay.desktop.persona_editor.placeholder_beschreibe_die_persona_oder_fuege_einen_chatverlauf': 'Beschreibe die Persona oder füge einen Chatverlauf ein...',
      'ui.overlay.desktop.persona_editor.placeholder_z_b_kunde_oder_max': 'z.B. \'Kunde\' oder \'Max\'',
      'ui.overlay.desktop.persona_editor.placeholder_z_b_skeptischer_geschaeftsfuehrer': 'z.B. \'Skeptischer Geschäftsführer\'',
      'ui.overlay.desktop.persona_editor.search_psychologie_suchen_z_b_skeptiker_dominanter_entschei': 'Psychologie suchen, z. B. Skeptiker, Dominanter Entscheider, Freigeist.',
      'ui.overlay.desktop.persona_editor.search_rolle_suchen_z_b_geschaeftsfuehrer_vater_mutter_stud': 'Rolle suchen, z. B. Geschäftsführer, Vater, Mutter, Student, Schüler.',
      'ui.overlay.desktop.persona_editor.stimme': 'STIMME',
      'ui.overlay.desktop.persona_editor.tooltip_stimme_testen': 'Stimme testen',
      'ui.overlay.desktop.personas.abbrechen': 'ABBRECHEN',
      'ui.overlay.desktop.personas.aktiv': 'AKTIV',
      'ui.overlay.desktop.personas.bearbeiten': 'BEARBEITEN',
      'ui.overlay.desktop.personas.deaktivieren': 'DEAKTIVIEREN',
      'ui.overlay.desktop.personas.erstellen': 'ERSTELLEN',
      'ui.overlay.desktop.personas.inaktiv': 'INAKTIV',
      'ui.overlay.desktop.personas.ja_deaktivieren': 'JA DEAKTIVIEREN',
      'ui.overlay.desktop.personas.object_admin': '03 OBJECT ADMIN',
      'ui.overlay.desktop.personas.personas': '02 PERSONAS',
      'ui.overlay.desktop.personas.reaktivieren': 'REAKTIVIEREN',
      'ui.overlay.desktop.personas.tooltip_stimme_testen': 'Stimme testen',
      'ui.overlay.desktop.profile.abbrechen': 'Abbrechen',
      'ui.overlay.desktop.profile.alt_profilbild': 'Profilbild',
      'ui.overlay.desktop.profile.alt_profilbild_ausrichten': 'Profilbild ausrichten',
      'ui.overlay.desktop.profile.aria_gespraeche_bearbeiten': 'Gespräche bearbeiten',
      'ui.overlay.desktop.profile.aria_sprache_bearbeiten': 'Sprache bearbeiten',
      'ui.overlay.desktop.profile.bild_loeschen': 'Bild Löschen',
      'ui.overlay.desktop.profile.change_image': 'Bild ändern',
      'ui.overlay.desktop.profile.default_role': 'Rolle',
      'ui.overlay.desktop.profile.default_user': 'User',
      'ui.overlay.desktop.profile.empfiehl_uns_weiter': 'Empfiehl uns weiter',
      'ui.overlay.desktop.profile.gespraeche': 'Gespräche',
      'ui.overlay.desktop.profile.gespraeche_caption': 'Gespräche:',
      'ui.overlay.desktop.profile.link_teilen': 'Link teilen',
      'ui.overlay.desktop.profile.loading_referral_link': 'Empfehlungslink wird geladen...',
      'ui.overlay.desktop.profile.no_referral_link': 'Kein Empfehlungslink verfügbar',
      'ui.overlay.desktop.profile.not_specified': 'Nicht festgelegt',
      'ui.overlay.desktop.profile.plan_caption': 'Plan:',
      'ui.overlay.desktop.profile.profil': '01 PROFIL',
      'ui.overlay.desktop.profile.referral_description': 'Erhalte gratis 1.000 Punkte gutgeschrieben, die du beliebig für neue Techniken einsetzen kannst. So hast du immer die passende Technik im Gespräch und gewinnst Deals, die du vorher verloren hast.',
      'ui.overlay.desktop.profile.rolle_caption': 'Rolle:',
      'ui.overlay.desktop.profile.saving': 'Speichert...',
      'ui.overlay.desktop.profile.select_language': 'Sprache auswählen',
      'ui.overlay.desktop.profile.select_languages': 'Sprachen auswählen',
      'ui.overlay.desktop.profile.speichern': 'Speichern',
      'ui.overlay.desktop.profile.sprache': 'Sprache',
      'ui.overlay.desktop.profile.sprache_caption': 'Sprache:',
      'ui.overlay.desktop.profile.tooltip_gespraeche_bearbeiten': 'Gespräche bearbeiten',
      'ui.overlay.desktop.profile.zoom': 'ZOOM',
      'ui.overlay.desktop.rescue.abbrechen': 'Abbrechen',
      'ui.overlay.desktop.rescue.analyzing_structure': 'Analysiere Struktur...',
      'ui.overlay.desktop.rescue.gespraechsprotokoll': 'Gesprächsprotokoll',
      'ui.overlay.desktop.rescue.paste_full_transcript': 'Vollständiges Transkript einfügen',
      'ui.overlay.desktop.rescue.placeholder_fuege_hier_das_transkript_ein': 'Füge hier das Transkript ein...',
      'ui.overlay.desktop.rescue.placeholder_z_b_lisa_oder_gast': 'z.B. \'Lisa\' oder \'Gast\'',
      'ui.overlay.desktop.rescue.placeholder_z_b_tom_oder_ich': 'z.B. \'Tom\' oder \'Ich\'',
      'ui.overlay.desktop.rescue.rescue_operation': 'RESCUE OPERATION',
      'ui.overlay.desktop.rescue.transkript_import': 'TRANSKRIPT IMPORT',
      'ui.overlay.desktop.rescue.wer_bist_du_im_text': 'Wer bist DU im Text?',
      'ui.overlay.desktop.rescue.wer_ist_der_kunde': 'Wer ist der KUNDE?',
      'ui.overlay.desktop.script_filter.all_industries': 'Alle Industrien',
      'ui.overlay.desktop.script_filter.all_offers': 'Alle Angebote',
      'ui.overlay.desktop.script_filter.all_phases': 'Alle Phasen',
      'ui.overlay.desktop.script_filter.angebot': 'Angebot',
      'ui.overlay.desktop.script_filter.aria_filter_zuruecksetzen': 'Filter zurücksetzen',
      'ui.overlay.desktop.script_filter.filter': 'Filter',
      'ui.overlay.desktop.script_filter.industrie': 'Industrie',
      'ui.overlay.desktop.script_filter.keine_passenden_skripte_gefunden': 'Keine passenden Skripte gefunden.',
      'ui.overlay.desktop.script_filter.phase': 'Phase',
      'ui.overlay.desktop.script_filter.skriptauswahl_bestaetigen': 'Skriptauswahl bestätigen',
      'ui.overlay.desktop.script_filter.skripte_seite': 'Skripte · Seite',
      'ui.overlay.desktop.script_filter.skriptname': 'Skriptname',
      'ui.overlay.desktop.script_filter.von': 'von',
      'ui.overlay.desktop.script_filter.weiter': 'Weiter',
      'ui.overlay.desktop.script_filter.zurueck': 'Zurück',
      'ui.overlay.desktop.setup.angebot': 'ANGEBOT',
      'ui.overlay.desktop.setup.customization': 'CUSTOMIZATION',
      'ui.overlay.desktop.setup.saved_offer_outside_catalog': 'Gespeichertes Angebot (nicht im Katalog)',
      'ui.overlay.desktop.setup.startphase': 'Startphase',
      'ui.overlay.desktop.setup.tooltip_stimme_testen': 'Stimme testen',
      'ui.overlay.desktop.technique_filter.all_categories': 'Alle Kategorien',
      'ui.overlay.desktop.technique_filter.all_offers': 'Alle Angebote',
      'ui.overlay.desktop.technique_filter.all_types': 'Alle Typen',
      'ui.overlay.desktop.technique_filter.angebot': 'Angebot',
      'ui.overlay.desktop.technique_filter.aria_filter_zuruecksetzen': 'Filter zurücksetzen',
      'ui.overlay.desktop.technique_filter.filter': 'Filter',
      'ui.overlay.desktop.technique_filter.kategorie': 'Kategorie',
      'ui.overlay.desktop.technique_filter.keine_passenden_techniken_gefunden': 'Keine passenden Techniken gefunden.',
      'ui.overlay.desktop.technique_filter.technikauswahl_bestaetigen': 'Technikauswahl bestätigen',
      'ui.overlay.desktop.technique_filter.techniken_seite': 'Techniken · Seite',
      'ui.overlay.desktop.technique_filter.technikname': 'Technikname',
      'ui.overlay.desktop.technique_filter.typ': 'Typ',
      'ui.overlay.desktop.technique_filter.von': 'von',
      'ui.overlay.desktop.technique_filter.weiter': 'Weiter',
      'ui.overlay.desktop.technique_filter.zurueck': 'Zurück',
      'ui.overlay.desktop.training.art_der_verinnerlichung': 'Art der Verinnerlichung',
      'ui.overlay.desktop.training.auswahl': 'Auswahl',
      'ui.overlay.desktop.training.life_coaching': '2 Life Coaching',
      'ui.overlay.desktop.training.placeholder_auswahl_treffen': 'Auswahl treffen',
      'ui.overlay.desktop.training.placeholder_rollenspiel_auswaehlen': 'Rollenspiel auswählen',
      'ui.overlay.desktop.training.placeholder_trainingsinhalt_auswaehlen': 'Trainingsinhalt auswählen',
      'ui.overlay.desktop.training.rollenspiel': 'Rollenspiel',
      'ui.overlay.desktop.training.skript': 'Skript',
      'ui.overlay.desktop.training.training': '1 Training',
      'ui.overlay.desktop.training.trainingsinhalt': 'Trainingsinhalt',
      'ui.setup.persona.error_api_key': 'Bitte zuerst einen API-Key hinterlegen.',
      'ui.setup.persona.error_generate': 'Persona konnte nicht generiert werden.',



    // --- ScriptBuilder

"ui.script_builder.control.auto_hint": "Vollautomatisch erstellen und direkt fortfahren.",
"ui.script_builder.control.automatic_upper": "AUTOMATISCH",
"ui.script_builder.control.manual_hint": "Während des Erstellprogramms aktive Anpassungen vornehmen.",
"ui.script_builder.control.manual_title": "Manuelle Kontrolle",
"ui.script_builder.control.manual_upper": "MANUELL",
"ui.script_builder.hitl.add_goal_end": "Ziel am Ende ergänzen",
"ui.script_builder.hitl.changed": "geändert",
"ui.script_builder.hitl.changed_upper": "GEÄNDERT",
"ui.script_builder.hitl.delete": "Löschen",
"ui.script_builder.hitl.edit": "Anpassen",
"ui.script_builder.hitl.goals": "Ziele",
"ui.script_builder.hitl.insert_goal_after": "Zwischenziel danach",
"ui.script_builder.hitl.minutes": "Minuten",
"ui.script_builder.hitl.new": "neu",
"ui.script_builder.hitl.new_upper": "NEU",
"ui.script_builder.hitl.phase": "Phase",
"ui.script_builder.hitl.phases": "Phasen",
"ui.script_builder.hitl.reordered": "Reihenfolge",
"ui.script_builder.hitl.review_description": "Phasen und Ziele prüfen, anpassen, ergänzen oder löschen.",
"ui.script_builder.hitl.review_duration_prefix": "Manuelle Prüfung - voraussichtliche Erstellungsdauer ca.",
"ui.script_builder.hitl.save": "Speichern",
"ui.script_builder.hitl.save_continue": "Aktualisieren und Fortfahren",
"ui.script_builder.intro.script_engine": "Das Skript wird dadurch automatisch in eine Engine transformiert, die automatisch Einwände und Vorwände erkennt und Gespräche plan-, mess- und steuerbar macht.",
"ui.script_builder.intro.script_instructions": "Bitte kopieren Sie Ihr Skript in dieses Feld. Sie können dann wahlweise wählen, ob Sie selbst den Erstellprozess kontrollieren möchten oder sich für die vollautomatische Erstellung entscheiden.",
"ui.script_builder.intro.script_text": "Skript Text",
"ui.script_builder.intro.technique_engine": "Die Technik wird dadurch automatisch in eine Engine transformiert, die automatisch Einwände und Vorwände erkennt und Gespräche plan-, mess- und steuerbar macht.",
"ui.script_builder.intro.technique_instructions": "Bitte kopieren Sie Ihre Technik in dieses Feld. Sie können dann wahlweise wählen, ob Sie selbst den Erstellprozess kontrollieren möchten oder sich für die vollautomatische Erstellung entscheiden.",
"ui.script_builder.intro.technique_text": "Technik Text",
"ui.script_builder.json.hide": "JSON Output ausblenden",
"ui.script_builder.json.script_title": "SKRIPT JSON OUTPUT",
"ui.script_builder.json.show": "JSON Output anzeigen",
"ui.script_builder.json.technique_title": "TECHNIK JSON OUTPUT",
"ui.script_builder.modal.aria_title": "Script Builder",
"ui.script_builder.modal.close": "Script Builder schliessen",
"ui.script_builder.person.add_closer": "Closer hinzufügen",
"ui.script_builder.person.add_other": "Andere hinzufügen",
"ui.script_builder.person.add_prospect": "Prospect hinzufügen",
"ui.script_builder.person.contains_names": "Enthält der Text Personennamen?",
"ui.script_builder.person.first_name_placeholder": "Vorname, falls im Text vorhanden",
"ui.script_builder.person.last_name_placeholder": "Nachname, falls im Text vorhanden",
"ui.script_builder.person.mapping_hint": "Nur eintragen, was wirklich im Text vorkommt. Pro Eintrag ist Vorname oder Nachname erforderlich.",
"ui.script_builder.person.mapping_title": "Personen-Mapping",
"ui.script_builder.person.min_one": "Bitte mindestens einen enthaltenen Personennamen hinzufügen.",
"ui.script_builder.person.other": "Andere",
"ui.script_builder.person.remove": "Entfernen",
"ui.script_builder.person.sanitizer_hint": "Diese Angabe steuert den Server-Sanitizer vor der Erstellung.",
"ui.script_builder.prestep.description": "Der Text wurde geprüft. Titel und Beschreibung werden vor dem eigentlichen Erstellprozess festgelegt.",
"ui.script_builder.prestep.description_hint": "Kann durch KI neu erzeugt und vom Menschen angepasst werden.",
"ui.script_builder.prestep.generating": "Erzeuge...",
"ui.script_builder.prestep.marketable": "VERMARKTUNG",
"ui.script_builder.prestep.not_marketable": "NICHT VERMARKTUNG",
"ui.script_builder.prestep.regenerate_description": "Beschreibung neu erzeugen",
"ui.script_builder.prestep.regenerate_title": "Titel neu erzeugen",
"ui.script_builder.prestep.sanitized_text": "Sanitized Text",
"ui.script_builder.prestep.start_creation": "Erstellprozess starten",
"ui.script_builder.prestep.title_recommended": "Empfehlung. Kann optional ergänzt oder angepasst werden.",
"ui.script_builder.prestep.title_required": "Pflichtfeld. Darf nur durch KI erzeugt oder neu erzeugt werden.",
"ui.script_builder.progress.building_foundation": "Grundlage wird aufgebaut",
"ui.script_builder.progress.completed_detail": "Die Erstellung wurde abgeschlossen.",
"ui.script_builder.progress.failed": "Erstellung konnte nicht abgeschlossen werden",
"ui.script_builder.progress.finalizing": "Erstellung wird finalisiert",
"ui.script_builder.progress.keep_window_open": "Die letzten Schritte werden vorbereitet. Bitte lassen Sie das Fenster geöffnet.",
"ui.script_builder.progress.merging_components": "Die erkannten Bestandteile werden geprüft und sauber zusammengeführt.",
"ui.script_builder.progress.preparing": "Erstellung wird vorbereitet",
"ui.script_builder.progress.preparing_usage": "Die spätere Nutzung wird vorbereitet und konsistent aufgebaut.",
"ui.script_builder.progress.processing_input": "Die Eingabe wird verarbeitet und für die weitere Erstellung vorbereitet.",
"ui.script_builder.progress.quality_assurance": "Qualität wird abgesichert",
"ui.script_builder.progress.retry_detail": "Bitte prüfen Sie die Angaben und starten Sie den Vorgang erneut.",
"ui.script_builder.progress.script_created": "Skript wurde erstellt",
"ui.script_builder.progress.script_preparing": "Skript wird vorbereitet",
"ui.script_builder.progress.structuring_content": "Die Inhalte werden in eine nutzbare Struktur überführt.",
"ui.script_builder.progress.technique_created": "Technik wurde erstellt",
"ui.script_builder.progress.technique_preparing": "Technik wird vorbereitet",
"ui.script_builder.progress.wait_preparing": "Erstellung wird vorbereitet. Bitte lassen Sie das Fenster geöffnet.",
"ui.script_builder.start.script": "Skript prüfen & vorbereiten",
"ui.script_builder.start.script_running": "Skript wird geprüft...",
"ui.script_builder.start.technique": "Technik prüfen & vorbereiten",
"ui.script_builder.start.technique_running": "Technik wird geprüft...",




    // --- ObjectsManagement
"ui.object_admin.error.get_objects_invalid_list": "getObjects hat keine gültige Objektliste geliefert.",
"ui.object_admin.status.script_worker_saved": "Script wurde nach Worker 10 erfolgreich in der DB gespeichert. 🎉",
"ui.object_admin.status.script_saved": "Script wurde erfolgreich in der DB gespeichert. 🎉",
"ui.object_admin.status.technique_worker_saved": "Technik wurde nach Worker 10 erfolgreich in der DB gespeichert. 🎉",
"ui.object_admin.status.technique_saved": "Technik wurde erfolgreich in der DB gespeichert. 🎉",
"ui.object_admin.status.technique_updated": "Technik wurde aktualisiert. 🎉",

"ui.object_admin.dropdown.all": "Alle",
"ui.object_admin.dropdown.required": "Bitte waehlen",
"ui.object_admin.dropdown.no_selection": "Keine Auswahl",
"ui.object_admin.dropdown.trials_first": "Bitte zuerst Anzahl Trials wählen",


"ui.object_admin.field.search": "Suche",
"ui.object_admin.field.status": "Status",
"ui.object_admin.field.sharable": "Teilen",
"ui.object_admin.field.offer": "Angebot",
"ui.object_admin.field.industry": "Zielmarkt",
"ui.object_admin.field.sales_stage": "Verkaufsphase",
"ui.object_admin.field.technique_type": "Techniktyp",
"ui.object_admin.field.technique_application": "Technikanwendung",
"ui.object_admin.field.tier": "Tier",
"ui.object_admin.field.unlock_mode": "Nutzung",
"ui.object_admin.field.pricing_model": "Preismodell",
"ui.object_admin.field.currency": "Währung",
"ui.object_admin.field.immutable": "Immutable",
"ui.object_admin.field.technique_type_tier": "Techniktyp / Tier",
"ui.object_admin.field.sales_stage_tier": "Verkaufsphase / Tier",
"ui.object_admin.field.share_market": "Teilen / Vermarkten",
"ui.object_admin.field.price": "Preis",
"ui.object_admin.field.points": "Punkte",
"ui.object_admin.field.updated": "Update",
"ui.object_admin.field.mode": "Modus",
"ui.object_admin.field.created_by_user": "CREATED BY USER ID",
"ui.object_admin.field.target_mail": "TARGET MAIL",
"ui.object_admin.field.admin_api_key": "ADMIN API KEY",
"ui.object_admin.field.action": "Aktion",
"ui.object_admin.field.immutable_key": "object_key_immutable",
"ui.object_admin.field.company_size": "Unternehmensgröße Zielmarkt",
"ui.object_admin.field.language": "Sprache",
"ui.object_admin.field.contact": "Kontaktart",
"ui.object_admin.field.icon": "Icon",
"ui.object_admin.field.title": "Titel",
"ui.object_admin.field.short_description": "Kurzbeschreibung",
"ui.object_admin.field.trial_uses": "Anzahl Trials",
"ui.object_admin.field.trial_points": "Trial-Punkte",
"ui.object_admin.field.purchase_cents": "Kaufpreis in Cents",
"ui.object_admin.field.permanent_points": "Punkte Permanent",
"ui.object_admin.field.description": "Beschreibung",
"ui.object_admin.field.trigger_reason": "Trigger-Grund",
"ui.object_admin.field.engine_json": "ENGINE JSON",
"ui.object_admin.field.trigger_json": "TRIGGER JSON",
"ui.object_admin.field.micro_goals": "MicroGoals",
"ui.object_admin.field.api_key": "SUPERADMIN API KEY",
"ui.object_admin.field.user_id": "User ID",
"ui.object_admin.field.payload": "VOLLSTAENDIGES JSON OBJECT FUER DEN UPLOAD",

"ui.object_admin.search.script": "Script, Phase, Key, Area oder Topic suchen...",
"ui.object_admin.search.technique": "Technik, Einwand, Kategorie, Key oder Trigger suchen...",
"ui.object_admin.search.offer": "Angebot suchen...",
"ui.object_admin.search.industry": "Zielmarkt suchen...",
"ui.object_admin.search.stage": "Verkaufsphase suchen...",
"ui.object_admin.search.type": "Techniktyp suchen...",
"ui.object_admin.search.application": "Technikanwendung suchen...",
"ui.object_admin.search.offer_examples": "Angebot suchen, z. B. SEO, Recruiting, SaaS.",
"ui.object_admin.search.industry_examples": "Zielmarkt suchen, z. B. Arzt, Handwerk, KMU.",
"ui.object_admin.search.type_examples": "Techniktyp suchen, z. B. Einwand, Storytelling, Vertrauensaufbau.",
"ui.object_admin.search.application_examples": "Technikanwendung suchen, z. B. Preis, Kundenstory, Perspektivwechsel.",

"ui.object_admin.action.edit": "Bearbeiten",
"ui.object_admin.action.edit_technique": "Technik Bearbeiten",
"ui.object_admin.action.edit_text": "Texte bearbeiten",
"ui.object_admin.action.edit_objections": "Einwände bearbeiten",
"ui.object_admin.action.save_objections": "Einwandfreigaben speichern",
"ui.object_admin.action.save_text": "Texte speichern",
"ui.object_admin.action.save_technique": "Technik speichern",
"ui.object_admin.action.save_loading": "Speichert...",
"ui.object_admin.action.loading": "lädt...",
"ui.object_admin.action.refresh": "Aktualisieren",
"ui.object_admin.action.filter_reset": "FILTER RESET",
"ui.object_admin.action.all_filters_reset": "ALLE FILTER RESET",
"ui.object_admin.action.create_script": "SCRIPT ERSTELLEN",
"ui.object_admin.action.edit_script": "SCRIPT BEARBEITEN",
"ui.object_admin.action.create_technique": "TECHNIK ERSTELLEN",
"ui.object_admin.action.edit_technique_title": "TECHNIK BEARBEITEN",
"ui.object_admin.action.stop_market": "Vermarktung Stoppen",
"ui.object_admin.action.market": "Vermarkten",
"ui.object_admin.action.create_title": "Titel neu erzeugen",
"ui.object_admin.action.create_description": "Beschreibung neu erzeugen",
"ui.object_admin.action.generating": "Erzeuge...",
"ui.object_admin.action.update_and_save": "Aktualisieren und speichern",
"ui.object_admin.action.preflight": "Vorprüfung starten",
"ui.object_admin.action.preflighting": "Prüfung läuft...",
"ui.object_admin.action.show_payload": "PAYLOAD PRÜFEN",
"ui.object_admin.action.hide_payload": "PAYLOAD AUSBLENDEN",
"ui.object_admin.action.close": "SCHLIESSEN",
"ui.object_admin.action.send_loading": "SENDE...",
"ui.object_admin.action.to_tenant_admin": "ZU TENANT ADMIN ANSICHT",
"ui.object_admin.action.to_superadmin": "ZU SUPERADMIN ANSICHT",
"ui.object_admin.action.close_short": "Schließen",

"ui.object_admin.badge.technique": "TECHNIK",
"ui.object_admin.badge.script": "SCRIPT",
"ui.object_admin.badge.script_count": "Scripts sichtbar",
"ui.object_admin.badge.phases": "Phasen",
"ui.object_admin.badge.visible": "sichtbar",
"ui.object_admin.badge.total": "gesamt",

"ui.object_admin.title.asset_hub": "Asset Hub",
"ui.object_admin.title.scripts": "Skripte",
"ui.object_admin.title.techniques": "Techniken",
"ui.object_admin.title.objections": "Einwandbehandlung bearbeiten",
"ui.object_admin.title.script_prepare": "Skript vorbereiten",
"ui.object_admin.title.technique_prepare": "Technik vorbereiten",
"ui.object_admin.title.script_microgoals": "Texte bearbeiten:",
"ui.object_admin.title.technique_microgoals": "Technik bearbeiten:",

"ui.object_admin.hint.admin_key": "x-admin-key fuer /api/admin/manage",
"ui.object_admin.hint.filter_fields": "Filtert nur sinnvolle Object-Felder für",
"ui.object_admin.hint.objections": "Lege für jede Scriptphase fest, ob erkannte Einwände unmittelbar behandelt werden dürfen.",
"ui.object_admin.hint.no_script_phases": "Keine Script-Phasen im engine_json gefunden.",
"ui.object_admin.hint.objections_allowed": "Einwände erlaubt",
"ui.object_admin.hint.objections_blocked": "Einwände gesperrt",
"ui.object_admin.hint.no_scripts": "Keine passenden Skripte gefunden.",
"ui.object_admin.hint.no_techniques": "Keine passenden Techniken gefunden.",
"ui.object_admin.hint.no_script_phase": "Keine Script-Phase im engine_json gefunden.",
"ui.object_admin.hint.microgoal_script": "Es werden nur MicroGoal-Texte dieser Script-Phase geändert.",
"ui.object_admin.hint.no_microgoals_script": "Diese Script-Phase enthält keine microGoals.",
"ui.object_admin.hint.no_technique": "Keine Technik im engine_json gefunden.",
"ui.object_admin.hint.microgoal_technique": "Trigger-Grund und MicroGoal-Texte dieser Technik bearbeiten.",
"ui.object_admin.hint.trigger_reason": "Legt fest, wann diese Technik ausgelöst werden soll.",
"ui.object_admin.hint.no_microgoals_technique": "Diese Technik enthält keine microGoals.",
"ui.object_admin.hint.script_compiler": "Geführter Script-Compiler mit Text-Upload und Human-in-the-Loop.",
"ui.object_admin.hint.script_json": "SuperAdmin JSON/Object Upload für fertige Script-Engine-Objects.",
"ui.object_admin.hint.technique_compiler": "Geführter Technik-Compiler mit Text-Upload und Human-in-the-Loop.",
"ui.object_admin.hint.technique_json": "SuperAdmin JSON/Object Upload für fertige Technik-Engine-Objects.",
"ui.object_admin.hint.script_config_missing": "config fehlt. Script Builder braucht config.apiKey.",
"ui.object_admin.hint.technique_config_missing": "config fehlt. Technik Builder braucht config.apiKey.",
"ui.object_admin.hint.prepared": "Der Text wurde geprüft. Titel und Beschreibung werden vor der Vermarktungsänderung festgelegt.",
"ui.object_admin.hint.ai_optional": "Kann durch KI neu erzeugt und vom Menschen angepasst werden.",
"ui.object_admin.hint.ai_required": "Pflichtfeld. Darf nur durch KI erzeugt oder neu erzeugt werden.",
"ui.object_admin.hint.asset_hub": "Eigene Assets erstellen und Ihren Umsatz nachhaltig steigern.",
"ui.object_admin.hint.scripts": "Verwandeln Sie Ihr Verkaufsskript in ein trainierbares, messbares Asset, das jeden Kundeneinwand erkennt und Ihren vertrieblichen Erfolg maximiert.",
"ui.object_admin.hint.techniques": "Machen Sie Ihre Technik zur Battle Card, die Ihnen auf Abruf in kritischen Verkaufssituationen automatisch empfohlen wird und direkt abgerufen werden kann. Mit Ihrem Set an Battle Cards haben Sie jederzeit das Gespräch im Griff.",
"ui.object_admin.hint.payload_tenant": "Request Body ist ausschliesslich das unten angezeigte JSON. Tenant Admin läuft über Session Cookie und Tenant-Fork.",
"ui.object_admin.hint.load_current": "Aktuelle Assets laden",
"ui.object_admin.hint.title_optional": "Empfehlung. Kann optional ergänzt oder angepasst werden.",
"ui.object_admin.hint.script_bundle": "Script-Bundle",
"ui.object_admin.hint.payload_before": "Request Body ist ausschliesslich das unten angezeigte JSON. Der SuperAdmin Key wird nur als Header",
"ui.object_admin.hint.payload_after": "gesendet.",
"ui.object_admin.hint.marketing_full": "Die Vermarktung ist damit verbunden, dass wir Verkäufern passende Techniken und Skripte empfehlen, die kostenfrei für eine begrenzte Anzahl an Nutzungen verwendet werden können. Anschließend stehen sie nicht mehr zur Verfügung. Durch die gezielte Empfehlung steigt die Wahrscheinlichkeit für ein erfolgreiches Abonnement deutlich. Mit der Auswahl geben Sie Ihr Einverständnis für die Nutzungsrechtübertragung.",

"ui.object_admin.aria.objections.enable": "aktivieren",
"ui.object_admin.aria.objections.disable": "deaktivieren",
"ui.object_admin.aria.objections_prefix": "Einwände in",
"ui.object_admin.aria.microgoal_edit": "bearbeiten",
"ui.object_admin.aria.microgoal_close": "schließen",
"ui.object_admin.aria.trigger_reason": "Trigger-Grund bearbeiten",

"ui.object_admin.error.api_key_missing": "SuperAdmin API Key fehlt",
"ui.object_admin.error.invalid_engine_json": "engine_json ist ungültig.",
"ui.object_admin.error.invalid_trigger_json": "trigger_json ist ungültig.",
"ui.object_admin.error.could_not_generate_title": "Titel konnte nicht neu erzeugt werden.",
"ui.object_admin.error.could_not_generate_description": "Beschreibung konnte nicht neu erzeugt werden.",
"ui.object_admin.error.marketplace_upgrade": "Marketplace-Upgrade fehlgeschlagen.",
"ui.object_admin.error.prestep": "Marketplace-Prestep fehlgeschlagen.",
"ui.object_admin.error.upload_script": "Script Upload fehlgeschlagen.",
"ui.object_admin.error.upload_technique": "Technik Upload fehlgeschlagen.",
"ui.object_admin.error.required_fields": "Pflichtfelder fehlen.",
"ui.object_admin.error.payload_build": "Payload kann noch nicht gebaut werden.",
"ui.object_admin.error.preflight": "Preflight fehlgeschlagen.",
"ui.object_admin.error.missing_fields_prefix": "Bitte zuerst ausfüllen/korrigieren:",
"ui.object_admin.error.unknown_saved_type": "Unbekannter Objekttyp nach erfolgreichem Speichern.",
"ui.object_admin.error.object_key_missing": "object_key_immutable fehlt.",
"ui.object_admin.error.get_objects": "getObjects fehlgeschlagen.",
"ui.object_admin.error.unknown_load": "Unbekannter Ladefehler.",
"ui.object_admin.error.save_reload_prefix": "Speichern erfolgreich, aber Neuladen oder Objektzuordnung fehlgeschlagen:",
"ui.object_admin.error.save_reload_suggestion": "Bitte nur „Aktualisieren“ verwenden, nicht erneut speichern.",
"ui.object_admin.error.script_text_update": "Script-Textbearbeitung ist nur im Tenant-Admin- oder SuperAdmin-Modus erlaubt.",
"ui.object_admin.error.required_field_trials": "Trials",
"ui.object_admin.error.required_field_trial_points": "Punkte Trials",

"ui.object_admin.status.script_published": "Skript wurde zur Vermarktung veröffentlicht. 🎉",
"ui.object_admin.status.script_unpublished": "Skript wurde vom Marktplatz entfernt. 🎉",
"ui.object_admin.status.technique_published": "Technik wurde zur Vermarktung veröffentlicht. 🎉",
"ui.object_admin.status.technique_unpublished": "Technik wurde vom Marktplatz entfernt. 🎉",
"ui.object_admin.status.update_attempt": "Update-Attempt abgeschlossen. Bitte Titel und Beschreibung prüfen.",
"ui.object_admin.status.assets_loaded": "Assets geladen.",
"ui.object_admin.status.script_saved_reloaded": "Skript gespeichert und vollständig neu geladen.",
"ui.object_admin.status.technique_saved_reloaded": "Technik gespeichert und vollständig neu geladen.",
"ui.object_admin.status.objections_updated": "Einwandfreigaben wurden aktualisiert. 🎉",
"ui.object_admin.status.script_text_updated": "Script-Texte wurden aktualisiert. 🎉",
"ui.object_admin.status.important_missing": "Nutzung darf nicht TEMPORARY sein",

"ui.object_admin.mode.json_upload": "JSON/Object Upload",
"ui.object_admin.mode.text_compiler": "Text/HITL Compiler",

"ui.object_admin.boolean.yes": "Ja",
"ui.object_admin.boolean.no": "Nein",
"ui.object_admin.boolean.yes_upper": "JA",
"ui.object_admin.boolean.no_upper": "NEIN",

"ui.object_admin.action.edit_text_singular": "Text bearbeiten",
"ui.object_admin.action.marketing_stop": "Vermarktung stoppen",
"ui.object_admin.field.usage_type": "Nutzungsart",
"ui.object_admin.field.immutable_key_upper": "OBJECT KEY IMMUTABLE",
"ui.object_admin.filter.unlock_both": "Empfehlungen & Verkauf",
"ui.object_admin.title.asset_builder": "ASSET BUILDER",



    // --- OfferCreator

'ui.offer_generator.alleinstellungsmerkmale': 'Alleinstellungsmerkmale',
'ui.offer_generator.alternativen': 'Alternativen',
'ui.offer_generator.amortisation': 'Amortisation',
'ui.offer_generator.amortisationszeit': 'Amortisationszeit',
'ui.offer_generator.angebotskategorie': 'Angebotskategorie',
'ui.offer_generator.angebotskategorie_suchen': 'Angebotskategorie suchen.',
'ui.offer_generator.angebotsname': 'Angebotsname',
'ui.offer_generator.angebotswissen_kb': 'Angebotswissen / KB',
'ui.offer_generator.ausgangsbasis_des_produzierten_vorergebnisses_je': 'Ausgangsbasis des produzierten Vorergebnisses je',
'ui.offer_generator.ausgangsbasis_des_produzierten_vorergebnisses_je_zeitmetrik': 'Ausgangsbasis des produzierten Vorergebnisses je Zeitmetrik',
'ui.offer_generator.bearbeiten': 'Bearbeiten',
'ui.offer_generator.beginn_der_veranderung_des_produzierten_vorergebnisses': 'Beginn der Veränderung des produzierten Vorergebnisses',
'ui.offer_generator.berechneter_fortwirkender_return_je': 'Berechneter fortwirkender Return je',
'ui.offer_generator.besonderheiten': 'Besonderheiten',
'ui.offer_generator.besonderheiten_beim_rechenweg': 'Besonderheiten beim Rechenweg',
'ui.offer_generator.branche_suchen': 'Branche suchen.',
'ui.offer_generator.branchenfokus': 'Branchenfokus',
'ui.offer_generator.branchenfokus_kontext_ist_nicht_verfugbar': 'Branchenfokus-Kontext ist nicht verfügbar.',
'ui.offer_generator.branchenfokusse': 'Branchenfokusse',
'ui.offer_generator.einheit_des_kundenziels': 'Einheit des Kundenziels',
'ui.offer_generator.einmaliger_anteil_investment_fur_dritte_sonstige_investitionen': 'Einmaliger Anteil Investment für Dritte / sonstige Investitionen',
'ui.offer_generator.einmaliger_anteil_investment_fur_unsere_leistung': 'Einmaliger Anteil Investment für unsere Leistung',
'ui.offer_generator.erreichte_einheiten_des_kundenziels_uber_den_zeitraum': 'Erreichte Einheiten des Kundenziels über den Zeitraum',
'ui.offer_generator.erstellen': 'Erstellen',
'ui.offer_generator.erstellen_lower': 'erstellen',
'ui.offer_generator.fortwirkender_return_je_zeitmetrik_im_optionalen_wirkungshorizont': 'Fortwirkender Return je Zeitmetrik im optionalen Wirkungshorizont',
'ui.offer_generator.gesamtdauer_inklusive_wirkungshorizont': 'Gesamtdauer inklusive Wirkungshorizont',
'ui.offer_generator.gesamtinvestment_fur_dritte_sonstige_investitionen_uber_den_zeitraum': 'Gesamtinvestment für Dritte / sonstige Investitionen über den Zeitraum',
'ui.offer_generator.gesamtinvestment_fur_unsere_leistung_uber_den_zeitraum': 'Gesamtinvestment für unsere Leistung über den Zeitraum',
'ui.offer_generator.gesamtinvestment_uber_den_zeitraum': 'Gesamtinvestment über den Zeitraum',
'ui.offer_generator.gesamtinvestment_uber_die_gesamtdauer': 'Gesamtinvestment über die Gesamtdauer',
'ui.offer_generator.im_optionalen_wirkungshorizont': 'im optionalen Wirkungshorizont',
'ui.offer_generator.investment': 'Investment',
'ui.offer_generator.investment_fur_dritte_sonstige_investitionen_lauft_im_wirkungshorizont_weiter': 'Investment für Dritte / sonstige Investitionen läuft im Wirkungshorizont weiter',
'ui.offer_generator.investment_fur_unsere_leistung_lauft_im_wirkungshorizont_weiter': 'Investment für unsere Leistung läuft im Wirkungshorizont weiter',
'ui.offer_generator.keine_analogie_hinterlegt': 'Keine Analogie hinterlegt',
'ui.offer_generator.konservative_annahmen': 'Konservative Annahmen',
'ui.offer_generator.kundenbeziehung': 'Kundenbeziehung',
'ui.offer_generator.level.effect.addLabel': 'Vorteil hinzufügen',
'ui.offer_generator.level.effect.singular': 'Vorteil',
'ui.offer_generator.level.effect.subtitle': 'Was bringt dieser Mechanismus grundsätzlich – unabhängig vom konkreten Kunden?',
'ui.offer_generator.level.effect.title': 'Vorteile',
'ui.offer_generator.level.effect_unit.addLabel': 'Produziertes Vorergebnis hinzufügen',
'ui.offer_generator.level.effect_unit.nextLabel': 'Mechanismen',
'ui.offer_generator.level.effect_unit.singular': 'Produziertes Vorergebnis',
'ui.offer_generator.level.effect_unit.subtitle': 'Bearbeite das von uns zuletzt produzierte Vorergebnis vor dem Wunschergebnis des Kunden.',
'ui.offer_generator.level.effect_unit.title': 'Produzierte Vorergebnisse',
'ui.offer_generator.level.mechanism.addLabel': 'Mechanismus hinzufügen',
'ui.offer_generator.level.mechanism.nextLabel': 'Vorteile',
'ui.offer_generator.level.mechanism.singular': 'Mechanismus',
'ui.offer_generator.level.mechanism.subtitle': 'Bearbeite ausschließlich die Mechanismen dieses produzierten Vorergebnisses.',
'ui.offer_generator.level.mechanism.title': 'Mechanismen',
'ui.offer_generator.level.offer.nextLabel': 'Branchenfokus',
'ui.offer_generator.level.offer.singular': 'Angebot',
'ui.offer_generator.level.offer.subtitle': 'Das Angebot ist der Einstiegspunkt der vollständigen Offer-Struktur.',
'ui.offer_generator.level.offer.title': 'Angebot',
'ui.offer_generator.level.roi_case.addLabel': 'Branchenfokus hinzufügen',
'ui.offer_generator.level.roi_case.nextLabel': 'Produzierte Vorergebnisse',
'ui.offer_generator.level.roi_case.singular': 'Branchenfokus',
'ui.offer_generator.level.roi_case.subtitle': 'Bearbeite die Branchenfokusse dieses Angebots.',
'ui.offer_generator.level.roi_case.title': 'Branchenfokus',
'ui.offer_generator.mechanismus': 'Mechanismus',
'ui.offer_generator.mechanismus_kontext_ist_nicht_verfugbar': 'Mechanismus-Kontext ist nicht verfügbar.',
'ui.offer_generator.mechanismus_unseres_services': 'Mechanismus unseres Services',
'ui.offer_generator.mehrwert': 'Mehrwert',
'ui.offer_generator.mehrwert_nach_abzug_des_investments_uber_den_zeitraum': 'Mehrwert nach Abzug des Investments über den Zeitraum',
'ui.offer_generator.mehrwert_nach_abzug_des_investments_uber_die_gesamtdauer': 'Mehrwert nach Abzug des Investments über die Gesamtdauer',
'ui.offer_generator.metapher_analogie': 'Metapher / Analogie',
'ui.offer_generator.mitarbeiter': 'Mitarbeiter',
'ui.offer_generator.mitarbeiter_bis': 'Mitarbeiter bis',
'ui.offer_generator.mitarbeiter_von': 'Mitarbeiter von',
'ui.offer_generator.nicht_ausgewahlt': 'Nicht ausgewählt',
'ui.offer_generator.nicht_gesetzt': 'nicht gesetzt',
'ui.offer_generator.nicht_verfugbar': 'Nicht verfügbar',
'ui.offer_generator.optional': 'Optional',
'ui.offer_generator.optionaler_deckungsbeitrag': 'Optionaler Deckungsbeitrag',
'ui.offer_generator.optionaler_deckungsbeitrag_percent': 'Optionaler Deckungsbeitrag (%)',
'ui.offer_generator.optionaler_wirkungshorizont_nach_dem_zeitraum': 'Optionaler Wirkungshorizont nach dem Zeitraum',
'ui.offer_generator.pricing': 'Pricing',
'ui.offer_generator.produzierte_vorergebnisse': 'Produzierte Vorergebnisse',
'ui.offer_generator.produziertes_vorergebnis_kontext_ist_nicht_verfugbar': 'Produziertes Vorergebnis-Kontext ist nicht verfügbar.',
'ui.offer_generator.rate_investment_fur_unsere_leistung_je': 'Rate Investment für unsere Leistung je',
'ui.offer_generator.rechenweg': 'Rechenweg',
'ui.offer_generator.rechenweg_aktuell_nicht_berechenbar': 'Rechenweg aktuell nicht berechenbar',
'ui.offer_generator.resultierende_veranderung_des_produzierten_vorergebnisses_uber_den_zeitraum': 'Resultierende Veränderung des produzierten Vorergebnisses über den Zeitraum',
'ui.offer_generator.resultierender_deckungsbeitrag': 'Resultierender Deckungsbeitrag',
'ui.offer_generator.return_ohne_abzug_des_investments_uber_den_zeitraum': 'Return ohne Abzug des Investments über den Zeitraum',
'ui.offer_generator.return_ohne_abzug_des_investments_uber_die_gesamtdauer': 'Return ohne Abzug des Investments über die Gesamtdauer',
'ui.offer_generator.scenario': 'Scenario',
'ui.offer_generator.start_einmaliger_anteil_investment_fur_dritte_sonstige_investitionen': 'Start einmaliger Anteil Investment für Dritte / sonstige Investitionen',
'ui.offer_generator.start_einmaliger_anteil_investment_fur_unsere_leistung': 'Start einmaliger Anteil Investment für unsere Leistung',
'ui.offer_generator.start_investment_fur_dritte_sonstige_investitionen': 'Start Investment für Dritte / sonstige Investitionen',
'ui.offer_generator.start_investment_fur_unsere_leistung': 'Start Investment für unsere Leistung',
'ui.offer_generator.start_ratenzahlung_fur_unsere_leistung': 'Start Ratenzahlung für unsere Leistung',
'ui.offer_generator.time_plural.day': 'Tage',
'ui.offer_generator.time_plural.month': 'Monate',
'ui.offer_generator.time_plural.quarter': 'Quartale',
'ui.offer_generator.time_plural.week': 'Wochen',
'ui.offer_generator.time_plural.year': 'Jahre',
'ui.offer_generator.umwandlungsquote_zum_zielergebnis_des_kunden': 'Umwandlungsquote zum Zielergebnis des Kunden',
'ui.offer_generator.update': 'Update',
'ui.offer_generator.veranderung_des_produzierten_vorergebnisses_durch_unseren_service_je': 'Veränderung des produzierten Vorergebnisses durch unseren Service je',
'ui.offer_generator.veranderung_des_produzierten_vorergebnisses_durch_unseren_service_je_zeitmetrik': 'Veränderung des produzierten Vorergebnisses durch unseren Service je Zeitmetrik',
'ui.offer_generator.von_uns_zuletzt_produziertes_vorergebnis_vor_dem_kundenziel': 'Von uns zuletzt produziertes Vorergebnis vor dem Kundenziel',
'ui.offer_generator.vorergebnis': 'Vorergebnis',
'ui.offer_generator.vorteil': 'Vorteil',
'ui.offer_generator.vorteil_was_bringt_der_mechanismus_grundsatzlich': 'Vorteil – was bringt der Mechanismus grundsätzlich?',
'ui.offer_generator.vorteile': 'Vorteile',
'ui.offer_generator.wirkung': 'Wirkung',
'ui.offer_generator.wirtschaftlich_wirksamer_zeitraum': 'Wirtschaftlich wirksamer Zeitraum',
'ui.offer_generator.wirtschaftlicher_wert': 'Wirtschaftlicher Wert',
'ui.offer_generator.wirtschaftlicher_wert_je_einheit_des_kundenziels': 'Wirtschaftlicher Wert je Einheit des Kundenziels',
'ui.offer_generator.wirtschaftlicher_wert_uber_den_zeitraum': 'Wirtschaftlicher Wert über den Zeitraum',
'ui.offer_generator.zahlungsart_investment_fur_dritte_sonstige_investitionen': 'Zahlungsart Investment für Dritte / sonstige Investitionen',
'ui.offer_generator.zahlungsart_investment_fur_unsere_leistung': 'Zahlungsart Investment für unsere Leistung',
'ui.offer_generator.zeit_bis_zum_ersten_wirtschaftlichen_ergebnis': 'Zeit bis zum ersten wirtschaftlichen Ergebnis',
'ui.offer_generator.zeitmetrik': 'Zeitmetrik',
'ui.offer_generator.zeitraum': 'Zeitraum',
'ui.offer_generator.zielkunde': 'Zielkunde',
'ui.offer_generator.zusatzlicher_return_im_optionalen_wirkungshorizont': 'Zusätzlicher Return im optionalen Wirkungshorizont',



    // -- Deck

      "ui.deck.member.marketplace": "MARKETPLACE",
      "ui.deck.member.heading": "VERWALTUNG DER ITEMS FÜR?",
      "ui.deck.member.label": "MITGLIED",
      "ui.deck.member.fallback_prefix": "Member ",
      "ui.deck.member.self_suffix": " (ich)",

      "ui.deck.section.cards": "01 BATTLE CARDS",
      "ui.deck.section.scripts": "02 SCRIPTS",

      "ui.deck.item.script_badge": "SCRIPT",
      "ui.deck.item.phases": "PHASEN",
      "ui.deck.item.bundle": "BUNDLE",
      "ui.deck.item.asset_badge": "ASSET",
      "ui.deck.item.free_badge": "FREE",
      "ui.deck.item.trial_badge": "TRIAL",
      "ui.deck.item.perm_badge": "PERM",
      "ui.deck.item.of": "von",
      "ui.deck.item.remaining": "offen",
      "ui.deck.item.per_month": "€/Monat",
      "ui.deck.item.start": "Start",
      "ui.deck.item.end": "Ende",
      "ui.deck.item.trial_locked_prefix": "Trial gesperrt bis ",
      "ui.deck.item.repurchase_locked_prefix": "Wiederkauf & Trials gesperrt bis ",
      "ui.deck.item.details_button": "Details",

      "ui.deck.details.brand": "DECK BUILDER",
      "ui.deck.details.heading": "MODUL DETAILS",
      "ui.deck.details.type": "TYP",
      "ui.deck.details.type_asset": "Asset",
      "ui.deck.details.type_free": "Free",
      "ui.deck.details.type_trial": "Trial",
      "ui.deck.details.type_permanent": "Permanent",
      "ui.deck.details.price_usage": "PREIS / NUTZUNG",
      "ui.deck.details.cancel_due_prefix": "Kündigung zum: ",
      "ui.deck.details.locked_until_prefix": "Gesperrt bis: ",
      "ui.deck.details.description": "BESCHREIBUNG",
      "ui.deck.details.no_description": "Keine Beschreibung vorhanden.",
      "ui.deck.details.price": "PREIS",
      "ui.deck.details.status": "STATUS",

      "ui.deck.cancel.warning_prefix": "Achtung: Du bist dabei, dieses permanente Modul für ",
      "ui.deck.cancel.warning_suffix": " zu kündigen.",
      "ui.deck.cancel.confirm_hint": "Die Kündigung wird vorgemerkt und muss jetzt nochmals ausdrücklich bestätigt werden.",
      "ui.deck.cancel.action": "Kündigen",
      "ui.deck.cancel.back": "Zurück",
      "ui.deck.cancel.close": "Schließen",
      "ui.deck.cancel.confirm": "Kündigung bestätigen",
      "ui.deck.cancel.confirm_heading": "KÜNDIGUNG BESTÄTIGEN",

      "ui.deck.sets.title_load": "Set Laden",
      "ui.deck.sets.title_edit": "Set Bearbeiten",
      "ui.deck.sets.title_save": "Set Speichern",
      "ui.deck.sets.close_aria": "Set-Modal schließen",
      "ui.deck.sets.none_saved": "Noch keine Sets gespeichert.",
      "ui.deck.sets.techniques_word": "Techniken",
      "ui.deck.sets.unavailable": "Set derzeit nicht ladbar: Verfügbarkeit oder Slots prüfen.",
      "ui.deck.sets.edit": "Bearbeiten",
      "ui.deck.sets.name": "Set-Bezeichnung",
      "ui.deck.sets.name_placeholder": "Bezeichnung eingeben",
      "ui.deck.sets.script_label": "Skript",
      "ui.deck.sets.current_techniques": "Techniken aus dem aktuellen Battle Deck",
      "ui.deck.sets.save_changes": "Änderungen speichern",
      "ui.deck.sets.save": "Set speichern",
      "ui.deck.sets.remove_trials": "Trials entfernen",

      "ui.deck.embedded.owner_prefix": "DECK VON ",
      "ui.deck.embedded.items_title": "Battle Items",
      "ui.deck.embedded.slots": "SLOTS",
      "ui.deck.active_deck": "ACTIVE DECK",

      "ui.deck.filter.all_industries": "Alle Zielmärkte",
      "ui.deck.filter.all_offers": "Alle Angebote",
      "ui.deck.filter.all_creators": "Alle Creator",
      "ui.deck.filter.without_creator": "Ohne Creator",
      "ui.deck.filter.industry": "Zielmarkt",
      "ui.deck.filter.offer": "Angebot",
      "ui.deck.filter.creator": "Creator",
      "ui.deck.script.select": "Skript auswählen",
      "ui.deck.filter.active": "AKTIV",
      "ui.deck.filter.history": "HISTORIE",

      "ui.deck.button.save": "SPEICHERN",

"ui.deck.error.load_sets": "Deine gespeicherten Sets konnten nicht geladen werden. Bitte versuche es erneut.",
"ui.deck.error.save_set": "Das Set konnte nicht gespeichert werden. Bitte versuche es erneut.",

"ui.deck.mobile.scripts": "SCRIPTS",
"ui.deck.mobile.filter.all_phases": "Alle Verkaufsphasen",
"ui.deck.mobile.filter.all_applications": "Alle Technikanwendung",
"ui.deck.mobile.member.profile_heading": "PROFIL?",
"ui.deck.mobile.owner_prefix": "⚡ BATTLE ITEMS VON ",
"ui.deck.mobile.global_filters": "GLOBALE FILTER",
"ui.deck.mobile.filter.all": "ALLE",



     // --- Shop

'ui.shop.brand.performance_upgrades_embedded': 'Performance Upgrades',
'ui.shop.common.unnamed': 'Unbenannt',
'ui.shop.common.other': 'Sonstige',
'ui.shop.error.trial_purchase_failed': 'Trial-Kauf fehlgeschlagen',
'ui.shop.error.purchase_data_incomplete': 'Kaufdaten unvollständig',
'ui.shop.error.trial_data_incomplete': 'Trial-Kaufdaten unvollständig',
// --- ModuleShopDesktop: fehlende UI-Einträge DE ---

'ui.shop.filter.all_industries': 'Alle Zielmärkte',
'ui.shop.filter.all_offers': 'Alle Angebote',
'ui.shop.filter.industry': 'Zielmarkt',
'ui.shop.filter.offer': 'Angebot',
'ui.shop.filter.search_placeholder': '🔎 Titel, Beschreibung, Creator ...',
'ui.shop.filter.recommended': 'Empfohlen',
'ui.shop.filter.assets_visible': 'Assets werden angezeigt',
'ui.shop.filter.assets_hidden': 'Assets werden ausgeblendet',
'ui.shop.filter.refresh': 'Aktualisieren',
'ui.shop.filter.reset': 'Filter zurücksetzen',
'ui.shop.filter.assets': 'Assets',
'ui.shop.filter.perm': 'PERM',
'ui.shop.filter.trial': 'TRIAL',

'ui.shop.price.per_month': '/ Monat',

'ui.shop.section.battle_cards': 'BATTLE CARDS',
'ui.shop.section.scripts': 'SKRIPTE',
'ui.shop.section.cards_suffix': 'KARTEN',
'ui.shop.section.scripts_suffix': 'SKRIPTE',

'ui.shop.card.recommended_badge': 'Empfohlen ☆',
'ui.shop.card.script': 'Skript',
'ui.shop.card.status_asset': 'ASSET',
'ui.shop.card.status_team': 'TEAM',
'ui.shop.card.status_new': 'NEU',
'ui.shop.card.details': 'Details',
'ui.shop.card.buy_trial': '🛒 TRIAL',
'ui.shop.card.buy_perm': '🛒 PERM',

'ui.shop.cart.permanent_access': 'DAUERHAFTER ZUGANG',
'ui.shop.cart.cancel_monthly': 'jederzeit monatlich kündbar',
'ui.shop.cart.trial_access': 'TESTZUGANG',
'ui.shop.cart.uses': 'Nutzungen',
'ui.shop.cart.uses_upper': 'NUTZUNGEN',
'ui.shop.cart.trial_block_notice': 'Nach Verwendung 2 Monate gesperrt, wenn kein Kauf erfolgt',
'ui.shop.cart.remove': 'Entfernen',

'ui.shop.meta.creator': 'Creator',

'ui.shop.checkout.title': 'Checkout:',
'ui.shop.checkout.clear': 'Leeren',
'ui.shop.checkout.subtotal': 'Zwischensumme',
'ui.shop.checkout.tax': 'MwSt. (19%)',
'ui.shop.checkout.total': 'GESAMT',
'ui.shop.checkout.pts_after': 'PTS DANACH',
'ui.shop.checkout.buy': 'JETZT KAUFEN',
'ui.shop.checkout.back': 'Zurück',

'ui.shop.details.heading': 'MODUL DETAILS',
'ui.shop.details.technique_type': 'Techniktyp',
'ui.shop.details.sales_phase': 'Verkaufsphase',
'ui.shop.details.technique_application': 'Technikanwendung',
'ui.shop.details.trial_notice': 'Trial-Hinweis',
'ui.shop.details.description': 'BESCHREIBUNG',
'ui.shop.details.description_empty': 'Keine Beschreibung vorhanden.',
'ui.shop.details.video_placeholder': 'Video-Erklärung wird später eingebunden',
'ui.shop.details.close': 'Schließen',

'ui.shop.member.buy_for': 'FÜR WEN KAUFEN?',
'ui.shop.member.label': 'MITGLIED',
'ui.shop.member.fallback': 'Mitglied',
'ui.shop.member.self_suffix': ' (ich)',
'ui.shop.member.cancel': 'Abbrechen',
'ui.shop.member.apply': 'Übernehmen',

'ui.shop.brand.marketplace': 'MARKETPLACE',
'ui.shop.brand.performance_upgrades': 'PERFORMANCE-UPGRADES',
'ui.shop.error.load_failed': 'Shop konnte nicht geladen werden',
'ui.shop.error.purchase_failed': 'Kauf fehlgeschlagen',


'ui.shop.filter.all_subcategories': 'Alle Subkategorien',
'ui.shop.mobile.brand.shop': 'SHOP',
'ui.shop.mobile.brand.upgrades': 'UPGRADES',
'ui.shop.mobile.brand.vault_balance': 'GUTHABEN',
'ui.shop.mobile.checkout.points': 'Punkte',
'ui.shop.mobile.empty.no_results': 'Keine passenden Inhalte gefunden.',
'ui.shop.mobile.filter.global': 'GLOBALER FILTER',
'ui.shop.mobile.filter.max': 'MAX',




      // --- Chat Mobile

      "ui.mobile.action.park": "Parken",
      "ui.mobile.goal.hidden_tap": "🔒 ZIEL VERDECKT (Tippen zum Aufdecken)",
      "ui.mobile.quick_reaction.cancel_selection": "Technikauswahl zurücknehmen",



      // --- Sidebar ---


'ui.sidebar.notebook.speech_total_short': 'GESAMT',
'ui.sidebar.notebook.speech_module_short': 'MODUL',
'ui.sidebar.notebook.speech_client_short': 'KUNDE',

'ui.sidebar.parked.checking_start_tooltip': 'Prüfe Start',
'ui.sidebar.parked.other_check_running': 'Andere Technik wird geprüft',
'ui.sidebar.parked.unavailable': 'Aktuell nicht verfügbar',
'ui.sidebar.parked.cancel_selection': 'Auswahl zurücknehmen',
'ui.sidebar.parked.checking_start': 'Prüfe Start …',

'ui.sidebar.notebook.deepness.clarity_reason': 'Klarheit',
'ui.sidebar.notebook.deepness.emotion_reason': 'Emotion',
'ui.sidebar.notebook.deepness.scene_reason': 'Szene',
'ui.sidebar.notebook.deepness.meaning_reason': 'Bedeutung',
'ui.sidebar.notebook.deepness.magnitude_reason': 'Ausmaß',
'ui.sidebar.notebook.deepness.consequence_reason': 'Konsequenz',

'ui.sidebar.notebook.deepness.insight': 'Erkenntnis:',
'ui.sidebar.notebook.deepness.mechanism': 'Mechanismus:',
'ui.sidebar.notebook.deepness.advantage': 'Vorteil:',
'ui.sidebar.notebook.deepness.meaning': 'Bedeutung:',

'ui.sidebar.action.parking': 'Wird geparkt …',
'ui.sidebar.action.terminating': 'Wird beendet …',
'ui.sidebar.action.terminate_new': 'Beenden & Neu',

'ui.sidebar.manual_completion.heading': 'MANUELLE ZIELERFÜLLUNG',
'ui.sidebar.manual_completion.missing_variables': 'FEHLENDE VARIABLEN',
'ui.sidebar.manual_completion.close': 'Modal schließen',
'ui.sidebar.manual_completion.missing_data': 'Fehlende Daten',
'ui.sidebar.manual_completion.option_placeholder': 'Option wählen...',
'ui.sidebar.manual_completion.cancel': 'Abbrechen',
'ui.sidebar.manual_completion.apply': 'Übernehmen',

'ui.sidebar.menu.tooltip': 'Menü',
'ui.sidebar.menu.placeholder': 'Aktion wählen...',

'ui.sidebar.notebook.psychology_live': 'PSYCHOLOGIE (LIVE)',
'ui.sidebar.notebook.wait_for_profile': 'Warte auf Profil...',

'ui.sidebar.exit.error_run_failed': 'BEENDEN FEHLER: Run konnte nicht sauber beendet werden',
'ui.sidebar.exit.abort_missing_ids': 'BEENDEN ABBRUCH: runID oder branchID fehlt',
'ui.sidebar.exit.error_prefix': 'BEENDEN FEHLER:',
'ui.sidebar.exit.status': 'Status:',
'ui.sidebar.exit.response': 'Response:',


      // --- ChatArea ---
     'ui.chat.life.role.closer_label': 'Verkäufer',
     'ui.chat.life.role.prospect_label': 'Interessent',
     'ui.chat.life.view.show_chat': 'Chat anzeigen',
     'ui.chat.life.view.show_video': 'Video anzeigen',


      // --- COMMON & INITIALIZATION ---
      'ui.common.initializing': 'Initialisierung...',
      'ui.common.detail': 'Detail...',
      'ui.common.none': 'Keine',
      'ui.common.unknown': 'Unbekannt',
      'ui.common.error.analysis': 'Analyse fehlgeschlagen.',
      'ui.common.error.no_audio_data': 'Keine Audiodaten verfügbar.',
      'ui.common.editable.click_to_edit': 'Klicken zum Bearbeiten',
      'ui.common.editable.empty': 'leer',
      'ui.common.editable.placeholder': 'Wert eingeben...',

      // --- SYSTEM STATUS ---
      'ui.status.init_system': 'System wird initialisiert...',
      'ui.status.loading_backstory': 'Backstory wird geladen...',
      'ui.status.ready_user': 'Szenario bereit. Du bist dran.',
      'ui.status.running': 'Szenario läuft.',
      'ui.status.ai_closer_thinking': 'AI (Closer) denkt nach...',
      'ui.status.ai_prospect_reacting': 'AI (Prospect) reagiert...',
      'ui.status.waiting_closer': 'Warte auf dich (Closer)...',
      'ui.status.error_logic': 'Fehler in der Logik-Verarbeitung.',
      'ui.status.creating_analysis': 'Erstelle Session-Analyse...',
      'ui.status.module_resumed': 'Modul fortgesetzt.',
      'ui.status.module_parked': 'Modul geparkt.',
      'ui.status.module_terminated': 'Modul beendet.',
      'ui.status.restarting': 'Jetzt neu starten...',
      'ui.status.simulation_ended': 'Jetzt beendet.',
      'ui.status.module_finished': 'Modul abgeschlossen.',
      'ui.status.restore_prefix': 'Wiederhergestellt: ',
      'ui.status.manual_override_prefix': 'Manueller Zugriff: ',
      'ui.status.mode_active_prefix': 'Modus Aktiv: ',
      'ui.status.please_speak': '(Bitte sprechen)',
      'ui.status.handover': 'Übergabe',
      'ui.status.soft_restart_log': '--- Neustart (Soft) ---',

      // --- SIDEBAR STATUS ---
      'ui.sidebar.status.header': 'Status',
      'ui.sidebar.status.ghost_pilot': 'Ghost Pilot',
      'ui.sidebar.status.life_mode': 'Life Modus',
      'ui.sidebar.status.system_focus': 'System Fokus',
      'ui.sidebar.status.speech_share_module': 'Redeanteil (Modul)',
      'ui.sidebar.status.debug_header': 'Debugging',
      'ui.sidebar.status.debug_fallback': 'Warte auf Logs...',
      'ui.sidebar.status.engine_version': 'ELITS ENGINE v2.3 Deep',
      'ui.sidebar.status.objection_active': 'Einwandbehandlung Aktiv',
      'ui.sidebar.status.ready': 'Bereit',
      'ui.sidebar.status.ready_fallback': 'Bereit',

      // --- GHOST & MODE TOOLTIPS ---
      'ui.sidebar.ghost.status_tooltip_active': 'Live Coach Aktiv',
      'ui.sidebar.ghost.status_tooltip_inactive': 'Live Coach Inaktiv',
      'ui.sidebar.mode.life_desc': 'LIFE Modus: KI stumm, nur Coaching',
      'ui.sidebar.mode.sim_desc': 'Simulations Modus: KI spricht',

      // --- DEEPNESS LADDER ---
      'ui.sidebar.deepness.header': 'Tiefe (relevant)',
      'ui.sidebar.deepness.fallback': 'Warte auf Analyse...',
      'ui.sidebar.deepness.clarity': 'Klarheit',
      'ui.sidebar.deepness.emotion': 'Emotion',
      'ui.sidebar.deepness.scene': 'Szene',
      'ui.sidebar.deepness.meaning': 'Bedeutung',
      'ui.sidebar.deepness.magnitude': 'Ausmaß',
      'ui.sidebar.deepness.consequence': 'Impact',

      // --- INTERVENTIONS & SUBSYSTEMS ---
      'ui.sidebar.subsystem.step': 'Schritt',
      'ui.sidebar.subsystem.skip': '✕ Überspringen',
      'ui.sidebar.action.park': 'Modul parken (später fortsetzen)',
      'ui.sidebar.action.terminate': 'Modul beenden (ohne Abschluss)',
      'ui.sidebar.action.skip_intervention': 'Intervention überspringen',
      'ui.sidebar.parked.header': 'Geparkt / Pausiert',
      'ui.sidebar.parked.resume': 'Wiederaufnahmen',
      'ui.sidebar.parked.at': 'Geparkt am:',
      'ui.sidebar.objections.header': 'Einwände (Erkannt)',
      'ui.sidebar.objections.default': 'Einwand',
      'ui.sidebar.objections.price': '💰 Budget / Preis',
      'ui.sidebar.objections.time': '⏳ Zeit / Priorität',
      'ui.sidebar.objections.partner': '🤝 Partner / Chef',
      'ui.sidebar.objections.fear': '🛑 Angst / Skepsis',
      'ui.sidebar.objections.stall': '💤 Schlafen / Zögern',

      // --- CHECKPOINTS ---
      'ui.sidebar.checkpoint.header': 'Checkpoints',
      'ui.sidebar.checkpoint.manual_done': 'Manuell Erfüllt',
      'ui.sidebar.checkpoint.waiting': 'Wartet',
      'ui.sidebar.checkpoint.done_tooltip': 'Erfüllt',
      'ui.sidebar.checkpoint.force_tooltip': 'Klicken zum Erzwingen',

      // --- GHOST PILOT HINTS ---
      'ui.sidebar.hint.intervention': '💡 Intervention / Subsystem',
      'ui.sidebar.hint.recommendation': '💡 AI Empfehlung',
      'ui.sidebar.hint.loading': 'Analysiere Situation...',
      'ui.sidebar.hint.fallback': 'Warte auf nächsten Zug...',
      'ui.sidebar.hint.apply_tooltip': 'Klicken zum Übernehmen',

      // --- NOTEBOOK SIDEBAR ---
      'ui.sidebar.notebook.speech_share_total': 'Redeanteil (Gesamt)',
      'ui.sidebar.notebook.needs_header': 'Bedürfnisse',
      'ui.sidebar.notebook.needs_fallback': 'Analysiere Profil...',
      'ui.sidebar.notebook.variables_header': 'Gesprächsdaten / Variablen',
      'ui.sidebar.notebook.variables_fallback': 'Keine Variablen relevant.',
      'ui.sidebar.notebook.header': 'Notizbuch (CRM)',
      'ui.sidebar.notebook.maximize': 'Vergrössern',
      'ui.sidebar.notebook.fallback': 'Höre zu...',
      'ui.sidebar.notebook.empty_placeholder': 'Keine Variablen relevant.',
      'ui.sidebar.notebook.collapsed_label': 'Notizbuch',
      'ui.sidebar.notebook.toggle_tooltip_open': 'Notizbuch öffnen',
      'ui.sidebar.notebook.toggle_tooltip_collapsed': 'Minimieren',
      'ui.sidebar.notebook.listening_fallback': 'Höre zu...',
      'ui.sidebar.notebook.no_fields': 'Keine Felder',
      'ui.sidebar.notebook.cat_other': 'Sonstige',
      'ui.sidebar.notebook.cat_module': 'Modul',
      'ui.sidebar.notebook.cat_phase': 'Phase',
      'ui.sidebar.notebook.missing_var_tooltip': 'Fehlende Variable für erfülltes Ziel',

      // --- NEEDS LABELS ---
      'ui.sidebar.needs.certainty': 'Sicherheit',
      'ui.sidebar.needs.variety': 'Abwechslung',
      'ui.sidebar.needs.significance': 'Status',
      'ui.sidebar.needs.connection': 'Verbindung',
      'ui.sidebar.needs.growth': 'Wachstum',
      'ui.sidebar.needs.contribution': 'Beitrag leisten',
      'ui.sidebar.speech.you': 'Du:',
      'ui.sidebar.speech.client': 'Kunde:',

      // --- NOTEBOOK SECTIONS ---
      'ui.notebook.section.current': 'Aktuelle Situation',
      'ui.notebook.section.gap': 'Lücke / Probleme',
      'ui.notebook.section.goal': 'Ziel / Wunsch',
      'ui.notebook.section.attempts': 'Bisherige Versuche',
      'ui.notebook.section.motivation': 'Motivation',
      'ui.notebook.section.verbatim': 'O-Töne',
      'ui.notebook.section.money': 'Budget',
      'ui.notebook.section.decision': 'Entscheidungsweg',
      'ui.notebook.section.commitment': 'Committment / Closing',

      // --- NOTEBOOK MODAL ---
      'ui.modal.notebook.title': 'Notizbuch(CRM)',
      'ui.modal.notebook.subtitle': 'Vollansicht & Variablen',
      'ui.modal.notebook.log_title': 'Extrahierte Variablen (LOG)',
      'ui.modal.notebook.log_subtitle': 'Live erfasste Datenpunkte aus dem Gespräch. Gelb = Pflichtfeld fehlt.',

      // --- SETUP OVERLAY ---
      'ui.setup.brand.subtitle': 'Sales Simulatio',
      'ui.setup.identity.title': '01 Identität',
      'ui.setup.config.title': '02 Konfiguration',
      'ui.setup.context.title': '03 Kontext & Wissen',
      'ui.setup.identity.closer_label': 'Closer Name',
      'ui.setup.identity.prospect_label': 'Interessenten Name',
      'ui.setup.identity.mode_label': 'Modus / Rolle',
      'ui.setup.identity.source_label': 'Quelle',
      'ui.setup.config.scenario_label': 'Szenario Rahmen',
      'ui.setup.config.lang_label': 'Sprache',
      'ui.setup.config.formality_label': 'Ansprache',
      'ui.setup.config.formality_formal': 'Formell',
      'ui.setup.config.formality_informal': 'Informell',
      'ui.setup.config.focus_label': 'Trainings Fokus',
      'ui.setup.context.persona_label': 'Ziel Persona',
      'ui.setup.context.kb_label': 'Angebot / Weitere Infos',
      'ui.setup.footer.launch': 'Jetzt Starten',
      'ui.setup.footer.audio_streaming': 'Audio',
      'ui.setup.config.groq_logic': 'Ultra-Schnell Logik (Groq)',
      'ui.setup.config.groq_text': 'Turbo Text Gen (GroQ)',
      'ui.setup.config.flow_linear': 'Linearer Flow',
      'ui.setup.config.flow_module': 'Spezifisches Modul',
      'ui.setup.config.flow_start': 'Start Modul (Flow)',
      'ui.setup.config.flow_stop': 'Stop Modul (Flow)',
      'ui.setup.config.module_start': 'Start Modul',
      'ui.setup.config.module_stop': 'Stop Modul',
      'ui.setup.role.prospect_label': 'Prospect (Coaching - AI führt)',
      'ui.setup.role.closer_label': 'Closer (Training - Du führst)',
      'ui.setup.source.calendar': 'Termin (Gebucht)',
      'ui.setup.source.ad': 'Werbeanzeige',
      'ui.setup.source.cold': 'Kaltakquise',
      'ui.setup.persona.placeholder': 'Profil wählen...',
      'ui.setup.persona.group_presets': 'Presets',
      'ui.setup.persona.group_custom': 'Custom',
      'ui.setup.persona.create_new': '+ Neues Profil Erstellen...',
      'ui.setup.kb.placeholder': 'Hier Infos zum Angebot, Preis und Rahmenbedingungen eingeben...',

      // --- PERSONA MODAL ---
      'ui.setup.persona.modal.title': 'Custom Persona Erstellen',
      'ui.setup.persona.modal.name': 'Name des Profils',
      'ui.setup.persona.modal.source': 'Quelle Text (Chat/E-Mail/Beschreibung)',
      'ui.setup.persona.modal.identifier': 'Zuordnung (wer ist der Interessent im Text)',
      'ui.setup.persona.modal.role': 'Rolle',
      'ui.setup.persona.modal.industry': 'Branche',
      'ui.setup.persona.modal.relationship': 'Beziehung',
      'ui.setup.persona.modal.cancel': 'Abbrechen',
      'ui.setup.persona.modal.save': 'Speichern',
      'ui.setup.persona.modal.generating': 'Generiere...',
      'ui.setup.persona.modal.desc_placeholder': 'Chatverlauf oder Beschreibung hier reinkopieren...',
      'ui.setup.persona.modal.id_placeholder': "z.B. 'Max' oder 'Person B'",
      'ui.setup.persona.modal.field_placeholders_role': 'CEO, Mutter...',
      'ui.setup.persona.modal.field_placeholders_industry': 'Handwerk...',
      'ui.setup.persona.modal.field_placeholders_rel': 'Kalt, Bestand...',

      // --- CHAT AREA ---
      'ui.chat.agent.name': 'Trainer AI',
      'ui.chat.button.finish': 'Beenden',
      'ui.chat.input.thinking': 'Denke nach...',
      'ui.chat.input.listening': 'Höre zu...',
      'ui.chat.input.placeholder': 'Schreiben oder sprechen...',
      'ui.chat.input.desktop_active': 'Desktop Audio aktiv... (Spiele Audio ab)',
      'ui.chat.tooltip.restart': 'Neustart (Einstellungen & Vorgeschichte behalten)',
      'ui.chat.tooltip.export_text': 'Text-Protokoll laden',
      'ui.chat.tooltip.export_audio': 'Audio laden (WAV)',
      'ui.chat.tooltip.mode_mic': 'Modus: Mikrofon (Standard)',
      'ui.chat.tooltip.mode_desktop': 'Modus: Desktop Audio (System Sound)',
      'ui.chat.tooltip.audio_toggle_on': 'Audio (TTS) AN',
      'ui.chat.tooltip.audio_toggle_off': 'Audio (TTS) AUS',
      'ui.chat.mic.tooltip_start': 'Mikrofon starten',
      'ui.chat.mic.tooltip_stop': 'Mikrofon stoppen',
      'ui.chat.desktop.tooltip_start': 'Desktop-Audio teilen & starten',
      'ui.chat.desktop.tooltip_stop': 'Desktop-Aufnahme stoppen',
      'ui.chat.error.no_desktop_audio': '⚠️ Kein System-Audio erkannt!\n\nBitte beim Teilen unten links "Audio teilen" aktivieren.',
      'ui.chat.error.desktop_prefix': 'Fehler bei Desktop Audio: ',

      // --- ANALYSIS VIEW ---
      'ui.analysis.title': 'Session-Analyse',
      'ui.analysis.score_rank': 'Gesamtbewertung / Rang',
      'ui.analysis.summary': 'Zusammenfassung',
      'ui.analysis.strengths': 'Stärken',
      'ui.analysis.weaknesses': 'Schwächen',
      'ui.analysis.top_questions': 'Top 3 Fragen (Closer)',
      'ui.analysis.turn_by_turn': 'Zug-Für-Zug-Analyse',
      'ui.analysis.feedback_label': 'Feedback:',
      'ui.analysis.better_label': 'Besser:',
      'ui.analysis.restart_button': 'Neue Session Starten',
      'ui.analysis.label.rank': 'Overall Rank',
      'ui.analysis.label.details_depth': 'Tiefe: ',
      'ui.analysis.label.details_emotion': 'Emotion: ',
      'ui.analysis.label.closer_move': 'Closer-Zug',
      'ui.analysis.label.prospect_resp': 'Antwort des Interessenten',
      'ui.analysis.speaker.ai': 'KI',
      'ui.analysis.speaker.user': 'Du',
      'ui.analysis.error.incomplete_title': 'Analyse unvollständig',
      'ui.analysis.error.incomplete_desc': 'Die KI-Antwort war zu lang und wurde abgeschnitten.',

      // --- LOGIC CONTEXT FALLBACKS ---
      'ui.context.default_topic': 'Ihr Thema',
      'ui.context.missing_prefix': 'das Fehlen von ',
      'ui.context.current_problem': 'das aktuelle Problem',
      'ui.context.solution_prefix': 'Lösung für ',
      'ui.context.default_goal': 'Ihr Ziel',
      'ui.context.subsystem.fallback_situation': 'die beschriebene Situation',
      'ui.context.subsystem.fallback_point': 'diesen Punkt',

      // Defficulty Overlay
      'ui.setup.config.difficulty_label': 'Schwierigkeit',
      'ui.setup.config.difficulty_rookie': 'Einsteiger',
      'ui.setup.config.difficulty_seller': 'Verkäufer',
      'ui.setup.config.difficulty_closer': 'Closer',
      'ui.setup.config.difficulty_elite': 'Elite Closer',

      // Internalisation Overlay
      'ui.setup.config.internalisation_label': 'Auswendig Lernen',
      'ui.setup.config.internalisation_none': 'Nein',
      'ui.setup.config.internalisation_random': 'Ja zufällig',
      'ui.setup.config.internalisation_fast': 'Ja geplant',

       // VAULT Anzeige
      'ui.hud.vault_label': 'Kontostand',
      'ui.hud.unit': 'Pkt',
      'ui.hud.quality_gate': 'Fortschritt',


      // Sidebar
      'ui.sidebar.notebook.history_divider': 'Verlauf Historie',
      'ui.sidebar.notebook.no_vars_extracted': 'Keine Variablen extrahiert.',
      'ui.sidebar.checkpoint.hidden_label': '🔒 Verdeckt',
      'ui.sidebar.checkpoint.reveal_tooltip': 'Klicken zum Aufdecken (-Punkte)',

      // Chat Area
      'ui.chat.hint_prefix': 'Empfehlung:',
      'ui.chat.live_connection_status': 'Live Connection',
      'ui.chat.header.phase_fallback': 'Phase',
      'ui.chat.goal.hidden_click': '🔒 Ziel verdeckt (Klicken)',


      // Setup Overlay App_neu
      'ui.setup.internalisation.hidden': 'Verdeckt',
      'ui.setup.error.persona_missing_fields': 'Name, Beschreibung und Mapping sind Pflichtfelder!',
      'ui.setup.error.missing_api_key': 'Bitte erst einen gültigen API Key eingeben.',
      'ui.setup.error.generation_failed': 'Fehler bei Generierung.',
      'ui.setup.brand.mission_control': 'Mission Control',
      'ui.setup.placeholder.me_closer': 'Ich (Closer)',
      'ui.setup.placeholder.ai_closer': 'KI (Closer)',
      'ui.setup.placeholder.ai_prospect': 'KI (Kunde)',
      'ui.setup.placeholder.me_prospect': 'Ich (Kunde)',
      'ui.setup.persona.mapping.title': 'Mapping: Wie heisst der Interessent im Text? (WICHTIG)',
      'ui.setup.persona.mapping.des': 	"Damit die KI weiß welche Rolle sie spielt, trage den Name des Interessenten aus dem Text hier ein. Beispiel: 'Max', 'Person B', 'Interessent'.",
      'ui.status.generating_opener': "Generiere Opener...",
      'ui.status.init_persona_psych': 'Initialisiere Persona-Psychologie...',
      'ui.setup.error.api_key_top': 'API Key oben eingeben.',

      // MISSING UI
      'ui.chat.deepness.target': 'Ziel',
      'ui.setup.api.groq_label': 'GROQ KEY',
      'ui.setup.api.openai_label': 'OPENAI KEY',
      'ui.setup.persona.modal.name_placeholder': 'z.B. Skeptischer CFO',
      'ui.setup.persona.modal.identifier_placeholder': "z.B. 'Kunde' oder 'Max'",
      'ui.setup.persona.modal.identifier_help': 'Wie wird der Kunde im Quelltext genannt? (z.B. "Max", "Kunde", "Person B"). Ohne dieses Feld weiß die KI nicht, wen sie spielen soll.',
      'ui.setup.internalisation.hidden_percentage': 'Verdeckt', // Für den Slider Label in App_new

      // LOGIC / SCORING
      'logic.score.penalty_reveal': 'Aufdecken',
      'logic.score.penalty_manual': 'Manuell erfüllt',
      'logic.score.goal_reached': 'Ziel erreicht',
      'logic.score.bonus_depth': ' (+Tiefe)',
      'logic.score.multiplier_tone': ' (x Ton)',
      'logic.score.bonus_speech': 'Redeanteil Top',
      'logic.score.bonus_efficiency': 'Effizienz-Bonus',
      'logic.score.combo_double': 'DOUBLE COMBO',
      'logic.score.combo_triple': 'TRIPLE COMBO',
      'logic.score.bonus_intervention': 'Einwand gelöst',
      'logic.score.bonus_tonality': 'Tonalität (Gesamt)',
      'logic.score.penalty_sandbox': 'Ghost Mode (1/5 Regel)',

      'ui.chat.error.mic_access': 'Mikrofon-Zugriff fehlgeschlagen:',
      'ui.chat.error.desktop_hardware': 'Hardware-Fehler bei Desktop Audio:',

      // DEUTSCH (de)
      'ui.setup.api.groq_placeholder': 'gsk_...',
      'ui.setup.api.openai_placeholder': 'sk-...',
      'ui.setup.placeholder.name_user': 'Dein Name',
      'ui.setup.placeholder.name_client': 'Kunden Name',
      'ui.setup.role.suffix_ai': ' (KI)',
      'ui.setup.role.suffix_me': ' (ICH)',
      'ui.scenario.business': 'Business / Coaching',
      'ui.scenario.b2b': 'B2B / Corporate',
      'ui.scenario.health': 'Health / B2C',
      'ui.scenario.improvement': 'Persönlichkeitsentwicklung',
      'ui.lang.de': 'Deutsch',
      'ui.lang.en': 'Englisch',
      'ui.sidebar.notebook.items_count': 'Einträge',
      'ui.analysis.turn_prefix': 'ZUG',

      'ui.chat.fallback_repeat': 'Könnten Sie das bitte wiederholen?',
      'ui.status.missing_module': 'Modul nicht gefunden',
      'ui.status.intervention_started': 'Intervention gestartet',
      'ui.status.intervention_done': 'Intervention abgeschlossen',
      'ui.debug.intervention_finished': 'Intervention beendet',
      'ui.trigger.phase': 'Ich bin mir nicht sicher.',

// --- SAVE & LOAD SYSTEM ---
      'ui.save.header': 'Gespeicherte Sessions (Lokal)',
      'ui.save.empty': 'Keine lokalen Spielstände gefunden.',
      'ui.save.label': 'Speicherstand',
      'ui.save.load_btn': 'Laden',
      'ui.save.delete_tooltip': 'Löschen',
      'ui.save.delete_confirm_title': 'Spielstand unwiderruflich löschen?',
      'ui.save.cancel': 'Abbrechen',
      'ui.save.confirm_delete': 'Ja, Löschen',
      'ui.save.delete_error': 'Löschen fehlgeschlagen.',
      'ui.save.load_success': "Spielstand '{key}' erfolgreich geladen! Klicke jetzt auf 'Starten'.",
      'ui.save.load_error': 'Fehler beim Laden des Spielstands.',
      'ui.save.save_success': '✅ Spielstand lokal gespeichert!',
      'ui.save.save_error_quota': '❌ Fehler beim Speichern (Speicher voll?)',
      'ui.save.save_error_alert': 'Speichern fehlgeschlagen! Der Verlauf ist evtl. zu lang für den LocalStorage.',
      
      // --- SESSION CONTROL MODAL ---
      'ui.modal.control.title': 'Mission Control',
      'ui.modal.control.timetravel_title': '⏳ Time Travel (Rückblende)',
      'ui.modal.control.confirm_jump': 'Wirklich Springen?',
      'ui.modal.control.confirm_desc': 'Klicken zum Bestätigen (Fortschritt geht verloren)',
      'ui.modal.control.id_prefix': 'ID:',
      'ui.modal.control.active_badge': 'Aktiv',
      'ui.modal.control.saves_title': '💾 Speicherstände',
      'ui.modal.control.save_current': 'Aktuellen Stand Speichern',

      'ui.modal.control.subline': 'ELITS MISSION CONTROL',
      'ui.modal.control.timeline_empty': 'Keine Historie verfügbar.',
      'ui.modal.control.show_less': 'Weniger anzeigen',
      'ui.modal.control.show_all_count': 'Alle anzeigen ({count})',


      "ui.status.loading": "Lade...",
      "ui.status.chaining": "Verknüpfe...",
      "ui.status.complete": "Komplett",
      "ui.status.save_loaded": "Spielstand geladen. Bitte Einstellungen prüfen und Starten.",
      "ui.error.timetravel_failed": "Zeitreise fehlgeschlagen.",
      "ui.error.load_failed": "Fehler beim Laden des Spielstands.",
      "ui.error.generic": "Fehler.",
      "ui.sidebar.action.save": "Speichern",
      "ui.save.load_tooltip": "Klicken zum Laden",
      "ui.debug.module_active": "Modul Aktiv",
      "ui.debug.module_response": "Modul Antwort",
      "ui.debug.silent_transition": "Stiller Übergang (Kein Input)",
      "ui.debug.subsystem_active": "Subsystem Aktiv",
      "ui.debug.intervention_aborted": "Intervention abgebrochen (Ziel erreicht)",
      "ui.debug.executing_subsystem": "Führe Subsystem-Schritt aus",
      "ui.debug.background_update": "Hintergrund-Update",
      "ui.debug.module_finished_pre": "Modul beendet (Pre-Recursion)",
      "ui.debug.closer_turn_complete": "Closer-Runde beendet",
      "ui.eval.deepness_missing": "Keine Tiefen-Analyse verfügbar (Daten fehlen).",
      "ui.eval.goal_not_met": "Ziel nicht erreicht.",
      "ui.eval.info_incomplete": "Information unvollständig.",
      "ui.mastery.mastered": "MASTERED: Voller Blindflug bestanden.",
      "ui.mastery.repeat_revealed": "REPEAT: {n}x gespickt. Level wiederholen.",
      "ui.mastery.repeat_manual": "REPEAT: {n}x aufgedeckt. Level wiederholen.",
      "ui.mastery.levelup": "LEVEL UP: Sauberer Durchlauf. Verdeckt: {a} -> {b}",
      "ui.chat.fallback_repeat_short": "Wiederholen bitte?",
      "ui.chat.fallback_not_understood": "Das habe ich nicht verstanden.",

      'ui.status.session_restored': 'Session fortgesetzt.',
      'ui.save.corrupt_error': 'Spielstand beschädigt oder ungültig.',
      
      'ui.mastery.no_data': 'Keine Daten.',
      'ui.error.no_active_module': 'Fehler: Kein aktives Modul gefunden.',
      'ui.status.module_completed_logic': 'Modul abgeschlossen.',
      'ui.error.hint_generation_failed': 'Konnte Tipp nicht generieren.',
      'ui.logic.intervention_step_fallback': 'Interventions-Schritt',
      
      'ui.analysis.no_turns': 'Keine vollständigen Züge gefunden.',
      'ui.analysis.fallback_title': 'Analyse unvollständig',
      'ui.analysis.fallback_summary': 'Die KI-Antwort war zu lang und wurde abgeschnitten.',
      'ui.analysis.failed_generic': 'Analyse fehlgeschlagen.',
      
      'ui.eval.vars_missing': 'Variablen fehlen/ungültig',
      'ui.prefix.module': '[Modul]',
      'ui.prefix.intervention': '[Unterbrechung]',
      'ui.suffix.completion': '[Abschluss]',
      'logic.score.suffix_tone': '(x Ton)',
    
      "ui.setup.button.mobile_interface": "📱 Mobiles Interface",
      "ui.setup.label.deepgram_key": "DEEPGRAM KEY",
      "ui.setup.placeholder.token": "Token...",
      "ui.setup.button.battle_deck": "BATTLE DECK",

      "ui.sidebar.settings.vad_label": "Pausenlänge",
      "ui.sidebar.settings.vad_fast": "Reaktiv",
      "ui.sidebar.settings.vad_slow": "Genügsam",
      "ui.toast.type.warning": "Hinweis",
      "ui.toast.type.info": "Info",

      "ui.mobile.input.placeholder": "Nachricht...",
      "ui.mobile.nav.chat": "Chat",
      "ui.mobile.nav.crm": "CRM",
      "ui.mobile.nav.deck": "Deck",
      "ui.mobile.nav.data": "Daten",
      "ui.mobile.nav.score": "Score",
      "ui.mobile.header.park_tooltip": "Modul Parken",

      "ui.mobile.notebook.subline": "CRM DASHBOARD",
      "ui.mobile.notebook.title": "Notizbuch",
      "ui.mobile.notebook.status.live": "LIVE INTEL",
      "ui.mobile.notebook.status.archive": "ARCHIV",
      "ui.mobile.notebook.divider.history": "Historie",

      "ui.mobile.deck.subline": "⚡ INTERVENTION DECK",
      "ui.mobile.deck.title": "Battledeck",
      "ui.mobile.deck.badge.detected_missing": "⚠️ ERKANNT (FEHLT)",
      "ui.mobile.deck.badge.recommended": "🎯 EMPFOHLEN",
      "ui.mobile.deck.empty": "DECK LEER",

      "ui.mobile.analytics.subline": "SESSION METRIKEN",
      "ui.mobile.analytics.title": "Analytics",
      "ui.mobile.analytics.card.psychology": "PSYCHOLOGIE",
      "ui.mobile.analytics.no_data": "Keine Daten verfügbar.",
      "ui.mobile.analytics.card.needs": "ANTRIEB & BEDÜRFNISSE",

      "ui.mobile.score.subline": "PERFORMANCE REPORT",
      "ui.mobile.score.title": "Scorecard",
      "ui.mobile.score.empty": "Keine Daten.",

      "ui.mobile.dynamics.title": "DYNAMIK",
      "ui.mobile.dynamics.legend.you": "DU (CLOSER)",
      "ui.mobile.dynamics.legend.client": "KUNDE",
      "ui.mobile.dynamics.donut.share": "Anteil",
      "ui.mobile.dynamics.kpi.goals": "ZIELE",
      "ui.mobile.dynamics.kpi.manual": "MANUELL",
      "ui.mobile.dynamics.kpi.avg_turns": "Ø TURNS",
      "ui.mobile.dynamics.kpi.total_turns": "TURNS",

      "ui.radar.trust": "VERTRAUEN",
      "ui.radar.offer": "ANGEBOT",
      "ui.radar.clarity": "KLARHEIT",
      "ui.radar.pain": "SCHMERZ",
      "ui.radar.ownership": "OWNER",
      "ui.radar.self": "SELF",
      "ui.radar.openness": "OFFENHEIT",

      "ui.deck.subline": "⚡ INTERVENTION DECK",
      "ui.deck.title": "Battledeck",
      "ui.deck.capacity_label": "MEMORY SLOTS",
      "ui.deck.loadout_title": "AKTIVES LOADOUT",
      "ui.deck.button.clear": "LEEREN",
      "ui.deck.slot.empty": "OFFENER SLOT",
      "ui.deck.count_suffix": " KARTEN",
      "ui.deck.equipped_suffix": " AKTIV",
      "ui.deck.button.cancel": "ABBRECHEN",
      "ui.deck.button.start": "SYSTEM STARTEN"

    },

    en: {

      // --- REPEAT

"ui.feedback.repeat.module_missing": "REPEAT: ❌ Module not passed. Score too low.",
"ui.feedback.repeat.api_fallback": "Not passed: The module will be repeated shortly.",


      // --- RUN OUTCOME MODAL ---

      'ui.run_outcome.header.kicker': 'RUN OUTCOME',
      'ui.run_outcome.header.title': 'REVIEW CONVERSATION',

      'ui.run_outcome.assessment.heading': 'Conversation Assessment',

      'ui.run_outcome.select.label': 'CONVERSATION OUTCOME',
      'ui.run_outcome.select.placeholder': 'Select the conversation outcome.',

      'ui.run_outcome.confirm.button': 'Confirm Conversation Outcome',
      'ui.run_outcome.confirmed_prefix': 'Confirmed:',
      'ui.run_outcome.change': 'Change',

      'ui.run_outcome.self_assessment': 'How would you rate your own conversation?',
      'ui.run_outcome.last_question': 'Final Question',

      'ui.run_outcome.revenue.label': 'Confirm / Correct Revenue',
      'ui.run_outcome.revenue.placeholder': 'e.g. 2500',

      'ui.run_outcome.feedback.optional_placeholder': 'Optional feedback...',
      'ui.run_outcome.feedback.answer_placeholder': 'Your answer...',
      'ui.run_outcome.feedback.save': 'Save Feedback',

      // --- RUN OUTCOME VALIDATION ---

      'ui.run_outcome.error.select': 'Please select the conversation outcome first.',
      'ui.run_outcome.error.confirm': 'Please confirm the conversation outcome first.',
      'ui.run_outcome.error.revenue': 'Please confirm or correct the revenue.',
      'ui.run_outcome.error.answer': 'Please answer the final question.',

      // --- RUN OUTCOME QUESTIONS ---

      'ui.run_outcome.question.sold': 'Great, is the revenue correct, or did I misunderstand something?',

      'ui.run_outcome.question.ended': 'It is a shame you are leaving already. To continuously improve the results for our users, please tell us what you would like us to do differently. Thank you.',

      'ui.run_outcome.question.logout': 'Oh, you are logging out? To continuously improve the results for our users, please tell us what you would like us to do differently. Many thanks.',

      'ui.run_outcome.question.follow_up': 'You can still close this deal. What prevented the prospect from being ready to make a decision today?',

      'ui.run_outcome.question.disqualified': 'Not every prospect is a good fit. Why did you disqualify this prospect?',

      'ui.run_outcome.question.failed': 'What do you think was the reason?',

      'ui.run_outcome.question.sold_revenue': 'Great, I understood the revenue to be {revenue}. Is that correct, or did I misunderstand something?',



     // --- Mobile Notebook

'ui.mobile.notebook.required_placeholder': 'Required field missing...',


     // --- TenantUpdates

// ============================================================
// TENANT UPDATES – EN
// ============================================================

'ui.tenant_updates.field.name': 'Company name',
'ui.tenant_updates.field.plz': 'Postal code',
'ui.tenant_updates.field.street': 'Street',
'ui.tenant_updates.field.street_no': 'House number',
'ui.tenant_updates.field.city': 'City',
'ui.tenant_updates.field.vat_id': 'Tax number',
'ui.tenant_updates.field.phone': 'Phone number',
'ui.tenant_updates.field.billing_email': 'Billing email',
'ui.tenant_updates.field.readonly': 'Read-only',

'ui.tenant_updates.section.company': '01 Company',
'ui.tenant_updates.section.address': '02 Address',
'ui.tenant_updates.section.contact': '03 Contact & billing',

'ui.tenant_updates.error.session_expired': 'Your session has expired. Please sign in again.',
'ui.tenant_updates.error.admin_only': 'You are not permitted to change these company details. This feature is available to administrators only.',
'ui.tenant_updates.error.tenant_not_found': 'The company details could not be associated with your account. Please reload the page or contact support.',
'ui.tenant_updates.error.no_changes': 'No changes were detected.',
'ui.tenant_updates.error.invalid_input': 'At least one entry is invalid. Please check the highlighted fields.',
'ui.tenant_updates.error.conflict': 'The data has been changed in the meantime. Please reload the latest version and repeat your changes.',
'ui.tenant_updates.error.rate_limited': 'Too many requests were sent. Please try again shortly.',
'ui.tenant_updates.error.network': 'The server is currently unavailable. Please check your internet connection and try again.',
'ui.tenant_updates.error.invalid_response': 'The server response could not be processed. Please reload the page.',
'ui.tenant_updates.error.server_load': 'The company details could not be loaded due to a server error. Please try again.',
'ui.tenant_updates.error.server_save': 'The changes could not be saved due to a server error. Your previous data remains unchanged.',
'ui.tenant_updates.error.default_load': 'The company details could not be loaded. Please try again.',
'ui.tenant_updates.error.default_save': 'The changes could not be saved. Your previous data remains unchanged.',
'ui.tenant_updates.error.no_data': 'There are currently no company details available for this account. Please reload the page or contact support.',

'ui.tenant_updates.feedback.incomplete_title': 'Incomplete information',
'ui.tenant_updates.feedback.incomplete_prefix': 'Please fill in the following fields: ',
'ui.tenant_updates.feedback.incomplete_suffix': '.',
'ui.tenant_updates.feedback.saved_title': 'Changes saved',
'ui.tenant_updates.feedback.saved_message': 'Your company details have been updated.',
'ui.tenant_updates.feedback.saved_reload': 'The data was saved. Reload the page to see the latest details everywhere.',
'ui.tenant_updates.feedback.save_failed_title': 'Unable to save',

'ui.tenant_updates.state.loading_title': 'Loading company details',
'ui.tenant_updates.state.wait': 'Please wait a moment.',
'ui.tenant_updates.state.unavailable_title': 'Company details unavailable',
'ui.tenant_updates.state.reload_failed': 'Could not reload the latest details',

'ui.tenant_updates.action.retry_upper': 'RELOAD',
'ui.tenant_updates.action.retry': 'Reload',
'ui.tenant_updates.action.discard': 'DISCARD',
'ui.tenant_updates.action.save_changes': 'SAVE CHANGES',
'ui.tenant_updates.action.cancel': 'CANCEL',
'ui.tenant_updates.action.saving': 'SAVING …',
'ui.tenant_updates.action.save_now': 'SAVE NOW',

'ui.tenant_updates.change.unsaved': 'Unsaved changes',
'ui.tenant_updates.change.single': '1 field was changed.',
'ui.tenant_updates.change.multiple': '{count} fields were changed.',

'ui.tenant_updates.confirm.title': 'Save changes?',
'ui.tenant_updates.confirm.fields': 'The following fields will be updated:',

'ui.tenant_updates.mobile.edit_aria': 'Edit company details',
'ui.tenant_updates.mobile.title': 'Company details',
'ui.tenant_updates.mobile.close_aria': 'Close company details',



     // --- SaveGames

// --- SAVE & LOAD SYSTEM ---

'ui.save_browser.mobile.filter': 'FILTER',
'ui.save_browser.mobile.previous': 'Previous savegame',
'ui.save_browser.mobile.next': 'Next savegame',
'ui.save_browser.mobile.show': 'Show savegame {index}',


'ui.save_browser.access.asset': 'Asset',
'ui.save_browser.access.creator': 'Creator',
'ui.save_browser.access.missing': 'Asset access missing',
'ui.save_browser.access.purchase_hint': 'Please start a trial or purchase the technique to regain access.',

'ui.save_browser.action.blocked': 'BLOCKED',
'ui.save_browser.action.cancel': 'CANCEL',
'ui.save_browser.action.confirm_delete': 'YES, DELETE',
'ui.save_browser.action.reactivate': 'REACTIVATE',

'ui.save_browser.card.industry': 'Industry',
'ui.save_browser.card.module_title': 'Module title',
'ui.save_browser.card.person_context': 'Contact / Persona',
'ui.save_browser.card.points': 'Points',

'ui.save_browser.count': 'Savegames',

'ui.save_browser.date.all': 'ALL',
'ui.save_browser.date.end': 'END',
'ui.save_browser.date.last_24h': '24H',
'ui.save_browser.date.last_30d': '30D',
'ui.save_browser.date.last_7d': '7D',
'ui.save_browser.date.start': 'START',

'ui.save_browser.empty.no_matches': 'No matching savegames found.',

'ui.save_browser.error.deactivate': 'Savegame could not be deactivated.',
'ui.save_browser.error.reactivate': 'Savegame could not be reactivated.',

'ui.save_browser.fallback.no_industry': 'No industry',
'ui.save_browser.fallback.no_module_title': 'Module title not saved',
'ui.save_browser.fallback.no_offer': 'No offer',
'ui.save_browser.fallback.no_person_context': 'Person / context not saved',
'ui.save_browser.fallback.unknown_asset': 'Unknown asset',

'ui.save_browser.filter.contact_touchpoint': 'CONTACT TOUCHPOINT',
'ui.save_browser.filter.difficulty': 'DIFFICULTY',
'ui.save_browser.filter.execution_mode': 'MODE',
'ui.save_browser.filter.industry': 'INDUSTRY',
'ui.save_browser.filter.module_title': 'MODULE TITLE',
'ui.save_browser.filter.offer': 'OFFER',
'ui.save_browser.filter.person_context': 'CONTACT / PERSONA',
'ui.save_browser.filter.sales_stage': 'SALES STAGE',

'ui.save_browser.search.label': 'SEARCH',
'ui.save_browser.search.placeholder': 'Search company, contact, persona, offer, industry or module...',

'ui.save_browser.tab.context': 'CONTEXT',
'ui.save_browser.tab.period': 'PERIOD',
'ui.save_browser.tab.training': 'TRAINING',

'ui.save_browser.title': 'SAVEGAMES',

'ui.save_browser.toggle.active': 'ACTIVE',
'ui.save_browser.toggle.inactive': 'INACTIVE',
'ui.save_browser.toggle.showing_active': 'Active savegames are displayed',
'ui.save_browser.toggle.showing_inactive': 'Inactive savegames are displayed',


      // --- AdminVerwaltung


'ui.admin_verwaltung.action.delete': 'DELETE',
'ui.admin_verwaltung.details.assigned_since': 'Assigned since',
'ui.admin_verwaltung.details.billing_period': 'Billing period',
'ui.admin_verwaltung.details.current_period': 'Current period',
'ui.admin_verwaltung.details.email': 'Email',
'ui.admin_verwaltung.details.hide': 'Hide details',
'ui.admin_verwaltung.details.license_start': 'License start',
'ui.admin_verwaltung.details.phone': 'Phone',
'ui.admin_verwaltung.details.price': 'Price',
'ui.admin_verwaltung.details.show': 'Show details',
'ui.admin_verwaltung.draft.discard': 'DISCARD',
'ui.admin_verwaltung.draft.save': 'SAVE CHANGES',
'ui.admin_verwaltung.draft.saving': 'SAVING...',
'ui.admin_verwaltung.draft.unsaved': 'Unsaved changes',
'ui.admin_verwaltung.filter.all_members': 'All members',
'ui.admin_verwaltung.invite.invite': 'Invite member',
'ui.admin_verwaltung.invite.invite_upper': 'INVITE MEMBER',
'ui.admin_verwaltung.invite.share': 'Share link',
'ui.admin_verwaltung.invite.share_title': 'Sellermind Tenant link',
'ui.admin_verwaltung.invite.share_upper': 'SHARE LINK',
'ui.admin_verwaltung.label.actions': 'Actions',
'ui.admin_verwaltung.label.license': 'License',
'ui.admin_verwaltung.label.member': 'Member',
'ui.admin_verwaltung.label.seats': 'Seats',
'ui.admin_verwaltung.label.status': 'Status',
'ui.admin_verwaltung.license.no_free': 'no free licenses',
'ui.admin_verwaltung.license.none': 'No license',
'ui.admin_verwaltung.license.open': 'Available:',
'ui.admin_verwaltung.license.swap_with': 'Swap license with...',
'ui.admin_verwaltung.member.activate': 'Activate member',
'ui.admin_verwaltung.member.deactivate': 'Deactivate member',
'ui.admin_verwaltung.member.profile_image': 'Profile image',
'ui.admin_verwaltung.member.search': 'Search members...',
'ui.admin_verwaltung.member.unknown': 'Unknown',
'ui.admin_verwaltung.modal.close': 'Close User Management modal',
'ui.admin_verwaltung.state.loading': 'Loading admin management...',
'ui.admin_verwaltung.state.no_data': 'No admin data loaded.',
'ui.admin_verwaltung.state.no_members': 'No members available for this filter.',
'ui.admin_verwaltung.status.active': 'Active',
'ui.admin_verwaltung.status.inactive': 'Deactivated',
'ui.admin_verwaltung.filter.without_license': 'Without license',
'ui.admin_verwaltung.filter.assigned_license': 'License assigned',



      // --- AdminLizenzen


// ============================================================
// ADMIN LICENSES – EN
// ============================================================

'ui.admin_licenses.action.cancel_team': 'Cancel team license',
'ui.admin_licenses.action.current_license': 'Current license',
'ui.admin_licenses.action.downgrade': 'Downgrade now',
'ui.admin_licenses.action.downgrade_to': 'Downgrade to {title} now',
'ui.admin_licenses.action.undo_change': 'Undo change',
'ui.admin_licenses.action.upgrade': 'Upgrade now',
'ui.admin_licenses.action.upgrade_to': 'Upgrade to {title} now',

'ui.admin_licenses.aria.active_license': 'Active license',
'ui.admin_licenses.aria.benefits': 'Benefits',
'ui.admin_licenses.aria.billing_period': 'Billing period',
'ui.admin_licenses.aria.close_license_modal': 'Close license dialog',
'ui.admin_licenses.aria.comparison': 'License comparison',
'ui.admin_licenses.aria.comparison_table': 'License comparison table',
'ui.admin_licenses.aria.enabler_benefits': 'Enabler benefits',
'ui.admin_licenses.aria.included': 'Included',
'ui.admin_licenses.aria.license_management': 'License management',
'ui.admin_licenses.aria.next_card': 'Next card',
'ui.admin_licenses.aria.next_license': 'Show next license',
'ui.admin_licenses.aria.next_solo_variant': 'Next solo add-on option',
'ui.admin_licenses.aria.not_included': 'Not included',
'ui.admin_licenses.aria.previous_card': 'Previous card',
'ui.admin_licenses.aria.previous_license': 'Show previous license',
'ui.admin_licenses.aria.previous_solo_variant': 'Previous solo add-on option',
'ui.admin_licenses.aria.pricing_tabs': 'License categories',
'ui.admin_licenses.aria.solo_level': 'Select solo level',
'ui.admin_licenses.aria.solo_variant': 'Solo add-on option',
'ui.admin_licenses.aria.switch_license': 'Switch license',
'ui.admin_licenses.aria.usage_bar': '{label}: Usage {reached}; contract limit {limit}; scale {scale}',
'ui.admin_licenses.aria.usage_limits': 'Usage and contract limits',

'ui.admin_licenses.confirm.continue': 'Would you like to continue?',
'ui.admin_licenses.confirm.replace_upgrade': 'and then replaced with the selected upgrade. ',
'ui.admin_licenses.confirm.revert_first': 'The existing change will first be reversed ',
'ui.admin_licenses.confirm.scheduled_prefix': 'A change has already been scheduled for this license. ',

'ui.admin_licenses.date.period_end_unknown': 'End of the current billing period',

'ui.admin_licenses.error.contract_load_failed': 'Could not load the license contract.',
'ui.admin_licenses.error.execution_failed': 'Could not execute the license change.',
'ui.admin_licenses.error.member_email_not_found': 'LICENSE_INFO.member_email was not found in get_members_from_tenant.',
'ui.admin_licenses.error.members_load_failed': 'Could not load the member list.',
'ui.admin_licenses.error.missing_member_email': 'LICENSE_INFO.member_email is missing.',
'ui.admin_licenses.error.revert_failed': 'Could not reverse the scheduled change.',

'ui.admin_licenses.internal_error.cancel_requested_downgrade_failed': 'Could not reverse the scheduled downgrade.',
'ui.admin_licenses.internal_error.current_member_billing_period_missing': 'The current member license billing period is missing.',
'ui.admin_licenses.internal_error.current_member_license_missing': 'The current member license is missing.',
'ui.admin_licenses.internal_error.current_member_period_end_at_missing_for_member_downgrade': 'The member billing-period end is missing for the downgrade.',
'ui.admin_licenses.internal_error.current_member_period_end_at_missing_for_solo_team_period_end_change': 'The member billing-period end is missing for the Solo/Team change.',
'ui.admin_licenses.internal_error.current_member_period_end_at_missing_for_team_downgrade': 'The member billing-period end is missing for the team downgrade.',
'ui.admin_licenses.internal_error.execute_license_action_plan_failed': 'Could not execute the license change plan.',
'ui.admin_licenses.internal_error.get_license_management_contract_failed': 'Could not load the license contract.',
'ui.admin_licenses.internal_error.get_members_from_tenant_failed': 'Could not load the member list.',
'ui.admin_licenses.internal_error.license_management_contract_missing_required_keys': 'The license contract is missing required information.',
'ui.admin_licenses.internal_error.member_license_action_not_determinable': 'The member license change could not be determined.',
'ui.admin_licenses.internal_error.paid_team_license_requires_paid_member_license': 'A paid team license requires a paid member license.',
'ui.admin_licenses.internal_error.purchase_team_license_failed': 'Could not change the team license.',
'ui.admin_licenses.internal_error.request_license_downgrade_failed': 'Could not request the license downgrade.',
'ui.admin_licenses.internal_error.request_license_upgrade_failed': 'Could not request the license upgrade.',
'ui.admin_licenses.internal_error.target_email_missing': 'The target email address is missing.',
'ui.admin_licenses.internal_error.target_member_license_not_found': 'The selected member license was not found.',
'ui.admin_licenses.internal_error.team_license_action_not_determinable': 'The team license change could not be determined.',

'ui.admin_licenses.label.active': 'Active',
'ui.admin_licenses.label.current': 'Current:',
'ui.admin_licenses.label.enabler': 'Enabler:',
'ui.admin_licenses.label.feature': 'Feature',
'ui.admin_licenses.label.per_month': '/month',
'ui.admin_licenses.label.period_monthly': 'Monthly',
'ui.admin_licenses.label.period_once': 'One-time',
'ui.admin_licenses.label.support': 'Support',

'ui.admin_licenses.notice.already_scheduled': 'A change has already been scheduled for this license.',
'ui.admin_licenses.notice.binding_click': 'Clicking the button makes the change binding.',
'ui.admin_licenses.notice.downgrade_same_period': 'The current license is cancelled immediately, effective at the end of the billing period on {date}. The license will then automatically downgrade to {license}. Clicking the button makes the change binding and it cannot be undone.',
'ui.admin_licenses.notice.ends_at_period': 'Cancellation takes effect at the end of the billing period on {date}.',
'ui.admin_licenses.notice.no_change_current': 'Current license. No change will be made.',
'ui.admin_licenses.notice.planned_effective': '{base} The change will take effect on {date}.{target} Further changes are blocked until then.',
'ui.admin_licenses.notice.renewal_appendix': 'The agreement renews automatically at the end of each billing period without requiring cancellation. Cancellation is possible monthly. Refunds are excluded.',
'ui.admin_licenses.notice.selected_license': 'the selected license',
'ui.admin_licenses.notice.target': ' Target: {license}{billing}.',
'ui.admin_licenses.notice.team_cancellation': 'If you cancel the team license, it will be downgraded to Solo at the end of the current billing period on {date}. Team caps will be forfeited. The downgrade can only take place if all additional team members have been deactivated by then and only the admin account remains active.',
'ui.admin_licenses.notice.upgrade_immediate': 'The current license is cancelled with immediate effect. The prorated amount will be credited against the new contract in the first billing period. The license will then automatically upgrade to {license}. Clicking the button makes the change binding and it cannot be undone.',
'ui.admin_licenses.notice.yearly_to_monthly_downgrade': 'Cancellation is requested immediately and takes effect at the end of the billing period on {date}. The license will then automatically downgrade to {license}. Limits will be reset. Clicking the button makes this change binding and it cannot be undone.',
'ui.admin_licenses.notice.yearly_to_monthly_upgrade': 'Cancellation is requested immediately and takes effect at the end of the billing period on {date}. The license will then automatically upgrade to {license}. Clicking the button makes this change binding and it cannot be undone.',

'ui.admin_licenses.placeholder.search_member': 'Search for member',

'ui.admin_licenses.status.active': 'Active',
'ui.admin_licenses.status.change_updated': 'License change updated.',
'ui.admin_licenses.status.expired': 'Expired',
'ui.admin_licenses.status.inactive': 'Inactive',
'ui.admin_licenses.status.loading_contract': 'Loading license contract...',
'ui.admin_licenses.status.loading_members': 'Loading member list...',
'ui.admin_licenses.status.no_cards': 'No cards available for this category.',
'ui.admin_licenses.status.no_change_required': 'No change required.',
'ui.admin_licenses.status.no_contract': 'No license contract loaded.',
'ui.admin_licenses.status.planned_reversed': 'The scheduled change was reversed.',
'ui.admin_licenses.status.processing': 'Processing...',
'ui.admin_licenses.status.reverting_existing': 'Reversing the existing license change...',
'ui.admin_licenses.status.reverting_planned': 'Reversing the scheduled change...',
'ui.admin_licenses.status.updating_contract': 'Processing license change. Updating contract data...',

'ui.admin_licenses.summary.enabler': 'Enabler: {label}',
'ui.admin_licenses.summary.enabler_empty': 'Enabler:',
'ui.admin_licenses.summary.main_empty': 'Main license: -',
'ui.admin_licenses.summary.main_solo': 'Main license Solo: {license}',
'ui.admin_licenses.summary.main_team': 'Main license Team: {license}',
'ui.admin_licenses.summary.member': 'Member: {license} {status}',
'ui.admin_licenses.summary.member_empty': 'Member: -',



     // --- DynamicsCard

      "ui.analytics.dynamics.title": "DYNAMICS",
      "ui.analytics.dynamics.legend.you": "YOU (CLOSER)",
      "ui.analytics.dynamics.legend.client": "CLIENT",
      "ui.analytics.dynamics.kpi.goals": "GOALS",
      "ui.analytics.dynamics.kpi.manual": "MANUAL",
      "ui.analytics.dynamics.kpi.avg_turns": "Ø TURNS",
      "ui.analytics.dynamics.kpi.total_turns": "TURNS",





     // --- Overlay


"ui.overlay.mobile.sections.identity": "01 IDENTITY",
"ui.overlay.mobile.sections.call_review": "CALL REVIEW",
"ui.overlay.mobile.sections.offers": "OFFERS",
"ui.overlay.mobile.sections.personas": "PERSONAS",
"ui.overlay.mobile.sections.profile": "PROFILE",
"ui.overlay.mobile.setup.language_label": "LANGUAGE",
"ui.overlay.mobile.setup.persona_label": "PERSONA",
"ui.overlay.mobile.shop.card_shop": "CARD SHOP",
"ui.overlay.mobile.admin.heading": "ADMIN",
"ui.overlay.mobile.navigation.nav": "NAV",
"ui.overlay.mobile.audio.audio": "AUDIO",
"ui.overlay.mobile.footer.checking_limit": "CHECKING LIMIT...",

      'ui.overlay.desktop.admin.keys_saved': 'Keys saved.',
      'ui.overlay.desktop.offer_editor.error_invalid_offer_json': 'The saved offer snapshot contains invalid offer JSON.',
      'ui.overlay.desktop.offer_editor.error_missing_canonical_snapshot': 'The offer was saved, but no canonical offer snapshot was returned.',
      'ui.overlay.desktop.offer_editor.error_missing_offer_id': 'The offer was saved, but no offer ID was returned.',
      'ui.overlay.desktop.offer_editor.error_missing_offer_roi_cases': 'The offer’s ROI cases are unavailable.',
      'ui.overlay.desktop.offer_editor.error_missing_roi_cases': 'Saved ROI cases are unavailable.',
      'ui.overlay.desktop.offer_editor.error_missing_saved_snapshot': 'The saved offer snapshot is unavailable.',
      'ui.overlay.desktop.offer_editor.error_no_selected_offer': 'No offer selected for editing.',
      'ui.overlay.desktop.offer_editor.error_offer_unavailable': 'The complete offer is unavailable.',
      'ui.overlay.desktop.offer_editor.error_required_fields': 'Please complete at least offer name, industry, offer category and knowledge base.',
      'ui.overlay.desktop.offer_editor.error_save_failed': 'The offer could not be saved.',
      'ui.overlay.desktop.persona_editor.error_choose_backup_voice': 'Please select a backup voice.',
      'ui.overlay.desktop.persona_editor.error_choose_voice': 'Please select a suitable voice.',
      'ui.overlay.desktop.persona_editor.error_no_selected_persona': 'No persona selected for editing.',
      'ui.overlay.desktop.persona_editor.error_save_failed': 'The persona could not be saved.',
      'ui.overlay.desktop.persona_editor.error_session_expired': 'Session expired. Please reload the page and try again.',
      'ui.overlay.desktop.persona_editor.error_update_failed': 'The persona could not be updated.',
      'ui.overlay.desktop.persona_editor.required_description': 'Description / context',
      'ui.overlay.desktop.persona_editor.required_fields_prefix': 'Please complete the following required fields:',
      'ui.overlay.desktop.persona_editor.required_identifier': 'Conversation identifier',
      'ui.overlay.desktop.persona_editor.required_name': 'Persona name',
      'ui.overlay.desktop.persona_editor.required_psychology': 'Persona psychology',
      'ui.overlay.desktop.persona_editor.required_role': 'Persona role',
      'ui.overlay.desktop.profile.error_crop_blob_failed': 'The image could not be processed.',
      'ui.overlay.desktop.profile.error_crop_canvas_failed': 'Image processing could not be initialized.',
      'ui.overlay.desktop.profile.error_crop_failed': 'The image could not be cropped.',
      'ui.overlay.desktop.profile.error_crop_image_load_failed': 'The image could not be loaded.',
      'ui.overlay.desktop.profile.error_delete_failed': 'The image could not be deleted.',
      'ui.overlay.desktop.profile.error_delete_image_failed': 'The image could not be deleted.',
      'ui.overlay.desktop.profile.error_invalid_file_type': 'Please use JPG, PNG or WebP.',
      'ui.overlay.desktop.profile.error_max_file_size': 'The image must not exceed 5 MB.',
      'ui.overlay.desktop.profile.error_missing_crop_source': 'The image source is missing.',
      'ui.overlay.desktop.profile.error_referral_failed': 'The referral link could not be loaded.',
      'ui.overlay.desktop.profile.error_save_image_failed': 'The image could not be saved.',
      'ui.overlay.desktop.profile.error_upload_failed': 'The image upload failed.',
      'ui.overlay.desktop.training.error_monthly_limit_reached': 'The monthly live conversation limit has been reached.',
      'ui.overlay.desktop.admin.admin': '02 ADMIN',
      'ui.overlay.desktop.admin.gemini_api_key': 'Gemini API key',
      'ui.overlay.desktop.admin.lizenz_management': 'LICENSE MANAGEMENT',
      'ui.overlay.desktop.admin.placeholder_deepgram_key': 'Deepgram key',
      'ui.overlay.desktop.admin.placeholder_gemini_key_optional': 'Gemini key (optional)',
      'ui.overlay.desktop.admin.placeholder_groq_key_optional': 'Groq key (optional)',
      'ui.overlay.desktop.admin.placeholder_openai_key_sk': 'OpenAI key (sk-...)',
      'ui.overlay.desktop.admin.speichern_schliessen': 'Save & close',
      'ui.overlay.desktop.admin.system_api_keys': 'SYSTEM API KEYS',
      'ui.overlay.desktop.admin.tenant_einstellungen': 'TENANT SETTINGS',
      'ui.overlay.desktop.admin.user_management': 'USER MANAGEMENT',
      'ui.overlay.desktop.audio.audio_stream': 'AUDIO STREAM',
      'ui.overlay.desktop.audio.audio_tuning': 'AUDIO TUNING',
      'ui.overlay.desktop.audio.geschwindigkeit': 'SPEED',
      'ui.overlay.desktop.audio.langsam': 'Slow',
      'ui.overlay.desktop.audio.schnell': 'Fast',
      'ui.overlay.desktop.audio.system': 'SYSTEM',
      'ui.overlay.desktop.audio.tooltip_stimmeinstellung': 'Voice setting',
      'ui.overlay.desktop.profile.profile_image_alt': 'Profile image',
      'ui.overlay.desktop.offers.showing_inactive': 'Showing inactive offers',
      'ui.overlay.desktop.personas.showing_inactive': 'Showing inactive personas',
      'ui.overlay.desktop.offer_editor.edit_offer': 'EDIT OFFER',
      'ui.overlay.desktop.offer_editor.save_changes': 'SAVE CHANGES',
      'ui.overlay.desktop.rescue.analyzing': 'Analyzing...',
      'ui.overlay.desktop.audio.loading': 'LOADING...',
      'ui.overlay.desktop.profile.language_update_failed': 'The language could not be saved.',
      'ui.overlay.desktop.profile.selling_languages_update_failed': 'The conversation languages could not be saved.',
      'ui.overlay.desktop.audio.enter_api_key': 'Please enter an API key first.',
      'ui.overlay.desktop.audio.generation_failed': 'Audio could not be generated.',
      'ui.overlay.desktop.audio.playback_error': 'Playback system error: ',
      'ui.overlay.desktop.rescue.text_too_short': 'Text is too short!',
      'ui.overlay.desktop.rescue.select_script_first': 'Please select a script first.',
      'ui.overlay.desktop.offers.showing_active': 'Showing active offers',
      'ui.overlay.desktop.personas.showing_active': 'Showing active personas',
      'ui.overlay.desktop.offer_editor.create_offer': 'CREATE OFFER',
      'ui.overlay.desktop.offer_editor.save_offer': 'SAVE OFFER',
      'ui.overlay.desktop.rescue.start_import': 'Start import',
      'ui.overlay.desktop.audio.play_test_audio': 'PLAY TEST AUDIO',
      'ui.overlay.desktop.gap_training.ausfuehrung': 'Execution',
      'ui.overlay.desktop.gap_training.auswahl': 'Selection',
      'ui.overlay.desktop.gap_training.gespraech_importieren': 'IMPORT CONVERSATION',
      'ui.overlay.desktop.gap_training.luecken_bearbeiten': '3 Work on gaps',
      'ui.overlay.desktop.gap_training.luecken_erkennung': '📂 Gap detection',
      'ui.overlay.desktop.gap_training.placeholder_funktion_auswaehlen': 'Select function',
      'ui.overlay.desktop.gap_training.script': 'Script',
      'ui.overlay.desktop.legacy_setup.angebot': 'OFFER',
      'ui.overlay.desktop.legacy_setup.call_review': '03 CALL REVIEW',
      'ui.overlay.desktop.legacy_setup.company_optional': 'COMPANY OPTIONAL',
      'ui.overlay.desktop.legacy_setup.contact_touchpoint': 'CONTACT TOUCHPOINT',
      'ui.overlay.desktop.legacy_setup.einstiegspunkt': 'ENTRY POINT',
      'ui.overlay.desktop.legacy_setup.gespraech_importieren': 'IMPORT CONVERSATION',
      'ui.overlay.desktop.legacy_setup.luecken_erkennung': '📂 Gap detection',
      'ui.overlay.desktop.legacy_setup.no_scripts_found': 'No scripts found',
      'ui.overlay.desktop.legacy_setup.placeholder_waehle_ein_skript': 'Choose a script...',
      'ui.overlay.desktop.legacy_setup.placeholder_z_b_muster_gmbh': 'e.g. Example GmbH',
      'ui.overlay.desktop.legacy_setup.sales_stage': 'SALES STAGE',
      'ui.overlay.desktop.legacy_setup.script': 'SCRIPT',
      'ui.overlay.desktop.legacy_setup.script_start': '02 SCRIPT START',
      'ui.overlay.desktop.legacy_setup.tooltip_stimme_testen': 'Test voice',
      'ui.overlay.desktop.navigation.aktiver_plan_caption': 'Active plan:',
      'ui.overlay.desktop.navigation.aria_echtzeit': 'Realtime',
      'ui.overlay.desktop.navigation.assets': 'ASSETS',
      'ui.overlay.desktop.navigation.assets_umsatz': 'Assets & revenue',
      'ui.overlay.desktop.navigation.battle_deck': 'BATTLE DECK',
      'ui.overlay.desktop.navigation.cockpit': 'COCKPIT',
      'ui.overlay.desktop.navigation.echtzeit': 'REALTIME',
      'ui.overlay.desktop.navigation.erstellen_verwalten': 'Create & manage',
      'ui.overlay.desktop.navigation.greeting_description': 'You are only seconds away from achieving new sales records.',
      'ui.overlay.desktop.navigation.guten_tag': 'Hello',
      'ui.overlay.desktop.navigation.laden_starten': 'Load & start',
      'ui.overlay.desktop.navigation.life_training': 'Live & training',
      'ui.overlay.desktop.navigation.marketplace': 'MARKETPLACE',
      'ui.overlay.desktop.navigation.neue_items': 'New items',
      'ui.overlay.desktop.navigation.performance_gespraeche': 'Performance & conversations',
      'ui.overlay.desktop.navigation.profil_verwaltung': 'Profile & administration',
      'ui.overlay.desktop.navigation.sales': 'SALES',
      'ui.overlay.desktop.navigation.sales_items': 'Sales items',
      'ui.overlay.desktop.navigation.saves': 'SAVES',
      'ui.overlay.desktop.navigation.shop': 'SHOP',
      'ui.overlay.desktop.navigation.user': 'USER',
      'ui.overlay.desktop.offer_editor.abbrechen': 'Cancel',
      'ui.overlay.desktop.offer_editor.asset_builder': 'ASSET BUILDER',
      'ui.overlay.desktop.offers.abbrechen': 'CANCEL',
      'ui.overlay.desktop.offers.aktiv': 'ACTIVE',
      'ui.overlay.desktop.offers.aktuelles_angebot': 'CURRENT OFFER',
      'ui.overlay.desktop.offers.angebote': '01 OFFERS',
      'ui.overlay.desktop.offers.bearbeiten': 'EDIT',
      'ui.overlay.desktop.offers.deaktivieren': 'DEACTIVATE',
      'ui.overlay.desktop.offers.erstellen': 'CREATE',
      'ui.overlay.desktop.offers.inaktiv': 'INACTIVE',
      'ui.overlay.desktop.offers.ja_deaktivieren': 'YES, DEACTIVATE',
      'ui.overlay.desktop.offers.reaktivieren': 'REACTIVATE',
      'ui.overlay.desktop.offers.search_name_branche_kategorie_szenario_oder_kundenbeziehung': 'Search name, industry, category, scenario, or customer relationship...',
      'ui.overlay.desktop.persona_editor.backup_stimme': 'BACKUP VOICE',
      'ui.overlay.desktop.persona_editor.create_persona': 'CREATE PERSONA',
      'ui.overlay.desktop.persona_editor.edit_persona': 'EDIT PERSONA',
      'ui.overlay.desktop.persona_editor.modus': 'MODE',
      'ui.overlay.desktop.persona_editor.persona_psychologie': 'Persona psychology',
      'ui.overlay.desktop.persona_editor.persona_rolle': 'Persona role',
      'ui.overlay.desktop.persona_editor.placeholder_beschreibe_die_persona_oder_fuege_einen_chatverlauf': 'Describe the persona or paste a chat history...',
      'ui.overlay.desktop.persona_editor.placeholder_z_b_kunde_oder_max': 'e.g. \'Customer\' or \'Max\'',
      'ui.overlay.desktop.persona_editor.placeholder_z_b_skeptischer_geschaeftsfuehrer': 'e.g. \'Skeptical managing director\'',
      'ui.overlay.desktop.persona_editor.search_psychologie_suchen_z_b_skeptiker_dominanter_entschei': 'Search psychology, e.g. skeptic, dominant decision-maker, free spirit.',
      'ui.overlay.desktop.persona_editor.search_rolle_suchen_z_b_geschaeftsfuehrer_vater_mutter_stud': 'Search roles, e.g. managing director, father, mother, student, pupil.',
      'ui.overlay.desktop.persona_editor.stimme': 'VOICE',
      'ui.overlay.desktop.persona_editor.tooltip_stimme_testen': 'Test voice',
      'ui.overlay.desktop.personas.abbrechen': 'CANCEL',
      'ui.overlay.desktop.personas.aktiv': 'ACTIVE',
      'ui.overlay.desktop.personas.bearbeiten': 'EDIT',
      'ui.overlay.desktop.personas.deaktivieren': 'DEACTIVATE',
      'ui.overlay.desktop.personas.erstellen': 'CREATE',
      'ui.overlay.desktop.personas.inaktiv': 'INACTIVE',
      'ui.overlay.desktop.personas.ja_deaktivieren': 'YES, DEACTIVATE',
      'ui.overlay.desktop.personas.object_admin': '03 OBJECT ADMIN',
      'ui.overlay.desktop.personas.personas': '02 PERSONAS',
      'ui.overlay.desktop.personas.reaktivieren': 'REACTIVATE',
      'ui.overlay.desktop.personas.tooltip_stimme_testen': 'Test voice',
      'ui.overlay.desktop.profile.abbrechen': 'Cancel',
      'ui.overlay.desktop.profile.alt_profilbild': 'Profile picture',
      'ui.overlay.desktop.profile.alt_profilbild_ausrichten': 'Position profile picture',
      'ui.overlay.desktop.profile.aria_gespraeche_bearbeiten': 'Edit conversation languages',
      'ui.overlay.desktop.profile.aria_sprache_bearbeiten': 'Edit language',
      'ui.overlay.desktop.profile.bild_loeschen': 'Delete image',
      'ui.overlay.desktop.profile.change_image': 'Change image',
      'ui.overlay.desktop.profile.default_role': 'Role',
      'ui.overlay.desktop.profile.default_user': 'User',
      'ui.overlay.desktop.profile.empfiehl_uns_weiter': 'Refer us',
      'ui.overlay.desktop.profile.gespraeche': 'Conversation languages',
      'ui.overlay.desktop.profile.gespraeche_caption': 'Conversation languages:',
      'ui.overlay.desktop.profile.link_teilen': 'Share link',
      'ui.overlay.desktop.profile.loading_referral_link': 'Loading referral link...',
      'ui.overlay.desktop.profile.no_referral_link': 'No referral link available',
      'ui.overlay.desktop.profile.not_specified': 'Not specified',
      'ui.overlay.desktop.profile.plan_caption': 'Plan:',
      'ui.overlay.desktop.profile.profil': '01 PROFILE',
      'ui.overlay.desktop.profile.referral_description': 'Earn 1,000 free points to spend on new techniques. Always have the right technique ready and win deals you previously lost.',
      'ui.overlay.desktop.profile.rolle_caption': 'Role:',
      'ui.overlay.desktop.profile.saving': 'Saving...',
      'ui.overlay.desktop.profile.select_language': 'Select language',
      'ui.overlay.desktop.profile.select_languages': 'Select languages',
      'ui.overlay.desktop.profile.speichern': 'Save',
      'ui.overlay.desktop.profile.sprache': 'Language',
      'ui.overlay.desktop.profile.sprache_caption': 'Language:',
      'ui.overlay.desktop.profile.tooltip_gespraeche_bearbeiten': 'Edit conversation languages',
      'ui.overlay.desktop.profile.zoom': 'ZOOM',
      'ui.overlay.desktop.rescue.abbrechen': 'Cancel',
      'ui.overlay.desktop.rescue.analyzing_structure': 'Analyzing structure...',
      'ui.overlay.desktop.rescue.gespraechsprotokoll': 'Conversation transcript',
      'ui.overlay.desktop.rescue.paste_full_transcript': 'Paste full transcript',
      'ui.overlay.desktop.rescue.placeholder_fuege_hier_das_transkript_ein': 'Paste the transcript here...',
      'ui.overlay.desktop.rescue.placeholder_z_b_lisa_oder_gast': 'e.g. \'Lisa\' or \'Guest\'',
      'ui.overlay.desktop.rescue.placeholder_z_b_tom_oder_ich': 'e.g. \'Tom\' or \'Me\'',
      'ui.overlay.desktop.rescue.rescue_operation': 'RESCUE OPERATION',
      'ui.overlay.desktop.rescue.transkript_import': 'TRANSCRIPT IMPORT',
      'ui.overlay.desktop.rescue.wer_bist_du_im_text': 'Who are YOU in the text?',
      'ui.overlay.desktop.rescue.wer_ist_der_kunde': 'Who is the CUSTOMER?',
      'ui.overlay.desktop.script_filter.all_industries': 'All industries',
      'ui.overlay.desktop.script_filter.all_offers': 'All offers',
      'ui.overlay.desktop.script_filter.all_phases': 'All phases',
      'ui.overlay.desktop.script_filter.angebot': 'Offer',
      'ui.overlay.desktop.script_filter.aria_filter_zuruecksetzen': 'Reset filters',
      'ui.overlay.desktop.script_filter.filter': 'Filter',
      'ui.overlay.desktop.script_filter.industrie': 'Industry',
      'ui.overlay.desktop.script_filter.keine_passenden_skripte_gefunden': 'No matching scripts found.',
      'ui.overlay.desktop.script_filter.phase': 'Phase',
      'ui.overlay.desktop.script_filter.skriptauswahl_bestaetigen': 'Confirm script selection',
      'ui.overlay.desktop.script_filter.skripte_seite': 'Scripts · page',
      'ui.overlay.desktop.script_filter.skriptname': 'Script name',
      'ui.overlay.desktop.script_filter.von': 'of',
      'ui.overlay.desktop.script_filter.weiter': 'Next',
      'ui.overlay.desktop.script_filter.zurueck': 'Back',
      'ui.overlay.desktop.setup.angebot': 'OFFER',
      'ui.overlay.desktop.setup.customization': 'CUSTOMIZATION',
      'ui.overlay.desktop.setup.saved_offer_outside_catalog': 'Saved offer (not in catalog)',
      'ui.overlay.desktop.setup.startphase': 'Starting phase',
      'ui.overlay.desktop.setup.tooltip_stimme_testen': 'Test voice',
      'ui.overlay.desktop.technique_filter.all_categories': 'All categories',
      'ui.overlay.desktop.technique_filter.all_offers': 'All offers',
      'ui.overlay.desktop.technique_filter.all_types': 'All types',
      'ui.overlay.desktop.technique_filter.angebot': 'Offer',
      'ui.overlay.desktop.technique_filter.aria_filter_zuruecksetzen': 'Reset filters',
      'ui.overlay.desktop.technique_filter.filter': 'Filter',
      'ui.overlay.desktop.technique_filter.kategorie': 'Category',
      'ui.overlay.desktop.technique_filter.keine_passenden_techniken_gefunden': 'No matching techniques found.',
      'ui.overlay.desktop.technique_filter.technikauswahl_bestaetigen': 'Confirm technique selection',
      'ui.overlay.desktop.technique_filter.techniken_seite': 'Techniques · page',
      'ui.overlay.desktop.technique_filter.technikname': 'Technique name',
      'ui.overlay.desktop.technique_filter.typ': 'Type',
      'ui.overlay.desktop.technique_filter.von': 'of',
      'ui.overlay.desktop.technique_filter.weiter': 'Next',
      'ui.overlay.desktop.technique_filter.zurueck': 'Back',
      'ui.overlay.desktop.training.art_der_verinnerlichung': 'Internalization method',
      'ui.overlay.desktop.training.auswahl': 'Selection',
      'ui.overlay.desktop.training.life_coaching': '2 Life coaching',
      'ui.overlay.desktop.training.placeholder_auswahl_treffen': 'Make a selection',
      'ui.overlay.desktop.training.placeholder_rollenspiel_auswaehlen': 'Select roleplay',
      'ui.overlay.desktop.training.placeholder_trainingsinhalt_auswaehlen': 'Select training content',
      'ui.overlay.desktop.training.rollenspiel': 'Roleplay',
      'ui.overlay.desktop.training.skript': 'Script',
      'ui.overlay.desktop.training.training': '1 Training',
      'ui.overlay.desktop.training.trainingsinhalt': 'Training content',
      'ui.setup.persona.error_api_key': 'Please configure an API key first.',
      'ui.setup.persona.error_generate': 'Persona could not be generated.',



     // --- ScriptBuilder


"ui.script_builder.control.auto_hint": "Create automatically and continue immediately.",
"ui.script_builder.control.automatic_upper": "AUTOMATIC",
"ui.script_builder.control.manual_hint": "Make adjustments during the creation process.",
"ui.script_builder.control.manual_title": "Manual control",
"ui.script_builder.control.manual_upper": "MANUAL",
"ui.script_builder.hitl.add_goal_end": "Add goal at the end",
"ui.script_builder.hitl.changed": "changed",
"ui.script_builder.hitl.changed_upper": "CHANGED",
"ui.script_builder.hitl.delete": "Delete",
"ui.script_builder.hitl.edit": "Edit",
"ui.script_builder.hitl.goals": "goals",
"ui.script_builder.hitl.insert_goal_after": "Insert intermediate goal after",
"ui.script_builder.hitl.minutes": "minutes",
"ui.script_builder.hitl.new": "new",
"ui.script_builder.hitl.new_upper": "NEW",
"ui.script_builder.hitl.phase": "Phase",
"ui.script_builder.hitl.phases": "Phases",
"ui.script_builder.hitl.reordered": "reordered",
"ui.script_builder.hitl.review_description": "Review, edit, add or delete phases and goals.",
"ui.script_builder.hitl.review_duration_prefix": "Manual review - estimated creation time approx.",
"ui.script_builder.hitl.save": "Save",
"ui.script_builder.hitl.save_continue": "Update and continue",
"ui.script_builder.intro.script_engine": "The script is automatically transformed into an engine that detects objections and pretexts and makes conversations plannable, measurable and controllable.",
"ui.script_builder.intro.script_instructions": "Paste your script into this field. You can choose to control the creation process yourself or use fully automatic creation.",
"ui.script_builder.intro.script_text": "Script text",
"ui.script_builder.intro.technique_engine": "The technique is automatically transformed into an engine that detects objections and pretexts and makes conversations plannable, measurable and controllable.",
"ui.script_builder.intro.technique_instructions": "Paste your technique into this field. You can choose to control the creation process yourself or use fully automatic creation.",
"ui.script_builder.intro.technique_text": "Technique text",
"ui.script_builder.json.hide": "Hide JSON output",
"ui.script_builder.json.script_title": "SCRIPT JSON OUTPUT",
"ui.script_builder.json.show": "Show JSON output",
"ui.script_builder.json.technique_title": "TECHNIQUE JSON OUTPUT",
"ui.script_builder.modal.aria_title": "Script builder",
"ui.script_builder.modal.close": "Close script builder",
"ui.script_builder.person.add_closer": "Add closer",
"ui.script_builder.person.add_other": "Add other person",
"ui.script_builder.person.add_prospect": "Add prospect",
"ui.script_builder.person.contains_names": "Does the text contain people's names?",
"ui.script_builder.person.first_name_placeholder": "First name, if present in the text",
"ui.script_builder.person.last_name_placeholder": "Last name, if present in the text",
"ui.script_builder.person.mapping_hint": "Only enter names that actually appear in the text. Each entry needs a first or last name.",
"ui.script_builder.person.mapping_title": "Person mapping",
"ui.script_builder.person.min_one": "Add at least one person's name from the text.",
"ui.script_builder.person.other": "Other",
"ui.script_builder.person.remove": "Remove",
"ui.script_builder.person.sanitizer_hint": "This setting controls the server-side sanitizer before creation.",
"ui.script_builder.prestep.description": "The text has been checked. The title and description are set before the actual creation process.",
"ui.script_builder.prestep.description_hint": "Can be regenerated by AI and edited manually.",
"ui.script_builder.prestep.generating": "Generating...",
"ui.script_builder.prestep.marketable": "MARKETABLE",
"ui.script_builder.prestep.not_marketable": "NOT MARKETABLE",
"ui.script_builder.prestep.regenerate_description": "Regenerate description",
"ui.script_builder.prestep.regenerate_title": "Regenerate title",
"ui.script_builder.prestep.sanitized_text": "Sanitized text",
"ui.script_builder.prestep.start_creation": "Start creation process",
"ui.script_builder.prestep.title_recommended": "Recommended. May optionally be added or edited.",
"ui.script_builder.prestep.title_required": "Required field. Must be generated or regenerated by AI.",
"ui.script_builder.progress.building_foundation": "Building foundation",
"ui.script_builder.progress.completed_detail": "Creation has been completed.",
"ui.script_builder.progress.failed": "Creation could not be completed",
"ui.script_builder.progress.finalizing": "Finalizing creation",
"ui.script_builder.progress.keep_window_open": "Preparing the final steps. Please keep this window open.",
"ui.script_builder.progress.merging_components": "The identified components are being checked and consolidated.",
"ui.script_builder.progress.preparing": "Preparing creation",
"ui.script_builder.progress.preparing_usage": "Preparing and consistently structuring the intended use.",
"ui.script_builder.progress.processing_input": "Processing the input and preparing it for subsequent creation.",
"ui.script_builder.progress.quality_assurance": "Checking quality",
"ui.script_builder.progress.retry_detail": "Check the information and start the process again.",
"ui.script_builder.progress.script_created": "Script created",
"ui.script_builder.progress.script_preparing": "Preparing script",
"ui.script_builder.progress.structuring_content": "Converting the content into a usable structure.",
"ui.script_builder.progress.technique_created": "Technique created",
"ui.script_builder.progress.technique_preparing": "Preparing technique",
"ui.script_builder.progress.wait_preparing": "Preparing creation. Please keep this window open.",
"ui.script_builder.start.script": "Review and prepare script",
"ui.script_builder.start.script_running": "Reviewing script...",
"ui.script_builder.start.technique": "Review and prepare technique",
"ui.script_builder.start.technique_running": "Reviewing technique...",



     // --- ObjectsManager
"ui.object_admin.error.get_objects_invalid_list": "getObjects did not return a valid object list.",
"ui.object_admin.status.script_worker_saved": "Script successfully saved to the database after Worker 10. 🎉",
"ui.object_admin.status.script_saved": "Script successfully saved to the database. 🎉",
"ui.object_admin.status.technique_worker_saved": "Technique successfully saved to the database after Worker 10. 🎉",
"ui.object_admin.status.technique_saved": "Technique successfully saved to the database. 🎉",
"ui.object_admin.status.technique_updated": "Technique updated. 🎉",


"ui.object_admin.dropdown.all": "All",
"ui.object_admin.dropdown.required": "Please select",
"ui.object_admin.dropdown.no_selection": "No selection",
"ui.object_admin.dropdown.trials_first": "Please select the number of trials first",

"ui.object_admin.field.search": "Search",
"ui.object_admin.field.status": "Status",
"ui.object_admin.field.sharable": "Share",
"ui.object_admin.field.offer": "Offer",
"ui.object_admin.field.industry": "Target industry",
"ui.object_admin.field.sales_stage": "Sales stage",
"ui.object_admin.field.technique_type": "Technique type",
"ui.object_admin.field.technique_application": "Technique application",
"ui.object_admin.field.tier": "Tier",
"ui.object_admin.field.unlock_mode": "Usage type",
"ui.object_admin.field.pricing_model": "Pricing model",
"ui.object_admin.field.currency": "Currency",
"ui.object_admin.field.immutable": "Immutable",
"ui.object_admin.field.technique_type_tier": "Technique type / tier",
"ui.object_admin.field.sales_stage_tier": "Sales stage / tier",
"ui.object_admin.field.share_market": "Share / Market",
"ui.object_admin.field.price": "Price",
"ui.object_admin.field.points": "Points",
"ui.object_admin.field.updated": "Updated",
"ui.object_admin.field.mode": "Mode",
"ui.object_admin.field.created_by_user": "CREATED BY USER ID",
"ui.object_admin.field.target_mail": "TARGET MAIL",
"ui.object_admin.field.admin_api_key": "ADMIN API KEY",
"ui.object_admin.field.action": "Action",
"ui.object_admin.field.immutable_key": "object_key_immutable",
"ui.object_admin.field.company_size": "Target company size",
"ui.object_admin.field.language": "Language",
"ui.object_admin.field.contact": "Contact type",
"ui.object_admin.field.icon": "Icon",
"ui.object_admin.field.title": "Title",
"ui.object_admin.field.short_description": "Short description",
"ui.object_admin.field.trial_uses": "Number of trials",
"ui.object_admin.field.trial_points": "Trial points",
"ui.object_admin.field.purchase_cents": "Purchase price in cents",
"ui.object_admin.field.permanent_points": "Permanent points",
"ui.object_admin.field.description": "Description",
"ui.object_admin.field.trigger_reason": "Trigger reason",
"ui.object_admin.field.engine_json": "ENGINE JSON",
"ui.object_admin.field.trigger_json": "TRIGGER JSON",
"ui.object_admin.field.micro_goals": "MicroGoals",
"ui.object_admin.field.api_key": "SUPERADMIN API KEY",
"ui.object_admin.field.user_id": "User ID",
"ui.object_admin.field.payload": "COMPLETE JSON OBJECT FOR UPLOAD",

"ui.object_admin.search.script": "Search script, phase, key, area or topic...",
"ui.object_admin.search.technique": "Search technique, objection, category, key or trigger...",
"ui.object_admin.search.offer": "Search offer...",
"ui.object_admin.search.industry": "Search target industry...",
"ui.object_admin.search.stage": "Search sales stage...",
"ui.object_admin.search.type": "Search technique type...",
"ui.object_admin.search.application": "Search technique application...",
"ui.object_admin.search.offer_examples": "Search offer, e.g. SEO, recruiting, SaaS.",
"ui.object_admin.search.industry_examples": "Search target industry, e.g. physicians, skilled trades, SMEs.",
"ui.object_admin.search.type_examples": "Search technique type, e.g. objection handling, storytelling, building trust.",
"ui.object_admin.search.application_examples": "Search technique application, e.g. price, customer story, perspective shift.",

"ui.object_admin.action.edit": "Edit",
"ui.object_admin.action.edit_technique": "Edit technique",
"ui.object_admin.action.edit_text": "Edit texts",
"ui.object_admin.action.edit_objections": "Edit objections",
"ui.object_admin.action.save_objections": "Save objection permissions",
"ui.object_admin.action.save_text": "Save texts",
"ui.object_admin.action.save_technique": "Save technique",
"ui.object_admin.action.save_loading": "Saving...",
"ui.object_admin.action.loading": "Loading...",
"ui.object_admin.action.refresh": "Refresh",
"ui.object_admin.action.filter_reset": "RESET FILTERS",
"ui.object_admin.action.all_filters_reset": "RESET ALL FILTERS",
"ui.object_admin.action.create_script": "CREATE SCRIPT",
"ui.object_admin.action.edit_script": "EDIT SCRIPT",
"ui.object_admin.action.create_technique": "CREATE TECHNIQUE",
"ui.object_admin.action.edit_technique_title": "EDIT TECHNIQUE",
"ui.object_admin.action.stop_market": "Stop marketing",
"ui.object_admin.action.market": "Market",
"ui.object_admin.action.create_title": "Regenerate title",
"ui.object_admin.action.create_description": "Regenerate description",
"ui.object_admin.action.generating": "Generating...",
"ui.object_admin.action.update_and_save": "Update and save",
"ui.object_admin.action.preflight": "Start preflight",
"ui.object_admin.action.preflighting": "Preflight in progress...",
"ui.object_admin.action.show_payload": "REVIEW PAYLOAD",
"ui.object_admin.action.hide_payload": "HIDE PAYLOAD",
"ui.object_admin.action.close": "CLOSE",
"ui.object_admin.action.send_loading": "SENDING...",
"ui.object_admin.action.to_tenant_admin": "SWITCH TO TENANT ADMIN VIEW",
"ui.object_admin.action.to_superadmin": "SWITCH TO SUPERADMIN VIEW",
"ui.object_admin.action.close_short": "Close",

"ui.object_admin.badge.technique": "TECHNIQUE",
"ui.object_admin.badge.script": "SCRIPT",
"ui.object_admin.badge.script_count": "scripts visible",
"ui.object_admin.badge.phases": "phases",
"ui.object_admin.badge.visible": "visible",
"ui.object_admin.badge.total": "total",

"ui.object_admin.title.asset_hub": "Asset Hub",
"ui.object_admin.title.scripts": "Scripts",
"ui.object_admin.title.techniques": "Techniques",
"ui.object_admin.title.objections": "Edit objection handling",
"ui.object_admin.title.script_prepare": "Prepare script",
"ui.object_admin.title.technique_prepare": "Prepare technique",
"ui.object_admin.title.script_microgoals": "Edit texts:",
"ui.object_admin.title.technique_microgoals": "Edit technique:",

"ui.object_admin.hint.admin_key": "x-admin-key for /api/admin/manage",
"ui.object_admin.hint.filter_fields": "Only relevant object fields are filtered for",
"ui.object_admin.hint.objections": "For each script phase, specify whether detected objections may be handled immediately.",
"ui.object_admin.hint.no_script_phases": "No script phases found in engine_json.",
"ui.object_admin.hint.objections_allowed": "Objections allowed",
"ui.object_admin.hint.objections_blocked": "Objections blocked",
"ui.object_admin.hint.no_scripts": "No matching scripts found.",
"ui.object_admin.hint.no_techniques": "No matching techniques found.",
"ui.object_admin.hint.no_script_phase": "No script phase found in engine_json.",
"ui.object_admin.hint.microgoal_script": "Only the micro-goal texts of this script phase are changed.",
"ui.object_admin.hint.no_microgoals_script": "This script phase contains no microGoals.",
"ui.object_admin.hint.no_technique": "No technique found in engine_json.",
"ui.object_admin.hint.microgoal_technique": "Edit the trigger reason and micro-goal texts for this technique.",
"ui.object_admin.hint.trigger_reason": "Defines when this technique should be triggered.",
"ui.object_admin.hint.no_microgoals_technique": "This technique contains no microGoals.",
"ui.object_admin.hint.script_compiler": "Guided script compiler with text upload and human-in-the-loop.",
"ui.object_admin.hint.script_json": "SuperAdmin JSON/object upload for completed script-engine objects.",
"ui.object_admin.hint.technique_compiler": "Guided technique compiler with text upload and human-in-the-loop.",
"ui.object_admin.hint.technique_json": "SuperAdmin JSON/object upload for completed technique-engine objects.",
"ui.object_admin.hint.script_config_missing": "Missing config. Script Builder requires config.apiKey.",
"ui.object_admin.hint.technique_config_missing": "Missing config. Technique Builder requires config.apiKey.",
"ui.object_admin.hint.prepared": "The text was checked. Title and description are set before the marketplace change.",
"ui.object_admin.hint.ai_optional": "Can be regenerated by AI and edited manually.",
"ui.object_admin.hint.ai_required": "Required field. Must be generated or regenerated by AI.",
"ui.object_admin.hint.asset_hub": "Create your own assets and sustainably increase your revenue.",
"ui.object_admin.hint.scripts": "Turn your sales script into a trainable, measurable asset that recognizes customer objections and helps maximize sales performance.",
"ui.object_admin.hint.techniques": "Turn your technique into a battle card that is automatically recommended and available on demand in critical sales situations. Your collection of battle cards helps you stay in control of the conversation.",
"ui.object_admin.hint.payload_tenant": "The request body contains only the JSON shown below. Tenant admin uses a session cookie and tenant fork.",
"ui.object_admin.hint.load_current": "Load current assets",
"ui.object_admin.hint.title_optional": "Recommended. May be optionally added or edited.",
"ui.object_admin.hint.script_bundle": "Script bundle",
"ui.object_admin.hint.payload_before": "Only the JSON shown below is sent in the request body. The SuperAdmin key is sent exclusively as the",
"ui.object_admin.hint.payload_after": "header.",
"ui.object_admin.hint.marketing_full": "Marketing means recommending suitable techniques and scripts to sellers, which may be used free of charge for a limited number of uses. They will then no longer be available. Targeted recommendations increase the likelihood of a successful subscription. By selecting this option, you consent to the transfer of usage rights.",

"ui.object_admin.aria.objections.enable": "enable",
"ui.object_admin.aria.objections.disable": "disable",
"ui.object_admin.aria.objections_prefix": "Objections in",
"ui.object_admin.aria.microgoal_edit": "edit",
"ui.object_admin.aria.microgoal_close": "close",
"ui.object_admin.aria.trigger_reason": "Edit trigger reason",

"ui.object_admin.error.api_key_missing": "SuperAdmin API key missing",
"ui.object_admin.error.invalid_engine_json": "engine_json is invalid.",
"ui.object_admin.error.invalid_trigger_json": "trigger_json is invalid.",
"ui.object_admin.error.could_not_generate_title": "Could not regenerate title.",
"ui.object_admin.error.could_not_generate_description": "Could not regenerate description.",
"ui.object_admin.error.marketplace_upgrade": "Marketplace upgrade failed.",
"ui.object_admin.error.prestep": "Marketplace prestep failed.",
"ui.object_admin.error.upload_script": "Script upload failed.",
"ui.object_admin.error.upload_technique": "Technique upload failed.",
"ui.object_admin.error.required_fields": "Required fields are missing.",
"ui.object_admin.error.payload_build": "Payload cannot be built yet.",
"ui.object_admin.error.preflight": "Preflight failed.",
"ui.object_admin.error.missing_fields_prefix": "Please fill in or correct first:",
"ui.object_admin.error.unknown_saved_type": "Unknown object type after successful save.",
"ui.object_admin.error.object_key_missing": "object_key_immutable is missing.",
"ui.object_admin.error.get_objects": "Failed to load objects.",
"ui.object_admin.error.unknown_load": "Unknown loading error.",
"ui.object_admin.error.save_reload_prefix": "Saved successfully, but reloading or matching the object failed:",
"ui.object_admin.error.save_reload_suggestion": "Use only “Refresh”; do not save again.",
"ui.object_admin.error.script_text_update": "Editing script text is only permitted in tenant admin or superadmin mode.",
"ui.object_admin.error.required_field_trials": "Trials",
"ui.object_admin.error.required_field_trial_points": "Trial points",

"ui.object_admin.status.script_published": "Script published to the marketplace. 🎉",
"ui.object_admin.status.script_unpublished": "Script removed from the marketplace. 🎉",
"ui.object_admin.status.technique_published": "Technique published to the marketplace. 🎉",
"ui.object_admin.status.technique_unpublished": "Technique removed from the marketplace. 🎉",
"ui.object_admin.status.update_attempt": "Update attempt completed. Please review title and description.",
"ui.object_admin.status.assets_loaded": "Assets loaded.",
"ui.object_admin.status.script_saved_reloaded": "Script saved and fully reloaded.",
"ui.object_admin.status.technique_saved_reloaded": "Technique saved and fully reloaded.",
"ui.object_admin.status.objections_updated": "Objection permissions updated. 🎉",
"ui.object_admin.status.script_text_updated": "Script texts updated. 🎉",
"ui.object_admin.status.important_missing": "Usage must not be TEMPORARY",

"ui.object_admin.mode.json_upload": "JSON/object upload",
"ui.object_admin.mode.text_compiler": "Text/HITL compiler",

"ui.object_admin.boolean.yes": "Yes",
"ui.object_admin.boolean.no": "No",
"ui.object_admin.boolean.yes_upper": "YES",
"ui.object_admin.boolean.no_upper": "NO",

"ui.object_admin.action.edit_text_singular": "Edit text",
"ui.object_admin.action.marketing_stop": "Stop marketing",
"ui.object_admin.field.usage_type": "Usage type",
"ui.object_admin.field.immutable_key_upper": "OBJECT KEY IMMUTABLE",
"ui.object_admin.filter.unlock_both": "Recommendations & sales",
"ui.object_admin.title.asset_builder": "ASSET BUILDER",



     // --- OfferCreator

'ui.offer_generator.alleinstellungsmerkmale': 'Unique Selling Points',
'ui.offer_generator.alternativen': 'Alternatives',
'ui.offer_generator.amortisation': 'Payback',
'ui.offer_generator.amortisationszeit': 'Payback Period',
'ui.offer_generator.angebotskategorie': 'Offer Category',
'ui.offer_generator.angebotskategorie_suchen': 'Search offer categories.',
'ui.offer_generator.angebotsname': 'Offer Name',
'ui.offer_generator.angebotswissen_kb': 'Offer Knowledge / KB',
'ui.offer_generator.ausgangsbasis_des_produzierten_vorergebnisses_je': 'Intermediate Result Baseline per',
'ui.offer_generator.ausgangsbasis_des_produzierten_vorergebnisses_je_zeitmetrik': 'Intermediate Result Baseline per Time Unit',
'ui.offer_generator.bearbeiten': 'Edit',
'ui.offer_generator.beginn_der_veranderung_des_produzierten_vorergebnisses': 'Start of the Change in the Intermediate Result',
'ui.offer_generator.berechneter_fortwirkender_return_je': 'Calculated Continuing Return per',
'ui.offer_generator.besonderheiten': 'Special Notes',
'ui.offer_generator.besonderheiten_beim_rechenweg': 'Special Notes on the Calculation',
'ui.offer_generator.branche_suchen': 'Search industries.',
'ui.offer_generator.branchenfokus': 'Industry Focus',
'ui.offer_generator.branchenfokus_kontext_ist_nicht_verfugbar': 'Industry focus context is unavailable.',
'ui.offer_generator.branchenfokusse': 'Industry Focuses',
'ui.offer_generator.einheit_des_kundenziels': 'Customer Goal Unit',
'ui.offer_generator.einmaliger_anteil_investment_fur_dritte_sonstige_investitionen': 'One-Time Share of Third-Party / Other Investments',
'ui.offer_generator.einmaliger_anteil_investment_fur_unsere_leistung': 'One-Time Share of Our Service Investment',
'ui.offer_generator.erreichte_einheiten_des_kundenziels_uber_den_zeitraum': 'Customer Goal Units Achieved Over the Period',
'ui.offer_generator.erstellen': 'Create',
'ui.offer_generator.erstellen_lower': 'create',
'ui.offer_generator.fortwirkender_return_je_zeitmetrik_im_optionalen_wirkungshorizont': 'Continuing Return per Time Unit During the Optional Impact Horizon',
'ui.offer_generator.gesamtdauer_inklusive_wirkungshorizont': 'Total Duration Including Impact Horizon',
'ui.offer_generator.gesamtinvestment_fur_dritte_sonstige_investitionen_uber_den_zeitraum': 'Total Third-Party / Other Investments Over the Period',
'ui.offer_generator.gesamtinvestment_fur_unsere_leistung_uber_den_zeitraum': 'Total Investment in Our Service Over the Period',
'ui.offer_generator.gesamtinvestment_uber_den_zeitraum': 'Total Investment Over the Period',
'ui.offer_generator.gesamtinvestment_uber_die_gesamtdauer': 'Total Investment Over the Total Duration',
'ui.offer_generator.im_optionalen_wirkungshorizont': 'during the Optional Impact Horizon',
'ui.offer_generator.investment': 'Investment',
'ui.offer_generator.investment_fur_dritte_sonstige_investitionen_lauft_im_wirkungshorizont_weiter': 'Third-Party / Other Investments Continue During the Impact Horizon',
'ui.offer_generator.investment_fur_unsere_leistung_lauft_im_wirkungshorizont_weiter': 'Our Service Investment Continues During the Impact Horizon',
'ui.offer_generator.keine_analogie_hinterlegt': 'No analogy provided',
'ui.offer_generator.konservative_annahmen': 'Conservative Assumptions',
'ui.offer_generator.kundenbeziehung': 'Customer Relationship',
'ui.offer_generator.level.effect.addLabel': 'Add Benefit',
'ui.offer_generator.level.effect.singular': 'Benefit',
'ui.offer_generator.level.effect.subtitle': 'What does this mechanism generally provide, independent of the specific customer?',
'ui.offer_generator.level.effect.title': 'Benefits',
'ui.offer_generator.level.effect_unit.addLabel': 'Add Intermediate Result',
'ui.offer_generator.level.effect_unit.nextLabel': 'Mechanisms',
'ui.offer_generator.level.effect_unit.singular': 'Produced Intermediate Result',
'ui.offer_generator.level.effect_unit.subtitle': 'Edit the final intermediate result we produce before the customer\'s desired outcome.',
'ui.offer_generator.level.effect_unit.title': 'Produced Intermediate Results',
'ui.offer_generator.level.mechanism.addLabel': 'Add Mechanism',
'ui.offer_generator.level.mechanism.nextLabel': 'Benefits',
'ui.offer_generator.level.mechanism.singular': 'Mechanism',
'ui.offer_generator.level.mechanism.subtitle': 'Edit only the mechanisms of this produced intermediate result.',
'ui.offer_generator.level.mechanism.title': 'Mechanisms',
'ui.offer_generator.level.offer.nextLabel': 'Industry Focus',
'ui.offer_generator.level.offer.singular': 'Offer',
'ui.offer_generator.level.offer.subtitle': 'The offer is the entry point for the complete offer structure.',
'ui.offer_generator.level.offer.title': 'Offer',
'ui.offer_generator.level.roi_case.addLabel': 'Add Industry Focus',
'ui.offer_generator.level.roi_case.nextLabel': 'Produced Intermediate Results',
'ui.offer_generator.level.roi_case.singular': 'Industry Focus',
'ui.offer_generator.level.roi_case.subtitle': 'Edit the industry focuses for this offer.',
'ui.offer_generator.level.roi_case.title': 'Industry Focus',
'ui.offer_generator.mechanismus': 'Mechanism',
'ui.offer_generator.mechanismus_kontext_ist_nicht_verfugbar': 'Mechanism context is unavailable.',
'ui.offer_generator.mechanismus_unseres_services': 'Mechanism of Our Service',
'ui.offer_generator.mehrwert': 'Added value',
'ui.offer_generator.mehrwert_nach_abzug_des_investments_uber_den_zeitraum': 'Added Value After Investment Over the Period',
'ui.offer_generator.mehrwert_nach_abzug_des_investments_uber_die_gesamtdauer': 'Added Value After Investment Over the Total Duration',
'ui.offer_generator.metapher_analogie': 'Metaphor / Analogy',
'ui.offer_generator.mitarbeiter': 'Employees',
'ui.offer_generator.mitarbeiter_bis': 'Employees To',
'ui.offer_generator.mitarbeiter_von': 'Employees From',
'ui.offer_generator.nicht_ausgewahlt': 'Not selected',
'ui.offer_generator.nicht_gesetzt': 'not set',
'ui.offer_generator.nicht_verfugbar': 'Not available',
'ui.offer_generator.optional': 'Optional',
'ui.offer_generator.optionaler_deckungsbeitrag': 'Optional Contribution Margin',
'ui.offer_generator.optionaler_deckungsbeitrag_percent': 'Optional Contribution Margin (%)',
'ui.offer_generator.optionaler_wirkungshorizont_nach_dem_zeitraum': 'Optional Impact Horizon After the Period',
'ui.offer_generator.pricing': 'Pricing',
'ui.offer_generator.produzierte_vorergebnisse': 'Produced Intermediate Results',
'ui.offer_generator.produziertes_vorergebnis_kontext_ist_nicht_verfugbar': 'Intermediate result context is unavailable.',
'ui.offer_generator.rate_investment_fur_unsere_leistung_je': 'Our Service Investment Rate per',
'ui.offer_generator.rechenweg': 'Calculation',
'ui.offer_generator.rechenweg_aktuell_nicht_berechenbar': 'Calculation currently unavailable',
'ui.offer_generator.resultierende_veranderung_des_produzierten_vorergebnisses_uber_den_zeitraum': 'Resulting Change in the Intermediate Result Over the Period',
'ui.offer_generator.resultierender_deckungsbeitrag': 'Resulting Contribution Margin',
'ui.offer_generator.return_ohne_abzug_des_investments_uber_den_zeitraum': 'Return Before Deducting Investment Over the Period',
'ui.offer_generator.return_ohne_abzug_des_investments_uber_die_gesamtdauer': 'Return Before Deducting Investment Over the Total Duration',
'ui.offer_generator.scenario': 'Scenario',
'ui.offer_generator.start_einmaliger_anteil_investment_fur_dritte_sonstige_investitionen': 'Start of One-Time Share of Third-Party / Other Investments',
'ui.offer_generator.start_einmaliger_anteil_investment_fur_unsere_leistung': 'Start of One-Time Share of Our Service Investment',
'ui.offer_generator.start_investment_fur_dritte_sonstige_investitionen': 'Start of Third-Party / Other Investments',
'ui.offer_generator.start_investment_fur_unsere_leistung': 'Start of Investment in Our Service',
'ui.offer_generator.start_ratenzahlung_fur_unsere_leistung': 'Start of Installment Payments for Our Service',
'ui.offer_generator.time_plural.day': 'Days',
'ui.offer_generator.time_plural.month': 'Months',
'ui.offer_generator.time_plural.quarter': 'Quarters',
'ui.offer_generator.time_plural.week': 'Weeks',
'ui.offer_generator.time_plural.year': 'Years',
'ui.offer_generator.umwandlungsquote_zum_zielergebnis_des_kunden': 'Conversion Rate to the Customer Goal',
'ui.offer_generator.update': 'Update',
'ui.offer_generator.veranderung_des_produzierten_vorergebnisses_durch_unseren_service_je': 'Change in Intermediate Result from Our Service per',
'ui.offer_generator.veranderung_des_produzierten_vorergebnisses_durch_unseren_service_je_zeitmetrik': 'Change in Intermediate Result from Our Service per Time Unit',
'ui.offer_generator.von_uns_zuletzt_produziertes_vorergebnis_vor_dem_kundenziel': 'Final Intermediate Result Produced by Us Before the Customer Goal',
'ui.offer_generator.vorergebnis': 'Intermediate result',
'ui.offer_generator.vorteil': 'Benefit',
'ui.offer_generator.vorteil_was_bringt_der_mechanismus_grundsatzlich': 'Benefit – What Does This Mechanism Generally Provide?',
'ui.offer_generator.vorteile': 'Benefits',
'ui.offer_generator.wirkung': 'Impact',
'ui.offer_generator.wirtschaftlich_wirksamer_zeitraum': 'Economically Effective Period',
'ui.offer_generator.wirtschaftlicher_wert': 'Economic Value',
'ui.offer_generator.wirtschaftlicher_wert_je_einheit_des_kundenziels': 'Economic Value per Customer Goal Unit',
'ui.offer_generator.wirtschaftlicher_wert_uber_den_zeitraum': 'Economic Value Over the Period',
'ui.offer_generator.zahlungsart_investment_fur_dritte_sonstige_investitionen': 'Payment Method for Third-Party / Other Investments',
'ui.offer_generator.zahlungsart_investment_fur_unsere_leistung': 'Payment Method for Our Service Investment',
'ui.offer_generator.zeit_bis_zum_ersten_wirtschaftlichen_ergebnis': 'Time Until the First Economic Result',
'ui.offer_generator.zeitmetrik': 'Time Unit',
'ui.offer_generator.zeitraum': 'Period',
'ui.offer_generator.zielkunde': 'Target Customer',
'ui.offer_generator.zusatzlicher_return_im_optionalen_wirkungshorizont': 'Additional Return During the Optional Impact Horizon',



     // --- DeckBuilder

      "ui.deck.member.marketplace": "MARKETPLACE",
      "ui.deck.member.heading": "MANAGE ITEMS FOR?",
      "ui.deck.member.label": "MEMBER",
      "ui.deck.member.fallback_prefix": "Member ",
      "ui.deck.member.self_suffix": " (me)",

      "ui.deck.section.cards": "01 BATTLE CARDS",
      "ui.deck.section.scripts": "02 SCRIPTS",

      "ui.deck.item.script_badge": "SCRIPT",
      "ui.deck.item.phases": "PHASES",
      "ui.deck.item.bundle": "BUNDLE",
      "ui.deck.item.asset_badge": "ASSET",
      "ui.deck.item.free_badge": "FREE",
      "ui.deck.item.trial_badge": "TRIAL",
      "ui.deck.item.perm_badge": "PERM",
      "ui.deck.item.of": "of",
      "ui.deck.item.remaining": "remaining",
      "ui.deck.item.per_month": "€/month",
      "ui.deck.item.start": "Start",
      "ui.deck.item.end": "End",
      "ui.deck.item.trial_locked_prefix": "Trial locked until ",
      "ui.deck.item.repurchase_locked_prefix": "Repurchases & trials locked until ",
      "ui.deck.item.details_button": "Details",

      "ui.deck.details.brand": "DECK BUILDER",
      "ui.deck.details.heading": "MODULE DETAILS",
      "ui.deck.details.type": "TYPE",
      "ui.deck.details.type_asset": "Asset",
      "ui.deck.details.type_free": "Free",
      "ui.deck.details.type_trial": "Trial",
      "ui.deck.details.type_permanent": "Permanent",
      "ui.deck.details.price_usage": "PRICE / USAGE",
      "ui.deck.details.cancel_due_prefix": "Cancellation effective: ",
      "ui.deck.details.locked_until_prefix": "Locked until: ",
      "ui.deck.details.description": "DESCRIPTION",
      "ui.deck.details.no_description": "No description available.",
      "ui.deck.details.price": "PRICE",
      "ui.deck.details.status": "STATUS",

      "ui.deck.cancel.warning_prefix": "Warning: You are about to cancel this permanent module for ",
      "ui.deck.cancel.warning_suffix": ".",
      "ui.deck.cancel.confirm_hint": "The cancellation will be scheduled and must now be explicitly confirmed again.",
      "ui.deck.cancel.action": "Cancel subscription",
      "ui.deck.cancel.back": "Back",
      "ui.deck.cancel.close": "Close",
      "ui.deck.cancel.confirm": "Confirm cancellation",
      "ui.deck.cancel.confirm_heading": "CONFIRM CANCELLATION",

      "ui.deck.sets.title_load": "Load Set",
      "ui.deck.sets.title_edit": "Edit Set",
      "ui.deck.sets.title_save": "Save Set",
      "ui.deck.sets.close_aria": "Close set dialog",
      "ui.deck.sets.none_saved": "No sets saved yet.",
      "ui.deck.sets.techniques_word": "Techniques",
      "ui.deck.sets.unavailable": "Set currently unavailable: Check availability or slots.",
      "ui.deck.sets.edit": "Edit",
      "ui.deck.sets.name": "Set name",
      "ui.deck.sets.name_placeholder": "Enter name",
      "ui.deck.sets.script_label": "Script",
      "ui.deck.sets.current_techniques": "Techniques from the current Battle Deck",
      "ui.deck.sets.save_changes": "Save changes",
      "ui.deck.sets.save": "Save set",
      "ui.deck.sets.remove_trials": "Remove trials",

      "ui.deck.embedded.owner_prefix": "DECK OF ",
      "ui.deck.embedded.items_title": "Battle Items",
      "ui.deck.embedded.slots": "SLOTS",
      "ui.deck.active_deck": "ACTIVE DECK",

      "ui.deck.filter.all_industries": "All target markets",
      "ui.deck.filter.all_offers": "All offers",
      "ui.deck.filter.all_creators": "All creators",
      "ui.deck.filter.without_creator": "Without creator",
      "ui.deck.filter.industry": "Target market",
      "ui.deck.filter.offer": "Offer",
      "ui.deck.filter.creator": "Creator",
      "ui.deck.script.select": "Select script",
      "ui.deck.filter.active": "ACTIVE",
      "ui.deck.filter.history": "HISTORY",

      "ui.deck.button.save": "SAVE",

"ui.deck.error.load_sets": "Your saved sets could not be loaded. Please try again.",
"ui.deck.error.save_set": "The set could not be saved. Please try again.",

"ui.deck.mobile.scripts": "SCRIPTS",
"ui.deck.mobile.filter.all_phases": "All sales phases",
"ui.deck.mobile.filter.all_applications": "All technique applications",
"ui.deck.mobile.member.profile_heading": "PROFILE?",
"ui.deck.mobile.owner_prefix": "⚡ BATTLE ITEMS OF ",
"ui.deck.mobile.global_filters": "GLOBAL FILTERS",
"ui.deck.mobile.filter.all": "ALL",


     // --- Shop

'ui.shop.brand.performance_upgrades_embedded': 'Performance Upgrades',
'ui.shop.common.unnamed': 'Unnamed',
'ui.shop.common.other': 'Other',
'ui.shop.error.trial_purchase_failed': 'Trial purchase failed',
'ui.shop.error.purchase_data_incomplete': 'Incomplete purchase data',
'ui.shop.error.trial_data_incomplete': 'Incomplete trial purchase data',
// --- ModuleShopDesktop: missing UI entries EN ---

'ui.shop.filter.all_industries': 'All Target Markets',
'ui.shop.filter.all_offers': 'All Offers',
'ui.shop.filter.industry': 'Target Market',
'ui.shop.filter.offer': 'Offer',
'ui.shop.filter.search_placeholder': '🔎 Title, Description, Creator ...',
'ui.shop.filter.recommended': 'Recommended',
'ui.shop.filter.assets_visible': 'Assets are shown',
'ui.shop.filter.assets_hidden': 'Assets are hidden',
'ui.shop.filter.refresh': 'Refresh',
'ui.shop.filter.reset': 'Reset filters',
'ui.shop.filter.assets': 'Assets',
'ui.shop.filter.perm': 'PERM',
'ui.shop.filter.trial': 'TRIAL',

'ui.shop.price.per_month': '/ month',

'ui.shop.section.battle_cards': 'BATTLE CARDS',
'ui.shop.section.scripts': 'SCRIPTS',
'ui.shop.section.cards_suffix': 'CARDS',
'ui.shop.section.scripts_suffix': 'SCRIPTS',

'ui.shop.card.recommended_badge': 'Recommended ☆',
'ui.shop.card.script': 'Script',
'ui.shop.card.status_asset': 'ASSET',
'ui.shop.card.status_team': 'TEAM',
'ui.shop.card.status_new': 'NEW',
'ui.shop.card.details': 'Details',
'ui.shop.card.buy_trial': '🛒 TRIAL',
'ui.shop.card.buy_perm': '🛒 PERM',

'ui.shop.cart.permanent_access': 'PERMANENT ACCESS',
'ui.shop.cart.cancel_monthly': 'Cancel monthly at any time',
'ui.shop.cart.trial_access': 'TRIAL ACCESS',
'ui.shop.cart.uses': 'Uses',
'ui.shop.cart.uses_upper': 'USES',
'ui.shop.cart.trial_block_notice': 'Locked for 2 months after use unless purchased',
'ui.shop.cart.remove': 'Remove',

'ui.shop.meta.creator': 'Creator',

'ui.shop.checkout.title': 'Checkout:',
'ui.shop.checkout.clear': 'Clear',
'ui.shop.checkout.subtotal': 'Subtotal',
'ui.shop.checkout.tax': 'VAT (19%)',
'ui.shop.checkout.total': 'TOTAL',
'ui.shop.checkout.pts_after': 'PTS AFTER',
'ui.shop.checkout.buy': 'BUY NOW',
'ui.shop.checkout.back': 'Back',

'ui.shop.details.heading': 'MODULE DETAILS',
'ui.shop.details.technique_type': 'Technique Type',
'ui.shop.details.sales_phase': 'Sales Stage',
'ui.shop.details.technique_application': 'Technique Application',
'ui.shop.details.trial_notice': 'Trial Notice',
'ui.shop.details.description': 'DESCRIPTION',
'ui.shop.details.description_empty': 'No description available.',
'ui.shop.details.video_placeholder': 'Video explanation will be added later',
'ui.shop.details.close': 'Close',

'ui.shop.member.buy_for': 'WHO ARE YOU BUYING FOR?',
'ui.shop.member.label': 'MEMBER',
'ui.shop.member.fallback': 'Member',
'ui.shop.member.self_suffix': ' (me)',
'ui.shop.member.cancel': 'Cancel',
'ui.shop.member.apply': 'Apply',

'ui.shop.brand.marketplace': 'MARKETPLACE',
'ui.shop.brand.performance_upgrades': 'PERFORMANCE UPGRADES',
'ui.shop.error.load_failed': 'Failed to load shop',
'ui.shop.error.purchase_failed': 'Purchase failed',

'ui.shop.filter.all_subcategories': 'All Subcategories',
'ui.shop.mobile.brand.shop': 'SHOP',
'ui.shop.mobile.brand.upgrades': 'UPGRADES',
'ui.shop.mobile.brand.vault_balance': 'VAULT BALANCE',
'ui.shop.mobile.checkout.points': 'Points',
'ui.shop.mobile.empty.no_results': 'No matching content found.',
'ui.shop.mobile.filter.global': 'GLOBAL FILTER',
'ui.shop.mobile.filter.max': 'MAX',



      // --- Chat Mobile

      "ui.mobile.action.park": "Park",
      "ui.mobile.goal.hidden_tap": "🔒 GOAL HIDDEN (Tap to reveal)",
      "ui.mobile.quick_reaction.cancel_selection": "Cancel technique selection",

      // --- Sidebar ---


'ui.sidebar.notebook.speech_total_short': 'TOTAL',
'ui.sidebar.notebook.speech_module_short': 'MODULE',
'ui.sidebar.notebook.speech_client_short': 'CLIENT',

'ui.sidebar.parked.checking_start_tooltip': 'Checking start',
'ui.sidebar.parked.other_check_running': 'Another technique is being checked',
'ui.sidebar.parked.unavailable': 'Currently unavailable',
'ui.sidebar.parked.cancel_selection': 'Cancel selection',
'ui.sidebar.parked.checking_start': 'Checking start …',

'ui.sidebar.notebook.deepness.clarity_reason': 'Clarity',
'ui.sidebar.notebook.deepness.emotion_reason': 'Emotion',
'ui.sidebar.notebook.deepness.scene_reason': 'Scene',
'ui.sidebar.notebook.deepness.meaning_reason': 'Meaning',
'ui.sidebar.notebook.deepness.magnitude_reason': 'Magnitude',
'ui.sidebar.notebook.deepness.consequence_reason': 'Consequence',

'ui.sidebar.notebook.deepness.insight': 'Insight:',
'ui.sidebar.notebook.deepness.mechanism': 'Mechanism:',
'ui.sidebar.notebook.deepness.advantage': 'Advantage:',
'ui.sidebar.notebook.deepness.meaning': 'Meaning:',

'ui.sidebar.action.parking': 'Parking …',
'ui.sidebar.action.terminating': 'Terminating …',
'ui.sidebar.action.terminate_new': 'End & Restart',

'ui.sidebar.manual_completion.heading': 'MANUAL COMPLETION',
'ui.sidebar.manual_completion.missing_variables': 'MISSING VARIABLES',
'ui.sidebar.manual_completion.close': 'Close dialog',
'ui.sidebar.manual_completion.missing_data': 'Missing Data',
'ui.sidebar.manual_completion.option_placeholder': 'Select option...',
'ui.sidebar.manual_completion.cancel': 'Cancel',
'ui.sidebar.manual_completion.apply': 'Apply',

'ui.sidebar.menu.tooltip': 'Menu',
'ui.sidebar.menu.placeholder': 'Select action...',

'ui.sidebar.notebook.psychology_live': 'PSYCHOLOGY (LIVE)',
'ui.sidebar.notebook.wait_for_profile': 'Waiting for profile...',

'ui.sidebar.exit.error_run_failed': 'EXIT ERROR: Run could not be ended properly',
'ui.sidebar.exit.abort_missing_ids': 'EXIT ABORTED: runID or branchID is missing',
'ui.sidebar.exit.error_prefix': 'EXIT ERROR:',
'ui.sidebar.exit.status': 'Status:',
'ui.sidebar.exit.response': 'Response:',



      // --- ChatArea ---
      'ui.chat.life.role.closer_label': 'Seller',
      'ui.chat.life.role.prospect_label': 'Prospect',
      'ui.chat.life.view.show_chat': 'Show chat',
      'ui.chat.life.view.show_video': 'Show video',


      // --- COMMON & INITIALIZATION ---
      'ui.common.initializing': 'Initializing...',
      'ui.common.detail': 'Detail...',
      'ui.common.none': 'None',
      'ui.common.unknown': 'Unknown',
      'ui.common.error.analysis': 'Analysis failed.',
      'ui.common.error.no_audio_data': 'No audio data available.',
      'ui.common.editable.click_to_edit': 'Click to edit',
      'ui.common.editable.empty': 'empty',
      'ui.common.editable.placeholder': 'Enter value...',

      // --- SYSTEM STATUS ---
      'ui.status.init_system': 'Initializing system...',
      'ui.status.loading_backstory': 'Loading backstory...',
      'ui.status.ready_user': 'Scenario ready. Your turn.',
      'ui.status.running': 'Scenario running.',
      'ui.status.ai_closer_thinking': 'AI (Closer) is thinking...',
      'ui.status.ai_prospect_reacting': 'AI (Prospect) is reacting...',
      'ui.status.waiting_closer': 'Waiting for you (Closer)...',
      'ui.status.error_logic': 'Error in logic processing.',
      'ui.status.creating_analysis': 'Creating session review...',
      'ui.status.module_resumed': 'Module resumed.',
      'ui.status.module_parked': 'Module parked.',
      'ui.status.module_terminated': 'Module terminated.',
      'ui.status.restarting': 'Restarting simulation...',
      'ui.status.simulation_ended': 'It just ended.',
      'ui.status.module_finished': 'Module finished.',
      'ui.status.restore_prefix': 'Restored: ',
      'ui.status.manual_override_prefix': 'Manual Override: ',
      'ui.status.mode_active_prefix': 'Mode Active: ',
      'ui.status.please_speak': '(Please speak)',
      'ui.status.handover': 'Handover',
      'ui.status.soft_restart_log': '--- Restart (Soft) ---',

      // --- SIDEBAR STATUS ---
      'ui.sidebar.status.header': 'Status',
      'ui.sidebar.status.ghost_pilot': 'Ghost Pilot',
      'ui.sidebar.status.life_mode': 'Life Mode',
      'ui.sidebar.status.system_focus': 'System Focus',
      'ui.sidebar.status.speech_share_module': 'Speech Share (Module)',
      'ui.sidebar.status.debug_header': 'Debugging',
      'ui.sidebar.status.debug_fallback': 'Waiting for logs...',
      'ui.sidebar.status.engine_version': 'ELITS ENGINE v2.3 Deep',
      'ui.sidebar.status.objection_active': 'Objection Handling Active',
      'ui.sidebar.status.ready': 'Ready',
      'ui.sidebar.status.ready_fallback': 'Ready',

      // --- GHOST & MODE TOOLTIPS ---
      'ui.sidebar.ghost.status_tooltip_active': 'Live Coach Active',
      'ui.sidebar.ghost.status_tooltip_inactive': 'Live Coach Inactive',
      'ui.sidebar.mode.life_desc': 'LIFE Mode: AI silent, coaching only',
      'ui.sidebar.mode.sim_desc': 'Sim Mode: AI speaks',

      // --- DEEPNESS LADDER ---
      'ui.sidebar.deepness.header': 'Depth (relevant)',
      'ui.sidebar.deepness.fallback': 'Waiting for analysis...',
      'ui.sidebar.deepness.clarity': 'Clarity',
      'ui.sidebar.deepness.emotion': 'Emotion',
      'ui.sidebar.deepness.scene': 'Scene',
      'ui.sidebar.deepness.meaning': 'Meaning',
      'ui.sidebar.deepness.magnitude': 'Magnitude',
      'ui.sidebar.deepness.consequence': 'Impact',

      // --- INTERVENTIONS & SUBSYSTEMS ---
      'ui.sidebar.subsystem.step': 'Step',
      'ui.sidebar.subsystem.skip': '✕ Skip',
      'ui.sidebar.action.park': 'Park module (resume later)',
      'ui.sidebar.action.terminate': 'Terminate module (without closing)',
      'ui.sidebar.action.skip_intervention': 'Skip intervention',
      'ui.sidebar.parked.header': 'Parked / Paused',
      'ui.sidebar.parked.resume': 'Resume',
      'ui.sidebar.parked.at': 'Parked at:',
      'ui.sidebar.objections.header': 'Objections (detected)',
      'ui.sidebar.objections.default': 'Objection',
      'ui.sidebar.objections.price': '💰 Budget / Price',
      'ui.sidebar.objections.time': '⏳ Time / Priority',
      'ui.sidebar.objections.partner': '🤝 Partner / Boss',
      'ui.sidebar.objections.fear': '🛑 Fear / Skepticism',
      'ui.sidebar.objections.stall': '💤 Sleep / Stall',

      // --- CHECKPOINTS ---
      'ui.sidebar.checkpoint.header': 'Checkpoints',
      'ui.sidebar.checkpoint.manual_done': 'MANUALLY COMPLETED',
      'ui.sidebar.checkpoint.waiting': 'WAITING',
      'ui.sidebar.checkpoint.done_tooltip': 'Completed',
      'ui.sidebar.checkpoint.force_tooltip': 'Click to Force',

      // --- GHOST PILOT HINTS ---
      'ui.sidebar.hint.intervention': '💡 INTERVENTION / SUBSYSTEM',
      'ui.sidebar.hint.recommendation': '💡 AI RECOMMENDATION',
      'ui.sidebar.hint.loading': 'Analyzing situation...',
      'ui.sidebar.hint.fallback': 'Waiting for next move...',
      'ui.sidebar.hint.apply_tooltip': 'Click to Apply',

      // --- NOTEBOOK SIDEBAR ---
      'ui.sidebar.notebook.speech_share_total': 'SPEECH SHARE (TOTAL)',
      'ui.sidebar.notebook.needs_header': 'Human Needs',
      'ui.sidebar.notebook.needs_fallback': 'Analyzing profile...',
      'ui.sidebar.notebook.variables_header': 'Session Data / Variables',
      'ui.sidebar.notebook.variables_fallback': 'No relevant variables.',
      'ui.sidebar.notebook.header': 'NOTEBOOK (CRM)',
      'ui.sidebar.notebook.maximize': 'MAXIMIZE',
      'ui.sidebar.notebook.fallback': 'Listening...',
      'ui.sidebar.notebook.empty_placeholder': 'No relevant variables.',
      'ui.sidebar.notebook.collapsed_label': 'Notebook',
      'ui.sidebar.notebook.toggle_tooltip_open': 'Open Notebook',
      'ui.sidebar.notebook.toggle_tooltip_collapsed': 'Minimize',
      'ui.sidebar.notebook.listening_fallback': 'Listening...',
      'ui.sidebar.notebook.no_fields': 'No fields',
      'ui.sidebar.notebook.cat_other': 'OTHER',
      'ui.sidebar.notebook.cat_module': 'MODULE',
      'ui.sidebar.notebook.cat_phase': 'PHASE',
      'ui.sidebar.notebook.missing_var_tooltip': 'Missing variable for completed goal',

      // --- NEEDS LABELS ---
      'ui.sidebar.needs.certainty': 'Certainty',
      'ui.sidebar.needs.variety': 'Variety',
      'ui.sidebar.needs.significance': 'Significance',
      'ui.sidebar.needs.connection': 'Connection',
      'ui.sidebar.needs.growth': 'Growth',
      'ui.sidebar.needs.contribution': 'Contribution',
      'ui.sidebar.speech.you': 'You:',
      'ui.sidebar.speech.client': 'Client:',

      // --- NOTEBOOK SECTIONS ---
      'ui.notebook.section.current': 'Current Situation',
      'ui.notebook.section.gap': 'Gap / Problems',
      'ui.notebook.section.goal': 'Goal / Desire',
      'ui.notebook.section.attempts': 'Past Attempts',
      'ui.notebook.section.motivation': 'Motivation',
      'ui.notebook.section.verbatim': 'Quotes',
      'ui.notebook.section.money': 'Budget',
      'ui.notebook.section.decision': 'Decision Path',
      'ui.notebook.section.commitment': 'Commitment / Closing',

      // --- NOTEBOOK MODAL ---
      'ui.modal.notebook.title': 'Notebook (CRM)',
      'ui.modal.notebook.subtitle': 'Full View & Variable Tracebility',
      'ui.modal.notebook.log_title': 'Extracted Variables (LOG)',
      'ui.modal.notebook.log_subtitle': 'Live captured data points. Yellow = required field missing.',

      // --- SETUP OVERLAY ---
      'ui.setup.brand.subtitle': 'Sales Engine',
      'ui.setup.identity.title': '01 Identity',
      'ui.setup.config.title': '02 Configuration',
      'ui.setup.context.title': '03 Context & Knowledge',
      'ui.setup.identity.closer_label': 'Closer Name',
      'ui.setup.identity.prospect_label': 'Prospect Name',
      'ui.setup.identity.mode_label': 'Mode / Role',
      'ui.setup.identity.source_label': 'Source',
      'ui.setup.config.scenario_label': 'Scenario Framework',
      'ui.setup.config.lang_label': 'language',
      'ui.setup.config.formality_label': 'Salutation',
      'ui.setup.config.formality_formal': 'Formal',
      'ui.setup.config.formality_informal': 'Informal',
      'ui.setup.config.focus_label': 'Training Focus',
      'ui.setup.context.persona_label': 'Target Persona',
      'ui.setup.context.kb_label': 'Offer / KB',
      'ui.setup.footer.launch': 'Launch',
      'ui.setup.footer.audio_streaming': 'Audio',
      'ui.setup.config.groq_logic': 'Ultra-Fast Logic (Groq)',
      'ui.setup.config.groq_text': 'Turbo Text Gen (Groq)',
      'ui.setup.config.flow_linear': 'Linear Flow',
      'ui.setup.config.flow_module': 'Specific Module',
      'ui.setup.config.flow_start': 'Start Module (Flow)',
      'ui.setup.config.flow_stop': 'Stop Module (Flow)',
      'ui.setup.config.module_start': 'Start Module',
      'ui.setup.config.module_stop': 'Stop Module',
      'ui.setup.role.prospect_label': 'Prospect (Coaching - AI leads)',
      'ui.setup.role.closer_label': 'Closer (Training - You lead)',
      'ui.setup.source.calendar': 'Appointment (Booked)',
      'ui.setup.source.ad': 'Advertisement',
      'ui.setup.source.cold': 'Cold Outreach',
      'ui.setup.persona.placeholder': 'Select profile...',
      'ui.setup.persona.group_presets': 'Presets',
      'ui.setup.persona.group_custom': 'Custom',
      'ui.setup.persona.create_new': '+ Create New Profile...',
      'ui.setup.kb.placeholder': 'Enter details about offer, price and terms...',

      // --- PERSONA MODAL ---
      'ui.setup.persona.modal.title': 'Create Custom Persona',
      'ui.setup.persona.modal.name': 'Profile Name',
      'ui.setup.persona.modal.source': 'Source Text (Chat/Email/Description)',
      'ui.setup.persona.modal.identifier': 'Identifier (Who is the client in the text?)',
      'ui.setup.persona.modal.role': 'Role',
      'ui.setup.persona.modal.industry': 'Industry',
      'ui.setup.persona.modal.relationship': 'Relationship',
      'ui.setup.persona.modal.cancel': 'Cancel',
      'ui.setup.persona.modal.save': 'Save',
      'ui.setup.persona.modal.generating': 'Generating...',
      'ui.setup.persona.modal.desc_placeholder': 'Paste chat history or description here...',
      'ui.setup.persona.modal.id_placeholder': "e.g. 'Max' or 'Person B'",
      'ui.setup.persona.modal.field_placeholders_role': 'CEO, Mother...',
      'ui.setup.persona.modal.field_placeholders_industry': 'Craft, IT...',
      'ui.setup.persona.modal.field_placeholders_rel': 'Cold, Client...',

      // --- CHAT AREA ---
      'ui.chat.agent.name': 'AI TRAINER',
      'ui.chat.button.finish': 'FINISH',
      'ui.chat.input.thinking': 'Thinking...',
      'ui.chat.input.listening': 'Listening...',
      'ui.chat.input.placeholder': 'Type or speak...',
      'ui.chat.input.desktop_active': 'Desktop Audio active... (Playing audio)',
      'ui.chat.tooltip.restart': 'Restart (Keep settings & history)',
      'ui.chat.tooltip.export_text': 'Download Transcript',
      'ui.chat.tooltip.export_audio': 'Download Audio (WAV)',
      'ui.chat.tooltip.mode_mic': 'Mode: Microphone (Default)',
      'ui.chat.tooltip.mode_desktop': 'Mode: Desktop Audio (System Sound)',
      'ui.chat.tooltip.audio_toggle_on': 'Audio (TTS) ON',
      'ui.chat.tooltip.audio_toggle_off': 'Audio (TTS) OFF',
      'ui.chat.mic.tooltip_start': 'Start Microphone',
      'ui.chat.mic.tooltip_stop': 'Stop Microphone',
      'ui.chat.desktop.tooltip_start': 'Share Desktop Audio & Start',
      'ui.chat.desktop.tooltip_stop': 'Stop Desktop Recording',
      'ui.chat.error.no_desktop_audio': '⚠️ No system audio detected!\n\nPlease enable "Share audio" in the bottom left when sharing.',
      'ui.chat.error.desktop_prefix': 'Error with Desktop Audio: ',

      // --- ANALYSIS VIEW ---
      'ui.analysis.title': 'Session Review',
      'ui.analysis.score_rank': 'Total Score / Rank',
      'ui.analysis.summary': 'Summary',
      'ui.analysis.strengths': 'Strengths',
      'ui.analysis.weaknesses': 'Weaknesses',
      'ui.analysis.top_questions': 'Top 3 Questions (Closer)',
      'ui.analysis.turn_by_turn': 'Turn-by-Turn Analysis',
      'ui.analysis.feedback_label': 'Feedback:',
      'ui.analysis.better_label': 'Better:',
      'ui.analysis.restart_button': 'Start New Session',
      'ui.analysis.label.rank': 'Overall Rank',
      'ui.analysis.label.details_depth': 'Depth: ',
      'ui.analysis.label.details_emotion': 'Emotion: ',
      'ui.analysis.label.closer_move': "Closer's Move",
      'ui.analysis.label.prospect_resp': "Prospect's Response",
      'ui.analysis.speaker.ai': 'AI',
      'ui.analysis.speaker.user': 'You',
      'ui.analysis.error.incomplete_title': 'Analysis Incomplete',
      'ui.analysis.error.incomplete_desc': 'The AI response was too long and got truncated.',

      // --- LOGIC CONTEXT FALLBACKS ---
      'ui.context.default_topic': 'Your topic',
      'ui.context.missing_prefix': 'the lack of ',
      'ui.context.current_problem': 'the current issue',
      'ui.context.solution_prefix': 'Solution for ',
      'ui.context.default_goal': 'Your goal',
      'ui.context.subsystem.fallback_situation': 'the described situation',
      'ui.context.subsystem.fallback_point': 'this point',

      // Defficulty Overlay
      'ui.setup.config.difficulty_label': 'Difficulty',
      'ui.setup.config.difficulty_rookie': 'Rookie',
      'ui.setup.config.difficulty_seller': 'Seller',
      'ui.setup.config.difficulty_closer': 'Closer',
      'ui.setup.config.difficulty_elite': 'Elite Closer',

      // Internalisation Overlay
      'ui.setup.config.internalisation_label': 'Memorization',
      'ui.setup.config.internalisation_none': 'None',
      'ui.setup.config.internalisation_random': 'Yes random',
      'ui.setup.config.internalisation_fast': 'Yes planned',

      // Sidebar
      'ui.sidebar.notebook.history_divider': 'History',
      'ui.sidebar.notebook.no_vars_extracted': 'No variables extracted.',
      'ui.sidebar.checkpoint.hidden_label': '🔒 Hidden',
      'ui.sidebar.checkpoint.reveal_tooltip': 'Click to reveal (-Points)',

      // Chat Area
      'ui.chat.hint_prefix': 'Recommendation:',
      'ui.chat.live_connection_status': 'Live Connection',
      'ui.chat.header.phase_fallback': 'Phase',
      'ui.chat.goal.hidden_click': '🔒 Goal hidden (Click)',

       // Vault
      'ui.hud.vault_label': 'Balance',
      'ui.hud.unit': 'Pts',
      'ui.hud.quality_gate': 'QUALITY GATE',


      // Setup Overlay App_neu
      'ui.setup.internalisation.hidden': 'Hidden',
      'ui.setup.error.persona_missing_fields': 'Name, Description and Mapping are required!',
      'ui.setup.error.missing_api_key': 'Please enter a valid API Key first.',
      'ui.setup.error.generation_failed': 'Generation failed.',
      'ui.setup.brand.mission_control': 'Mission Control',
      'ui.setup.placeholder.me_closer': 'Me (Closer)',
      'ui.setup.placeholder.ai_closer': 'Ai (Closer)',
      'ui.setup.placeholder.me_prospect': 'Me (Prospect)',
      'ui.setup.placeholder.ai_prospect': 'Ai (Prospect)',
      'ui.setup.persona.mapping.title': 'Mapping: What is the clients Name? (CRITICAL)',
      'ui.setup.persona.mapping.des': 	"So the AI knows who to play, enter the customer's name/identifier from the source text here. Examples: 'Max', 'Person B', 'Client'.",
      'ui.status.generating_opener': "Generate Opener...",
      'ui.status.init_persona_psych': 'Initializing Persona Psychology...',
      'ui.setup.error.api_key_top': 'Enter API Key above.',

      // MISSING UI
      'ui.chat.deepness.target': 'Target',
      'ui.setup.api.groq_label': 'Groq API',
      'ui.setup.api.openai_label': 'OpenAi API',
      'ui.setup.persona.modal.name_placeholder': 'e.g. Skeptical CFO',
      'ui.setup.persona.modal.identifier_placeholder': "e.g. 'Client' or 'Max'",
      'ui.setup.persona.modal.identifier_help': 'How is the client referred to in the source text? (e.g. "Max", "Client"). Without this, the AI does not know who to play.',
      'ui.setup.internalisation.hidden_percentage': 'Hidden',

      // LOGIC / SCORING
      'logic.score.penalty_reveal': 'Reveal',
      'logic.score.penalty_manual': 'Manually completed',
      'logic.score.goal_reached': 'Goal reached',
      'logic.score.bonus_depth': ' (+Depth)',
      'logic.score.multiplier_tone': ' (x Tone)',
      'logic.score.bonus_speech': 'Speech Share Top',
      'logic.score.bonus_efficiency': 'Efficiency Bonus',
      'logic.score.combo_double': 'Double Combo',
      'logic.score.combo_triple': 'Triple Combo',
      'logic.score.bonus_intervention': 'Objection solved',
      'logic.score.bonus_tonality': 'Tonality (Overall)',
      'logic.score.penalty_sandbox': 'Ghost Mode (1/5 Rule)',

      'ui.chat.error.mic_access': 'Microphone access denied:',
      'ui.chat.error.desktop_hardware': 'Desktop audio error:',

      'ui.setup.api.groq_placeholder': 'gsk_...',
      'ui.setup.api.openai_placeholder': 'sk-...',
      'ui.setup.placeholder.name_user': 'Your Name',
      'ui.setup.placeholder.name_client': 'Client Name',
      'ui.setup.role.suffix_ai': ' (Ai)',
      'ui.setup.role.suffix_me': ' (Me)',
      'ui.scenario.business': 'Business / Coaching',
      'ui.scenario.b2b': 'B2B / Corporate',
      'ui.scenario.health': 'Health / B2C',
      'ui.scenario.improvement': 'Self Improvement',
      'ui.lang.de': 'German',
      'ui.lang.en': 'English',
      'ui.sidebar.notebook.items_count': 'Items',
      'ui.analysis.turn_prefix': 'Turn',

      // Ergänze dies im 'en' Objekt:
      'ui.chat.fallback_repeat': 'Could you please repeat that?',
      'ui.status.missing_module': 'Module not found',
      'ui.status.intervention_started': 'Intervention started',
      'ui.status.intervention_done': 'Intervention completed',
      'ui.debug.intervention_finished': 'Intervention finished',
      'ui.trigger.phase': 'I am not sure.',

// --- SAVE & LOAD SYSTEM ---
      'ui.save.header': 'Saved Sessions (Local)',
      'ui.save.empty': 'No local savegames found.',
      'ui.save.label': 'Savegame',
      'ui.save.load_btn': 'Load',
      'ui.save.delete_tooltip': 'Delete',
      'ui.save.delete_confirm_title': 'Delete savegame permanently?',
      'ui.save.cancel': 'Cancel',
      'ui.save.confirm_delete': 'Yes, Delete',
      'ui.save.delete_error': 'Delete failed.',
      'ui.save.load_success': "Savegame '{key}' loaded successfully! Press 'Start Now'",
      'ui.save.load_error': 'Error loading savegame.',
      'ui.save.save_success': '✅ Savegame saved locally!',
      'ui.save.save_error_quota': '❌ Error saving (Quota exceeded?)',
      'ui.save.save_error_alert': 'Save failed! History might be too long for LocalStorage.',

      // --- SESSION CONTROL MODAL ---
      'ui.modal.control.title': 'Mission Control',
      'ui.modal.control.timetravel_title': '⏳ Time Travel (Flashback)',
      'ui.modal.control.confirm_jump': 'Really Jump?',
      'ui.modal.control.confirm_desc': 'Click to confirm (Progress will be lost)',
      'ui.modal.control.id_prefix': 'ID:',
      'ui.modal.control.active_badge': 'Active',
      'ui.modal.control.saves_title': '💾 Savegames',
      'ui.modal.control.save_current': 'Save Current State',

      'ui.modal.control.subline': 'ELITS MISSION CONTROL',
      'ui.modal.control.timeline_empty': 'No history available.',
      'ui.modal.control.show_less': 'Show less',
      'ui.modal.control.show_all_count': 'Show all ({count})',



      "ui.status.loading": "Loading...",
      "ui.status.chaining": "Chaining...",
      "ui.status.complete": "Complete",
      "ui.status.save_loaded": "Savegame loaded. Please check settings and start.",
      "ui.error.timetravel_failed": "Time travel failed.",
      "ui.error.load_failed": "Error loading savegame.",
      "ui.error.generic": "Error.",
      "ui.sidebar.action.save": "Save",
      "ui.save.load_tooltip": "Click to load",
      "ui.debug.module_active": "Module Active",
      "ui.debug.module_response": "Module Response",
      "ui.debug.silent_transition": "Silent Transition (No Input)",
      "ui.debug.subsystem_active": "Subsystem Active",
      "ui.debug.intervention_aborted": "Intervention Aborted (Goal Met)",
      "ui.debug.executing_subsystem": "Executing Subsystem Step",
      "ui.debug.background_update": "Background Update",
      "ui.debug.module_finished_pre": "Module Finished (Pre-Recursion)",
      "ui.debug.closer_turn_complete": "Closer Turn Complete",
      "ui.eval.deepness_missing": "No deepness analysis available (missing data).",
      "ui.eval.goal_not_met": "Goal not met.",
      "ui.eval.info_incomplete": "Information incomplete.",
      "ui.mastery.mastered": "MASTERED: Full blind run passed.",
      "ui.mastery.repeat_revealed": "REPEAT: Peeked {n}x. Repeating level.",
      "ui.mastery.repeat_manual": "REPEAT: Revealed {n}x. Repeating level.",
      "ui.mastery.levelup": "LEVEL UP: Clean run. Hidden: {a} -> {b}",
      "ui.chat.fallback_repeat_short": "Repeat please?",
      "ui.chat.fallback_not_understood": "I didn't understand that.",

      'ui.status.session_restored': 'Session restored.',
      'ui.save.corrupt_error': 'Savegame corrupt or invalid.',
      
      'ui.mastery.no_data': 'No Data.',
      'ui.error.no_active_module': 'Error: No active module found.',
      'ui.status.module_completed_logic': 'Module completed.',
      'ui.error.hint_generation_failed': 'Could not generate hint.',
      'ui.logic.intervention_step_fallback': 'Intervention Step',
      
      'ui.analysis.no_turns': 'No complete turns found.',
      'ui.analysis.fallback_title': 'Analysis Incomplete',
      'ui.analysis.fallback_summary': 'The AI response was too long and got truncated.',
      'ui.analysis.failed_generic': 'Analysis failed.',
      
      'ui.eval.vars_missing': 'Variables missing/invalid',
      'ui.prefix.module': '[Module]',
      'ui.prefix.intervention': '[Intervention]',
      'ui.suffix.completion': '[Completion]',
      'logic.score.suffix_tone': '(x Tone)',

      "ui.setup.button.mobile_interface": "📱 Mobile Interface",
      "ui.setup.label.deepgram_key": "DEEPGRAM KEY",
      "ui.setup.placeholder.token": "Token...",
      "ui.setup.button.battle_deck": "BATTLE DECK",

      "ui.sidebar.settings.vad_label": "Pause Duration",
      "ui.sidebar.settings.vad_fast": "Reactive",
      "ui.sidebar.settings.vad_slow": "Relaxed",
      "ui.toast.type.warning": "Notice",
      "ui.toast.type.info": "Info",

      "ui.mobile.input.placeholder": "Message...",
      "ui.mobile.nav.chat": "Chat",
      "ui.mobile.nav.crm": "CRM",
      "ui.mobile.nav.deck": "Deck",
      "ui.mobile.nav.data": "Data",
      "ui.mobile.nav.score": "Score",
      "ui.mobile.header.park_tooltip": "Park Module",

      "ui.mobile.notebook.subline": "CRM DASHBOARD",
      "ui.mobile.notebook.title": "Notebook",
      "ui.mobile.notebook.status.live": "LIVE INTEL",
      "ui.mobile.notebook.status.archive": "ARCHIVE",
      "ui.mobile.notebook.divider.history": "History",

      "ui.mobile.deck.subline": "⚡ INTERVENTION DECK",
      "ui.mobile.deck.title": "Battledeck",
      "ui.mobile.deck.badge.detected_missing": "⚠️ DETECTED (MISSING)",
      "ui.mobile.deck.badge.recommended": "🎯 RECOMMENDED",
      "ui.mobile.deck.empty": "DECK EMPTY",

      "ui.mobile.analytics.subline": "SESSION METRICS",
      "ui.mobile.analytics.title": "Analytics",
      "ui.mobile.analytics.card.psychology": "PSYCHOLOGY",
      "ui.mobile.analytics.no_data": "No data available.",
      "ui.mobile.analytics.card.needs": "DRIVERS & NEEDS",

      "ui.mobile.score.subline": "PERFORMANCE REPORT",
      "ui.mobile.score.title": "Scorecard",
      "ui.mobile.score.empty": "No data.",

      "ui.mobile.dynamics.title": "DYNAMICS",
      "ui.mobile.dynamics.legend.you": "YOU (CLOSER)",
      "ui.mobile.dynamics.legend.client": "CLIENT",
      "ui.mobile.dynamics.donut.share": "Share",
      "ui.mobile.dynamics.kpi.goals": "GOALS",
      "ui.mobile.dynamics.kpi.manual": "MANUAL",
      "ui.mobile.dynamics.kpi.avg_turns": "Ø TURNS",
      "ui.mobile.dynamics.kpi.total_turns": "TURNS",

      "ui.radar.trust": "TRUST",
      "ui.radar.offer": "OFFER",
      "ui.radar.clarity": "CLARITY",
      "ui.radar.pain": "PAIN",
      "ui.radar.ownership": "OWNER",
      "ui.radar.self": "SELF",
      "ui.radar.openness": "OPENNESS",

      "ui.deck.subline": "⚡ INTERVENTION DECK",
      "ui.deck.title": "Battledeck",
      "ui.deck.capacity_label": "MEMORY SLOTS",
      "ui.deck.loadout_title": "ACTIVE LOADOUT",
      "ui.deck.button.clear": "CLEAR",
      "ui.deck.slot.empty": "OPEN SLOT",
      "ui.deck.count_suffix": " CARDS",
      "ui.deck.equipped_suffix": " ACTIVE",
      "ui.deck.button.cancel": "CANCEL",
      "ui.deck.button.start": "START SYSTEM"

    }
};


export const DIFFICULTY_RULES: DifficultyRulesRegistry = {
  // -----------------------------------------------------------------------------------------
  // LEVEL 1: ROOKIE (LERNMODUS)
  // Philosophie: Frustfrei lernen. Alles sichtbar. Fehler werden verziehen.
  // -----------------------------------------------------------------------------------------
  rookie: { 
    microGoalRules: { 
      // Scoring (Bestehend)
      hintsCostPoints: false, 
      deepnessCountsForScore: false, 
      turnPenaltyPerRedTurn: -1, 
      hintPenaltyPerClick: 0, 
      deepnessBonusPoints: 2, 
      toneMultiplier: 1, 
      turnLimitBonus: 3,

      // --- NEU: LOGIK & VERHALTEN ---
      revealPenaltyPoints: 1,       // 🔍 Aufdecken kostet nichts
      fastManualOverridePenaltyPoints: 0,
      deepnessBlocksProgress: false,// 🛑 Oberflächliche Antworten erlauben Weiterkommen
      deepnessThresholdModifier: 0, // 🛑 Keine Erhöhung der Hürde
      tonalityBlocksProgress: false,// 🛑 Falscher Ton blockiert nicht
      tonalityRequiresMatch: false  // 🎯🔝 Ton egal für Score
    }, 
    moduleRules: {
      comboPoints: 2,
      triplePoints: 4,
      speechCorridorPoints: 2,
      speechNearPoints: 1,
      integrityKillsSpeech: false,
      integrityKillsCombos: false,
      integrityKillsTriples: false,
      integrityKillsIntervention: false,
      efficiencyTurnThreshold: 3.0,
      efficiencyBonusPerGoal: 1,
      finishTreshold: 0.5,
      speechThresholds: {
        low: { corridorMaxDeviation: 0.15, nearMaxDeviation: 0.25 },
        medium: { corridorMaxDeviation: 0.15, nearMaxDeviation: 0.25 },
        high: { corridorMaxDeviation: 0.15, nearMaxDeviation: 0.25 }
      },
      // --- NEU ---
      tonalityBonusPoints: 1,    // Kein spezieller Tonalitätsbonus
      objectionPointsPerMicroGoal: 3,
    }, 

sessionRules: {
  personaStartProfileModifiers: {
    calculated_trust_in_closer: 15,
    calculated_trust_in_self: 0,
    calculated_trust_in_offer: 15,
    calculated_clarity_now: 0,
    calculated_clarity_consequence: +5,
    calculated_skepticism: -15,
    calculated_commitment_ownership: 0
  },

   outcomeCompletionMultiplier: 0.5,
},
    
    // --- NEU: UI VERHALTEN ---
    uiRules: {
      goalVisibility: 'visible',    // Alles lesbar
      allowReveal: true,            // Auge-Icon aktiv (aber sinnlos da visible)
      showRealtimeAnalysis: true    // Deepness-Balken live sichtbar
    }
  },

  // -----------------------------------------------------------------------------------------
  // LEVEL 2: SELLER (STANDARD)
  // Philosophie: Solide Leistung. Deepness wird gefordert, Internalisierung optional.
  // -----------------------------------------------------------------------------------------
  seller: { 
    microGoalRules: { 
      hintsCostPoints: true, 
      deepnessCountsForScore: true, 
      turnPenaltyPerRedTurn: -1, 
      hintPenaltyPerClick: -2, 
      deepnessBonusPoints: 2, 
      toneMultiplier: 1.2, 
      turnLimitBonus: 2,

      // --- NEU: LOGIK & VERHALTEN ---
      revealPenaltyPoints: 2,       // 🔍 Kleiner Abzug beim Aufdecken (falls User blurrt)
      fastManualOverridePenaltyPoints: 4,
      deepnessBlocksProgress: true, // 🛑 Ohne Tiefe geht es nicht weiter (Soft Block)
      deepnessThresholdModifier: 0, // 🛑 Standard Hürden
      tonalityBlocksProgress: false,// 🛑 Falscher Ton warnt nur
      tonalityRequiresMatch: false  // 🎯🔝 Ton gibt Bonus, ist aber keine Pflicht
    }, 
    moduleRules: {
      comboPoints: 3,
      triplePoints: 5,
      speechCorridorPoints: 3,
      speechNearPoints: 2,
      integrityKillsSpeech: true,
      integrityKillsCombos: false,
      integrityKillsTriples: false,
      integrityKillsIntervention: false,
      efficiencyTurnThreshold: 2.0,
      efficiencyBonusPerGoal: 2,
      finishTreshold: 0.6,
      speechThresholds: {
        low: { corridorMaxDeviation: 0.10, nearMaxDeviation: 0.20 },
        medium: { corridorMaxDeviation: 0.10, nearMaxDeviation: 0.20 },
        high: { corridorMaxDeviation: 0.10, nearMaxDeviation: 0.20 }
      },
      // --- NEU ---
      tonalityBonusPoints: 3,        // Kleiner Bonus für gute Gesamt-Tonalität
      objectionPointsPerMicroGoal: 5,
    }, 

sessionRules: {
  personaStartProfileModifiers: {
    calculated_trust_in_closer: 0,
    calculated_trust_in_self: 0,
    calculated_trust_in_offer: 0,
    calculated_clarity_now: 0,
    calculated_clarity_consequence: 0,
    calculated_skepticism: 0,
    calculated_commitment_ownership: 0
  },

  outcomeCompletionMultiplier: 1.0,

},

    // --- NEU: UI VERHALTEN ---
    uiRules: {
      goalVisibility: 'blurred',    // Standard sichtbar (User kann selbst blurren)
      allowReveal: true,
      showRealtimeAnalysis: true
    }
  },

  // -----------------------------------------------------------------------------------------
  // LEVEL 3: CLOSER (PROFI)
  // Philosophie: High-Performance. Blind Mode aktiv. Tiefe & Tonalität wichtig.
  // -----------------------------------------------------------------------------------------
  closer: { 
    microGoalRules: { 
      hintsCostPoints: true, 
      deepnessCountsForScore: true, 
      turnPenaltyPerRedTurn: -2, 
      hintPenaltyPerClick: -3, 
      deepnessBonusPoints: 2, 
      toneMultiplier: 1.5, 
      turnLimitBonus: 5,

      // --- NEU: LOGIK & VERHALTEN ---
      revealPenaltyPoints: 5,       // 🔍 Spürbare Strafe fürs "Spicken"
      fastManualOverridePenaltyPoints: 10,
      deepnessBlocksProgress: true, // 🛑 Harter Block bei fehlender Tiefe
      deepnessThresholdModifier: 1, // 🛑 Standard Hürden
      tonalityBlocksProgress: false,// 🛑 Noch kein Block, aber...
      tonalityRequiresMatch: true   // 🎯🔝 ...für den Punkt MUSS der Ton stimmen!
    }, 
    moduleRules: {
      comboPoints: 5,
      triplePoints: 7,
      speechCorridorPoints: 5,
      speechNearPoints: 2,
      integrityKillsSpeech: true,
      integrityKillsCombos: true,
      integrityKillsTriples: true,
      integrityKillsIntervention: true,
      efficiencyTurnThreshold: 1.8,
      efficiencyBonusPerGoal: 3,
      finishTreshold: 0.7,
      speechThresholds: {
        low: { corridorMaxDeviation: 0.08, nearMaxDeviation: 0.15 },
        medium: { corridorMaxDeviation: 0.08, nearMaxDeviation: 0.15 },
        high: { corridorMaxDeviation: 0.08, nearMaxDeviation: 0.15 }
      },
      // --- NEU ---
      tonalityBonusPoints: 5,        // Hoher Bonus für Tonalität
      objectionPointsPerMicroGoal: 8,
    }, 

sessionRules: {
  personaStartProfileModifiers: {
    calculated_trust_in_closer: -10,
    calculated_trust_in_self: 0,
    calculated_trust_in_offer: -5,
    calculated_clarity_now: 0,
    calculated_clarity_consequence: -5,
    calculated_skepticism: 10,
    calculated_commitment_ownership: 0
  },
  outcomeCompletionMultiplier: 1.5,

},

    // --- NEU: UI VERHALTEN ---
    uiRules: {
      goalVisibility: 'hidden',    // 🔍 Standardmäßig Milchglas (Blind Mode)
      allowReveal: true,            // Darf aufgedeckt werden (kostet 5 Pkt)
      showRealtimeAnalysis: true
    }
  },

  // -----------------------------------------------------------------------------------------
  // LEVEL 4: ELITE (MEISTER)
  // Philosophie: Perfektion. Blindflug. Höhere Hürden. Tonalität ist Pflicht.
  // -----------------------------------------------------------------------------------------
  elite: { 
    microGoalRules: { 
      hintsCostPoints: true, 
      deepnessCountsForScore: true, 
      turnPenaltyPerRedTurn: -3, 
      hintPenaltyPerClick: -5, 
      deepnessBonusPoints: 2, 
      toneMultiplier: 2, 
      turnLimitBonus: 10,

      // --- NEU: LOGIK & VERHALTEN ---
      revealPenaltyPoints: 10,      // 🔍 "Highscore-Killer" Strafe beim Spicken
      fastManualOverridePenaltyPoints: 20,
      deepnessBlocksProgress: true, // 🛑 Blockiert
      deepnessThresholdModifier: 2, // 🛑 EXTREME: Erfordert +1 auf alle Deepness-Werte (5 wird 6)
      tonalityBlocksProgress: false, // 🛑 Falscher Ton BLOCKIERT den Fortschritt!
      tonalityRequiresMatch: true   // 🎯🔝 Nur perfekter Ton zählt
    }, 
    moduleRules: {
      comboPoints: 10,
      triplePoints: 15,
      speechCorridorPoints: 10,
      speechNearPoints: 5,
      integrityKillsSpeech: true,
      integrityKillsCombos: true,
      integrityKillsTriples: true,
      integrityKillsIntervention: true,
      efficiencyTurnThreshold: 1.6,
      efficiencyBonusPerGoal: 5,
      finishTreshold: 0.85,
      speechThresholds: {
        low: { corridorMaxDeviation: 0.05, nearMaxDeviation: 0.10 },
        medium: { corridorMaxDeviation: 0.05, nearMaxDeviation: 0.10 },
        high: { corridorMaxDeviation: 0.05, nearMaxDeviation: 0.10 }
      },
      // --- NEU ---
      tonalityBonusPoints: 10,       // Maximaler Bonus
      objectionPointsPerMicroGoal: 12,
    }, 
  
sessionRules: {
  personaStartProfileModifiers: {
    calculated_trust_in_closer: -20,
    calculated_trust_in_self: 0,
    calculated_trust_in_offer: -15,
    calculated_clarity_now: 0,
    calculated_clarity_consequence: -10,
    calculated_skepticism: 20,
    calculated_commitment_ownership: 0
  },
   outcomeCompletionMultiplier: 2.5,
},


    // --- NEU: UI VERHALTEN ---
    uiRules: {
      goalVisibility: 'hidden',     // 🔍 Komplett unsichtbar (Hardcore)
      allowReveal: true,            // Notfall-Button erlaubt (kostet aber 10 Pkt)
      showRealtimeAnalysis: true   // 🚫 Kein visuelles Feedback ("Flieg nach Gefühl")
    }
  }
};



export function resolvePriority(priority: PriorityConfig, scenario: string): number {
    if (typeof priority === 'number') return priority;
    if (priority[scenario] !== undefined) return priority[scenario];
    return priority.default;
}

export const MODULE_TRIGGER_PHRASES: Record<string, string> = {
    "mod_handle_price": "Puh, also ganz ehrlich: Das ist echt eine Menge Geld. Das ist mir eigentlich zu teuer.",
    "mod_handle_time": "Das klingt alles super, aber ich habe aktuell einfach absolut keine Zeit dafür. Ich bin komplett Land unter.",
    "mod_handle_partner": "Das klingt gut, aber das kann ich nicht alleine entscheiden. Ich muss da erst mit meinem Partner sprechen.",
    "mod_handle_stall": "Ich muss da erst noch mal eine Nacht drüber schlafen. Ich treffe keine Entscheidungen sofort.",
    "mod_handle_fear": "Ich weiß nicht... das klingt alles zu schön, um wahr zu sein. Ich bin da skeptisch, ob das wirklich klappt."
};




// 1. GLOBAL INTERRUPT RULES (The "Global Monitor")
export const GLOBAL_INTERRUPTS: InterruptRule[] = [
{
  id: 'global_seller_conversation_end',
  priority: 999,
  allowedScenarios: ['all'],

  triggerCondition: {
    var: 'seller_conversation_end_signal',
    op: 'eq',
    value: true
  },

  opensRunOutcomeModal: true,

  detectionDefinition: `
Erkenne ausschließlich, ob der Verkäufer/Closer das Gespräch eindeutig beendet hat.

Bestätige diese Variable nur, wenn die letzte relevante Verkäufer-/Closer-Nachricht eine klare Verabschiedung, Abschlussformel oder eindeutige Gesprächsbeendigung enthält.

Eine Verabschiedung, Absage, Zustimmung oder Abschlussformel des Prospects allein reicht niemals aus.

Der Prospect kann Abschluss, Follow-Up, Disqualifikation oder Ablehnung signalisieren. Das Gespräch gilt aber erst dann als beendet, wenn der Verkäufer/Closer selbst das Gespräch beendet oder sich verabschiedet.

Wenn nur der Prospect das Gespräch beendet oder sich verabschiedet, aber der Verkäufer/Closer noch nicht, darf die Variable nicht bestätigt werden.
`
}

];

const GLOBAL_INTERRUPS_CUT = [
    {
        id: "ir_identity_long_ago",
        targetModuleId: "mod_identity_based_selling_framework",
        triggerCondition: { var: "5_other_letzter_versuch_wann", op: "contains", value: "Jahr" }, 
        triggerPopSpecific: { var: "mod_other_identity_bridge_positioned", op: "truthy" },
        detectionDefinition: "Automatischer Trigger in Phase 6, wenn der letzte Versuch Jahre zurückliegt.",
        allowedModules: [], 
        priority: 45,
        scope: "once_global",
        returnBehavior: "resume_next",
        allowedScenarios: ["all"]
    },
    {
        id: "ir_stall_sleep",
        triggerCondition: { var: "objection_stall_detected", value: "true", op: "truthy" },
        triggerPopSpecific: { var: "stall_objection_resolved", op: "truthy" },
        targetModuleId: "mod_handle_stall",
        detectionDefinition: "Triggered NUR, wenn der User sagt 'Ich muss erst darüber schlafen', 'Ich treffe keine Entscheidungen heute', 'Ich muss mir das durch den Kopf gehen lassen'. EXIT wenn er sagt 'Okay, ich entscheide jetzt' oder 'Lass es uns machen'.",
        allowedModules: [], 
        priority: 50, 
        scope: "once_global", 
        returnBehavior: "resume_exact",
        allowedScenarios: ["all"]
    },
    {
        id: "ir_price_objection",
        triggerCondition: { var: "objection_price_detected", value: "true", op: "truthy" },
        targetModuleId: "mod_handle_price",
        triggerPopSpecific: { var: "price_accepted", op: "truthy" },
        detectionDefinition: "Triggered NUR wenn der User explizit sagt, dass es 'zu teuer' ist, 'kein Budget da ist' oder 'der Preis zu hoch ist'. NICHT bei allgemeinen Fragen nach dem Preis. EXIT wenn User sagt 'Der Preis ist okay', 'Das kriege ich hin' oder 'Verstanden'.",
        allowedModules: [], 
        priority: { default: 40, health: 60, business: 50, b2b: 30 }, 
        scope: "once_global", 
        returnBehavior: "resume_exact", 
        maxExecutions: 3 ,
        allowedScenarios: ["all"],
    },
    {
        id: "ir_time_objection",
        triggerCondition: { var: "objection_time_detected", value: "true", op: "truthy" },
        triggerPopSpecific: { var: "time_objection_resolved", op: "truthy" },
        targetModuleId: "mod_handle_time",
        detectionDefinition: "Triggered NUR, wenn der User sagt 'Ich habe aktuell keine Zeit', 'Zuviel Stress', 'Erst nächsten Monat'. EXIT wenn User sagt 'Ich nehme mir die Zeit', 'Das hat Priorität' oder 'Zeit passt'.",
        allowedModules: [], 
        priority: { default: 30, b2b: 45 },
        scope: "once_global", 
        returnBehavior: "resume_exact",
        allowedScenarios: ["all"]
    },
    {
        id: "ir_partner_objection",
        triggerCondition: { var: "objection_partner_detected", value: "true", op: "truthy" },
        triggerPopSpecific: { var: "partner_objection_resolved", op: "truthy" },
        targetModuleId: "mod_handle_partner",
        detectionDefinition: "Triggered NUR, wenn der User sagt 'Ich muss meinen Partner fragen', 'Das entscheide ich nicht allein', 'Ich muss mit meiner Frau/Mann reden' oder 'Chef fragen'. EXIT wenn User sagt 'Ich entscheide doch alleine' oder 'Partner ist einverstanden'.",
        allowedModules: [], 
        priority: { default: 20, b2b: 60, health: 20, business: 25 },
        scope: "once_global", 
        returnBehavior: "resume_exact",
        allowedScenarios: ["all"]
    },
    {
        id: "ir_fear_objection",
        triggerCondition: { var: "objection_fear_detected", value: "true", op: "truthy" },
        triggerPopSpecific: { var: "fear_objection_resolved", op: "truthy" },
        targetModuleId: "mod_handle_fear",
        detectionDefinition: "Triggered, wenn Unsicherheit, Skepsis oder Angst erkennbar wird, oder der Gesprächspartner bestätigt, dass er Angst hat oder sich unsicher fühlt. Also zum Beispiel wenn der User soetwas sagt wie 'Das klingt zu schön um wahr zu sein', 'Ich wurde schonmal verarscht', 'Wo ist der Haken?'. EXIT wenn User sagt 'Ich vertraue dir', 'Das klingt seriös' oder 'Risiko passt'.",
        allowedModules: [], 
        priority: 10,
        scope: "once_global", 
        returnBehavior: "resume_exact",
        allowedScenarios: ["business", "improvement", "health"]
    },
    {
        id: "mod_handle_fear_b2b",
        triggerCondition: { var: "objection_fear_detected", value: "true", op: "truthy" },
        triggerPopSpecific: { var: "fear_objection_resolved", op: "truthy" },
        targetModuleId: "mod_handle_fear_b2b",
        detectionDefinition: "Triggered NUR, wenn der User 'Skeptisch' ist, sagt 'Das klingt zu schön um wahr zu sein', 'Ich wurde schonmal verarscht', 'Wo ist der Haken?'. EXIT wenn User sagt 'Ich vertraue dir', 'Das klingt seriös' oder 'Risiko passt'.",
        allowedModules: [], 
        priority: 10,
        scope: "once_global", 
        returnBehavior: "resume_exact",
        allowedScenarios: ["b2b"]
    }
];

// 2. MODULE REGISTRY (The "Building Blocks")
export const ELITS_MODULE_REGISTRY: Record<string, ElitsModule> = {};
const ELITS_CUT = {
  "flow_phase_0_tech": {
    id: "flow_phase_0_tech",
    type: "phase",
    label: "Phase 0: Tech / Arrival",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: ["repair_misunderstanding", "classification"],
    microGoals: [
      {
        text: "Zustimmung vom Interessenten dafür, dass wir gut gesehen und gehört werden (Tech-Check) sowie Begrüßung und Klärung des Namens.",
        model: ["all"],
        variables: ["0_tech_check_complete"],
        requiredTonality: "neugierig",
        voraussetzung: null,
        specificQuestions: [
          "Können Sie mich gut hören und sehen?",
          "Mit der Technik alles in Ordnung - Sie hören und sehen mich gut?",
          "Hallo, ich bin [Name], wie darf ich Sie heute ansprechen?",
          "Kurzer Check vorab: Bild und Ton passen bei Ihnen?"
        ]
      }
    ],
    tonality: {
      intention: "Rahmen setzen. Status Quo verstehen. Druck rausnehmen.",
      emotion: "Neutral, Professionell, Hilfsbereit",
      pace: "Normal",
      allowed_cues: [],
      forbidden_words: ["Angebot", "Lösung", "Verkauf", "Fantastisch", "Spannend", "Pitch"],
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.",
      clarify_questions: [],
      probe_questions: []
    },
    questionsPool: [
      "Können Sie mich gut hören und sehen?",
      "Wie darf ich Sie ansprechen?",
      "Hallo, kurze Technik-Frage: Verstehen Sie mich gut?",
      "Sind wir bereit zu starten, klappt die Technik?"
    ],
    nextModule: ["flow_phase_1_connection"],
    metaScript: {
      isScriptStart: true,
      version: 1,
      isValid: true,
      scriptLabel: "Sales Call – Standard",
      scriptStatus: "active"
    },
shopInfo: {
  category: "SCRIPT",
  tier: "BASIC",
  unlockMode: "BOTH",

  // PERM
  pricePointsPermanent: 11999,
  priceEurPermanent: 399,

  // TRIAL (USES)
  pricePointsTemporary: 2000,
  priceEurTemporary: 0,
  temporaryGrantType: "USES",
  temporaryUses: 1,

  description: "ELITS Sales Script: vollständiger Run inkl. aller Module dieses Scripts.",
  icon: "📜"
},
  scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_1_connection": {
    id: "flow_phase_1_connection",
    type: "phase",
    label: "Phase 1: Call 1 / Connection & Rahmen",
    speechShareType: "low",
    allowedSubsystems: ["classification"],
    microGoals: [
      {
        text: "Der Interessent hat in eigenen Worten beschrieben, was ihn neugier an diesem Gespräch gemacht hat.",
        model: ["all"],
        variables: ["1_focus_neugier_grund"],
        requiredTonality: "neugierig",
        voraussetzung: null,
        specificQuestions: [
          "Was wäre aus Ihrer Sicht eine gute Möglichkeit, diese Zeit gemeinsam zu verbringen?",
          "Wie sind Sie zu dem Entschluss gekommen, möglicherweise externe Hilfe in Anspruch zu nehmen?"
        ]
      },
      {
        text: "Der Interessent hat erklärt, warum dieses Thema {1_focus_neugier_grund} JETZT für ihn wichtig / relevant ist.",
        model: ["all"],
        variables: ["1_focus_dringlichkeit_jetzt", "1_focus_einordnung_thema"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 5, meaning: 4 },
        voraussetzung: null,
        variableConfiguration:[{variable: '1_focus_einordnung_thema',
        outcome:["Fehlende Kenntnisse und Skills", "mehr Sicherheit", "Einkommen", "mehr Flexibilität"]}],
        specificQuestions: [ "Weshalb gerade jetzt?", "Warum ist das Thema aktuell?" ]
      },
      {
        text: "Der Interessent hat seine konkrete Erwartung an dieses Gespräch formuliert, wenn es um {1_focus_einordnung_thema} Flexibilität geht.",
        model: ["all"],
        variables: ["1_focus_konkrete_erwartung", "1_focus_themen_fokus"],
        requiredTonality: "sicher",
        voraussetzung: { var: "1_focus_einordnung_thema", op: "eq", value: "mehr Flexibilität" },
        requiredDeepness: { clarity: 5, meaning: 4 },
        specificQuestions: [
          "Was möchten Sie heute besprechen, damit wir uns auf Sie konzentrieren können und auf das, was Sie suchen?",
          "Was erhoffen Sie sich von einer möglichen Zusammenarbeit mit mir?"
        ]
      },
      {
        text: "Der Interessent hat seine konkrete Erwartung an dieses Gespräch formuliert, wenn es um {1_focus_einordnung_thema} Einkommen geht.",
        model: ["all"],
        variables: ["1_focus_konkrete_erwartung", "1_focus_themen_fokus"],
        requiredTonality: "sicher",
        voraussetzung: { var: "1_focus_einordnung_thema", op: "eq", value: "Einkommen" },
        requiredDeepness: { clarity: 5, meaning: 4 },
        specificQuestions: [
          "Was möchten Sie heute besprechen, damit wir uns auf Sie konzentrieren können und auf das, was Sie suchen?",
          "Was erhoffen Sie sich von einer möglichen Zusammenarbeit mit mir?"
        ]
      },
      {
        text: "Der Interessent hat seine konkrete Erwartung an dieses Gespräch formuliert, wenn es um {1_focus_einordnung_thema} Sicherheit geht.",
        model: ["all"],
        variables: ["1_focus_konkrete_erwartung", "1_focus_themen_fokus"],
        requiredTonality: "sicher",
        voraussetzung: { var: "1_focus_einordnung_thema", op: "eq", value: "mehr Sicherheit" },
        requiredDeepness: { clarity: 5, meaning: 4 },
        specificQuestions: [
          "Was möchten Sie heute besprechen, damit wir uns auf Sie konzentrieren können und auf das, was Sie suchen?",
          "Was erhoffen Sie sich von einer möglichen Zusammenarbeit mit mir?"
        ]
      }            
    ],
    tonality: {
      intention: "Rahmen setzen. Status Quo verstehen. Druck rausnehmen.",
      emotion: "Neutral, Professionell, Hilfsbereit",
      pace: "Normal",
      allowed_cues: CUES_TRUST,
      forbidden_words: ["Angebot", "Lösung", "Verkauf", "Fantastisch", "Spannend"],
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.",
      clarify_questions: ["Haben Sie schon etwas gefunden?", "Suchen Sie noch aktiv?"],
      probe_questions: ["Was genau suchen Sie?", "Was ist Ihnen dabei wichtig?", "Weshalb gerade jetzt?", "Warum ist das Thema aktuell?"]
    },
    questionsPool: [
      "Was wäre aus Ihrer Sicht eine gute Möglichkeit, diese Zeit gemeinsam zu verbringen?",
      "Was möchten Sie heute besprechen, damit wir uns auf Sie konzentrieren können und auf das, was Sie suchen?",
      "Wie sind Sie zu dem Entschluss gekommen, möglicherweise externe Hilfe in Anspruch zu nehmen?",
      "Was erhoffen Sie sich von einer möglichen Zusammenarbeit mit mir?"
    ], 
  nextModule: ["flow_phase_2_situation"],
  scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_2_situation": {
    id: "flow_phase_2_situation",
    type: "phase",
    label: "Phase 2: Call 1 / Situation in Bezug zu {1_focus_themen_fokus}",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: ["classification"],
    checkVagueness: true,
    microGoals: [
      {
        text: "Der Interessent hat beschrieben, was er aktuell beruflich macht.",
        model: ["business", "health", "improvement"],
        variables: ["2_other_aktuelle_taetigkeit"],
        requiredTonality: "neugierig",
        voraussetzung: null,
        specificQuestions: [ "Was machen Sie jetzt ... ?", "Erzählen Sie mir mehr über Ihren Job.", "Was genau sind Ihre Aufgaben?" ]
      },
      {
        text: "Der Interessent hat gesagt, wie lange er dieser Tätigkeit als {2_other_aktuelle_taetigkeit} bereits nachgeht.",
        model: ["business", "health", "improvement"],
        variables: ["2_other_taetigkeit_dauer"],
        requiredTonality: "neugierig",
        voraussetzung: null,
        specificQuestions: [ "Wie lange sind Sie schon ... ?", "Wie lange machen Sie das schon?" ]
      },
      {
        text: "Der Interessent hat beschrieben, wie er das/die relevante/n Thema/Themen im Bezug zum ({1_focus_themen_fokus}) aktuell wahrnimmt bzw was es für ihn bedeutet, dass es ihm fehlt.",
        model: ["all"],
        variables: ["2_focus_emotionaler_zustand_job"],
        voraussetzung: null,
        requiredTonality: "sicher",
        specificQuestions: [ "Wie ist ihre derzeitige Situation?", "Wie sieht Ihr Alltag aus?" ]
      },
      {
        text: "Der Interessent hat beschrieben, wie lange als Dauer er dieses/diese Thema/Themen ({1_focus_themen_fokus}) bereits so wahrnimmt.",
        model: ["all"],
        variables: ["2_other_problem_dauer"],
        voraussetzung: null,
        requiredTonality: "besorgt",
        specificQuestions: [ "Was hat Sie dazu gebracht, sich mit ... zu beschäftigen?", "Was hat Sie zu ... geführt?" ]
      },
      {
        text: "Der Interessent hat benannt, was er an der aktuellen Situation aufgrund von {1_focus_themen_fokus} belastend findet oder was er gern ändern würde.",
        model: ["business", "health", "improvement"],
        variables: ["2_focus_stoerfaktoren"],
        requiredDeepness: { clarity: 6, meaning: 5, emotion: 3 },
        voraussetzung: null,
        requiredTonality: "besorgt",
        specificQuestions: [ "Was daran würden Sie am liebsten ändern?", "Was daran ist für Sie gerade am meisten belastend?" ]
      }
    ],
    tonality: {
      intention: "Rolle und Kontext verstehen. Wer sitzt mir gegenüber?",
      emotion: "Interessiert, Professionell, Respektvoll",
      pace: "Normal",
      allowed_cues: CUES_FACTS,
      forbidden_words: ["Problem", "Schwierig", "Herausforderung", "Rolle", "Funktion"],
      formatting_rule: "Fette Fakten im Mardown Format **Fakten**",
      clarify_questions: ["Wie lange machen Sie das schon?", "Wie sieht Ihr Alltag aus?"],
      probe_questions: ["Erzählen Sie mir mehr über Ihren Job.", "Was genau sind Ihre Aufgaben?"]
    },
    questionsPool: [
      "Wie ist ihre derzeitige Situation?",
      "Was machen Sie jetzt ... ?",
      "Was benutzen Sie jetzt ... ?",
      "Wen benutzen Sie für ... ?",
      "Wie lange sind Sie schon ... ?",
      "Was hat Sie dazu gebracht, sich mit ... zu beschäftigen?",
      "Was hat Sie zu ... geführt?"
    ],
    nextModule: ["flow_phase_3_current_approach"],
  scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_3_current_approach": {
    id: "flow_phase_3_current_approach",
    type: "phase",
    label: "Phase 3: Call 1 / Aktueller Ansatz ({1_focus_einordnung_thema}) zu {1_focus_themen_fokus}",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    microGoals: [
      {
        text: "Der Interessent hat beschrieben, was er HEUTE / aktuell konkret unternimmt oder bewusst nicht unternimmt, einschließlich ob er hierzu professionelle Hilfe in Anspruch nimmt oder nicht.",
        model: ["all"],
        variables: ["3_other_ansatz_beschreibung", "3_other_hat_profi_hilfe", "3_other_ansatz_art"],
        requiredTonality: "neugierig",
        variableConfiguration:[{variable: '3_other_hat_profi_hilfe',
        outcome:["Professionelle Hilfe wird genutzt", "Keine professionelle Hilfe"]}],
        voraussetzung: null,
        specificQuestions: [ "Was tun Sie aktuell konkret – oder lassen bewusst – um {1_focus_themen_fokus} zu beeinflussen?", "Nutzen Sie dafür gerade irgendeine Form von professioneller Hilfe oder strukturierter Unterstützung – ja oder nein?" ]
      },
      {
        text: "Alle für {1_focus_themen_fokus} relevanten Kennzahlen ({kennzahlen}) wurden für einen klar eingegrenzten aktuellen Zeitraum genannt – einschließlich der Ergebnisse, soweit diese derzeit vorliegen.",
        model: ["b2b"],
        variables: ["3_other_aktuelle_kpis"],
        requiredDeepness: { clarity: 6, meaning: 2 },
        requiredTonality: "neugierig",
        voraussetzung: null,
        specificQuestions: [ "Welche Kennzahlen bilden das Thema aktuell am besten ab – und für welchen Zeitraum genau?", "Was sind die aktuellen Ergebnisse – so konkret wie möglich?" ]
      },
      {
        text: "Der Interessent hat beschrieben, welche Schritte oder Abläufe er im Bezug zum Thema {1_focus_themen_fokus} aktuell nutzt.",
        model: ["all"],
        variables: ["3_other_prozess_ablauf", "3_other_prozess_art"],
        voraussetzung: null,
        requiredTonality: "neugierig",
        specificQuestions: [ "Wie läuft Ihr aktueller Prozess im Detail ab?", "Welche Tools nutzen Sie dabei?" ]
      },
      {
        text: "Der Interessent hat beschrieben, wo er im aktuellen Ablauf in Bezug zu {1_focus_themen_fokus} einen Engpass sieht.",
        model: ["all"],
        variables: ["3_focus_prozess_engpass"],
        voraussetzung: null,
        specificQuestions: [ "Wo genau im Ablauf hakt es aktuell am meisten?", "An welcher Stelle verlieren Sie am ehesten Zeit, Qualität oder Ergebnis?" ]
      },
      {
        text: "Der Interessent hat beschrieben, welche Fähigkeiten, Kenntnisse oder Erfahrungen er aktuell einsetzt, um {1_focus_themen_fokus} anzugehen.",
        model: ["all"],
        variables: ["3_other_skill_level"],
        voraussetzung: null,
        specificQuestions: [ "Welche Fähigkeiten oder Erfahrungen setzen Sie aktuell ein, um das Thema zu lösen?", "Worauf greifen Sie dabei konkret zurück – Wissen, Routine, Erfahrung?" ]
      },
      {
        text: "Der Interessent hat beschrieben, woran er erkennt, dass seine Fähigkeiten, Erfahrungen oder Kenntnisse in Bezug auf {1_focus_themen_fokus} aus seiner Sicht nicht ausreichen.",
        model: ["all"],
        variables: ["3_focus_skill_gap_proof"],
        voraussetzung: null,
        specificQuestions: [ "Woran merken Sie konkret, dass es (noch) nicht reicht?", "Was passiert dann – woran sehen Sie den Gap?" ]
      }
    ],
    tonality: {
      intention: "Diagnose Prozess: Analytisch wie ein Arzt den Status Quo zerlegen.",
      emotion: "Analytisch, Nüchtern, Präzise",
      pace: "Präzise",
      allowed_cues: CUES_FACTS,
      forbidden_words: ["Problem (zu früh)", "Schlecht", "Falsch", "Logik", "Check"],
      formatting_rule: "Fette Zahlen im Markdown Format **Zahl**",
      clarify_questions: ["Welche Tools nutzen Sie?", "Wie läuft der Prozess genau ab?"],
      probe_questions: ["Können Sie das genauer beschreiben?", "Wie funktioniert das im Detail?"]
    },
    questionsPool: [
      "Wären Sie bereit, mir Ihre größten Herausforderungen mitzuteilen, die Sie derzeit haben?",
      "Welche dieser beiden Herausforderungen ist die wichtigste, die es zu lösen gilt?",
      "Warum ist sie gerade jetzt so wichtig für Sie?",
      "Wie lange besteht das Problem schon?",
      "Was ist die Ursache des Problems? Erzählen Sie mir mehr....",
      "Welche Hindernisse stehen Ihnen bei der Überwindung dieses Problems im Weg?"
    ],
    nextModule: ["flow_phase_4_results_gap"],
  scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_4_results_gap": {
    id: "flow_phase_4_results_gap",
    type: "phase",
    label: "Phase 4: Call 1 / Ergebnisse & GAP zu {1_focus_themen_fokus}",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    microGoals: [
      {
        text: "Der Interessent hat genau beschrieben, wo er heute in Bezug auf {1_focus_themen_fokus} steht (konkrete aktuelle Ergebnisse oder keine innerhalb eines Zeitrahmens).",
        model: ["all"],
        variables: ["4_other_ist_ergebnisse"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 6, meaning: 2 },
        voraussetzung: null,
        specificQuestions: [ "Welche Ergebnisse haben Sie damit erzielt?", "Wenn Sie es zeitlich eingrenzen: Was ist das aktuelle Ergebnis im relevanten Zeitraum?" ]
      },
      {
        text: "Der Interessent hat beschrieben, woran er erkennt, dass diese aktuellen Ergebnisse ({4_other_ist_ergebnisse}) für ihn ein Problem darstellen.",
        model: ["all"],
        variables: ["4_focus_ist_probleme"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 5, meaning: 5, emotion: 3 },
        voraussetzung: null,
        specificQuestions: [ "Woran merken Sie, dass das für Sie nicht okay ist?", "Was macht genau daran den Unterschied – warum ist das ein Problem für Sie?" ]
      },
      {
        text: "Der gewünschte Zielzustand wurde klar benannt: welches Ergebnis er tatsächlich erreichen möchte – und weshalb genau dieses Ergebnis.",
        model: ["all"],
        variables: ["4_focus_soll_ergebnisse"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 5, meaning: 5, emotion: 3 },
        voraussetzung: null,
        specificQuestions: [ "Welche Ergebnisse würden Sie sich wünschen, wenn Sie könnten?", "Warum genau dieses Ergebnis – was würde das für Sie verändern?" ]
      },
      {
        text: "Die Lücke zwischen aktuellem Ergebnis ({4_other_ist_ergebnisse}) und gewünschtem Ergebnis ({4_focus_soll_ergebnisse}) wurde klar benannt (zahlenbasiert oder beschreibend).",
        model: ["all"],
        variables: ["4_focus_gap_differenz"],
        voraussetzung: null,
        requiredTonality: "neugierig",
        specificQuestions: [ "Wo liegt die Differenz – wenn Sie es in Zahlen oder klaren Worten ausdrücken?", "Was ist der Abstand zwischen dem, wo Sie stehen, und dem, wo Sie hinwollen?" ]
      },
      {
        text: "Der Interessent hat beschrieben, welche Folgen oder Auswirkungen diese Lücke heute auf andere Lebens- oder Arbeitsbereiche hat (z.B. Zeit, Energie, Alltag, Umfeld, Motivation).",
        model: ["all"],
        variables: ["4_focus_gap_auswirkungen"],
        requiredDeepness: { clarity: 6, meaning: 6 },
        voraussetzung: null,
        requiredTonality: "besorgt",
        specificQuestions: [ "Wie wirkt sich dieses Problem auf den Absatz Ihres Unternehmens aus? Auf die Produktion? Rentabilität? Auf die Planung?", "Wie wirkt sich das Problem auf Ihr Endergebnis aus? Auf welche Weise?" ]
      }
    ],
    tonality: {
      intention: "Ergebnisse & GAP: Die Lücke zwischen Ist und Soll sichtbar machen.",
      emotion: "Nüchtern, Faktenbasiert, Zahlenorientiert",
      pace: "Normal",
      allowed_cues: CUES_LOGIC,
      forbidden_words: [],
      formatting_rule: "Fette Ergebnisse im Markdown Format **Ergebnis**",
      clarify_questions: ["Welche Zahlen haben Sie?", "Wie hoch ist der Verlust?"],
      probe_questions: ["Ist das der gewünschte Output?", "Wo liegt die Differenz?"]
    },
    questionsPool: [
      "Welche Ergebnisse haben Sie damit erzielt?",
      "Welche Ergebnisse würden Sie sich wünschen, wenn Sie könnten?",
      "Wie wirkt sich dieses Problem auf den Absatz Ihres Unternehmens aus? Auf die Produktion? Rentabilität? Auf die Planung?",
      "Wie wirkt sich das Problem auf Ihr Endergebnis aus? Auf welche Weise?"
    ],
    nextModule: ["flow_phase_5_history"],
  scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

"flow_phase_5_history": {
    id: "flow_phase_5_history",
    type: "phase",
    label: "Phase 5: Call 1 / Historie zu {1_focus_themen_fokus}",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    microGoals: [
      {
        // SCHRITT 1: Die Weiche stellen.
        // Hier MUSS die Variable '5_other_status_profi_hilfe' auf 'true' oder 'false' gesetzt werden.
        text: "Es wurde geklärt, ob der Interessent in der Vergangenheit bereits professionelle Hilfe / strukturierte Unterstützung zu {1_focus_themen_fokus} in Anspruch genommen hat oder nicht.",
        model: ["all"],
        variableConfiguration:[{variable: '5_other_status_profi_hilfe',
        outcome:["Professionelle Hilfe über Dienstleister schon gehabt", "Keine professionelle Hilfe zb über Dienstleister gehabt"]}],        
        variables: ["5_other_status_profi_hilfe"],
        voraussetzung: null
      },
      {
        // JA-PFAD START
        // Änderung: op: "eq", value: "true". 
        // Wenn Variable = false ("Nein"), liefert dies SKIPPED -> Engine springt weiter.
        text: "Weil es professionelle Hilfe gab: Der Interessent hat beschrieben, welche Art von Unterstützung er genutzt hat (außer Videos auf YouTube oder Bücher gelesen) und was konkret gemacht wurde.",
        model: ["all"],
        requiredTonality: "neugierig",
        variables: ["5_other_versuche_was", "5_other_versuche_art"],
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "Professionelle Hilfe über Dienstleister schon gehabt" }
      },
      {
        text: "Weil es professionelle Hilfe gab: Der Interessent hat beschrieben, welche konkreten Ergebnisse oder Resultate diese Unterstützung in Bezug auf {1_focus_themen_fokus} gebracht hat.",
        model: ["all"],
        variables: ["5_other_versuche_ergebnis"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 6, meaning: 3, scene: 3 },
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "Professionelle Hilfe über Dienstleister schon gehabt" }
      },
      {
        text: "Weil es professionelle Hilfe in der Vergangenheit gab: Der Interessent hat benannt, wann zuletzt Unterstützung in Bezug auf {1_focus_themen_fokus} in Anspruch genommen wurde und um welche Maßnahme es sich gehandelt hat.",
        model: ["all"],
        requiredTonality: "neugierig",
        variables: ["5_other_letzter_versuch_wann", "5_other_letzter_versuch_art"],
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "Professionelle Hilfe über Dienstleister schon gehabt" }
      },
      {
        text: "Weil es professionelle Hilfe in der Vergangenheit gab: Der Interessent hat grobe oder konkrete Angaben zur Dauer und/oder zum finanziellen Invest der letzten Unterstützung in Bezug auf {1_focus_themen_fokus} gemacht.",
        model: ["all"],
        variables: ["5_other_versuche_invest"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 6, meaning: 3 },
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "Professionelle Hilfe über Dienstleister schon gehabt" }
      },
      {
        text: "Weil es professionelle Hilfe gab: Der Interessent hat beschrieben, was damals der Hauptgrund war, genau diese Form der Unterstützung in Bezug auf {1_focus_themen_fokus} zu wählen.",
        model: ["all"],
        requiredTonality: "neugierig",
        variables: ["5_other_versuche_grund"],
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "Professionelle Hilfe über Dienstleister schon gehabt" }
      },
      {
        // NEIN-PFAD (Auffangnetz)
        // op: "falsy" ist hier korrekt. 
        // Wenn Variable = true ("Ja"), liefert 'falsy' SKIPPED.
        // Wenn Variable = false ("Nein"), liefert 'falsy' MET -> Ziel wird aktiv.
        text: "Weil es bisher keine professionelle Hilfe gab: Der Interessent hat bestätigt, dass er bisher keine externe Unterstützung in Anspruch genommen hat.",
        model: ["all"],
        variables: [],
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "Keine professionelle Hilfe zb über Dienstleister gehabt" }
      }
    ],
    tonality: { 
      intention: "Historie: Detektivisch nach Fehlern in der Vergangenheit suchen.", 
      emotion: "Detektivisch, Neugierig, 'Sherlock Holmes'", 
      pace: "Normal", 
      allowed_cues: CUES_LOGIC, 
      forbidden_words: [], 
      formatting_rule: "Fette Versuche im Markdown Format **Versuche**", 
      clarify_questions: ["Was genau haben Sie probiert?", "Warum hat das nicht geklappt?"], 
      probe_questions: ["Erzählen Sie mir mehr über diesen Versuch.", "Was war das Ergebnis?"] 
    },
    questionsPool: [
      "Welche möglichen Hürden haben Sie in der Vergangenheit genommen, um dieses Problem zu lösen?",
      "Können Sie mir ein Beispiel für ein aktuelles Problem nennen, mit dem Sie zu kämpfen hatten?",
      "Was ist heute in Ihrem Unternehmen anders als damals, als Sie hier anfingen, wenn überhaupt?",
      "Können Sie mir etwas über die Veränderungen, die Ihre Abteilung in letzter Zeit erlebt hat? Haben sich diese auf Sie ausgewirkt? In welcher Weise?",
      "Wenn Sie die Sache noch einmal machen könnten, was würden Sie anders machen als vorher?",
      "Welche Schritte haben Sie in der Vergangenheit unternommen, um dieses Problem zu lösen?"
    ],
    nextModule: ["flow_phase_6_own_explanations"],
    // Diese Regel ist optional, aber gut zur Sicherheit, falls das letzte Mikroziel (Nein-Pfad) 
    // aus irgendeinem Grund übersprungen würde.
    nextModuleRules: [
      { condition: { var: "5_other_status_profi_hilfe", op: "eq", value: "Keine professionelle Hilfe zb über Dienstleister gehabt" }, targetModuleId: "flow_phase_7_meaning_motives" }
    ],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_6_own_explanations": {
    id: "flow_phase_6_own_explanations",
    type: "phase",
    label: "Phase 6: Call 1 / Eigene Erklärungen",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    microGoals: [
      {
        text: "Benennung der Gründe für das was hätte besser laufen können in Bezug zu {1_focus_themen_fokus} und den Ergebnissen: {5_other_versuche_ergebnis}.",
        model: ["all"],
        variables: ["6_focus_hilfe_optimierung"],
        requiredTonality: "neugierig",
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "true"  }
      },
      {
        text: "Weil es schon länger her ist, dass Hilfe in Anspruch genommen wurde: Die Hauptgründe wurden benannt, warum der Interessent bisher nicht erneut aktiv geworden ist.",
        model: ["all"],
        variables: ["6_focus_blockaden"],
        requiredDeepness: { clarity: 7, meaning: 3 },
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "eq", value: "true"  }
      },
      {
        text: "Weil es bisher keine professionelle Hilfe gab: Die Hauptgründe wurden benannt, warum der Interessent bisher nicht aktiv geworden ist.",
        model: ["all"],
        variables: ["6_focus_blockaden"],
        voraussetzung: { var: "5_other_status_profi_hilfe", op: "falsy" }
      }
    ],
    tonality: { 
      intention: "Glaubenssätze sammeln: Zuhören ohne Wertung. Die Welt des Kunden verstehen.", 
      emotion: "Neutral, Offen, Akzeptierend", 
      pace: "Langsam", 
      allowed_cues: CUES_TRUST, 
      forbidden_words: ["Falsch", "Aber", "Das stimmt nicht", "Sehe ich anders"], 
      formatting_rule: "Fette O-Töne", 
      clarify_questions: ["Warum glauben Sie, ist das so?", "Was ist Ihrer Meinung nach der Grund?"], 
      probe_questions: ["Erzählen Sie mir mehr über diese Sichtweise.", "Woher kommt dieser Gedanke?"] 
    },
    questionsPool: [
      "Es hört sich so als liefen die Dinge ziemlich gut für Sie. Gibt es etwas, das Sie ändern würden, wenn Sie könnten?",
      "Warum sollten Sie das ändern?",
      "Warum ist das für Sie jetzt so wichtig?",
      "Was ist die Ursache des Problems? Erzählen Sie mir mehr....",
      "Was hat Sie bisher davon abgehalten, das zu bekommen, was Sie suchen?"
    ],
    nextModule: ["flow_phase_7_meaning_motives"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_7_meaning_motives": {
    id: "flow_phase_7_meaning_motives",
    type: "phase",
    label: "Phase 7: Call 1 / Bedeutung & Motive der Auswirkungen",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "WISH: Den inneren Antrieb finden. Vision aufbauen.", 
      emotion: "Inspirierend, Visionär, Positiv", 
      pace: "Langsam", 
      allowed_cues: CUES_VISION, 
      forbidden_words: ["Unmöglich", "Schwer", "Vielleicht"], 
      formatting_rule: "Fette Wünsche im Markdown Format **Wunsch**", 
      clarify_questions: ["Was wünschen Sie sich?", "Wie sähe das idealerweise aus?"], 
      probe_questions: ["Warum ist das wichtig für Sie?", "Was würde das ändern?"] 
    },
    questionsPool: [
      "Wie könnte Ihr Leben anders aussehen als jetzt?",
      "Wenn Sie dieses Problem lösen würden, wie würde es sich für Sie von der jetzigen Situation unterscheiden?",
      "Was würde das dann bedeuten?",
      "Was gibt Ihnen das Gefühl, dass ....?",
      "Was haben Sie erlebt, dass Ihnen da Gefühl gibt, dass Sie .... zu wenig haben?",
      "Angenommen Sie könnten sich etwas wünschen, was wäre das und warum?",
      "Könnten Sie mir mitteilen, was Sie in den nächsten 3 Monaten/6 Monaten/12 Monaten zu erreichen hoffen?",
      "Wie sieht das im Vergleich zu Ihrem jetzigen Stand aus?"
    ],
    microGoals: [
      {
        text: "Aus den in {4_focus_gap_auswirkungen} und {4_focus_ist_probleme} genannten Punkten wurde mindestens eine zentrale Auswirkung ausgewählt, auf die sich das Gespräch im nächsten Schritt konkret bezieht.",
        model: ["all"],
        variables: ["7_focus_gap_aspekt"],
        requiredTonality: "neugierig",
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, welche Bedeutung diese ausgewählte Auswirkung ({7_focus_gap_aspekt}) für ihn persönlich bzw. für sein Business hat.",
        model: ["all"],
        variables: ["7_focus_bedeutung"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 5, meaning: 5, emotion: 4 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat präzisiert, was er mit dieser Bedeutung ({7_focus_bedeutung}) genau meint.",
        model: ["all"],
        variables: ["7_focus_bedeutung_konkret"],
        requiredTonality: "verwirrt",
        requiredDeepness: { clarity: 4, meaning: 4 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat erläutert, weshalb genau diese Bedeutung bzw. dieses Prinzip ({7_focus_bedeutung_konkret}) besonders wichtig ist.",
        model: ["all"],
        variables: ["7_focus_bedeutung_motive"],
        requiredTonality: "verwirrt",
        requiredDeepness: { clarity: 7, meaning: 6 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_8_need"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_8_need": {
    id: "flow_phase_8_need",
    type: "phase",
    label: "Phase 8: Call 1 / NEED (Kernbedürfnis hinter {VARIABLE})",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "NEED: Wahres Bedürfnis (Mangel) aufdecken. Warum JETZT?", 
      emotion: "Empathisch, Verstehend, Tief", 
      pace: "Langsam", 
      allowed_cues: CUES_EMPATHY, 
      forbidden_words: [], 
      formatting_rule: "Fette Bedürfnisse im Markdown Format **Bedürfnis**", 
      clarify_questions: ["Warum gerade jetzt?", "Was fehlt Ihnen aktuell am meisten?"], 
      probe_questions: ["Was würde es für Sie persönlich bedeuten?", "Wie fühlen Sie sich dabei?"] 
    },
    questionsPool: [
      "Was würde das für Sie persönlich bedeuten, um dieses Problem/diese Frage/diese Herausforderung zu lösen?",
      "Was würde es für Sie bedeuten, wenn Sie dieses Problem lösen könnten?",
      "Was würde das für Sie bedeuten, wenn Sie das tun könnten?",
      "Wenn Sie könnten ... was würde das für Sie persönlich bedeuten?",
      "Was bringt es Ihnen, dies für das Unternehmen umzusetzen?"
    ],
    microGoals: [
      {
        text: "Aus den Bedeutungen und Motiven ({7_focus_bedeutung}, {7_focus_bedeutung_konkret}, {7_focus_bedeutung_motive}) wurde ein übergeordnetes Kernbedürfnis abgeleitet.",
        model: ["all"],
        variables: ["8_focus_kernbeduerfnis"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 7, meaning: 6 },
        voraussetzung: null
      },
      {
        text: "Es wurde geklärt, in welchem Bereich der Situation dieses Kernbedürfnis für den Interessenten aktuell besonders relevant ist.",
        model: ["all"],
        variables: ["8_focus_need_kontext"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 5, scene: 5 },
        voraussetzung: null
      },
      {
        text: "Es wurde geklärt, inwieweit dieses Kernbedürfnis ({8_focus_kernbeduerfnis}) aus Sicht des Interessenten heute eher gut abgedeckt ist oder eher zu kurz kommt.",
        model: ["all"],
        variables: ["8_focus_need_status"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_9_real_pain"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_9_real_pain": {
    id: "flow_phase_9_real_pain",
    type: "phase",
    label: "Phase 9: Call 1 / REAL PAIN (Need-Verletzung)",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "REAL PAIN: Schmerz fühlbar machen. In die Wunde drücken (sanft).", 
      emotion: "Besorgt, Ernst, Tiefgründig", 
      pace: "Langsam (Pausen)", 
      allowed_cues: CUES_EMPATHY, 
      forbidden_words: ["Super", "Toll", "Lösung", "Fantastisch"], 
      formatting_rule: "Fette Schmerzpunkte im Markdown Format **Schmerzpunkt**", 
      clarify_questions: ["Können Sie mir ein Beispiel geben?", "Wann tut es am meisten weh?"], 
      probe_questions: ["Wie wirkt sich das aus?", "Erzählen Sie mir von einer konkreten Situation."] 
    },
    questionsPool: [
      "Was ist Ihr größtes Hindernis, das Sie im Moment davon abhält, dieses Problem zu lösen?",
      "Welche Bereiche würden Sie gerne verbessern, wenn Sie könnten?",
      "Was war das Schwierigste, mit dem Sie in letzter Zeit zu kämpfen hatten? Erzähl mir mehr....",
      "Können Sie mir ein Beispiel für ein aktuelles Problem nennen, mit dem Sie zu kämpfen hatten?",
      "Welches dieser Probleme belastet Sie am meisten? Aber warum gerade dieses?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat erklärt, in welchen Situationen oder Bereichen seines Alltags er besonders stark wahrnimmt, dass das Kernbedürfnis ({8_focus_kernbeduerfnis}) aktuell fehlt.",
        model: ["all"],
        variables: ["9_focus_problem_main"],
        requiredTonality: "besorgt",
        requiredDeepness: { clarity: 6, emotion: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat ein typisches Beispiel beschrieben, in dem {9_focus_problem_main} deutlich sichtbar wird.",
        model: ["all"],
        variables: ["9_other_beispiel"],
        requiredTonality: "besorgt",
        requiredDeepness: { clarity: 5, scene: 6 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat benannt, wie häufig ähnliche Situationen wie {9_other_beispiel} auftreten.",
        model: ["all"],
        variables: ["9_other_haeufigkeit"],
        requiredDeepness: { clarity: 5, magnitude: 4 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat benannt, seit wann ähnliche Situationen wie {9_other_beispiel} in dieser Form auftreten.",
        model: ["all"],
        variables: ["9_other_dauer"],
        requiredTonality: "neugierig",
        requiredDeepness: { clarity: 5, scene: 4 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, in welchen anderen Bereichen sich die Auswirkungen von {9_focus_problem_main} bemerkbar machen.",
        model: ["all"],
        variables: ["9_focus_impact_areas"],
        requiredDeepness: { clarity: 5, meaning: 5, magnitude: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, in welcher Form sich die Auswirkungen von {9_focus_problem_main} konkret niederschlagen, insbesondere in Bezug auf begrenzte oder messbare Ressourcen.",
        model: ["all"],
        variables: ["9_focus_impact_manifestation"],
        requiredDeepness: { clarity: 5, meaning: 5, magnitude: 5, emotion: 4 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, welche betriebswirtschaftlichen Auswirkungen sich aus {9_focus_impact_manifestation} ergeben.",
        model: ["b2b"],
        variables: ["9_focus_financial_loss"],
        requiredDeepness: { clarity: 6, meaning: 4, magnitude: 7, emotion: 3, consequence: 5 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_10_causal_chain"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_10_causal_chain": {
    id: "flow_phase_10_causal_chain",
    type: "phase",
    label: "Phase 10: Call 1 / Folgeauswirkungen (B2B-Kausalkette)",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "Impact: Die harten Kosten realisieren. Business Case rechnen.", 
      emotion: "Ernst, Business-Fokus, Hart", 
      pace: "Präzise", 
      allowed_cues: CUES_REALITY, 
      forbidden_words: ["Vielleicht", "Ungefähr", "Schätzungsweise"], 
      formatting_rule: "Fette Summen", 
      clarify_questions: ["Wie viel kostet das genau?", "Haben Sie das mal ausgerechnet?"], 
      probe_questions: ["Was bedeutet das in Euro?", "Wie hoch ist der Schaden?"] 
    },
    questionsPool: [
      "Wie viel, glauben Sie, kostet Sie dieses Problem an entgangenen Einnahmen/Verkäufen?",
      "Wie wirkt sich dieses Problem auf den Absatz Ihres Unternehmens aus? Auf die Produktion? Rentabilität? Auf die Planung?",
      "Wie wirkt sich das Problem auf Ihr Endergebnis aus? Auf welche Weise?",
      "Wie viel kostet Sie das an verlorener Zeit?",
      "Wenn Sie sich jetzt nicht entscheiden, dieses Problem anzugehen, wie viel wird es Sie in diesem Jahr an entgangenem Gewinn/Ertrag/Absatz kosten?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat beschrieben, welche direkten Folgeauswirkungen sich aus {9_focus_impact_manifestation} ergeben.",
        model: ["b2b"],
        variables: ["10_focus_folge_1"],
        requiredDeepness: { clarity: 5, meaning: 4 },
        requiredTonality: "neugierig",
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, welche weiteren mittelbaren Auswirkungen sich aus {10_focus_folge_1} ergeben.",
        model: ["b2b"],
        variables: ["10_focus_folge_2"],
        requiredDeepness: { clarity: 5, meaning: 4 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, welche zusätzlichen nachgelagerten Effekte sich aus {10_focus_folge_3} ergeben.",
        model: ["b2b"],
        variables: ["10_focus_folge_3"],
        requiredDeepness: { clarity: 5, meaning: 4, consequence: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, welche zusätzlichen nachgelagerten Effekte sich aus {10_focus_folge_3} ergeben.",
        model: ["b2b"],
        variables: ["10_focus_folge_4"],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, welche betriebswirtschaftlichen Auswirkungen die Kette der Folgeauswirkungen aus {10_focus_folge_1}, {10_focus_folge_2},{10_focus_folge_3} und {10_focus_folge_4} auf das Business haben.",
        model: ["b2b"],
        variables: ["10_focus_total_impact"],
        requiredDeepness: { clarity: 6, meaning: 5, magnitude: 6, consequence: 6 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_11_emotion_identity"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_11_emotion_identity": {
    id: "flow_phase_11_emotion_identity",
    type: "phase",
    label: "Phase 11: Call 1 / Emotion & Identität",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "Emotion & Identität: Dissonanz erzeugen. Passt das zu mir?", 
      emotion: "Tief, Persönlich, Reflektiert, Philosophisch", 
      pace: "Sehr Langsam", 
      allowed_cues: CUES_DEEP, 
      forbidden_words: ["Egal", "Oberflächlich", "Passt schon"], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Wie geht es Ihnen damit?", "Passt das zu dem, wer Sie sein wollen?"], 
      probe_questions: ["Was macht das mit Ihnen?", "Wie fühlt sich das an?"] 
    },
    questionsPool: [
      "Wenn Sie sich vorstellen, Sie hätten ...., die Sie sich wünschen – was würde das über Sie als Person aussagen?",
      "Warum ist es Ihnen wichtig, die Person zu sein, die über .... verfügt?",
      "Wenn Sie dieses Ziel ....  – wie würden Sie sich dann selbst sehen?",
      "Was wäre für Sie persönlich das Wichtigste daran, die Person zu sein, die ihre ..... aufgebaut hat?",
      "Woran würden Sie merken, dass Sie nicht mehr die Person sind, die mit fehlenden ..., sondern jemand, der ... im Griff hat?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat beschrieben, wie sich die wiederkehrenden Situationen rund um {9_focus_problem_main} und deren Dauer ({9_other_dauer}) mit der Auswirkung: {10_focus_total_impact} und fehlendem {8_focus_kernbeduerfnis} persönlich auf ihn auswirken.",
        model: ["b2b"],
        variables: ["11_focus_personal_impact"],
        requiredDeepness: { clarity: 6, meaning: 5, emotion: 5, consequence: 6 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, wie sich die wiederkehrenden Situationen rund um {9_focus_problem_main} und deren Dauer ({9_other_dauer}) mit der Auswirkung: {9_focus_impact_manifestation} und fehlendem {8_focus_kernbeduerfnis} persönlich auf ihn auswirken.",
        model: ["business", "health", "improvement"],
        variables: ["11_focus_personal_impact"],
        requiredDeepness: { clarity: 6, meaning: 5, emotion: 5, consequence: 6 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, dass er die Person sein möchte mit mehr {8_focus_kernbeduerfnis}, ohne das Problem {9_focus_problem_main}.",
        model: ["all"],
        requiredTonality: "sicher",
        variables: ["11_focus_identity_gap"],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, weshalb er die Person sein möchte mit mehr {8_focus_kernbeduerfnis}, ohne das Problem {9_focus_problem_main}",
        model: ["all"],
        variables: ["11_focus_identity_why"],
        requiredTonality: "sicher",
        voraussetzung: null
      },
      {
        text: "Der Interessent hat leicht angedeutet, dass er als Mensch etwas an seiner Situation ändern will.",
        model: ["all"],
        variables: ["11_focus_change_intent"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_12_criteria_completeness"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_12_criteria_completeness": {
    id: "flow_phase_12_criteria_completeness",
    type: "phase",
    label: "Phase 12: Call 1 / Kriterien & Vollständigkeit zur Zielerreichung",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "Kriterien & Vollständigkeit zur Zielerreichung", 
      emotion: "Kompetent, Erklärend, Sicher", 
      pace: "Normal", 
      allowed_cues: ["Schau...", "Pass auf...", "Die gute Nachricht ist..."], 
      forbidden_words: ["Garantie", "Versprechen", "Kauf"], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: [], 
      probe_questions: [] 
    },
    questionsPool: [
      "Können Sie mir die Kriterien erläutern, die Sie bei der Auswahl eines Unternehmens zur Lösung der von Ihnen angesprochenen Probleme anwenden?",
      "Wie messen Sie den Erfolg Ihres derzeitigen Anbieters?",
      "Nehmen wir an, Sie sehen sich mehrere Unternehmen an, die alle Ihre Kriterien, einschließlich des Preises, erfüllen. Wie würden Sie dann entscheiden, was Sie tun sollen?",
      "Sie hatten erwähnt, dass das Wichtigste für Sie die Kosten sind. Darf ich fragen, wie das im Verhältnis dazu steht, dass Sie tatsächlich Ergebnisse erzielen und dieses Problem lösen können?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat die für ihn wichtigen eigenen Kriterien genannt in Bezug auf {4_focus_soll_ergebnisse} bzw. das Ziel – und anschließend deutlich gemacht, dass ihm aktuell keine weiteren wichtigen Punkte mehr einfallen.",
        model: ["all"],
        variables: ["12_focus_sollvoraussetzungen"],
        requiredTonality: "sicher",
        requiredDeepness: { clarity: 7, meaning: 5 },
        voraussetzung: null
      },
      {
        text: "Mindestens ein externes Kriterium wurde vom Closer zusätzlich eingebracht und vom Interessenten beantwortet. Danach wurden alle Kriterien und die Kriterien vom Interessenten: {12_focus_sollvoraussetzungen} vom Closer zusammengefasst und vom Interessenten klar bestätigt.",
        model: ["all"],
        variables: ["12_focus_kriterien_final"],
        requiredTonality: "sicher",
        voraussetzung: null
      },
      {
        text: "Der Interessent hat das 'Kriterium' bestätigt oder selbst genannt.",
        model: ["all"],
        variables: ["12_focus_kriterium_x"],
        requiredTonality: "sicher",
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_13_priority_criteria"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_13_priority_criteria": {
    id: "flow_phase_13_priority_criteria",
    type: "phase",
    label: "Phase 13: Call 1 / Priorität & Bedeutung der Kriterien",
    speechShareType: "low",
    icon: "",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "Priorität festhalten", 
      emotion: "Kompetent, Erklärend, Sicher", 
      pace: "Normal", 
      allowed_cues: ["Schau...", "Pass auf...", "Die gute Nachricht ist..."], 
      forbidden_words: ["Garantie", "Versprechen", "Kauf"], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: [], 
      probe_questions: [] 
    },
    questionsPool: [
      "Können Sie mir sagen, was Ihre wichtigsten Prioritäten für dieses Jahr sind?",
      "Welche dieser beiden Herausforderungen ist die wichtigste, die es zu lösen gilt?",
      "Warum ist sie gerade jetzt so wichtig für Sie?",
      "Wie wichtig ist dieses Thema für Sie?",
      "Welche Bereiche würden Sie gerne verbessern, wenn Sie könnten?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat aus allen genannten Kriterien: {12_focus_kriterien_final} klar ein Kriterium ausgewählt, das für ihn am wichtigsten ist.",
        model: ["all"],
        variables: ["13_focus_prio_kriterium"],
        requiredDeepness: { clarity: 5, meaning: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat in eigenen Worten erklärt, weshalb genau dieses priorisierte Kriterium für ihn besonders wichtig ist.",
        model: ["all"],
        variables: ["13_focus_prio_reason"],
        requiredDeepness: { clarity: 7, meaning: 7 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_14_outlook_decision"],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_14_outlook_decision": {
    id: "flow_phase_14_outlook_decision",
    type: "phase",
    label: "Phase 14: Call 1 / Aussicht & Entscheidungsstruktur für den nächsten Schritt",
    speechShareType: "medium",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "Hoffnung geben (Cliffhanger): Spannung aufbauen. Problem ist lösbar.", 
      emotion: "Zuversichtlich, Ruhig, Bestärkend", 
      pace: "Normal", 
      allowed_cues: ["Schau...", "Pass auf...", "Die gute Nachricht ist..."], 
      forbidden_words: ["Garantie", "Versprechen", "Kauf"], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: [], 
      probe_questions: [] 
    },
    questionsPool: [
      "Können Sie mir den Entscheidungsfindungsprozess in Ihrem Unternehmen erläutern?",
      "Wer außer Ihnen würde entscheiden, mit welcher Firma Sie zusammenarbeiten wollen?",
      "Nehmen wir an, Sie sehen sich mehrere Unternehmen an, die alle Ihre Kriterien, einschließlich des Preises, erfüllen. Wie würden Sie dann entscheiden, was Sie tun sollen?",
      "Sie hatten mir gesagt, dass Ihr Unternehmen ein Budget um dieses Problem zu beheben. Darf ich fragen, wie das ermittelt wurde?",
      "Sind Sie der Meinung, dass das Ihnen zur Verfügung gestellte Budget ausreicht, um dieses Problem zu lösen?"
    ],
    microGoals: [
      {
        text: "Hoffnung auf Lösbarkeit vermittelt (ohne Pitch).",
        model: ["all"],
        variables: [],
        voraussetzung: null
      },
      {
        text: "Es wurde geklärt, ob der Interessent allein entscheidet oder ob weitere Personen (z. B. Vorgesetzte, Geschäftsführung) in die Entscheidung eingebunden werden müssen.",
        model: ["b2b"],
        variables: ["14_other_entscheider"],
        requiredDeepness: { clarity: 3 },
        voraussetzung: null
      },
      {
        text: "Es wurde geklärt, ob der Interessent allein entscheidet oder ob weitere Personen (z. B. Geschäftspartner, Partner) in die Entscheidung eingebunden werden müssen.",
        model: ["business"],
        variables: ["14_other_entscheider"],
        requiredDeepness: { clarity: 3 },
        voraussetzung: null
      },
      {
        text: "Es wurde geklärt, ob der Interessent allein entscheidet oder ob weitere Personen (z.B. Partner, Vertraute) in die Entscheidung eingebunden werden müssen.",
        model: ["health", "improvement"],
        variables: ["14_other_entscheider"],
        requiredDeepness: { clarity: 3 },
        voraussetzung: null
      },
      {
        text: "Ein grober Budgetrahmen, direkter Preis oder finanzieller Rahmen wurde vom Closer genannt und vom Prospect akzeptiert.",
        model: ["all"],
        variables: ["14_focus_budget_ok"],
        requiredDeepness: { clarity: 4 },
        voraussetzung: null
      },
      {
        text: "Ein grober Budgetrahmen, direkter Preis oder finanzieller Rahmen wurde vom Closer genannt und vom Prospect auch nach Einwandbehandlung nicht akzeptiert.",
        model: ["all"],
        variables: ["14_focus_budget_reject"],
        requiredDeepness: { clarity: 4 },
        voraussetzung: null
      },
      {
        text: "Brücke zum nächsten Call gebaut mit explizitem Terminvorschlag durch den Closer und eventuelle Einladung eines weiteren Beteiligten wurde durch den Interessent bestätigt.",
        model: ["all"],
        variables: [],
        requiredDeepness: { clarity: 5 },
        voraussetzung: null
      },
      {
        text: "Brücke zu einem möglichen Follow-Up Call in 3 bis 6 Monaten wurde durch den Closer vorgeschlagen und vom Interessenten angenommen.",
        model: ["all"],
        variables: ["other_follow_up_agreed"],
        requiredDeepness: { clarity: 5 },
        voraussetzung: null
      },
      {
        text: "Brücke zu einem möglichen Follow-Up Call in 3 bis 6 Monaten wurde durch den Closer vorgeschlagen und vom Interessenten NICHT angenommen.",
        model: ["all"],
        variables: ["other_follow_up_budget_false"],
        requiredDeepness: { clarity: 5 },
        voraussetzung: null
      }
    ],
 //   nextModule: ["flow_phase_15_rejoin"],
      nextModule: [],
      scriptId: "script_sales_call_v1",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_15_rejoin": {
    id: "flow_phase_15_rejoin",
    type: "phase",
    label: "Phase 15: Call 2 / Rejoin",
    speechShareType: "medium",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "Rejoin: Basis prüfen. Vertrauen wiederherstellen.", 
      emotion: "Professionell, Check-in, Wach", 
      pace: "Normal", 
      allowed_cues: CUES_TRUST, 
      forbidden_words: [], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Stimmt das noch so?", "Hat sich etwas geändert?"], 
      probe_questions: [] 
    },
    questionsPool: [
      "Was meinen Sie genau?",
      "Können Sie etwas genauer werden?",
      "Geben Sie mir ein Beispiel ...",
      "Erzähl mir mehr....",
      "Können Sie mir ein Beispiel für ein aktuelles Problem nennen, mit dem Sie zu kämpfen hatten?"
    ],
    microGoals: [
      {
        text: "Die wesentlichen Punkte aus dem ersten Gespräch – aktueller Stand ({4_other_ist_ergebnisse}), gewünschtes Ergebnis ({4_focus_soll_ergebnisse}) und Verständnis auf Basis von Bedürfnis {8_focus_kernbeduerfnis} und der Auswirkungen: {9_focus_impact_manifestation} bestätigt.",
        model: ["all"],
        variables: ["15_focus_rejoin_ok"],
        requiredDeepness: { clarity: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat bestätigt, dass die grundlegende Situation und die relevanten Zahlen ({4_other_ist_ergebnisse}) weiterhin gelten – oder er hat klar benannt, welche Aspekte sich seitdem verändert haben.",
        model: ["all"],
        variables: ["15_other_rejoin_changes"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_16_emotion_reactivate"],

    metaScript: {
      isScriptStart: true,
      version: 1,
      isValid: true,
      scriptLabel: "Sales Call – Standard2",
      scriptStatus: "active"
    },
shopInfo: {
  category: "SCRIPT",
  tier: "PRO",
  unlockMode: "BOTH",

  // PERM
  pricePointsPermanent: 14999,
  priceEurPermanent: 999,

  // TRIAL (USES)
  pricePointsTemporary: 5000,
  priceEurTemporary: 0,
  temporaryGrantType: "USES",
  temporaryUses: 1,

  description: "ELITS Sales Script: vollständiger Run inkl. aller Module Advanced.",
  icon: "📜"
},
  scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_16_emotion_reactivate": {
    id: "flow_phase_16_emotion_reactivate",
    type: "phase",
    label: "Phase 16: Call 2 / Emotion Reaktivieren",
    speechShareType: "medium",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "Emotion reaktivieren: Schmerz zurückholen. Anker werfen.", 
      emotion: "Erinnernd, Einfühlend, Ernst", 
      pace: "Langsam", 
      allowed_cues: CUES_EMPATHY, 
      forbidden_words: [], 
      formatting_rule: "Fette Gefühle", 
      clarify_questions: ["Erinnern Sie sich an das Gefühl?", "Wie war das nochmal?"], 
      probe_questions: ["Erzählen Sie mir nochmal von diesem Moment.", "Wie stark ist das Gefühl heute?"] 
    },
    questionsPool: [
      "Hat es sich auf Sie ausgewirkt? In welcher Weise?",
      "Was war das Schwierigste, mit dem Sie in letzter Zeit zu kämpfen hatten? Erzähl mir mehr....",
      "Welches dieser Probleme belastet Sie am meisten? Aber warum gerade dieses?",
      "Sagen Sie mir, warum Sie Ihre Situation jetzt ändern wollen?",
      "Warum ist das jetzt so wichtig?"
    ],
    microGoals: [
      {
        text: "Eine der wesentlichen Auswirkungen der Lücke {4_focus_gap_auswirkungen} bzw. die persönlichen Auswirkungen {11_focus_personal_impact} werden aufgegriffen und der Interessent hat beschrieben, wie sich diese Situation aktuell für ihn anfühlt.",
        model: ["all"],
        variables: ["16_focus_emotion_recall"],
        requiredDeepness: { clarity: 5, emotion: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent erklärt, weshalb es ihm am Herzen liegt, etwas zu ändern, statt sich damit zufrieden zu geben, wie es aktuell läuft.",
        model: ["all"],
        variables: ["16_focus_deep_impact"],
        requiredDeepness: { clarity: 5, emotion: 5, consequence: 3 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_17_futureself"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_17_futureself": {
    id: "flow_phase_17_futureself",
    type: "phase",
    label: "Phase 17: Call 2 / FutureSelf",
    speechShareType: "medium",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "FutureSelf: Vision malen. Das neue Ich.", 
      emotion: "Visionär, Positiv, Energetisch", 
      pace: "Energetisch", 
      allowed_cues: CUES_VISION, 
      forbidden_words: ["Unmöglich"], 
      formatting_rule: "Fette Ziele im Markdown Format **Ziel**", 
      clarify_questions: ["Wie sieht das genau aus?", "Beschreiben Sie mir diesen Tag."], 
      probe_questions: ["Was würden Sie dann tun?", "Wie würde sich Ihr Leben verändern?"] 
    },
    questionsPool: [
      "Wenn Sie dieses Ziel erreichen könnten, was würde das für Sie persönlich bedeuten?",
      "Nehmen wir an, Ihre Bedürfnisse könnten befriedigt werden; was hoffen Sie zu erreichen?",
      "Könnten Sie mir mitteilen, was Sie in den nächsten 3 Monaten/6 Monaten/12 Monaten zu erreichen hoffen?",
      "Wie sieht das im Vergleich zu Ihrem jetzigen Stand aus?",
      "Wie würde Ihnen die Umsetzung dieser Veränderung helfen?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat den Unterschied zwischen seiner aktuellen Situation ({4_other_ist_ergebnisse}, inkl. {9_focus_impact_manifestation}) und seinem gewünschten zukünftigen Zustand ({4_focus_soll_ergebnisse}) klar beschrieben.",
        model: ["all"],
        variables: ["17_focus_diff_confirmed"],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat nach einer Reise des Closers in die Zukunft benannt, welchen konkreten Vorteil es für ihn und sein Umfeld hätte, wenn {9_focus_impact_manifestation} gelöst ist und die Situation dem gewünschten Ergebnis ({4_focus_soll_ergebnisse}) entspricht.",
        model: ["all"],
        requiredDeepness: { meaning: 5, clarity: 6, emotion: 5, scene: 4 },
        variables: ["17_focus_benefit_concrete"],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, wie er sich in genau diesem Zukunftsszenario {17_focus_benefit_concrete} fühlen würde.",
        model: ["all"],
        variables: ["17_focus_feeling_goal"],
        requiredDeepness: { meaning: 5, clarity: 6, emotion: 6 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_18_probability"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_18_probability": {
    id: "flow_phase_18_probability",
    type: "phase",
    label: "Phase 18: Call 2 / Wahrscheinlichkeit",
    speechShareType: "medium",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "Wahrscheinlichkeit: Realitätscheck. Zweifel säen.", 
      emotion: "Skeptisch, Realistisch, 'Columbo'", 
      pace: "Nachdenklich", 
      allowed_cues: CUES_CHALLENGE, 
      forbidden_words: [], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Hand aufs Herz, schaffen Sie das so?", "Was spricht dagegen?"], 
      probe_questions: ["Warum hat es bisher nicht geklappt?", "Was muss sich ändern?"] 
    },
    questionsPool: [
      "Was hat Sie in der Vergangenheit daran gehindert, Ihre Situation zu ändern?",
      "Was hindert Sie daran, Ihre Situation zu ändern?",
      "Wie ernst ist dieses Problem für Sie?",
      "Sind Sie bereit, Ihre Situation zu ändern, damit Sie ... ?",
      "Können Sie Ihre Ziele erreichen, ohne dieses Problem anzugehen?"
    ],
    microGoals: [
      {
        text: "Nachdem der Closer dem Interessenten aufgezeigt hat, wo er jetzt steht ({4_other_ist_ergebnisse}), hat der Interessent eingeschätzt, wie wahrscheinlich es ist, ({4_focus_soll_ergebnisse}) zu erreichen.",
        model: ["all"],
        variables: ["18_focus_reality_check_1"],
        requiredDeepness: { meaning: 5, clarity: 6, emotion: 5, consequence: 6 },
        voraussetzung: null
      },
      {
        text: "Der Interessent beschreibt, welche Hindernisse, Muster oder Gründe ihn bisher daran gehindert haben, zu der Person zu werden, die er sein will, aufgrund dieser bereits genannten Punkte: {11_focus_identity_why}.",
        model: ["all"],
        variables: ["18_focus_obstacles"],
        requiredDeepness: { meaning: 3, clarity: 6, emotion: 3 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat in eigenen Worten ausgesprochen, dass es ohne Änderungen im aktuellen Vorgehen unmöglich ist, seine Ziele wie {17_focus_benefit_concrete} zu erreichen.",
        model: ["all"],
        variables: ["18_focus_reality_check_2"],
        requiredDeepness: { meaning: 3, clarity: 6, emotion: 5 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_19_inaction_consequences"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_19_inaction_consequences": {
    id: "flow_phase_19_inaction_consequences",
    type: "phase",
    label: "Phase 19: Call 2 / Konsequenzen 'Nichts tun'",
    speechShareType: "medium",
    allowedSubsystems: [],
    checkVagueness: true,
    icon: "",
    tonality: { 
      intention: "Konsequenzen: Worst Case Szenario. Angst vor Verlust.", 
      emotion: "Warnend, Ernst, Direkt", 
      pace: "Fest", 
      allowed_cues: CUES_REALITY, 
      forbidden_words: ["Vielleicht"], 
      formatting_rule: "Fette Konsequenzen im Markdown Format **Konsequenz**", 
      clarify_questions: ["Was passiert im schlimmsten Fall?", "Wo stehen Sie in 5 Jahren?"], 
      probe_questions: ["Können Sie sich das leisten?", "Wollen Sie das riskieren?"] 
    },
    questionsPool: [
      "Was werden sie tun, wenn sie ihre Situation nicht ändern?",
      "Was ist, wenn Sie nichts gegen dieses Problem unternehmen und Ihre Situation noch schlimmer wird?",
      "Haben Sie über die möglichen Folgen nachgedacht, wenn Sie nichts gegen Ihre Situation unternehmen?",
      "Haben Sie darüber nachgedacht, was passieren würde, wenn Sie nichts unternehmen?",
      "Was werden Sie tun, wenn sich nichts ändert, wenn Sie in den nächsten 5-10 Jahren immer das Gleiche tun?",
      "Was ist, wenn du nichts unternimmst und weiterhin ... ?",
      "Wenn Sie dieses Problem nicht lösen, welche Auswirkungen hat das auf Ihr Unternehmen?",
      "Können Sie es sich leisten, dieses Risiko einzugehen?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat benannt, wie er sich fühlt, wenn er nichts ändert und er der Mensch bleibt mit ({9_focus_impact_manifestation}) und fehlendem {8_focus_kernbeduerfnis}.",
        model: ["all"],
        variables: ["19_focus_scenario_inaction"],
        requiredDeepness: { meaning: 5, clarity: 6, emotion: 6 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat begründet, weshalb es sinnlos wäre, weiterzumachen wie bisher und der Mensch zu sein, der {9_focus_impact_manifestation} hat und dem {8_focus_kernbeduerfnis} im Leben fehlt.",
        model: ["all"],
        variables: ["19_focus_longterm_damage"],
        requiredDeepness: { meaning: 5, clarity: 6, emotion: 5, consequence: 7 },
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_20_beliefs_change"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_20_beliefs_change": {
    id: "flow_phase_20_beliefs_change",
    type: "phase",
    label: "Phase 20: Call 2 / Glaubenssätze rund um Ziel & Veränderung",
    speechShareType: "medium",
    allowedSubsystems: [],
    checkVagueness: true,
    icon: "",
    tonality: { 
      intention: "Glaubenssätze vs Impact: Widerspruch aufzeigen. Logik-Falle.", 
      emotion: "Herausfordernd, Logisch, Konfrontierend", 
      pace: "Normal", 
      allowed_cues: CUES_CHALLENGE, 
      forbidden_words: [], 
      formatting_rule: "Fette Widersprüche des Interessenten im Markdownformat **Widerspruch**", 
      clarify_questions: ["Wie passt das zusammen?", "Erklären Sie mir diesen Widerspruch."], 
      probe_questions: ["Wenn alles gut ist, warum ändern wir dann was?", "Warum ist das jetzt plötzlich wichtig?"] 
    },
    questionsPool: [
      "Darf ich fragen, wie Ihre ideale Situation im Vergleich zu der ist, die Sie jetzt mit dem Anbieter haben, den Sie benutzen?",
      "Damals, als Sie sich für die Zusammenarbeit mit diesem Unternehmen entschieden haben (aktueller Anbieter), was waren Ihre Auswahlkriterien, wenn ich fragen darf?",
      "Inwiefern haben sich diese Kriterien mit Blick auf Ihre heutigen Bedürfnisse verändert?",
      "Darf ich fragen, was Sie ändern würden, wenn Sie könnten?",
      "Für mich hört es sich so als liefen die Dinge für Sie ziemlich gut laufen mit dem, was Sie jetzt verwenden, gibt es etwas, das Sie ändern würden, wenn Sie könnten?",
      "Was würden Sie sagen, sind Ihre größten Stärken als Unternehmen?",
      "Was sind einige Ihrer Schwächen? Wie lange gibt es das schon? Hatte es Auswirkungen? In welcher Hinsicht?"
    ],
    microGoals: [
      {
        text: "Eine konkrete blockierende Erklärung oder ein hinderlicher Glaubenssatz aus {6_focus_blockaden} wurde aufgegriffen und der Interessent hat in eigenen Worten beschrieben, dass ihn diese Sicht daran hindert, den von ihm genannten Nutzen ({17_focus_benefit_concrete}) und das gewünschte Gefühl ({17_focus_feeling_goal}) tatsächlich zu erreichen.",
        model: ["all"],
        variables: ["20_focus_limiting_belief"],
        requiredDeepness: { meaning: 5, clarity: 5 },
        voraussetzung: null
      },
      {
        text: "Der Interessent hat beschrieben, weshalb er nicht die Person bleiben möchte, die an dieser blockierenden Erklärung aus {6_focus_blockaden} festhält, wenn er den beschriebenen Nutzen ({17_focus_benefit_concrete}) und das gewünschte Gefühl ({17_focus_feeling_goal}) wirklich erleben will.",
        model: ["all"],
        requiredDeepness: { meaning: 6, clarity: 6 },
        variables: ["20_focus_new_identity"],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat erkannt, dass er, um die Person zu sein, die {17_focus_benefit_concrete} erreicht, ihren Glaubenssatz loslassen muss und bereit dazu ist.",
        model: ["all"],
        variables: [],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_21_shift"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_21_shift": {
    id: "flow_phase_21_shift",
    type: "phase",
    label: "Phase 21: Call 2 / Shift",
    speechShareType: "medium",
    allowedSubsystems: [],
    checkVagueness: true,
    icon: "",
    tonality: { 
      intention: "Shift: Verantwortung übernehmen. Opferrolle verlassen.", 
      emotion: "Führend, Klar, Dominant (Wohlwollend)", 
      pace: "Bestimmt", 
      allowed_cues: CUES_REALITY, 
      forbidden_words: [], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Wer ist dafür verantwortlich?", "Was können SIE tun?"], 
      probe_questions: ["Liegt es am Markt oder an der Strategie?", "Was ist Ihr Anteil daran?"] 
    },
    questionsPool: [
      "Was veranlasst Sie dazu, dies zu ändern?",
      "Darf ich fragen, was Sie ursprünglich zu dieser Entscheidung veranlasst hat und was Ihnen jetzt wichtig ist?",
      "Können Sie mir die Schritte erläutern, die Sie zu diesem Schluss geführt haben?",
      "Wenn Sie die Sache noch einmal machen könnten, was würden Sie anders machen als vorher?",
      "Können Sie mir ein konkretes Beispiel nennen, damit ich das besser verstehen kann?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat anerkannt, dass die Hauptursachen für {9_focus_problem_main} nicht nur in äußeren Umständen liegen, sondern auch mit eigenen Entscheidungen zusammenhängen.",
        model: ["all"],
        variables: ["21_focus_ownership_partial"],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat in eigenen Worten wiedergegeben, weshalb er selbst derjenige ist, der die Zügel in der Hand hat, um seine Situation aktiv zu ändern und zu der Person zu werden, die {17_focus_benefit_concrete} erreicht.",
        model: ["all"],
        variables: ["21_focus_ownership_full"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_22_ownership_commit"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_22_ownership_commit": {
    id: "flow_phase_22_ownership_commit",
    type: "phase",
    label: "Phase 22: Call 2 / Ownership & Commit",
    speechShareType: "medium",
    allowedSubsystems: [],
    checkVagueness: true,
    icon: "",
    tonality: { 
      intention: "Ownership & Commit: Ernsthaftigkeit prüfen. Qualifikation.", 
      emotion: "Erwachsen, Fordernd, Klartext", 
      pace: "Ruhig", 
      allowed_cues: CUES_CLOSE, 
      forbidden_words: ["Bitte", "Versuch"], 
      formatting_rule: "Fette Commitments im Markdown Format **Committment**", 
      clarify_questions: ["Wie wichtig ist das wirklich?", "Auf einer Skala von 1-10?"], 
      probe_questions: ["Warum jetzt?", "Warum nicht warten?"] 
    },
    questionsPool: [
      "Wie wichtig ist es für Sie, Ihre Situation zu ändern und mit der ... zu beginnen?",
      "Wie wichtig ist es für Sie, etwas dagegen zu unternehmen? Aber warum gerade jetzt?",
      "Ist es für Sie wichtig, dieses Problem zu lösen? Und warum?",
      "Wenn Sie es könnten ... was würde das für Sie persönlich bedeuten?",
      "Sind Sie bereit, Ihre Situation zu ändern, damit Sie ... ?"
    ],
    microGoals: [
      { 
        text: "Wichtigkeit der Lösung auf Skala (1-10) ist hoch (8-10).", 
        model: ["all"], 
        variables: ["22_focus_commitment_score"], 
        voraussetzung: null 
      },
      { 
        text: "Commitment zur Umsetzung (Zeit/Energie) ist gegeben.", 
        model: ["all"], 
        variables: [], 
        voraussetzung: null 
      }
    ],
    nextModule: ["flow_phase_23_permission_risk"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_23_permission_risk": {
    id: "flow_phase_23_permission_risk",
    type: "phase",
    label: "Phase 23: Call 2 / Permission & Risiko",
    speechShareType: "medium",
    allowedSubsystems: [],
    checkVagueness: true,
    icon: "",
    tonality: { 
      intention: "Permission & Risiko: Vertrauen festigen. Erlaubnis holen.", 
      emotion: "Transparent, Fair, Partnerschaftlich", 
      pace: "Normal", 
      allowed_cues: CUES_TRUST, 
      forbidden_words: [], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: [], 
      probe_questions: [] 
    },
    questionsPool: [
      "Haben Sie das Gefühl, dass es das ist, wonach Sie suchen?",
      "Warum glauben Sie, dass es das ist?",
      "Wären Sie bereit, mir einen Rat zu geben, wie wir uns besser für Sie einsetzen können?",
      "Gibt es noch etwas, das Sie besprechen möchten, bevor wir unser Konzept abschließen?"
    ],
    microGoals: [
      {
        text: "Der Interessent stimmt dem Vorschlag zu, ihm zu zeigen, wie er zu der Person werden kann, die {4_focus_soll_ergebnisse} erreicht, ohne {11_focus_personal_impact}.",
        model: ["all"],
        variables: ["23_focus_permission"],
        voraussetzung: null
      },
      {
        text: "Der Interessent bestätigt, nachdem der Closer den Teil mit einem Abschlussplädoyer beendet hat, dass sich die bisher besprochenen Punkte als passender Ansatz anfühlen und ihm mehr Sicherheit und/oder Klarheit geben.",
        model: ["all"],
        variables: [],
        voraussetzung: null
      },
      {
        text: "Der Interessent begründet seine Ausführungen und beschreibt den für ihn wichtigsten Punkt.",
        model: ["all"],
        variables: ["23_focus_risk_reason"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_24_solution"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_24_solution": {
    id: "flow_phase_24_solution",
    type: "phase",
    label: "Phase 24: Call 2 / Lösung",
    speechShareType: "high",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "Lösung: Architektur zeigen. Brücke bauen.", 
      emotion: "Kompetent, Erklärend, Sicher", 
      pace: "Normal", 
      allowed_cues: ["Hier ist der Weg...", "Schau...", "Folgendes..."], 
      forbidden_words: [], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Macht das Sinn?", "Verstehen Sie das Konzept?"], 
      probe_questions: ["Wie klingt das für Sie?", "Können Sie sich das vorstellen?"] 
    },
    questionsPool: [
      "Welche Möglichkeiten gibt es, die Ihnen helfen könnten?",
      "Was haben Sie in der Vergangenheit versucht, dagegen zu tun? Was hat funktioniert? Was hat nicht funktioniert?",
      "Was haben Sie gefunden? Wie hat das für Sie funktioniert?",
      "Wie könnte das für Sie am besten funktionieren?",
      "Wenn diese Änderungen das Problem für Ihr Unternehmen lösen würden, wie würde Ihnen das am meisten helfen? Und in welcher Hinsicht?"
    ],
    microGoals: [
      {
        text: "Es wurde eine konkrete Lösung vorgestellt, die zeigt, wie {4_focus_soll_ergebnisse} mit besonderem Fokus auf {13_focus_prio_reason} und weiteren Punkten von {12_focus_sollvoraussetzungen} erreicht werden kann und durch eine abschließende Frage an den Interessenten, ob außer dem Invest noch weitere Fragen bestehen, beendet.",
        model: ["all"],
        variables: [],
        voraussetzung: null
      },
      {
        text: "Falls vorhanden, werden abschließende Fragen des Interessenten durch den Closer beantwortet. Anschließend wird der Interessent darum gebeten zu begründen, welche Vorteile er in der Lösung sieht.",
        model: ["all"],
        variables: [],
        voraussetzung: null
      },
      {
        text: "Der Interessent benennt die Vorteile, die sich aus der Lösung ergeben.",
        model: ["all"],
        variables: ["24_focus_solution_confirmed"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_25_invest_roi"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_25_invest_roi": {
    id: "flow_phase_25_invest_roi",
    type: "phase",
    label: "Phase 25: Call 2 / Invest & ROI",
    speechShareType: "high",
    allowedSubsystems: [],
    icon: "",
    checkVagueness: true,
    tonality: { 
      intention: "Invest & ROI: Logik schließen. Preis verankern.", 
      emotion: "Rational, Logisch, Geschäftsmäßig", 
      pace: "Präzise", 
      allowed_cues: CUES_LOGIC, 
      forbidden_words: ["Teuer", "Kosten"], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Sehen Sie den Wert?", "Rechnet sich das für Sie?"], 
      probe_questions: ["Was ist Ihnen das wert?", "Ist das a gutes Invest?"] 
    },
    questionsPool: [
      "Welchen Nutzen sehen Sie darin, dass Sie dieses Problem tatsächlich gelöst haben?",
      "Welchen Nutzen sehen Sie für sich und Ihr Unternehmen darin?",
      "Könnten Sie mit mir Ihr Haushaltsverfahren erläutern? Nur um zu sehen, ob ich Ihnen helfen kann, mit welchen finanziellen Rahmenbedingungen arbeiten Sie?",
      "Können Sie mir sagen, wie die Finanzierung für dieses Projekt festgelegt wird?",
      "Sind Sie der Meinung, dass das Ihnen zur Verfügung gestellte Budget ausreicht, um dieses Problem zu lösen?"
    ],
    microGoals: [
      {
        text: "Der Interessent hat bestätigt, dass der Invest in die Lösung im Verhältnis zu den aktuellen betriebswirtschaftlichen Auswirkungen ({9_focus_financial_loss} und {10_focus_total_impact}) für ihn nachvollziehbar ist.",
        model: ["b2b"],
        variables: ["25_focus_roi_confirmed"],
        voraussetzung: null
      }
    ],
    nextModule: ["flow_phase_26_close"],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "flow_phase_26_close": {
    id: "flow_phase_26_close",
    type: "phase",
    label: "Phase 26: Call 2 / Close",
    speechShareType: "medium",
    allowedSubsystems: [],
    checkVagueness: true,
    tonality: { 
      intention: "Close: Entscheidung führen. Handeln.", 
      emotion: "Final, Souverän, Ruhig", 
      pace: "Ruhig", 
      allowed_cues: CUES_CLOSE, 
      forbidden_words: ["Kauf bitte", "Bitte", "Deal?", "Okay?"], 
      formatting_rule: "STRIKTE SYSTEM-DIREKTIVE: Nutze AUSNAHMSLOS Fettschrift (Markdown: text) für alle geschäftsrelevanten Schlüsseldaten. Dies umfasst zwingend: 1. Alle numerischen Werte (z.B. 5.000 Euro, 12 Wochen, 3 Mitarbeiter). 2. Den Kern des gewünschten Outcomes (z.B. 9to5 Exit, Sichtbarkeit erhöhen). 3. Identifizierte Schmerzpunkte (z.B. Zeitmangel, Umsatzeinbruch). 4. Eigennamen von Personen oder Firmen. Jede Nichterfüllung dieser Formatierung bei diesen Kategorien gilt als Logikfehler.", 
      clarify_questions: ["Wollen wir das machen?", "Was spricht dagegen?"], 
      probe_questions: ["Wie möchten Sie vorgehen?", "Sind Sie bereit?"] 
    },
    questionsPool: [
      "Haben Sie das Gefühl, dass dies die Antwort für Sie sein könnte? Aber warum?",
      "Haben Sie das Gefühl, dass dies das sein könnte, wonach Sie suchen? Warum glauben Sie, dass es das ist?",
      "Haben Sie das Gefühl, dass dies etwas ist, das Sie tun können (haben, tun) und das Sie an Ihr Ziel bringt? Warum haben Sie das Gefühl, dass es so ist?",
      "Wäre das angemessen, oder wie möchten Sie von hier aus weiter vorgehen?"
    ],
    microGoals: [
      {
        text: "Der Interessent bestätigt, nachdem er keine weiteren Fragen mehr hat, dass er unabhängig vom Preis eine Zusammenarbeit angehen würde.",
        model: ["all"],
        variables: ["26_focus_feeling_close"],
        voraussetzung: null
      },
      {
        text: "Das erforderliche Investment wird vom Closer genannt und mit oder ohne Einwandbehandlung bestätigt.",
        model: ["all"],
        variables: [],
        voraussetzung: null
      },
      {
        text: "Der Interessent hat bestätigt, dass es riskanter ist, so weiterzumachen wie bisher, anstatt etwas an seiner Situation zu ändern.",
        model: ["all"],
        variables: ["26_focus_logic_close"],
        voraussetzung: null
      },
      {
        text: "Es wurden klare nächste Schritte vereinbart (z.B. Start, interne Freigaben, Vertragsabschluss) oder eine explizite Entscheidung wurde getroffen.",
        model: ["all"],
        variables: ["26_focus_next_step"],
        voraussetzung: null
      }
    ],
    nextModule: [],
      scriptId: "script_sales_call_v2",
  modulArea: 'sales',
  modulTopic: 'sales call',
  modulStatus: 'active',
  isGlobal: true
  },

  "mod_identity_based_selling_framework": {
        id: "mod_identity_based_selling_framework",
        type: "intervention",
        label: "Modul: Identity-Based Selling (6-Step Framework)",
        allowedSubsystems: ["gegenfrage", "emotion_trigger", "clarification", "gegenfrage_antwort_plus", "uncertainty_check", "repair_misunderstanding"],
        speechShareType: "medium",
        microGoals: [
          // SPLIT START: Mutually exclusive entry points based on Phase 5 variable
          {
            text: "ZIEL: (Pfad A - Noch Nie) Der Interessent bestätigt explizit (Ja/Nein), dass der Fokus dieses Gesprächs auf Identität/Selbstbild liegt.",
            model: ["all"],
            variables: ["mod_other_identity_frame_accepted"],
            voraussetzung: { var: "5_other_status_profi_hilfe", op: "falsy" }, // ONLY if never had help
            specificQuestions: [
              "Da Sie das bisher noch nicht professionell angegangen sind: Nur damit wir den Fokus richtig setzen – es geht hier weniger um perfekte Worte, sondern darum, wer Sie als Person werden müssen, damit das Ergebnis möglich wird – ist das fair?"
            ]
          },
          {
            text: "ZIEL: (Pfad B - Lange Her) Der Interessent bestätigt explizit (Ja/Nein), dass der Fokus dieses Gesprächs auf Identität/Selbstbild liegt.",
            model: ["all"],
            variables: ["mod_other_identity_frame_accepted"],
            voraussetzung: { var: "5_other_status_profi_hilfe", op: "truthy" }, // ONLY if had help before
            specificQuestions: [
              "Da der letzte Versuch schon lange her ist: Darf ich kurz den Rahmen setzen? Wir sprechen heute nicht über alte Kamellen, sondern darüber, wer Sie heute werden müssen, damit das Ergebnis diesmal wirklich passiert – passt das?"
            ]
          },
          // END SPLIT - Common Path Resumes
          {
            text: "ZIEL: Der Interessent beschreibt in eigenen Worten sein aktuelles Selbstbild/Identität im Kontext des Problems.",
            model: ["all"],
            variables: ["mod_other_current_identity"],
            voraussetzung: { var: "mod_other_identity_frame_accepted", op: "defined" },
            specificQuestions: [
              "Wenn Sie sich heute in dieser Situation beschreiben müssten: Was für ein Typ Mensch sind Sie gerade in Bezug auf dieses Thema?",
              "Wie würden Sie Ihr aktuelles Selbstbild hier in einem Satz beschreiben – so wie es heute wirklich ist?"
            ]
          },
          {
            text: "ZIEL: Der Interessent nennt den persönlichen Antrieb/Grund (Identity-Driver).",
            model: ["all"],
            variables: ["mod_other_identity_driver"],
            voraussetzung: { var: "mod_other_current_identity", op: "defined" },
            specificQuestions: [
              "Woher kommt bei Ihnen dieser innere Drive, das genau jetzt zu verändern?",
              "Warum sind Sie nicht okay damit, einfach so weiterzumachen wie bisher?"
            ]
          },
          {
            text: "ZIEL: Der Interessent benennt eine interne Blockade/limitierende Überzeugung.",
            model: ["all"],
            variables: ["mod_other_limiting_belief"],
            voraussetzung: { var: "mod_other_identity_driver", op: "defined" },
            specificQuestions: [
              "Was hat Sie intern bisher wirklich davon abgehalten – unabhängig von Zeit oder Geld?",
              "Welche Story erzählen Sie sich selbst, warum es bisher nicht passiert ist?"
            ]
          },
          {
            text: "ZIEL: Der Interessent quantifiziert oder konkretisiert die Kosten der aktuellen Identität/Story.",
            model: ["all"],
            variables: ["mod_other_identity_cost"],
            voraussetzung: { var: "mod_other_limiting_belief", op: "defined" },
            specificQuestions: [
              "Wenn Sie dieser Denkweise treu bleiben: Was kostet Sie das konkret – an Zeit, Energie, Chancen oder Selbstvertrauen?",
              "Wenn Sie einen Preis daran hängen müssten: Was hat es Sie bisher gekostet, so zu bleiben wie gerade?"
            ]
          },
          {
            text: "ZIEL: Der Interessent beschreibt ein klares Bild seiner Zukunfts-Identität.",
            model: ["all"],
            variables: ["mod_other_future_identity"],
            voraussetzung: { var: "mod_other_identity_cost", op: "defined" },
            specificQuestions: [
              "Die Version von Ihnen, die das Ergebnis sicher erreicht: Wie denkt und entscheidet die anders als Sie heute?",
              "Woran würde man diese Version von Ihnen erkennen – an Standards, Verhalten, Entscheidungen?"
            ]
          },
          {
            text: "ZIEL: Der Interessent bestätigt die Identitäts-Lücke (Identity Gap) explizit.",
            model: ["all"],
            variables: ["mod_other_identity_gap_confirmed"],
            voraussetzung: { var: "mod_other_future_identity", op: "defined" },
            specificQuestions: [
              "Wenn wir es simpel machen: Gerade sind wir eher ({mod_other_current_identity}) – und wir müssten eher ({mod_other_future_identity}) werden, damit das klappt. Trifft das zu?",
              "Würden Sie sagen, die Lücke liegt weniger im Wissen – sondern darin, dass Sie noch nicht als diese Person handeln?"
            ]
          },
          {
            text: "ZIEL: Der Interessent sagt explizit, dass er NICHT okay damit ist, die aktuelle Identität zu behalten.",
            model: ["all"],
            variables: ["mod_other_not_ok_staying_current_identity"],
            voraussetzung: { var: "mod_other_identity_gap_confirmed", op: "defined" },
            specificQuestions: [
              "Ich muss Sie direkt fragen: Sind Sie okay damit, genau diese Person zu bleiben – ja oder nein?",
              "Warum nicht? Was macht es für Sie unerträglich, so weiterzumachen?"
            ]
          },
          {
            text: "ZIEL: Der Interessent benennt mindestens 1–2 konkrete Dinge, die er loslassen/ändern muss.",
            model: ["all"],
            variables: ["mod_other_let_go_items"],
            voraussetzung: { var: "mod_other_not_ok_staying_current_identity", op: "defined" },
            specificQuestions: [
              "Was müssten Sie loslassen, damit Sie wirklich zu dieser Version von Ihnen werden?",
              "Welche Gewohnheit oder Story müsste verschwinden, damit Sie dort ankommen?"
            ]
          },
          {
            text: "ZIEL: Der Interessent formuliert persönliche Konsequenzen/Dringlichkeit auf Identitäts-Ebene.",
            model: ["all"],
            variables: ["mod_other_identity_consequences"],
            voraussetzung: { var: "mod_other_let_go_items", op: "defined" },
            specificQuestions: [
              "Wenn Sie in 2 Jahren immer noch dieselben Stories erzählen und dieselben Entscheidungen treffen: Wie fühlt sich Ihr Leben dann an?",
              "Was macht das mit Ihrem Selbstbild, wenn Sie wissen, dass Sie mehr könnten – aber es nicht leben?"
            ]
          },
          {
            text: "ZIEL: Der Interessent stimmt zu, dass die passende Lösung als Brücke zur Identitäts-Veränderung dienen muss.",
            model: ["all"],
            variables: ["mod_other_identity_bridge_positioned"],
            voraussetzung: { var: "mod_other_identity_consequences", op: "defined" },
            specificQuestions: [
              "Darf ich Ihnen zeigen, wie so eine Brücke aussehen kann, damit Sie wirklich zu dieser Person werden – ja?",
              "Wollen wir das einmal konkret durchgehen, wie man aus 'hoffen' zu 'machen' wird – passt das?"
            ]
          }
        ],
        tonality: DEFAULT_TONALITY,
        questionsPool: [
          "Woher kommt der Drive, das zu verändern?",
          "Was hält Sie intern wirklich zurück?",
          "Was kostet es Sie, diese Person zu bleiben?",
          "Wer müssten Sie werden, damit das Ergebnis zwangsläufig passiert?",
          "Was müssen Sie loslassen, um diese Version von sich zu werden?",
          "Wenn nichts sich ändert: Wie fühlt sich Ihr Leben in 2 Jahren an?"
        ]
  },

  "mod_handle_price": {
          id: "mod_handle_price",
          type: "intervention",
          label: "Einwandbehandlung: Preis / Budget",
          allowedSubsystems: ["gegenfrage", "clarification", "gegenfrage_antwort_plus", "uncertainty_check"], 
          speechShareType: "medium",
          shopInfo: {category: "DEFENSE", tier: "ELITE"},

          microGoals: [
            { 
              text: "Interessent beantwortet die Isolierungsfrage (Ja oder Nein).", 
              model: ["all"], 
              variables: ["mod_other_isolation_answer"], // FORCE EXTRACTION
              variableConfiguration: [{
                  variable: "mod_other_isolation_answer",
                  outcome: ["Ja", "Nein"]
              }],
              voraussetzung: null,
              specificQuestions: [
                "Wenn wir den Preis mal kurz beiseite schieben: Ist das grundsätzlich die Lösung, die Sie suchen?",
                "Angenommen Geld wäre kein Thema - würde das Programm Ihr Problem lösen?"
              ]
            },
            { 
              text: "Der Interessent erkennt den Wert der Lösung unabhängig vom Preis (positiv auf Isolierungsfrage).", 
              model: ["all"], 
              variables: ["mod_other_value_seen"],
              voraussetzung: { var: "mod_other_isolation_answer", op: "eq", value: "Ja" },
              requiredDeepness: { emotion: 6, clarity: 7 }, 
              specificQuestions: [
                "Okay, warum aber? Warum wäre das grundsätzlich die richtige Lösung für Sie?",
                "Was genau überzeugt Sie daran, dass das Programm Ihr Problem wirklich lösen kann?"
              ],
            },
            { 
              text: "Der Interessent erkennt den Wert nicht (negativ auf Isolierungsfrage).", 
              model: ["all"], 
              variables: ["mod_other_reframe_accepted"],
              voraussetzung: { var: "mod_other_isolation_answer", op: "eq", value: "Nein" },
              specificQuestions: [
                "Verstehe. Was geht Ihnen noch durch den Kopf, was wir noch nicht besprochen haben?"
              ]
            },
            { 
              text: "Der Interessent begründet, weshalb er es machen will (nach erkanntem Wert).", 
              model: ["all"], 
              variables: ["mod_other_value_why"],
              voraussetzung: { var: "mod_other_isolation_answer", op: "eq", value: "Nein" }, // Only if NO path taken
              specificQuestions: [
                "Welche Vorteile sehn Sie denn darin?",
                "Warum wäre es Ihnen wichtig, das jetzt anzugehen?"
              ]
            },
            { 
              text: "Maximal verfügbarer Betrag für direkte Zahlung ist klar benannt worden.", 
              model: ["all"], 
              variables: ["mod_other_financing_discussed"],
              voraussetzung: null, // Always reachable (via Jump or Linear)
              specificQuestions: [
                "Was ist der maximale Betrag, den Sie jetzt investieren könnten, um das gewünschte Ergebnis zu erreichen?"
              ]
            },
            { 
              text: "Gegebenenfalls wurde eine Ratenzahlung vereinbart und ein Betrag festgelegt, der direkt zu zahlen ist.", 
              model: ["all"], 
              variables: ["mod_other_installment_agreed"],
              voraussetzung: { var: "mod_other_financing_discussed", op: "truthy" },
              specificQuestions: [
                "Würde es helfen, wenn wir den Betrag in ein paar Häppchen aufteilen?", "Würde es helfen, wenn wir eine Ratenzahlung vereinbaren?",
              ]
            }
          ],
          tonality: {
              intention: "Deeskalieren, Unterscheiden, Wert aufbauen. Keinen Druck ausüben.",
              emotion: "Ruhig, Souverän, Nicht-Reaktiv, 'Auf der Seite des Kunden'",
              pace: "Langsam",
              allowed_cues: ["Ich verstehe...", "Das ist ein wichtiger Punkt...", "Lassen Sie uns das anschauen..."],
              forbidden_words: ["Rabatt", "Billiger", "Aber", "Doch"],
              formatting_rule: "Keine",
              clarify_questions: [],
              probe_questions: []
          },
          questionsPool: [
              "Wenn wir den Preis mal kurz beiseite schieben: Ist das grundsätzlich die Lösung, die Sie suchen?",
              "Meinen Sie damit, dass das Budget gerade absolut nicht da ist, oder dass Sie den Wert für diesen Preis noch nicht sehen?",
              "Was genau hatten Sie denn budgetiert?"
          ],
          checkVagueness: true
  },

  "mod_handle_stall": {
    id: "mod_handle_stall",
    type: "intervention",
    label: "Einwand: Drüber schlafen / Nachdenken",
    allowedSubsystems: ["gegenfrage", "emotion_trigger", "clarification", "gegenfrage_antwort_plus"],
    speechShareType: "medium",
    microGoals: [
      {
        text: "Interessent fühlt sich mit dem Wunsch, darüber nachzudenken, verstanden und nicht unter Druck gesetzt.",
        model: ["all"],
        variables: ["mod_other_stall_acknowledged"],
        voraussetzung: null,
        specificQuestions: [
          "Das macht total Sinn, dass Sie sich das noch einmal in Ruhe durch den Kopf gehen lassen wollen.",
          "Ich verstehe, dass Sie da nicht einfach spontan entscheiden wollen – das ist ja auch eine wichtige Sache."
        ]
      },
      {
        text: "Konkreter Zeitpunkt oder Rahmen für erneute Rückmeldung bzw. Gespräch wurde vereinbart.",
        model: ["all"],
        variables: ["mod_other_stall_followup_time"],
        voraussetzung: { var: "mod_other_stall_acknowledged", op: "defined" },
        specificQuestions: [
          "Damit wir beide planen können: Bis wann wollten Sie das für sich sortiert haben?",
          "Was wäre ein realistischer Zeitpunkt, an dem wir noch einmal konkret sprechen können?"
        ]
      },
      {
        text: "Interessent gibt zu, dass es noch etwas gibt, das unklar ist oder über das er nachdenken möchte.",
        model: ["all"],
        variables: ["mod_other_stall_isolation"],
        voraussetzung: { var: "mod_other_stall_followup_time", op: "defined" },
        specificQuestions: [
          "Bevor ich gehe: Woran genau möchten Sie in Ruhe noch denken, nur damit ich weiß, was Ihnen wichtig ist?",
          "Wenn Sie sagen, Sie wollen noch einmal darüber schlafen – was meinen Sie damit ganz konkret?"
        ]
      },
      {
        text: "Interessent benennt konkret, was ihm durch den Kopf geht (Themen, Fragen, Zweifel).",
        model: ["all"],
        variables: ["mod_other_stall_topics"],
        voraussetzung: { var: "mod_other_stall_isolation", op: "defined" },
        specificQuestions: [
          "Was ist es genau, was Ihnen gerade durch den Kopf geht – nur damit ich es besser verstehe?",
          "Welche 1–2 Punkte würden Sie sagen, müssten für Sie noch klarer sein, bevor Sie eine Entscheidung treffen?"
        ]
      },
      {
        text: "Hauptursache des Zögerns (z. B. Geld, Partner, Vertrauen, Timing) wurde klar benannt.",
        model: ["all"],
        variables: ["mod_other_stall_root_type"],
        voraussetzung: { var: "mod_other_stall_topics", op: "defined" },
        specificQuestions: [
          "Wenn Sie es in eine Schublade stecken müssten: Geht es eher um das Budget, um eine andere Person, um Vertrauen oder um etwas ganz anderes?",
          "Würden Sie sagen, Ihr Zögern hat mehr mit Geld, mit einer zweiten Meinung, mit Unsicherheit oder mit etwas anderem zu tun?"
        ]
      },
      {
        text: "Hauptursache des Zögerns (Geld/Budget/Kosten/Invest) wurde klar benannt und bestätigt.",
        model: ["all"],
        variables: ["mod_other_stall_root_money","objection_price_detected"],
        voraussetzung: { var: "mod_other_stall_root_type", op: "defined" },
        specificQuestions: [
          "Klingt so, als ob es in erster Linie um das Thema Geld bzw. Budget geht, richtig?",
          "Lassen Sie uns, wenn Sie möchten, genau das Thema Budget einmal sauber sortieren, damit Sie eine klare Entscheidungsgrundlage haben – passt das?"
        ]
      },
      {
        text: "Zögern wird als Partner-/Mitentscheider-Thema identifiziert.",
        model: ["all"],
        variables: ["mod_other_stall_to_partner", "objection_partner_detected"],
        voraussetzung: { var: "mod_other_stall_root_type", op: "contains", value: "Partner" },
        specificQuestions: [
          "Habe ich Sie richtig verstanden, dass es vor allem darum geht, dass noch jemand anderes mit im Boot sein soll?",
          "Dann lassen Sie uns kurz konkret machen, wie wir die andere Person am besten einbinden, damit Sie nichts alleine weitergeben müssen – ist das fair?"
        ]
      },
      {
        text: "Zögern wird als Angst-/Unsicherheits-Thema identifiziert.",
        model: ["all"],
        variables: ["mod_other_stall_to_fear", "objection_fear_detected"],
        voraussetzung: { var: "mod_other_stall_root_type", op: "contains", value: "Angst" },
        specificQuestions: [
          "Klingt so, als wäre es weniger das Fachliche, sondern eher dieses innere Gefühl von „großer Schritt“ – liege ich da daneben?",
          "Wenn wir ganz ehrlich sind: Geht es mehr darum, dass sich dieser Schritt gerade ungewohnt oder riskant anfühlt?"
        ]
      }
    ],
    tonality: DEFAULT_TONALITY,
    questionsPool: [
      "Was genau lässt Sie gerade zögern?",
      "Woran möchten Sie in Ruhe noch denken, bevor Sie eine Entscheidung treffen?",
      "Was müsste für Sie heute klar sein, damit Sie kein schlechtes Gefühl mehr beim Entscheiden haben?"
    ]
  },
      
  "mod_handle_time": {
    id: "mod_handle_time",
    type: "intervention",
    label: "Einwand: Keine Zeit",
    allowedSubsystems: ["gegenfrage", "clarification", "gegenfrage_antwort_plus"],
    speechShareType: "medium",
    microGoals: [
      {
        text: "Interessent stimmt dem Closer zu ihm eine neue Perspektive zu geben.",
        model: ["all"],
        variables: ["mod_other_perspective_check"],
        voraussetzung: null,
        specificQuestions: [
          "Darf ich eine neue Perspektive vermitteln?",
          "Darf ich hierzu kurz etwas ergänzen?",
          "Darf ich Ihnen hierzu eine andere Sichtweise geben?"
        ]
      },
      {
        text: "Interessent erkennt Gemeinsamkeit.",
        model: ["all"],
        variables: ["mod_other_common_ground"],
        voraussetzung: null,
        specificQuestions: [
          "Was haben Bill Gates, Sie und Elon Musk gemeinsam?",
          "Was haben alle Menschen, sowohl diejenigen die erfolgreich sind als auch die, die es nicht sind gemeinsam?"
        ]
      },
      {
        text: "Zeitproblem als Prioritätsproblem identifiziert (nennt klar Zeit oder Priorität).",
        model: ["all"],
        variables: ["mod_other_time_priority"],
        voraussetzung: null,
        specificQuestions: [
          "Wenn wir ehrlich sind: Haben wir keine Zeit, oder hat das Thema gerade keine Priorität?",
          "Ist es die Zeit oder die Priorität?"
        ]
      },
      {
        text: "Interessent nennt Gründe.",
        model: ["all"],
        variables: ["mod_other_time_commitment", "objection_price_detected"],
        voraussetzung: { var: "mod_other_time_priority", op: "contains", value: "Prio" },
        specificQuestions: [
          "Woran denken Sie liegt das?"
        ]
      },
      {
        text: "Interessent bestätigt, dass er etwas ändern will (mit Ja).",
        model: ["all"],
        variables: ["mod_other_commitment_change"],
        voraussetzung: null,
        specificQuestions: [
          "Wollen Sie denn überhaupt etwas ändern, ich meine Sie müssen nicht?"
        ]
      },
      {
        text: "Interessent nennt Gründe weshalb er etwas ändern will.",
        model: ["all"],
        variables: ["mod_other_time_commitment_why", "objection_fear_detected"],
        voraussetzung: { var: "mod_other_commitment_change", op: "truthy" },
        specificQuestions: [
          "Damit ich es besser verstehe, weshalb ist Ihnen das so wichtig?"
        ]
      },
      {
        text: "Interessent nennt Gründe weshalb er nicht so bleiben will.",
        model: ["all"],
        variables: ["mod_other_commitment_why_not_stay"],
        voraussetzung: { var: "mod_other_commitment_change", op: "defined" },
        specificQuestions: [
          "Warum nicht so bleiben wie jetzt, so wie es andere auch tun?",
          "Weshalb nicht einfach nichts tun und hoffen, dass sich etwas ändert?"
        ]
      }
    ],
    tonality: DEFAULT_TONALITY,
    questionsPool: [
      "Wann wäre dann der richtige Zeitpunkt?"
    ]
  },

  "mod_handle_partner": {
    id: "mod_handle_partner",
    type: "intervention",
    label: "Einwand: Partner / Entscheidung mit Dritten",
    allowedSubsystems: ["gegenfrage", "clarification", "gegenfrage_antwort_plus"],
    speechShareType: "medium",
    microGoals: [
      {
        text: "Interessent bestätigt, dass das Programm für ihn persönlich die richtige Lösung wäre, wenn der Partner / die andere Person an Bord ist.",
        model: ["all"],
        variables: ["mod_other_partner_solution_fit"],
        voraussetzung: null,
        specificQuestions: [
          "Wenn die Person, mit der Sie das besprechen wollen, jetzt mit im Gespräch wäre und das unterstützen würde – hätten Sie persönlich dann das Gefühl, dass das hier die richtige Lösung für Sie ist?",
          "Unabhängig von Ihrem Partner oder Geschäftspartner: Wenn diese Person voll dahinter stehen würde – wäre das hier die Lösung, die Sie sich eigentlich wünschen?"
        ]
      },
      {
        text: "Interessent begründet, warum das Programm für ihn die richtige Lösung ist.",
        model: ["all"],
        variables: ["mod_other_partner_solution_why"],
        voraussetzung: { var: "mod_other_partner_solution_fit", op: "truthy" },
        specificQuestions: [
          "Warum genau sagen Sie, dass das hier für Sie die richtige Lösung wäre?",
          "Was macht dieses Programm aus Ihrer Sicht zu der Lösung, nach der Sie gesucht haben?"
        ]
      },
      {
        text: "Interessent benennt, was ihn dazu bringt, das zuerst mit einem Partner / einer anderen Person zu besprechen (logistische Gründe wie Geld, Zeit, Abstimmung, etc.).",
        model: ["all"],
        variables: ["mod_other_partner_logistic_reason"],
        voraussetzung: { var: "mod_other_partner_solution_fit", op: "defined" },
        specificQuestions: [
          "Wenn Sie nach unserem Gespräch mit Ihrem Partner oder Geschäftspartner darüber sprechen – was genau geht Ihnen da im Kopf herum, dass Sie es unbedingt mit dieser Person besprechen möchten?",
          "Was ist der Hauptgrund, warum Sie das zuerst mit jemand anderem abstimmen wollen?"
        ]
      },
      {
        text: "Logistische Hürde ist klar als Geld- bzw. Budget-Thema identifiziert (Money-Einwand).",
        model: ["all"],
        variables: ["mod_other_partner_money_flag", "objection_price_detected"],
        voraussetzung: { var: "mod_other_partner_logistic_reason", op: "contains", value: "Geld" },
        specificQuestions: [
          "Wenn Sie an das Gespräch mit Ihrem Partner denken – geht es da vor allem um das Budget bzw. die Investition?",
          "Wäre es fair zu sagen, dass es bei dem Gespräch hauptsächlich um die Frage geht, ob die Investition für Sie beide finanziell sinnvoll ist?"
        ]
      },
      {
        text: "Logistische Hürden (z. B. Geld, Zahlungsweise) wurden in eine machbare Option übersetzt (z. B. Ratenplan).",
        model: ["all"],
        variables: ["mod_other_partner_logistics_resolved"],
        voraussetzung: { var: "mod_other_partner_logistic_reason", op: "defined" },
        specificQuestions: [
          "Wenn wir die Investition in gut machbare Schritte aufteilen – würde Ihnen das helfen, dass es finanziell besser für Sie passt?",
          "Würde es Ihnen helfen, wenn wir gemeinsam eine Zahlungsvariante finden, die in Ihr aktuelles Budget passt?"
        ]
      },
      {
        text: "Interessent beantwortet, was er tun würde, wenn der Partner / die andere Person klar Nein sagt (Verantwortung / Ownership).",
        model: ["all"],
        variables: ["mod_other_partner_would_do_anyway"],
        voraussetzung: { var: "mod_other_partner_logistics_resolved", op: "defined" },
        specificQuestions: [
          "Angenommen, Sie erzählen Ihrem Partner oder Geschäftspartner von dem Programm und er oder sie sagt klar Nein – was würden Sie dann tun?",
          "Stellen wir uns vor, die andere Person wäre dagegen – wie würden Sie dann mit der Entscheidung umgehen?"
        ]
      },
      {
        text: "Emotionaler Anteil (Angst / Selbstzweifel / Konfliktvermeidung) hinter dem Partner-Einwand ist klar benannt.",
        model: ["all"],
        variables: ["mod_other_partner_fear_reason", "objection_fear_detected"],
        voraussetzung: { var: "mod_other_partner_would_do_anyway", op: "defined" },
        specificQuestions: [
          "Was wäre für Sie das Unangenehme daran, wenn es am Ende doch nicht so läuft wie geplant und Sie das Ihrem Partner erklären müssten?",
          "Wenn Sie ganz ehrlich sind: Geht es bei dem Gespräch mit der anderen Person eher um Respekt – oder steckt da auch die Sorge dahinter, dass es nicht klappt und Sie sich dafür rechtfertigen müssten?"
        ]
      },
      {
        text: "Interessent stimmt einem ersten Commitment zu (z. B. Anzahlung / Startentscheidung), bevor er mit dem Partner spricht.",
        model: ["all"],
        variables: ["mod_other_partner_downpayment"],
        voraussetzung: { var: "mod_other_partner_would_do_anyway", op: "truthy" },
        specificQuestions: [
          "Damit Sie nicht mit leeren Händen in das Gespräch gehen: Wäre es für Sie stimmig, heute schon den ersten Schritt zu machen und den Rest nach dem Gespräch gemeinsam zu finalisieren?",
          "Macht es für Sie Sinn, heute bereits eine Anzahlung zu leisten, sodass Sie klar sagen können: „Ich habe die Entscheidung für uns getroffen – und jetzt hole ich mir nur noch dein Feedback dazu“?"
        ]
      },
      {
        text: "Interessent erkennt, dass die Verantwortung für die gewünschte Entwicklung (beruflich/geschäftlich/familiär) letztlich bei ihm selbst liegt.",
        model: ["all"],
        variables: ["mod_other_partner_responsibility"],
        voraussetzung: { var: "mod_other_partner_would_do_anyway", op: "falsy" },
        specificQuestions: [
          "Wenn Sie an Ihre Situation und Ihr Umfeld denken: Wer trägt aus Ihrer Sicht am Ende die Verantwortung dafür, dass Sie auf dem Niveau sind, das Sie sich wünschen – Sie selbst oder jemand anders?",
          "In Ihrer Wahrnehmung: Bei wem liegt die Hauptverantwortung, Ihre berufliche oder geschäftliche Entwicklung dorthin zu bringen, wo Sie sie gerne hätten?"
        ]
      },
      {
        text: "Interessent formuliert, warum es nicht fair ist, die Verantwortung dauerhaft auf andere abzuwälzen.",
        model: ["all"],
        variables: ["mod_other_partner_fairness"],
        voraussetzung: { var: "mod_other_partner_responsibility", op: "truthy" },
        specificQuestions: [
          "Ist es aus Ihrer Sicht fair, diese Verantwortung jemand anderem aufzubürden, wenn eigentlich Sie derjenige sind, der etwas verändern möchte? Warum?",
          "Wie fühlt es sich für Sie an, wenn Sie die Verantwortung für Ihre Zukunft auf jemand anderen schieben – ist das wirklich das, was Sie wollen?"
        ]
      },
      {
        text: "Interessent beschreibt die Konsequenzen, wenn er Verantwortung weiterhin abgibt und nichts ändert.",
        model: ["all"],
        variables: ["mod_other_partner_consequence"],
        voraussetzung: { var: "mod_other_partner_fairness", op: "defined" },
        specificQuestions: [
          "Was passiert realistisch, wenn Sie weiter warten und Ihre Entscheidungen immer von anderen abhängig machen – wie sieht Ihr Alltag dann in 6 bis 12 Monaten aus?",
          "Wenn sich nichts ändert und Sie die Verantwortung weiter aus der Hand geben – was bedeutet das langfristig für Sie, Ihr Geschäft oder Ihre Familie?"
        ]
      },
      {
        text: "Interessent beschreibt, wer er werden muss, um die gewünschte Rolle als Entscheider und Verantwortlicher auszufüllen.",
        model: ["all"],
        variables: ["mod_other_partner_identity"],
        voraussetzung: { var: "mod_other_partner_consequence", op: "defined" },
        specificQuestions: [
          "Wenn Sie an die Rolle als Entscheider denken – wer müssten Sie dafür werden und welche Art von Entscheidungen würden Sie aus dieser Rolle heraus heute treffen?",
          "Wer müssten Sie sein, damit Sie sagen können: „Ich treffe die Entscheidungen, die mich und mein Umfeld wirklich nach vorne bringen“?"
        ]
      },
      {
        text: "Interessent bekennt sich dazu, wachsen zu wollen und seine bisherige Denkweise zu hinterfragen (Leadership-Challenge).",
        model: ["all"],
        variables: ["mod_other_partner_leadership"],
        voraussetzung: { var: "mod_other_partner_identity", op: "defined" },
        specificQuestions: [
          "Sind Sie heute auf dieses Gespräch gekommen, um alles so zu lassen wie es ist – oder um zu lernen und zu wachsen?",
          "Würden Sie sagen, dass Sie jemand sind, der bereit ist, seine bisherige Art zu entscheiden zu hinterfragen, um wirklich voranzukommen?"
        ]
      }
    ],
    tonality: DEFAULT_TONALITY,
    questionsPool: [
      "Wie möchten Sie nach unserem Gespräch ganz konkret mit der anderen Person weiter vorgehen?",
      "Was wäre aus Ihrer Sicht der nächste sinnvolle Schritt, damit Sie und Ihr Partner bzw. Geschäftspartner sich mit der Entscheidung wohlfühlen?"
    ]
  },

  "mod_handle_fear": {
    id: "mod_handle_fear",
    type: "intervention",
    label: "Einwand: Angst / Vertrauen",
    allowedSubsystems: ["gegenfrage", "emotion_trigger", "gegenfrage_antwort_plus"],
    speechShareType: "medium",
    microGoals: [
      {
        text: "Interessent gibt zu, dass der Schritt sich zumindest ein Stück weit \"scary\" / beängstigend anfühlt.",
        model: ["all"],
        variables: ["mod_other_fear_admitted"],
        voraussetzung: null,
        specificQuestions: [
          "Ich will es gar nicht Angst nennen, aber es fühlt sich schon wie ein großer Schritt an, oder?",
          "Wenn Sie ehrlich sind: Fühlt sich das gerade ein bisschen \"größer\" an als das, was Sie sonst so entscheiden?"
        ]
      },
      {
        text: "Gefühl wird normalisiert und als natürlicher Teil von Wachstum gerahmt (Komfortzone).",
        model: ["all"],
        variables: ["mod_other_fear_normalized"],
        voraussetzung: { var: "mod_other_fear_admitted", op: "truthy" },
        specificQuestions: [
          "Wissen Sie, warum sich das so anfühlt? Wer wächst denn wirklich innerhalb seiner Komfortzone?",
          "Wenn Sie daran denken, wann Sie das letzte Mal gewachsen sind – war das eher bequem oder eher außerhalb der Komfortzone?"
        ]
      },
      {
        text: "Interessent reagiert emotional entspannter (Humor / Entschärfung akzeptiert).",
        model: ["all"],
        variables: ["mod_other_fear_relaxed"],
        voraussetzung: { var: "mod_other_fear_normalized", op: "defined" },
        specificQuestions: [
          "Ganz ehrlich: Wenn Sie jetzt komplett cool wie ein Fels in der Brandung wären, müsste ich fast Ihren Puls checken – macht das Sinn für Sie?",
          "Wenn das sich völlig neutral anfühlen würde, würde das wirklich zu etwas Wichtigem in Ihrem Leben passen?"
        ]
      },
      {
        text: "Bedeutung der Angst wird bewusst umgedeutet (von Blockade zu potenziellem Antrieb).",
        model: ["all"],
        variables: ["mod_other_fear_reframed"],
        voraussetzung: { var: "mod_other_fear_relaxed", op: "defined" },
        specificQuestions: [
          "Wenn wir dieses Gefühl nicht als Stopp-Schild sehen, sondern als Zeichen, dass Sie gerade wirklich etwas verändern – macht das für Sie Sinn?",
          "Könnten wir uns darauf einigen, dass dieses Gefühl eher zeigt, dass es wichtig ist – statt dass es ein Grund ist, gar nichts zu tun?"
        ]
      },
      {
        text: "Interessent entscheidet sich bewusst zwischen zwei Optionen: Angst engt ein vs. Angst wird in Handlung verwandelt (Binary Choice).",
        model: ["all"],
        variables: ["mod_other_fear_choice"],
        voraussetzung: { var: "mod_other_fear_reframed", op: "truthy" },
        specificQuestions: [
          "Wenn wir ehrlich sind, haben Sie jetzt zwei Möglichkeiten: Entweder dieses Gefühl engt Sie ein und alles bleibt, wie es ist – oder Sie nutzen es als Energie, um jetzt den Schritt zu gehen. Was fühlt sich für Sie stimmiger an?",
          "Würden Sie lieber der Person entsprechen, die sich von diesem Gefühl bremsen lässt – oder der Person, die es nutzt, um jetzt bewusst ins Handeln zu kommen?"
        ]
      },
      {
        text: "Konsequenzen werden beschrieben, falls die Angst weiter einengt und nichts verändert wird.",
        model: ["all"],
        variables: ["mod_other_fear_consequence"],
        voraussetzung: { var: "mod_other_fear_choice", op: "defined" },
        specificQuestions: [
          "Was passiert realistisch, wenn dieses Gefühl Sie weiterhin zurückhält und Sie alles so lassen, wie es jetzt ist – wie sieht Ihr Alltag dann in 6–12 Monaten aus?",
          "Wenn Sie dieses Gefühl weiter entscheiden lassen, statt selbst zu entscheiden – was bedeutet das langfristig für Sie und Ihre Ziele?"
        ]
      },
      {
        text: "Interessent entscheidet, ob er diese Konsequenzen wirklich zulassen will.",
        model: ["all"],
        variables: ["mod_other_fear_allow"],
        voraussetzung: { var: "mod_other_fear_consequence", op: "defined" },
        specificQuestions: [
          "Sind Sie bereit, genau das zuzulassen – oder sagen Sie, dass das so eigentlich nicht weitergehen soll?",
          "Ist das ein Ergebnis, mit dem Sie sich wirklich abfinden könnten, oder sagen Sie innerlich: Nein, das möchte ich so nicht?"
        ]
      },
      {
        text: "Interessent beschreibt, wer er werden muss, um die gewünschte Veränderung trotz Angst umzusetzen (Identität / Future Self).",
        model: ["all"],
        variables: ["mod_other_fear_identity"],
        voraussetzung: { var: "mod_other_fear_allow", op: "truthy" },
        specificQuestions: [
          "Wer müssten Sie sein, damit Sie sagen können: Ich treffe Entscheidungen trotz Nervosität, die mich wirklich voranbringen?",
          "Wenn Sie an Ihr zukünftiges Ich denken, das dieses Ziel bereits erreicht hat – wie würde diese Person heute entscheiden?"
        ]
      },
      {
        text: "Konkreter nächster Schritt wird benannt, um zu verhindern, dass die negative Konsequenz eintritt (Handlungsplan).",
        model: ["all"],
        variables: ["mod_other_fear_action"],
        voraussetzung: { var: "mod_other_fear_identity", op: "defined" },
        specificQuestions: [
          "Was genau müssen wir jetzt gemeinsam tun, damit das, wovor Sie sich eben noch gefürchtet haben, nicht eintritt und Sie Ihr Ziel wirklich erreichen?",
          "Welchen konkreten Schritt machen wir jetzt als Nächstes, damit Sie nicht in ein paar Monaten zurückblicken und sagen: Ich habe mich von meiner Angst bremsen lassen?"
        ]
      }
    ],
    tonality: DEFAULT_TONALITY,
    questionsPool: [
      "Woher kommt dieses Gefühl gerade, wenn Sie ehrlich zu sich sind?",
      "Was macht diese Entscheidung für Sie so groß oder ungewohnt?"
    ]
  },

  "mod_handle_fear_b2b": {
    id: "mod_handle_fear_b2b",
    type: "intervention",
    label: "Einwand: Risiko / Skepsis (B2B)",
    allowedSubsystems: ["gegenfrage", "emotion_trigger", "gegenfrage_antwort_plus"],
    speechShareType: "medium",
    microGoals: [
      {
        text: "Interessent benennt, worauf sich sein Zögern konkret bezieht.",
        model: ["b2b"],
        variables: ["mod_other_fear_source"],
        voraussetzung: null,
        specificQuestions: [
          "Worauf bezieht sich Ihr Zögern im Kern?",
          "Wenn Sie es greifbar machen müssten: Was genau fühlt sich hier gerade unsicher an?",
          "Und falls es noch unscharf ist – was fühlt sich für Sie noch nicht stimmig an?"
        ]
      },
      {
        text: "Interessent nennt Gründe dafür, dass ein Weiter-so auch keine Lösung ist.",
        model: ["b2b"],
        variables: ["mod_other_status_quo_facts"],
        voraussetzung: { var: "mod_other_fear_source", op: "defined" },
        specificQuestions: [
          "Wenn wir nur die Fakten ansehen: Was passiert, wenn es so weiter geht?",
          "Weshalb nicht einfach so weitermachen wie immer?"
        ]
      },
      {
        text: "Interessent stimmt dem Closer zu, dass die Richtung seines aktuellen Kurses die falsche Richtung ist",
        model: ["b2b"],
        variables: ["mod_other_status_quo_direction"],
        voraussetzung: { var: "mod_other_status_quo_facts", op: "defined" },
        specificQuestions: [
          "Wenn Sie Ihren aktuellen Kurs weiterdenken – führt das eher in Richtung Entlastung oder bewirkt es das Gegenteil?",
          "Was denken Sie in welche Richtung es geht, wenn sich nichts ändert - die richtige oder die falsche?"
        ]
      },
      {
        text: "Interessent erklärt selbst, warum dieser aktuelle Kurs den Engpass verstärkt.",
        model: ["b2b"],
        variables: ["mod_other_direction_reason"],
        voraussetzung: { var: "mod_other_status_quo_direction", op: "defined" },
        specificQuestions: [
          "Ich stimmen Ihnen da zu. Wir sind am selben Tisch. Doch weshalb sehen Sie das so?",
          "Ich bin da ganz bei Ihnen, doch weshalb eigentlich?"
        ]
      },
      {
        text: "Der Closer bringt unseren Ansatz als Gegenrichtung in das Gespräch ein und wird vom Interessent bestätigt.",
        model: ["b2b"],
        variables: ["mod_other_solution_match"],
        voraussetzung: { var: "mod_other_direction_reason", op: "defined" },
        specificQuestions: [
          "In welche Richtung sollten wir schauen, um voran zu kommen?"
        ]
      },
      {
        text: "Interessent nennt den Grund, warum diese Richtung für ihn richtig ist.",
        model: ["b2b"],
        variables: ["mod_other_direction_why"],
        voraussetzung: { var: "mod_other_solution_match", op: "truthy" },
        specificQuestions: [
          "Weshalb denken Sie geht das in die richtige Richtung?",
          "Welchen Unterschied sehen Sie zur aktuellen Herangehensweise?"
        ]
      },
      {
        text: "Interessent commitet sich verbal zur Richtung.",
        model: ["b2b"],
        variables: ["mod_other_direction_commitment"],
        voraussetzung: { var: "mod_other_direction_why", op: "defined" },
        specificQuestions: [
          "Wenn Sie die beiden Möglichkeiten gegeinander abwägen, was wäre dann der nächste sinnvolle Schritt für Sie?",
          "Was ergibt für Sie jetzt mehr Sinn: Den aktuellen Kurs weiterfahren oder bewusst eine neue Richtung einzuleiten?"
        ]
      },
      {
        text: "Interessent vergleicht beide Ansätze miteinander und wägt das Risiko ab.",
        model: ["b2b"],
        variables: ["mod_other_risk_comparison"],
        voraussetzung: { var: "mod_other_direction_commitment", op: "defined" },
        specificQuestions: [
          "Sie können auch so weitermachen wie bisher, doch was ist riskanter?",
          "Welches dieser beiden Risiken wäre im Zweifel schwerer zu korrigieren?"
        ]
      }
    ],
    tonality: DEFAULT_TONALITY,
    questionsPool: [
      "Was genau macht den Schritt für Sie gerade schwer greifbar?",
      "Was davon sorgt im Moment am stärksten dafür, dass Sie zögern?"
    ]
  }
};

export const SUBSYSTEMS: Record<string, SubsystemConfig> = {
  uncertainty_check: {
    id: "uncertainty_check",
    label: "UNCERTAINTY / WEISS NICHT",
    trigger_logic: "conditional",
    trigger_description:
      "Ziel: Eine Wissenslücke/Unsicherheit so auflösen, dass das AKTUELLE MIKROZIEL danach konkret beantwortbar wird. " +
      "ABSOLUTE REGEL: NENNE NIEMALS DEN BEGRIFF MIKROZIEL ODER ANDERE TECHNISCHE BEGRIFFE IN DER FRAGE. Jede Frage muss KONTEXTBEZOGEN auf das aktuelle Mikroziel sein und ausschließlich dazu dienen, dieses Mikroziel zu erreichen. " +
      "Keine Neben-Themen vertiefen. Kein neues Thema eröffnen. " +
      "Triggered, wenn der Prospect/Interessent auf eine Frage mit 'weiß nicht', 'keine Ahnung', 'bin unsicher', 'weiß ich ehrlich gesagt nicht' antwortet " +
      "und KEINE weitere Erklärung liefert.",
    allowedModules: [],
    steps: [
      {
        text: [
        "Okay – nur bezogen auf diese Frage und damit wir das aktuelle Ziel sauber treffen: Was wäre Ihre beste grobe Einschätzung?",
        "Verstanden – nur im Kontext: Was wäre ein plausibler Richtwert, auch wenn er nicht perfekt ist?",
        "Wenn Sie raten müssten Was wäre eher zutreffend?"
        ].join(" | ")
      }
    ]
  },

  clarification_vague_state: {
    id: "clarification_vague_state",
    label: "CLARIFICATION: VAGUE STATE",
    trigger_logic: "conditional",
    trigger_description:
      "Ziel: Vage Zustandsbeschreibungen in beobachtbare Realität übersetzen (Bedeutung → Beispiel → Impact), " +
      "damit das AKTUELLE MIKROZIEL EXAKT erfüllt werden kann. " +
      "ABSOLUTE REGEL: NENNE NIEMALS DEN BEGRIFF MIKROZIEL ODER ANDERE TECHNISCHE BEGRIFFE IN DER FRAGE. Jede Frage muss KONTEXTBEZOGEN auf das aktuelle Mikroziel sein und ausschließlich dazu dienen, dieses Mikroziel zu erreichen. " +
      "Keine Themenvertiefung außerhalb des Mikroziels. Keine neuen Nebenpfade. " +
      "Triggered, wenn der Prospect/Interessent einen subjektiven, vagen Zustand nennt (z.B. „schlecht“, „schwierig“, „kompliziert“, „chaotisch“, „anstrengend“, „läuft nicht“) " +
      "UND kein konkretes Beispiel liefert. " +
      "EXCLUSION: 'weiß nicht/keine Ahnung/unsicher' → uncertainty_check. " +
      "'ich verstehe nicht/ich kann nicht folgen/was meinen Sie?' → repair_misunderstanding.",
    allowedModules: [],
    steps: [
      {
        text: [
          "Wenn Sie '{word}' sagen – was heißt das ganz konkret?",
          "Was meinen Sie konkret mit {word}?",
          "Wie meinen Sie das genau?",
          "{word}?",
          "Woran machen Sie das fest?"
        ].join(" | "),
        input_variable: "meaning"
      },
      {
        text: [
          "Okay – haben Sie da ein Beispiel für mich?",
          "Welche konkrete Situation zeigt '{word}' am deutlichsten?",
          "Erzählen Sie mir eine konkrete Situation, in der sich das gezeigt hat."
        ].join(" | "),
        input_variable: "example"
      },
      {
        text: [
          "Und in dieser Situation ({example}) – was ist die konkrete Auswirkung?",
          "Wenn Sie an ({example}) denken: Welche konkrete Folge?",
          "Was ist in ({example}) der schwierigste Teil?"
        ].join(" | "),
        input_variable: "impact"
      }
    ]
  },

  clarification_criterion: {
    id: "clarification_criterion",
    label: "CLARIFICATION: CRITERION / REQUIREMENT",
    trigger_logic: "conditional",
    trigger_description:
      "Ziel: Ein genanntes Muss-Kriterium/Requirement so konkretisieren, dass es als eindeutige Entscheidungs- oder Bewertungsgrundlage " +
      "für das AKTUELLE MIKROZIEL nutzbar ist. " +
      "ABSOLUTE REGEL: NENNE NIEMALS DEN BEGRIFF MIKROZIEL ODER ANDERE TECHNISCHE BEGRIFFE IN DER FRAGE. Jede Frage muss KONTEXTBEZOGEN auf das aktuelle Mikroziel sein und ausschließlich dazu dienen, dieses Mikroziel zu erreichen. " +
      "Keine Detail-Vertiefung in Nebenthemen (z.B. keine Methoden-/Theorie-Interviews). " +
      "Triggered, wenn der Prospect/Interessent ein Muss-Kriterium oder Ablehnungsgrund nennt, aber NICHT sagt, was das konkret bedeutet (Schwelle, Beispiele, Kriterien). " +
      "EXCLUSION: 'weiß nicht/keine Ahnung/unsicher' → uncertainty_check. " +
      "'ich verstehe nicht/ich kann nicht folgen/was meinen Sie?' → repair_misunderstanding.",
    allowedModules: [],
    steps: [
      {
        text: [
          "Ist '{criterion}' bei Ihnen ein hartes Muss, oder gibt es Spielraum?",
          "Bezogen auf das, was wir gerade klären: '{criterion}' – ist das eine fixe Voraussetzung?",
          "Damit ich das korrekt einordne: '{criterion}' – verhandelbar oder nicht?"
        ].join(" | "),
        input_variable: "strictness"
      },
      {
        text: [
          "Was wäre Ihr konkretes Minimum, damit '{criterion}' für Sie erfüllt ist?",
          "Woran erkennen Sie konkret: 'Ja, das erfüllt {criterion}' – im Kontext dessen, was wir gerade entscheiden?",
          "Gibt es ein Beispiel oder einen Grenzwert, der für Sie klar '{criterion}' bedeutet?"
        ].join(" | "),
        input_variable: "threshold"
      },
      {
        text: [
          "Und wenn ({criterion}) nicht erfüllt ist – was ist dann die konkrete Folge?",
          "Welche konkrete Konsequenz entsteht für Sie, wenn ({criterion}) nicht passt?",
          "Was passiert dann konkret – Absage, Risiko, Mehrarbeit, Kosten?"
        ].join(" | "),
        input_variable: "consequence"
      }
    ]
  },

  clarification_outcome_metric: {
    id: "clarification_outcome_metric",
    label: "CLARIFICATION: OUTCOME / METRIC",
    trigger_logic: "conditional",
    trigger_description:
      "Ziel: Abstrakte Outcomes in eine messbare Metrik + Zielwert + Deadline übersetzen, " +
      "damit das AKTUELLE MIKROZIEL eindeutig erfüllt werden kann. " +
      "ABSOLUTE REGEL: NENNE NIEMALS DEN BEGRIFF MIKROZIEL ODER ANDERE TECHNISCHE BEGRIFFE IN DER FRAGE. Jede Frage muss KONTEXTBEZOGEN auf das aktuelle Mikroziel sein und ausschließlich dazu dienen, dieses Mikroziel zu erreichen. " +
      "Keine Ablenkung in Nebenpfade. " +
      "Triggered, wenn der Prospect/Interessent ein gewünschtes Ergebnis nennt, aber ohne Zielwert, Zeitraum oder Messgröße. " +
      "EXCLUSION: 'weiß nicht/keine Ahnung/unsicher' → uncertainty_check. " +
      "'ich verstehe nicht/ich kann nicht folgen/was meinen Sie?' → repair_misunderstanding.",
    allowedModules: [],
    steps: [
      {
        text: [
          "Woran würden Sie konkret merken, dass '{outcome}' erreicht ist?",
          "Was wäre ein klares Zeichen, dass '{outcome}' wirklich eingetreten ist?",
          "Was genau müsste passieren, damit Sie sagen: 'Ja, {outcome} ist erreicht'?"
        ].join(" | "),
        input_variable: "metric"
      },
      {
        text: [
          "Okay – und welcher grobe Zielwert wäre dafür passend?",
          "Wenn Sie es grob beziffern müssten: was wäre ein Zielwert für ({metric})?",
          "Was wäre für Sie ein guter Zielwert bei ({metric})?"
        ].join(" | "),
        input_variable: "target_value"
      },
      {
        text: [
          "Bis wann muss ({target_value}) bei ({metric}) erreicht sein, damit es für Sie passt?",
          "In welchem Zeitraum muss das stehen, damit es nicht eng wird?",
          "Welche Deadline haben Sie dafür im Kopf?"
        ].join(" | "),
        input_variable: "deadline"
      }
    ]
  },

  clarification_cause_link: {
    id: "clarification_cause_link",
    label: "CLARIFICATION: CAUSE / LOGIC LINK",
    trigger_logic: "conditional",
    trigger_description:
      "Ziel: Eine Ursache-Wirkung-Behauptung so absichern (Beobachtung → Beispiel → Impact), " +
      "dass sie als belastbare Grundlage für das AKTUELLE MIKROZIEL taugt. " +
      "ABSOLUTE REGEL: NENNE NIEMALS DEN BEGRIFF MIKROZIEL ODER ANDERE TECHNISCHE BEGRIFFE IN DER FRAGE. Jede Frage muss KONTEXTBEZOGEN auf das aktuelle Mikroziel sein und ausschließlich dazu dienen, dieses Mikroziel zu erreichen. " +
      "Keine Methoden-Interviews, keine Nebenthemen-Vertiefung. " +
      "Triggered, wenn der Prospect/Interessent einen Zusammenhang behauptet ('wegen X passiert Y'), aber ohne Beobachtung/Beispiel. " +
      "EXCLUSION: 'weiß nicht/keine Ahnung/unsicher' → uncertainty_check. " +
      "'ich verstehe nicht/ich kann nicht folgen/was meinen Sie?' → repair_misunderstanding.",
    allowedModules: [],
    steps: [
      {
        text: [
          "Was lässt Sie glauben, dass '{claim}' stimmt?",
          "Woran merken Sie konkret, dass '{claim}' zutrifft – im Kontext dessen, was wir gerade klären?",
          "Welche Beobachtung hat Sie zu '{claim}' gebracht?"
        ].join(" | "),
        input_variable: "evidence"
      },
      {
        text: [
          "Können Sie mir ein konkretes Beispiel nennen, wo sich das gezeigt hat?",
          "Wann war das zuletzt so – können Sie die Situation kurz beschreiben?",
          "Gab es einen Moment, wo Ihnen das besonders deutlich wurde?"
        ].join(" | "),
        input_variable: "example"
      },
      {
        text: [
          "Und was war die konkrete Auswirkung daraus?",
          "Welche Folge hatte das in dieser Situation ({example}) für Sie/den Betrieb?",
          "Was war das Ergebnis davon – messbar oder spürbar?"
        ].join(" | "),
        input_variable: "impact"
      }
    ]
  },

  repair_misunderstanding: {
    id: "repair_misunderstanding",
    label: "REPAIR: MISUNDERSTANDING",
    trigger_logic: "conditional",
    trigger_description:
      "Triggered, wenn der Prospect/Interessent signalisiert, dass er die Frage nicht versteht ('ich kann nicht folgen', 'was meinen Sie?', 'verwirrt'). Ziel: (1) kurz Verantwortung übernehmen, (2) die Frage extrem einfach und konkret neu stellen, (3) dabei NIEMALS das zuletzt strittige/off-scope Thema wiederholen. Fokus bleibt auf dem aktuellen Mikroziel. Keine A/B-Auswahl, keine Meta-Kategorisierung.",
    allowedModules: [],
    steps: [
      {
        text: [
          "Danke – dann war ich gerade unklar. Ich frage ganz simpel: Was ist der Hauptgrund, warum Sie heute mit mir sprechen?",
          "Verstanden – ich formuliere es einfacher: Worum soll es in diesem Gespräch für Sie im Kern gehen?",
          "Danke, ich mache es kurz und klar: Was möchten Sie durch dieses Gespräch erreichen?",
          "Alles gut – ich stelle die Frage anders: Was hat Sie konkret dazu gebracht, heute hier zu sein?"
        ].join(" | ")
      }
    ]
  },

    wishful_thinking: {
      id: "wishful_thinking",
      label: "WISHFUL THINKING TRAP",
      trigger_logic: "conditional",
      trigger_description:
        "Triggered, wenn der Prospect/Interessent ein Ergebnis oder eine Veränderung als „wäre schön“, „nice to have“ oder „ganz nett“ beschreibt, OHNE es als klare Voraussetzung, Notwendigkeit oder Muss zu formulieren (z. B. „wäre schon gut, mehr Bewerber zu haben“).WICHTIG EXAKT AN DIESE STRUKTUR HALTEN.",
      // SCOPE: Goal & Commitment phases
      allowedModules: [],
      steps: [
        {
          text:
            "Sagen Sie... Wäre es nur 'schön', das zu haben, oder ist es eine echte Voraussetzung für Sie?"
        },
        {
          text:
            "Okay, warum ist es für Sie eine Voraussetzung? Nur damit ich es besser nachvollziehen kann."
        },
        {
          text:
            "Kennen Sie den Unterschied zwischen einem 'wäre schön' und einer echten 'Voraussetzung'?"
        },
        {
          text:
            "Was glauben Sie würde passieren, wenn wir nur denken 'es wäre schön' versus 'es ist eine Voraussetzung'? Mit welchem Gedanken kommen wir eher voran?"
        },
        {
          text:
            "Können wir uns ab jetzt darauf einigen, dass wir von echten Voraussetzungen und 'Musts' sprechen, statt von vagen Ideen?"
        }
      ]
    },

    emotion_trigger: {
      id: "emotion_trigger",
      label: "EMOTION DEEP DIVE",
      trigger_logic: "conditional",
      trigger_description:
        "Triggered, wenn der Prospect/Interessent ein klares Gefühl für seine Situation benennt (z. B. „frustriert“, „Angst“, „genervt“, „überfordert“, „macht mich fertig“) und damit vor allem seine EMOTION ausdrückt, nicht nur einen Zustand sachlich beschreibt.",
      // SCOPE: Deep dive phases
      allowedModules: [],
      steps: [
        {
          text:
            "Sie sagten gerade '{emotion}'... was bedeutet '{emotion}' für Sie in diesem Kontext?"
        },
        {
          text:
            "Wann bemerken Sie dieses Gefühl ({emotion}) am stärksten? Gibt es da spezielle Situationen?"
        }
      ]
    },

    person_trigger: {
      id: "person_trigger",
      label: "OWNERSHIP SHIFT (MAN -> ICH)",
      trigger_logic: "conditional",
      trigger_description:
        "Triggered, wenn der Prospect/Interessent über ein eigenes Problem oder Gefühl spricht, dabei aber „man“ statt „ich“ verwendet (z. B. „man fühlt sich dann hilflos“), obwohl er offensichtlich von sich selbst spricht. Ziel ist, die Verantwortung von „man“ auf „ich“ zu verschieben.",
      // SCOPE: Identity & Responsibility phases
      allowedModules: [],
      steps: [
        {
          text:
            "Meinen Sie sich selbst, wenn Sie sagen, dass '{word_fragment}'?"
        },
        {
          text:
            "Gut... denn wer hat am Ende die Zügel in der Hand? 'Man' oder Sie?"
        }
      ]
    },

    blocking_trigger: {
      id: "blocking_trigger",
      label: "BLOCKING / OFFER PUSHBACK",
      trigger_logic: "always",
      trigger_description:
        "Triggered, wenn der Prospect/Interessent zu früh nach Preis, Kosten, Budget oder dem konkreten Angebot fragt oder entsprechend antwortet. (selbst indirekt) (BEISPIELE sind: „Was bieten Sie an?“, „Was kostet das?“, „Kommen Sie mal auf den Punkt“, „Sagen Sie mir einfach den Preis“. NUTZE immer einer der BEISPIELE.)",
      // SCOPE: Early phases ONLY. If we are in Phase 24 (Offer) or 25 (Invest), this must NOT trigger.
      allowedModules: [],
      steps: [
        {
          text:[
            "Ich verstehe, dass Sie wissen möchten was wir anbieten. Doch wir bieten nichts von der Stange an. Um zu sehen, ob das was wir machen überhaupt zu Ihren Vorstellungen passt, brauche ich noch einige Informationen von Ihnen. Klingt das fair?",
            "Natürlich, allerdings sprechen wir heute nicht ohne Grund miteinander. Damit wir unsere Zeit sinnvoll einsetzen können, lassen Sie uns bitte darauf konzentrieren ob das was wir machen Ihnen hilft. Wenn Sie keine Hilfe möchten, bin ich der erste der sagt: Bleiben Sie einfach auf dem Stand wo Sie jetzt sind. Heute geht es um Sie, nicht um mich."
      ].join(" | ") },
      ]
    },

    gegenfrage: {
      id: "gegenfrage",
      label: "GEGENFRAGE / RÜCKFÜHRUNG",
      trigger_logic: "always",
      trigger_description:
        "BEACHTE IMMER DAS AKTUELLE MIKROZIEL: activeGoalText. Die GEGENFRAGE MUSS SICH DARAN ORIENTIEREN, wenn die last_closer_question KEINEN BEZUG ZUM MIKROZIEL HAT.Falls die Frage einfach beantwortbar ist auch gerne erst antworten. Triggered bei ECHTER, INHALTLICHER RÜCKFRAGE des Prospect/Interessents (z.B. 'Warum fragen Sie das?', 'Wie meinen Sie das?', 'Was kostet das?'). IGNORIERE 'was'-Ausrufe ('was doch...'), 'echt?' oder rhetorische Floskeln. IGNORIERE Unwissenheit ('Ich weiß nicht'). Nur echte Fragen, die eine Antwort erwarten und BEANTWORTE DIE FRAGE, bevor du eine Gegenfrage stellst.",
      // SCOPE: Global allowed, except maybe at the very end.
      allowedModules: [], // Empty = Global (default behavior in new logic)
      steps: [
        {
          text: [
            "Gerne komme ich gleich noch einmal darauf zurück. Vorher kurz:",
            "Das beantworte ich Ihnen gerne gleich. Damit ich Sie richtig verstehe:",
            "Klar, darauf gehen wir gleich ein. Helfen Sie mir davor kurz:",
            "Gute Frage, dazu gleich mehr. Zuerst würde ich gern wissen:",
            "Kein Problem, das klären wir gleich. Darf ich vorher noch fragen:"
          ].join(" | ")
        }
      ]
    },

  gegenfrage_antwort_plus: {
    id: "gegenfrage_antwort_plus",
    label: "GEGENFRAGE MIT ANTWORT",
    trigger_logic: "always",
    trigger_description:
      "BEACHTE IMMER DAS AKTUELLE MIKROZIEL: activeGoalText. Die GEGENFRAGE MUSS SICH DARAN ORIENTIEREN - IMMEr:NIEMALS TRIGGERN OHNE ECHTE FRAGE. Triggered bei Kombination: [ANTWORT] + [ECHTE RÜCKFRAGE]. Beispiel: 'Ja, und Sie?', 'Gut und selbst?'. EXCLUSIONS: 'was doch ich habe das budget' (Ausruf!), 'echt jetzt?' (Zweifel), 'oder?' (Füllwort). Es muss eine klare Informationsfrage sein. Ignoriere 'Ich weiß nicht'.",
    // SCOPE: Global allowed
    allowedModules: [],
    steps: [
      {
        text: [
          "1. Beantworte die Rückfrage des Prospect/Interessents kurz, souverän und authentisch passend zum Kontext.",
          "2. WICHTIG: Wenn das aktuelle Mikroziel ({activeGoalText}) noch nicht erfüllt ist, stelle eine neue, vertiefende Frage dazu.",
          "3. WENN das Ziel erfüllt scheint, stelle die NÄCHSTE logische Frage im Skriptverlauf.",
          "4. VERMEIDE generische Floskeln wie 'Wie geht es Ihnen?', wenn wir schon mitten im Thema sind.",
          "FORMEL: [Kurze Antwort] + [Souveräne Überleitung] + [Gezielte Frage zum Fortschritt]"
        ].join(" ")
      }
    ]
  }

};

export const PROMPT_MODULES = {
    classification: `
        *** CRITICAL CLASSIFICATION REQUIRED FOR '1_focus_einordnung_thema' ***
        You MUST classify the user's main issue into exactly ONE of these three categories:
        1. "outcome" (Result problems: missing revenue, missing employees/drivers, weight loss not reached, specific goal not met).
        2. "process" (Workflow problems: efficiency, chaos, bad structure, too much time spent).
        3. "skill" (Knowledge problems: doesn't know how, lacks ability/experience).
        
        STRICT EVIDENCE RULE:
        - ONLY classify as "skill" if the user explicitly says "I don't know how".
        - ONLY classify as "process" if the user mentions "chaos", "structure", or "time".
        - If unsure, leave empty.
        `
};




function replacePlaceholders(text: string, variables: Record<string, any>, genericLabel: string): string {
    if (!text) return "";
    
    let processed = text.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
        if (key === 'VARIABLE') return match; 
        const val = variables[key];
        if (val !== undefined && val !== null && val !== "") {
             return String(val);
        }
        return ""; 
    });
    
    processed = processed.replace(/\(\)/g, '').replace(/\s+/g, ' ').trim();

    if (processed.includes('{VARIABLE}')) {
        processed = processed.replace(/\{VARIABLE\}/g, genericLabel);
    }
    
    return processed;
}
